import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, memo, useMemo, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { ShoppingCart, X, Search, Trash2, ChevronRight, ChevronLeft, ChevronDown, Menu, Filter, Minus, Plus, MessageCircle, Facebook, Instagram, Loader2, Pencil, Image as ImageIcon, LayoutTemplate, User, UserPlus, LogIn, Twitter, PanelRightClose, Languages, Monitor, Smartphone, Tablet, Undo2, Redo2, History, Eye, HelpCircle, LayoutGrid, Tag, PanelBottom, Palette, Brush, SlidersHorizontal, Layers, Settings, Truck, ShieldCheck, RotateCcw, Headphones, Mail, Play, Download, FileText, Home, PackagePlus, BookOpen, Award, Phone, ShoppingBag, ExternalLink, LogOut } from 'lucide-react';
import axios from "axios";
import { API_URL } from '../apiConfig';
import { useCatalogStudioApi } from '../utils/catalogStudioApi';
import useMobileScrollLock from '../utils/useMobileScrollLock';
import { takeEarlyResponse, takeEarlyProductsByIds } from '../utils/earlyFetch';
import { ensureAllStudioFonts, ensureCatalogFontStack } from '../utils/ensureFonts';
import { bearerAuthHeaders, readAccessToken } from "../authTokenUtils";
import FullscreenLoader from '../components/FullscreenLoader';
import WhatsAppBrandIcon from '../components/WhatsAppBrandIcon';
import TikTokBrandIcon from '../components/TikTokBrandIcon';
import {
  CATALOG_STUDIO_TOUR_STORAGE_KEY,
  isCatalogStudioTourCompletedLocally,
  markCatalogStudioTourCompletedLocally,
} from '../components/catalogStudioTourStorage';
import LanguageSwitchOverlay from '../components/LanguageSwitchOverlay';
import { runMobileLanguageDirectionTransition } from '../utils/mobileLanguageSwitchTransition';
import {
  DEFAULT_CLOSED_MESSAGE_AR,
  isStoreOpenNow,
  normalizeBusinessHours,
} from '../utils/businessHours';
import {
  categoryCircleFontSizePx,
  categoryCircleGlyphMeta,
  isAddonAvailableForSize,
  resolveAddonPriceForSize,
} from '../utils/productAddons';
import './Catalog.css';
import { useNavigate, useParams, useBlocker } from "react-router-dom";
import {
  availableQtyForSelection,
  cartLineStockKey,
  consolidateCartLines,
  maxQtyForSelection,
  mergeCartLine,
  normalizeOptionList,
  optionDepleted,
  productGloballyOutOfStock,
  productHasAnyStockTracked,
  productVariantSelectionError,
  sortExtraPricesList,
  sortOptionListByPrice,
  sumCartQtyForStockKey,
  validateCartStockLimits,
} from "../utils/variantStock";
import { resolveVariantImageIndex } from "../utils/variantImages";
import { runWhenIdle } from "../utils/runWhenIdle";
import { initMetaPixel, markMetaPixelPending, trackMetaViewContent, trackMetaAddToCart, trackMetaInitiateCheckout } from "../utils/metaPixel";
import {
  initTikTokPixel,
  markTikTokPixelPending,
  trackTikTokViewContent,
  trackTikTokAddToCart,
  trackTikTokInitiateCheckout,
} from "../utils/tiktokPixel";
import { normalizeOrderStatus, orderStatusLabel, ORDER_STATUS_VALUES } from "../utils/orderStatus";
import { formatOrderNumberDisplay, normalizeOrderNumberInput } from "../utils/orderNumber";
import {
  formatOrderItemLineLabel,
  formatOrderItemVariantMeta,
  isAllSizesVariantLabel,
  shouldShowAllSizesVariant,
  dedupeVariantLabel,
  variantLabelForOrderLine,
} from "../utils/variantDisplay";
import { compressProductImageFile } from "../utils/productImageCompress";
import { compressHeroImageFile, heroImageSources } from "../utils/heroImage";
import {
  DELIVERY_REGIONS,
  defaultDeliveryPricesMap,
  deliveryRegionLabel,
} from "../utils/deliveryRegions";
import {
  checkoutShouldShowDeliveryFee,
  deliveryFeeForRegion,
} from "../utils/deliveryFeeDisplay";
import { useCatalogWholesaleSession } from "../hooks/useCatalogWholesaleSession";
import {
  cartItemWholesaleUnitPrice,
  getWholesaleToken,
  resolveVariantLabelForSize,
  wholesaleCatalogFetch,
} from "../utils/catalogWholesale";
import CatalogWholesaleStrip from "../components/catalog/CatalogWholesaleStrip";
import CatalogCategoryPillsStrip from "../components/catalog/CatalogCategoryPillsStrip";
import CatalogNotFoundModal from "../components/catalog/CatalogNotFoundModal";
import CatalogStoreUnavailable from "../components/catalog/CatalogStoreUnavailable";
import CustomOrderSection from "../components/catalog/CustomOrderSection";
import BundleOffersSection from "../components/catalog/BundleOffersSection";
import {
  customOrderMergeKey,
  customOrderNoteText,
  customOrderAddonsFromAnswers,
  serializeCustomOrderAnswersForApi,
} from "../utils/customOrderForm";
import {
  bundleOfferMergeKey,
  bundleOfferNoteText,
  bundleOfferSnapshotForCart,
  inferBundleOfferMetaFromCartItem,
  computeBundleLineTotal,
  enrichBundlePickRow,
  normalizeBundleOffers,
  serializeBundlePicksForApi,
  defaultBundleOffer,
  whatsappOrderTitleForCart,
} from "../utils/bundleOffers";
import SearchableSelect from "../components/SearchableSelect";
import { carouselSlideHeightPx, hasDedicatedStudioPanel, isProImageBannerVariant, INFO_CARD_ICONS } from "../components/catalogStudioShared";
import {
  preloadStudioEditors,
  preloadStorefrontModals,
  CatalogStudioTour,
  HeroImageFocalPicker,
  CatalogStudioSectionPickerSkeleton,
  StudioCustomOrderEditor,
  StudioBundleOffersEditor,
  ConfirmModal,
  ErrorModal,
  CatalogWholesaleAuthModal,
  CatalogWholesaleOrdersModal,
  StudioSectionFrameSettings,
  StudioSectionHeadingFields,
  StudioFaqEditor,
  StudioCountdownEditor,
  StudioBrandsAppearanceFields,
  StudioStatsEditor,
  StudioDealsProductsEditor,
  StudioCategoryItemsEditor,
  StudioCircleItemsEditor,
  StudioTrustItemsEditor,
  StudioPromoBannerEditor,
  StudioSlidesEditor,
  StudioBlogPostsEditor,
  StudioAboutCardsEditor,
  StudioCategoryLayoutFields,
  StudioCarouselOverlaySettings,
  StudioCarouselSectionEditor,
  StudioPanel,
  StudioField,
  StudioColorField,
  StudioInput,
  StudioSelect,
  StudioTextarea,
  StudioInfoCardsEditor,
} from "../components/CatalogStudioLazy";

const BUILDER_IMAGE_UPLOAD_TIMEOUT_MS = 120000;
/** Videos can be large; allow up to 10 minutes before aborting the client request. */
const BUILDER_VIDEO_UPLOAD_TIMEOUT_MS = 600000;

function getDeliveryCheckoutMissingFields(form, { isRestaurant, cities }) {
  const missing = [];
  const name = (form?.name || "").trim();
  const region = String(form?.deliveryRegion || "").trim();
  const cityId = String(form?.cityId || "").trim();
  const addressDetails = (form?.address || "").trim();
  const phone = (form?.phone || "").trim();

  if (!name) missing.push("الاسم");
  if (!isRestaurant && !region) missing.push("منطقة التوصيل");
  if (isRestaurant && !region) missing.push("منطقة التوصيل");
  const citiesInRegion = region
    ? (cities || []).filter((c) => String(c.region) === region)
    : [];
  if (!isRestaurant && citiesInRegion.length > 0 && !cityId) {
    missing.push("العنوان");
  }
  if (!addressDetails) missing.push("تفاصيل العنوان");
  if (!phone) missing.push("رقم الهاتف");
  return missing;
}

function normalizeCheckoutPhoneDigits(phone) {
  return String(phone || "").replace(/\D/g, "");
}

function isValidCheckoutPhone(phone) {
  return /^\d{10}$/.test(normalizeCheckoutPhoneDigits(phone));
}

/** Store setting: open WhatsApp after checkout (handles bool/string from API). */
function parseSendOrdersToWhatsapp(value) {
  if (value === false || value === 0) return false;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "false" || s === "0" || s === "no" || s === "off") return false;
  }
  return true;
}

function normalizeWhatsAppDigits(number) {
  return String(number || "").replace(/\D/g, "");
}

function isValidStoredWhatsappNumber(number) {
  const digits = normalizeWhatsAppDigits(number);
  if (!digits) return false;
  for (const cc of ["970", "972"]) {
    if (digits.startsWith(cc)) {
      const local = digits.slice(cc.length);
      return local.length === 9 && local.startsWith("5");
    }
  }
  return digits.length === 9 && digits.startsWith("5");
}

/**
 * When can the catalog send an order via WhatsApp?
 * WhatsApp-only plans (Basic): a valid number is enough — the extra toggle is ignored.
 * Plans with an orders page: still honor send_orders_to_whatsapp.
 */
function shouldSendOrderViaWhatsapp(settings, { whatsappOnlyPlan = false } = {}) {
  if (!isValidStoredWhatsappNumber(settings?.whatsappNumber)) return false;
  if (whatsappOnlyPlan) return true;
  return parseSendOrdersToWhatsapp(settings?.sendOrdersToWhatsapp);
}

/** When applying a library theme, use only merchant-owned contact/name — never theme placeholders. */
function preferMerchantThemeValue(_themeVal, merchantVal) {
  return merchantVal ?? "";
}

function stripStudioTourFlagsFromLayoutExtras(extras) {
  const out = { ...(extras || {}) };
  for (const k of Object.keys(out)) {
    if (String(k).startsWith("studio_tour")) delete out[k];
  }
  return out;
}

function storeContactSnapshot(storeEdit, settings) {
  const phone = String(storeEdit?.phone || settings?.phone || "").trim();
  const email = String(storeEdit?.email || settings?.email || "").trim();
  const address = String(storeEdit?.address || settings?.address || "").trim();
  const facebook = String(storeEdit?.facebook || settings?.facebook || "").trim();
  const instagram = String(storeEdit?.instagram || settings?.instagram || "").trim();
  const whatsappNumber = String(settings?.whatsappNumber || "").trim();
  const waDigits = normalizeWhatsAppDigits(whatsappNumber);
  const socialWhatsapp = waDigits ? `https://wa.me/${waDigits}` : "";
  return { phone, email, address, facebook, instagram, whatsappNumber, socialWhatsapp };
}

function hydrateBuilderSectionStoreContact(settings, contact) {
  if (!settings || typeof settings !== "object" || !contact) return settings;
  const next = { ...settings };
  next.social_facebook = contact.facebook || "";
  next.social_instagram = contact.instagram || "";
  next.social_whatsapp = contact.socialWhatsapp || "";
  return next;
}

function hydrateBuilderSectionsStoreContact(sections, contact) {
  if (!Array.isArray(sections) || !contact) return sections;
  return sections.map((sec) => ({
    ...sec,
    settings: hydrateBuilderSectionStoreContact(sec.settings || {}, contact),
  }));
}

function categoryCircleGlyphStyle(label) {
  const len = String(label || "").trim().length;
  const vw = typeof window !== "undefined" ? window.innerWidth : 390;
  const circlePx = Math.min(92, Math.max(68, vw * 0.18));
  const fontSize = categoryCircleFontSizePx(label, circlePx);
  const lineHeight = len > 16 ? 1.05 : len > 10 ? 1.1 : 1.15;
  return { fontSize: `${fontSize}px`, lineHeight };
}

function buildWhatsAppMeUrl(digits, messageText) {
  const phone = normalizeWhatsAppDigits(digits);
  if (!phone) return "";
  const encoded = encodeURIComponent(String(messageText || ""));
  return `https://wa.me/${phone}?text=${encoded}`;
}

/**
 * Open WhatsApp without losing the user-gesture chain (popup blockers).
 * Call openWhatsAppPopupPlaceholder() synchronously on click, then this after async work.
 */
function openWhatsAppPopupPlaceholder() {
  if (typeof window === "undefined") return null;
  try {
    return window.open("about:blank", "_blank");
  } catch {
    return null;
  }
}

function openWhatsAppUrl(url, preOpenedWindow) {
  if (!url || typeof window === "undefined") return false;

  try {
    if (preOpenedWindow && !preOpenedWindow.closed) {
      preOpenedWindow.location.href = url;
      preOpenedWindow.focus?.();
      return true;
    }
  } catch (_) {
    /* fall through */
  }

  try {
    if (window.open(url, "_blank", "noopener,noreferrer")) return true;
  } catch (_) {
    /* fall through */
  }

  try {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } catch (_) {
    return false;
  }
}

/** Never show raw Python/server internals to store visitors */
function isTechnicalCatalogErrorDetail(detail) {
  if (!detail || typeof detail !== 'string') return false;
  return /nonetype|attributeerror|traceback|keyerror|typeerror|internal server|exception/i.test(
    detail
  );
}

/** Store exists but is not live for visitors (expired / suspended subscription). */
function isCatalogStoreUnavailableError(status, detail) {
  if (status !== 403) return false;
  const d = String(detail || "").toUpperCase();
  if (d.includes("CATALOG_INACTIVE") || d.includes("CATALOG_SUSPENDED")) return true;
  if (d.includes("SUBSCRIPTION") && d.includes("EXPIRED")) return true;
  if (d.includes("CATALOG") && (d.includes("UNAVAILABLE") || d.includes("DEACTIVATED"))) {
    return true;
  }
  return false;
}

function pickCustomizerErrorMessage(e) {
  const d = e?.response?.data?.detail;
  if (d == null || d === '') return String(e?.message || '').trim();
  if (typeof d === 'string') return d.trim();
  if (Array.isArray(d)) {
    try {
      return d
        .map((x) => (x && typeof x === 'object' && x.msg ? String(x.msg) : String(x)))
        .filter(Boolean)
        .join(' — ');
    } catch {
      return 'خطأ في التحقق من البيانات';
    }
  }
  if (typeof d === 'object' && d.message) return String(d.message);
  try {
    return JSON.stringify(d);
  } catch {
    return String(d);
  }
}

/** Safe `backgroundImage` value for arbitrary image URLs (spaces, parentheses in query strings, etc.). */
function cssBackgroundImageFromUrl(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return undefined;
  const escaped = s.replace(/\\/g, "/").replace(/"/g, "%22");
  return `url("${escaped}")`;
}

function catalogHeroSessionKey(ident) {
  const id = String(ident || "").trim();
  return id ? `kataloji:catalog-hero:${id}` : "";
}

function catalogLogoStorageKey(ident) {
  const id = String(ident || "").trim();
  return id ? `kataloji:catalog-logo:${id}` : "";
}

function readCachedCatalogHeroUrl(ident) {
  try {
    const key = catalogHeroSessionKey(ident);
    if (!key || typeof sessionStorage === "undefined") return "";
    return String(sessionStorage.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

function writeCachedCatalogHeroUrl(ident, url) {
  try {
    const key = catalogHeroSessionKey(ident);
    if (!key || typeof sessionStorage === "undefined") return;
    const u = String(url || "").trim();
    if (u) sessionStorage.setItem(key, u);
    else sessionStorage.removeItem(key);
  } catch {
    // ignore quota / private mode
  }
}

function readCachedCatalogLogoUrl(ident) {
  try {
    const key = catalogLogoStorageKey(ident);
    if (!key) return "";
    if (typeof localStorage !== "undefined") {
      const fromLocal = String(localStorage.getItem(key) || "").trim();
      if (fromLocal) return fromLocal;
    }
    if (typeof sessionStorage !== "undefined") {
      return String(sessionStorage.getItem(key) || "").trim();
    }
    return "";
  } catch {
    return "";
  }
}

function writeCachedCatalogLogoUrl(ident, url) {
  try {
    const key = catalogLogoStorageKey(ident);
    if (!key) return;
    const u = String(url || "").trim();
    const store = (storage) => {
      if (!storage) return;
      if (u) storage.setItem(key, u);
      else storage.removeItem(key);
    };
    if (typeof localStorage !== "undefined") store(localStorage);
    if (typeof sessionStorage !== "undefined") store(sessionStorage);
  } catch {
    // ignore quota / private mode
  }
}

function catalogLoaderPrefsKey(ident) {
  const id = String(ident || "").trim();
  return id ? `kataloji:catalog-loader:${id}` : "";
}

function readCachedCatalogLoaderPrefs(ident) {
  try {
    const key = catalogLoaderPrefsKey(ident);
    if (!key || typeof localStorage === "undefined") return { text: "", color: "" };
    const raw = localStorage.getItem(key);
    if (!raw) return { text: "", color: "" };
    const parsed = JSON.parse(raw);
    return {
      text: String(parsed?.text || "").trim(),
      color: String(parsed?.color || "").trim(),
    };
  } catch {
    return { text: "", color: "" };
  }
}

function writeCachedCatalogLoaderPrefs(ident, text, color) {
  try {
    const key = catalogLoaderPrefsKey(ident);
    if (!key || typeof localStorage === "undefined") return;
    localStorage.setItem(
      key,
      JSON.stringify({
        text: String(text || "").trim(),
        color: String(color || "").trim() || "#000000",
      })
    );
  } catch {
    // ignore
  }
}

/**
 * Store data injected by the server into the HTML (`window.__STORE__`) so the header + banner can
 * paint immediately, without waiting for the /catalog fetch (the banner is also preloaded in <head>).
 * Returns null when absent or when it belongs to another store -> the normal fetch flow is used.
 */
function readStoreSeed(ident) {
  try {
    if (typeof window === "undefined") return null;
    const seed = window.__STORE__;
    if (!seed || typeof seed !== "object" || !Array.isArray(seed.keys)) return null;
    const id = String(ident || "").trim().toLowerCase();
    if (!id || !seed.keys.some((k) => String(k || "").toLowerCase() === id)) return null;
    return seed;
  } catch {
    return null;
  }
}

function storeSeedSettings(seed, base) {
  const t = seed.theme || {};
  const h = seed.hero || {};
  const f = seed.features || {};
  const hero = String(seed.banner || "").trim();
  return {
    ...base,
    storeName: String(seed.name || ""),
    logo: String(seed.logo || "").trim() || null,
    primaryColor: t.primary_color || "#ffc200",
    secondaryColor: t.secondary_color || "#ff5b00",
    layoutDirection: t.layout_direction || "rtl",
    headerTitle: h.title || "",
    subheaderText: h.subtitle || "",
    heroUrl: hero,
    bgType: hero ? "image" : "color",
    feature_description_1: f.description_1 || "",
    feature_description_2: f.description_2 || "",
    feature_description_3: f.description_3 || "",
    feature_title_1: f.title_1 || "توصيل سريع",
    feature_title_2: f.title_2 || "ضمان الجودة",
    feature_title_3: f.title_3 || "دعم فني",
    features_bar_bg: f.bar_bg || "",
    features_bar_text: f.bar_text || "",
    features_bar_accent: f.bar_accent || "",
  };
}

function storeSeedLayouts(seed, base) {
  const l = seed.layouts || {};
  const topHero = l.show_top_hero ?? l.show_hero ?? true;
  const rawExtras =
    l.catalog_layout_extras && typeof l.catalog_layout_extras === "object" ? l.catalog_layout_extras : null;
  const lx = { ...DEFAULT_CATALOG_LAYOUT_EXTRAS, ...(rawExtras || {}) };
  migrateLegacyLayoutExtras(lx, rawExtras);
  return {
    ...base,
    show_hero: topHero,
    show_top_hero: topHero,
    show_grid: l.show_grid ?? true,
    show_features_bar: l.show_features_bar ?? true,
    product_columns: l.product_columns || 3,
    catalog_layout_extras: lx,
  };
}

const KATALOJI_WORDMARK_SRC = "/kataloji logo wout bg-01.png";
const KATALOJI_LOADER_FALLBACK_SRC = "/kataloji_512x512.png";

/** Hero desktop/mobile swap breakpoint — mirrors `max-width: 1023.98px` in Catalog.css (.hero-bg-layer--desktop / --mobile). */
const HERO_DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

/**
 * Is this hero layer actually displayed at the current viewport?
 * "desktop" layers are `display:none` below 1024px, "mobile" layers at/above it, "all" layers always show.
 * A hidden layer must not fetch with high priority (it would compete with the visible LCP image).
 */
function useHeroLayerActive(viewport) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? true
      : window.matchMedia(HERO_DESKTOP_MEDIA_QUERY).matches
  );
  useEffect(() => {
    if (viewport === "all" || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mq = window.matchMedia(HERO_DESKTOP_MEDIA_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [viewport]);
  if (viewport === "desktop") return isDesktop;
  if (viewport === "mobile") return !isDesktop;
  return true;
}

/** <link rel="preload" as="image"> for the visible hero image (removed on unmount / URL change). */
function useHeroImagePreload(src, active, srcSet, sizes) {
  useEffect(() => {
    if (!src || !active || typeof document === "undefined") return undefined;
    // The server shell already preloads this exact URL (same href): a second preload would only be noise.
    const already = Array.from(document.head.querySelectorAll('link[rel="preload"][as="image"]')).some(
      (l) => l.getAttribute("href") === src
    );
    if (already) return undefined;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    // Same candidates as the <img> (see heroImageSources), so both resolve to one request.
    if (srcSet) {
      link.setAttribute("imagesrcset", srcSet);
      if (sizes) link.setAttribute("imagesizes", sizes);
    }
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
    return () => {
      try {
        link.remove();
      } catch {
        // ignore
      }
    };
  }, [src, active, srcSet, sizes]);
}

/**
 * Reliable hero cover: real <img> (not only CSS background) with one cache-bust retry.
 * CSS backgrounds fail silently and leave the brand-color fallback looking "broken".
 * Fills its absolutely-positioned `.hero-bg-layer` (CSS width/height 100%), so it cannot shift layout;
 * width/height attributes are only an intrinsic-size hint.
 */
function HeroCoverImage({ src, position = "center center", className = "", fit = "cover", viewport = "all" }) {
  const base = String(src || "").trim();
  const [displaySrc, setDisplaySrc] = useState(base);
  const [failed, setFailed] = useState(false);
  const retriedRef = useRef(false);
  const active = useHeroLayerActive(viewport);

  useEffect(() => {
    setDisplaySrc(base);
    setFailed(false);
    retriedRef.current = false;
  }, [base]);

  const sources = useMemo(() => heroImageSources(base, viewport), [base, viewport]);
  useHeroImagePreload(base, active, sources.srcSet, sources.sizes);

  if (!base || failed) return null;

  // After the cache-bust retry the URL differs from the srcSet candidate: fall back to plain src.
  const retrying = displaySrc !== base;

  return (
    <img
      src={displaySrc}
      srcSet={retrying ? undefined : sources.srcSet}
      sizes={retrying ? undefined : sources.sizes}
      alt=""
      className={`hero-bg-layer__cover-img${className ? ` ${className}` : ""}`}
      width={1920}
      height={1080}
      decoding="async"
      loading={active ? "eager" : "lazy"}
      fetchPriority={active ? "high" : "auto"}
      style={{
        objectFit: fit === "contain" ? "contain" : "cover",
        objectPosition: position,
      }}
      onError={() => {
        if (!retriedRef.current && base) {
          retriedRef.current = true;
          const bust = `${base}${base.includes("?") ? "&" : "?"}cb=${Date.now()}`;
          setDisplaySrc(bust);
          return;
        }
        setFailed(true);
      }}
    />
  );
}

/**
 * Natural size of the full-width (`contain`) mobile hero image is not stored by the backend, so:
 *  - the real size is remembered per URL after the first load (repeat visits reserve the exact box), and
 *  - the first visit falls back to 16:9 (the typical desktop banner that mobile reuses when no mobile asset is set).
 */
const HERO_CONTAIN_FALLBACK_SIZE = { w: 1600, h: 900 };

function heroImageSizeStorageKey(src) {
  return `kataloji:hero-img-size:${src}`;
}

function readCachedHeroImageSize(src) {
  try {
    if (!src || typeof localStorage === "undefined") return null;
    const [w, h] = String(localStorage.getItem(heroImageSizeStorageKey(src)) || "")
      .split(",")
      .map((n) => Number(n));
    return w > 0 && h > 0 ? { w, h } : null;
  } catch {
    return null;
  }
}

function writeCachedHeroImageSize(src, w, h) {
  try {
    if (!src || !(w > 0) || !(h > 0) || typeof localStorage === "undefined") return;
    localStorage.setItem(heroImageSizeStorageKey(src), `${w},${h}`);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * In-flow hero image (mobile/tablet `contain` mode: the section height follows the image).
 * width/height attributes + CSS `width:100%; height:auto` (.hero-bg-layer__img) reserve the box before load.
 */
function HeroContainImage({ src, viewport = "mobile" }) {
  const base = String(src || "").trim();
  const active = useHeroLayerActive(viewport);
  const sources = useMemo(() => heroImageSources(base, viewport), [base, viewport]);
  useHeroImagePreload(base, active, sources.srcSet, sources.sizes);
  const size = useMemo(
    () => readCachedHeroImageSize(base) || HERO_CONTAIN_FALLBACK_SIZE,
    [base]
  );
  if (!base) return null;
  return (
    <img
      src={base}
      srcSet={sources.srcSet}
      sizes={sources.sizes}
      alt=""
      className="hero-bg-layer__img"
      width={size.w}
      height={size.h}
      decoding="async"
      loading={active ? "eager" : "lazy"}
      fetchPriority={active ? "high" : "auto"}
      onLoad={(e) => writeCachedHeroImageSize(base, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
    />
  );
}

function clampHeroPositionPct(n, fallback = 50) {
  const v = Number(n);
  return Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : fallback;
}

/** Focal point for hero background (x/y: 0–100%). */
function heroBackgroundPositionFromXY(x, y) {
  return `${clampHeroPositionPct(x, 50)}% ${clampHeroPositionPct(y, 50)}%`;
}

function formatCustomizerSaveError(e) {
  const raw = pickCustomizerErrorMessage(e);
  const s = String(raw || '').trim();
  if (!s) return 'تعذّر إكمال العملية. أعد المحاولة.';
  if (
    /connectionterminated|error_code|last_stream|stream_id|http\s*2|http2|h2\s*err|ECONNRESET|ECONNABORTED|ETIMEDOUT|socket|network error|failed to fetch|net::ERR|additional_data|stream closed|goaway/i.test(
      s
    )
  ) {
    return 'انقطع الاتصال بالخادم. تحقق من الشبكة ثم أعد المحاولة.';
  }
  if (isTechnicalCatalogErrorDetail(s)) {
    return 'حدث خطأ في الخادم. أعد المحاولة لاحقًا.';
  }
  if (s.length > 260) return `${s.slice(0, 257)}…`;
  return s;
}

function serializeStudioPersistBundle(bundle) {
  try {
    return JSON.stringify({
      storeEdit: bundle.storeEdit,
      layoutsEdit: bundle.layoutsEdit,
      builderDraft: bundle.builderDraft,
      builderTheme: bundle.builderTheme,
    });
  } catch {
    return "";
  }
}

const STUDIO_HISTORY_MAX = 50;

function cloneStudioPersistSnapshot(bundle) {
  try {
    return {
      storeEdit: JSON.parse(JSON.stringify(bundle.storeEdit || {})),
      layoutsEdit: JSON.parse(JSON.stringify(bundle.layoutsEdit || {})),
      builderDraft: JSON.parse(JSON.stringify(Array.isArray(bundle.builderDraft) ? bundle.builderDraft : [])),
      builderTheme: String(bundle.builderTheme ?? "minimal"),
    };
  } catch {
    return {
      storeEdit: {},
      layoutsEdit: {},
      builderDraft: [],
      builderTheme: "minimal",
    };
  }
}

function studioLocalDraftStorageKey(userId) {
  const id = String(userId || "").trim();
  return id ? `catalogStudioLocalDraft_v1_${id}` : "";
}

function formatCartLineExtra(item) {
  if (item?.isBundleOffer) {
    const note = String(item.note || "").trim();
    if (!note) return "";
    return note.split("\n").slice(1).map((l) => l.replace(/^•\s*/, "")).join(" — ");
  }
  if (item?.isCustomOrder) {
    const note = String(item.note || "").trim();
    if (!note) return "";
    const lines = note.split("\n").slice(1);
    return lines.map((l) => l.replace(/^•\s*/, "")).join(" — ");
  }
  const parts = [];
  const variant = (item.selectedVariant || '').trim();
  const sz = (item.selectedSize || '').trim();
  const cl = (item.selectedColor || '').trim();
  if (
    variant &&
    variant !== 'الافتراضي' &&
    (!isAllSizesVariantLabel(variant) || shouldShowAllSizesVariant(variant, sz, cl))
  ) {
    const shown = variantLabelForOrderLine(variant, sz, cl);
    if (shown) parts.push(shown);
  }
  const variantMeta = formatOrderItemVariantMeta(
    { selected_size: sz, selected_color: cl },
    'ar'
  );
  if (variantMeta) parts.push(variantMeta);
  const ads = Array.isArray(item.selectedAddons) ? item.selectedAddons : [];
  if (ads.length) {
    parts.push(
      ads
        .map((a) => `${String(a.name || '').trim()} (+₪${Number(a.price || 0)})`)
        .join('، ')
    );
  }
  return parts.length ? parts.join(' — ') : '';
}

/** Detail lines for bundle/custom order items (one line per product/option). */
function formatCartLineDetailLines(item) {
  if (item?.isBundleOffer || item?.isCustomOrder) {
    const note = String(item.note || "").trim();
    if (!note) return [];
    return note
      .split("\n")
      .slice(1)
      .map((l) => l.trim())
      .filter(Boolean);
  }
  const extra = formatCartLineExtra(item);
  return extra ? [extra] : [];
}

function formatWhatsAppCartLine(item, idx) {
  const qty = Number(item.quantity || 1);
  const unit = cartLineUnitPrice(item);
  const lineTotal = unit * qty;
  const lines = [`${idx + 1}) ${item.name}`];
  const detailLines = formatCartLineDetailLines(item);
  if (detailLines.length) {
    for (const line of detailLines) {
      lines.push(`   ${line.startsWith("•") ? line : `• ${line}`}`);
    }
  } else {
    const extra = formatCartLineExtra(item);
    if (extra) lines[0] = `${idx + 1}) ${item.name} (${extra})`;
    const note = String(item.note || "").trim();
    if (note && !item?.isBundleOffer && !item?.isCustomOrder) {
      lines.push(`   - ملاحظة: ${note}`);
    }
  }
  lines.push(`   - السعر: ₪${unit}`);
  lines.push(`   - الكمية: ${qty}`);
  lines.push(`   - المجموع: ₪${lineTotal}`);
  return lines.join("\n");
}

function resolveCartItemForWhatsApp(item, productsById) {
  if (!item?.isBundleOffer) return item;
  const existing = String(item.note || "").trim();
  const picks = Array.isArray(item.bundlePicks) ? item.bundlePicks : [];
  const meta = inferBundleOfferMetaFromCartItem(item);
  const brokenBase = existing && /سعر العرض:\s*₪0(?:\.0+)?/.test(existing);
  const needsRebuild =
    Boolean(item.bundleOfferMeta) ||
    !existing ||
    brokenBase ||
    !existing.includes("سعر العرض");
  if (!needsRebuild) return item;
  if (!picks.length && !existing) return item;
  const map = productsById instanceof Map ? productsById : new Map();
  const note = bundleOfferNoteText(meta, picks, map);
  return { ...item, note };
}

function cartItemsForWhatsApp(cart, productsById) {
  return (Array.isArray(cart) ? cart : []).map((item) =>
    resolveCartItemForWhatsApp(item, productsById)
  );
}

function cartAddonSum(item) {
  const ads = Array.isArray(item.selectedAddons) ? item.selectedAddons : [];
  return ads.reduce((s, a) => s + Number(a.price || 0), 0);
}

/** سعر القطعة الواحدة للسلة وللـ API: أساس الخيار + الإضافات المختارة */
function cartLineUnitPrice(item) {
  if (item?.isBundleOffer || item?.isCustomOrder) {
    return Math.round((Number(item.price || 0) + Number.EPSILON) * 100) / 100;
  }
  const addonSum = cartAddonSum(item);
  const bu = item.baseUnitPrice;
  if (bu != null && bu !== '' && Number.isFinite(Number(bu))) {
    return Math.round((Number(bu) + addonSum + Number.EPSILON) * 100) / 100;
  }
  return Math.round((Number(item.price || 0) + Number.EPSILON) * 100) / 100;
}

/** Show «أضيف حديثاً» for 7 days after product created_at */
function isProductWithinNewBadgeWindow(createdAt) {
  if (createdAt == null || createdAt === '') return false;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return false;
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - t < weekMs;
}

/** أقسام البناء: ما فوق المنتجات الأساسي vs ما تحته (حقل settings.after_products) */
function partitionBuilderSections(draft) {
  const list = Array.isArray(draft) ? draft : [];
  const before = [];
  const after = [];
  for (const s of list) {
    if (s?.settings?.after_products) after.push(s);
    else before.push(s);
  }
  return { before, after };
}

/** ثيم الملابس: الميزات (trust) ثم بطاقات الفئات — وليس العكس */
function orderFashionBuilderBeforeSections(sections) {
  const list = Array.isArray(sections) ? sections : [];
  if (list.length < 2) return list;
  const rank = (s) => {
    const v = String(s?.settings?.section_variant || "").toLowerCase();
    if (v === "trust") return 0;
    if (v === "categories") return 1;
    return 2;
  };
  return [...list].sort((a, b) => rank(a) - rank(b));
}

function mergeBuilderSections(before, after) {
  return [...(before || []), ...(after || [])];
}

function reorderWithMainProductsBoundary(shown, fromIndex, toIndex) {
  const list = Array.isArray(shown) ? shown : [];
  const f = Number(fromIndex);
  const t = Number(toIndex);
  if (!Number.isFinite(f) || !Number.isFinite(t)) return list;
  if (f < 0 || f >= list.length || t < 0 || t >= list.length) return list;
  if (f === t) return list;
  const next = [...list];
  const [moved] = next.splice(f, 1);
  next.splice(t, 0, moved);
  return next;
}

/** لون طبقة التعتيم فوق صورة كاروسيل الهيرو (#rgb / #rrggbb) وشفافية 0–1 */
function applyOpacityToColor(color, opacity) {
  const o = Math.min(1, Math.max(0, Number(opacity)));
  const safeO = Number.isNaN(o) ? 0.45 : o;
  const raw = String(color ?? "").trim();
  if (!raw) return `rgba(15, 23, 42, ${safeO})`;
  if (raw.startsWith("rgba(")) return raw;
  if (raw.startsWith("rgb(")) {
    return raw.replace("rgb(", "rgba(").replace(/\)\s*$/, `, ${safeO})`);
  }
  let hex = raw.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return `rgba(15, 23, 42, ${safeO})`;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${safeO})`;
}

// Alias used by restaurant theme controls
function toRgba(color, opacity) {
  return applyOpacityToColor(color, opacity);
}

/** Parses common CSS colors for topbar icon contrast (hex / rgb) */
function parseCssColorToRgb(input) {
  const s = String(input ?? "").trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === "transparent") return null;
  if (lower === "white" || lower === "#fff" || lower === "#ffffff") return { r: 255, g: 255, b: 255 };
  if (lower === "black" || lower === "#000" || lower === "#000000") return { r: 0, g: 0, b: 0 };
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
  if (rgb) return { r: +rgb[1], g: +rgb[2], b: +rgb[3] };
  return null;
}

function relativeLuminanceFromRgb(rgb) {
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const R = lin(rgb.r);
  const G = lin(rgb.g);
  const B = lin(rgb.b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function contrastRatioRgb(a, b) {
  const L1 = relativeLuminanceFromRgb(a);
  const L2 = relativeLuminanceFromRgb(b);
  const hi = Math.max(L1, L2);
  const lo = Math.min(L1, L2);
  return (hi + 0.05) / (lo + 0.05);
}

function readableAccentOnWhite(color, fallback = "#0f172a") {
  const rgb = parseCssColorToRgb(color);
  if (!rgb) return fallback;
  return contrastRatioRgb(rgb, { r: 255, g: 255, b: 255 }) >= 2.4 ? String(color).trim() : fallback;
}

/** Icon for a navbar link in the mobile drawer — by target, then by label keywords. */
function catalogNavLinkIcon(link) {
  const target = String(link?.target || "").trim();
  const label = String(link?.label || "").toLowerCase();
  const has = (...words) => words.some((w) => label.includes(w));
  if (target === "hero" || has("الرئيسية", "home")) return Home;
  if (has("جديد", "new")) return PackagePlus;
  if (has("فئ", "أقسام", "اقسام", "categor")) return LayoutGrid;
  if (has("قصت", "من نحن", "about", "story")) return BookOpen;
  if (has("لماذا", "ليش", "why")) return Award;
  if (has("عروض", "خصم", "offer", "sale")) return Tag;
  if (target === "footer" || has("تواصل", "اتصل", "contact")) return Phone;
  if (target === "products" || has("منتج", "product", "تسوق", "shop")) return ShoppingBag;
  if (target === "url") return ExternalLink;
  return LayoutTemplate;
}

/** Icon stroke color readable on the login chip background (avoids white-on-white when login text is for a dark bar) */
function pickReadableIconColor(bgStr, preferredStr, accentStr) {
  const bg = parseCssColorToRgb(bgStr) || { r: 255, g: 255, b: 255 };
  const pref = parseCssColorToRgb(preferredStr);
  const acc = parseCssColorToRgb(accentStr) || { r: 255, g: 91, b: 0 };
  if (pref && contrastRatioRgb(bg, pref) >= 2.4) return String(preferredStr).trim();
  if (contrastRatioRgb(bg, acc) >= 2.4) return String(accentStr).trim() || "#ff5b00";
  return relativeLuminanceFromRgb(bg) > 0.55 ? "#0f172a" : "#ffffff";
}

/** عرض عمود المحتوى الموحّد (هيدر، أقسام البناء، منتجات، فوتر) بالبكسل */
const CATALOG_PAGE_CONTENT_WIDTH_PX = { boxed: 1200, wide: 1400 };

/**
 * سكشن «تسوق حسب الفئة»: إظهار اسم الفئة كنص HTML فوق/أسفل الصورة.
 * اضبطه إلى false إذا كان الاسم مدمجاً داخل صورة الفئة (لتجنب التكرار).
 * يمكن أيضاً التحكم لكل قسم من الاستوديو عبر show_category_label.
 */
const CATEGORIES_SECTION_SHOW_LABEL = true;

/** أقصى عرض لبطاقة الفئة على الديسكتوب (px) — يقلّل ارتفاع الشبكة مع aspect-ratio */
const CATEGORIES_CARD_MAX_WIDTH_PX = { sm: 260, md: 300, lg: 340 };

const DEFAULT_CATALOG_LAYOUT_EXTRAS = {
  /** تفعيل لون خلفية يدوي لجسم المتجر (غير الهيرو/الفوتر/شريط التصنيفات) — الافتراضي: معطّل */
  global_page_bg_enabled: false,
  page_bg: "#ffffff",
  /**
   * عرض الصفحة الموحّد: فارغ = يتبع إعداد «عرض الحاوية» لكل قسم (سلوك قديم) | boxed | wide | full
   * عند التعيين: نفس القيمة لكل الأقسام ما عدا الأقسام المعيّنة «عرض كامل».
   */
  builder_page_container_width: "",
  /** شريط ثيم المطعم (العلوي المدمج) */
  restaurant_topbar_bg: "#0f172a",
  restaurant_topbar_text: "#ffffff",
  /** Restaurant theme palette + card controls */
  restaurant_bg: "#faf8f3",
  restaurant_surface: "#ffffff",
  restaurant_ink: "#2d2416",
  restaurant_muted: "rgba(45, 36, 22, 0.65)",
  restaurant_line: "rgba(45, 36, 22, 0.10)",
  restaurant_accent: "#3d6b4f",
  restaurant_accent_warm: "#c8813a",
  /** حجم صورة المنتج في الشبكة (دائرة أو مربع) — يُطبَّق على كل الثيمات */
  product_card_image_size: 240,
  /** توافق مع بيانات قديمة */
  restaurant_card_image_size: 240,
  /** إظهار وصف المنتج تحت الاسم في الشبكة */
  products_show_description: true,
  restaurant_show_desc: true,
  /** Category bar (restaurant): use color+opacity controls (more reliable than free text in RTL) */
  restaurant_category_bar_bg_color: "#faf8f3",
  restaurant_category_bar_bg_opacity: 0.97,
  restaurant_category_bar_border_color: "#2d2416",
  restaurant_category_bar_border_opacity: 0.1,
  /** Decorative shapes under products */
  restaurant_decor_style: "none", // none | circles | arcs | rings | waves
  restaurant_decor_color: "#c8813a",
  restaurant_decor_opacity: 0.28,
  category_nav: "top",
  /** محاذاة صف أزرار التصنيفات الأفقي: start | center | end — فارغ = السلوك الافتراضي للثيم */
  category_pills_align: "",
  filters_sidebar: false,
  filter_price: true,
  filter_new: true,
  filter_offers: true,
  /** قسم المنتجات (تصميم) */
  products_section_padding_y: "md",
  products_grid_gap: "md",
  products_card_style: "default",
  products_heading_color: "",
  products_subtitle_color: "",
  /** لون اسم المنتج ووصفه على البطاقة — فارغ = الافتراضي من التصميم */
  products_card_name_color: "",
  products_card_desc_color: "",
  /** لون سعر المنتج — فارغ = لون التمييز الأساسي */
  products_card_price_color: "",
  /** موقع السعر: "" = أسفل البطاقة بجانب السلة | below_name | on_image */
  products_card_price_position: "",
  /** محاذاة السعر أفقياً: "" = تلقائي (بجانب السلة) | right | center | left */
  products_card_price_align: "",
  /** خلفية شارة السعر عند on_image — فارغ = أبيض */
  products_card_price_bg_color: "",
  /** اسم الفئة: "" = نص صغير فوق اسم المنتج | on_image = شارة فوق الصورة | hidden */
  products_card_category_position: "",
  products_card_category_color: "",
  /** لون عنوان الهيرو — فارغ = الافتراضي من التصميم */
  hero_title_color: "",
  /** لون نص وصف الهيرو (الفقرة تحت العنوان) — فارغ = الافتراضي من التصميم */
  hero_subtitle_color: "",
  /** لون خلفية/طبقة الهيرو ولون نص زر الهيرو — فارغ = يتبع لون التمييز العام للصفحة (primary_color) */
  hero_primary_color: "",
  /**
   * لون تمييز شريط تجار الجملة (كلمة «تاجر جملة» + زر الدخول) — فارغ = الأصفر الافتراضي #fbbf24
   */
  wholesale_strip_accent_color: "",
  /** لون زر «دخول» في نافذة تاجر الجملة — فارغ = يتبع لون الشريط */
  wholesale_auth_button_color: "",
  /** لون حدود حقول الدخول (تركيز/إطار) في نافذة تاجر الجملة — فارغ = يتبع لون الزر */
  wholesale_auth_field_color: "",
  /** نص تحت شعار شاشة تحميل الكتالوج — فارغ = بدون نص */
  loader_text: "",
  /** لون نص/شريط التحميل — فارغ = لم يُختر (لا شريط؛ النص إن وُجد بلون افتراضي) */
  loader_text_color: "",
  /** موضع صورة الهيرو (0–100% لكل محور) */
  hero_bg_position_x: 50,
  hero_bg_position_y: 50,
  /** standard = 66vh | compact | tall | full | custom */
  hero_height_mode: "",
  /** عند hero_height_mode = custom — ارتفاع بالبكسل (280–1200) */
  hero_height_px: "",
  /** محاذاة نص الهيرو على الشاشات الكبيرة: start | center */
  hero_content_align: "",
  /** default = صورة خلفية | split_circle = نص + صورة دائرية (منيو مطعم) */
  hero_layout: "",
  /** صورة الدائرة في هيرو split — فارغ = نفس heroUrl */
  hero_circle_image_url: "",
  /**
   * صورة هيرو اختيارية للجوال/الشاشات الصغيرة — فارغ = استخدام صورة الهيرو العادية
   */
  hero_mobile_url: "",
  /** cover = قص لملء الإطار | contain = إظهار الصورة كاملة بدون قص */
  hero_mobile_fit: "contain",
  /** موضع بؤرة صورة هيرو الجوال (عند fit = cover) */
  hero_mobile_bg_position_x: 50,
  hero_mobile_bg_position_y: 50,
  /** صورة خلفية اختيارية للفوتر بالكامل (المحتوى فوقها) */
  footer_image_url: "",
  /** شفافية طبقة تعتيم فوق صورة الفوتر (0–1) لقراءة النص */
  footer_image_overlay: 0.45,
  /** أرقام هواتف إضافية في الفوتر (بعد رقم الهاتف الأساسي) */
  footer_extra_phones: [],
  footer_fax: "",
  footer_show_email: true,
  footer_tiktok: "",
  /** حقول مخصصة في الفوتر: [{ id, label, value }] مثل «رقم مجاني» أو «الرقم المختصر» */
  footer_custom_fields: [],
  /** نص زر الهيرو — فارغ = الافتراضي */
  hero_cta_label: "",
  /** ظهور زر الهيرو: "" = تلقائي (مع وجود عنوان/وصف) | always | hidden */
  hero_cta_visibility: "",
  /** إخفاء زر الهيرو على الجوال فقط */
  hero_cta_hide_mobile: false,
  /** ألوان زر الهيرو — فارغ = الافتراضي من التصميم */
  hero_cta_bg_color: "",
  hero_cta_text_color: "",
  hero_cta_border_color: "",
  /** حجم زر الهيرو: "" (متوسط) | sm | lg | xl */
  hero_cta_size: "",
  /** زوايا زر الهيرو بالبكسل؛ null = افتراضي */
  hero_cta_radius: null,
  hero_cta_shadow: false,
  /** "" = تحت النص | custom = موضع حر فوق الصورة (hero_cta_pos_x / hero_cta_pos_y بالنسبة المئوية) */
  hero_cta_position: "",
  /** محاذاة الزر تحت النص: "" | right | center | left */
  hero_cta_align: "",
  hero_cta_pos_x: 50,
  hero_cta_pos_y: 75,
  /** موضع الزر الحر على الجوال/التابلت — فارغ = نفس موضع اللابتوب */
  hero_cta_mobile_pos_x: "",
  hero_cta_mobile_pos_y: "",
  /** تصغير الزر الحر على الجوال (0.4–1.2) */
  hero_cta_mobile_scale: 0.72,
  /** ثيم داكن: خلفيات الأقسام = page_bg، بطاقات داكنة */
  catalog_dark_surface: false,
  /** لون نص الأقسام في الثيم الداكن */
  catalog_section_text_color: "#e2e8f0",
  /** هيدر ثيم «متجر افتراضي» (الشريط فوق الهيرو) */
  minimal_topbar_bg: "#ffffff",
  minimal_topbar_store_name_color: "#111827",
  minimal_topbar_login_label: "",
  minimal_topbar_register_label: "",
  minimal_topbar_login_bg: "#111827",
  minimal_topbar_login_text: "#ffffff",
  minimal_topbar_login_border: "#111827",
  minimal_topbar_register_bg: "#111827",
  minimal_topbar_register_text: "#ffffff",
  minimal_topbar_register_border: "#111827",
  /** icon = أيقونة مستخدم | buttons = زرّا تسجيل الدخول وإنشاء حساب */
  minimal_topbar_auth_display: "buttons",
  products_underline_color: "",
  /** grid | list (منيو) | gallery (bento: كبير + اثنان بجانبه) */
  products_main_layout: "grid",
  /** شكل صورة المنتج في الشبكة: دائرة | مربع | مستطيل طولي (portrait 3:4) */
  product_image_shape: "circle",
  /** سلة: topbar = مع الهيدر/الشريط | left / right = زر عائم */
  cart_placement: "topbar",
  /** زوايا بطاقة المنتج بالبكسل؛ null/undefined = تلقائي حسب الثيم ونمط البطاقة */
  products_card_border_radius: null,
  /** لون خلفية بطاقة المنتج — فارغ = أبيض */
  products_card_bg_color: "",
  /** حد أيقونة السلة على بطاقة المنتج */
  products_cart_icon_border_width: 0,
  products_cart_icon_border_color: "",
  /** موقع أيقونة السلة على بطاقة المنتج: right | left (اتجاه فيزيائي) */
  products_cart_icon_side: "right",
  /** عرض قسم المنتجات: فارغ = يتبع المظهر العام | boxed | wide | full */
  products_section_container_width: "",
  /** زوايا صورة المنتج (مربع) في ثيم المطعم — null = افتراضي CSS (~18px) */
  restaurant_square_image_radius_px: null,
  /** لون زر السلة العائمة (فارغ = تدرّج من لون الثيم) */
  floating_cart_button_bg: "",
  floating_cart_icon_color: "",
  /** خط عرض الكتالوج: system | cairo | tajawal | ibm_plex_arabic | almarai | noto_kufi */
  catalog_font_stack: "tajawal",
  /** ثيم أزياء احترافي (URBANICO) — يفعّل أنماط الأقسام المحسّنة */
  fashion_store: false,
  /** زوايا حادة بدون border-radius (متجر مخصص / أزياء) */
  fashion_sharp_corners: false,
  /** متجر إلكتروني احترافي — بطاقات منتجات، فئات، فلاتر محسّنة */
  online_store_pro: false,
  products_show_discount_badge: false,
  products_show_rating: false,
  /** شريط تنقل علوي: روابط سكرول لأقسام الصفحة */
  navbar_enabled: false,
  /** عدد الروابط الظاهرة قبل «المزيد» على الديسكتوب */
  navbar_visible_count: 4,
  navbar_link_color: "#111827",
  navbar_link_hover_color: "#b45309",
  navbar_more_label: "",
  /** لون زر «المزيد» — فارغ = نفس لون الروابط */
  navbar_more_color: "",
  navbar_more_hover_color: "",
  /** [{ id, label, target, section_id?, url?, color?, hover_color? }] target: hero|products|footer|section|url */
  navbar_links: [],
};

function parseCatalogNavbarLinks(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((l, i) => ({
    id: String(l?.id || `nav_${i}`).trim() || `nav_${i}`,
    // لا نعمل trim على النص أثناء التحرير حتى يعمل مفتاح المسافة
    label: String(l?.label ?? ""),
    target: String(l?.target || "products").trim() || "products",
    section_id: String(l?.section_id || "").trim(),
    url: String(l?.url || "").trim(),
    color: String(l?.color || "").trim(),
    hover_color: String(l?.hover_color || "").trim(),
    open_in_new_tab: Boolean(l?.open_in_new_tab),
  }));
}

function normalizeCatalogNavbarLinks(raw) {
  return parseCatalogNavbarLinks(raw)
    .map((l) => ({ ...l, label: String(l.label || "").trim() }))
    .filter((l) => l.label);
}

function newCatalogNavbarLinkId() {
  return `nav_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** اسم واضح لسكشن البيلدر في قوائم الوجهة (عنوان مخصص أو وصف النوع/القالب) */
function getCatalogSectionNavLabel(sec, tStudio, lang = "ar") {
  const en = lang === "en";
  const title = String(sec?.settings?.title || "").trim();
  const subtitle = String(sec?.settings?.subtitle || "").trim();
  const type = String(sec?.type || "").trim();
  const variant = String(
    sec?.settings?.section_variant || sec?.settings?.presetId || ""
  )
    .trim()
    .toLowerCase();

  const variantLabel = {
    categories: en ? "Shop by category" : "تسوق حسب الفئة",
    category_circles: en ? "Circle categories" : "فئات دائرية",
    trust: en ? "Trust cards" : "بطاقات ثقة",
    deals_row: en ? "Daily deals" : "عروض اليوم",
    stats: en ? "Store stats" : "إحصائيات المتجر",
  }[variant];

  const typeLabel = {
    video: en ? "Video section" : "سكشن الفيديو",
    text: tStudio?.sectionLabelText || (en ? "Text section" : "سكشن نص"),
    text_image_split: tStudio?.sectionLabelSplit || (en ? "Text + image" : "نص + صورة"),
    image_banner: tStudio?.sectionLabelImageBanner || (en ? "Image banner" : "بانر صورة"),
    features_grid: tStudio?.sectionLabelFeatures || (en ? "Features grid" : "شبكة مميزات"),
    featured_products: tStudio?.sectionLabelFeatured || (en ? "Featured products" : "منتجات مختارة"),
    carousel: tStudio?.sectionLabelCarousel || (en ? "Carousel" : "كاروسيل"),
    products_carousel:
      tStudio?.sectionLabelProductsCarousel || (en ? "Products carousel" : "كاروسيل منتجات"),
    custom_order: en ? "Custom order section" : "سكشن طلب مخصص",
    bundle_offers: en ? "Offers section" : "سكشن العروض",
    info_cards: en ? "Info cards" : "بطاقات تعريفية",
  }[type];

  const fallback =
    variantLabel || typeLabel || tStudio?.sectionFallback || (en ? "Section" : "سكشن");

  if (title) {
    if (subtitle && subtitle !== title) return `${title} — ${subtitle}`;
    return title;
  }
  return fallback;
}

/** True when `el` is an actual overflow scrollport (not just a layout wrapper). */
function isElementScrollport(el) {
  if (!el || el === document.body || el === document.documentElement) return false;
  try {
    const style = window.getComputedStyle(el);
    const oy = style.overflowY;
    const ox = style.overflowX;
    const yScrollable =
      (oy === "auto" || oy === "scroll" || oy === "overlay") &&
      el.scrollHeight > el.clientHeight + 2;
    const xScrollable =
      (ox === "auto" || ox === "scroll" || ox === "overlay") &&
      el.scrollWidth > el.clientWidth + 2;
    return yScrollable || xScrollable;
  } catch {
    return false;
  }
}

/** Nearest scrollable ancestor, or null → use the window. */
function getCatalogScrollParent(el) {
  if (!el || typeof el.parentElement === "undefined") return null;
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    if (isElementScrollport(node)) return node;
    node = node.parentElement;
  }
  return null;
}

function scrollToCatalogNavTarget(link) {
  if (!link || typeof document === "undefined") return;
  const target = String(link.target || "").trim();
  if (target === "url") {
    const url = String(link.url || "").trim();
    if (!url) return;
    if (link.open_in_new_tab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.assign(url);
    }
    return;
  }
  const resolveEl = () => {
    if (target === "hero") {
      return document.getElementById("catalog-hero");
    }
    if (target === "products") {
      return (
        document.getElementById("catalog-products-anchor") ||
        document.getElementById("products") ||
        document.querySelector(".products-section-flat") ||
        document.getElementById("categories-section") ||
        document.getElementById("categories-section-sidebar")
      );
    }
    if (target === "footer") {
      return document.getElementById("catalog-footer");
    }
    if (target === "section") {
      const sid = String(link.section_id || "").trim();
      if (sid) {
        const byId =
          document.getElementById(`builder-sec-${sid}`) ||
          document.getElementById(sid);
        if (byId) return byId;
      }
      // Legacy / misconfigured «فئاتنا»: scroll to shop-by-category or products
      return (
        document.querySelector(".catalog-section--categories") ||
        document.getElementById("catalog-products-anchor") ||
        document.getElementById("products") ||
        document.querySelector(".products-section-flat")
      );
    }
    if (link.section_id) {
      return document.getElementById(`builder-sec-${link.section_id}`);
    }
    // Unknown target → products as safe default
    return (
      document.getElementById("catalog-products-anchor") ||
      document.getElementById("products") ||
      document.querySelector(".products-section-flat")
    );
  };
  const run = () => {
    const el = resolveEl();
    if (!el) return false;
    scrollCatalogElementIntoView(el, {
      stickyOffsetPx: 96,
      extraDownPx: target === "products" ? 64 : 24,
    });
    return true;
  };
  run();
  window.setTimeout(run, 50);
  window.setTimeout(run, 200);
  window.setTimeout(run, 450);
  window.setTimeout(run, 900);
}

/** Smooth scroll that works in studio canvas + live page; extraDownPx scrolls a bit further into the target. */
function scrollCatalogElementIntoView(el, { stickyOffsetPx = 72, extraDownPx = 56 } = {}) {
  if (!el || typeof el.getBoundingClientRect !== "function") return;
  const rect = el.getBoundingClientRect();
  const scrollParent = getCatalogScrollParent(el);

  if (scrollParent) {
    const rootRect = scrollParent.getBoundingClientRect();
    const nextTop =
      scrollParent.scrollTop + (rect.top - rootRect.top) - stickyOffsetPx + extraDownPx;
    if (typeof scrollParent.scrollTo === "function") {
      scrollParent.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
    } else {
      scrollParent.scrollTop = Math.max(0, nextTop);
    }
    return;
  }

  // Public storefront: page scrolls on the window — NOT on .catalog-studio-storefront-canvas__inner
  // (that wrapper exists on public pages but is not a scrollport).
  const y = (window.scrollY || window.pageYOffset || 0) + rect.top - stickyOffsetPx + extraDownPx;
  if (typeof window.scrollTo === "function") {
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  } else if (typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

const BUILDER_SECTION_VARIANTS = {
  features_grid: [
    { value: "", label: "شبكة عامة" },
    { value: "trust", label: "ثقة (أيقونات)" },
    { value: "categories", label: "فئات (صور)" },
    { value: "category_circles", label: "فئات دائرية" },
    { value: "deals_row", label: "عروض اليوم" },
    { value: "stats", label: "إحصائيات" },
  ],
  image_banner: [
    { value: "", label: "بانر صورة" },
    { value: "promo", label: "بانر عرض (نص فوق الصورة)" },
    { value: "countdown", label: "عرض + عدّاد تنازلي" },
  ],
  carousel: [
    { value: "", label: "سلايدر" },
    { value: "testimonials", label: "آراء العملاء" },
    { value: "brands", label: "شعارات + نص (غير محدود)" },
  ],
  text: [
    { value: "", label: "نص" },
    { value: "faq", label: "أسئلة شائعة" },
  ],
  text_image_split: [
    { value: "", label: "نص + صورة" },
    { value: "brand_story", label: "قصة العلامة" },
    { value: "about_cards", label: "من نحن + بطاقتان" },
  ],
};

function hexToRgba(hex, alpha = 1) {
  let h = String(hex || "").trim();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(h)) return `rgba(15,23,42,${alpha})`;
  if (h.length === 4) {
    const c = h.slice(1);
    h = `#${c[0]}${c[0]}${c[1]}${c[1]}${c[2]}${c[2]}`;
  }
  const n = parseInt(h.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function resolveCountdownTheme(st, fallbackAccent = "#111827", { onDark = false } = {}) {
  const accent =
    String(st?.countdown_accent || "").trim() ||
    String(st?.style?.textColor || "").trim() ||
    fallbackAccent;
  const labelColor = String(st?.countdown_label_color || "").trim() || undefined;
  const cellBg =
    String(st?.countdown_cell_bg || "").trim() ||
    (onDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.06)");
  const cellBorder =
    String(st?.countdown_cell_border || "").trim() ||
    (onDark ? "rgba(255,255,255,0.2)" : "rgba(15,23,42,0.1)");
  return { accent, labelColor, cellBg, cellBorder };
}


const BUILDER_PRODUCTS_LINK = "#products";

function resolveBuilderCtaUrl(url) {
  const u = String(url ?? "").trim();
  if (!u || u === "#" || u.toLowerCase() === "javascript:void(0)") return BUILDER_PRODUCTS_LINK;
  return u;
}

/** روابط خارجية (فيسبوك، واتساب…) — لا تُلحق بنطاق المتجر */
function resolveExternalUrl(url) {
  const u = String(url ?? "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u) || u.startsWith("mailto:") || u.startsWith("tel:")) return u;
  if (u.startsWith("#")) return resolveBuilderCtaUrl(u);
  return `https://${u.replace(/^\/+/, "")}`;
}

function proSectionHeadWrapStyle(ha, mb = 16) {
  return { textAlign: ha, marginBottom: mb };
}

function proSectionTitleStyle(st) {
  const k = (st?.title_size || "md").toString();
  const map = { sm: 18, md: 22, lg: 28, xl: 36 };
  return { fontSize: map[k] ?? 22, fontWeight: 950, margin: "0 0 6px" };
}

function proSectionSubtitleStyle(st) {
  const k = (st?.subtitle_size || "md").toString();
  const map = { sm: 12, md: 15, lg: 18 };
  return { fontSize: map[k] ?? 15, margin: 0, opacity: 0.72 };
}

function cardsRowJustify(align) {
  const a = String(align || "start").toLowerCase();
  if (a === "center") return "center";
  if (a === "end") return "flex-end";
  return "flex-start";
}

function circleImageSizePx(sizeKey) {
  const map = { sm: 72, md: 88, lg: 104, xl: 120, "2xl": 140, "3xl": 168, "4xl": 200 };
  return map[String(sizeKey || "md").toLowerCase()] ?? 88;
}

/** نسبة مستطيل المنتج الطولي: عرض:طول = 3:4 (ثابتة عند التكبير/التصغير) */
const PRODUCT_IMAGE_PORTRAIT_RATIO_W = 3;
const PRODUCT_IMAGE_PORTRAIT_RATIO_H = 4;

function normalizeProductImageShape(raw) {
  const s = String(raw || "circle").toLowerCase();
  if (s === "square" || s === "portrait" || s === "rectangle") {
    return s === "rectangle" ? "portrait" : s;
  }
  return "circle";
}

/** أبعاد العرض من السلايدر — الدائرة 1:1 | المربع/المستطيل الطولي 3:4 (عرض:طول) ثابتة */
function productImageDisplayDims(sizePx, shape) {
  const width = Math.max(84, Math.min(360, Math.round(Number(sizePx) || 240)));
  const s = normalizeProductImageShape(shape);
  if (s === "portrait" || s === "square") {
    return {
      width,
      height: Math.round((width * PRODUCT_IMAGE_PORTRAIT_RATIO_H) / PRODUCT_IMAGE_PORTRAIT_RATIO_W),
    };
  }
  return { width, height: width };
}

function isProGridVariant(v) {
  return ["stats", "deals_row", "category_circles", "categories", "trust"].includes(String(v || "").toLowerCase());
}

function storeCategoryNamesFromList(list) {
  return (Array.isArray(list) ? list : [])
    .map((name) => String(name || "").trim())
    .filter((name) => name && name.toLowerCase() !== "all");
}

function resolveCategoryPicRows(st, storeCategories) {
  const saved = [
    ...(Array.isArray(st?.category_items) ? st.category_items : []),
    ...(Array.isArray(st?.items) ? st.items : []),
  ];
  const savedByName = new Map();
  saved.forEach((it) => {
    const key = String(it?.slug || it?.name || it?.title || "").trim();
    if (key && !savedByName.has(key)) savedByName.set(key, it);
  });
  const names = storeCategoryNamesFromList(storeCategories);
  if (!names.length) {
    return Array.isArray(st?.category_items) && st.category_items.length ? st.category_items : Array.isArray(st?.items) ? st.items : [];
  }
  return names.map((name) => {
    const existing = savedByName.get(name);
    return existing
      ? { ...existing, slug: name, name, title: existing.title || name }
      : { slug: name, name, title: name, image_url: "" };
  });
}

function brandLogoSizePx(st) {
  const map = { sm: 48, md: 64, lg: 80, xl: 104, "2xl": 128, "3xl": 160 };
  const key = String(st?.brand_logo_size || "md").toLowerCase();
  return map[key] ?? 64;
}

function CatalogProCountdown({ endAt, accent = "#111827", labelColor, cellBg, cellBorder, compact = false }) {
  const target = useMemo(() => {
    const raw = String(endAt || "").trim();
    if (raw) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) return d;
    }
    const t = new Date();
    t.setDate(t.getDate() + 7);
    return t;
  }, [endAt]);
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, target.getTime() - Date.now());
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const labels = ["أيام", "ساعات", "دقائق", "ثوانٍ"];
  const vals = [left.d, left.h, left.m, left.s];
  const cdStyle = {
    "--countdown-accent": accent,
    "--countdown-cell-bg": cellBg || (compact ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.12)"),
    "--countdown-cell-border": cellBorder || (compact ? "rgba(15,23,42,0.1)" : "rgba(255,255,255,0.2)"),
  };
  if (labelColor) cdStyle["--countdown-label-color"] = labelColor;
  return (
    <div
      className={`catalog-pro-countdown${compact ? " catalog-pro-countdown--compact" : ""}`}
      style={cdStyle}
      aria-live="polite"
    >
      {vals.map((v, i) => (
        <div key={labels[i]} className="catalog-pro-countdown__cell">
          <span className="catalog-pro-countdown__val">{String(v).padStart(2, "0")}</span>
          <span className="catalog-pro-countdown__lbl">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

const TRUST_SECTION_ICONS = {
  truck: Truck,
  shield: ShieldCheck,
  delivery: RotateCcw,
  return: RotateCcw,
  support: Headphones,
};

/** صور جاهزة لقوالب الاستوديو (Unsplash / Magnific — روابط ثابتة) */
const CATALOG_TEMPLATE_MEDIA = {
  heroStreetFashion:
    "https://img.magnific.com/free-photo/beautiful-amazing-brunette-woman-with-long-wavy-hairstyle-spring-fall-stylish-urban-outfit-walking-street-red-lips-slim-body-street-fashion-concept_273443-1169.jpg?semt=ais_hybrid&w=740&q=80",
  heroFashion: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1920&q=80",
  heroUrbanico: "https://images.pexels.com/photos/23496709/pexels-photo-23496709.jpeg",
  heroRetail: "https://images.unsplash.com/photo-1607082348824-0a96f2a48b44?auto=format&fit=crop&w=1600&q=80",
  cardHoodie: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?auto=format&fit=crop&w=900&q=80",
  cardTshirt: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=900&q=80",
  cardPants: "https://images.unsplash.com/photo-1473966968600-fa801b869a1a?auto=format&fit=crop&w=900&q=80",
  cardJacket: "https://images.unsplash.com/photo-1544022613-e87ca75a784a?auto=format&fit=crop&w=900&q=80",
  bannerSale: "https://images.unsplash.com/photo-1441984904996-e0b6a0e4e984?auto=format&fit=crop&w=1600&q=80",
  splitFashion: "https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=1400&q=80",
  heroTech: "https://images.unsplash.com/photo-1498049794561-7780e7231661?auto=format&fit=crop&w=1600&q=80",
  heroDark: "https://images.pexels.com/photos/4881655/pexels-photo-4881655.jpeg",
  slideRetailA: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=1200&q=80",
  slideRetailB: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=1200&q=80",
  slideRetailC: "https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?auto=format&fit=crop&w=1200&q=80",
  slideDarkA: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80",
  slideDarkB: "https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=1200&q=80",
  slideDarkC: "https://images.unsplash.com/photo-1608231387042-66d1773070a5?auto=format&fit=crop&w=1200&q=80",
  slideTechA: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&w=1200&q=80",
  slideTechB: "https://images.unsplash.com/photo-1583394838336-acd977736f90?auto=format&fit=crop&w=1200&q=80",
  slideTechC: "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?auto=format&fit=crop&w=1200&q=80",
  cardFashion: "https://images.unsplash.com/photo-1434389677669-e08b4cac310f?auto=format&fit=crop&w=900&q=80",
  cardBag: "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?auto=format&fit=crop&w=900&q=80",
  cardShoes: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=900&q=80",
  splitTech: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1400&q=80",
};

/** Preset content when adding a section from the picker (merged with defaultSettingsFor). */
const SECTION_PRESET_PATCHES = {
  trust: {
    section_variant: "trust",
    title: "",
    cards_count: 4,
    card_height: "sm",
    items: [
      { title: "شحن مجاني", details: "للطلبات فوق ٧٥$", icon: "truck", image_url: "" },
      { title: "دفع آمن", details: "دفع مشفّر ١٠٠٪", icon: "shield", image_url: "" },
      { title: "توصيل سريع", details: "٢–٥ أيام عمل", icon: "delivery", image_url: "" },
      { title: "دعم ٢٤/٧", details: "فريق دعم مخصّص", icon: "support", image_url: "" },
    ],
  },
  categories: {
    section_variant: "categories",
    category_items: [],
    title: "تسوّق حسب الفئة",
    cards_count: 4,
    card_height: "md",
    card_image_mode: "cover",
    show_category_label: true,
    items: [
      { title: "رجالي", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardHoodie, cta_url: "#products" },
      { title: "نسائي", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardTshirt, cta_url: "#products" },
      { title: "أطفال", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardPants, cta_url: "#products" },
      { title: "إكسسوارات", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardJacket, cta_url: "#products" },
    ],
  },
  category_circles: {
    section_variant: "category_circles",
    category_items: [],
    title: "تسوّق من أهم الفئات",
    subtitle: "اختر الفئة وابدأ التسوّق",
    cards_count: 6,
    card_height: "sm",
    items: [
      { title: "هواتف", image_url: CATALOG_TEMPLATE_MEDIA.slideRetailA },
      { title: "تجميل", image_url: CATALOG_TEMPLATE_MEDIA.cardBag },
      { title: "إلكترونيات", image_url: CATALOG_TEMPLATE_MEDIA.heroTech },
      { title: "أثاث", image_url: CATALOG_TEMPLATE_MEDIA.heroRetail },
      { title: "ساعات", image_url: CATALOG_TEMPLATE_MEDIA.slideRetailB },
      { title: "ديكور", image_url: CATALOG_TEMPLATE_MEDIA.splitFashion },
    ],
  },
  deals_row: {
    section_variant: "deals_row",
    product_ids: [],
    title: "عروض اليوم",
    subtitle: "لفترة محدودة",
    cards_count: 4,
    items: [
      { title: "-٢٥٪", subtitle: "سماعات لاسلكية", details: "₪٩٩", image_url: CATALOG_TEMPLATE_MEDIA.slideRetailB },
      { title: "-٣٠٪", subtitle: "ساعة ذكية", details: "₪١٤٩", image_url: CATALOG_TEMPLATE_MEDIA.slideRetailA },
      { title: "-٢٠٪", subtitle: "كاميرا", details: "₪٢٩٩", image_url: CATALOG_TEMPLATE_MEDIA.slideRetailC },
      { title: "-٤٠٪", subtitle: "إكسسوارات", details: "₪٤٩", image_url: CATALOG_TEMPLATE_MEDIA.heroTech },
    ],
  },
  stats: {
    section_variant: "stats",
    cards_count: 3,
    style: { backgroundColor: "#111827", textColor: "#f9fafb" },
    items: [
      { title: "+١٠ آلاف", details: "عميل سعيد" },
      { title: "+٥٠٠", details: "منتج متوفر" },
      { title: "٤٫٩", subtitle: "★★★★★", details: "تقييم المتجر" },
    ],
  },
  promo: {
    section_variant: "promo",
    heading_align: "center",
    title: "عرض الموسم — خصم حتى ٣٠٪",
    subtitle: "عروض محدودة • وصل حديثاً",
    promo_cta_label: "تسوّق العرض",
    promo_cta_url: "#products",
    promo_cta_fg: "#111827",
    promo_cta_bg: "#ffffff",
    images: [{ url: CATALOG_TEMPLATE_MEDIA.bannerSale }],
    section_height: "lg",
    container_width: "full",
  },
  dual_promo: {
    section_variant: "dual_promo",
    promo_slots: [
      { title: "خصم ٢٠٪", subtitle: "مكبرات صوت", cta: "تسوّق الآن", url: "#products", image: CATALOG_TEMPLATE_MEDIA.slideRetailB },
      { title: "عرض ساخن", subtitle: "لفترة محدودة", cta: "اكتشف العرض", url: "#products", image: CATALOG_TEMPLATE_MEDIA.heroTech },
    ],
  },
  countdown: {
    section_variant: "countdown",
    heading_align: "center",
    title: "عرض ساخن",
    subtitle: "ينتهي قريباً — لا تتأخر",
    promo_cta_label: "تسوّق العرض",
    promo_cta_url: "#products",
    promo_cta_fg: "#111827",
    promo_cta_bg: "#ffffff",
    images: [{ url: CATALOG_TEMPLATE_MEDIA.bannerSale }],
    section_height: "lg",
    container_width: "full",
  },
  testimonials: {
    section_variant: "testimonials",
    title: "آراء عملائنا",
    subtitle: "تجارب حقيقية",
    slides: [
      { title: "سارة م.", subtitle: "★★★★★", body: "«جودة ممتازة وتجربة شراء سلسة.»" },
      { title: "أحمد ك.", subtitle: "★★★★★", body: "«شحن سريع وتغليف راقٍ.»" },
      { title: "ليلى ر.", subtitle: "★★★★★", body: "«أنصح به بشدة.»" },
    ],
  },
  brands: {
    section_variant: "brands",
    title: "أفضل العلامات",
    slides: [
      { image: CATALOG_TEMPLATE_MEDIA.slideRetailA, title: "Samsung" },
      { image: CATALOG_TEMPLATE_MEDIA.slideRetailB, title: "Apple" },
      { image: CATALOG_TEMPLATE_MEDIA.slideRetailC, title: "Sony" },
      { image: CATALOG_TEMPLATE_MEDIA.heroTech, title: "HP" },
    ],
  },
  faq: {
    section_variant: "faq",
    title: "الأسئلة الشائعة",
    subtitle: "كل ما تحتاج معرفته",
    faq_items: [
      { q: "الشحن والتوصيل", a: "نوصّل خلال ٢–٥ أيام عمل." },
      { q: "الإرجاع", a: "إرجاع خلال ٣٠ يوماً." },
      { q: "الدفع", a: "بطاقات ومحافظ رقمية." },
    ],
  },
  blog: {
    section_variant: "blog",
    title: "مدونة المتجر",
    subtitle: "نصائح وإلهام",
    blog_posts: [
      { title: "أفضل منتجات الموسم", subtitle: "١٢ مايو ٢٠٢٦", image_url: CATALOG_TEMPLATE_MEDIA.cardHoodie, link_url: "#" },
      { title: "كيف تختار المقاس", subtitle: "٣ مايو ٢٠٢٦", image_url: CATALOG_TEMPLATE_MEDIA.cardTshirt, link_url: "#" },
      { title: "دليل العناية", subtitle: "٢٨ أبريل ٢٠٢٦", image_url: CATALOG_TEMPLATE_MEDIA.cardJacket, link_url: "#" },
    ],
  },
  flash_sale: {
    section_variant: "flash_sale",
    title: "عروض فلاش",
    subtitle: "تنتهي قريباً",
  },
  brand_story: {
    section_variant: "brand_story",
    title: "قصة العلامة",
    subtitle: "لماذا نحن موجودون",
    body: "• جودة عالية\n• تصاميم عصرية\n• خدمة مميزة",
    images: [{ url: CATALOG_TEMPLATE_MEDIA.splitFashion }],
    image_side: "left",
  },
  newsletter: {
    section_variant: "newsletter",
    title: "انضم إلى نشرتنا",
    subtitle: "خصم ١٠٪ على طلبك الأول",
    body: "اشترك لتصلك العروض الحصرية.",
    newsletter_placeholder: "بريدك الإلكتروني",
    newsletter_button: "اشترك",
    style: { backgroundColor: "#111827", textColor: "#f9fafb" },
  },
  about_cards: {
    section_variant: "about_cards",
    title: "من نحن",
    images: [{ url: CATALOG_TEMPLATE_MEDIA.splitFashion }],
    image_side: "right",
    about_cards: [
      { title: "اكتشف أحدث المنتجات", body: "تشكيلة مختارة بعناية.", cta: "تسوّق الآن", url: "#products" },
      { title: "جودة وثقة", body: "نلتزم بأعلى المعايير.", cta: "اعرف المزيد", url: "#products" },
    ],
  },
  featured: {
    title: "منتجات مميزة",
    subtitle: "عرض الكل ←",
    columns: 4,
    max_cards: 8,
  },
  products_carousel: {
    title: "الأكثر مبيعاً",
    subtitle: "منتجات رائجة",
    products_per_slide: 0,
  },
};


const CATALOG_STUDIO_THEME_KEYS = new Set([
  "minimal",
  "showcase",
  "restaurant",
  "general_store",
  "dark_store",
  "electronics_store",
  "urbanico_modern_fashion",
  "custom_store_fashion",
]);

const CATALOG_THEME_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isCatalogThemeCatalogId(v) {
  return CATALOG_THEME_UUID_RE.test(String(v || "").trim());
}

function catalogPageThemeFromKatalojiRow(theme) {
  const raw = theme?.catalog_page_theme || theme?.theme_key || "minimal";
  return String(raw).toLowerCase() || "minimal";
}

function catalogFontFamilyFromStackKey(key) {
  const k = String(key || "system").toLowerCase();
  const stacks = {
    system: "",
    // "* Fallback" = size/metric-matched local font declared in public/index.html (keeps CLS at 0 while the web font swaps in)
    cairo: '"Cairo", "Cairo Fallback", "Segoe UI", Tahoma, sans-serif',
    tajawal: '"Tajawal", "Tajawal Fallback", "Segoe UI", Tahoma, sans-serif',
    ibm_plex_arabic: '"IBM Plex Sans Arabic", "Segoe UI", Tahoma, sans-serif',
    almarai: '"Almarai", "Segoe UI", Tahoma, sans-serif',
    noto_kufi: '"Noto Kufi Arabic", "Segoe UI", Tahoma, sans-serif',
  };
  return stacks[k] || "";
}

/** شريط التصنيفات (ثيم مطعم): ألوان — يُعرض في «المنتجات» */
function RestaurantCategoryBarCustomizerBlock({ extras, onPatch, tStudio }) {
  const lx = { ...DEFAULT_CATALOG_LAYOUT_EXTRAS, ...(extras || {}) };
  return (
    <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
      <div style={{ fontWeight: 950, marginBottom: 8 }}>{tStudio.categoryStripTitle}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.categoryStripBg}</div>
          <input
            type="color"
            value={lx.restaurant_category_bar_bg_color || "#faf8f3"}
            onChange={(e) => onPatch({ restaurant_category_bar_bg_color: e.target.value })}
            style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
          />
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.categoryStripBgOpacity}</div>
            <input
              type="range"
              min={0.7}
              max={1}
              step={0.01}
              value={Number(lx.restaurant_category_bar_bg_opacity ?? 0.97)}
              onChange={(e) => onPatch({ restaurant_category_bar_bg_opacity: Number(e.target.value) })}
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.categoryStripBorderColor}</div>
          <input
            type="color"
            value={lx.restaurant_category_bar_border_color || "#2d2416"}
            onChange={(e) => onPatch({ restaurant_category_bar_border_color: e.target.value })}
            style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
          />
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.categoryStripBorderOpacity}</div>
            <input
              type="range"
              min={0}
              max={0.4}
              step={0.01}
              value={Number(lx.restaurant_category_bar_border_opacity ?? 0.1)}
              onChange={(e) => onPatch({ restaurant_category_bar_border_opacity: Number(e.target.value) })}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function layoutExtrasBool(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v === null || v === undefined) return false;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes";
}

function hexToRgb(hex) {
  const h = String(hex || "").trim().replace(/^#/, "");
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return Number.isFinite(r) ? { r, g, b } : null;
  }
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return Number.isFinite(r) ? { r, g, b } : null;
}

function isDarkHexColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return lum < 0.42;
}

function catalogDarkSurfaceActive(extras) {
  if (!extras || typeof extras !== "object") return false;
  if (layoutExtrasBool(extras.catalog_dark_surface)) return true;
  if (layoutExtrasBool(extras.global_page_bg_enabled) && isDarkHexColor(extras.page_bg)) return true;
  return false;
}

const LIGHT_SECTION_BG_DEFAULTS = new Set(["#ffffff", "#fff", "#faf8f3", "#f8fafc", "#f5f5f0", "#fdf2f8", "#f0fdf4", "#fffbeb", "#fefce8"]);

function resolveBuilderSectionColors(st, layoutExtras) {
  const style = st?.style || {};
  const dark = catalogDarkSurfaceActive(layoutExtras);
  const pageBg = String(layoutExtras?.page_bg || "#0b1220").trim() || "#0b1220";
  const defaultFg = String(layoutExtras?.catalog_section_text_color || "#e2e8f0").trim() || "#e2e8f0";
  let bg = style.backgroundColor || "#ffffff";
  let fg = style.textColor || "#0f172a";
  if (dark) {
    const bgNorm = String(bg).trim().toLowerCase();
    if (!style.backgroundColor || LIGHT_SECTION_BG_DEFAULTS.has(bgNorm)) {
      bg = pageBg;
    }
    const fgNorm = String(fg).trim().toLowerCase();
    if (!style.textColor || fgNorm === "#0f172a" || fgNorm === "#111827") {
      fg = defaultFg;
    }
  }
  return { bg, fg };
}

/** متاجر حفظت catalog_layout_extras قبل مفتاح global_page_bg_enabled — نبقي سلوك الخلفية كما كان */
function migrateLegacyLayoutExtras(merged, rawExtras) {
  if (rawExtras && typeof rawExtras === "object" && Object.keys(rawExtras).length > 0) {
    if (!Object.prototype.hasOwnProperty.call(rawExtras, "global_page_bg_enabled")) {
      merged.global_page_bg_enabled = true;
    }
  }
}

/** تمرير أفقي بالإصبع لكاروسيلات الكتالوج (جوال) */
function useHorizontalSwipe(onSwipeLeft, onSwipeRight, enabled = true) {
  const startXRef = useRef(null);
  const startYRef = useRef(null);

  const onTouchStart = useCallback(
    (e) => {
      if (!enabled) return;
      startXRef.current = e.touches[0].clientX;
      startYRef.current = e.touches[0].clientY;
    },
    [enabled]
  );

  const onTouchEnd = useCallback(
    (e) => {
      if (!enabled || startXRef.current == null) return;
      const dx = e.changedTouches[0].clientX - startXRef.current;
      const dy = e.changedTouches[0].clientY - (startYRef.current ?? 0);
      startXRef.current = null;
      startYRef.current = null;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
      if (dx < 0) onSwipeLeft();
      else onSwipeRight();
    },
    [enabled, onSwipeLeft, onSwipeRight]
  );

  return { onTouchStart, onTouchEnd };
}

/** هيرو سلايدر — بدون شريط أزرق؛ نفس إطار الصورة النظيف */
function BuilderHeroCarousel({ urls, slideH, innerR, rtl, frameBorder, dotColor }) {
  const list = Array.isArray(urls) && urls.length ? urls : [""];
  const [idx, setIdx] = useState(0);
  const n = list.length;
  const goPrev = () => setIdx((p) => (p - 1 + n) % n);
  const goNext = () => setIdx((p) => (p + 1) % n);
  const leftAction = rtl ? goPrev : goNext;
  const rightAction = rtl ? goNext : goPrev;
  const slideTx = `-${(idx * 100) / n}%`;

  const dot = dotColor || "#ffc200";

  useEffect(() => {
    if (n <= 1) return undefined;
    const id = setInterval(() => setIdx((p) => (p + 1) % n), 9000);
    return () => clearInterval(id);
  }, [n]);

  return (
    <>
      <div
        className="catalog-carousel-bleed catalog-carousel-bleed--flush"
        style={{
          position: "relative",
          boxSizing: "border-box",
          background: "transparent",
          padding: 0,
        }}
      >
        <div
          className="catalog-carousel-chrome"
          style={{
            borderRadius: innerR,
            overflow: "hidden",
            border: frameBorder || "1px solid rgba(15,23,42,0.08)",
            background: "#fff",
            position: "relative",
            boxShadow: "none",
          }}
        >
          <div style={{ overflow: "hidden", width: "100%", direction: "ltr" }}>
            <div
              className="catalog-carousel-track catalog-carousel-track--hero"
              style={{
                display: "flex",
                width: `${n * 100}%`,
                transform: `translateX(${slideTx})`,
                transition: "transform 0.55s cubic-bezier(0.25, 0.85, 0.35, 1)",
              }}
            >
              {list.map((u, si) => (
                <div
                  key={si}
                  style={{
                    position: "relative",
                    flex: `0 0 ${100 / n}%`,
                    width: `${100 / n}%`,
                    minHeight: slideH,
                    background: "#0f172a",
                  }}
                >
                  {u ? (
                    <img src={u} alt="" className="catalog-hero-carousel-img catalog-carousel-slide-img" width={1600} height={slideH} decoding="async" style={{ width: "100%", height: slideH, objectFit: "cover", display: "block" }} />
                  ) : (
                    <div style={{ height: slideH, display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 13 }}>
                      صورة الهيرو
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {n > 1 ? (
            <>
              <button
                type="button"
                className="catalog-carousel-nav-btn catalog-carousel-nav-btn--start"
                onClick={leftAction}
                aria-label="prev"
              >
                <ChevronLeft size={28} strokeWidth={2.25} aria-hidden />
              </button>
              <button
                type="button"
                className="catalog-carousel-nav-btn catalog-carousel-nav-btn--end"
                onClick={rightAction}
                aria-label="next"
              >
                <ChevronRight size={28} strokeWidth={2.25} aria-hidden />
              </button>
            </>
          ) : null}
        </div>
      </div>
      {n > 1 ? (
        <div
          style={{
            padding: "12px 0 0",
            display: "flex",
            justifyContent: "center",
            gap: 8,
            flexDirection: rtl ? "row-reverse" : "row",
            background: "transparent",
          }}
        >
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`slide ${i + 1}`}
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                border: i === idx ? `2px solid ${dot}` : "2px solid rgba(15,23,42,0.22)",
                background: i === idx ? dot : "transparent",
                cursor: "pointer",
                padding: 0,
              }}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

/* بطاقة منتج واحدة – memo لتقليل إعادة الرسم عند الكتابة في البحث */
const CatalogMenuOnlyContext = createContext(false);
/** إعدادات سعر بطاقة المنتج: color / bg فارغ = افتراضي، position: "" | below_name | on_image */
const CatalogCardPriceContext = createContext({ color: "", position: "", bg: "", categoryPosition: "", categoryColor: "" });

/** مشغّل فيديو السكشن: غلاف + زر تشغيل، بدون خلفية زائدة حول الإطار */
const CatalogSectionVideoPlayer = memo(function CatalogSectionVideoPlayer({
  videoUrl,
  posterUrl,
  videoFit = "cover",
  autoplay = false,
  loop = false,
  muted = false,
  height = 400,
  borderRadius = 28,
  compact = false,
  emptyLabel = "أضف رابط فيديو من لوحة التحكم",
}) {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(Boolean(autoplay));

  useEffect(() => {
    setStarted(Boolean(autoplay));
  }, [autoplay, videoUrl]);

  const startPlayback = useCallback(() => {
    setStarted(true);
    const el = videoRef.current;
    if (!el) return;
    const playPromise = el.play?.();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
  }, []);

  return (
    <div
      className={`catalog-video-section__frame${compact ? " catalog-video-section__frame--compact" : ""}`}
      style={
        compact
          ? {
              borderRadius,
              height: "auto",
              minHeight: 0,
              aspectRatio: "16 / 9",
            }
          : {
              height,
              minHeight: height,
              borderRadius,
            }
      }
    >
      {!videoUrl ? (
        <div className="catalog-video-section__empty">{emptyLabel}</div>
      ) : (
        <>
          <video
            ref={videoRef}
            src={videoUrl}
            poster={posterUrl || undefined}
            controls={started}
            playsInline
            autoPlay={Boolean(autoplay)}
            loop={Boolean(loop)}
            muted={Boolean(muted || autoplay)}
            className="catalog-video-section__media"
            style={{ objectFit: videoFit === "contain" ? "contain" : "cover" }}
            onPlay={() => setStarted(true)}
          />
          {!started ? (
            <button
              type="button"
              className="catalog-video-section__cover"
              onClick={startPlayback}
              aria-label="تشغيل الفيديو"
            >
              {posterUrl ? (
                <img src={posterUrl} alt="" className="catalog-video-section__cover-img" width={1280} height={720} loading="lazy" decoding="async" draggable={false} />
              ) : (
                <span className="catalog-video-section__cover-fallback" aria-hidden />
              )}
              <span className="catalog-video-section__play" aria-hidden>
                <Play size={30} fill="currentColor" strokeWidth={0} />
              </span>
            </button>
          ) : null}
        </>
      )}
    </div>
  );
});

function wholesalePriceInquiryUrl(whatsappNumber, productName) {
  const digits = normalizeWhatsAppDigits(whatsappNumber);
  if (!digits) return "";
  const text = `مرحباً، أود معرفة سعر الجملة للمنتج: ${String(productName || "").trim()}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

function WholesalePriceInquiryButton({ product, whatsappNumber, primaryColor, secondaryColor, block = false }) {
  const href = wholesalePriceInquiryUrl(whatsappNumber, product?.name_ar || product?.name);
  const style = {
    backgroundColor: primaryColor,
    color: secondaryColor,
    ...(block ? { width: "100%" } : null),
  };
  if (!href) {
    return (
      <span className="catalog-btn wholesale-inquiry-btn wholesale-inquiry-btn--static" style={style}>
        تواصل لمعرفة سعر الجملة
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="catalog-btn wholesale-inquiry-btn"
      style={style}
      onClick={(e) => e.stopPropagation()}
    >
      <WhatsAppBrandIcon size={16} />
      تواصل لمعرفة سعر الجملة
    </a>
  );
}

/**
 * The categories carousel only mounts the current page, so images of the other pages used to
 * start downloading only after the user pressed next/prev. This warms the browser cache + decode
 * cache for every page as soon as the browser is idle, so paging shows the images instantly.
 */
function useWarmImages(urls) {
  const keepRef = useRef([]);
  const key = urls.join("|");
  useEffect(() => {
    if (!urls.length || typeof window === "undefined") return undefined;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const imgs = urls.map((u) => {
        const im = new Image();
        im.decoding = "async";
        im.src = u;
        if (typeof im.decode === "function") im.decode().catch(() => {});
        return im;
      });
      keepRef.current = imgs; // keep references so the decoded bitmaps are not dropped
    };
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(run, { timeout: 1500 })
      : window.setTimeout(run, 300);
    return () => {
      cancelled = true;
      if (window.cancelIdleCallback && window.requestIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

const CategoryImagesWarmer = memo(function CategoryImagesWarmer({ urls }) {
  useWarmImages(urls);
  return null;
});

const ProductCard = memo(function ProductCard({
  product,
  primaryColor,
  secondaryColor,
  cardBgColor,
  onSelect,
  index,
  addMode = "icon",
}) {
  const menuOnly = useContext(CatalogMenuOnlyContext);
  const pricePrefs = useContext(CatalogCardPriceContext);
  const priceColor = pricePrefs.color || primaryColor;
  const wholesaleMissing = Boolean(product.wholesale_price_missing);
  const pricePosition = wholesaleMissing ? "" : pricePrefs.position || "";
  const hasPrice = !wholesaleMissing && Number(product.base_price) > 0;
  const hasCompare =
    hasPrice && product.base_compare_at != null && Number(product.base_compare_at) !== Number(product.base_price);
  const priceStack = hasPrice ? (
    <div
      className={`product-card-price-stack${pricePosition ? ` product-card-price-stack--${pricePosition}` : ""}${
        pricePrefs.align && pricePosition !== "on_image" ? ` product-card-price-stack--align-${pricePrefs.align}` : ""
      }`}
      style={
        pricePosition === "on_image"
          ? { backgroundColor: pricePrefs.bg || "rgba(255,255,255,0.94)" }
          : undefined
      }
    >
      {hasCompare && (
        <span className="product-compare-price" style={{ fontSize: '0.95rem', fontWeight: '700', color: '#9ca3af', textDecoration: 'line-through' }}>
          ₪{product.base_compare_at}
        </span>
      )}
      <span
        className={`product-price-nike${pricePrefs.color ? " product-price-nike--custom" : ""}`}
        style={{
          fontSize: '1.1rem',
          fontWeight: '800',
          color: priceColor,
          ...(pricePrefs.color ? { "--product-price-color": pricePrefs.color } : {}),
        }}
      >
        ₪{product.base_price}
      </span>
    </div>
  ) : null;
  const showNewBadge = isProductWithinNewBadgeWindow(product.created_at);
  const showOfferRibbon = Boolean(product.on_sale);
  const outOfStock = productGloballyOutOfStock(product);
  const desc =
    (product.description_ar ||
      product.description_en ||
      product.description ||
      product.descriptionAr ||
      product.descriptionEn ||
      "")
      .toString()
      .trim();
  const openProduct = () => onSelect(product);
  return (
    <div
      className="product-card-nike"
      onClick={openProduct}
      style={{
        animationDelay: `${index * 0.05}s`,
        overflow: 'hidden',
        backgroundColor: cardBgColor || 'var(--products-card-bg, #fff)',
      }}
    >
      <div className="product-card-visual">
        <div className="product-image-wrapper" style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer' }}>
          <img
            src={product.image || (typeof product.displayImage === 'string' ? product.displayImage : product.displayImage?.image_url) || product.images?.[0]?.image_url || ''}
            className="product-image-nike"
            alt={product.name}
            width={600}
            height={600}
            loading={index < 8 ? "eager" : "lazy"}
            fetchPriority={index < 4 ? "high" : "auto"}
            decoding="async"
            style={{ width: '100%', display: 'block' }}
            onLoad={(e) => {
              // Some GPUs skip painting a decoded image until the next repaint: force one.
              const el = e.currentTarget;
              el.style.opacity = "0.999";
              requestAnimationFrame(() => { el.style.opacity = ""; });
            }}
            onError={(e) => {
              const el = e.currentTarget;
              if (el.dataset.retried === "1" || !el.src) return;
              el.dataset.retried = "1";
              const src = el.getAttribute("src") || "";
              setTimeout(() => { el.src = src; }, 800);
            }}
          />
        </div>
        <div className="product-card-badges-layer">
        {showOfferRibbon && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '108px',
              height: '108px',
              overflow: 'hidden',
              zIndex: 4,
              pointerEvents: 'none',
            }}
          >
            <span
              style={{
                position: 'absolute',
                bottom: '22px',
                right: '-48px',
                width: '168px',
                padding: '7px 0',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: '#fff',
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: '900',
                letterSpacing: '0.04em',
                transform: 'rotate(-45deg)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
              }}
            >
              عرض خاص
            </span>
          </div>
        )}
        {showNewBadge && (
          <span
            className="product-card-chip product-card-chip--new"
            style={{ background: primaryColor, color: secondaryColor }}
          >
            أضيف حديثاً
          </span>
        )}
        {outOfStock && (
          <span className="product-card-chip product-card-chip--oos">نفذ من المخزن</span>
        )}
        {product.category && pricePrefs.categoryPosition === "on_image" && (
          <span
            className="product-card-category-badge"
            style={{
              backgroundColor: primaryColor,
              color: secondaryColor,
              padding: '4px 10px',
              fontSize: '10px',
              fontWeight: '700',
              borderRadius: '4px',
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 2
            }}
          >
            {product.category}
          </span>
        )}
        {pricePosition === "on_image" ? priceStack : null}
        </div>
      </div>
      <div className="product-info-nike">
        {product.category && !pricePrefs.categoryPosition ? (
          <span
            className="product-card-category-label"
            style={pricePrefs.categoryColor ? { color: pricePrefs.categoryColor } : undefined}
            title={product.category}
          >
            {product.category}
          </span>
        ) : null}
        <h3
          className="product-name-nike"
          style={{
            fontSize: "1rem",
            fontWeight: "700",
            marginBottom: "6px",
            color: "var(--products-card-name-color, #111)",
          }}
        >
          {product.name}
        </h3>
        {pricePosition === "below_name" ? priceStack : null}
        <p
          className="product-desc-nike"
          style={{
            margin: "0 0 10px",
            fontSize: 13,
            color: "var(--products-card-desc-color, #64748b)",
            lineHeight: 1.6,
          }}
          aria-hidden={!desc}
        >
          {desc || "\u00A0"}
        </p>
        <div className="product-card-meta-row">
          {wholesaleMissing ? (
            <WholesalePriceInquiryButton
              product={product}
              whatsappNumber={pricePrefs.whatsappNumber}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              block
            />
          ) : !menuOnly ? (
          <button
            aria-label={`إضافة ${String(product?.name || "").trim() || "المنتج"} إلى السلة`}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(product);
            }}
            className="catalog-btn add-to-cart-nike product-card-add-btn"
            style={{
              backgroundColor: primaryColor,
              color: secondaryColor,
              width: addMode === "text" ? "auto" : '40px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              opacity: 1,
              padding: addMode === "text" ? "0 14px" : undefined,
              gap: addMode === "text" ? 8 : undefined,
            }}
          >
            {addMode === "text" ? "إضافة" : <ShoppingCart size={16} />}
          </button>
          ) : null}
          {!pricePosition && !wholesaleMissing ? priceStack || <div className="product-card-price-stack" /> : null}
        </div>
      </div>
    </div>
  );
});

export default function Catalog({
  editorMode = false,
  enabledStoreKey = "",
  adminStudioUserId = "",
  menuOnly = false,
  forcedStoreKey = "",
  customDomainMode = false,
}) {
  const navigate = useNavigate();
  const { storeKey: paramStoreKey, productId } = useParams();
  const storeKey = (forcedStoreKey || paramStoreKey || "").trim();
  const userId = (editorMode ? (enabledStoreKey || storeKey) : storeKey) || "";
  const token = readAccessToken();
  const { authHeader, url: studioApiUrl, canEdit, headers: studioHeaders } = useCatalogStudioApi(
    editorMode,
    adminStudioUserId
  );

  // Customizer sidebar state (WordPress-like)
  const [customizerOpen, setCustomizerOpen] = useState(() => Boolean(editorMode));
  const [customizerTab, setCustomizerTab] = useState("hero"); // hero | look | cart | sections | products | footer
  const [customizerMainTab, setCustomizerMainTab] = useState("sections"); // legacy
  const [activeKey, setActiveKey] = useState("hero"); // hero | products | footer | sec:<id>
  const [sectionsMenuOpen, setSectionsMenuOpen] = useState(null); // null | builder | hero | products | footer | themes
  const [customizerPane, setCustomizerPane] = useState("menu"); // menu | form
  const [customizerFormTarget, setCustomizerFormTarget] = useState(null); // { kind: "builder", id } | { kind: "core", key }
  const [dragOrderFrom, setDragOrderFrom] = useState(null);
  const [sectionsManagerOpen, setSectionsManagerOpen] = useState(false);
  const [sectionsManagerZone, setSectionsManagerZone] = useState("before"); // before | after
  const [sectionsInsertIndex, setSectionsInsertIndex] = useState(0);
  const [savingCustomizer, setSavingCustomizer] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveErrorStep, setSaveErrorStep] = useState("");
  const [saveProgressStep, setSaveProgressStep] = useState("");
  const [studioUnsaved, setStudioUnsaved] = useState(false);
  const [studioLoadHintRotating, setStudioLoadHintRotating] = useState("");
  const [studioToast, setStudioToast] = useState(null);
  const [themePresetConfirmOpen, setThemePresetConfirmOpen] = useState(false);
  const [pendingThemePresetKey, setPendingThemePresetKey] = useState(null);
  const [catalogThemes, setCatalogThemes] = useState([]);
  const [catalogThemesLoading, setCatalogThemesLoading] = useState(false);
  const [catalogThemesError, setCatalogThemesError] = useState("");
  const [selectedCatalogThemeId, setSelectedCatalogThemeId] = useState("");
  const catalogThemeDetailCacheRef = useRef(new Map());
  const [localDraftBanner, setLocalDraftBanner] = useState(false);
  const [studioPreviewFrame, setStudioPreviewFrame] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767.98px)").matches
      ? "mobile"
      : "desktop"
  );
  const [studioPreviewZoom, setStudioPreviewZoom] = useState(100);
  const [studioShortcutsOpen, setStudioShortcutsOpen] = useState(false);
  const [customizerMenuSearch, setCustomizerMenuSearch] = useState("");
  /** Elementor-style panel: page blocks vs site-wide settings */
  const [studioPanelLibraryTab, setStudioPanelLibraryTab] = useState("widgets");
  const studioWidgetsHintStorageKey = "catalogStudioWidgetsHintDismissed_v1";
  const [studioWidgetsHintDismissed, setStudioWidgetsHintDismissed] = useState(() => {
    try {
      return localStorage.getItem("catalogStudioWidgetsHintDismissed_v1") === "1";
    } catch {
      return false;
    }
  });
  const dismissStudioWidgetsHint = useCallback(() => {
    setStudioWidgetsHintDismissed(true);
    try {
      localStorage.setItem(studioWidgetsHintStorageKey, "1");
    } catch {
      /* ignore */
    }
  }, [studioWidgetsHintStorageKey]);
  const [studioDocMenuOpen, setStudioDocMenuOpen] = useState(false);
  const studioPanelSearchInputRef = useRef(null);
  const studioDocMenuWrapRef = useRef(null);
  const [saveUiSuccess, setSaveUiSuccess] = useState(false);
  const [studioThemePreview, setStudioThemePreview] = useState(null);
  const [studioLayoutWide, setStudioLayoutWide] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true
  );
  /** جوال/آيباد ≤1023: تخطيط مضغوط (فئات 2×2، إلخ) */
  const [storefrontIsCompact, setStorefrontIsCompact] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 1023.98px)").matches : false
  );
  /** آيباد تقريباً 768–1023 */
  const [storefrontIsTablet, setStorefrontIsTablet] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px) and (max-width: 1023.98px)").matches
      : false
  );
  const [heroImageUploading, setHeroImageUploading] = useState(false);
  const [heroMobileUploading, setHeroMobileUploading] = useState(false);
  const [footerImageUploading, setFooterImageUploading] = useState(false);
  const [builderImageUploadKey, setBuilderImageUploadKey] = useState(null);
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [newProductsOnly, setNewProductsOnly] = useState(false);
  const [priceMaxFilter, setPriceMaxFilter] = useState("");
  const [productFiltersDrawerOpen, setProductFiltersDrawerOpen] = useState(false);
  const [categoryCarouselPages, setCategoryCarouselPages] = useState({});
  const categorySwipeStartRef = useRef(null);
  const [categoryCarouselMotion, setCategoryCarouselMotion] = useState({});
  const [navbarMoreOpen, setNavbarMoreOpen] = useState(false);
  const [navbarMobileOpen, setNavbarMobileOpen] = useState(false);
  const navDrawerSwipeRef = useRef(null);
  const navDrawerPendingLinkRef = useRef(null);

  useEffect(() => {
    setNavbarMoreOpen(false);
    setNavbarMobileOpen(false);
  }, [studioPreviewFrame]);

  // Lock the storefront behind the mobile nav drawer (studio preview drawer is scoped to its canvas).
  useMobileScrollLock(navbarMobileOpen && !canEdit, ".catalog-nav-drawer__links");

  useEffect(() => {
    if (navbarMobileOpen) return;
    const pendingLink = navDrawerPendingLinkRef.current;
    navDrawerPendingLinkRef.current = null;
    if (pendingLink) {
      window.requestAnimationFrame(() => scrollToCatalogNavTarget(pendingLink));
    }
  }, [navbarMobileOpen]);

  /** On a real phone the canvas is phone-width, so a desktop frame would force the desktop navbar into it. */
  useEffect(() => {
    if (!studioLayoutWide) setStudioPreviewFrame("mobile");
  }, [studioLayoutWide]);

  const openStudioCoreForm = useCallback((key) => {
    const tab =
      key === "products"
        ? "products"
        : key === "footer"
          ? "footer"
          : key === "navbar"
            ? "navbar"
            : "hero";
    setCustomizerOpen(true);
    setCustomizerTab(tab);
    setActiveKey(key);
    setCustomizerPane("form");
    setCustomizerFormTarget({ kind: "core", key });
    setSectionsMenuOpen(null);
  }, []);

  const openStudioBuilderSectionForm = useCallback((secId) => {
    setCustomizerOpen(true);
    setCustomizerTab("sections");
    setActiveKey(`sec:${secId}`);
    setCustomizerPane("form");
    setCustomizerFormTarget({ kind: "builder", id: secId });
    setSectionsMenuOpen(null);
  }, []);

  const [builderDraft, setBuilderDraft] = useState([]);
  const [builderTheme, setBuilderTheme] = useState("minimal");
  const [builderLoading, setBuilderLoading] = useState(() =>
    Boolean(editorMode && typeof window !== "undefined" && readAccessToken())
  );

  const [ownerProducts, setOwnerProducts] = useState([]);
  const [ownerCategories, setOwnerCategories] = useState([]);
  const ownerProductsById = useMemo(() => new Map((ownerProducts || []).map((p) => [p.id, p])), [ownerProducts]);

  // store settings (editable in customizer)
  const [storeEdit, setStoreEdit] = useState({
    primaryColor: '#ffc200',
    secondaryColor: '#ff5b00',
    headerTitle: '',
    subheaderText: '',
    productsSectionTitle: 'منتجاتنا المختارة',
    productsSectionSubtitle: 'تصفح تشكيلتنا الواسعة من أفضل المنتجات ذات الجودة العالية',
    productsHeadingAlign: 'start',
    footerBgColor: '#000000',
    footerTextColor: '#ffffff',
    phone: '',
    email: '',
    address: '',
    facebook: '',
    instagram: '',
    heroUrl: '',
    bgType: 'color',
    featureTitle1: 'توصيل سريع',
    featureTitle2: 'ضمان الجودة',
    featureTitle3: 'دعم فني',
    featureDesc1: '',
    featureDesc2: '',
    featureDesc3: '',
    featuresBarBg: '#f8fafc',
    featuresBarText: '#475569',
    featuresBarAccent: '',
  });

  const [layoutsEdit, setLayoutsEdit] = useState({
    show_hero: true,
    show_grid: true,
    show_top_hero: true,
    show_features_bar: true,
    product_columns: 3,
    best_products: [],
    catalog_sections: [],
    catalog_layout_extras: { ...DEFAULT_CATALOG_LAYOUT_EXTRAS },
  });

  const studioPersistedSnapRef = useRef("");
  const studioServerSyncedRef = useRef(false);
  const studioUnsavedRef = useRef(false);
  const studioHistoryRef = useRef({ entries: [], index: -1 });
  const historyInitializedRef = useRef(false);
  const skipNextHistoryPushRef = useRef(false);
  const pendingHistoryLabelRef = useRef(null);
  const [studioHistoryTick, setStudioHistoryTick] = useState(0);
  const [studioHistoryMenuOpen, setStudioHistoryMenuOpen] = useState(false);
  const studioHistoryUiRef = useRef(null);
  const studioNavigateBlockerPromptedRef = useRef(false);
  const [studioLeaveModalOpen, setStudioLeaveModalOpen] = useState(false);
  const studioBundleRef = useRef({
    storeEdit: {},
    layoutsEdit: {},
    builderDraft: [],
    builderTheme: "minimal",
  });
  /** Latest public/settings state for callbacks declared before `useState(settings)`. */
  const settingsSnapshotRef = useRef(null);

  useLayoutEffect(() => {
    studioBundleRef.current = { storeEdit, layoutsEdit, builderDraft, builderTheme };
  }, [storeEdit, layoutsEdit, builderDraft, builderTheme]);

  useLayoutEffect(() => {
    studioUnsavedRef.current = studioUnsaved;
  }, [studioUnsaved]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const fn = () => setStudioLayoutWide(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqCompact = window.matchMedia("(max-width: 1023.98px)");
    const mqTablet = window.matchMedia("(min-width: 768px) and (max-width: 1023.98px)");
    const sync = () => {
      setStorefrontIsCompact(mqCompact.matches);
      setStorefrontIsTablet(mqTablet.matches);
    };
    sync();
    mqCompact.addEventListener("change", sync);
    mqTablet.addEventListener("change", sync);
    return () => {
      mqCompact.removeEventListener("change", sync);
      mqTablet.removeEventListener("change", sync);
    };
  }, []);

  useLayoutEffect(() => {
    if (!canEdit || typeof window === "undefined") return;
    const chrome = studioTopChromeRef.current;
    const root = catalogPageRootRef.current;
    if (!chrome || !root) return;
    const sync = () => {
      const h = Math.ceil(chrome.getBoundingClientRect().height);
      if (h > 0) root.style.setProperty("--catalog-studio-chrome-offset", `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(chrome);
    window.addEventListener("resize", sync);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [canEdit, studioUnsaved, savingCustomizer, saveError, studioDocMenuOpen, studioHistoryMenuOpen]);

  function uid() {
    try {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }
    } catch {}
    // Fallback: UUID v4-ish (good enough for client ids if randomUUID unavailable)
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  const BUILDER_FRAME_DEFAULTS = {
    section_height: "md",
    container_width: "boxed",
    heading_align: "start",
    margin_top: "md",
    margin_bottom: "md",
    border_style: "solid",
    border_color: "rgba(15,23,42,0.12)",
    border_width: 1,
    radius: "lg",
    padding: "md",
  };

  function defaultSettingsFor(type) {
    const frame = { ...BUILDER_FRAME_DEFAULTS };
    if (type === "hero") {
      return {
        ...frame,
        title_size: "xl",
        subtitle_size: "md",
        visible: true,
        title: "",
        subtitle: "",
        images: [],
        hero_as_carousel: false,
        /** محاذاة عمودية لكتلة النص والزر داخل السكشن: start | center | end */
        hero_copy_valign: "start",
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    if (type === "text") {
      return { ...frame, title_size: "md", subtitle_size: "md", visible: true, title: "", subtitle: "", body: "", style: { backgroundColor: "#ffffff", textColor: "#0f172a" } };
    }
    if (type === "image_banner") {
      return {
        ...frame,
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        images: [],
        link_url: "",
        alt_text: "",
        image_fit: "cover",
        style: { backgroundColor: "#f1f5f9", textColor: "#0f172a" },
      };
    }
    if (type === "video") {
      return {
        ...frame,
        section_height: "md",
        border_style: "none",
        padding: "sm",
        radius: "lg",
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        title: "",
        subtitle: "",
        video_url: "",
        poster_url: "",
        video_fit: "cover",
        video_autoplay: false,
        video_loop: false,
        video_muted: false,
        /** شفاف افتراضياً — الغلاف نفسه هو العنصر المرئي */
        style: { backgroundColor: "transparent", textColor: "#0f172a" },
      };
    }
    if (type === "features_grid") {
      return {
        ...frame,
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        title: "",
        subtitle: "",
        cards_count: 3,
        /** ارتفاع البطاقات: sm | md | lg */
        card_height: "md",
        /** إعدادات البطاقة (افتراضي عام + ممكن تخصيص لكل بطاقة) */
        card_bg_color: "#ffffff",
        card_bg_opacity: 0.6,
        card_text: "#0f172a",
        card_border_color: "#0f172a",
        card_border_opacity: 0.12,
        card_shadow: "none", // none | soft | md | lg
        card_radius: "md", // sm | md | lg
        /** صورة + نمط (لكل بطاقة ممكن تخصيص) */
        card_image_mode: "circle", // circle | cover
        card_cover_grad_from: "#0f172a",
        card_cover_grad_to: "#0f172a",
        card_cover_grad_opacity: 0.45,
        /** حجم خط عنوان / فرعي / فقرة البطاقة (sm | md | lg) */
        card_title_size: "md",
        card_subtitle_size: "md",
        card_text_size: "md",
        /** إعدادات زر البطاقة (افتراضي) */
        card_btn_variant: "outline", // outline | solid
        card_btn_bg: "#0f172a",
        card_btn_fg: "#ffffff",
        card_btn_border: "#0f172a",
        card_btn_radius: 12,
        items: [
          { title: "", subtitle: "", details: "", image_url: "", image_position_x: 50, image_position_y: 50, image_mode: "", cover_grad_from: "", cover_grad_to: "", cover_grad_opacity: "", text_size: "", cta_label: "", cta_url: "", btn_variant: "", btn_bg: "", btn_fg: "", btn_border: "", btn_radius: "", card_bg_color: "", card_bg_opacity: "", card_text: "", card_border_color: "", card_border_opacity: "", card_shadow: "" },
          { title: "", subtitle: "", details: "", image_url: "", image_position_x: 50, image_position_y: 50, image_mode: "", cover_grad_from: "", cover_grad_to: "", cover_grad_opacity: "", text_size: "", cta_label: "", cta_url: "", btn_variant: "", btn_bg: "", btn_fg: "", btn_border: "", btn_radius: "", card_bg_color: "", card_bg_opacity: "", card_text: "", card_border_color: "", card_border_opacity: "", card_shadow: "" },
          { title: "", subtitle: "", details: "", image_url: "", image_position_x: 50, image_position_y: 50, image_mode: "", cover_grad_from: "", cover_grad_to: "", cover_grad_opacity: "", text_size: "", cta_label: "", cta_url: "", btn_variant: "", btn_bg: "", btn_fg: "", btn_border: "", btn_radius: "", card_bg_color: "", card_bg_opacity: "", card_text: "", card_border_color: "", card_border_opacity: "", card_shadow: "" },
        ],
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    if (type === "featured_products") {
      return {
        ...frame,
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        title: "",
        subtitle: "",
        product_ids: [],
        columns: 3,
        max_cards: 8,
        /** grid | list | gallery — نفس خيارات قسم المنتجات الرئيسي */
        products_layout: "grid",
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    if (type === "carousel") {
      const emptySlide = () => ({
        image: "",
        image_position_x: 50,
        image_position_y: 50,
        title: "",
        subtitle: "",
        link_url: "",
        link_label: "",
        product_id: "",
      });
      return {
        ...frame,
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        title: "",
        subtitle: "",
        slides: [emptySlide(), emptySlide(), emptySlide()],
        /** طبقة فوق الصورة (لون + شفافية) */
        hero_overlay_color: "#0f172a",
        hero_overlay_opacity: 0.5,
        /** إظهار زر الانتقال لقسم المنتجات */
        hero_show_cta: true,
        /** مسافة إضافية للنص والزر من الأعلى ومن بداية السطر (اليمين في RTL) بالبكسل */
        hero_inset_top: 0,
        hero_inset_inline: 0,
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    if (type === "products_carousel") {
      return {
        ...frame,
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        title: "",
        subtitle: "",
        product_ids: [],
        products_per_slide: 3,
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    if (type === "info_cards") {
      return {
        ...frame,
        title_size: "lg",
        subtitle_size: "md",
        heading_align: "center",
        visible: true,
        title: "تعرّف علينا",
        subtitle: "نبذة مختصرة عن الشركة وما يميّزنا",
        columns: 3,
        icon_color: "",
        icon_bg: "",
        card_bg: "",
        cards: [
          { id: uid(), icon: "building", title: "من نحن", text: "شركة رائدة في مجالها منذ سنوات طويلة." },
          { id: uid(), icon: "target", title: "رؤيتنا", text: "أن نكون الخيار الأول لعملائنا في السوق." },
          { id: uid(), icon: "award", title: "الجودة", text: "منتجات بمعايير جودة عالية وأسعار منافسة." },
        ],
        file_url: "",
        file_name: "",
        button_label: "تحميل ملف الشركة",
        button_bg: "",
        button_text_color: "",
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    if (type === "custom_order") {
      return {
        ...frame,
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        title: "طلب مخصص",
        subtitle: "",
        cart_item_name: "طلب مخصص",
        submit_label: "أضف إلى السلة",
        base_price: 0,
        fields: [
          {
            id: uid(),
            type: "select",
            label: "النصف الأول",
            options: [
              { id: uid(), label: "خضار", price: "7" },
              { id: uid(), label: "بيبروني", price: "8" },
            ],
          },
          {
            id: uid(),
            type: "select",
            label: "النصف الثاني",
            options: [
              { id: uid(), label: "خضار", price: "7" },
              { id: uid(), label: "بيبروني", price: "8" },
            ],
          },
          {
            id: uid(),
            type: "checkbox_group",
            label: "إضافات",
            options: [{ id: uid(), label: "جبنة إضافية", price: "15" }],
          },
        ],
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    if (type === "bundle_offers") {
      return {
        ...frame,
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        title: "عروضنا",
        subtitle: "",
        offers: [],
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    if (type === "text_image_split") {
      return {
        ...frame,
        title_size: "md",
        subtitle_size: "md",
        visible: true,
        title: "",
        subtitle: "",
        body: "",
        images: [],
        image_position_x: 50,
        image_position_y: 50,
        image_side: "left",
        bullet_list: "",
        social_instagram: "",
        social_facebook: "",
        social_twitter: "",
        social_whatsapp: "",
        social_tiktok: "",
        cta_label_1: "",
        cta_url_1: "",
        cta_label_2: "",
        cta_url_2: "",
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      };
    }
    return { ...frame, title_size: "md", subtitle_size: "md", visible: true, title: "", subtitle: "", images: [], style: { backgroundColor: "#ffffff", textColor: "#0f172a" } };
  }

  function builderFontDefaultsForType(type) {
    if (type === "hero") return { title_size: "xl", subtitle_size: "md" };
    return { title_size: "md", subtitle_size: "md" };
  }

  function normalizeImageUrlFromSettings(settings) {
    const imgs = settings?.images;
    if (!Array.isArray(imgs) || imgs.length === 0) return "";
    const v = imgs[0];
    return typeof v === "string" ? v : v?.url || "";
  }

  function StudioSwitch({ checked, onChange, label, disabled }) {
    const hasLabel = label != null && String(label).trim() !== "";
    return (
      <div className={`catalog-studio-switch-row${hasLabel ? "" : " catalog-studio-switch-row--compact"}`}>
        {hasLabel ? <span className="catalog-studio-switch-row__label">{label}</span> : null}
        <button
          type="button"
          role="switch"
          aria-checked={Boolean(checked)}
          disabled={Boolean(disabled)}
          className={`catalog-studio-switch${checked ? " catalog-studio-switch--on" : ""}`}
          onClick={() => {
            if (disabled) return;
            onChange?.(!checked);
          }}
        >
          <span className="catalog-studio-switch__thumb" aria-hidden />
        </button>
      </div>
    );
  }

  function StudioCardPicker({ value, onChange, onPreview, previewLabel, label, hint, options = [], columns = 3, disabled }) {
    const cols = Math.max(1, Math.min(4, Number(columns) || 3));
    const pv = typeof previewLabel === "string" && previewLabel.trim() ? previewLabel.trim() : "";
    return (
      <div className="catalog-studio-cardpicker">
        {label ? <div className="catalog-studio-field-title">{label}</div> : null}
        {hint ? <div className="catalog-studio-field-hint">{hint}</div> : null}
        <div className="catalog-studio-cardpicker-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {options.map((o) => {
            const active = String(o.value) === String(value);
            return (
              <div key={String(o.value)} className="catalog-studio-choice-card-wrap">
                <button
                  type="button"
                  disabled={Boolean(disabled)}
                  className={`catalog-studio-choice-card${active ? " is-active" : ""}`}
                  onClick={() => {
                    if (disabled) return;
                    onChange?.(o.value);
                  }}
                >
                  <div className="catalog-studio-choice-card__label">{o.label}</div>
                  {o.desc ? <div className="catalog-studio-choice-card__desc">{o.desc}</div> : null}
                </button>
                {typeof onPreview === "function" && pv ? (
                  <button
                    type="button"
                    className="catalog-studio-choice-card__preview-btn"
                    disabled={Boolean(disabled)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (disabled) return;
                      onPreview(o.value);
                    }}
                  >
                    {pv}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function EditableInline({ value, placeholder, onChange, textStyle, multiline = false, onOpenSidebar }) {
    const [hover, setHover] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? "");
    useEffect(() => { if (!editing) setDraft(value ?? ""); }, [value, editing]);
    const commit = () => { setEditing(false); onChange?.((draft ?? "").toString()); };
    const trimmed = (value && String(value).trim()) || "";
    if (!canEdit) return trimmed ? <span style={textStyle}>{trimmed}</span> : null;
    const pencilBtn = (
      <button
        type="button"
        className="catalog-studio-inline-pencil"
        aria-label="تعديل النص"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        style={{
          position: "absolute",
          top: -10,
          insetInlineStart: -10,
          width: 28,
          height: 28,
          borderRadius: 9,
          border: "1px solid rgba(0,0,0,0.12)",
          background: "rgba(255,255,255,0.95)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 3,
          opacity: hover || editing ? 1 : 0,
          pointerEvents: hover || editing ? "auto" : "none",
          transition: "opacity 0.15s",
        }}
      >
        <Pencil size={13} />
      </button>
    );
    return (
      <span
        style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={(e) => {
          if (e.target.closest(".catalog-studio-inline-pencil")) return;
          if (!editing && onOpenSidebar) onOpenSidebar();
        }}
      >
        {!editing ? (
          <>
            <span
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") setEditing(true); }}
              style={{
                ...textStyle,
                cursor: "text",
                minHeight: trimmed ? undefined : 20,
                display: "inline-block",
                outline: hover ? "1px dashed rgba(15,23,42,0.2)" : "none",
                borderRadius: 4,
                padding: hover ? "0 2px" : 0,
              }}
            >
              {trimmed || placeholder || "\u00a0"}
            </span>
            {pencilBtn}
          </>
        ) : multiline ? (
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} rows={3} style={{ width: "min(720px, 100%)", marginTop: 6, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", padding: "10px 12px", resize: "vertical" }} autoFocus />
        ) : (
          <input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); } }} style={{ width: "min(720px, 100%)", marginTop: 6, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", padding: "10px 12px" }} autoFocus />
        )}
      </span>
    );
  }

  const patchBuilderSectionById = useCallback((secId, patch) => {
    if (!secId || !patch) return;
    setBuilderDraft((prev) =>
      prev.map((s) => (s.id === secId ? { ...s, settings: { ...(s.settings || {}), ...patch } } : s))
    );
  }, []);

  const activeSection = useMemo(() => {
    if (!activeKey?.startsWith("sec:")) return null;
    const id = activeKey.slice(4);
    return (builderDraft || []).find((s) => s.id === id) || null;
  }, [activeKey, builderDraft]);

  const patchActiveSection = (patch) => {
    if (!activeSection) return;
    setBuilderDraft((prev) => prev.map((s) => (s.id === activeSection.id ? { ...s, settings: { ...(s.settings || {}), ...(patch || {}) } } : s)));
  };
  const patchActiveSectionStyle = (patch) => {
    if (!activeSection) return;
    const cur = activeSection.settings || {};
    const nextStyle = { ...(cur.style || {}), ...(patch || {}) };
    patchActiveSection({ style: nextStyle });
  };

  const sectionHeightScale = (st) => {
    const h = String(st?.section_height || "md").trim().toLowerCase();
    /** حد أدنى لارتفاع سكشن نص+صورة — يتمدد مع المحتوى دون تمرير داخلي */
    if (h === "sm") return { sectionMin: 200, splitH: 200, splitMedia: 200, banner: 280 };
    if (h === "lg") return { sectionMin: 500, splitH: 500, splitMedia: 500, banner: 520 };
    return { sectionMin: 340, splitH: 340, splitMedia: 340, banner: 400 };
  };

  const sectionContentMinHeight = (st) => sectionHeightScale(st).sectionMin;

  const builderSectionSurfaceStyle = (st, frame, bg, fg, { bannerHeight = false } = {}) => ({
    borderRadius: frame.radius,
    border: frame.border,
    background: bg,
    color: fg,
    padding: frame.pad,
    direction: settings.layoutDirection || "rtl",
    minHeight: bannerHeight ? sectionHeightScale(st).banner : sectionContentMinHeight(st),
    boxSizing: "border-box",
  });

  const resolveProductsSectionWidthKey = (layoutExtras) => {
    const sec = String(layoutExtras?.products_section_container_width ?? "").trim();
    if (sec === "full" || sec === "wide" || sec === "boxed") return sec;
    const g = String(layoutExtras?.builder_page_container_width ?? "").trim();
    if (g === "boxed" || g === "wide" || g === "full") return g;
    return "boxed";
  };

  /** دمج عرض الصفحة الموحّد مع خيار «عرض كامل» لقسم معيّن — إعداد القسم يتقدّم على العامي */
  const resolveBuilderContainerWidthKey = (sectionSettings, layoutExtras) => {
    const sec = String(sectionSettings?.container_width || "").trim();
    if (sec === "full") return "full";
    if (sec === "wide" || sec === "boxed") return sec;
    const g = String(layoutExtras?.builder_page_container_width ?? "").trim();
    if (g === "boxed" || g === "wide" || g === "full") return g;
    return "boxed";
  };

  const sectionFrameStyle = (st, layoutExtras) => {
    const radiusKey = (st?.radius || "lg").toString();
    const radius =
      radiusKey === "none"
        ? 0
        : radiusKey === "sm"
          ? 12
          : radiusKey === "md"
            ? 18
            : radiusKey === "xl"
              ? 44
              : 28;

    const padKey = (st?.padding || "md").toString();
    const pad = padKey === "sm" ? 14 : padKey === "lg" ? 30 : 22;

    const bStyle = (st?.border_style || "solid").toString(); // none|solid|dashed
    const bWidth = Number.isFinite(Number(st?.border_width)) ? Number(st.border_width) : 1;
    const bColor = (st?.border_color || "rgba(15,23,42,0.12)").toString();
    const border = bStyle === "none" ? "none" : `${Math.max(1, Math.min(bWidth, 4))}px ${bStyle} ${bColor}`;

    const widthKey = resolveBuilderContainerWidthKey(st, layoutExtras); // boxed|wide|full
    const maxWidth =
      widthKey === "full"
        ? "100%"
        : widthKey === "wide"
          ? CATALOG_PAGE_CONTENT_WIDTH_PX.wide
          : CATALOG_PAGE_CONTENT_WIDTH_PX.boxed;

    return { radius, pad, border, maxWidth, widthKey };
  };

  const builderSectionWrapStyle = (frame) => {
    if (frame.widthKey === "full") {
      return {
        width: "100vw",
        maxWidth: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        position: "relative",
        boxSizing: "border-box",
      };
    }
    return {
      width: "100%",
      maxWidth: frame.maxWidth,
      marginLeft: "auto",
      marginRight: "auto",
      boxSizing: "border-box",
    };
  };

  const sectionHeadingAlign = (st) => {
    const a = (st?.heading_align || "start").toString();
    if (a === "center" || a === "end") return a;
    return "start";
  };

  const sectionBlockMargins = (st) => {
    const map = { none: 0, sm: 12, md: 28, lg: 56 };
    const mtKey = (st?.margin_top ?? "md").toString();
    const mbKey = (st?.margin_bottom ?? "md").toString();
    return {
      marginTop: map[mtKey] ?? 28,
      marginBottom: map[mbKey] ?? 28,
    };
  };

  const sectionTitleFontPx = (st) => {
    const k = (st?.title_size || "md").toString();
    const map = { sm: 18, md: 22, lg: 28, xl: 36 };
    return map[k] ?? 22;
  };

  const sectionSubtitleFontPx = (st) => {
    const k = (st?.subtitle_size || "md").toString();
    const map = { sm: 12, md: 15, lg: 18 };
    return map[k] ?? 15;
  };

  useEffect(() => {
    if (!canEdit || customizerTab !== "sections") return;
    if (activeKey?.startsWith("sec:")) return;
    const first = (builderDraft || [])[0];
    if (first?.id) setActiveKey(`sec:${first.id}`);
  }, [canEdit, customizerTab, builderDraft, activeKey]);

  /** `heroKind` ("desktop" | "mobile") applies the hero upload profile (WebP, q75, <=200KB, 1920/800px). */
  const uploadBuilderImage = async (file, heroKind) => {
    const authTok =
      studioHeaders?.Authorization ||
      (readAccessToken() ? `Bearer ${readAccessToken()}` : "");
    if (!authTok) throw new Error("Not signed in");
    const fileType = String(file?.type || "");
    const isImage = fileType.startsWith("image/");
    const isVideo = fileType.startsWith("video/");
    const compressed = isImage
      ? heroKind
        ? await compressHeroImageFile(file, heroKind)
        : await compressProductImageFile(file)
      : file;
    const form = new FormData();
    form.append("file", compressed);
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      isImage ? BUILDER_IMAGE_UPLOAD_TIMEOUT_MS : BUILDER_VIDEO_UPLOAD_TIMEOUT_MS
    );
    try {
      const res = await fetch(studioApiUrl('/builder/catalog/upload'), {
        method: "POST",
        headers: { Authorization: authTok.startsWith("Bearer ") ? authTok : `Bearer ${authTok}` },
        body: form,
        signal: controller.signal,
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Upload failed");
      }
      const data = await res.json().catch(() => ({}));
      const url = typeof data?.url === "string" ? data.url.trim() : "";
      if (!url) throw new Error("Upload returned no image URL");
      return url;
    } catch (err) {
      if (err?.name === "AbortError") {
        throw new Error(
          isVideo
            ? "انتهت مهلة رفع الفيديو. جرّب ملفاً أصغر أو اتصالاً أسرع، ثم أعد المحاولة."
            : isImage
              ? "انتهت مهلة الرفع. جرّب صورة أصغر أو تحقّق من الاتصال."
              : "انتهت مهلة رفع الملف. جرّب ملفاً أصغر أو تحقّق من الاتصال."
        );
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Load editable data when in studio
  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    const isTransientNetworkError = (err) => {
      const msg = String(err?.message || err || "");
      const code = String(err?.code || "");
      return (
        /connectionterminated|error_code|last_stream|stream_id|http\s*2|http2|h2\s*err|ECONNRESET|ECONNABORTED|ETIMEDOUT|socket|network error|failed to fetch|net::ERR|additional_data|stream closed|goaway/i.test(
          msg
        ) ||
        /ECONNRESET|ECONNABORTED|ETIMEDOUT|ERR_NETWORK/i.test(code)
      );
    };
    const fetchAllWithRetry = async (attempts = 3) => {
      let lastErr = null;
      for (let i = 0; i < attempts; i++) {
        try {
          return await Promise.all([
            axios.get(studioApiUrl('/settings/all'), authHeader),
            axios.get(studioApiUrl('/settings/layouts'), authHeader),
            axios.get(studioApiUrl('/builder/catalog'), authHeader),
            axios.get(studioApiUrl('/products'), authHeader),
            axios.get(studioApiUrl('/categories'), authHeader).catch(() => null),
          ]);
        } catch (err) {
          lastErr = err;
          if (i < attempts - 1 && isTransientNetworkError(err)) {
            await new Promise((r) => setTimeout(r, 400 * (i + 1)));
            continue;
          }
          throw err;
        }
      }
      throw lastErr;
    };
    const load = async () => {
      setBuilderLoading(true);
      setSaveError("");
      studioServerSyncedRef.current = false;
      historyInitializedRef.current = false;
      studioHistoryRef.current = { entries: [], index: -1 };
      try {
        const [settingsRes, layoutsRes, builderRes, productsRes, catRes] = await fetchAllWithRetry(3);
        if (cancelled) return;
        const s = settingsRes?.data || {};
        const storeNameFromSettings = String(
          s.store_name_ar || s.store_name_en || ""
        ).trim();
        const catalogKeyFromSettings = String(s.catalog_key || "").trim();
        if (catalogKeyFromSettings) setCatalogSlug(catalogKeyFromSettings);
        if (storeNameFromSettings) {
          setSettings((prev) => ({
            ...prev,
            storeName: storeNameFromSettings,
            logo: s.logo_url || prev.logo,
            whatsappNumber: s.whatsapp_number || prev.whatsappNumber,
            primaryColor: s.primary_color || prev.primaryColor,
            secondaryColor: s.secondary_color || prev.secondaryColor,
            layoutDirection: s.layout_direction || prev.layoutDirection,
          }));
        }
        setStoreEdit((p) => ({
          ...p,
          primaryColor: s.primary_color || p.primaryColor,
          secondaryColor: s.secondary_color || p.secondaryColor,
          headerTitle: s.header_title_ar || "",
          subheaderText: s.subheader_text_ar || "",
          productsSectionTitle: s.products_section_title_ar || p.productsSectionTitle,
          productsSectionSubtitle: s.products_section_subtitle_ar || p.productsSectionSubtitle,
          productsHeadingAlign: s.products_heading_align || p.productsHeadingAlign || "start",
          footerBgColor: s.footer_bg_color || p.footerBgColor,
          footerTextColor: s.footer_text_color || p.footerTextColor,
          phone: s.phone_number || "",
          email: s.email || "",
          address: s.address || "",
          facebook: s.facebook_url || "",
          instagram: s.instagram_url || "",
          heroUrl: s.hero_url || "",
          bgType: s.bg_type || "color",
          featureTitle1: s.feature_title_1 || p.featureTitle1,
          featureTitle2: s.feature_title_2 || p.featureTitle2,
          featureTitle3: s.feature_title_3 || p.featureTitle3,
          featureDesc1: s.feature_description_1 ?? p.featureDesc1,
          featureDesc2: s.feature_description_2 ?? p.featureDesc2,
          featureDesc3: s.feature_description_3 ?? p.featureDesc3,
          featuresBarBg: s.features_bar_bg || p.featuresBarBg,
          featuresBarText: s.features_bar_text || p.featuresBarText,
          featuresBarAccent: s.features_bar_accent ?? p.featuresBarAccent,
        }));
        if (cancelled) return;
        const rawLayouts = layoutsRes?.data;
        const l = Array.isArray(rawLayouts) ? rawLayouts[0] || {} : rawLayouts || {};
        const topHero = l.show_top_hero ?? l.show_hero ?? true;
        const rawLx = l.catalog_layout_extras && typeof l.catalog_layout_extras === "object" ? l.catalog_layout_extras : null;
        const lx = { ...DEFAULT_CATALOG_LAYOUT_EXTRAS, ...(rawLx || {}) };
        migrateLegacyLayoutExtras(lx, rawLx);
        if (cancelled) return;
        setLayoutsEdit({
          show_hero: topHero,
          show_grid: l.show_grid ?? true,
          show_top_hero: topHero,
          show_features_bar: l.show_features_bar ?? true,
          product_columns: l.product_columns || 3,
          best_products: l.best_products || [],
          catalog_sections: l.catalog_sections || [],
          catalog_layout_extras: lx,
        });
        const b = builderRes?.data || {};
        if (cancelled) return;
        setBuilderTheme(b?.page?.theme || "minimal");
        const loaded = (b?.sections || []).map((sec, idx) => {
          const raw = sec.settings || {};
          return {
            id: sec.id || uid(),
            type: sec.type,
            order: Number.isFinite(sec.order) ? sec.order : idx,
            settings: {
              ...BUILDER_FRAME_DEFAULTS,
              ...builderFontDefaultsForType(sec.type),
              ...raw,
              style: {
                backgroundColor: "#ffffff",
                textColor: "#0f172a",
                ...(raw.style || {}),
              },
            },
          };
        }).sort((a, b2) => (a.order ?? 0) - (b2.order ?? 0));
        const { before: lb, after: la } = partitionBuilderSections(loaded);
        if (cancelled) return;
        setBuilderDraft(mergeBuilderSections(lb, la));
        setOwnerProducts(Array.isArray(productsRes?.data) ? productsRes.data : []);
        setOwnerCategories(Array.isArray(catRes?.data) ? catRes.data : []);
      } catch (e) {
        if (!cancelled) setSaveError(formatCustomizerSaveError(e));
      } finally {
        if (!cancelled) setBuilderLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [canEdit, authHeader]);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    (async () => {
      setCatalogThemesLoading(true);
      setCatalogThemesError("");
      try {
        const res = await axios.get(studioApiUrl('/catalog-themes'), authHeader);
        if (cancelled) return;
        setCatalogThemes(Array.isArray(res?.data?.themes) ? res.data.themes : []);
      } catch (e) {
        if (!cancelled) setCatalogThemesError(formatCustomizerSaveError(e));
      } finally {
        if (!cancelled) setCatalogThemesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEdit, authHeader]);

  useEffect(() => {
    if (!canEdit || builderLoading) return;
    const run = () => {
      const b = studioBundleRef.current;
      const snap = serializeStudioPersistBundle(b);
      if (!snap) return;
      if (!studioServerSyncedRef.current) {
        studioServerSyncedRef.current = true;
        studioPersistedSnapRef.current = snap;
        setStudioUnsaved(false);
        const key = studioLocalDraftStorageKey(userId);
        if (key && typeof localStorage !== "undefined") {
          try {
            const raw = localStorage.getItem(key);
            if (raw) {
              const parsed = JSON.parse(raw);
              const localSnap = parsed?.snap;
              if (typeof localSnap === "string" && localSnap !== snap) setLocalDraftBanner(true);
            }
          } catch {
            /* ignore */
          }
        }
        return;
      }
      setStudioUnsaved(snap !== studioPersistedSnapRef.current);
    };
    const id = window.setTimeout(run, 450);
    return () => window.clearTimeout(id);
  }, [canEdit, builderLoading, storeEdit, layoutsEdit, builderDraft, builderTheme, userId]);

  useEffect(() => {
    if (!canEdit) return;
    const onBeforeUnload = (e) => {
      if (!studioUnsavedRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [canEdit]);

  useEffect(() => {
    if (!canEdit || !userId) return;
    const key = studioLocalDraftStorageKey(userId);
    if (!key) return;
    const id = window.setInterval(() => {
      if (!studioUnsavedRef.current) return;
      const b = studioBundleRef.current;
      try {
        localStorage.setItem(key, JSON.stringify({ t: Date.now(), snap: serializeStudioPersistBundle(b) }));
      } catch {
        /* ignore */
      }
    }, 120000);
    return () => window.clearInterval(id);
  }, [canEdit, userId]);

  useEffect(() => {
    if (!studioToast?.message) return;
    const t = window.setTimeout(() => setStudioToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [studioToast]);

  const addSection = async (type) => {
    const { before, after } = partitionBuilderSections(builderDraft);
    const optimistic = {
      id: uid(),
      type,
      order: before.length,
      settings: { ...defaultSettingsFor(type), after_products: false },
      __optimistic: true,
    };
    setBuilderDraft(() => mergeBuilderSections([...before, optimistic], after));
    setCustomizerTab("sections");
    setActiveKey(`sec:${optimistic.id}`);
    setCustomizerOpen(true);
    try {
      const res = await axios.post(studioApiUrl('/builder/catalog/section'), { type, settings: optimistic.settings }, authHeader);
      const created = res?.data;
      if (created?.id) {
        setBuilderDraft((prev) => prev.map((s) => (s.id === optimistic.id ? { id: created.id, type: created.type, order: created.order, settings: created.settings || optimistic.settings } : s)));
        setActiveKey(`sec:${created.id}`);
      }
    } catch (e) {
      setSaveError(formatCustomizerSaveError(e));
    }
  };

  const addSectionAt = async (type, insertIndex, opts = {}) => {
    const afterProducts = Boolean(opts.afterProducts);
    const presetId = String(opts.presetId || "").trim();
    const presetPatch = presetId && SECTION_PRESET_PATCHES[presetId] ? SECTION_PRESET_PATCHES[presetId] : null;
    const categoryImageItems = presetId === "categories"
      ? (categories || [])
          .filter((name) => String(name || "").trim() && String(name).trim().toLowerCase() !== "all")
          .map((name) => ({ slug: String(name), name: String(name), image_url: "" }))
      : null;
    const { before, after } = partitionBuilderSections(builderDraft);
    const pool = afterProducts ? after : before;
    const safeIndex = Math.max(0, Math.min(Number(insertIndex ?? 0), pool.length));
    const optimistic = {
      id: uid(),
      type,
      order: safeIndex,
      settings: {
        ...defaultSettingsFor(type),
        ...(presetPatch || {}),
        ...(categoryImageItems
          ? {
              category_items: categoryImageItems,
              cards_count: Math.min(4, Math.max(2, categoryImageItems.length || 4)),
              card_shape: "square",
            }
          : {}),
        after_products: afterProducts,
      },
      __optimistic: true,
    };
    if (afterProducts) {
      const na = [...after];
      na.splice(safeIndex, 0, optimistic);
      setBuilderDraft(mergeBuilderSections(before, na));
    } else {
      const nb = [...before];
      nb.splice(safeIndex, 0, optimistic);
      setBuilderDraft(mergeBuilderSections(nb, after));
    }
    setCustomizerTab("sections");
    setActiveKey(`sec:${optimistic.id}`);
    setCustomizerOpen(true);
    try {
      const res = await axios.post(studioApiUrl('/builder/catalog/section'), { type, settings: optimistic.settings }, authHeader);
      const created = res?.data;
      if (created?.id) {
        setBuilderDraft((prev) =>
          (prev || []).map((s) =>
            s.id === optimistic.id
              ? { id: created.id, type: created.type, order: created.order, settings: created.settings || optimistic.settings }
              : s
          )
        );
        setActiveKey(`sec:${created.id}`);
      }
    } catch (e) {
      setSaveError(formatCustomizerSaveError(e));
    }
  };

  const deleteSection = async (id) => {
    const before = builderDraft;
    setBuilderDraft((p) => p.filter((s) => s.id !== id));
    if (activeKey === `sec:${id}`) setActiveKey("hero");
    try {
      // Optimistic-only section id (not yet created on backend)
      if (String(id).startsWith("sec_")) return;
      await axios.delete(studioApiUrl(`/builder/catalog/section/${id}`), authHeader);
    } catch (e) {
      setSaveError(formatCustomizerSaveError(e));
      setBuilderDraft(before);
    }
  };

  const moveSection = (id, dir) => {
    setBuilderDraft((prev) => {
      if (catalogVisualTheme === "restaurant") {
        const list = Array.isArray(prev) ? [...prev] : [];
        const i = list.findIndex((s) => s.id === id);
        if (i < 0) return prev;
        const j = dir === "up" ? i - 1 : i + 1;
        if (j < 0 || j >= list.length) return prev;
        const next = [...list];
        const t = next[i];
        next[i] = next[j];
        next[j] = t;
        return next.map((s) => ({
          ...s,
          settings: { ...(s.settings || {}), after_products: true },
        }));
      }
      const { before, after } = partitionBuilderSections(prev);
      const ib = before.findIndex((s) => s.id === id);
      if (ib >= 0) {
        const arr = [...before];
        if (dir === "down" && ib === arr.length - 1) {
          const sec = arr[ib];
          const nb = arr.filter((s) => s.id !== id);
          const moved = {
            ...sec,
            settings: { ...(sec.settings || {}), after_products: true },
          };
          return mergeBuilderSections(nb, [moved, ...after]);
        }
        const j = dir === "up" ? ib - 1 : ib + 1;
        if (j < 0 || j >= arr.length) return prev;
        const t = arr[ib];
        arr[ib] = arr[j];
        arr[j] = t;
        return mergeBuilderSections(arr, after);
      }
      const ia = after.findIndex((s) => s.id === id);
      if (ia >= 0) {
        const arr = [...after];
        if (dir === "up" && ia === 0) {
          const sec = arr[0];
          const na = arr.slice(1);
          const moved = {
            ...sec,
            settings: { ...(sec.settings || {}), after_products: false },
          };
          return mergeBuilderSections([...before, moved], na);
        }
        const j = dir === "up" ? ia - 1 : ia + 1;
        if (j < 0 || j >= arr.length) return prev;
        const t = arr[ia];
        arr[ia] = arr[j];
        arr[j] = t;
        return mergeBuilderSections(before, arr);
      }
      return prev;
    });
  };

  const saveAllCustomizer = async () => {
    if (!canEdit) return;
    setSavingCustomizer(true);
    setSaveError("");
    setSaveErrorStep("");
    let failedAt = "settings";
    try {
      failedAt = "settings";
      setSaveProgressStep("settings");
      const fd = new FormData();
      fd.append("primary_color", storeEdit.primaryColor || "#4f46e5");
      fd.append("secondary_color", storeEdit.secondaryColor || "#f3f4f6");
      fd.append("header_title", storeEdit.headerTitle || "");
      fd.append("subheader_text", storeEdit.subheaderText || "");
      fd.append("products_section_title", storeEdit.productsSectionTitle || "");
      fd.append("products_section_subtitle", storeEdit.productsSectionSubtitle || "");
      fd.append("products_heading_align", storeEdit.productsHeadingAlign || "start");
      fd.append("feature_desc_1", storeEdit.featureDesc1 ?? "");
      fd.append("feature_desc_2", storeEdit.featureDesc2 ?? "");
      fd.append("feature_desc_3", storeEdit.featureDesc3 ?? "");
      fd.append("feature_title_1", storeEdit.featureTitle1 ?? "");
      fd.append("feature_title_2", storeEdit.featureTitle2 ?? "");
      fd.append("feature_title_3", storeEdit.featureTitle3 ?? "");
      fd.append("features_bar_bg", storeEdit.featuresBarBg ?? "");
      fd.append("features_bar_text", storeEdit.featuresBarText ?? "");
      fd.append("features_bar_accent", storeEdit.featuresBarAccent ?? "");
      fd.append("footer_bg_color", storeEdit.footerBgColor || "#000000");
      fd.append("footer_text_color", storeEdit.footerTextColor || "#ffffff");
      fd.append("phone_number", storeEdit.phone || "");
      fd.append("email", storeEdit.email || "");
      fd.append("facebook_url", storeEdit.facebook || "");
      fd.append("instagram_url", storeEdit.instagram || "");
      fd.append("address", storeEdit.address || "");
      const heroUrlTrim = String(storeEdit.heroUrl || "").trim();
      const bgTypeFinal = heroUrlTrim ? "image" : "color";
      fd.append("bg_type", bgTypeFinal);
      fd.append("hero_url", heroUrlTrim);
      await axios.post(studioApiUrl('/settings/update'), fd, { headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" } });
      setSettings((prev) => ({ ...prev, heroUrl: heroUrlTrim }));

      failedAt = "layouts";
      setSaveProgressStep("layouts");
      const lfd = new FormData();
      lfd.append("catalog_sections", JSON.stringify(layoutsEdit.catalog_sections || []));
      lfd.append("best_products", JSON.stringify(layoutsEdit.best_products || []));
      const heroShown = Boolean(layoutsEdit.show_top_hero);
      lfd.append("show_hero", String(heroShown));
      lfd.append("show_grid", String(Boolean(layoutsEdit.show_grid)));
      lfd.append("show_top_hero", String(heroShown));
      lfd.append("show_features_bar", String(Boolean(layoutsEdit.show_features_bar)));
      lfd.append("product_columns", String(Number(layoutsEdit.product_columns || 3)));
      const extrasToSave = {
        ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
        ...(layoutsEdit.catalog_layout_extras || {}),
      };
      lfd.append("catalog_layout_extras", JSON.stringify(extrasToSave));
      writeCachedCatalogLoaderPrefs(userId, extrasToSave.loader_text, extrasToSave.loader_text_color);
      await axios.post(studioApiUrl('/settings/update-layouts'), lfd, { headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" } });

      failedAt = "builder";
      setSaveProgressStep("builder");
      const normalized = (builderDraft || []).map((s, idx) => ({ id: s.id, type: s.type, order: idx, settings: s.settings || {} }));
      await axios.post(studioApiUrl('/builder/catalog/save'), { theme: builderTheme || "minimal", sections: normalized }, authHeader);

      setSaveProgressStep("");
      const snap = serializeStudioPersistBundle({
        storeEdit,
        layoutsEdit,
        builderDraft,
        builderTheme,
      });
      studioPersistedSnapRef.current = snap;
      setStudioUnsaved(false);
      studioUnsavedRef.current = false;
      setSaveError("");
      setSaveErrorStep("");
      const dk = studioLocalDraftStorageKey(userId);
      if (dk) {
        try {
          localStorage.removeItem(dk);
        } catch {
          /* ignore */
        }
      }
      setLocalDraftBanner(false);
      const sl =
        typeof window !== "undefined"
          ? (() => {
              try {
                return localStorage.getItem("catalogStudioLanguage") === "en" ? "en" : "ar";
              } catch {
                return "ar";
              }
            })()
          : "ar";
      setStudioToast({ message: sl === "en" ? "Changes saved" : "تم حفظ التغييرات" });
      setSaveUiSuccess(true);
      window.setTimeout(() => setSaveUiSuccess(false), 2200);
    } catch (e) {
      setSaveErrorStep(failedAt);
      setSaveError(formatCustomizerSaveError(e));
    } finally {
      setSavingCustomizer(false);
      setSaveProgressStep("");
    }
  };

  const applyThemePreset = useCallback(
    (themeKey) => {
      const k = String(themeKey || "minimal").toLowerCase();
      const patchExtras = (patch) =>
        setLayoutsEdit((p) => ({
          ...p,
          catalog_layout_extras: {
            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
            ...(p.catalog_layout_extras || {}),
            ...(patch || {}),
          },
        }));

      const keepOnlySectionTypes = (types) => {
        const allowed = new Set((types || []).map(String));
        setBuilderDraft((prev) => {
          const cur = Array.isArray(prev) ? prev : [];
          const kept = cur.filter((s) => allowed.has(String(s?.type || "")));
          // ensure at least one featured section exists when requested
          const need = Array.from(allowed);
          for (const t of need) {
            if (!kept.some((s) => s?.type === t)) {
              kept.push({ id: uid(), type: t, settings: { ...defaultSettingsFor(t), after_products: false } });
            }
          }
          return kept.map((s, idx) => ({ ...s, order: idx }));
        });
      };

      if (k === "restaurant") {
        setLayoutsEdit((p) => ({ ...p, show_top_hero: true, show_hero: true, show_features_bar: false }));
        patchExtras({
          global_page_bg_enabled: true,
          page_bg: "#f2f2f2",
          products_main_layout: "grid",
          product_image_shape: "circle",
          filters_sidebar: false,
          category_nav: "top",
          builder_page_container_width: "boxed",
          restaurant_topbar_bg: "#1a1a1a",
          restaurant_topbar_text: "#ffffff",
          restaurant_bg: "#f2f2f2",
          restaurant_surface: "#ffffff",
          restaurant_ink: "#1a1a1a",
          restaurant_muted: "rgba(26, 26, 26, 0.62)",
          restaurant_line: "rgba(26, 26, 26, 0.10)",
          restaurant_accent: "#e85d04",
          restaurant_accent_warm: "#f5a623",
          restaurant_decor_style: "arcs",
          restaurant_decor_color: "#f5a623",
          restaurant_decor_opacity: 0.38,
          product_card_image_size: 168,
          restaurant_card_image_size: 168,
          products_show_description: true,
          restaurant_show_desc: true,
          hero_layout: "split_circle",
          hero_height_mode: "default",
          hero_content_align: "start",
          hero_title_color: "#1a1a1a",
          hero_subtitle_color: "#64748b",
          hero_primary_color: "#1a1a1a",
          hero_cta_label: "اطلب الآن",
        });
        // فقط قسم المفضلة (منتجات مختارة) قبل المنتجات الرئيسية
        keepOnlySectionTypes(["featured_products"]);
        return;
      }

      if (k === "showcase") {
        // متجر عرض: هيرو + سكاشن إضافية + منتجات أساسية + فوتر
        setLayoutsEdit((p) => ({ ...p, show_top_hero: true, show_hero: true, show_features_bar: false }));
        patchExtras({
          page_bg: "#ffffff",
          products_main_layout: "grid",
          filters_sidebar: true,
          category_nav: "top",
          catalog_font_stack: "tajawal",
          builder_page_container_width: "wide",
        });
        setBuilderDraft(() => {
          const mk = (type, after_products) => ({
            id: uid(),
            type,
            settings: { ...defaultSettingsFor(type), after_products: Boolean(after_products) },
          });
          const fp = mk("featured_products", false);
          const cr = mk("carousel", false);
          const slideTemplate = () => ({
            image: "",
            title: "",
            subtitle: "",
            link_url: "",
            link_label: "",
            product_id: "",
          });
          const slides = Array.isArray(cr.settings.slides) ? cr.settings.slides.map(slideTemplate) : [slideTemplate(), slideTemplate(), slideTemplate()];
          while (slides.length < 3) slides.push(slideTemplate());
          slides[0] = { ...slides[0], image: CATALOG_TEMPLATE_MEDIA.slideRetailA };
          slides[1] = { ...slides[1], image: CATALOG_TEMPLATE_MEDIA.slideRetailB };
          slides[2] = { ...slides[2], image: CATALOG_TEMPLATE_MEDIA.slideRetailC };
          cr.settings.slides = slides.slice(0, 8);
          const sp = mk("text_image_split", true);
          sp.settings.title = "قسم نص وصورة";
          sp.settings.subtitle = "للتواصل أو سياسة الشحن";
          sp.settings.body = "عدّل النص والصورة من أيقونة القلم على القسم.";
          sp.settings.images = [{ url: CATALOG_TEMPLATE_MEDIA.cardFashion }];
          const pc = mk("products_carousel", true);
          return mergeBuilderSections([fp, cr], [sp, pc]).map((s, idx) => ({ ...s, order: idx }));
        });
        return;
      }

      if (k === "general_store") {
        setLayoutsEdit((p) => ({ ...p, show_top_hero: true, show_hero: true, show_features_bar: true }));
        patchExtras({
          global_page_bg_enabled: false,
          page_bg: "#fafaf9",
          products_main_layout: "grid",
          filters_sidebar: false,
          category_nav: "top",
          products_heading_color: "",
          products_subtitle_color: "",
          hero_subtitle_color: "",
          catalog_font_stack: "cairo",
          builder_page_container_width: "boxed",
        });
        setStoreEdit((p) => ({
          ...p,
          primaryColor: "#ea580c",
          secondaryColor: "#fffbeb",
          footerBgColor: "#1c1917",
          footerTextColor: "#fafaf9",
          heroUrl: CATALOG_TEMPLATE_MEDIA.heroRetail,
          headerTitle: "متجرك — قالب تجزئة",
          subheaderText: "عروض يومية وشحن سريع لراحة عملائك",
          featureTitle1: "شحن سريع",
          featureDesc1: "توصيل خلال يوم إلى يومين عمل",
          featureTitle2: "دفع آمن",
          featureDesc2: "خيارات دفع متعددة",
          featureTitle3: "دعم بالعربية",
          featureDesc3: "فريق يرد على استفساراتكم بسرعة",
          productsSectionTitle: "تسوق حسب الفئة",
          productsSectionSubtitle: "اختر ما يناسبك من المعروض",
        }));
        const emptyCard = () => ({
          title: "",
          subtitle: "",
          details: "",
          image_url: "",
          image_position_x: 50,
          image_position_y: 50,
          image_mode: "",
          cover_grad_from: "",
          cover_grad_to: "",
          cover_grad_opacity: "",
          text_size: "",
          cta_label: "",
          cta_url: "",
          btn_variant: "",
          btn_bg: "",
          btn_fg: "",
          btn_border: "",
          btn_radius: "",
          card_bg_color: "",
          card_bg_opacity: "",
          card_text: "",
          card_border_color: "",
          card_border_opacity: "",
          card_shadow: "",
        });
        setBuilderDraft(() => {
          const fg = defaultSettingsFor("features_grid");
          fg.title = "لماذا تتسوق معنا؟";
          fg.subtitle = "جودة، ثقة، وتجربة بسيطة على الجوال والكمبيوتر";
          fg.style = { backgroundColor: "#fff7ed", textColor: "#1c1917" };
          fg.card_shadow = "soft";
          const a = emptyCard();
          a.title = "أزياء";
          a.subtitle = "تشكيلات موسمية مختارة";
          a.details = "تابع الجديد أسبوعياً";
          a.image_url = CATALOG_TEMPLATE_MEDIA.cardFashion;
          const b = emptyCard();
          b.title = "حقائب";
          b.subtitle = "عملية وأنيقة";
          b.details = "مناسبة لليوم والسفر";
          b.image_url = CATALOG_TEMPLATE_MEDIA.cardBag;
          const c = emptyCard();
          c.title = "أحذية";
          c.subtitle = "راحة ولون";
          c.details = "خيارات تناسب كل الأذواق";
          c.image_url = CATALOG_TEMPLATE_MEDIA.cardShoes;
          fg.items = [a, b, c];
          return mergeBuilderSections([{ id: uid(), type: "features_grid", settings: { ...fg, after_products: false } }], []).map((s, idx) => ({
            ...s,
            order: idx,
          }));
        });
        return;
      }

      if (k === "dark_store") {
        setLayoutsEdit((p) => ({ ...p, show_top_hero: true, show_hero: true, show_features_bar: false }));
        patchExtras({
          catalog_dark_surface: true,
          global_page_bg_enabled: true,
          page_bg: "#0b1220",
          products_main_layout: "grid",
          filters_sidebar: true,
          category_nav: "top",
          products_heading_color: "#f1f5f9",
          products_subtitle_color: "#94a3b8",
          products_card_bg_color: "#131c2e",
          hero_subtitle_color: "#cbd5e1",
          catalog_font_stack: "tajawal",
          builder_page_container_width: "wide",
        });
        setStoreEdit((p) => ({
          ...p,
          primaryColor: "#38bdf8",
          secondaryColor: "#0f172a",
          footerBgColor: "#020617",
          footerTextColor: "#e2e8f0",
          heroUrl: CATALOG_TEMPLATE_MEDIA.heroDark,
          headerTitle: "متجر ليلي",
          subheaderText: "مظهر داكن جاهز مع أقسام عرض وكاروسيل",
          productsSectionTitle: "منتجاتنا",
          productsSectionSubtitle: "تصفح الفئات والفلاتر",
        }));
        setBuilderDraft(() => {
          const mk = (type, after_products) => ({
            id: uid(),
            type,
            settings: { ...defaultSettingsFor(type), after_products: Boolean(after_products) },
          });
          const slideTemplate = () => ({
            image: "",
            title: "",
            subtitle: "",
            link_url: "",
            link_label: "",
            product_id: "",
          });
          const fp = mk("featured_products", false);
          fp.settings.title = "اختيارات المحرر";
          fp.settings.subtitle = "منتجات مميزة تظهر أولاً";
          const cr = mk("carousel", false);
          cr.settings.title = "عروض الكاروسيل";
          cr.settings.subtitle = "صور جاهزة — بدّلها من التخصيص";
          cr.settings.hero_overlay_color = "#020617";
          cr.settings.hero_overlay_opacity = 0.55;
          const slides = Array.isArray(cr.settings.slides) ? cr.settings.slides.map(slideTemplate) : [slideTemplate(), slideTemplate(), slideTemplate()];
          while (slides.length < 3) slides.push(slideTemplate());
          slides[0] = { ...slides[0], image: CATALOG_TEMPLATE_MEDIA.slideDarkA };
          slides[1] = { ...slides[1], image: CATALOG_TEMPLATE_MEDIA.slideDarkB };
          slides[2] = { ...slides[2], image: CATALOG_TEMPLATE_MEDIA.slideDarkC };
          cr.settings.slides = slides.slice(0, 8);
          const split = mk("text_image_split", true);
          split.settings.title = "قسم نص + صورة";
          split.settings.subtitle = "لوّح بالهاتف أو التابلت";
          split.settings.body = "استخدم هذا القسم لشرح سياسات الاستبدال أو ضمان الجودة.";
          split.settings.images = [{ url: CATALOG_TEMPLATE_MEDIA.splitTech }];
          split.settings.style = { backgroundColor: "#0f172a", textColor: "#e2e8f0" };
          const pc = mk("products_carousel", true);
          pc.settings.title = "تمرير المنتجات";
          pc.settings.subtitle = "أضف معرفات منتجاتك من لوحة التخصيص";
          return mergeBuilderSections([fp, cr], [split, pc]).map((s, idx) => ({ ...s, order: idx }));
        });
        return;
      }

      if (k === "electronics_store") {
        setLayoutsEdit((p) => ({ ...p, show_top_hero: true, show_hero: true, show_features_bar: false }));
        patchExtras({
          global_page_bg_enabled: true,
          page_bg: "#f1f5f9",
          products_main_layout: "grid",
          filters_sidebar: true,
          category_nav: "top",
          products_heading_color: "#0f172a",
          products_subtitle_color: "#475569",
          catalog_font_stack: "ibm_plex_arabic",
          builder_page_container_width: "wide",
        });
        setStoreEdit((p) => ({
          ...p,
          primaryColor: "#2563eb",
          secondaryColor: "#f8fafc",
          footerBgColor: "#0f172a",
          footerTextColor: "#e2e8f0",
          heroUrl: CATALOG_TEMPLATE_MEDIA.heroTech,
          headerTitle: "متجر إلكترونيات",
          subheaderText: "هواتف، سماعات، وإكسسوارات تقنية",
          productsSectionTitle: "أحدث الإلكترونيات",
          productsSectionSubtitle: "فلتر بالسعر والجديد والعروض",
        }));
        setBuilderDraft(() => {
          const mk = (type, after_products) => ({
            id: uid(),
            type,
            settings: { ...defaultSettingsFor(type), after_products: Boolean(after_products) },
          });
          const slideTemplate = () => ({
            image: "",
            title: "",
            subtitle: "",
            link_url: "",
            link_label: "",
            product_id: "",
          });
          const fp = mk("featured_products", false);
          fp.settings.title = "الأكثر طلباً";
          fp.settings.subtitle = "مجموعة مختارة للواجهة الرئيسية";
          const cr = mk("carousel", false);
          cr.settings.title = "عروض تقنية";
          cr.settings.subtitle = "صور استوديو — عيّن روابطك من التخصيص";
          cr.settings.hero_overlay_color = "#0f172a";
          cr.settings.hero_overlay_opacity = 0.4;
          const slides = Array.isArray(cr.settings.slides) ? cr.settings.slides.map(slideTemplate) : [slideTemplate(), slideTemplate(), slideTemplate()];
          while (slides.length < 3) slides.push(slideTemplate());
          slides[0] = { ...slides[0], image: CATALOG_TEMPLATE_MEDIA.slideTechA };
          slides[1] = { ...slides[1], image: CATALOG_TEMPLATE_MEDIA.slideTechB };
          slides[2] = { ...slides[2], image: CATALOG_TEMPLATE_MEDIA.slideTechC };
          cr.settings.slides = slides.slice(0, 8);
          const split = mk("text_image_split", true);
          split.settings.title = "لماذا التقنية عندنا؟";
          split.settings.subtitle = "قطع أصلية وضمان";
          split.settings.body = "صف هنا سياسة الضمان، خيارات التقسيط، أو فروع الصيانة المعتمدة.";
          split.settings.images = [{ url: CATALOG_TEMPLATE_MEDIA.splitTech }];
          split.settings.style = { backgroundColor: "#ffffff", textColor: "#0f172a" };
          const pc = mk("products_carousel", true);
          pc.settings.title = "سلسلة منتجات";
          pc.settings.subtitle = "مرّر أفقياً بين بطاقات المنتجات";
          return mergeBuilderSections([fp, cr], [split, pc]).map((s, idx) => ({ ...s, order: idx }));
        });
        return;
      }

      if (k === "custom_store_fashion" || k === "urbanico_modern_fashion") {
        const isCustom = k === "custom_store_fashion";
        setLayoutsEdit((p) => ({ ...p, show_top_hero: true, show_hero: true, show_features_bar: false, product_columns: 4 }));
        patchExtras({
          page_bg: isCustom ? "#ffffff" : "#f5f5f0",
          products_main_layout: "grid",
          filters_sidebar: false,
          category_nav: "top",
          catalog_font_stack: "tajawal",
          builder_page_container_width: "boxed",
          hero_height_mode: "full",
          hero_content_align: "start",
          hero_bg_position_x: 50,
          hero_bg_position_y: 35,
          hero_subtitle_color: "rgba(255,255,255,0.92)",
          fashion_store: true,
          fashion_sharp_corners: true,
          products_card_border_radius: 0,
          product_image_shape: "square",
          products_card_style: "flat",
        });
        setStoreEdit((p) => ({
          ...p,
          primaryColor: "#111827",
          secondaryColor: isCustom ? "#ffffff" : "#f5f5f0",
          footerBgColor: "#111827",
          footerTextColor: "#f9fafb",
          heroUrl: isCustom ? CATALOG_TEMPLATE_MEDIA.heroFashion : CATALOG_TEMPLATE_MEDIA.heroUrbanico,
          headerTitle: isCustom ? "مجموعة الموسم الجديد" : "ارتقِ بأسلوبك اليومي",
          subheaderText: isCustom
            ? "اكتشف قطعاً خالدة مصممة للراحة والجودة"
            : "مجموعة الموسم الجديد — قطع خالدة للراحة والجودة والحياة العصرية",
          productsSectionTitle: isCustom ? "جميع المنتجات" : "منتجاتنا",
          productsSectionSubtitle: "تصفّح المجموعة الكاملة",
          ...(isCustom ? { storeName: "متجر مخصص" } : {}),
        }));
        const emptyCard = () => ({
          title: "",
          subtitle: "",
          details: "",
          image_url: "",
          image_position_x: 50,
          image_position_y: 50,
          image_mode: "",
          cover_grad_from: "",
          cover_grad_to: "",
          cover_grad_opacity: "",
          text_size: "",
          cta_label: "",
          cta_url: "",
          btn_variant: "",
          btn_bg: "",
          btn_fg: "",
          btn_border: "",
          btn_radius: "",
          card_bg_color: "",
          card_bg_opacity: "",
          card_text: "",
          card_border_color: "",
          card_border_opacity: "",
          card_shadow: "",
        });
        setBuilderDraft(() => {
          const mk = (type, after, patch = {}) => ({
            id: uid(),
            type,
            settings: { ...defaultSettingsFor(type), after_products: Boolean(after), ...patch },
          });
          const trust = mk("features_grid", false, {
            section_variant: "trust",
            radius: "none",
            card_radius: "none",
            cards_count: 4,
            card_height: "sm",
            style: { backgroundColor: "#ffffff", textColor: "#111827" },
            items: [
              { ...emptyCard(), title: "شحن مجاني", details: "للطلبات فوق ٧٥$", icon: "truck" },
              { ...emptyCard(), title: "دفع آمن", details: "دفع مشفّر ١٠٠٪", icon: "shield" },
              { ...emptyCard(), title: "توصيل سريع", details: "٢–٥ أيام عمل", icon: "delivery" },
              { ...emptyCard(), title: "دعم ٢٤/٧", details: "فريق دعم مخصّص", icon: "support" },
            ],
          });
          const cats = mk("features_grid", false, {
            section_variant: "categories",
            radius: "none",
            card_radius: "none",
            title: "تسوّق حسب الفئة",
            cards_count: 4,
            card_height: "lg",
            card_image_mode: "cover",
            card_shadow: "none",
            style: { backgroundColor: isCustom ? "#ffffff" : "#f5f5f0", textColor: "#111827" },
            items: isCustom
              ? [
                  { ...emptyCard(), title: "رجالي", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardHoodie, image_mode: "cover", cta_label: "تسوّق الآن", cta_url: "#products" },
                  { ...emptyCard(), title: "نسائي", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardTshirt, image_mode: "cover", cta_label: "تسوّق الآن", cta_url: "#products" },
                  { ...emptyCard(), title: "أطفال", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardPants, image_mode: "cover", cta_label: "تسوّق الآن", cta_url: "#products" },
                  { ...emptyCard(), title: "إكسسوارات", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardJacket, image_mode: "cover", cta_label: "تسوّق الآن", cta_url: "#products" },
                ]
              : [
                  { ...emptyCard(), title: "هوديز", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardHoodie, image_mode: "cover", cta_label: "تسوّق الآن", cta_url: "#products" },
                  { ...emptyCard(), title: "تيشيرتات", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardTshirt, image_mode: "cover", cta_label: "تسوّق الآن", cta_url: "#products" },
                  { ...emptyCard(), title: "بناطيل", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardPants, image_mode: "cover", cta_label: "تسوّق الآن", cta_url: "#products" },
                  { ...emptyCard(), title: "جاكيتات", details: "تسوّق الآن ←", image_url: CATALOG_TEMPLATE_MEDIA.cardJacket, image_mode: "cover", cta_label: "تسوّق الآن", cta_url: "#products" },
                ],
          });
          const fp = mk("featured_products", false, {
            title: "منتجات مميزة",
            subtitle: "اختيارات الموسم — جودة وراحة في كل قطعة",
            columns: 4,
          });
          const promo = mk("image_banner", false, {
            title: "عرض حصري — خصم حتى ٣٠٪",
            subtitle: "لفترة محدودة على منتجات مختارة",
            images: [{ url: CATALOG_TEMPLATE_MEDIA.bannerSale }],
            section_height: "lg",
            container_width: "full",
            style: { backgroundColor: "#111827", textColor: "#ffffff" },
          });
          const best = mk("products_carousel", true, { title: "الأكثر مبيعاً", subtitle: "المنتجات الأكثر طلباً هذا الأسبوع", products_per_slide: 4 });
          const reviews = mk("features_grid", true);
          reviews.settings.title = "ماذا يقول عملاؤنا";
          reviews.settings.cards_count = 3;
          reviews.settings.items = [
            { ...emptyCard(), title: "سارة م.", subtitle: "★★★★★", details: "«جودة القماش ممتازة والتصميم أنيق جداً.»" },
            { ...emptyCard(), title: "أحمد ك.", subtitle: "★★★★★", details: "«تجربة شراء سلسة وشحن سريع.»" },
            { ...emptyCard(), title: "ليلى ر.", subtitle: "★★★★★", details: "«أحب البساطة والألوان المحايدة.»" },
          ];
          const news = mk("text_image_split", true, {
            title: "انضم إلى نشرتنا البريدية",
            subtitle: "اشترك واحصل على خصم ١٠٪ على طلبك الأول",
            body: "أدخل بريدك في الفوتر — نرسل عروضاً حصرية وإطلاقات الموسم.",
            style: { backgroundColor: "#f5f5f0", textColor: "#111827" },
          });
          const story = mk("text_image_split", true, {
            title: "قصتنا",
            subtitle: "أزياء عصرية بروح بسيطة",
            body: "نؤمن بأن الأناقة اليومية تبدأ من قطع مريحة وخالدة.",
            images: [{ url: CATALOG_TEMPLATE_MEDIA.splitFashion }],
            style: { backgroundColor: "#f5f5f0", textColor: "#111827" },
          });
          return mergeBuilderSections([trust, cats, fp, promo], [best, reviews, news, story]).map((s, idx) => ({ ...s, order: idx }));
        });
        return;
      }

      // default minimal store
      // Minimal: hero + main products + footer only
      setLayoutsEdit((p) => ({ ...p, show_top_hero: true, show_hero: true, show_features_bar: false }));
      patchExtras({
        page_bg: "#ffffff",
        products_main_layout: "grid",
        filters_sidebar: false,
        category_nav: "top",
        catalog_font_stack: "tajawal",
        builder_page_container_width: "boxed",
      });
      // No extra builder sections in minimal
      keepOnlySectionTypes([]);
    },
    [setLayoutsEdit, setBuilderDraft, setStoreEdit]
  );

  const fetchCatalogThemeDetail = useCallback(
    async (themeId) => {
      const id = String(themeId || "").trim();
      if (!isCatalogThemeCatalogId(id)) return null;
      if (catalogThemeDetailCacheRef.current.has(id)) {
        return catalogThemeDetailCacheRef.current.get(id);
      }
      const res = await axios.get(studioApiUrl(`/catalog-themes/${id}`), authHeader);
      const data = res?.data || null;
      if (data) catalogThemeDetailCacheRef.current.set(id, data);
      return data;
    },
    [authHeader]
  );

  const applyCatalogThemeFromServer = useCallback(
    (payload) => {
      const theme = payload?.theme;
      const sections = Array.isArray(payload?.sections) ? payload.sections : [];
      if (!theme || typeof theme !== "object") return;
      setBuilderTheme(catalogPageThemeFromKatalojiRow(theme));
      if (theme.id) setSelectedCatalogThemeId(String(theme.id));

      const contactBefore = storeContactSnapshot(
        storeEdit,
        settingsSnapshotRef.current || {}
      );

      setStoreEdit((p) => ({
        ...p,
        primaryColor: theme.primary_color ?? p.primaryColor,
        secondaryColor: theme.secondary_color ?? p.secondaryColor,
        headerTitle: theme.header_title_ar ?? p.headerTitle,
        subheaderText: preferMerchantThemeValue(theme.subheader_text_ar, p.subheaderText),
        productsSectionTitle: theme.products_section_title_ar ?? p.productsSectionTitle,
        productsSectionSubtitle: theme.products_section_subtitle_ar ?? p.productsSectionSubtitle,
        productsHeadingAlign: theme.products_heading_align ?? p.productsHeadingAlign ?? "start",
        footerBgColor: theme.footer_bg_color ?? p.footerBgColor,
        footerTextColor: theme.footer_text_color ?? p.footerTextColor,
        phone: preferMerchantThemeValue(theme.phone_number, p.phone),
        email: preferMerchantThemeValue(theme.email, p.email),
        address: preferMerchantThemeValue(theme.address, p.address),
        facebook: preferMerchantThemeValue(theme.facebook_url, p.facebook),
        instagram: preferMerchantThemeValue(theme.instagram_url, p.instagram),
        heroUrl: theme.hero_url ?? p.heroUrl,
        bgType: theme.bg_type ?? p.bgType,
        featureTitle1: theme.feature_title_1 ?? p.featureTitle1,
        featureTitle2: theme.feature_title_2 ?? p.featureTitle2,
        featureTitle3: theme.feature_title_3 ?? p.featureTitle3,
        featureDesc1: theme.feature_description_1 ?? p.featureDesc1,
        featureDesc2: theme.feature_description_2 ?? p.featureDesc2,
        featureDesc3: theme.feature_description_3 ?? p.featureDesc3,
        featuresBarBg: theme.features_bar_bg ?? p.featuresBarBg,
        featuresBarText: theme.features_bar_text ?? p.featuresBarText,
        featuresBarAccent: theme.features_bar_accent ?? p.featuresBarAccent,
      }));

      setSettings((prev) => ({
        ...prev,
        whatsappNumber: preferMerchantThemeValue(theme.whatsapp_number, prev.whatsappNumber),
        phone: preferMerchantThemeValue(theme.phone_number, prev.phone),
        email: preferMerchantThemeValue(theme.email, prev.email),
        address: preferMerchantThemeValue(theme.address, prev.address),
        facebook: preferMerchantThemeValue(theme.facebook_url, prev.facebook),
        instagram: preferMerchantThemeValue(theme.instagram_url, prev.instagram),
        subheaderText: preferMerchantThemeValue(theme.subheader_text_ar, prev.subheaderText),
        footerBgColor: theme.footer_bg_color ?? prev.footerBgColor,
        footerTextColor: theme.footer_text_color ?? prev.footerTextColor,
      }));

      const rawLx =
        theme.catalog_layout_extras && typeof theme.catalog_layout_extras === "object"
          ? stripStudioTourFlagsFromLayoutExtras(theme.catalog_layout_extras)
          : null;
      const lx = { ...DEFAULT_CATALOG_LAYOUT_EXTRAS, ...(rawLx || {}) };
      migrateLegacyLayoutExtras(lx, rawLx);
      const topHero = theme.show_top_hero ?? theme.show_hero ?? true;
      setLayoutsEdit({
        show_hero: topHero,
        show_grid: theme.show_grid ?? true,
        show_top_hero: topHero,
        show_features_bar: theme.show_features_bar ?? true,
        product_columns: theme.product_columns ?? 3,
        best_products: Array.isArray(theme.best_products) ? theme.best_products : [],
        catalog_sections: Array.isArray(theme.catalog_sections) ? theme.catalog_sections : [],
        catalog_layout_extras: lx,
      });

      const loaded = sections
        .map((sec, idx) => {
          const raw = sec.settings || {};
          const hydratedRaw = hydrateBuilderSectionStoreContact(raw, contactBefore);
          return {
            id: uid(),
            type: sec.type,
            order: Number.isFinite(sec.order) ? sec.order : idx,
            settings: {
              ...BUILDER_FRAME_DEFAULTS,
              ...builderFontDefaultsForType(sec.type),
              ...hydratedRaw,
              style: {
                backgroundColor: "#ffffff",
                textColor: "#0f172a",
                ...(hydratedRaw.style || {}),
              },
            },
          };
        })
        .sort((a, b2) => (a.order ?? 0) - (b2.order ?? 0));
      const { before, after } = partitionBuilderSections(loaded);
      setBuilderDraft(mergeBuilderSections(before, after));
    },
    [setBuilderTheme, setStoreEdit, setLayoutsEdit, setBuilderDraft, storeEdit]
  );

  const applyPersistBundleObject = useCallback((o) => {
    if (!o || typeof o !== "object") return;
    skipNextHistoryPushRef.current = true;
    try {
      if (o.storeEdit && typeof o.storeEdit === "object") setStoreEdit(JSON.parse(JSON.stringify(o.storeEdit)));
      if (o.layoutsEdit && typeof o.layoutsEdit === "object") setLayoutsEdit(JSON.parse(JSON.stringify(o.layoutsEdit)));
      if (Array.isArray(o.builderDraft)) setBuilderDraft(JSON.parse(JSON.stringify(o.builderDraft)));
      if (o.builderTheme != null && o.builderTheme !== "") setBuilderTheme(String(o.builderTheme));
    } catch {
      /* ignore */
    }
  }, []);

  const beginStudioThemePreview = useCallback(
    async (nextKey) => {
      const snap = serializeStudioPersistBundle(studioBundleRef.current);
      if (isCatalogThemeCatalogId(nextKey)) {
        setStudioThemePreview({ snap, key: String(nextKey) });
        try {
          const data = await fetchCatalogThemeDetail(nextKey);
          if (data) applyCatalogThemeFromServer(data);
        } catch (e) {
          setStudioToast({ message: formatCustomizerSaveError(e) });
          try {
            applyPersistBundleObject(JSON.parse(snap));
          } catch {
            /* ignore */
          }
          setStudioThemePreview(null);
        }
        return;
      }
      const k = String(nextKey || "minimal").toLowerCase();
      setStudioThemePreview({ snap, key: k });
      setBuilderTheme(k);
      setSelectedCatalogThemeId("");
      applyThemePreset(k);
    },
    [applyThemePreset, applyCatalogThemeFromServer, fetchCatalogThemeDetail, applyPersistBundleObject]
  );

  const cancelStudioThemePreview = useCallback(() => {
    setStudioThemePreview((cur) => {
      if (cur?.snap) {
        try {
          applyPersistBundleObject(JSON.parse(cur.snap));
        } catch {
          /* ignore */
        }
      }
      return null;
    });
  }, [applyPersistBundleObject]);

  const restoreLocalStudioDraft = useCallback(() => {
    const key = studioLocalDraftStorageKey(userId);
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const { snap } = JSON.parse(raw);
      if (typeof snap !== "string") return;
      const o = JSON.parse(snap);
      if (o.storeEdit && typeof o.storeEdit === "object") setStoreEdit(o.storeEdit);
      if (o.layoutsEdit && typeof o.layoutsEdit === "object") setLayoutsEdit(o.layoutsEdit);
      if (Array.isArray(o.builderDraft)) setBuilderDraft(o.builderDraft);
      if (o.builderTheme) setBuilderTheme(String(o.builderTheme));
      try {
        pendingHistoryLabelRef.current =
          localStorage.getItem("catalogStudioLanguage") === "en" ? "Draft restored" : "استعادة مسودة";
      } catch {
        pendingHistoryLabelRef.current = "استعادة مسودة";
      }
      setLocalDraftBanner(false);
    } catch {
      /* ignore */
    }
  }, [userId]);

  const jumpStudioHistoryIndex = useCallback((nextIndex) => {
    const h = studioHistoryRef.current;
    if (!Number.isFinite(nextIndex) || nextIndex < 0 || nextIndex >= h.entries.length) return;
    const snap = h.entries[nextIndex].snapshot;
    skipNextHistoryPushRef.current = true;
    setStoreEdit(JSON.parse(JSON.stringify(snap.storeEdit)));
    setLayoutsEdit(JSON.parse(JSON.stringify(snap.layoutsEdit)));
    setBuilderDraft(JSON.parse(JSON.stringify(snap.builderDraft || [])));
    setBuilderTheme(String(snap.builderTheme || "minimal"));
    h.index = nextIndex;
    setStudioHistoryTick((x) => x + 1);
    setStudioHistoryMenuOpen(false);
  }, []);

  const undoStudioHistory = useCallback(() => {
    const h = studioHistoryRef.current;
    if (h.index <= 0) return;
    jumpStudioHistoryIndex(h.index - 1);
  }, [jumpStudioHistoryIndex]);

  const redoStudioHistory = useCallback(() => {
    const h = studioHistoryRef.current;
    if (h.index < 0 || h.index >= h.entries.length - 1) return;
    jumpStudioHistoryIndex(h.index + 1);
  }, [jumpStudioHistoryIndex]);

  const studioNavigateBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      Boolean(canEdit) &&
      Boolean(studioUnsaved) &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  );

  useLayoutEffect(() => {
    if (!canEdit || builderLoading) return;
    if (historyInitializedRef.current) return;
    historyInitializedRef.current = true;
    const b = studioBundleRef.current;
    const snap = cloneStudioPersistSnapshot(b);
    const h = studioHistoryRef.current;
    let label = "تحميل من الخادم";
    try {
      if (localStorage.getItem("catalogStudioLanguage") === "en") label = "Loaded from server";
    } catch {
      /* ignore */
    }
    h.entries = [
      {
        id: uid(),
        timestamp: Date.now(),
        label,
        snapshot: snap,
      },
    ];
    h.index = 0;
    setStudioHistoryTick((x) => x + 1);
  }, [canEdit, builderLoading]);

  useEffect(() => {
    if (!canEdit || builderLoading) return;
    if (!historyInitializedRef.current) return;
    if (skipNextHistoryPushRef.current) {
      skipNextHistoryPushRef.current = false;
      return;
    }
    const id = window.setTimeout(() => {
      const b = studioBundleRef.current;
      const serialized = serializeStudioPersistBundle(b);
      const h = studioHistoryRef.current;
      const cur = h.entries[h.index];
      if (!cur) return;
      if (serializeStudioPersistBundle(cur.snapshot) === serialized) return;
      let label = pendingHistoryLabelRef.current;
      pendingHistoryLabelRef.current = null;
      if (!label) {
        try {
          label = localStorage.getItem("catalogStudioLanguage") === "en" ? "Edit" : "تعديل";
        } catch {
          label = "تعديل";
        }
      }
      h.entries = h.entries.slice(0, h.index + 1);
      h.entries.push({
        id: uid(),
        timestamp: Date.now(),
        label,
        snapshot: cloneStudioPersistSnapshot(b),
      });
      h.index = h.entries.length - 1;
      while (h.entries.length > STUDIO_HISTORY_MAX) {
        h.entries.shift();
        h.index--;
      }
      setStudioHistoryTick((x) => x + 1);
    }, 500);
    return () => window.clearTimeout(id);
  }, [canEdit, builderLoading, storeEdit, layoutsEdit, builderDraft, builderTheme]);

  useEffect(() => {
    if (!canEdit) return;
    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;
      const t = e.target;
      const inField = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      const mod = e.metaKey || e.ctrlKey;
      if (e.key === "Escape") {
        if (studioShortcutsOpen) {
          e.preventDefault();
          setStudioShortcutsOpen(false);
          return;
        }
      }
      if (inField) {
        if (mod && (e.key === "s" || e.key === "S")) {
          e.preventDefault();
          saveAllCustomizer();
        }
        return;
      }
      if (mod && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        saveAllCustomizer();
        return;
      }
      if (mod && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        setCustomizerOpen((p) => !p);
        return;
      }
      if (!mod && (e.key === "?" || (e.shiftKey && e.key === "/"))) {
        e.preventDefault();
        setStudioShortcutsOpen((o) => !o);
        return;
      }
      if (mod && (e.key === "z" || e.key === "Z") && !e.shiftKey) {
        e.preventDefault();
        undoStudioHistory();
      } else if (mod && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redoStudioHistory();
      } else if (mod && (e.key === "z" || e.key === "Z") && e.shiftKey) {
        e.preventDefault();
        redoStudioHistory();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canEdit, undoStudioHistory, redoStudioHistory, saveAllCustomizer, studioShortcutsOpen]);

  useEffect(() => {
    if (!studioHistoryMenuOpen) return;
    const onDown = (ev) => {
      if (studioHistoryUiRef.current && studioHistoryUiRef.current.contains(ev.target)) return;
      setStudioHistoryMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [studioHistoryMenuOpen]);

  useEffect(() => {
    if (!studioDocMenuOpen) return undefined;
    const onDown = (ev) => {
      if (studioDocMenuWrapRef.current && studioDocMenuWrapRef.current.contains(ev.target)) return;
      setStudioDocMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [studioDocMenuOpen]);

  function AddBetweenBar({ index, zone = "before" }) {
    if (!canEdit) return null;
    return (
      <div
        style={{
          position: "relative",
          height: 26,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "6px 0",
        }}
      >
        <div style={{ position: "absolute", left: 0, right: 0, height: 1, background: "rgba(15,23,42,0.10)" }} />
        <button
          type="button"
          onClick={() => openSectionTypePicker({ zone, index })}
          style={{
            position: "relative",
            border: `1px solid ${storeEdit.primaryColor}55`,
            background: "#fff",
            color: "#111827",
            borderRadius: 999,
            padding: "6px 10px",
            fontWeight: 900,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          }}
          title="إضافة سكشن هنا"
        >
          <Plus size={16} />
          إضافة سكشن
        </button>
      </div>
    );
  }

  function CarouselSection({
    sec,
    st,
    fg,
    frame,
    accentColor,
    headingAlign,
    titleFontPx,
    subtitleFontPx,
    productsById,
    mapProduct,
    onSelectProduct,
    onScrollToCatalogProducts,
  }) {
    const slides = Array.isArray(st.slides) ? st.slides : [];
    const safeSlides = slides.length ? slides : [{}];
    const [idx, setIdx] = useState(0);
    const n = Math.max(1, safeSlides.length);
    let carouselVariant = String(st.section_variant || "").toLowerCase();
    if (carouselVariant === "instagram") carouselVariant = "brands";

    useEffect(() => {
      if (n <= 1) return undefined;
      const id = setInterval(() => {
        setIdx((p) => (p + 1) % n);
      }, 10000);
      return () => clearInterval(id);
    }, [n]);

    if (carouselVariant === "testimonials") {
      const haT = headingAlign || "start";
      const rtlT = (settings.layoutDirection || "rtl") === "rtl";
      const goPrevT = () => setIdx((p) => (p - 1 + n) % n);
      const goNextT = () => setIdx((p) => (p + 1) % n);
      const slideTxT = `-${(idx * 100) / n}%`;
      const tBg = String((st?.style || {}).backgroundColor || "").trim() || "#ffffff";
      return (
        <section
          key={sec.id}
          className="catalog-builder-carousel catalog-carousel--testimonials catalog-section--testimonials catalog-builder-sec-surface"
          data-section-height={(st.section_height || "md").toString()}
          style={{
            ...builderSectionSurfaceStyle(st, frame, tBg, fg),
            color: fg,
            padding: 0,
            direction: settings.layoutDirection || "rtl",
            borderRadius: frame.radius,
            border: frame.border,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: frame.pad, boxSizing: "border-box" }}>
            {String(st.title || "").trim() ? (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: titleFontPx ?? 22, fontWeight: 950, textAlign: haT }}>{st.title}</div>
                {st.subtitle ? (
                  <div style={{ marginTop: 8, fontSize: subtitleFontPx ?? 15, opacity: 0.75, textAlign: haT }}>{st.subtitle}</div>
                ) : null}
              </div>
            ) : null}
            <div className="catalog-testimonials-track-wrap" style={{ position: "relative", padding: "0 48px" }}>
              <div style={{ overflow: "hidden", direction: "ltr" }}>
                <div
                  style={{
                    display: "flex",
                    width: `${n * 100}%`,
                    transform: `translateX(${slideTxT})`,
                    transition: "transform 0.5s ease",
                  }}
                >
                  {safeSlides.map((sl, si) => (
                    <div key={si} style={{ flex: `0 0 ${100 / n}%`, width: `${100 / n}%`, flexShrink: 0, padding: "0 6px", boxSizing: "border-box" }}>
                      <div className="catalog-testimonial-slide">
                        <div className="catalog-testimonial-stars">{String(sl.subtitle || "★★★★★")}</div>
                        <div className="catalog-testimonial-quote">
                          {String(sl.body || "").trim() || "«تجربة رائعة مع المتجر.»"}
                        </div>
                        <div className="catalog-testimonial-author">{String(sl.title || "").trim() || "عميل"}</div>
                        {sl.subtitle2 ? <div className="catalog-testimonial-meta">{sl.subtitle2}</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {n > 1 ? (
                <>
                  <button type="button" className="catalog-carousel-nav catalog-carousel-nav--prev" aria-label="السابق" onClick={rtlT ? goNextT : goPrevT} style={{ position: "absolute", top: "50%", insetInlineStart: 0, transform: "translateY(-50%)", zIndex: 3, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 999, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(15,23,42,0.08)" }}>
                    {rtlT ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
                  </button>
                  <button type="button" className="catalog-carousel-nav catalog-carousel-nav--next" aria-label="التالي" onClick={rtlT ? goPrevT : goNextT} style={{ position: "absolute", top: "50%", insetInlineEnd: 0, transform: "translateY(-50%)", zIndex: 3, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 999, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(15,23,42,0.08)" }}>
                    {rtlT ? <ChevronRight size={22} /> : <ChevronLeft size={22} />}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </section>
      );
    }

    if (carouselVariant === "brands") {
      const haB = headingAlign || "start";
      const openBrandsSidebar = () => openStudioBuilderSectionForm(sec.id);
      const patchBrands = (patch) => patchBuilderSectionById(sec.id, patch);
      const patchBrandSlide = (si, slidePatch) => {
        const cur = Array.isArray(st.slides) ? [...st.slides] : [];
        while (cur.length <= si) cur.push({});
        cur[si] = { ...(cur[si] || {}), ...slidePatch };
        patchBrands({ slides: cur });
      };
      const logoPx = brandLogoSizePx(st);
      const brandsBg = String((st?.style || {}).backgroundColor || "").trim() || "#ffffff";
      return (
        <section
          key={sec.id}
          className="catalog-section--brands catalog-builder-sec-surface"
          data-section-height={(st.section_height || "md").toString()}
          style={{
            ...builderSectionSurfaceStyle(st, frame, brandsBg, fg),
            "--brand-logo-size": `${logoPx}px`,
          }}
        >
          {(String(st.title || "").trim() || String(st.subtitle || "").trim() || canEdit) ? (
            <div className="catalog-pro-section-head" style={{ textAlign: haB, marginBottom: 16 }}>
              {(String(st.title || "").trim() || canEdit) ? (
                <h2>
                  {canEdit ? (
                    <EditableInline value={st.title || ""} placeholder="عنوان القسم" onChange={(v) => patchBrands({ title: v })} onOpenSidebar={openBrandsSidebar} textStyle={{ fontWeight: 950 }} />
                  ) : (
                    String(st.title).trim()
                  )}
                </h2>
              ) : null}
              {(String(st.subtitle || "").trim() || canEdit) ? (
                <p>
                  {canEdit ? (
                    <EditableInline value={st.subtitle || ""} placeholder="وصف" onChange={(v) => patchBrands({ subtitle: v })} onOpenSidebar={openBrandsSidebar} />
                  ) : (
                    st.subtitle
                  )}
                </p>
              ) : null}
            </div>
          ) : null}
          <div
            className="catalog-brands-row"
            style={{
              justifyContent:
                (String(st.slides_content_align || st.cards_align || "center").toLowerCase() === "end"
                  ? "flex-end"
                  : String(st.slides_content_align || st.cards_align || "center").toLowerCase() === "start"
                    ? "flex-start"
                    : "center"),
            }}
          >
            {safeSlides.map((sl, si) => {
              const img = String(sl.image || "").trim();
              return (
                <div key={si} className="catalog-brand-chip">
                  {img ? <img src={img} alt={String(sl.title || "")} width={logoPx} height={logoPx} loading="lazy" decoding="async" /> : null}
                  {(String(sl.title || "").trim() || canEdit) ? (
                    <span>
                      {canEdit ? (
                        <EditableInline value={sl.title || ""} placeholder="اسم / نص" onChange={(v) => patchBrandSlide(si, { title: v })} onOpenSidebar={openBrandsSidebar} />
                      ) : (
                        String(sl.title).trim()
                      )}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      );
    }

    const heightKey = (st.section_height || "md").toString();
    const slideH = carouselSlideHeightPx(heightKey, carouselVariant);
    const rad = Number(frame?.radius ?? 0);
    const innerR = rad <= 0 ? 0 : Math.max(0, rad - 4);
    const rtl = (settings.layoutDirection || "rtl") === "rtl";
    const goPrev = () => setIdx((p) => (p - 1 + n) % n);
    const goNext = () => setIdx((p) => (p + 1) % n);
    const leftAction = rtl ? goPrev : goNext;
    const rightAction = rtl ? goNext : goPrev;

    const dotColor = accentColor || catalogThemePrimary || "#ffc200";
    const haRaw = String(st.slides_content_align || headingAlign || "start").toLowerCase();
    const ha = haRaw === "center" || haRaw === "end" ? haRaw : "start";
    const tPx = titleFontPx ?? 22;
    const sPx = subtitleFontPx ?? 15;
    const showHeading = Boolean(String(st.title || "").trim() || String(st.subtitle || "").trim());
    const slideTx = `-${(idx * 100) / n}%`;

    const resolveSlide = (sl) => {
      const pidRaw = sl?.product_id;
      const pidStr = pidRaw != null && pidRaw !== "" ? String(pidRaw).trim() : "";
      if (pidStr && productsById) {
        const p = productsById.get(Number(pidStr)) ?? productsById.get(pidStr);
        if (p) {
          const img0 = p.images && p.images[0];
          const image =
            p.image ||
            (typeof img0 === "string" ? img0 : img0?.image_url) ||
            p.displayImage ||
            "";
          const name = String(p.name_ar || p.name || "").trim();
          return {
            kind: "product",
            image,
            name,
            product: p,
            linkUrl: String(sl.link_url || "").trim(),
            linkLabel: String(sl.link_label || "").trim(),
          };
        }
      }
      return {
        kind: "image",
        image: String(sl.image || "").trim(),
        linkUrl: String(sl.link_url || "").trim(),
        linkLabel: String(sl.link_label || "").trim(),
      };
    };

    const fullBleed = frame?.widthKey === "full";
    const slideChromeBorder = fullBleed ? frame.border || "1px solid rgba(15,23,42,0.08)" : "none";

    return (
      <section
        key={sec.id}
        className="catalog-builder-carousel"
        style={{
          ...(fullBleed
            ? { border: "none", borderRadius: 0, overflow: "visible" }
            : { border: frame.border, borderRadius: rad, overflow: "hidden" }),
          background: "transparent",
          color: fg,
          padding: 0,
          margin: 0,
          direction: settings.layoutDirection || "rtl",
        }}
      >
        <div
          style={
            fullBleed
              ? { padding: 0, boxSizing: "border-box" }
              : { padding: frame.pad, boxSizing: "border-box" }
          }
        >
        {showHeading ? (
          <div
            style={{
              padding: fullBleed ? `0 ${Math.min(frame.pad || 0, 24)}px` : 0,
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: tPx, fontWeight: 950, textAlign: ha, lineHeight: 1.2 }}>
              {st.title}
            </div>
            {st.subtitle ? (
              <div style={{ marginTop: 8, fontSize: sPx, opacity: 0.9, lineHeight: 1.7, textAlign: ha }}>
                {st.subtitle}
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          className={frame?.widthKey === "full" ? "catalog-carousel-bleed catalog-carousel-bleed--flush" : undefined}
          style={{
            position: "relative",
            boxSizing: "border-box",
            background: "transparent",
            padding: 0,
          }}
        >
          <div
            className="catalog-carousel-chrome"
            style={{
              borderRadius: innerR,
              overflow: "hidden",
              border: slideChromeBorder,
              background: "#fff",
              position: "relative",
              boxShadow: "none",
            }}
          >
            <div style={{ overflow: "hidden", width: "100%", direction: "ltr" }}>
              <div
                className="catalog-carousel-track"
                style={{
                  display: "flex",
                  width: `${n * 100}%`,
                  transform: `translateX(${slideTx})`,
                  transition: "transform 0.55s cubic-bezier(0.25, 0.85, 0.35, 1)",
                }}
              >
                {safeSlides.map((sl, si) => {
                  const slice = 100 / n;
                  const r = resolveSlide(sl);
                  const showImg = Boolean(r.image);
                  const slideTitle =
                    String(sl.title || "").trim() || (r.kind === "product" ? String(r.name || "").trim() : "");
                  const slideSubtitle = String(sl.subtitle || "").trim();
                  const showHeroCopy = Boolean(slideTitle || slideSubtitle);
                  const defaultCtaLabel = "اكتشف المجموعة";
                  const label = (r.linkLabel && r.linkLabel.trim()) || defaultCtaLabel;
                  const showHeroCta = st.hero_show_cta !== false;
                  const overlayColor = String(st.hero_overlay_color ?? "#0f172a").trim();
                  const overlayOpacity = st.hero_overlay_opacity != null ? Number(st.hero_overlay_opacity) : 0.5;
                  const slideScrimBg = applyOpacityToColor(overlayColor, overlayOpacity);

                  const ctaHeroClass = "hero-action-btn catalog-carousel-slide-cta";
                  const ctaHeroStyle = {
                    backgroundColor: "#ffffff",
                    color: "#0f172a",
                    border: "1px solid rgba(15, 23, 42, 0.14)",
                    borderRadius: 10,
                    pointerEvents: "auto",
                    fontWeight: 700,
                  };

                  const ctaEl =
                    showHeroCta && onScrollToCatalogProducts ? (
                      <button
                        type="button"
                        className={ctaHeroClass}
                        style={ctaHeroStyle}
                        onClick={() => onScrollToCatalogProducts()}
                      >
                        {label}
                      </button>
                    ) : null;

                  const showHeroStack = Boolean(showHeroCopy || ctaEl);
                  const heroInsetTop = Math.max(0, Math.min(240, Number(st.hero_inset_top) || 0));
                  const heroInsetInline = Math.max(0, Math.min(240, Number(st.hero_inset_inline) || 0));
                  const heroPadBlock = "clamp(20px, 5vw, 48px)";
                  const heroPadInline = "clamp(18px, 6vw, 56px)";
                  /** صف (row): alignItems = وسط عمودي دائماً؛ justifyContent = أفقي حسب المحاذاة (في RTL يمين = flex-start) */
                  const heroRowJustify = ha === "center" ? "center" : ha === "end" ? "flex-end" : "flex-start";
                  const heroTextAlign = ha === "center" ? "center" : ha === "end" ? "end" : "start";
                  const heroCtaJustify = ha === "center" ? "center" : ha === "end" ? "flex-end" : "flex-start";
                  const stackBoxStyle = {
                    width: "min(92%, 560px)",
                    maxWidth: "min(92%, 560px)",
                    flexShrink: 0,
                    textAlign: heroTextAlign,
                  };

                  const heroStackBlock = showHeroStack ? (
                    <>
                      <div
                        className="catalog-carousel-slide-hero-scrim"
                        aria-hidden
                        style={{
                          position: "absolute",
                          inset: 0,
                          zIndex: 1,
                          background: slideScrimBg,
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        className="catalog-carousel-slide-hero-stack"
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0,
                          zIndex: 2,
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "nowrap",
                          alignItems: "center",
                          justifyContent: heroRowJustify,
                          boxSizing: "border-box",
                          width: "100%",
                          minHeight: slideH,
                          height: slideH,
                          paddingTop: `calc(${heroPadBlock} + ${heroInsetTop}px)`,
                          paddingBottom: `calc(${heroPadBlock} + ${heroInsetTop}px)`,
                          paddingInlineStart: `calc(${heroPadInline} + ${heroInsetInline}px)`,
                          paddingInlineEnd: heroPadInline,
                          direction: settings.layoutDirection || "rtl",
                          pointerEvents: "none",
                        }}
                      >
                        <div style={{ ...stackBoxStyle, pointerEvents: "auto" }}>
                          {slideTitle ? <h2 className="hero-main-h1 catalog-carousel-slide-hero-h1">{slideTitle}</h2> : null}
                          {slideSubtitle ? <p className="hero-sub-p catalog-carousel-slide-hero-sub-p">{slideSubtitle}</p> : null}
                          {ctaEl ? (
                            <div
                              className="catalog-carousel-slide-cta-wrap"
                              style={{ display: "flex", justifyContent: heroCtaJustify }}
                            >
                              {ctaEl}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </>
                  ) : null;

                  return (
                    <div
                      key={si}
                      style={{
                        position: "relative",
                        flex: `0 0 ${slice}%`,
                        width: `${slice}%`,
                        height: slideH,
                        minHeight: slideH,
                        background: "#e2e8f0",
                      }}
                    >
                      {showImg ? (
                        <img
                          src={r.image}
                          alt={String(sl.title || "").trim()}
                          className="catalog-carousel-slide-img"
                          width={1600}
                          height={slideH}
                          decoding="async"
                          style={{
                            width: "100%",
                            height: slideH,
                            objectFit: "cover",
                            objectPosition: heroBackgroundPositionFromXY(
                              sl.image_position_x,
                              sl.image_position_y
                            ),
                            display: "block",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            height: slideH,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#64748b",
                            fontSize: 12,
                          }}
                        >
                          صورة أو منتج
                        </div>
                      )}
                      {heroStackBlock}
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              className="catalog-carousel-nav-btn catalog-carousel-nav-btn--start"
              onClick={leftAction}
              aria-label={rtl ? "next" : "prev"}
            >
              <ChevronLeft size={28} strokeWidth={2.25} aria-hidden />
            </button>
            <button
              type="button"
              className="catalog-carousel-nav-btn catalog-carousel-nav-btn--end"
              onClick={rightAction}
              aria-label={rtl ? "prev" : "next"}
            >
              <ChevronRight size={28} strokeWidth={2.25} aria-hidden />
            </button>
          </div>
        </div>

        <div
          style={{
            padding: "12px 0 0",
            display: "flex",
            justifyContent: "center",
            gap: 8,
            flexDirection: rtl ? "row-reverse" : "row",
            background: "transparent",
          }}
        >
          {safeSlides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`go ${i + 1}`}
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                border: i === idx ? `2px solid ${dotColor}` : "2px solid rgba(15,23,42,0.22)",
                background: i === idx ? dotColor : "transparent",
                cursor: "pointer",
                padding: 0,
              }}
            />
          ))}
        </div>
        </div>
      </section>
    );
  }

  const ProductsCarouselSection = useCallback(function ProductsCarouselSection({
    st,
    fg,
    frame,
    accentColor,
    secondaryColor,
    headingAlign,
    titleFontPx,
    subtitleFontPx,
    productsById,
    mapProduct,
    onSelectProduct,
    productsSettled = true,
    layoutDirection,
    productsSectionDesign,
    catalogThemePrimary,
    catalogThemeSecondary,
    catalogVisualTheme,
  }) {
    const ids = Array.isArray(st.product_ids) ? st.product_ids : [];
    /** حد أقصى اختياري للأعمدة؛ 0 = نفس أعمدة قسم المنتجات */
    const perRaw = Number(st.products_per_slide);
    const perCap = Number.isFinite(perRaw) && perRaw >= 1 ? Math.min(6, Math.round(perRaw)) : 0;
    const picked = ids
      .map((id) => productsById?.get(Number(id)) ?? productsById?.get(id))
      .filter(Boolean);
    const waiting = !productsSettled && picked.length < ids.length;
    const count = waiting ? ids.length : picked.length;
    const rtl = (layoutDirection || "rtl") === "rtl";
    const dirSign = rtl ? -1 : 1;
    const viewportRef = useRef(null);
    const pageRef = useRef(0);
    const hoverRef = useRef(false);
    const [page, setPage] = useState(0);
    const [pages, setPages] = useState(1);

    const measure = useCallback(() => {
      const vp = viewportRef.current;
      const track = vp?.firstElementChild;
      const cell = track?.firstElementChild;
      if (!vp || !track || !cell) return null;
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const step = cell.getBoundingClientRect().width + gap;
      if (!(step > 0)) return null;
      const cols = Math.max(1, Math.round((vp.clientWidth + gap) / step));
      return { pageWidth: step * cols, pagesTotal: Math.max(1, Math.ceil(count / cols)) };
    }, [count]);

    const sync = useCallback(() => {
      const vp = viewportRef.current;
      const m = measure();
      if (!vp || !m) {
        setPages(1);
        setPage(0);
        pageRef.current = 0;
        return;
      }
      const maxScroll = vp.scrollWidth - vp.clientWidth;
      const pos = Math.abs(vp.scrollLeft);
      const p = pos >= maxScroll - 2 && maxScroll > 0 ? m.pagesTotal - 1 : Math.round(pos / m.pageWidth);
      const clamped = Math.max(0, Math.min(m.pagesTotal - 1, p));
      pageRef.current = clamped;
      setPages(m.pagesTotal);
      setPage(clamped);
    }, [measure]);

    useEffect(() => {
      sync();
      const vp = viewportRef.current;
      if (!vp || typeof ResizeObserver === "undefined") return undefined;
      const ro = new ResizeObserver(() => sync());
      ro.observe(vp);
      return () => ro.disconnect();
    }, [sync]);

    const goTo = useCallback(
      (p) => {
        const vp = viewportRef.current;
        const m = measure();
        if (!vp || !m) return;
        const target = ((p % m.pagesTotal) + m.pagesTotal) % m.pagesTotal;
        vp.scrollTo({ left: dirSign * target * m.pageWidth, behavior: "smooth" });
      },
      [measure, dirSign]
    );
    const goNext = () => goTo(pageRef.current + 1);
    const goPrev = () => goTo(pageRef.current - 1);
    const leftAction = rtl ? goNext : goPrev;
    const rightAction = rtl ? goPrev : goNext;

    useEffect(() => {
      if (pages <= 1) return undefined;
      const id = setInterval(() => {
        if (!hoverRef.current) goTo(pageRef.current + 1);
      }, 10000);
      return () => clearInterval(id);
    }, [pages, goTo]);

    const dotColor = accentColor || catalogThemePrimary || "#ffc200";
    const ha = headingAlign || "start";
    const tPx = { sm: 24, md: 30, lg: 36, xl: 44 }[String(st.title_size || "md")] ?? titleFontPx ?? 30;
    const sPx = { sm: 15, md: 17, lg: 20 }[String(st.subtitle_size || "md")] ?? subtitleFontPx ?? 17;
    const titleFont = `clamp(${Math.round(tPx * 0.72)}px, 5.2vw, ${tPx}px)`;
    const subFont = `clamp(${Math.max(13, Math.round(sPx * 0.85))}px, 3.6vw, ${sPx}px)`;
    const showHeading = Boolean(String(st.title || "").trim() || String(st.subtitle || "").trim());
    const primaryCol = accentColor || "#ffc200";
    const secondaryCol = secondaryColor || "#ff5b00";
    const textColor = String(st?.style?.textColor || "").trim() || (fg || "#0f172a");
    const customBg = String(st?.style?.backgroundColor || "").trim();
    const rad = Number(frame?.radius ?? 0);
    const innerR = rad <= 0 ? 0 : Math.max(0, rad - 4);
    const fullBleed = frame?.widthKey === "full";
    const d = productsSectionDesign;

    return (
      <section
        className="catalog-builder-carousel catalog-products-carousel-section"
        style={{
          ...(fullBleed ? { border: "none", borderRadius: 0 } : { border: frame.border, borderRadius: rad }),
          overflow: "visible",
          background: "transparent",
          color: textColor,
          padding: 0,
          margin: 0,
          direction: layoutDirection || "rtl",
          ["--products-carousel-nav-bg"]: primaryCol,
          ["--products-carousel-nav-fg"]: pickReadableIconColor(primaryCol, secondaryCol, "#0f172a"),
        }}
      >
        <div style={{ padding: fullBleed ? `0 ${Math.min(frame.pad || 0, 24)}px` : frame.pad, boxSizing: "border-box" }}>
          {showHeading ? (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: titleFont, fontWeight: 950, textAlign: ha, lineHeight: 1.2 }}>{st.title}</div>
              {st.subtitle ? (
                <div style={{ marginTop: 8, fontSize: subFont, opacity: 0.85, lineHeight: 1.6, textAlign: ha }}>
                  {st.subtitle}
                </div>
              ) : null}
            </div>
          ) : null}

          <div
            className="products-section-flat catalog-products-carousel-cards"
            dir={layoutDirection || "rtl"}
            data-product-cards={d.cardStyle !== "default" ? d.cardStyle : undefined}
            data-product-image-shape={d.imageShape}
            style={{
              ...(d.cardNameColor ? { "--products-card-name-color": d.cardNameColor } : {}),
              ...(d.cardDescColor ? { "--products-card-desc-color": d.cardDescColor } : {}),
              ...(d.cardBorderRadiusPx != null ? { "--products-card-radius": `${d.cardBorderRadiusPx}px` } : {}),
              ...(d.cardBgColor ? { "--products-card-bg": d.cardBgColor } : {}),
              "--product-cart-icon-border-width": `${d.cartIconBorderWidthPx || 0}px`,
              ...(d.cartIconBorderColor ? { "--product-cart-icon-border-color": d.cartIconBorderColor } : {}),
            }}
          >
            <div
              className="catalog-products-carousel-chrome catalog-pc-chrome"
              style={{
                position: "relative",
                borderRadius: innerR,
                background: customBg || "transparent",
                padding: customBg ? "clamp(12px, 2vw, 20px)" : 0,
              }}
              onMouseEnter={() => {
                hoverRef.current = true;
              }}
              onMouseLeave={() => {
                hoverRef.current = false;
              }}
            >
              <div
                className="catalog-pc-stage"
                style={{
                  "--pc-gap": `${d.gridGap}px`,
                  ...(perCap ? { "--pc-cap": perCap } : {}),
                }}
              >
              {count > 0 ? (
                <div
                  ref={viewportRef}
                  className="catalog-pc-viewport"
                  dir={rtl ? "rtl" : "ltr"}
                  onScroll={sync}
                >
                  <div className="catalog-pc-track" aria-busy={waiting || undefined}>
                    {waiting ? ids.map((id, i) => (
                      <div key={`sk_${id}_${i}`} className="products-flat-grid catalog-pc-cell">
                        <div className="catalog-pc-skeleton" aria-hidden>
                          <div className="catalog-pc-skeleton__img" />
                          <div className="catalog-pc-skeleton__line" />
                          <div className="catalog-pc-skeleton__line catalog-pc-skeleton__line--short" />
                        </div>
                      </div>
                    )) : picked.map((rawP, i) => (
                      <div key={`${rawP.id}_${i}`} className="products-flat-grid catalog-pc-cell">
                        <ProductCard
                          product={mapProduct?.(rawP) || rawP}
                          primaryColor={catalogThemePrimary}
                          secondaryColor={catalogThemeSecondary}
                          onSelect={onSelectProduct}
                          index={i}
                          addMode={catalogVisualTheme === "restaurant" ? "text" : "icon"}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    padding: "28px 12px",
                    color: "#94a3b8",
                    fontSize: 13,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  اختر منتجات لهذا السكشن من لوحة التخصيص
                </div>
              )}

              {pages > 1 ? (
                <>
                  <button
                    type="button"
                    className="catalog-pc-nav catalog-pc-nav--left"
                    onClick={leftAction}
                    aria-label={rtl ? "next" : "prev"}
                  >
                    <ChevronLeft size={22} strokeWidth={2.25} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="catalog-pc-nav catalog-pc-nav--right"
                    onClick={rightAction}
                    aria-label={rtl ? "prev" : "next"}
                  >
                    <ChevronRight size={22} strokeWidth={2.25} aria-hidden />
                  </button>
                </>
              ) : null}
              </div>
            </div>
          </div>

          {pages > 1 ? (
            <div
              style={{
                padding: "14px 0 0",
                display: "flex",
                justifyContent: "center",
                gap: 8,
                flexDirection: rtl ? "row-reverse" : "row",
              }}
            >
              {Array.from({ length: pages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`slide ${i + 1}`}
                  style={{
                    width: i === page ? 22 : 8,
                    height: 8,
                    borderRadius: 999,
                    border: "none",
                    background: i === page ? dotColor : "rgba(15,23,42,0.18)",
                    cursor: "pointer",
                    padding: 0,
                    transition: "width 0.25s ease, background 0.25s ease",
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>
      </section>
    );
  }, []);

  const [contactForm, setContactForm] = useState({ name: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trackOrderNumber, setTrackOrderNumber] = useState("");
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackEditLoading, setTrackEditLoading] = useState(false);
  const [trackError, setTrackError] = useState("");
  const [trackedOrders, setTrackedOrders] = useState([]);
  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const [returnPolicyModalOpen, setReturnPolicyModalOpen] = useState(false);
  const [expandedTrackedOrderId, setExpandedTrackedOrderId] = useState(null);
  const [storefrontEditingOrderId, setStorefrontEditingOrderId] = useState(null);
  // Success modal بعد إتمام الطلب — نُظهره دائماً بصرف النظر عن فتح الواتساب أم لا.
  const [orderSuccessOpen, setOrderSuccessOpen] = useState(false);
  const [orderSuccessInfo, setOrderSuccessInfo] = useState({ orderType: 'delivery', whatsappOpened: false });
  const [products, setProducts] = useState([]);
  const [catalogBundleOffers, setCatalogBundleOffers] = useState([]);
  const [sectionProducts, setSectionProducts] = useState([]);
  const catalogProductsById = useMemo(
    () => new Map((sectionProducts || []).map((p) => [p.id, p])),
    [sectionProducts]
  );
  const [catalogError, setCatalogError] = useState(null);
  const [catalogNotFound, setCatalogNotFound] = useState(false);
  const [catalogUnavailable, setCatalogUnavailable] = useState(false);
  const [categories, setCategories] = useState(['All']);
  const [catalogSlug, setCatalogSlug] = useState("");
  const [cart, setCart] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesValue, setNotesValue] = useState("");
  const pendingCartItemRef = useRef(null);
  // Server-injected store data (window.__STORE__): paint header + banner now, load products in background.
  const storeSeedRef = useRef(undefined);
  if (storeSeedRef.current === undefined) {
    storeSeedRef.current = !editorMode && !canEdit ? readStoreSeed(userId) : null;
  }
  // Store-home <title> computed by the server (custom seo_title or auto-generated). Kept apart from the seed
  // because window.__STORE__ is dropped once the API answers, and the tab title must not fall back to the name.
  const seedHomeTitleRef = useRef(String(storeSeedRef.current?.seo_title || ""));
  const [loading, setLoading] = useState(!storeSeedRef.current);
  const [productsLoading, setProductsLoading] = useState(Boolean(storeSeedRef.current));
  // Only header + banner are painted early; everything below the banner (features bar, builder
  // sections, products, footer) is held back until it can all appear at once, so nothing pops in
  // one by one and pushes content around. Released when products + builder sections are loaded.
  const [seedHold, setSeedHold] = useState(Boolean(storeSeedRef.current));
  // Wholesale strip sits above the banner: render it from the first paint so it can't push the banner down.
  const seedWholesaleStrip = Boolean(storeSeedRef.current?.wholesale_enabled);
  const catalogShellReadyRef = useRef(false);
  const catalogBrandingReadyRef = useRef(false);
  const catalogPageRootRef = useRef(null);
  const studioTopChromeRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  useMobileScrollLock(Boolean(selectedProduct) && !canEdit, ".product-modal-container", true);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  useMobileScrollLock(isCartOpen && !isCheckoutOpen && !canEdit, ".cart-items-section");
  useMobileScrollLock(isCheckoutOpen && !canEdit, ".catalog-customer-checkout-scroll");
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [productPickModal, setProductPickModal] = useState({
    open: false,
    message: "",
    focus: null,
  });
  const closeProductPickModal = useCallback(() => {
    setProductPickModal({ open: false, message: "", focus: null });
  }, []);
  const [checkoutValidationModal, setCheckoutValidationModal] = useState({
    open: false,
    message: "",
    title: "حقول مطلوبة",
  });
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    deliveryRegion: '',
    cityId: '',
    address: '',
    phone: '',
    altPhone: '',
    notes: '',
    // فورم المطاعم: dine_in | pickup | delivery
    orderType: 'delivery',
    tableNumber: '',
  });
  const [categoryFilter, setCategoryFilter] = useState('All');
  const catalogPageCacheRef = useRef(new Map());
  const catalogFetchSeqRef = useRef(0);
  const [offersOnly, setOffersOnly] = useState(false);
  const [hasOffers, setHasOffers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchDebounced(String(searchQuery || "").trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);
  const searchPending = String(searchQuery || "").trim() !== searchDebounced;
  const focusCatalogProductSearch = useCallback(() => {
    document.getElementById("catalog-products-search")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const inp = document.getElementById("catalog-products-search-input");
    if (inp && typeof inp.focus === "function") inp.focus();
  }, []);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Storefront (shopper) auth + abandoned cart persistence
  const [shopper, setShopper] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // login | register
  const [restaurantUserMenuOpen, setRestaurantUserMenuOpen] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authForm, setAuthForm] = useState({
    full_name: '',
    phone: '',
    password: '',
    password_confirm: '',
  });
  const didLoadServerCartRef = useRef(false);
  const cartSaveTimerRef = useRef(null);
  const showStorefrontCartRef = useRef(true);
  const didTrackLoggedInVisitRef = useRef(false);
  const [selectedPriceObj, setSelectedPriceObj] = useState(null);
  const [selectedPriceIndex, setSelectedPriceIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [language] = useState('ar'); // Storefront language is always Arabic
  const [studioLang, setStudioLang] = useState(() => {
    if (typeof window === 'undefined') return 'ar';
    try {
      const stored = localStorage.getItem('catalogStudioLanguage');
      return stored === 'en' ? 'en' : 'ar';
    } catch (_e) {
      return 'ar';
    }
  });
  const [langSwitchOverlay, setLangSwitchOverlay] = useState(false);
  useEffect(() => {
    try { localStorage.setItem('catalogStudioLanguage', studioLang); } catch (_e) {}
  }, [studioLang]);

  useEffect(() => {
    if (!canEdit) return;
    if (studioNavigateBlocker.state !== "blocked") {
      studioNavigateBlockerPromptedRef.current = false;
      setStudioLeaveModalOpen(false);
      return;
    }
    if (studioNavigateBlockerPromptedRef.current) return;
    studioNavigateBlockerPromptedRef.current = true;
    setStudioLeaveModalOpen(true);
  }, [canEdit, studioNavigateBlocker.state, studioNavigateBlocker]);

  useEffect(() => {
    if (!canEdit || (!loading && !builderLoading)) {
      setStudioLoadHintRotating("");
      return;
    }
    const msgs =
      studioLang === "en"
        ? ["Loading settings…", "Loading layout…", "Loading page sections…", "Loading products…"]
        : ["جاري تحميل الإعدادات…", "جاري تحميل التخطيط…", "جاري تحميل أقسام الصفحة…", "جاري تحميل المنتجات…"];
    let i = 0;
    setStudioLoadHintRotating(msgs[0]);
    const id = window.setInterval(() => {
      i = (i + 1) % msgs.length;
      setStudioLoadHintRotating(msgs[i]);
    }, 1500);
    return () => window.clearInterval(id);
  }, [canEdit, loading, builderLoading, studioLang]);

  const [pagination, setPagination] = useState({
  page: 1,
  total_pages: 1,
  total_products: 0,
  has_next: false,
  has_previous: false,
  page_size: 20,
});
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;
  const loadMoreInFlightRef = useRef(false);
const [layouts, setLayouts] = useState(() => {
  const base = {
    sections: [],
    best_products_ids: [],
    show_hero: true,
    show_grid: true,
    show_top_hero: true,
    show_features_bar: true,
    product_columns: 3,
    catalog_layout_extras: { ...DEFAULT_CATALOG_LAYOUT_EXTRAS },
  };
  return storeSeedRef.current ? storeSeedLayouts(storeSeedRef.current, base) : base;
});
  const [builderPage, setBuilderPage] = useState({ theme: null, sections: [] });
  // builderLoading is false until the load effect runs, so it can't tell "not started" from "done".
  // This flips to true only once the first builder request has actually finished (ok or failed).
  const [builderFetched, setBuilderFetched] = useState(false);

  const displayBuilderSections = useMemo(() => {
    const base = canEdit ? builderDraft || [] : builderPage?.sections || [];
    const active = normalizeBundleOffers(catalogBundleOffers).filter(
      (o) => o.is_active && o.name
    );
    const { before, after } = partitionBuilderSections(base);
    const offersInAfter = after.filter((s) => s.type === "bundle_offers");
    const offersInBefore = before.filter((s) => s.type === "bundle_offers");
    const nonOffersBefore = before.filter((s) => s.type !== "bundle_offers");
    const nonOffersAfter = after.filter((s) => s.type !== "bundle_offers");
    const relocatedOffers = [...offersInBefore, ...offersInAfter].map((s) =>
      s.settings?.after_products
        ? { ...s, settings: { ...s.settings, after_products: false } }
        : s
    );
    if (relocatedOffers.length) {
      const heroFirst = nonOffersBefore.filter((s) => s.type === "hero");
      const restBefore = nonOffersBefore.filter((s) => s.type !== "hero");
      return mergeBuilderSections(
        [...heroFirst, ...relocatedOffers, ...restBefore],
        nonOffersAfter
      );
    }
    if (!active.length) return base;
    const heroFirst = nonOffersBefore.filter((s) => s.type === "hero");
    const restBefore = nonOffersBefore.filter((s) => s.type !== "hero");
    const virtualSec = {
      id: "__auto_bundle_offers__",
      type: "bundle_offers",
      order: 99999,
      settings: {
        ...BUILDER_FRAME_DEFAULTS,
        ...builderFontDefaultsForType("bundle_offers"),
        title: "عروضنا",
        subtitle: "",
        visible: true,
        after_products: false,
        style: { backgroundColor: "#ffffff", textColor: "#0f172a" },
      },
    };
    return mergeBuilderSections([...heroFirst, virtualSec, ...restBefore], nonOffersAfter);
  }, [canEdit, builderDraft, builderPage?.sections, catalogBundleOffers]);
  const [hasVideo, setHasVideo] = useState(true); // Always true for Cloudinary video
  const [hasImage, setHasImage] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const cloudinaryVideoUrl = 'https://player.cloudinary.com/embed/?cloud_name=dlwsxhrp6&public_id=model_wcwwnq&autoplay=true&loop=true&muted=true&fit=crop&crop=fill';

 const [settings, setSettings] = useState(() => {
  const base = {
  storeName: '',
  logo: null,
  whatsappNumber: '',
  primaryColor: '#ffc200',
  secondaryColor: '#ff5b00',
  // حقول الفوتر
  footerBgColor: '#000000',
  footerTextColor: '#ffffff',
  phone: '',
  email: '',
  address: '',
  // المميزات الثلاث
  feature_description_1: '',
  feature_description_2: '',
  feature_description_3: '',
  // التواصل الاجتماعي
  facebook: '',
  instagram: '',
  // الهيرو (Hero) والتنسيق
  headerTitle: '',
  subheaderText: '',
  productsSectionTitle: 'منتجاتنا المختارة',
  productsSectionSubtitle:
    'تصفح تشكيلتنا الواسعة من أفضل المنتجات ذات الجودة العالية',
  products_heading_align: 'start',
  heroUrl: '',
  bgType: 'color', // (color أو image)
  layoutDirection: 'rtl', // مضاف ليتناسب مع الباك إند
  feature_title_1: 'توصيل سريع',
  feature_title_2: 'ضمان الجودة',
  feature_title_3: 'دعم فني',
  features_bar_bg: '',
  features_bar_text: '',
  features_bar_accent: '',
  // إعدادات إتمام الطلب — تُقرأ من /catalog/{user_id}
  // sendOrdersToWhatsapp = true: نفتح واتساب أيضاً بعد إنشاء الطلب
  // checkoutFormType = 'store' | 'restaurant'
  sendOrdersToWhatsapp: true,
  showCatalogWhatsapp: false,
  checkoutFormType: 'store',
  storefrontCartEnabled: true,
  storefrontFullOrdersEnabled: true,
  metaPixelId: '',
  tiktokPixelId: '',
  storeCityId: '',
  storeCityName: '',
  storeDeliveryRegion: '',
  deliveryPrices: defaultDeliveryPricesMap(),
  deliveryEnabled: false,
  businessHoursEnabled: false,
  businessHours: null,
  returnExchangePolicy: "",
  };
  return storeSeedRef.current ? storeSeedSettings(storeSeedRef.current, base) : base;
});

  useLayoutEffect(() => {
    settingsSnapshotRef.current = settings;
  }, [settings]);

  const catalogThemePrimary = useMemo(
    () => (canEdit ? (storeEdit.primaryColor || settings.primaryColor) : settings.primaryColor),
    [canEdit, storeEdit.primaryColor, settings.primaryColor]
  );
  const catalogThemeSecondary = useMemo(
    () => (canEdit ? (storeEdit.secondaryColor || settings.secondaryColor) : settings.secondaryColor),
    [canEdit, storeEdit.secondaryColor, settings.secondaryColor]
  );

  /** أزرار تسجيل الدخول / إنشاء حساب في الهيدر — لا تتبع لون الهيرو الأساسي حتى لا تصبح سوداء معه */
  const catalogHeaderAuthAccent = useMemo(
    () => String(catalogThemeSecondary || "#ff5b00").trim() || "#ff5b00",
    [catalogThemeSecondary]
  );

  const catalogFooterColors = useMemo(
    () => ({
      bg: canEdit ? (storeEdit.footerBgColor || settings.footerBgColor || "#000000") : (settings.footerBgColor || "#000000"),
      fg: canEdit ? (storeEdit.footerTextColor || settings.footerTextColor || "#ffffff") : (settings.footerTextColor || "#ffffff"),
      phone: canEdit ? (storeEdit.phone || settings.phone || "") : (settings.phone || ""),
      email: canEdit ? (storeEdit.email || settings.email || "") : (settings.email || ""),
      address: canEdit ? (storeEdit.address || settings.address || "") : (settings.address || ""),
      facebook: canEdit ? (storeEdit.facebook || settings.facebook || "") : (settings.facebook || ""),
      instagram: canEdit ? (storeEdit.instagram || settings.instagram || "") : (settings.instagram || ""),
    }),
    [
      canEdit,
      storeEdit.footerBgColor,
      storeEdit.footerTextColor,
      storeEdit.phone,
      storeEdit.email,
      storeEdit.address,
      storeEdit.facebook,
      storeEdit.instagram,
      settings.footerBgColor,
      settings.footerTextColor,
      settings.phone,
      settings.email,
      settings.address,
      settings.facebook,
      settings.instagram,
    ]
  );

  const catalogFooterDisplay = useMemo(
    () => ({
      ...catalogFooterColors,
      storeName: String(settings.storeName || "").trim(),
      tagline: canEdit
        ? String(storeEdit.subheaderText || settings.subheaderText || "").trim()
        : String(settings.subheaderText || "").trim(),
      whatsappNumber: String(settings.whatsappNumber || "").trim(),
    }),
    [
      catalogFooterColors,
      canEdit,
      storeEdit.subheaderText,
      settings.storeName,
      settings.subheaderText,
      settings.whatsappNumber,
    ]
  );

  // Meta Pixel: load fbq + PageView when the store has a Pixel ID (public branding).
  // Third-party tracking never competes with first paint / LCP: init runs when the browser is idle after
  // the first frames (runWhenIdle); events fired earlier are queued by the pixel util and replayed.
  useEffect(() => {
    const pixelId = String(settings?.metaPixelId || "").replace(/\D/g, "");
    if (!pixelId) return undefined;
    markMetaPixelPending(pixelId);
    return runWhenIdle(() => initMetaPixel(pixelId));
  }, [settings?.metaPixelId]);

  // TikTok Pixel: load ttq + page() when the store has a Pixel ID (public branding).
  useEffect(() => {
    const pixelId = String(settings?.tiktokPixelId || "").replace(/[^a-zA-Z0-9]/g, "");
    if (!pixelId) return undefined;
    markTikTokPixelPending(pixelId);
    return runWhenIdle(() => initTikTokPixel(pixelId));
  }, [settings?.tiktokPixelId]);

  // Studio: start the editors chunk right away (the panel needs it). Storefront: warm the modal chunks on idle.
  useEffect(() => {
    if (canEdit) {
      preloadStudioEditors();
      return undefined;
    }
    return runWhenIdle(preloadStorefrontModals, { timeout: 6000 });
  }, [canEdit]);

  const catalogLayoutEffective = useMemo(() => {
    const raw = canEdit ? layoutsEdit.catalog_layout_extras : layouts.catalog_layout_extras;
    const merged = { ...DEFAULT_CATALOG_LAYOUT_EXTRAS, ...(raw && typeof raw === "object" ? raw : {}) };
    const rawKeys = raw && typeof raw === "object" ? raw : {};
    migrateLegacyLayoutExtras(merged, raw && typeof raw === "object" ? raw : null);
    const minimalTopbarKeys = [
      "minimal_topbar_bg",
      "minimal_topbar_store_name_color",
      "minimal_topbar_login_bg",
      "minimal_topbar_login_text",
      "minimal_topbar_login_border",
      "minimal_topbar_register_bg",
      "minimal_topbar_register_text",
      "minimal_topbar_register_border",
    ];
    for (const key of minimalTopbarKeys) {
      if (!String(merged[key] ?? "").trim()) {
        merged[key] = DEFAULT_CATALOG_LAYOUT_EXTRAS[key];
      }
    }
    let imagePx;
    if (
      Object.prototype.hasOwnProperty.call(rawKeys, "product_card_image_size") &&
      merged.product_card_image_size != null &&
      merged.product_card_image_size !== ""
    ) {
      imagePx = Number(merged.product_card_image_size);
    } else if (merged.restaurant_card_image_size != null && merged.restaurant_card_image_size !== "") {
      imagePx = Number(merged.restaurant_card_image_size);
    } else {
      imagePx = Number(DEFAULT_CATALOG_LAYOUT_EXTRAS.product_card_image_size);
    }
    if (!Number.isFinite(imagePx)) imagePx = Number(DEFAULT_CATALOG_LAYOUT_EXTRAS.product_card_image_size);
    imagePx = Math.max(84, Math.min(360, imagePx));

    let products_show_description;
    if (Object.prototype.hasOwnProperty.call(rawKeys, "products_show_description")) {
      products_show_description = merged.products_show_description !== false && String(merged.products_show_description) !== "false";
    } else if (Object.prototype.hasOwnProperty.call(rawKeys, "restaurant_show_desc")) {
      products_show_description = merged.restaurant_show_desc !== false && String(merged.restaurant_show_desc) !== "false";
    } else {
      products_show_description = DEFAULT_CATALOG_LAYOUT_EXTRAS.products_show_description !== false;
    }

    return { ...merged, product_card_image_size: imagePx, products_show_description };
  }, [canEdit, layoutsEdit.catalog_layout_extras, layouts.catalog_layout_extras]);

  const { footerPhones, footerFax, footerEmail, footerTiktok, footerCustomFields } = useMemo(() => {
    const ex = catalogLayoutEffective;
    const phones = [catalogFooterDisplay.phone, ...(Array.isArray(ex.footer_extra_phones) ? ex.footer_extra_phones : [])]
      .map((p) => String(p || "").trim())
      .filter(Boolean);
    const showEmail = ex.footer_show_email !== false && String(ex.footer_show_email) !== "false";
    let tiktok = String(ex.footer_tiktok || "").trim();
    if (tiktok && !/^https?:\/\//i.test(tiktok)) {
      tiktok = tiktok.startsWith("@") ? `https://www.tiktok.com/${tiktok}` : `https://${tiktok}`;
    }
    return {
      footerPhones: phones,
      footerFax: String(ex.footer_fax || "").trim(),
      footerEmail: showEmail ? String(catalogFooterDisplay.email || "").trim() : "",
      footerTiktok: tiktok,
      footerCustomFields: (Array.isArray(ex.footer_custom_fields) ? ex.footer_custom_fields : [])
        .map((f, i) => ({
          id: String(f?.id || `f${i}`),
          label: String(f?.label || "").trim(),
          value: String(f?.value || "").trim(),
        }))
        .filter((f) => f.value),
    };
  }, [catalogLayoutEffective, catalogFooterDisplay.phone, catalogFooterDisplay.email]);

  // Browser tab: store icon (favicon → logo fallback) + store name instead of Kataloji's.
  const storeTabIcon =
    String(catalogLayoutEffective.favicon_url || "").trim() || String(settings.logo || "").trim();
  // Store home: same title as the server-rendered <title> (merchant seo_title used verbatim, else the one the
  // server generated), never shortened to the bare store name. Product deep links keep the store name as before.
  const storeNameForTab = String(settings.storeName || "").trim();
  const customSeoTitle = String(catalogLayoutEffective.seo_title || "");
  const storeTabTitle = productId
    ? storeNameForTab
    : customSeoTitle.trim()
      ? customSeoTitle
      : seedHomeTitleRef.current.trim() || storeNameForTab;
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const iconLinks = Array.from(document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]'));
    const saved = iconLinks.map((el) => ({ el, href: el.getAttribute("href"), type: el.getAttribute("type") }));
    const savedTitle = document.title;
    if (storeTabIcon) {
      iconLinks.forEach((el) => {
        el.removeAttribute("type");
        el.setAttribute("href", storeTabIcon);
      });
    }
    if (storeTabTitle) document.title = storeTabTitle;
    return () => {
      saved.forEach(({ el, href, type }) => {
        if (href != null) el.setAttribute("href", href);
        if (type != null) el.setAttribute("type", type);
      });
      document.title = savedTitle;
    };
  }, [storeTabIcon, storeTabTitle]);

  const patchFooterExtras = (patch) =>
    setLayoutsEdit((p) => ({
      ...p,
      catalog_layout_extras: {
        ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
        ...(p.catalog_layout_extras || {}),
        ...patch,
      },
    }));

  /** لون الهيرو فقط (خلفية، طبقة فوق الصورة، زر الهيرو). إن وُجد في extras يُستخدم؛ وإلا نفس لون التمييز العام. */
  const catalogHeroPrimary = useMemo(() => {
    const hex = String(catalogLayoutEffective.hero_primary_color || "").trim();
    if (hex) return hex;
    return catalogThemePrimary;
  }, [catalogLayoutEffective.hero_primary_color, catalogThemePrimary]);

  /** لون شريط تجار الجملة (الكلمة المميزة + زر الدخول). فارغ = الأصفر الافتراضي. */
  const wholesaleStripAccent = useMemo(() => {
    const hex = String(catalogLayoutEffective.wholesale_strip_accent_color || "").trim();
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return hex;
    return "#fbbf24";
  }, [catalogLayoutEffective.wholesale_strip_accent_color]);

  /** لون زر دخول نافذة تاجر الجملة */
  const wholesaleAuthButtonColor = useMemo(() => {
    const hex = String(catalogLayoutEffective.wholesale_auth_button_color || "").trim();
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return hex;
    return wholesaleStripAccent;
  }, [catalogLayoutEffective.wholesale_auth_button_color, wholesaleStripAccent]);

  /** لون إطار/تركيز حقول نافذة تاجر الجملة */
  const wholesaleAuthFieldColor = useMemo(() => {
    const hex = String(catalogLayoutEffective.wholesale_auth_field_color || "").trim();
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return hex;
    return wholesaleAuthButtonColor;
  }, [catalogLayoutEffective.wholesale_auth_field_color, wholesaleAuthButtonColor]);

  /** max-width للـ .container والشريط — يطابق أقسام البناء (1200 / 1400) */
  const catalogPageContentMaxPx = useMemo(() => {
    const g = String(catalogLayoutEffective.builder_page_container_width ?? "").trim();
    if (g === "wide") return CATALOG_PAGE_CONTENT_WIDTH_PX.wide;
    if (g === "full") return null;
    if (g === "boxed") return CATALOG_PAGE_CONTENT_WIDTH_PX.boxed;
    const secs = canEdit ? builderDraft : builderPage?.sections;
    let w = CATALOG_PAGE_CONTENT_WIDTH_PX.boxed;
    if (Array.isArray(secs)) {
      for (const s of secs) {
        const cw = String(s?.settings?.container_width || "").trim();
        if (cw === "wide") w = Math.max(w, CATALOG_PAGE_CONTENT_WIDTH_PX.wide);
      }
    }
    return w;
  }, [
    catalogLayoutEffective.builder_page_container_width,
    canEdit,
    builderDraft,
    builderPage?.sections,
  ]);

  const catalogUiFontFamily = useMemo(
    () => catalogFontFamilyFromStackKey(catalogLayoutEffective.catalog_font_stack),
    [catalogLayoutEffective.catalog_font_stack]
  );

  // Tajawal + Cairo are self-hosted (index.html). Almarai / IBM Plex Sans Arabic / Noto Kufi, and the
  // studio UI fonts, are fetched only when this store/screen needs them (see utils/ensureFonts.js).
  useEffect(() => {
    if (canEdit) {
      ensureAllStudioFonts();
      return;
    }
    ensureCatalogFontStack(catalogLayoutEffective.catalog_font_stack);
  }, [canEdit, catalogLayoutEffective.catalog_font_stack]);

  /** minimal = هيدر + هيرو + مميزات | showcase = شبكة حادة | restaurant = منيو */
  const catalogVisualTheme = useMemo(() => {
    const heroLayout = String(catalogLayoutEffective.hero_layout || "").trim().toLowerCase();
    if (heroLayout === "split_circle") return "restaurant";
    const raw = String((canEdit ? builderTheme : (builderPage.theme || builderTheme)) || "minimal").toLowerCase();
    if (raw === "showcase" || raw === "grid" || raw === "sharp" || raw === "dark_store" || raw === "electronics_store") {
      return "showcase";
    }
    if (raw === "restaurant" || raw === "menu") return "restaurant";
    return "minimal";
  }, [canEdit, builderTheme, builderPage.theme, catalogLayoutEffective.hero_layout]);

  const productsChrome = useMemo(() => {
    const categoryNav = catalogLayoutEffective.category_nav === "sidebar" ? "sidebar" : "top";
    const filtersSidebar = Boolean(catalogLayoutEffective.filters_sidebar);
    if (catalogVisualTheme === "showcase") {
      const aside = categoryNav === "sidebar";
      return { categoryNav, filtersSidebar, aside };
    }
    const aside = filtersSidebar || categoryNav === "sidebar";
    return { categoryNav, filtersSidebar, aside };
  }, [catalogLayoutEffective, catalogVisualTheme]);

  const cartPlacement = useMemo(() => {
    const raw = String(catalogLayoutEffective.cart_placement || "topbar").toLowerCase();
    if (raw === "left" || raw === "right") return raw;
    return "topbar";
  }, [catalogLayoutEffective.cart_placement]);

  /** عمودي فقط — الأفقي عبر Catalog.css (--phys-left / --phys-right) */
  const floatingCartChromeStyle = useMemo(() => {
    const editorBottom = canEdit ? 96 : 20;
    return { top: "auto", bottom: editorBottom };
  }, [canEdit]);

  /** إزاحة زر السلة عن حافة اللوحة المفتوحة حتى لا يغطي التخصيص */
  const floatingCartEditorPanelClass = useMemo(() => {
    if (!canEdit || !customizerOpen) return "";
    if (cartPlacement !== "left" && cartPlacement !== "right") return "";
    if (studioLang === "en") {
      return cartPlacement === "left" ? "catalog-floating-actions--editor-panel-offset-start" : "";
    }
    return cartPlacement === "right" ? "catalog-floating-actions--editor-panel-offset-end" : "";
  }, [canEdit, customizerOpen, cartPlacement, studioLang]);

  const requestOpenCart = useCallback(() => {
    if (canEdit) return;
    if (showStorefrontCartRef.current === false) return;
    setIsCartOpen(true);
  }, [canEdit]);

  const [cartBumpOn, setCartBumpOn] = useState(false);
  const [cartToastText, setCartToastText] = useState("");
  const cartBumpOffTimerRef = useRef(null);
  const cartToastTimerRef = useRef(null);
  const bumpCartFeedback = useCallback(() => {
    if (canEdit) return;
    if (cartBumpOffTimerRef.current) clearTimeout(cartBumpOffTimerRef.current);
    if (cartToastTimerRef.current) clearTimeout(cartToastTimerRef.current);
    setCartBumpOn(false);
    requestAnimationFrame(() => {
      setCartBumpOn(true);
    });
    cartBumpOffTimerRef.current = window.setTimeout(() => {
      setCartBumpOn(false);
      cartBumpOffTimerRef.current = null;
    }, 2000);
    setCartToastText("تمت الإضافة إلى السلة");
    cartToastTimerRef.current = window.setTimeout(() => {
      setCartToastText("");
      cartToastTimerRef.current = null;
    }, 2400);
  }, [canEdit]);

  const cartFloatPhysClass =
    cartPlacement === "left"
      ? "catalog-floating-actions--phys-left"
      : cartPlacement === "right"
        ? "catalog-floating-actions--phys-right"
        : "";

  const floatingCartWrapperClass = (baseClasses) =>
    [baseClasses, cartFloatPhysClass, floatingCartEditorPanelClass].filter(Boolean).join(" ");

  const catalogWhatsappDigits = useMemo(
    () => normalizeWhatsAppDigits(settings.whatsappNumber),
    [settings.whatsappNumber]
  );
  const showFloatingCatalogWhatsapp = Boolean(
    settings.showCatalogWhatsapp && catalogWhatsappDigits && !menuOnly
  );
  const floatingWhatsappHref = catalogWhatsappDigits
    ? `https://wa.me/${catalogWhatsappDigits}`
    : "";
  const floatingWhatsappBtn = showFloatingCatalogWhatsapp ? (
    <a
      href={floatingWhatsappHref}
      target="_blank"
      rel="noopener noreferrer"
      className="catalog-floating-action-btn catalog-floating-action-btn--whatsapp"
      style={{
        "--floating-whatsapp-bg": "#128C7E",
        "--floating-whatsapp-icon": "#ffffff",
      }}
      aria-label={language === "en" ? "WhatsApp" : "واتساب"}
      onClick={() => {
        try {
          trackWhatsAppClick();
        } catch {
          /* ignore */
        }
      }}
    >
      <WhatsAppBrandIcon size={22} />
    </a>
  ) : null;

  const restaurantVars = useMemo(() => {
    if (catalogVisualTheme !== "restaurant") return null;
    const x = catalogLayoutEffective || DEFAULT_CATALOG_LAYOUT_EXTRAS;
    const bgColor = String(x.restaurant_category_bar_bg_color || "#faf8f3").trim();
    const bgOpacity = Number(x.restaurant_category_bar_bg_opacity ?? 0.97);
    const borderColor = String(x.restaurant_category_bar_border_color || "#2d2416").trim();
    const borderOpacity = Number(x.restaurant_category_bar_border_opacity ?? 0.1);
    const decorOpacity = Number(x.restaurant_decor_opacity ?? 0.28);
    return {
      "--restaurant-bg": x.restaurant_bg || "#faf8f3",
      "--restaurant-surface": x.restaurant_surface || "#ffffff",
      "--restaurant-ink": x.restaurant_ink || "#2d2416",
      "--restaurant-muted": x.restaurant_muted || "rgba(45, 36, 22, 0.65)",
      "--restaurant-line": x.restaurant_line || "rgba(45, 36, 22, 0.10)",
      "--restaurant-accent": x.restaurant_accent || "#3d6b4f",
      "--restaurant-accent-warm": x.restaurant_accent_warm || "#c8813a",
      "--restaurant-serif": 'Georgia, "Times New Roman", Times, serif',
      "--restaurant-topbar-bg":
        String(x.minimal_topbar_bg || "").trim() || x.restaurant_topbar_bg || "#2d2416",
      "--restaurant-topbar-text":
        String(x.minimal_topbar_store_name_color || "").trim() || x.restaurant_topbar_text || "#f5e6c8",
      "--restaurant-card-image-size": `${Number(x.product_card_image_size || x.restaurant_card_image_size || 168)}px`,
      "--restaurant-category-bar-bg": toRgba(bgColor, bgOpacity),
      "--restaurant-category-bar-border": toRgba(borderColor, borderOpacity),
      "--restaurant-decor-color": x.restaurant_decor_color || "#c8813a",
      "--restaurant-decor-opacity": String(Number.isFinite(decorOpacity) ? Math.min(0.8, Math.max(0, decorOpacity)) : 0.28),
    };
  }, [catalogLayoutEffective, catalogVisualTheme]);

  const productsSectionDesign = useMemo(() => {
    const x = catalogLayoutEffective;
    const padKey = x.products_section_padding_y || "md";
    const padYMap = { sm: 28, md: 44, lg: 60 };
    const gapKey = x.products_grid_gap || "md";
    const gapMap = { sm: 12, md: 20, lg: 28 };
    const layoutRaw = String(x.products_main_layout || "grid").toLowerCase();
    const mainLayout =
      catalogVisualTheme === "restaurant"
        ? layoutRaw === "gallery"
          ? "gallery"
          : layoutRaw === "list"
            ? "list"
            : "grid"
        : layoutRaw === "list" || layoutRaw === "gallery"
          ? layoutRaw
          : "grid";
    const imageShape = normalizeProductImageShape(x.product_image_shape);
    const rRaw = x.products_card_border_radius;
    const cardRadiusParsed =
      rRaw === "" || rRaw === undefined || rRaw === null ? NaN : Number(rRaw);
    const cardBorderRadiusPx = Number.isFinite(cardRadiusParsed)
      ? Math.max(0, Math.min(40, cardRadiusParsed))
      : null;
    const sqImgRaw = x.restaurant_square_image_radius_px;
    const sqImgParsed =
      sqImgRaw === "" || sqImgRaw === undefined || sqImgRaw === null ? NaN : Number(sqImgRaw);
    const restaurantSquareImageRadiusPx = Number.isFinite(sqImgParsed)
      ? Math.max(0, Math.min(48, sqImgParsed))
      : null;
    const imagePxRaw = Number(
      x.product_card_image_size ?? x.restaurant_card_image_size ?? DEFAULT_CATALOG_LAYOUT_EXTRAS.product_card_image_size
    );
    const imagePx = Number.isFinite(imagePxRaw)
      ? Math.max(84, Math.min(360, imagePxRaw))
      : DEFAULT_CATALOG_LAYOUT_EXTRAS.product_card_image_size;
    /** 84→~0.35 من عرض البطاقة، 360→1 (امتلاء كامل) — للمربع/المستطيل الطولي */
    const imageFrameScale = Math.max(0.35, Math.min(1, imagePx / 360));
    const widthKey = resolveProductsSectionWidthKey(x);
    const maxWidthPx =
      widthKey === "full"
        ? null
        : widthKey === "wide"
          ? CATALOG_PAGE_CONTENT_WIDTH_PX.wide
          : CATALOG_PAGE_CONTENT_WIDTH_PX.boxed;
    const shellWrapStyle =
      widthKey === "full"
        ? {
            width: "100vw",
            maxWidth: "100vw",
            marginLeft: "calc(50% - 50vw)",
            marginRight: "calc(50% - 50vw)",
            boxSizing: "border-box",
          }
        : {
            width: "100%",
            maxWidth: maxWidthPx ?? CATALOG_PAGE_CONTENT_WIDTH_PX.boxed,
            marginLeft: "auto",
            marginRight: "auto",
            boxSizing: "border-box",
          };
    return {
      sectionPaddingY: padYMap[padKey] ?? padYMap.md,
      gridGap: gapMap[gapKey] ?? gapMap.md,
      cardStyle: x.products_card_style || "default",
      cardBgColor: String(x.products_card_bg_color || "").trim(),
      widthKey,
      maxWidthPx,
      shellWrapStyle,
      productsSectionMaxWidthCss:
        widthKey === "full" ? "100%" : `${maxWidthPx ?? CATALOG_PAGE_CONTENT_WIDTH_PX.boxed}px`,
      productImageFillCard: imagePx >= 360,
      productImageFrameScale: imageFrameScale,
      titleColor: String(x.products_heading_color || "").trim(),
      subtitleColor: String(x.products_subtitle_color || "").trim(),
      cardNameColor: String(x.products_card_name_color || "").trim(),
      cardDescColor: String(x.products_card_desc_color || "").trim(),
      underlineColor: String(x.products_underline_color || "").trim(),
      mainLayout,
      imageShape,
      cardBorderRadiusPx,
      restaurantSquareImageRadiusPx,
      cartIconBorderWidthPx: Math.max(
        0,
        Math.min(12, Number(x.products_cart_icon_border_width) || 0)
      ),
      cartIconBorderColor: String(x.products_cart_icon_border_color || "").trim(),
      cartIconSide:
        String(x.products_cart_icon_side || "right").toLowerCase() === "left" ? "left" : "right",
    };
  }, [catalogLayoutEffective, catalogVisualTheme]);

  const cardPricePrefs = useMemo(() => {
    const x = catalogLayoutEffective;
    const pos = String(x.products_card_price_position || "").trim().toLowerCase();
    const catPos = String(x.products_card_category_position || "").trim().toLowerCase();
    const align = String(x.products_card_price_align || "").trim().toLowerCase();
    return {
      color: String(x.products_card_price_color || "").trim(),
      position: pos === "below_name" || pos === "on_image" ? pos : "",
      align: align === "right" || align === "center" || align === "left" ? align : "",
      bg: String(x.products_card_price_bg_color || "").trim(),
      categoryPosition: catPos === "on_image" || catPos === "hidden" ? catPos : "",
      categoryColor: String(x.products_card_category_color || "").trim(),
      whatsappNumber: String(settings.whatsappNumber || "").trim(),
    };
  }, [
    settings.whatsappNumber,
    catalogLayoutEffective.products_card_price_color,
    catalogLayoutEffective.products_card_price_position,
    catalogLayoutEffective.products_card_price_align,
    catalogLayoutEffective.products_card_price_bg_color,
    catalogLayoutEffective.products_card_category_position,
    catalogLayoutEffective.products_card_category_color,
  ]);

  const showCatalogMainHeader = catalogVisualTheme === "minimal";
  const showCatalogDefaultHero =
    showCatalogMainHeader && (canEdit ? layoutsEdit.show_top_hero : layouts.show_top_hero);
  const showCatalogFeaturesBar = Boolean(canEdit ? layoutsEdit.show_features_bar : layouts.show_features_bar);

  const featureBarTitles = useMemo(() => {
    if (canEdit) {
      return [
        storeEdit.featureTitle1 || settings.feature_title_1 || 'توصيل سريع',
        storeEdit.featureTitle2 || settings.feature_title_2 || 'ضمان الجودة',
        storeEdit.featureTitle3 || settings.feature_title_3 || 'دعم فني',
      ];
    }
    return [
      settings.feature_title_1 || 'توصيل سريع',
      settings.feature_title_2 || 'ضمان الجودة',
      settings.feature_title_3 || 'دعم فني',
    ];
  }, [canEdit, storeEdit.featureTitle1, storeEdit.featureTitle2, storeEdit.featureTitle3, settings.feature_title_1, settings.feature_title_2, settings.feature_title_3]);

  const featureBarDescs = useMemo(() => {
    if (canEdit) {
      return [storeEdit.featureDesc1, storeEdit.featureDesc2, storeEdit.featureDesc3];
    }
    return [settings.feature_description_1, settings.feature_description_2, settings.feature_description_3];
  }, [canEdit, storeEdit.featureDesc1, storeEdit.featureDesc2, storeEdit.featureDesc3, settings.feature_description_1, settings.feature_description_2, settings.feature_description_3]);

  const featureBarBg = useMemo(() => {
    const raw = canEdit ? (storeEdit.featuresBarBg || settings.features_bar_bg) : settings.features_bar_bg;
    return (raw && String(raw).trim()) || catalogThemeSecondary || '#f3f4f6';
  }, [canEdit, storeEdit.featuresBarBg, settings.features_bar_bg, catalogThemeSecondary]);

  const featureBarText = useMemo(() => {
    const raw = canEdit ? (storeEdit.featuresBarText || settings.features_bar_text) : settings.features_bar_text;
    return (raw && String(raw).trim()) || '#475569';
  }, [canEdit, storeEdit.featuresBarText, settings.features_bar_text]);

  const featureBarAccent = useMemo(() => {
    const raw = canEdit ? (storeEdit.featuresBarAccent || settings.features_bar_accent) : settings.features_bar_accent;
    return (raw && String(raw).trim()) || catalogThemePrimary;
  }, [canEdit, storeEdit.featuresBarAccent, settings.features_bar_accent, catalogThemePrimary]);

  const handleContactSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  const payload = {
    catalog_id: userId,
    customer_name: contactForm.name.trim(),
    customer_message: contactForm.message.trim(),
  };

  try {
    const response = await fetch(`${API_URL}/catalog/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      alert('تم إرسال رسالتك بنجاح!');
      setContactForm({ name: '', message: '' }); // تصفير الحقول
    } else {
      const errorData = await response.json().catch(() => ({}));
      alert(errorData.detail || 'حدث خطأ أثناء الإرسال، يرجى المحاولة لاحقاً.');
    }
  } catch (error) {
    console.error('Error sending contact email:', error);
    alert('تعذر الاتصال بالخادم.');
  } finally {
    setIsSubmitting(false);
  }
};

  const formatTrackDate = (iso) => {
    try {
      if (!iso) return "-";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleString("en-GB", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return "-";
    }
  };

  const formatTrackNumber = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0";
    return n.toLocaleString("en-US");
  };

  const formatTrackMoney = (value) => `₪${formatTrackNumber(value)}`;

  const trackOrderShowsDelivery = (o) => {
    const ot = String(o?.order_type || "delivery").trim().toLowerCase();
    return ot === "delivery" && (o?.city?.name || o?.city_id != null);
  };

  const formatTrackItemTitle = (it) => {
    const p = it?.product || {};
    const rawName = String(
      it?.display_name || p?.name_ar || p?.name || ""
    ).trim();
    const name = rawName
      ? formatOrderItemLineLabel({
          productName: rawName,
          selectedVariant: it?.selected_variant,
          selectedSize: it?.selected_size,
          selectedColor: it?.selected_color,
        })
      : "";
    const unavailable = Boolean(it?.product_unavailable);
    if (!name) return unavailable ? "منتج — لم يعد هذا المنتج متوفراً" : "منتج";
    return unavailable ? `${name} — لم يعد هذا المنتج متوفراً` : name;
  };

  const formatTrackItemOptions = (it) => {
    const parts = [];
    const v = String(it?.selected_variant || "").trim();
    const s = String(it?.selected_size || "").trim();
    const c = String(it?.selected_color || "").trim();
    if (
      v &&
      v !== "الافتراضي" &&
      (!isAllSizesVariantLabel(v) || shouldShowAllSizesVariant(v, s, c))
    ) {
      parts.push(v);
    }
    const meta = formatOrderItemVariantMeta(it, "ar");
    if (meta) parts.push(meta);
    return parts.join(" — ");
  };

  const handleTrackOrders = async (e) => {
    e.preventDefault();
    const raw = normalizeOrderNumberInput(trackOrderNumber);
    if (raw.length < 6) return;
    setTrackLoading(true);
    setTrackError("");
    setTrackedOrders([]);
    setExpandedTrackedOrderId(null);
    try {
      const res = await axios.get(
        `${API_URL}/catalog/${encodeURIComponent(userId)}/orders/track`,
        { params: { order_number: raw } }
      );
      const order = res?.data?.order || null;
      const list = order ? [order] : [];
      setTrackedOrders(list);
      if (!list.length) {
        setTrackError("لم يتم العثور على طلب بهذا الرقم ضمن هذا الكتالوج.");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "حدث خطأ أثناء تتبع الطلب.";
      setTrackError(String(detail));
    } finally {
      setTrackLoading(false);
    }
  };

  const handleEditTrackedOrder = useCallback(
    async (order) => {
      if (!order?.can_edit || !order?.order_number) return;
      const norm = normalizeOrderNumberInput(order.order_number);
      if (!norm) return;
      setTrackEditLoading(true);
      setTrackError("");
      try {
        const res = await axios.get(
          `${API_URL}/catalog/${encodeURIComponent(userId)}/orders/edit-cart`,
          { params: { order_number: norm } }
        );
        const data = res?.data || {};
        const items = Array.isArray(data.items) ? data.items : [];
        if (!items.length) {
          setTrackError("لا توجد منتجات في هذه الطلبية للتعديل.");
          return;
        }
        setCart(items);
        setStorefrontEditingOrderId(data.editing_order_id || null);
        const cf = data.checkout || {};
        setCheckoutForm({
          name: String(cf.name || ""),
          deliveryRegion: String(cf.delivery_region || ""),
          cityId: cf.city_id != null ? String(cf.city_id) : "",
          address: String(cf.address || ""),
          phone: String(cf.phone || ""),
          altPhone: String(cf.alt_phone || ""),
          notes: String(cf.notes || ""),
          orderType: cf.order_type || "delivery",
          tableNumber: String(cf.table_number || ""),
        });
        setTrackModalOpen(false);
        setTrackedOrders([]);
        setExpandedTrackedOrderId(null);
        setIsCartOpen(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        const detail =
          err?.response?.data?.detail || err?.message || "تعذر تحميل الطلبية للتعديل.";
        setTrackError(String(detail));
      } finally {
        setTrackEditLoading(false);
      }
    },
    [userId]
  );
  
  // Translations object
  const translations = {
    en: {
      searchPlaceholder: 'Search products...',
      exploreNow: 'Explore Now',
      add: 'ADD',
      all: 'All',
      yourBag: 'Your Bag',
      emptyCart: 'Your shopping bag is empty.',
      subtotal: 'Total',
      itemsTotal: 'Items total',
      deliveryLabel: 'Delivery',
      deliveryByRegion: 'Based on delivery area',
      grandTotal: 'Grand total',
      plusDelivery: '+ delivery',
      sendOrder: 'Send Order via WhatsApp',
      continueToOrder: 'Continue to place order',
      whatsappNoteTitle: 'Order confirmation via WhatsApp',
      whatsappNoteBody: (store) =>
        `${store || 'This store'} requires orders to be sent via WhatsApp as well. WhatsApp will open with a ready message containing your order details — please send it as is to complete your order.`,
      deliveryNoteTitle: 'Delivery not included',
      deliveryNoteBody: 'Prices shown do not include delivery fees. The delivery cost will be confirmed with you when your order is confirmed.',
      confirmAdd: 'Confirm & Add to Cart',
      experienceExcellence: 'Experience excellence with this exclusive piece from',
      designedFor: 'Designed for those who appreciate quality.',
      handcrafted: 'Handcrafted Shopping Experience',
      orderDetails: 'Order Details:',
      total: 'Total:',
      cartAriaLabel: (n) => `Cart, ${n} item${n === 1 ? '' : 's'}`,
      headerLogin: "Log in",
      headerRegister: "Sign up",
    },
    ar: {
      searchPlaceholder: 'ابحث عن المنتجات...',
      exploreNow: 'استكشف الآن',
      add: 'أضف',
      all: 'الكل',
      yourBag: 'سلتك',
      emptyCart: 'سلتك فارغة.',
      subtotal: 'المجموع',
      itemsTotal: 'مجموع المنتجات',
      deliveryLabel: 'التوصيل',
      deliveryByRegion: 'حسب منطقة التوصيل',
      grandTotal: 'المجموع الكلي',
      plusDelivery: '+ التوصيل',
      sendOrder: 'إرسال الطلب عبر واتساب',
      continueToOrder: 'المتابعة لإرسال الطلب',
      whatsappNoteTitle: 'تأكيد الطلب عبر واتساب',
      whatsappNoteBody: (store) =>
        `يتطلّب ${store || 'المتجر'} إرسال الطلبية عبر واتساب أيضاً. سيُفتح واتساب برسالة جاهزة تتضمّن تفاصيل طلبك، يُرجى إرسالها كما هي لإتمام الطلب.`,
      deliveryNoteTitle: 'التوصيل غير مشمول',
      deliveryNoteBody: 'الأسعار المعروضة لا تشمل رسوم التوصيل، وسيتم تأكيد تكلفة التوصيل معك عند تأكيد الطلب.',
      confirmAdd: 'تأكيد وإضافة إلى السلة',
      experienceExcellence: 'استمتع بالتميز مع هذه القطعة الحصرية من',
      designedFor: 'مصممة لمن يقدرون الجودة.',
      handcrafted: 'تجربة تسوق يدوية الصنع',
      orderDetails: 'تفاصيل الطلب:',
      total: 'المجموع:',
      cartAriaLabel: (n) => `السلة، ${n} منتجات`,
      headerLogin: "تسجيل الدخول",
      headerRegister: "إنشاء حساب",
    }
  };

  const t = translations[language];

  /** ألوان ونصوص الشريط العلوي فوق الهيرو (متجر افتراضي + منيو/مطعم) */
  const catalogHeroTopbarUi = useMemo(() => {
    const x = catalogLayoutEffective || DEFAULT_CATALOG_LAYOUT_EXTRAS;
    const d = DEFAULT_CATALOG_LAYOUT_EXTRAS;
    const accent = catalogHeaderAuthAccent;
    const authRaw = String(x.minimal_topbar_auth_display || "buttons").trim().toLowerCase();
    const showIconAuth = authRaw === "icon";
    const tbBg = String(x.minimal_topbar_bg || "").trim() || d.minimal_topbar_bg;
    const nameColor =
      String(x.minimal_topbar_store_name_color || "").trim() || d.minimal_topbar_store_name_color;
    const loginBg = String(x.minimal_topbar_login_bg || "").trim() || d.minimal_topbar_login_bg;
    const loginText = String(x.minimal_topbar_login_text || "").trim() || d.minimal_topbar_login_text;
    const loginBorder =
      String(x.minimal_topbar_login_border || "").trim() || d.minimal_topbar_login_border;
    const regBg = String(x.minimal_topbar_register_bg || "").trim() || d.minimal_topbar_register_bg;
    const regText = String(x.minimal_topbar_register_text || "").trim() || d.minimal_topbar_register_text;
    const regBorder =
      String(x.minimal_topbar_register_border || "").trim() || d.minimal_topbar_register_border;
    const loginLabelRaw = String(x.minimal_topbar_login_label || "").trim();
    const registerLabelRaw = String(x.minimal_topbar_register_label || "").trim();
    const tr = translations[language] || translations.ar;
    const loginBtnStyle = {
      background: loginBg,
      border: `1px solid ${loginBorder}`,
      color: loginText,
      fontWeight: 900,
      borderRadius: 999,
      padding: "8px 12px",
    };
    const storeTitleColor = nameColor;
    const barStyle = {
      background: tbBg,
      color: nameColor,
      "--catalog-topbar-bg": tbBg,
      "--catalog-topbar-name": nameColor,
      "--catalog-topbar-login-bg": loginBg,
      "--catalog-topbar-login-text": loginText,
      "--catalog-topbar-login-border": loginBorder,
      "--catalog-topbar-register-bg": regBg,
      "--catalog-topbar-register-text": regText,
      "--catalog-topbar-register-border": regBorder,
    };
    return {
      showIconAuth,
      barStyle,
      headerStyle: {
        ...barStyle,
        borderBottom: "1px solid #e5e7eb",
        boxShadow: "0 1px 0 rgba(0, 0, 0, 0.04)",
      },
      storeTitleStyle: {
        color: storeTitleColor,
        WebkitTextFillColor: storeTitleColor,
      },
      /** نص التحية بنفس لون عنوان المتجر (مع نفس الافتراضي عند عدم تخصيص اللون) */
      greetingStyle: {
        fontWeight: 900,
        letterSpacing: "-0.02em",
        fontSize: "1rem",
        color: storeTitleColor,
        background: "transparent",
        border: "none",
        padding: "8px 12px",
        borderRadius: 999,
      },
      loginBtnStyle,
      logoutBtnStyle: { ...loginBtnStyle },
      registerBtnStyle: {
        background: regBg,
        border: `1px solid ${regBorder}`,
        color: regText,
        fontWeight: 900,
        borderRadius: 999,
        padding: "8px 12px",
      },
      loginLabel: loginLabelRaw || tr.headerLogin,
      registerLabel: registerLabelRaw || tr.headerRegister,
      mobileUserChipStyle: {
        background: loginBg,
        border: `1px solid ${loginBorder}`,
        color: pickReadableIconColor(loginBg, loginText, "#ffffff"),
      },
    };
  }, [catalogLayoutEffective, language]);

  const catalogNavbarUi = useMemo(() => {
    const x = catalogLayoutEffective || DEFAULT_CATALOG_LAYOUT_EXTRAS;
    const d = DEFAULT_CATALOG_LAYOUT_EXTRAS;
    const links = normalizeCatalogNavbarLinks(x.navbar_links);
    const enabled = Boolean(x.navbar_enabled);
    const visibleRaw = Number(x.navbar_visible_count);
    const visibleCount = Number.isFinite(visibleRaw)
      ? Math.max(1, Math.min(10, Math.round(visibleRaw)))
      : Number(d.navbar_visible_count) || 4;
    const linkColor = String(x.navbar_link_color || "").trim() || d.navbar_link_color;
    const linkHoverColor =
      String(x.navbar_link_hover_color || "").trim() || d.navbar_link_hover_color;
    const moreLabel = String(x.navbar_more_label || "").trim() || "المزيد";
    const moreColor = String(x.navbar_more_color || "").trim() || linkColor;
    const moreHoverColor = String(x.navbar_more_hover_color || "").trim() || linkHoverColor;
    return {
      enabled,
      links,
      visibleCount,
      linkColor,
      linkHoverColor,
      moreLabel,
      moreColor,
      moreHoverColor,
      show: enabled && (canEdit || links.length > 0),
    };
  }, [catalogLayoutEffective, canEdit]);

  const patchCatalogLayoutExtras = useCallback((patch) => {
    setLayoutsEdit((p) => ({
      ...p,
      catalog_layout_extras: {
        ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
        ...(p.catalog_layout_extras || {}),
        ...patch,
      },
    }));
  }, []);

  const studioTranslations = useMemo(() => ({
    ar: {
      backToDashboard: "رجوع للداشبورد",
      customize: "تخصيص",
      hidePanel: "إخفاء اللوحة",
      save: "حفظ",
      saving: "جاري الحفظ...",
      panelTitle: "لوحة التخصيص",
      menuTitle: "خيارات التخصيص",
      menuHint: "اضغط على أي بند لعرض الخيارات. الثيمات الجاهزة من «التصاميم»؛ «المظهر العام والألوان» لعرض المحتوى الموحّد (1200/1400px) ولون جسم المتجر ولونَي التمييز العامين للصفحة؛ ألوان شريط التصنيفات المثبت وشريط المطعم العلوي من «المنتجات» عند ثيم المطعم؛ «السلة» لموضع زر السلة العائم وألوانه.",
      panelLibraryTitle: "العناصر",
      tabWidgets: "محتوى الصفحة",
      tabGlobals: "إعدادات المتجر",
      widgetsFavoritesHint: "استخدم البحث للانتقال بسرعة إلى أي إعداد في القائمة.",
      widgetsFavoritesGotIt: "حسنًا",
      publish: "نشر",
      studioSearchFocus: "بحث في اللوحة",
      collapsePanel: "طي اللوحة",
      docMenuAria: "قائمة الكتالوج",
      quickAddSection: "إضافة قسم",
      lookQuick: "المظهر والألوان",
      layersPanel: "اللوحة",
      draftBadge: "مسودة",
      back: "رجوع",
      addedSections: "الأقسام المضافة",
      hero: "الهيرو",
      products: "المنتجات",
      footer: "الفوتر",
      footerContact: "الفوتر والتواصل",
      navbar: "شريط التنقل",
      navbarSettings: "إعدادات شريط التنقل",
      themes: "التصاميم",
      lookAndFeel: "المظهر العام والألوان",
      customSection: "قسم مخصص",
      heroSettings: "إعدادات الهيرو",
      heroContentGroup: "نصوص الهيرو",
      heroDescriptionColor: "لون نص وصف الهيرو",
      heroLookGroup: "مظهر الهيرو (لون وصورة)",
      showHero: "إظهار الهيرو",
      minimalTopbarGroup: "الشريط العلوي (فوق الهيرو)",
      minimalTopbarHint:
        "يخصّص الشريط فوق الهيرو: الخلفية، اسم المتجر، وطريقة عرض الدخول (أيقونة أو أزرار نصية). ينطبق على كل أنماط الهيرو بما فيها صورة دائرية.",
      minimalTopbarAuthDisplay: "عرض تسجيل الدخول",
      minimalTopbarAuthIcon: "أيقونة فقط",
      minimalTopbarAuthButtons: "أزرار نصية (دخول + إنشاء حساب)",
      minimalTopbarBg: "خلفية الشريط",
      minimalTopbarStoreNameColor: "لون اسم المتجر",
      minimalTopbarLoginBlock: "زر تسجيل الدخول",
      minimalTopbarRegisterBlock: "زر إنشاء حساب",
      minimalTopbarButtonLabel: "نص الزر",
      minimalTopbarBorderColor: "لون الإطار",
      heroPrimaryColor: "لون الهيرو",
      heroPrimaryColorHint:
        "يؤثر على خلفية الهيرو، الطبقة الشفافة فوق صورة الهيرو، ولون نص زر «استكشف المجموعة» فقط. ألوان المنتجات وبقية الأزرار من «المظهر العام والألوان».",
      heroPrimaryFollowSite: "موازاة لون التمييز العام",
      siteAccentColors: "تمييز الصفحة (عام)",
      siteAccentHint:
        "اللونان الأساسي والثانوي يظهران في بطاقات المنتجات، أزرار الإضافة، التصنيفات، التحميل، وغيرها في الصفحة. لا يغيّران لون الهيرو إلا إذا لم تضبط لوناً خاصاً للهيرو.",
      heroImageLabel: "صورة الهيرو (ديسكتوب)",
      heroImageMobileLabel: "صورة الهيرو للجوال (اختياري)",
      heroImageMobileHint:
        "صورة مربّعة أو عمودية أنسب للشاشات الصغيرة. اختر «إظهار كاملة» لتجنّب قص المبنى/التصميم.",
      heroImageMobilePlaceholder: "لا توجد صورة جوال — سيُستخدم هيرو الديسكتوب",
      heroMobileFitLabel: "عرض صورة الجوال",
      heroMobileFitContain: "إظهار كاملة (بدون قص)",
      heroMobileFitCover: "تغطية الإطار (قد تُقص)",
      heroMobileFitHint: "اختر «إظهار كاملة» إن كانت الصورة مصممة للجوال وتريد ظهورها بالكامل بدون قص الأسفل أو الأعلى.",
      heroMobilePositionHint: "اسحب لتحديد الجزء الظاهر عند وضع التغطية",
      footerImageLabel: "خلفية الفوتر (صورة اختيارية)",
      footerImageHint: "تُستخدم كخلفية لكامل الفوتر، ومحتويات الفوتر تظهر فوقها.",
      footerImagePlaceholder: "لا توجد خلفية صورة — لون الفوتر فقط",
      footerImageOverlayLabel: "تعتيم فوق خلفية الفوتر",
      heroImagePreview: "معاينة صورة الهيرو",
      heroImagePlaceholder: "لا توجد صورة بعد — لون الهيرو هو الخلفية",
      changeImage: "تغيير الصورة",
      heroUrlPlaceholder: "أو الصق رابط الصورة هنا",
      heroImageHint2: "تظهر كخلفية للهيرو فوق طبقة شفافة بلون الهيرو. اتركها فارغة لاستخدام لون الهيرو وحده.",
      heroImagePosition: "موضع الصورة",
      heroImagePositionHint: "اسحب الدائرة على المعاينة لتحريك الصورة يميناً، يساراً، أعلى، أو أسفل.",
      heroImagePositionTop: "أعلى",
      heroImagePositionCenter: "وسط",
      heroImagePositionBottom: "أسفل",
      heroImageUploading: "جاري التحميل…",
      featuresBarTitle: "شريط المميزات",
      featuresBarColorsGroup: "ألوان الشريط",
      featuresBarColumnsGroup: "نصوص الأعمدة",
      showBar: "إظهار الشريط",
      column1: "عمود 1",
      column2: "عمود 2",
      column3: "عمود 3",
      shortDescription: "وصف قصير",
      featureTitlePh: "عنوان",
      barBackground: "خلفية الشريط",
      headingsColor: "لون العناوين",
      descriptionColor: "لون نص وصف أعمدة الشريط",
      catalogAppearance: "ألوان ومظهر الكتالوج",
      fontFamilyTitle: "عائلة الخط (عربي)",
      fontFamilyHint: "تُطبَّق على صفحة الكتالوج بالكامل. الخطوط تُحمَّل عند الاختيار.",
      fontStackSystem: "النظام الافتراضي",
      fontStackCairo: "Cairo",
      fontStackTajawal: "Tajawal",
      fontStackIbm: "IBM Plex Sans Arabic",
      fontStackAlmarai: "Almarai",
      fontStackNotoKufi: "Noto Kufi Arabic",
      lookHint: "الثيمات الجاهزة من «التصاميم». هنا: خط الصفحة الموحّد (1200 أو 1400 بكسل) لكل الهيدر والأقسام والمنتجات والفوتر، وألوان اختيارية لجسم المتجر، ولونَي التمييز العامين للصفحة (المنتجات والأزرار). ألوان شريط التصنيفات المثبت وشريط المطعم العلوي تُضبط من «المنتجات» عند ثيم المطعم. يُحفظ مع «حفظ / نشر».",
      generalColors: "ألوان المتجر العامة (اختياري)",
      loaderTextTitle: "نص شاشة التحميل",
      loaderTextHint:
        "يظهر تحت شعار المتجر. اتركه فارغاً لإخفاء النص. إن اخترت لوناً فقط يظهر شريط تحميل بلونك تحت الشعار.",
      loaderTextColor: "لون التحميل (نص / شريط)",
      loaderTextPlaceholder: "مثال: جاري التحميل…",
      loaderTextColorClear: "مسح اللون",
      enablePageBgLong: "تفعيل لون خلفية لجسم المتجر",
      pageBgHint: "لا يغيّر خلفية الهيرو أو الفوتر أو منطقة التصنيفات؛ يظهر في بقية الصفحة حسب الثيم.",
      pageContentWidthTitle: "عرض عمود المحتوى",
      pageContentWidthHint:
        "نفس القيمة بالبكسل للهيدر، أقسام الصفحة، منطقة المنتجات، والفوتر. «عرض كامل» للصفحة يوسّع الحاوية؛ يبقى خيار «عرض كامل» داخل قسم معيّن (مثل كاروسيل) كما هو.",
      contentWidthBoxed: "قياسي — 1200px",
      contentWidthWide: "عريض — 1400px",
      contentWidthFull: "عرض كامل",
      cartCustomizerTitle: "السلة",
      cartCustomizerHint:
        "موضع زر السلة العائم والألوان. عند فتح لوحة التخصيص، تُزاح السلة تلقائياً إن كانت على نفس جانب اللوحة حتى لا تغطيها.",
      floatingCartBgLabel: "لون السلة",
      floatingCartBgHint:
        "يُطبَّق على زر السلة في كل المواضع (شريط علوي أو عائم). مستقل عن لون الهيرو؛ اتركه فارغاً للون الافتراضي.",
      floatingCartIconColorLabel: "لون أيقونة السلة",
      floatingCartIconColorHint: "اختياري — إن تُرك فارغاً تُستخدم ألواناً مناسبة تلقائياً.",
      cartPositionLabel: "موضع السلة",
      cartPositionHint:
        "شريط علوي: داخل هيدر المتجر أو شريط المطعم أو شريط «متجر العرض». يمين أو شمال: زر عائم على حافة الشاشة.",
      topbar: "شريط علوي",
      topbarDesc: "مع الهيدر أو الشريط العلوي",
      right: "يمين",
      left: "شمال",
      floatingButton: "عائم على الحافة",
      restaurantTopbar: "شريط ثيم المطعم (أعلى الصفحة)",
      textIconColor: "لون النص والأيقونات",
      productsSettings: "إعدادات المنتجات",
      productsTitleLabel: "عنوان المنتجات",
      productsSubtitleLabel: "وصف المنتجات",
      productsAlign: "محاذاة العنوان",
      productsAlignStart: "بداية",
      productsAlignCenter: "وسط",
      productsAlignEnd: "نهاية",
      footerSettings: "إعدادات الفوتر",
      footerBgColor: "لون خلفية الفوتر",
      footerTextColor: "لون نص الفوتر",
      contactInfo: "بيانات التواصل",
      socialLinks: "روابط التواصل الاجتماعي",
      addSection: "إضافة قسم",
      removeSection: "حذف القسم",
      moveUp: "تحريك للأعلى",
      moveDown: "تحريك للأسفل",
      sectionType: "نوع القسم",
      sectionSettings: "إعدادات القسم",
      title: "العنوان",
      subtitle: "العنوان الفرعي",
      description: "الوصف",
      buttonText: "نص الزر",
      link: "الرابط",
      image: "الصورة",
      uploadImage: "رفع صورة",
      removeImage: "إزالة الصورة",
      backgroundColor: "لون الخلفية",
      textColor: "لون النص",
      accentColor: "اللون المميز",
      buttonBgColor: "لون الزر",
      buttonTextColor: "لون نص الزر",
      primaryColor: "لون التمييز الأساسي (الصفحة)",
      secondaryColor: "لون التمييز الثانوي (الصفحة)",
      pageBackground: "خلفية الصفحة",
      enablePageBg: "تفعيل خلفية مخصصة للصفحة",
      cartPosition: "موضع السلة",
      cartTopRight: "أعلى اليمين",
      cartTopLeft: "أعلى اليسار",
      cartBottomRight: "أسفل اليمين",
      cartBottomLeft: "أسفل اليسار",
      enable: "تفعيل",
      disable: "إيقاف",
      show: "إظهار",
      hide: "إخفاء",
      yes: "نعم",
      no: "لا",
      none: "بدون",
      auto: "تلقائي",
      light: "فاتح",
      dark: "داكن",
      width: "العرض",
      height: "الارتفاع",
      padding: "الحشو الداخلي",
      margin: "الحاشية",
      radius: "التدوير",
      shadow: "الظل",
      align: "المحاذاة",
      alignStart: "بداية",
      alignCenter: "وسط",
      alignEnd: "نهاية",
      columns: "عدد الأعمدة",
      productsHeading: "عنوان قسم المنتجات",
      productsSubheading: "وصف قسم المنتجات",
      productsLayout: "شكل عرض المنتجات",
      productCardSize: "حجم بطاقة المنتج",
      productImageShape: "شكل صورة المنتج",
      shapeSquare: "مربع",
      shapeRound: "دائري",
      shapeRounded: "زوايا دائرية",
      heroBgType: "نوع الخلفية",
      heroBgColor: "لون",
      heroBgImage: "صورة",
      heroImageUrl: "رابط الصورة",
      heroImageHint: "ارفع صورة أو أدخل رابط مباشر للصورة.",
      footerBg: "خلفية الفوتر",
      footerText: "نص الفوتر",
      phone: "رقم الهاتف",
      email: "البريد الإلكتروني",
      address: "العنوان",
      facebook: "فيسبوك",
      instagram: "إنستغرام",
      whatsapp: "واتساب",
      featuresBar: "شريط المميزات",
      featuresBarBg: "خلفية الشريط",
      featuresBarText: "نص الشريط",
      featuresBarAccent: "لون مميز للشريط",
      feature: "ميزة",
      addFeature: "إضافة ميزة",
      removeFeature: "إزالة الميزة",
      preset: "نمط جاهز",
      preview: "معاينة",
      reset: "إعادة تعيين",
      apply: "تطبيق",
      cancel: "إلغاء",
      confirm: "تأكيد",
      delete: "حذف",
      edit: "تعديل",
      duplicate: "تكرار",
      sectionAddedSuccess: "تم إضافة القسم بنجاح",
      sectionDeletedSuccess: "تم حذف القسم بنجاح",
      saveSuccess: "تم الحفظ بنجاح",
      saveError: "تعذّر الحفظ. حاول مرة أخرى.",
      saveProgressSettings: "حفظ إعدادات المتجر…",
      saveProgressLayouts: "حفظ التخطيط والعرض…",
      saveProgressBuilder: "حفظ أقسام الصفحة…",
      saveFailedStepPrefix: "فشلت المرحلة:",
      stepSettingsName: "إعدادات المتجر",
      stepLayoutsName: "التخطيط والعرض",
      stepBuilderName: "أقسام الصفحة (البناّء)",
      partialSaveWarning:
        "قد تكون بعض التغييرات محفوظة على الخادم والبعض لا. راجع الصفحة ثم أعد المحاولة أو عدّل يدوياً.",
      themeApplyConfirmTitle: "تطبيق التصميم الجاهز؟",
      themeApplyConfirmMessage:
        "سيتم نسخ القالب إلى إعدادات متجرك وأقسام الصفحة (نسخة مستقلة — لن يتأثر القالب الأصلي). ليس هناك تراجع تلقائي. احفظ من «نشر» لتثبيت التغييرات.",
      themeApplyConfirmAction: "نعم، طبّق القالب",
      themeApplyCancel: "إلغاء",
      draftLocalBanner:
        "توجد مسودة محلية (حُفظت تلقائياً كل دقيقتين تقريباً) تختلف عن آخر تحميل من الخادم.",
      draftRestore: "استعادة المسودة",
      draftDismiss: "تجاهل",
      unsavedChangesShort: "تغييرات غير محفوظة",
      leaveStudioTitle: "مغادرة الاستوديو؟",
      leaveStudioMessage: "لديك تغييرات غير محفوظة. إن غادرت الآن قد تفقد التعديلات التي لم تحفظها.",
      leaveStudioStay: "البقاء",
      leaveStudioLeave: "مغادرة",
      undo: "تراجع",
      redo: "إعادة",
      undoTitle: "تراجع (Ctrl+Z)",
      redoTitle: "إعادة (Ctrl+Y)",
      historyRecentTitle: "آخر التعديلات",
      toastChangesSaved: "تم حفظ التغييرات",
      previewDesktop: "عرض سطح المكتب",
      previewMobile: "معاينة موبايل",
      previewTablet: "معاينة لوحي",
      previewZoomLabel: "تكبير",
      visitorPreview: "معاينة كزائر",
      shortcutsTitle: "اختصارات",
      shortcutsClose: "إغلاق",
      shortcutSave: "حفظ",
      shortcutUndo: "تراجع",
      shortcutRedo: "إعادة",
      shortcutPanel: "اللوحة",
      shortcutHelp: "المساعدة",
      themePreviewBadge: "معاينة قالب فقط — لم يُحفظ على الخادم بعد",
      themePreviewCancel: "إلغاء المعاينة",
      themePreviewKeep: "متابعة",
      themePreviewVerb: "معاينة",
      menuSearchPlaceholder: "بحث في القائمة…",
      breadcrumbCustomize: "خيارات التخصيص",
      menuSubBuilder: "ترتيب الكتل وإضافة أقسام",
      menuSubHero: "عنوان، خلفية، أزرار",
      menuSubProducts: "العناوين، الفلاتر، العرض",
      menuSubFooter: "التواصل والروابط",
      menuSubNavbar: "روابط الأقسام والقائمة",
      menuSubThemes: "قوالب جاهزة",
      menuSubLook: "الخط والألوان والعرض",
      menuSubCart: "موضع السلة والألوان",
      navbarEnable: "تفعيل شريط التنقل",
      navbarEnableHint: "أضف كلمات في الشريط العلوي تربطها بأقسام الصفحة. على الجوال تظهر قائمة جانبية.",
      navbarVisibleCount: "عدد الروابط قبل «المزيد»",
      navbarLinkColor: "لون الروابط",
      navbarLinkHoverColor: "لون الروابط عند المرور",
      navbarMoreLabel: "نص زر المزيد",
      navbarMoreLabelPh: "المزيد",
      navbarMoreColor: "لون زر المزيد",
      navbarMoreHoverColor: "لون المزيد عند المرور",
      navbarLinksGroup: "روابط التنقل",
      navbarAddLink: "إضافة رابط",
      navbarLinkLabel: "النص المعروض",
      navbarLinkLabelPh: "مثال: منتجاتنا",
      navbarLinkTarget: "الوجهة",
      navbarTargetHero: "الهيرو",
      navbarTargetProducts: "قسم المنتجات",
      navbarTargetFooter: "الفوتر",
      navbarTargetSection: "قسم مخصص",
      navbarTargetUrl: "رابط خارجي",
      navbarPageSectionsGroup: "أقسام الصفحة",
      navbarPickSection: "اختر القسم",
      navbarNoPageSections: "لا توجد أقسام مضافة بعد على الصفحة",
      navbarExternalUrl: "الرابط",
      navbarOpenNewTab: "فتح في تبويب جديد",
      navbarLinkColorOverride: "لون مخصص (اختياري)",
      navbarLinkHoverOverride: "لون المرور (اختياري)",
      navbarRemoveLink: "حذف",
      navbarEmpty: "لا توجد روابط بعد — أضف رابطاً للبدء.",
      navbarMobileMenu: "القائمة",
      navbarCloseMenu: "إغلاق القائمة",
      studioPanelLangNote: "لغة اللوحة للاستوديو فقط؛ صفحة المتجر للزوار تبقى بالعربية.",
      tourNeverAgain: "لا تظهر هذه الجولة مرة أخرى",
      uploadError: "تعذّر رفع الصورة.",
      loading: "جارٍ التحميل...",
      noData: "لا توجد بيانات",
      optional: "اختياري",
      required: "مطلوب",
      sectionsLibrary: "مكتبة الأقسام",
      pickSection: "اختر قسماً لإضافته",
      sectionHero: "هيرو",
      sectionFeatures: "مميزات",
      sectionTextImage: "نص + صورة",
      sectionGallery: "معرض",
      sectionTestimonials: "آراء العملاء",
      sectionFaq: "الأسئلة الشائعة",
      sectionCta: "دعوة لاتخاذ إجراء",
      sectionBanner: "بانر",
      sectionVideo: "فيديو",
      sectionRichText: "نص منسّق",
      sectionContact: "تواصل",
      sectionCarousel: "كاروسيل",
      sectionPromo: "عرض ترويجي",
      sectionCountdown: "عدّاد تنازلي",
      sectionCategories: "الفئات",
      sectionMap: "خريطة",
      sectionInstagram: "إنستغرام",
      sectionLogo: "شعارات",
      uppercase: "حروف كبيرة",
      lowercase: "حروف صغيرة",
      bold: "غامق",
      regular: "عادي",
      light_weight: "خفيف",
      fontWeight: "وزن الخط",
      fontSize: "حجم الخط",
      letterSpacing: "تباعد الأحرف",
      lineHeight: "ارتفاع السطر",
      categoryStripTitle: "شريط التصنيفات (مثبت)",
      categoryStripBg: "خلفية الشريط",
      categoryStripBgOpacity: "شفافية الخلفية",
      categoryStripBorderColor: "لون الحد السفلي",
      categoryStripBorderOpacity: "شفافية الحد",
      productsCardRadiusPx: "زوايا بطاقة المنتج (بكسل)",
      productsCardBgColorLabel: "لون خلفية بطاقة المنتج",
      productsCartIconBorderWidthLabel: "سمك حد أيقونة السلة",
      productsCartIconBorderColorLabel: "لون حد أيقونة السلة",
      productsCartIconBorderHint: "يظهر حول أيقونة السلة في بطاقة المنتج.",
      productsCartIconSideLabel: "موقع أيقونة السلة",
      productsCartIconSideHint: "يمين أو يسار أسفل بطاقة المنتج — نفس الموقع لكل المنتجات.",
      productsCartIconSideRight: "يمين",
      productsCartIconSideLeft: "يسار",
      productsSectionWidthLabel: "عرض قسم المنتجات",
      productsSectionWidthFollowPage: "يتبع عرض الصفحة",
      productsCardRadiusHint: "اترك الحقل فارغاً لاستخدام الشكل الافتراضي للثيم. أدخل 0 لمربعات حادة.",
      productsCardRadiusPlaceholder: "تلقائي",
      restaurantSquareImageRadius: "زوايا صورة المنتج (مربع — ثيم المطعم)",
      restaurantSquareImageRadiusHint:
        "بالبكسل. اترك الحقل فارغاً للافتراضي (~18 في الشبكة والكاروسيل، حادّ في قائمة المنيو).",
      restaurantSquareImageRadiusPlaceholder: "تلقائي",
      themePickerLabel: "التصاميم الجاهزة",
      themePickerHint:
        "اختر قالباً لنسخه إلى متجرك — التعديلات لا تغيّر القالب الأصلي. احفظ من «نشر» لتثبيت التغييرات.",
      catalogThemesLoading: "جاري تحميل القوالب…",
      catalogThemesEmpty: "لا توجد قوالب نشطة في المكتبة بعد.",
      catalogThemesLoadError: "تعذّر تحميل القوالب",
      themeMinimal: "متجر افتراضي",
      themeMinimalDesc: "هيرو + عروض + منتجات + فوتر",
      themeShowcase: "متجر عرض",
      themeShowcaseDesc: "هيرو عام + أقسام عرض/كاروسيل + منتجات + فوتر",
      themeRestaurant: "مطاعم / منيو",
      themeRestaurantDesc: "خلفية داكنة + منتجات ليست + كاتيجوري توب بار ثابت",
      themeGeneralStore: "متجر تجزئة",
      themeGeneralStoreDesc: "هيرو + شريط مميزات + بطاقات قبل المنتجات",
      themeDarkStore: "واجهة داكنة",
      themeDarkStoreDesc: "عرض + كاروسيل + أقسام على خلفية ليلية",
      themeElectronicsStore: "إلكترونيات",
      themeElectronicsStoreDesc: "عرض تقني + كاروسيل صور وفلاتر",
      themeUrbanico: "أوربانيكو — أزياء عصرية",
      themeUrbanicoDesc: "هيرو بملء الشاشة + بطاقات ثقة + فئات + عروض (عربي RTL)",
      themeCustomStore: "متجر مخصص — أزياء عصرية",
      themeCustomStoreDesc: "زوايا حادة • موبايل مثل المرجع • رجالي/نسائي/أطفال",
      productsSectionLookTitle: "مظهر قسم المنتجات",
      sectionPaddingY: "هوامش القسم عمودياً",
      sectionGridGap: "مسافة بين بطاقات المنتجات",
      productCardShapeSelect: "شكل بطاقة المنتج",
      paddingTight: "ضيق",
      paddingNormal: "عادي",
      paddingWide: "واسع",
      gapSmall: "صغيرة",
      gapMedium: "متوسطة",
      gapLarge: "كبيرة",
      cardShapeDefault: "افتراضي",
      cardShapeFlat: "مسطح (حدود خفيفة)",
      cardShapeSoft: "ناعم (زوايا دائرية)",
      productImageShapeLabel: "شكل صورة المنتج (الشبكة)",
      productImageShapeHint:
        "الدائرة: 1:1. المربع والمستطيل الطولي: نسبة ثابتة 3:4 (عرض×طول) والصورة تملأ الإطار دائماً عند أي حجم.",
      shapeCircle: "دائرة",
      shapeCircleDesc: "أكبر ومركّز",
      shapeSquare: "مستطيل طولي",
      shapeSquareDesc: "نسبة 3:4 · يملأ الإطار",
      shapePortrait: "مستطيل طولي",
      shapePortraitDesc: "نسبة 3:4 ثابتة",
      productImageBlockTitle: "صورة المنتج في الشبكة",
      productImageSizeLabel: "حجم صورة المنتج",
      productImageSizeDims: "العرض × الطول",
      productImageSizeRatioFixed: "النسبة ثابتة 3:4 — الإطار يمتلئ بالصورة عند أي حجم",
      productImageSizeMaxHint: "عند 360px تملأ الصورة عرض البطاقة بالكامل.",
      showProductDescription: "إظهار وصف المنتج تحت الاسم",
      productsTextsGroup: "نصوص قسم المنتجات",
      productsSectionColorsMini: "ألوان عنوان القسم",
      productsHeadingColorLabel: "لون عنوان «المنتجات»",
      productsSubtitleColorLabel: "لون وصف القسم",
      productsCardTextColorsGroup: "نصوص بطاقة المنتج",
      productsCardNameColorLabel: "لون اسم المنتج",
      productsCardDescColorLabel: "لون وصف المنتج",
      productsUnderlineColorLabel: "لون الشريط تحت العنوان",
      productsGridNavGroup: "الشبكة، التصنيفات والفلاتر",
      productsFiltersNavTitle: "التصنيفات والفلاتر",
      categoryNavPlacementLabel: "مكان قائمة التصنيفات",
      categoryNavOptionTop: "شريط أفقي فوق المنتجات",
      categoryNavOptionSidebar: "عمود جانبي بجانب المنتجات",
      filtersSidebarLabel: "عمود فلاتر (السعر / الجديد / العروض)",
      categoryPillsAlignLabel: "محاذاة أزرار التصنيفات",
      categoryPillsAlignHint: "للشريط الأفقي فقط؛ لا يغيّر ألوان الأزرار أو شكلها.",
      categoryPillsAlignDefault: "تلقائي (حسب عرض الشاشة والثيم)",
      restaurantMenuColorsTitle: "مظهر مطعم / منيو (اختياري)",
      restaurantMenuColorsHint:
        "الألوان أدناه تُحفظ مع الكتالوج، وتظهر بشكل كامل عند تفعيل ثيم «مطاعم / منيو».",
      restaurantMenuColorsApplyNote:
        "جميع الإعدادات التالية خاصة بثيم «مطاعم / منيو» فقط. من «التصاميم» غيّر الثيم إلى مطاعم/منيو لمعاينة الفرق على الصفحة.",
      restaurantMenuBaseColors: "ألوان الخلفية والنص",
      restaurantMenuAccentColors: "السعر وزر الإضافة",
      restaurantPageBg: "خلفية صفحة المنيو (خلف البطاقات)",
      restaurantInk: "لون النصوص والعناوين في المنيو",
      restaurantAccentGreen: "لون السعر في بطاقة المنتج",
      restaurantAccentWarm: "لون زر «إضافة» في بطاقة المنتج",
      productsDecorTitle: "ديكور خلفية قسم المنتجات",
      decorPattern: "النمط",
      decorNone: "بدون",
      decorCircles: "دوائر كبيرة",
      decorArcs: "أقواس",
      decorRings: "حلقات",
      decorWaves: "موجات",
      decorColor: "لون الديكور",
      decorOpacity: "شفافية الديكور",
      goToSection: "اذهب إلى القسم",
      noAddedSections: "لا توجد أقسام مضافة.",
      reorderHintShort: "غيّر الترتيب من الأسهم أو السحب",
      mainProductsBlock: "المنتجات الأساسية",
      addNewSection: "+ إضافة قسم جديد",
      typeText: "نص",
      typeTextImage: "نص + صورة",
      typeImageBanner: "بانر صورة",
      typeFeaturedProducts: "منتجات مختارة",
      typeCarousel: "كاروسيل",
      typeProductsCarousel: "كاروسيل منتجات",
      typeFeaturesGrid: "بطاقات",
      sectionLabelHero: "هيرو",
      sectionLabelText: "نص",
      sectionLabelSplit: "نص + صورة",
      sectionLabelImageBanner: "بانر صورة",
      sectionLabelFeatures: "بطاقات",
      sectionLabelFeatured: "منتجات مختارة",
      sectionLabelCarousel: "كاروسيل",
      sectionLabelProductsCarousel: "كاروسيل منتجات",
      sectionFallback: "سكشن",
      addPickerTitle: "اختر نوع السكشن",
      addPickerZoneBefore: "يُضاف قبل المنتجات الأساسية",
      addPickerZoneAfter: "يُضاف بعد المنتجات الأساسية",
      addPickerCancelledTitle: "تم إلغاء هذه الشاشة. استخدم «الأقسام → إضافة قسم جديد» لإضافة الأقسام وترتيبها.",
      tourWelcomeTitle: "مرحبًا بك في استوديو الكتالوج",
      tourWelcomeBody:
        "هنا تعرض صفحة متجرك كما يراها الزوار. عدّل التصميم من اللوحة الجانبية، ثم احفظ التغييرات بالزر الأصفر عند الانتهاء.",
      tourCustomizeTitle: "فتح لوحة التخصيص",
      tourCustomizeBody:
        "اضغط «تخصيص» لفتح القائمة: الهيرو، المنتجات، الأقسام الإضافية، الألوان، السلة، والمزيد.",
      tourSaveTitle: "حفظ ونشر التغييرات",
      tourSaveBody: "بعد أي تعديل على الألوان أو الأقسام أو الهيرو، اضغط «حفظ» لتطبيقها على الكتالوج العام.",
      tourDashboardTitle: "العودة للوحة التحكم",
      tourDashboardBody: "عندما تنتهي من التصميم يمكنك العودة بسرعة إلى لوحة التحكم لإدارة المنتجات والطلبات.",
      tourPanelTitle: "قائمة التخصيص",
      tourPanelBody:
        "من هنا تصل لكل أجزاء المتجر: الأقسام، الهيرو، المنتجات، التصاميم، وأكثر. استخدم «رجوع» للعودة لهذه القائمة بعد فتح أي بند.",
      tourMenuIntroTitle: "خيارات التخصيص",
      tourMenuIntroBody:
        "هذا الملخص يشرح باختصار ما تفعله اللوحة. اضغط أي بطاقة أدناه للانتقال إلى إعدادات ذلك الجزء.",
      tourMenuBuilderTitle: "الأقسام المضافة",
      tourMenuBuilderBody: "أضف سكشننات (نص، صورة، منتجات مختارة، كاروسيل…) فوق أو تحت المنتجات، ورتّبها بالسحب أو الأسهم.",
      tourMenuHeroTitle: "الهيرو",
      tourMenuHeroBody: "عنوان الصفحة الرئيسية، الوصف، لون الهيرو فقط، وصورة الخلفية الاختيارية.",
      tourMenuProductsTitle: "المنتجات",
      tourMenuProductsBody: "عنوان قسم المنتجات، المحاذاة، والوصف الظاهر فوق الشبكة.",
      tourMenuFooterTitle: "الفوتر",
      tourMenuFooterBody: "ألوان الفوتر ومعلومات التواصل والروابط الاجتماعية.",
      tourMenuThemesTitle: "التصاميم",
      tourMenuThemesBody: "قوالب جاهزة (مثل ثيم المطعم) لتغيير مظهر المتجر بسرعة.",
      tourMenuLookTitle: "المظهر العام والألوان",
      tourMenuLookBody: "ألوان الصفحة والخلفيات الاختيارية لجسم المتجر.",
      tourMenuCartTitle: "السلة",
      tourMenuCartBody: "موضع زر السلة العائم وألوانه حتى لا يتعارض مع لوحة التخصيص.",
      close: "إغلاق",
    },
    en: {
      backToDashboard: "Back to dashboard",
      customize: "Customize",
      hidePanel: "Hide panel",
      save: "Save",
      saving: "Saving...",
      panelTitle: "Customizer panel",
      menuTitle: "Customization options",
      menuHint: "Click any item to view its options. Ready-made themes are under \"Designs\"; \"General look & colors\" sets the unified content width (1200/1400px), optional page background, and the two site-wide accent colors; sticky category bar and restaurant top bar are under \"Products\" when the restaurant theme is active; \"Cart\" sets the floating cart position and colors.",
      panelLibraryTitle: "Elements",
      tabWidgets: "Page content",
      tabGlobals: "Store settings",
      widgetsFavoritesHint: "Use search to jump to any setting in the panel.",
      widgetsFavoritesGotIt: "Got it",
      publish: "Publish",
      studioSearchFocus: "Search in panel",
      collapsePanel: "Collapse panel",
      docMenuAria: "Catalog menu",
      quickAddSection: "Add section",
      lookQuick: "Look & colors",
      layersPanel: "Panel",
      draftBadge: "Draft",
      back: "Back",
      addedSections: "Added sections",
      hero: "Hero",
      products: "Products",
      footer: "Footer",
      footerContact: "Footer & contact",
      navbar: "Navigation bar",
      navbarSettings: "Navigation settings",
      themes: "Templates",
      lookAndFeel: "General look & colors",
      customSection: "Custom section",
      heroSettings: "Hero settings",
      heroContentGroup: "Hero text",
      heroDescriptionColor: "Hero description color",
      heroLookGroup: "Hero look (color & image)",
      showHero: "Show hero",
      minimalTopbarGroup: "Top bar (above hero)",
      minimalTopbarHint:
        "Top bar above the hero: background, store name, and log in display (icon or text buttons). Applies to all hero layouts including split circle.",
      minimalTopbarAuthDisplay: "Log in display",
      minimalTopbarAuthIcon: "Icon only",
      minimalTopbarAuthButtons: "Text buttons (log in + sign up)",
      minimalTopbarBg: "Bar background",
      minimalTopbarStoreNameColor: "Store name color",
      minimalTopbarLoginBlock: "Log in button",
      minimalTopbarRegisterBlock: "Sign up button",
      minimalTopbarButtonLabel: "Button label",
      minimalTopbarBorderColor: "Border color",
      heroPrimaryColor: "Hero color",
      heroPrimaryColorHint:
        "Affects the hero background, the translucent overlay on a hero image, and the hero “explore” button text only. Product cards and other buttons use the site accents in General look & colors.",
      heroPrimaryFollowSite: "Match site primary accent",
      siteAccentColors: "Site accents (global)",
      siteAccentHint:
        "Primary and secondary accents appear on product cards, add buttons, loaders, and other UI across the page. They do not change the hero unless the hero color is left to follow the site primary.",
      heroImageLabel: "Hero image (desktop)",
      heroImageMobileLabel: "Mobile hero image (optional)",
      heroImageMobileHint:
        "A square or portrait image works best on phones. Use “Show full image” to avoid cropping.",
      heroImageMobilePlaceholder: "No mobile image — desktop hero will be used",
      heroMobileFitLabel: "Mobile image display",
      heroMobileFitContain: "Show full image (no crop)",
      heroMobileFitCover: "Cover frame (may crop)",
      heroMobileFitHint: "Choose “Show full image” for a designed mobile asset so nothing is cropped top or bottom.",
      heroMobilePositionHint: "Drag to set the visible area when using cover",
      footerImageLabel: "Footer background image (optional)",
      footerImageHint: "Used as the full footer background; footer content sits on top.",
      footerImagePlaceholder: "No background image — footer color only",
      footerImageOverlayLabel: "Darken overlay on footer image",
      heroImagePreview: "Hero image preview",
      heroImagePlaceholder: "No image yet — the hero color is used as background",
      changeImage: "Change image",
      heroUrlPlaceholder: "Or paste an image URL here",
      heroImageHint2: "Shown as the hero background over a translucent overlay of the hero color. Leave empty to use only the hero color.",
      heroImagePosition: "Image position",
      heroImagePositionHint: "Drag the handle on the preview to pan the image in any direction.",
      heroImagePositionTop: "Top",
      heroImagePositionCenter: "Center",
      heroImagePositionBottom: "Bottom",
      heroImageUploading: "Uploading image…",
      featuresBarTitle: "Features bar",
      featuresBarColorsGroup: "Bar colors",
      featuresBarColumnsGroup: "Column texts",
      showBar: "Show bar",
      column1: "Column 1",
      column2: "Column 2",
      column3: "Column 3",
      shortDescription: "Short description",
      featureTitlePh: "Title",
      barBackground: "Bar background",
      headingsColor: "Headings color",
      descriptionColor: "Features bar column description color",
      catalogAppearance: "Catalog colors & appearance",
      fontFamilyTitle: "Font family (Arabic)",
      fontFamilyHint: "Applies to the whole catalog page. Fonts load when selected.",
      fontStackSystem: "System default",
      fontStackCairo: "Cairo",
      fontStackTajawal: "Tajawal",
      fontStackIbm: "IBM Plex Sans Arabic",
      fontStackAlmarai: "Almarai",
      fontStackNotoKufi: "Noto Kufi Arabic",
      lookHint: "Ready-made themes are under \"Designs\". Here: unified page column width (1200 or 1400px) for header, sections, products, and footer, optional store body colors, and the two global site accent colors (products & buttons). Sticky category bar and restaurant top bar are under \"Products\" when the restaurant theme is active. Saved on \"Save / Publish\".",
      generalColors: "General store colors (optional)",
      loaderTextTitle: "Loading screen text",
      loaderTextHint:
        "Shown under the store logo. Leave empty to hide text. Color alone shows a colored progress bar under the logo.",
      loaderTextColor: "Loading color (text / bar)",
      loaderTextPlaceholder: "e.g. Loading…",
      loaderTextColorClear: "Clear color",
      enablePageBgLong: "Enable a background color for the store body",
      pageBgHint: "Does not change the hero, footer or categories area background; it appears across the rest of the page depending on the theme.",
      pageContentWidthTitle: "Content column width",
      pageContentWidthHint:
        "Same pixel width for the header, page sections, products area, and footer. “Full width” expands the column; a section can still use its own “full width” (e.g. carousel bleed) when needed.",
      contentWidthBoxed: "Standard — 1200px",
      contentWidthWide: "Wide — 1400px",
      contentWidthFull: "Full width",
      cartCustomizerTitle: "Cart",
      cartCustomizerHint:
        "Floating cart position and colors. While the customizer is open, the cart shifts inward if it sits on the same edge as the panel so it does not cover it.",
      floatingCartBgLabel: "Cart button color",
      floatingCartBgHint:
        "Applies to the cart in every placement (top bar or floating). Independent from the hero color; leave empty for the default tint.",
      floatingCartIconColorLabel: "Cart icon color",
      floatingCartIconColorHint: "Optional — leave empty for sensible automatic colors.",
      cartPositionLabel: "Cart placement",
      cartPositionHint:
        "Top bar: inside the store header, restaurant bar, or Showcase top strip. Right or left: a floating button on the screen edge.",
      topbar: "Top bar",
      topbarDesc: "With the header or top strip",
      right: "Right",
      left: "Left",
      floatingButton: "Floating on edge",
      restaurantTopbar: "Restaurant theme top bar",
      textIconColor: "Text & icons color",
      productsSettings: "Products settings",
      productsTitleLabel: "Products section title",
      productsSubtitleLabel: "Products section subtitle",
      productsAlign: "Heading alignment",
      productsAlignStart: "Start",
      productsAlignCenter: "Center",
      productsAlignEnd: "End",
      footerSettings: "Footer settings",
      footerBgColor: "Footer background",
      footerTextColor: "Footer text color",
      contactInfo: "Contact info",
      socialLinks: "Social links",
      addSection: "Add section",
      removeSection: "Delete section",
      moveUp: "Move up",
      moveDown: "Move down",
      sectionType: "Section type",
      sectionSettings: "Section settings",
      title: "Title",
      subtitle: "Subtitle",
      description: "Description",
      buttonText: "Button text",
      link: "Link",
      image: "Image",
      uploadImage: "Upload image",
      removeImage: "Remove image",
      backgroundColor: "Background color",
      textColor: "Text color",
      accentColor: "Accent color",
      buttonBgColor: "Button color",
      buttonTextColor: "Button text color",
      primaryColor: "Site primary accent",
      secondaryColor: "Site secondary accent",
      pageBackground: "Page background",
      enablePageBg: "Enable custom page background",
      cartPosition: "Cart position",
      cartTopRight: "Top right",
      cartTopLeft: "Top left",
      cartBottomRight: "Bottom right",
      cartBottomLeft: "Bottom left",
      enable: "Enable",
      disable: "Disable",
      show: "Show",
      hide: "Hide",
      yes: "Yes",
      no: "No",
      none: "None",
      auto: "Auto",
      light: "Light",
      dark: "Dark",
      width: "Width",
      height: "Height",
      padding: "Padding",
      margin: "Margin",
      radius: "Radius",
      shadow: "Shadow",
      align: "Align",
      alignStart: "Start",
      alignCenter: "Center",
      alignEnd: "End",
      columns: "Columns",
      productsHeading: "Products section title",
      productsSubheading: "Products section subtitle",
      productsLayout: "Products layout",
      productCardSize: "Product card size",
      productImageShape: "Product image shape",
      shapeSquare: "Square",
      shapeRound: "Round",
      shapeRounded: "Rounded",
      heroBgType: "Background type",
      heroBgColor: "Color",
      heroBgImage: "Image",
      heroImageUrl: "Image URL",
      heroImageHint: "Upload an image or paste a direct image URL.",
      footerBg: "Footer background",
      footerText: "Footer text",
      phone: "Phone",
      email: "Email",
      address: "Address",
      facebook: "Facebook",
      instagram: "Instagram",
      whatsapp: "WhatsApp",
      featuresBar: "Features bar",
      featuresBarBg: "Bar background",
      featuresBarText: "Bar text",
      featuresBarAccent: "Bar accent",
      feature: "Feature",
      addFeature: "Add feature",
      removeFeature: "Remove feature",
      preset: "Preset",
      preview: "Preview",
      reset: "Reset",
      apply: "Apply",
      cancel: "Cancel",
      confirm: "Confirm",
      delete: "Delete",
      edit: "Edit",
      duplicate: "Duplicate",
      sectionAddedSuccess: "Section added successfully",
      sectionDeletedSuccess: "Section deleted successfully",
      saveSuccess: "Saved successfully",
      saveError: "Could not save. Please try again.",
      saveProgressSettings: "Saving store settings…",
      saveProgressLayouts: "Saving layout & display…",
      saveProgressBuilder: "Saving page sections…",
      saveFailedStepPrefix: "Failed at step:",
      stepSettingsName: "Store settings",
      stepLayoutsName: "Layout & display",
      stepBuilderName: "Page builder sections",
      partialSaveWarning:
        "Some changes may have been saved on the server and some may not. Review the page, then retry or fix manually.",
      themeApplyConfirmTitle: "Apply this design theme?",
      themeApplyConfirmMessage:
        "The template will be copied into your store settings and page sections (your own copy — the library original stays unchanged). There is no automatic undo. Use Publish to save.",
      themeApplyConfirmAction: "Yes, apply theme",
      themeApplyCancel: "Cancel",
      draftLocalBanner:
        "A local draft (auto-saved about every 2 minutes) differs from what was last loaded from the server.",
      draftRestore: "Restore draft",
      draftDismiss: "Dismiss",
      unsavedChangesShort: "Unsaved changes",
      leaveStudioTitle: "Leave the studio?",
      leaveStudioMessage:
        "You have unsaved changes. If you leave now, edits that were not saved may be lost.",
      leaveStudioStay: "Stay",
      leaveStudioLeave: "Leave",
      undo: "Undo",
      redo: "Redo",
      undoTitle: "Undo (Ctrl+Z)",
      redoTitle: "Redo (Ctrl+Y)",
      historyRecentTitle: "Recent edits",
      toastChangesSaved: "Changes saved",
      previewDesktop: "Desktop view",
      previewMobile: "Mobile preview",
      previewTablet: "Tablet preview",
      previewZoomLabel: "Zoom",
      visitorPreview: "Preview as visitor",
      shortcutsTitle: "Keyboard shortcuts",
      shortcutsClose: "Close",
      shortcutSave: "Save",
      shortcutUndo: "Undo",
      shortcutRedo: "Redo",
      shortcutPanel: "Panel",
      shortcutHelp: "Help",
      themePreviewBadge: "Theme preview only — not saved to the server yet",
      themePreviewCancel: "Cancel preview",
      themePreviewKeep: "Continue",
      themePreviewVerb: "Preview",
      menuSearchPlaceholder: "Search menu…",
      breadcrumbCustomize: "Customize",
      menuSubBuilder: "Reorder blocks and add sections",
      menuSubHero: "Title, background, buttons",
      menuSubProducts: "Titles, filters, layout",
      menuSubFooter: "Contact and links",
      menuSubNavbar: "Section links and menu",
      menuSubThemes: "Copy a library template into your store",
      menuSubLook: "Font, colors, width",
      menuSubCart: "Cart position and colors",
      navbarEnable: "Enable navigation bar",
      navbarEnableHint: "Add words in the top bar linked to page sections. On mobile they open in a side drawer.",
      navbarVisibleCount: "Links before “More”",
      navbarLinkColor: "Link color",
      navbarLinkHoverColor: "Link hover color",
      navbarMoreLabel: "More button label",
      navbarMoreLabelPh: "More",
      navbarMoreColor: "More button color",
      navbarMoreHoverColor: "More hover color",
      navbarLinksGroup: "Navigation links",
      navbarAddLink: "Add link",
      navbarLinkLabel: "Label",
      navbarLinkLabelPh: "e.g. Our products",
      navbarLinkTarget: "Target",
      navbarTargetHero: "Hero",
      navbarTargetProducts: "Products section",
      navbarTargetFooter: "Footer",
      navbarTargetSection: "Custom section",
      navbarTargetUrl: "External URL",
      navbarPageSectionsGroup: "Page sections",
      navbarPickSection: "Pick section",
      navbarNoPageSections: "No custom sections on the page yet",
      navbarExternalUrl: "URL",
      navbarOpenNewTab: "Open in new tab",
      navbarLinkColorOverride: "Custom color (optional)",
      navbarLinkHoverOverride: "Hover color (optional)",
      navbarRemoveLink: "Remove",
      navbarEmpty: "No links yet — add one to get started.",
      navbarMobileMenu: "Menu",
      navbarCloseMenu: "Close menu",
      studioPanelLangNote: "Panel language is for the studio only; the shopper-facing catalog stays Arabic.",
      tourNeverAgain: "Never show this tour again",
      uploadError: "Could not upload the image.",
      loading: "Loading...",
      noData: "No data",
      optional: "optional",
      required: "required",
      sectionsLibrary: "Sections library",
      pickSection: "Pick a section to add",
      sectionHero: "Hero",
      sectionFeatures: "Features",
      sectionTextImage: "Text + Image",
      sectionGallery: "Gallery",
      sectionTestimonials: "Testimonials",
      sectionFaq: "FAQ",
      sectionCta: "Call to action",
      sectionBanner: "Banner",
      sectionVideo: "Video",
      sectionRichText: "Rich text",
      sectionContact: "Contact",
      sectionCarousel: "Carousel",
      sectionPromo: "Promo",
      sectionCountdown: "Countdown",
      sectionCategories: "Categories",
      sectionMap: "Map",
      sectionInstagram: "Instagram",
      sectionLogo: "Logos",
      uppercase: "Uppercase",
      lowercase: "Lowercase",
      bold: "Bold",
      regular: "Regular",
      light_weight: "Light",
      fontWeight: "Font weight",
      fontSize: "Font size",
      letterSpacing: "Letter spacing",
      lineHeight: "Line height",
      categoryStripTitle: "Category strip (sticky)",
      categoryStripBg: "Strip background",
      categoryStripBgOpacity: "Background opacity",
      categoryStripBorderColor: "Bottom border color",
      categoryStripBorderOpacity: "Border opacity",
      productsCardRadiusPx: "Product card corner radius (px)",
      productsCardBgColorLabel: "Product card background color",
      productsCartIconBorderWidthLabel: "Cart icon border width",
      productsCartIconBorderColorLabel: "Cart icon border color",
      productsCartIconBorderHint: "Drawn around the cart icon on each product card.",
      productsCartIconSideLabel: "Cart icon position",
      productsCartIconSideHint: "Left or right at the bottom of every product card — same for all products.",
      productsCartIconSideRight: "Right",
      productsCartIconSideLeft: "Left",
      productsSectionWidthLabel: "Products section width",
      productsSectionWidthFollowPage: "Follow page width",
      productsCardRadiusHint: "Leave empty to use the theme default. Enter 0 for sharp corners.",
      productsCardRadiusPlaceholder: "Auto",
      restaurantSquareImageRadius: "Product image corners (square — restaurant theme)",
      restaurantSquareImageRadiusHint:
        "In pixels. Leave empty for defaults (~18 in grid and carousel; sharp corners in menu list).",
      restaurantSquareImageRadiusPlaceholder: "Auto",
      themePickerLabel: "Ready-made themes",
      themePickerHint:
        "Pick a template to copy into your store — your edits won't change the library original. Use Publish to save.",
      catalogThemesLoading: "Loading templates…",
      catalogThemesEmpty: "No active templates in the library yet.",
      catalogThemesLoadError: "Could not load templates",
      themeMinimal: "Default store",
      themeMinimalDesc: "Hero + promos + products + footer",
      themeShowcase: "Showcase store",
      themeShowcaseDesc: "Global hero + showcase/carousel blocks + products + footer",
      themeRestaurant: "Restaurant / menu",
      themeRestaurantDesc: "Dark menu look + list products + sticky category bar",
      themeGeneralStore: "Retail store",
      themeGeneralStoreDesc: "Hero + feature bar + card grid before products",
      themeDarkStore: "Dark storefront",
      themeDarkStoreDesc: "Showcase layout with night palette, carousel, and split section",
      themeElectronicsStore: "Electronics",
      themeElectronicsStoreDesc: "Tech showcase: hero, carousel, filters, product scroller",
      themeUrbanico: "URBANICO Modern Fashion",
      themeUrbanicoDesc: "Full-screen hero + trust cards + categories (Arabic RTL)",
      themeCustomStore: "Custom Store Fashion",
      themeCustomStoreDesc: "Sharp corners, mobile-first, Arabic RTL",
      productsSectionLookTitle: "Products section look",
      sectionPaddingY: "Vertical section padding",
      sectionGridGap: "Gap between product cards",
      productCardShapeSelect: "Product card style",
      paddingTight: "Tight",
      paddingNormal: "Normal",
      paddingWide: "Wide",
      gapSmall: "Small",
      gapMedium: "Medium",
      gapLarge: "Large",
      cardShapeDefault: "Default",
      cardShapeFlat: "Flat (light border)",
      cardShapeSoft: "Soft (rounded)",
      productImageShapeLabel: "Product image shape (grid)",
      productImageShapeHint:
        "Circle: 1:1. Square and portrait: fixed 3:4 (width×height); the image always fills the frame at any size.",
      shapeCircle: "Circle",
      shapeCircleDesc: "Larger & centered",
      shapeSquare: "Portrait",
      shapeSquareDesc: "Fixed 3:4 · fills frame",
      shapePortrait: "Portrait",
      shapePortraitDesc: "Fixed 3:4 ratio",
      productImageBlockTitle: "Product image in the grid",
      productImageSizeLabel: "Product image size",
      productImageSizeDims: "Width × height",
      productImageSizeRatioFixed: "Fixed 3:4 — image fills the frame at any size",
      productImageSizeMaxHint: "At 360px the image fills the full card width.",
      showProductDescription: "Show product description under the name",
      productsTextsGroup: "Products section text",
      productsSectionColorsMini: "Section heading colors",
      productsHeadingColorLabel: "Products title color",
      productsSubtitleColorLabel: "Section subtitle color",
      productsCardTextColorsGroup: "Product card text",
      productsCardNameColorLabel: "Product name color",
      productsCardDescColorLabel: "Product description color",
      productsUnderlineColorLabel: "Underline bar color",
      productsGridNavGroup: "Grid, categories & filters",
      productsFiltersNavTitle: "Categories & filters",
      categoryNavPlacementLabel: "Category list placement",
      categoryNavOptionTop: "Horizontal bar above products",
      categoryNavOptionSidebar: "Sidebar next to products",
      filtersSidebarLabel: "Filter column (price / new / offers)",
      categoryPillsAlignLabel: "Category pills alignment",
      categoryPillsAlignHint: "Horizontal bar only; does not change pill colors or shapes.",
      categoryPillsAlignDefault: "Automatic (theme & screen width)",
      restaurantMenuColorsTitle: "Restaurant / menu look (optional)",
      restaurantMenuColorsHint:
        "These colors are saved with your catalog and show fully when the \"Restaurant / menu\" theme is active.",
      restaurantMenuColorsApplyNote:
        "All settings below apply only to the Restaurant / menu theme. Under Designs, switch to that theme to preview changes on the page.",
      restaurantMenuBaseColors: "Background & text",
      restaurantMenuAccentColors: "Price & add button",
      restaurantPageBg: "Menu page background (behind cards)",
      restaurantInk: "Menu text & headings color",
      restaurantAccentGreen: "Product price color (on each card)",
      restaurantAccentWarm: "“Add” button color on each card",
      productsDecorTitle: "Products section background decor",
      decorPattern: "Pattern",
      decorNone: "None",
      decorCircles: "Large circles",
      decorArcs: "Arcs",
      decorRings: "Rings",
      decorWaves: "Waves",
      decorColor: "Decor color",
      decorOpacity: "Decor opacity",
      goToSection: "Go to section",
      noAddedSections: "No sections added yet.",
      reorderHintShort: "Reorder with arrows or drag",
      mainProductsBlock: "Main products",
      addNewSection: "+ Add new section",
      typeText: "Text",
      typeTextImage: "Text + image",
      typeImageBanner: "Image banner",
      typeFeaturedProducts: "Featured products",
      typeCarousel: "Carousel",
      typeProductsCarousel: "Product carousel",
      typeFeaturesGrid: "Cards",
      sectionLabelHero: "Hero",
      sectionLabelText: "Text",
      sectionLabelSplit: "Text + image",
      sectionLabelImageBanner: "Image banner",
      sectionLabelFeatures: "Cards",
      sectionLabelFeatured: "Featured products",
      sectionLabelCarousel: "Carousel",
      sectionLabelProductsCarousel: "Product carousel",
      sectionFallback: "Section",
      addPickerTitle: "اختر نوع السكشن",
      addPickerZoneBefore: "يُضاف قبل المنتجات الأساسية",
      addPickerZoneAfter: "يُضاف بعد المنتجات الأساسية",
      addPickerTitle: "Choose a section type",
      addPickerZoneBefore: "Adds before the main products block",
      addPickerZoneAfter: "Adds after the main products block",
      addPickerCancelledTitle: "This screen was removed. Use \"Sections → Add new section\" to add and reorder sections.",
      tourWelcomeTitle: "Welcome to Catalog Studio",
      tourWelcomeBody:
        "This is how your storefront looks to shoppers. Use the side panel to customize it, then save your changes with the yellow button when you are done.",
      tourCustomizeTitle: "Open the customizer",
      tourCustomizeBody:
        "Tap \"Customize\" to open the panel: hero, products, extra sections, colors, cart placement, and more.",
      tourSaveTitle: "Save and publish",
      tourSaveBody: "After editing colors, sections, or the hero, tap \"Save\" to apply changes to your live catalog.",
      tourDashboardTitle: "Back to the dashboard",
      tourDashboardBody: "When you are finished designing, use this shortcut to return to the dashboard for products and orders.",
      tourPanelTitle: "Customization menu",
      tourPanelBody:
        "From here you reach every part of the store: sections, hero, products, themes, and more. Use \"Back\" to return to this list after opening any item.",
      tourMenuIntroTitle: "Customization options",
      tourMenuIntroBody: "The short hint under the title explains the panel. Tap the cards below to choose what to edit.",
      tourMenuBuilderTitle: "Added sections",
      tourMenuBuilderBody: "Add blocks (text, images, featured products, carousels…) above or below the main product grid, and reorder with drag-and-drop or arrows.",
      tourMenuHeroTitle: "Hero",
      tourMenuHeroBody: "Main headline, description, hero-only color, and optional background image.",
      tourMenuProductsTitle: "Products",
      tourMenuProductsBody: "Section title, alignment, and subtitle shown above the product grid.",
      tourMenuFooterTitle: "Footer",
      tourMenuFooterBody: "Footer colors, contact details, and social links.",
      tourMenuThemesTitle: "Designs",
      tourMenuThemesBody: "Ready-made themes (e.g. restaurant) to restyle the storefront quickly.",
      tourMenuLookTitle: "General look & colors",
      tourMenuLookBody: "Page colors and optional store body background.",
      tourMenuCartTitle: "Cart",
      tourMenuCartBody: "Floating cart position and colors so it stays usable next to the customizer.",
      close: "Close",
    }
  }), []);
  const tStudio = studioTranslations[studioLang] || studioTranslations.ar;

  const legacyThemePickerOptions = useMemo(
    () => [
      { value: "minimal", label: tStudio.themeMinimal, desc: tStudio.themeMinimalDesc },
      { value: "showcase", label: tStudio.themeShowcase, desc: tStudio.themeShowcaseDesc },
      { value: "restaurant", label: tStudio.themeRestaurant, desc: tStudio.themeRestaurantDesc },
      { value: "general_store", label: tStudio.themeGeneralStore, desc: tStudio.themeGeneralStoreDesc },
      { value: "dark_store", label: tStudio.themeDarkStore, desc: tStudio.themeDarkStoreDesc },
      { value: "electronics_store", label: tStudio.themeElectronicsStore, desc: tStudio.themeElectronicsStoreDesc },
      { value: "custom_store_fashion", label: tStudio.themeCustomStore, desc: tStudio.themeCustomStoreDesc },
      { value: "urbanico_modern_fashion", label: tStudio.themeUrbanico, desc: tStudio.themeUrbanicoDesc },
    ],
    [tStudio]
  );

  const catalogThemePickerOptions = useMemo(() => {
    const sl = studioLang === "en" ? "en" : "ar";
    const rows = [...(catalogThemes || [])].sort((a, b) => {
      const sa = Number(a.sort_order ?? 999);
      const sb = Number(b.sort_order ?? 999);
      if (sa !== sb) return sa - sb;
      return String(a.name_en || "").localeCompare(String(b.name_en || ""));
    });
    return rows.map((th) => {
      const key = String(th.theme_key || "");
      const isPro = key.startsWith("pro_");
      const proHint = sl === "en" ? "Pro online store template" : "قالب متجر إلكتروني احترافي";
      return {
        value: th.id,
        label: sl === "en" ? th.name_en || key : th.name_ar || key,
        desc: isPro ? proHint : key,
      };
    });
  }, [catalogThemes, studioLang]);

  const studioThemePickerOptions = catalogThemePickerOptions.length
    ? catalogThemePickerOptions
    : legacyThemePickerOptions;

  const studioThemePickerValue =
    selectedCatalogThemeId ||
    (CATALOG_STUDIO_THEME_KEYS.has(String(builderTheme || "").toLowerCase())
      ? String(builderTheme || "").toLowerCase()
      : catalogThemePickerOptions[0]?.value || "minimal");

  const activeCatalogThemeKey = useMemo(() => {
    const id = String(selectedCatalogThemeId || "");
    if (!id) return "";
    const row = (catalogThemes || []).find((t) => String(t.id) === id);
    return String(row?.theme_key || "").toLowerCase();
  }, [catalogThemes, selectedCatalogThemeId]);

  const showCatalogDarkSurfaceToggle = useMemo(() => {
    const extras = layoutsEdit?.catalog_layout_extras || {};
    if (catalogDarkSurfaceActive(extras)) return true;
    if (layoutExtrasBool(extras.global_page_bg_enabled) && isDarkHexColor(extras.page_bg)) return true;
    if (String(builderTheme || "").toLowerCase() === "dark_store") return true;
    return ["gaming_store", "streetwear_hoodie_store"].includes(activeCatalogThemeKey);
  }, [layoutsEdit?.catalog_layout_extras, builderTheme, activeCatalogThemeKey]);

  const BUILDER_SECTION_TYPE_OPTIONS = useMemo(() => {
    const en = studioLang === "en";
    const L = (ar, e) => (en ? e : ar);
    return [
      { type: "features_grid", presetId: "trust", label: L("بطاقات ثقة (شحن مجاني…)", "Trust bar") },
      { type: "features_grid", presetId: "categories", label: L("قسم الفئات بالصور", "Categories with images") },
      { type: "features_grid", presetId: "category_circles", label: L("فئات دائرية (MegaMart)", "Circle categories") },
      { type: "features_grid", presetId: "deals_row", label: L("عروض اليوم + خصم", "Daily deals") },
      { type: "features_grid", presetId: "stats", label: L("إحصائيات المتجر", "Store stats") },
      { type: "image_banner", presetId: "promo", label: L("بانر عرض (نص فوق الصورة)", "Promo banner") },
      { type: "image_banner", presetId: "countdown", label: L("عرض + عدّاد تنازلي", "Countdown offer") },
      { type: "video", label: L("قسم فيديو", "Video section") },
      { type: "carousel", presetId: "testimonials", label: L("آراء العملاء (سلايدر)", "Testimonials") },
      { type: "carousel", presetId: "brands", label: L("شعارات + صورة + نص", "Logos & labels") },
      { type: "text", presetId: "faq", label: L("أسئلة شائعة", "FAQ") },
      { type: "text_image_split", presetId: "brand_story", label: L("قصة العلامة", "Brand story") },
      { type: "text_image_split", presetId: "about_cards", label: L("من نحن + بطاقتان", "About + 2 cards") },
      { type: "featured_products", presetId: "featured", label: L("منتجات مميزة", "Featured products") },
      { type: "products_carousel", presetId: "products_carousel", label: L("كاروسيل منتجات", "Products carousel") },
      { type: "info_cards", label: L("بطاقات تعريفية + ملف PDF", "Info cards + PDF") },
      { type: "custom_order", label: L("طلب مخصص (فورم)", "Custom order form") },
      { type: "bundle_offers", label: L("سكشن العروض (مظهر)", "Offers section (style)") },
      { type: "text", label: L("نص حر", "Text block") },
      { type: "text_image_split", label: tStudio.typeTextImage },
      { type: "image_banner", label: tStudio.typeImageBanner },
      { type: "carousel", label: tStudio.typeCarousel },
      { type: "features_grid", label: tStudio.typeFeaturesGrid },
    ];
  }, [studioLang, tStudio]);

  const openSectionTypePicker = useCallback(({ zone = "before", index = 0 } = {}) => {
    setSectionsManagerZone(String(zone) === "after" ? "after" : "before");
    setSectionsInsertIndex(Math.max(0, Number(index) || 0));
    setStudioPanelLibraryTab("widgets");
    setCustomizerOpen(true);
    setCustomizerPane("menu");
    setCustomizerTab("sections");
    setSectionsMenuOpen("builder");
    setSectionsManagerOpen(false);
    setAddPickerOpen(true);
  }, []);

  const studioHistoryUi = useMemo(() => {
    const h = studioHistoryRef.current;
    const ready =
      Boolean(canEdit) &&
      !builderLoading &&
      historyInitializedRef.current &&
      h.entries.length > 0;
    const startIdx = Math.max(0, h.index - 9);
    return {
      canUndo: ready && h.index > 0,
      canRedo: ready && h.index >= 0 && h.index < h.entries.length - 1,
      menuEntries: h.entries.slice(startIdx, h.index + 1),
      menuStartIdx: startIdx,
    };
  }, [canEdit, builderLoading, studioHistoryTick]);
  const studioDir = studioLang === 'en' ? 'ltr' : 'rtl';

  const studioCustomizerPanelStyle = useMemo(() => {
    const sheet = !studioLayoutWide;
    const open = customizerOpen;
    const tr = !open
      ? sheet
        ? "translateY(110%)"
        : studioLang === "en"
          ? "translateX(-110%)"
          : "translateX(110%)"
      : "translate(0, 0)";
    const base = {
      direction: studioDir,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "transform 0.22s ease",
    };
    if (sheet) {
      return {
        ...base,
        position: "absolute",
        top: "auto",
        bottom: 0,
        left: 0,
        right: 0,
        width: "100%",
        maxWidth: "100%",
        height: "min(85dvh, 720px)",
        maxHeight: "min(85dvh, 720px)",
        borderRadius: "16px 16px 0 0",
        border: "1px solid var(--studio-border, #e5e7eb)",
        transform: tr,
        boxShadow: "0 -12px 40px rgba(15, 23, 42, 0.14)",
      };
    }
    return {
      ...base,
      position: "absolute",
      ...(studioLang === "en"
        ? {
            left: 0,
            right: "auto",
            borderRight: "1px solid var(--studio-border, #e5e7eb)",
            borderLeft: "none",
          }
        : {
            right: 0,
            left: "auto",
            borderLeft: "1px solid var(--studio-border, #e5e7eb)",
            borderRight: "none",
          }),
      width: "min(320px, 92vw)",
      maxWidth: "100%",
      transform: tr,
    };
  }, [studioLayoutWide, customizerOpen, studioLang, studioDir]);

  const handleStudioTourStepChange = useCallback((_index, s) => {
    if (!s?.id) return;
    if (s.id === "welcome" || s.id === "customize" || s.id === "save" || s.id === "dashboard") {
      setCustomizerOpen(false);
      return;
    }
    setCustomizerOpen(true);
    setCustomizerPane("menu");
    if (["menu_footer", "menu_themes", "menu_look", "menu_cart"].includes(s.id)) {
      setStudioPanelLibraryTab("globals");
    } else if (String(s.id).startsWith("menu_")) {
      setStudioPanelLibraryTab("widgets");
    }
    if (s.id === "menu_builder") setSectionsMenuOpen("builder");
    else if (s.id === "menu_themes") setSectionsMenuOpen("themes");
    else setSectionsMenuOpen(null);
  }, []);

  const catalogStudioTourStorageKey = useMemo(() => {
    let uid = "";
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      uid = String(u?.id || "").trim();
    } catch {
      uid = "";
    }
    const suffix = uid || String(token || "").trim() || "local";
    return `${CATALOG_STUDIO_TOUR_STORAGE_KEY}_${suffix}`;
  }, [token]);

  /** Per-account tour completion (syncs across devices); localStorage is checked first in the tour component. */
  const fetchCatalogStudioTourServerCompleted = useCallback(async () => {
    if (isCatalogStudioTourCompletedLocally(catalogStudioTourStorageKey)) {
      return true;
    }
    if (!token) {
      return isCatalogStudioTourCompletedLocally(catalogStudioTourStorageKey);
    }
    try {
      const res = await axios.get(`${API_URL}/settings/catalog-studio-tour`, authHeader);
      if (res.data?.completed === true) {
        markCatalogStudioTourCompletedLocally(catalogStudioTourStorageKey);
        return true;
      }
      return false;
    } catch {
      return isCatalogStudioTourCompletedLocally(catalogStudioTourStorageKey);
    }
  }, [token, authHeader, catalogStudioTourStorageKey]);

  const markCatalogStudioTourServerCompleted = useCallback(async () => {
    markCatalogStudioTourCompletedLocally(catalogStudioTourStorageKey);
    if (!token) return;
    try {
      await axios.post(
        `${API_URL}/settings/catalog-studio-tour`,
        { completed: true },
        authHeader
      );
    } catch (e) {
      console.warn("Catalog studio tour: server save failed", e);
    }
  }, [token, authHeader, catalogStudioTourStorageKey]);

  const catalogStudioTourSteps = useMemo(
    () => [
      { id: "welcome", target: "CENTER", title: tStudio.tourWelcomeTitle, body: tStudio.tourWelcomeBody },
      {
        id: "customize",
        target: '[data-tour="studio-panel-toggle"]',
        title: tStudio.tourCustomizeTitle,
        body: tStudio.tourCustomizeBody,
      },
      { id: "save", target: '[data-tour="customizer-save"]', title: tStudio.tourSaveTitle, body: tStudio.tourSaveBody },
      {
        id: "dashboard",
        target: '[data-tour="studio-back-dashboard"]',
        title: tStudio.tourDashboardTitle,
        body: tStudio.tourDashboardBody,
      },
      {
        id: "panel",
        target: '[data-tour="studio-panel-library-title"]',
        title: tStudio.tourPanelTitle,
        body: tStudio.tourPanelBody,
      },
      {
        id: "menu_intro",
        target: '[data-tour="studio-menu-grid"]',
        title: tStudio.tourMenuIntroTitle,
        body: tStudio.tourMenuIntroBody,
      },
      {
        id: "menu_builder",
        target: '[data-tour="studio-menu-builder"]',
        title: tStudio.tourMenuBuilderTitle,
        body: tStudio.tourMenuBuilderBody,
      },
      {
        id: "menu_hero",
        target: '[data-tour="studio-menu-hero"]',
        title: tStudio.tourMenuHeroTitle,
        body: tStudio.tourMenuHeroBody,
      },
      {
        id: "menu_products",
        target: '[data-tour="studio-menu-products"]',
        title: tStudio.tourMenuProductsTitle,
        body: tStudio.tourMenuProductsBody,
      },
      {
        id: "menu_footer",
        target: '[data-tour="studio-menu-footer"]',
        title: tStudio.tourMenuFooterTitle,
        body: tStudio.tourMenuFooterBody,
      },
      {
        id: "menu_themes",
        target: '[data-tour="studio-menu-themes"]',
        title: tStudio.tourMenuThemesTitle,
        body: tStudio.tourMenuThemesBody,
      },
      {
        id: "menu_look",
        target: '[data-tour="studio-menu-look"]',
        title: tStudio.tourMenuLookTitle,
        body: tStudio.tourMenuLookBody,
      },
      {
        id: "menu_cart",
        target: '[data-tour="studio-menu-cart"]',
        title: tStudio.tourMenuCartTitle,
        body: tStudio.tourMenuCartBody,
      },
    ],
    [tStudio]
  );

  const didCanonicalizeUrl = useRef(false);
  const looksLikeUuid = (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      (value || "").trim()
    );

  useEffect(() => {
    // Admin Catalog Studio: do not force global RTL/Arabic on <html> (breaks admin panel on back).
    if (adminStudioUserId) {
      const prevDir = document.documentElement.getAttribute("dir") || "ltr";
      const prevLang = document.documentElement.getAttribute("lang") || "en";
      document.documentElement.dir = "ltr";
      document.documentElement.lang = "en";
      return () => {
        document.documentElement.dir = prevDir || "ltr";
        document.documentElement.lang = prevLang || "en";
      };
    }
    document.documentElement.dir = "rtl";
    document.documentElement.lang = "ar";
    localStorage.setItem("catalogLanguage", "ar");
    window.dispatchEvent(new Event("kataloji:language-changed"));
  }, [adminStudioUserId]);

  const storefrontTokenKey = 'storefront_token';

  const storefrontFetch = useCallback(async (path, options = {}) => {
    const token = localStorage.getItem(storefrontTokenKey);
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_URL}${path}`, { ...options, headers });
  }, []);

  const loadStorefrontSession = useCallback(async () => {
    const token = localStorage.getItem(storefrontTokenKey);
    if (!token) {
      setShopper(null);
      didLoadServerCartRef.current = false;
      return;
    }
    try {
      const meResp = await storefrontFetch('/storefront/me');
      if (!meResp.ok) throw new Error('unauthorized');
      const me = await meResp.json().catch(() => ({}));
      setShopper(me?.user || null);

      if (storeKey) {
        const cartResp = await storefrontFetch(
          `/storefront/cart?catalog_slug=${encodeURIComponent(String(storeKey))}`
        );
        if (cartResp.ok) {
          const data = await cartResp.json().catch(() => ({}));
          const items = Array.isArray(data.items) ? data.items : [];
          setCart(consolidateCartLines(items));
          didLoadServerCartRef.current = true;
        }
      }
    } catch (e) {
      localStorage.removeItem(storefrontTokenKey);
      setShopper(null);
      didLoadServerCartRef.current = false;
    }
  }, [API_URL, storefrontFetch, storeKey]);

  useEffect(() => {
    loadStorefrontSession();
  }, [loadStorefrontSession]);

  useEffect(() => {
    if (!shopper || !storeKey) return;
    if (!didLoadServerCartRef.current) return;

    if (cartSaveTimerRef.current) window.clearTimeout(cartSaveTimerRef.current);
    cartSaveTimerRef.current = window.setTimeout(async () => {
      try {
        await storefrontFetch('/storefront/cart', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ catalog_slug: String(storeKey), items: cart || [] }),
        });
      } catch {}
    }, 700);

    return () => {
      if (cartSaveTimerRef.current) window.clearTimeout(cartSaveTimerRef.current);
    };
  }, [cart, shopper, storeKey, storefrontFetch]);

  const {
    wholesaleDealer,
    wholesaleCatalogEnabled,
    wholesaleAuthOpen,
    setWholesaleAuthOpen,
    wholesaleOrdersOpen,
    setWholesaleOrdersOpen,
    wholesaleEditingOrderId,
    setWholesaleEditingOrderId,
    wholesaleCatalogHeaders,
    wholesaleLogout,
    handleWholesaleLoginSuccess,
    handleWholesaleEditOrder,
    applyWholesaleBrandingFlags,
    wholesaleCatalogSlug,
    wholesaleLoginUsername,
    wholesaleLoginUsernameLocked,
    didLoadWholesaleCartRef,
  } = useCatalogWholesaleSession({ storeKey, catalogSlug, cart, setCart, canEdit });

  const catalogPublicBasePath = useMemo(() => {
    if (customDomainMode) {
      return menuOnly ? "/menu" : "/";
    }
    const key = encodeURIComponent(String(storeKey || userId || "").trim());
    return menuOnly ? `/catalog/${key}/menu` : `/catalog/${key}`;
  }, [storeKey, userId, menuOnly, customDomainMode]);

  const storefrontFullOrdersEnabled = useMemo(() => {
    if (wholesaleDealer) return true;
    return settings.storefrontFullOrdersEnabled !== false;
  }, [settings.storefrontFullOrdersEnabled, wholesaleDealer]);

  const storeHasValidWhatsapp = useMemo(
    () => isValidStoredWhatsappNumber(settings.whatsappNumber),
    [settings.whatsappNumber]
  );

  const showStorefrontCart = useMemo(() => {
    if (menuOnly) return false;
    if (wholesaleDealer) return true;
    if (settings.storefrontCartEnabled === false) return false;
    // Basic (no orders page): shoppers only get a cart if WhatsApp can actually send.
    if (!canEdit && !storefrontFullOrdersEnabled && !storeHasValidWhatsapp) {
      return false;
    }
    return true;
  }, [
    menuOnly,
    wholesaleDealer,
    settings.storefrontCartEnabled,
    storefrontFullOrdersEnabled,
    storeHasValidWhatsapp,
    canEdit,
  ]);

  const storefrontWhatsappCartOnly = useMemo(() => {
    return showStorefrontCart && !storefrontFullOrdersEnabled;
  }, [showStorefrontCart, storefrontFullOrdersEnabled]);

  const [hoursTick, setHoursTick] = useState(0);
  useEffect(() => {
    if (!settings.businessHoursEnabled) return undefined;
    const id = window.setInterval(() => setHoursTick((t) => t + 1), 60000);
    return () => window.clearInterval(id);
  }, [settings.businessHoursEnabled]);

  const orderingBlockedByHours = useMemo(() => {
    if (menuOnly) return false;
    if (wholesaleDealer || canEdit) return false;
    if (!settings.businessHoursEnabled) return false;
    return !isStoreOpenNow(settings.businessHours);
  }, [
    menuOnly,
    wholesaleDealer,
    canEdit,
    settings.businessHoursEnabled,
    settings.businessHours,
    hoursTick,
  ]);

  const closedHoursMessage = useMemo(() => {
    const msg = String(settings.businessHours?.closed_message_ar || "").trim();
    return msg || DEFAULT_CLOSED_MESSAGE_AR;
  }, [settings.businessHours]);

  const showOrderingClosedNotice = useCallback(() => {
    setCheckoutValidationModal({
      open: true,
      title: "خارج أوقات الدوام",
      message: closedHoursMessage,
    });
  }, [closedHoursMessage]);

  const handleCustomOrderAddToCart = useCallback(
    ({ sectionId, settings: coSettings, answers, unitPrice }) => {
      if (!showStorefrontCart || menuOnly) return;
      if (orderingBlockedByHours) {
        showOrderingClosedNotice();
        return;
      }
      const name = String(coSettings?.cart_item_name || coSettings?.title || "طلب مخصص").trim();
      const note = customOrderNoteText(coSettings, answers);
      const line = {
        id: `custom_order:${String(sectionId)}`,
        isCustomOrder: true,
        customOrderSectionId: String(sectionId),
        customOrderMergeKey: customOrderMergeKey(sectionId, answers),
        customOrderAnswers: answers,
        name,
        price: unitPrice,
        baseUnitPrice: unitPrice,
        note,
        selectedAddons: customOrderAddonsFromAnswers(answers),
        quantity: 1,
        image: settings.logo || null,
      };
      setCart((prev) => mergeCartLine(prev, line));
      trackMetaAddToCart(line, cartLineUnitPrice);
      trackTikTokAddToCart(line, cartLineUnitPrice);
      bumpCartFeedback();
      setIsCartOpen(true);
    },
    [
      showStorefrontCart,
      menuOnly,
      orderingBlockedByHours,
      showOrderingClosedNotice,
      settings.logo,
      bumpCartFeedback,
    ]
  );

  const handleBundleOfferAddToCart = useCallback(
    ({ sectionId, offer, picks, unitPrice }) => {
      if (!showStorefrontCart || menuOnly) return;
      if (orderingBlockedByHours) {
        showOrderingClosedNotice();
        return;
      }
      const productsMap = canEdit ? ownerProductsById : catalogProductsById;
      const name = String(offer?.name || "عرض").trim();
      const enrichedPicks = (picks || []).map((p, i) =>
        enrichBundlePickRow(p, offer, productsMap, i)
      );
      const note = bundleOfferNoteText(offer, enrichedPicks, productsMap);
      const line = {
        id: `bundle_offer:${String(sectionId)}:${String(offer?.id)}`,
        isBundleOffer: true,
        bundleOfferSectionId: String(sectionId),
        bundleOfferId: String(offer?.id),
        bundleOfferMergeKey: bundleOfferMergeKey(sectionId, offer?.id, enrichedPicks),
        bundlePicks: enrichedPicks,
        bundleOfferMeta: bundleOfferSnapshotForCart(offer),
        name,
        price: unitPrice,
        baseUnitPrice: unitPrice,
        note,
        quantity: 1,
        image: settings.logo || null,
      };
      setCart((prev) => mergeCartLine(prev, line));
      trackMetaAddToCart(line, cartLineUnitPrice);
      trackTikTokAddToCart(line, cartLineUnitPrice);
      bumpCartFeedback();
      setIsCartOpen(true);
    },
    [
      showStorefrontCart,
      menuOnly,
      orderingBlockedByHours,
      showOrderingClosedNotice,
      canEdit,
      ownerProductsById,
      catalogProductsById,
      settings.logo,
      bumpCartFeedback,
    ]
  );

  useEffect(() => {
    showStorefrontCartRef.current = showStorefrontCart;
    if (!showStorefrontCart) {
      setIsCartOpen(false);
    }
  }, [showStorefrontCart]);

  const openWholesaleOrders = useCallback(() => {
    const slug = wholesaleCatalogSlug || catalogSlug;
    const hasWholesaleSession =
      wholesaleDealer || getWholesaleToken(storeKey, slug);
    if (!hasWholesaleSession) {
      setWholesaleAuthOpen(true);
      return;
    }
    setWholesaleOrdersOpen(true);
  }, [
    wholesaleDealer,
    storeKey,
    wholesaleCatalogSlug,
    catalogSlug,
    setWholesaleAuthOpen,
    setWholesaleOrdersOpen,
  ]);

  const onWholesaleEditOrder = useCallback(
    (items, orderId) => {
      handleWholesaleEditOrder(items, orderId);
      setIsCartOpen(true);
    },
    [handleWholesaleEditOrder]
  );

  const openAuth = useCallback((mode) => {
    setAuthMode(mode);
    setAuthError('');
    setAuthLoading(false);
    setAuthForm({ full_name: '', phone: '', password: '', password_confirm: '' });
    setAuthOpen(true);
  }, []);

  const renderCatalogGuestAuth = useCallback(
    (iconClassName = "catalog-topbar-icon-btn") => {
      if (catalogHeroTopbarUi.showIconAuth) {
        return (
          <button
            type="button"
            className={iconClassName}
            onClick={() => openAuth("login")}
            aria-label={catalogHeroTopbarUi.loginLabel}
            style={catalogHeroTopbarUi.mobileUserChipStyle}
          >
            <User size={20} strokeWidth={2.25} />
          </button>
        );
      }
      return (
        <>
          <div className="catalog-hero-topbar-auth-icons catalog-hero-topbar-auth-icons--mobile">
            <button
              type="button"
              className={iconClassName}
              onClick={() => openAuth("login")}
              aria-label={catalogHeroTopbarUi.loginLabel}
              style={catalogHeroTopbarUi.mobileUserChipStyle}
            >
              <LogIn size={18} strokeWidth={2.25} />
            </button>
            <button
              type="button"
              className={iconClassName}
              onClick={() => openAuth("register")}
              aria-label={catalogHeroTopbarUi.registerLabel}
              style={catalogHeroTopbarUi.mobileUserChipStyle}
            >
              <UserPlus size={18} strokeWidth={2.25} />
            </button>
          </div>
          <div className="d-flex align-items-center gap-2 catalog-hero-topbar-auth-buttons catalog-hero-topbar-auth-buttons--desktop">
            <button
              type="button"
              className="btn catalog-hero-topbar-auth-btn"
              onClick={() => openAuth("login")}
              style={catalogHeroTopbarUi.loginBtnStyle}
            >
              {catalogHeroTopbarUi.loginLabel}
            </button>
            <button
              type="button"
              className="btn catalog-hero-topbar-auth-btn"
              onClick={() => openAuth("register")}
              style={catalogHeroTopbarUi.registerBtnStyle}
            >
              {catalogHeroTopbarUi.registerLabel}
            </button>
          </div>
        </>
      );
    },
    [catalogHeroTopbarUi, openAuth]
  );

  const handleCatalogNavClick = useCallback((link) => {
    setNavbarMoreOpen(false);
    setNavbarMobileOpen(false);
    scrollToCatalogNavTarget(link);
  }, []);

  const renderCatalogNavLinkButton = useCallback(
    (link, { className = "catalog-nav-link", onClick } = {}) => {
      const color = link.color || catalogNavbarUi.linkColor;
      const hover = link.hover_color || catalogNavbarUi.linkHoverColor;
      return (
        <button
          key={link.id}
          type="button"
          className={className}
          style={{
            "--catalog-nav-link-color": color,
            "--catalog-nav-link-hover": hover,
          }}
          onClick={() => {
            onClick?.(link);
            handleCatalogNavClick(link);
          }}
        >
          {link.label}
        </button>
      );
    },
    [catalogNavbarUi.linkColor, catalogNavbarUi.linkHoverColor, handleCatalogNavClick]
  );

  const renderCatalogDesktopNav = useCallback(() => {
    if (!catalogNavbarUi.show) return null;
    const links = catalogNavbarUi.links;
    const visible = links.slice(0, catalogNavbarUi.visibleCount);
    const overflow = links.slice(catalogNavbarUi.visibleCount);
    return (
      <nav
        className="catalog-header-nav catalog-header-nav--desktop"
        aria-label="التنقل"
        style={{
          "--catalog-nav-link-color": catalogNavbarUi.linkColor,
          "--catalog-nav-link-hover": catalogNavbarUi.linkHoverColor,
        }}
      >
        {visible.map((link) => renderCatalogNavLinkButton(link))}
        {overflow.length > 0 ? (
          <div className={`catalog-nav-more${navbarMoreOpen ? " is-open" : ""}`}>
            <button
              type="button"
              className="catalog-nav-link catalog-nav-more__trigger"
              aria-expanded={navbarMoreOpen}
              aria-haspopup="menu"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setNavbarMoreOpen((v) => !v);
              }}
              style={{
                "--catalog-nav-link-color": catalogNavbarUi.moreColor,
                "--catalog-nav-link-hover": catalogNavbarUi.moreHoverColor,
                color: catalogNavbarUi.moreColor,
              }}
            >
              {catalogNavbarUi.moreLabel}
              <ChevronDown size={14} strokeWidth={2.5} aria-hidden />
            </button>
            {navbarMoreOpen ? (
              <>
                <button
                  type="button"
                  className="catalog-nav-more__backdrop"
                  aria-label="إغلاق"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setNavbarMoreOpen(false);
                  }}
                />
                <div className="catalog-nav-more__menu" role="menu">
                  {overflow.map((link) =>
                    renderCatalogNavLinkButton(link, { className: "catalog-nav-more__item" })
                  )}
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </nav>
    );
  }, [
    catalogNavbarUi,
    navbarMoreOpen,
    renderCatalogNavLinkButton,
  ]);

  const renderCatalogMobileNavDrawer = useCallback(() => {
    if (!catalogNavbarUi.show || !navbarMobileOpen) return null;
    const drawerAccent = readableAccentOnWhite(
      catalogThemePrimary,
      readableAccentOnWhite(catalogThemeSecondary, "#0f172a")
    );
    const drawerRtl = (settings.layoutDirection || "rtl") === "rtl";
    // Panel sits on the inline-start edge: in RTL a finger moving right closes it, in LTR moving left.
    const closeDistance = (dx) => (drawerRtl ? dx : -dx);
    const onDrawerTouchStart = (e) => {
      const t = e.touches[0];
      const root = e.currentTarget;
      navDrawerSwipeRef.current = {
        x: t.clientX,
        y: t.clientY,
        t: Date.now(),
        mode: null,
        dist: 0,
        panel: root.querySelector(".catalog-nav-drawer__panel"),
        backdrop: root.querySelector(".catalog-nav-drawer__backdrop"),
      };
    };
    const onDrawerTouchMove = (e) => {
      const s = navDrawerSwipeRef.current;
      if (!s || s.mode === "scroll" || !s.panel) return;
      const t = e.touches[0];
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (!s.mode) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        s.mode = Math.abs(dx) > Math.abs(dy) * 1.2 && closeDistance(dx) > 0 ? "drag" : "scroll";
        if (s.mode !== "drag") return;
        s.panel.style.transition = "none";
        s.panel.style.animation = "none";
        if (s.backdrop) s.backdrop.style.transition = "none";
      }
      const width = s.panel.offsetWidth || 1;
      const dist = Math.min(Math.max(0, closeDistance(dx)), width);
      s.dist = dist;
      s.panel.style.transform = `translateX(${drawerRtl ? dist : -dist}px)`;
      if (s.backdrop) s.backdrop.style.opacity = String(Math.max(0, 1 - dist / width));
    };
    const onDrawerTouchEnd = () => {
      const s = navDrawerSwipeRef.current;
      navDrawerSwipeRef.current = null;
      if (!s || s.mode !== "drag" || !s.panel) return;
      const width = s.panel.offsetWidth || 1;
      const velocity = s.dist / Math.max(1, Date.now() - s.t);
      const shouldClose = s.dist > width * 0.3 || (velocity > 0.45 && s.dist > 30);
      const ease = "cubic-bezier(0.22, 1, 0.36, 1)";
      s.panel.style.transition = `transform 0.22s ${ease}`;
      if (s.backdrop) s.backdrop.style.transition = "opacity 0.22s ease";
      if (shouldClose) {
        s.panel.style.transform = `translateX(${drawerRtl ? width : -width}px)`;
        if (s.backdrop) s.backdrop.style.opacity = "0";
        window.setTimeout(() => setNavbarMobileOpen(false), 210);
      } else {
        s.panel.style.transform = "";
        if (s.backdrop) s.backdrop.style.opacity = "";
      }
    };
    const drawer = (
      <div
        className="catalog-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={tStudio.navbarMobileMenu}
        onTouchStart={onDrawerTouchStart}
        onTouchMove={onDrawerTouchMove}
        onTouchEnd={onDrawerTouchEnd}
        onTouchCancel={onDrawerTouchEnd}
      >
        <button
          type="button"
          className="catalog-nav-drawer__backdrop"
          aria-label={tStudio.navbarCloseMenu}
          onClick={() => setNavbarMobileOpen(false)}
        />
        <aside
          className="catalog-nav-drawer__panel"
          dir={settings.layoutDirection || "rtl"}
          style={{
            "--catalog-nav-link-color": catalogNavbarUi.linkColor,
            "--catalog-nav-link-hover": catalogNavbarUi.linkHoverColor,
            "--drawer-accent": drawerAccent,
          }}
        >
          <div className="catalog-nav-drawer__head">
            <div className="catalog-nav-drawer__brand">
              {settings.logo ? (
                <span className="catalog-nav-drawer__logo-tile">
                  <img src={settings.logo} alt="" className="catalog-nav-drawer__logo" width={54} height={54} loading="lazy" decoding="async" />
                </span>
              ) : null}
              <span className="catalog-nav-drawer__title">{settings.storeName}</span>
            </div>
            <button
              type="button"
              className="catalog-nav-drawer__close"
              aria-label={tStudio.navbarCloseMenu}
              onClick={() => setNavbarMobileOpen(false)}
            >
              <X size={20} strokeWidth={2.25} />
            </button>
          </div>
          <nav className="catalog-nav-drawer__links" aria-label="التنقل">
            {catalogNavbarUi.links.length ? (
              catalogNavbarUi.links.map((link, idx) => {
                const LinkIcon = catalogNavLinkIcon(link);
                return (
                  <button
                    key={link.id}
                    type="button"
                    className="catalog-nav-drawer__link"
                    style={{
                      ...(link.color ? { "--catalog-nav-link-color": link.color } : {}),
                      ...(link.hover_color ? { "--catalog-nav-link-hover": link.hover_color } : {}),
                      animationDelay: `${Math.min(idx, 10) * 35}ms`,
                    }}
                    onClick={() => {
                      if (canEdit) {
                        handleCatalogNavClick(link);
                        return;
                      }
                      // Body is still scroll-locked; scroll after the lock is released.
                      navDrawerPendingLinkRef.current = link;
                      setNavbarMoreOpen(false);
                      setNavbarMobileOpen(false);
                    }}
                  >
                    <span className="catalog-nav-drawer__link-icon" aria-hidden>
                      <LinkIcon size={18} strokeWidth={2.1} />
                    </span>
                    <span className="catalog-nav-drawer__link-label">{link.label}</span>
                    <span className="catalog-nav-drawer__link-chevron" aria-hidden>
                      {drawerRtl ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="catalog-nav-drawer__empty">{tStudio.navbarEmpty}</div>
            )}
          </nav>
          {!shopper && !wholesaleDealer ? (
            <div className="catalog-nav-drawer__auth">
              {catalogHeroTopbarUi.showIconAuth ? (
                <button
                  type="button"
                  className="btn catalog-nav-drawer__auth-btn catalog-nav-drawer__auth-btn--primary"
                  onClick={() => {
                    setNavbarMobileOpen(false);
                    openAuth("login");
                  }}
                >
                  <LogIn size={17} aria-hidden />
                  {catalogHeroTopbarUi.loginLabel}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn catalog-nav-drawer__auth-btn catalog-nav-drawer__auth-btn--primary"
                    onClick={() => {
                      setNavbarMobileOpen(false);
                      openAuth("login");
                    }}
                  >
                    <LogIn size={17} aria-hidden />
                    {catalogHeroTopbarUi.loginLabel}
                  </button>
                  <button
                    type="button"
                    className="btn catalog-nav-drawer__auth-btn"
                    onClick={() => {
                      setNavbarMobileOpen(false);
                      openAuth("register");
                    }}
                  >
                    <UserPlus size={17} aria-hidden />
                    {catalogHeroTopbarUi.registerLabel}
                  </button>
                </>
              )}
            </div>
          ) : shopper && !wholesaleDealer ? (
            <div className="catalog-nav-drawer__auth">
              <div className="catalog-nav-drawer__user">
                <span className="catalog-nav-drawer__user-avatar" aria-hidden>
                  <User size={18} />
                </span>
                <span>{String(shopper?.full_name || "").trim() || "حسابي"}</span>
              </div>
              <button
                type="button"
                className="btn catalog-nav-drawer__auth-btn catalog-nav-drawer__auth-btn--logout"
                onClick={() => {
                  setNavbarMobileOpen(false);
                  localStorage.removeItem(storefrontTokenKey);
                  setShopper(null);
                  didLoadServerCartRef.current = false;
                  setCart([]);
                }}
              >
                <LogOut size={17} aria-hidden />
                خروج
              </button>
            </div>
          ) : null}
        </aside>
      </div>
    );

    if (typeof document === "undefined") return null;
    const host = canEdit
      ? document.querySelector(".catalog-studio-storefront-canvas")
      : document.body;
    if (!host) return drawer;
    return createPortal(drawer, host);
  }, [
    catalogNavbarUi,
    navbarMobileOpen,
    tStudio.navbarMobileMenu,
    tStudio.navbarCloseMenu,
    tStudio.navbarEmpty,
    settings.layoutDirection,
    settings.logo,
    settings.storeName,
    handleCatalogNavClick,
    catalogThemePrimary,
    catalogThemeSecondary,
    shopper,
    wholesaleDealer,
    catalogHeroTopbarUi,
    openAuth,
    storefrontTokenKey,
    canEdit,
  ]);

  const closeAuth = useCallback(() => {
    setAuthOpen(false);
    setAuthError('');
    setAuthLoading(false);
  }, []);

  const handleAuthSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (authLoading) return;
    setAuthError('');
    setAuthLoading(true);
    try {
      const payload =
        authMode === 'register'
          ? {
              full_name: authForm.full_name,
              phone: authForm.phone,
              password: authForm.password,
              password_confirm: authForm.password_confirm,
            }
          : { phone: authForm.phone, password: authForm.password };

      const resp = await fetch(`${API_URL}/storefront/${authMode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setAuthError(data?.detail || 'حدث خطأ، حاول مرة أخرى.');
        return;
      }

      if (data?.access_token) {
        localStorage.setItem(storefrontTokenKey, data.access_token);
      }
      wholesaleLogout();
      setShopper(data?.user || null);
      setAuthOpen(false);

      // After login/register, load server cart (and mark as loaded so we start saving changes)
      didLoadServerCartRef.current = false;
      didTrackLoggedInVisitRef.current = false;
      await loadStorefrontSession();

      // Track a logged-in visit once (so Top Visitors works even if the page was opened before login)
      try {
        const token = localStorage.getItem(storefrontTokenKey);
        if (token && storeKey && !didTrackLoggedInVisitRef.current) {
          didTrackLoggedInVisitRef.current = true;
          fetch(`${API_URL}/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ user_id: String(storeKey), event_type: 'visit' }),
          }).catch(() => {});
        }
      } catch {}
    } catch (err) {
      setAuthError('تعذر الاتصال بالخادم.');
    } finally {
      setAuthLoading(false);
    }
  }, [API_URL, authForm, authLoading, authMode, loadStorefrontSession, storeKey, wholesaleLogout]);

  const logoutShopper = useCallback(() => {
    localStorage.removeItem(storefrontTokenKey);
    setShopper(null);
    didLoadServerCartRef.current = false;
    // When logging out, clear the shopper cart from the UI to avoid mixing carts between accounts.
    setCart([]);
  }, []);

const hasTracked = useRef(false);
useEffect(() => {
  if (!userId || hasTracked.current) return;

  const trackVisit = async () => {
    try {
      hasTracked.current = true; // نضع علامة أنه تم التتبع
      const token = localStorage.getItem('storefront_token');
      await fetch(`${API_URL}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_id: userId,
          event_type: 'visit'
        })
      });
    } catch (error) {
      console.error("Failed to track visit:", error);
    }
  };

  trackVisit();
}, [userId]);

const lastTrackedProductViewRef = useRef({ key: null, at: 0 });
const trackProductView = useCallback(async (catalogId, productId, product = null) => {
  const cid = (catalogId || '').trim();
  const pid = (productId || '').trim();
  if (!cid || !pid) return;

  // Avoid spamming when opening the same product repeatedly in a short time
  const now = Date.now();
  const key = `${cid}::${pid}`;
  const prev = lastTrackedProductViewRef.current || {};
  if (prev.key === key && now - (prev.at || 0) < 4000) return;
  lastTrackedProductViewRef.current = { key, at: now };

  // Meta Pixel ViewContent (same debounce as internal analytics)
  trackMetaViewContent(product || { id: pid });
  trackTikTokViewContent(product || { id: pid });

  try {
    await fetch(`${API_URL}/track-product-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ catalog_id: cid, product_id: pid })
    });
  } catch (error) {
    console.error("Failed to track product view:", error);
  }
}, []);

  useEffect(() => {
    catalogShellReadyRef.current = false;
    catalogBrandingReadyRef.current = false;
    catalogPageCacheRef.current = new Map();
    setCatalogNotFound(false);
    setCatalogUnavailable(false);
    setCatalogError(null);
    // Instant hero/logo from last successful visit while API loads.
    const cachedHero = readCachedCatalogHeroUrl(userId);
    const cachedLogo = readCachedCatalogLogoUrl(userId);
    if (cachedHero || cachedLogo) {
      setSettings((prev) => ({
        ...prev,
        ...(cachedHero && !prev.heroUrl ? { heroUrl: cachedHero, bgType: "image" } : {}),
        ...(cachedLogo && !prev.logo ? { logo: cachedLogo } : {}),
      }));
    }
  }, [userId]);

  // First visit: fetch store logo ASAP so loader never shows Kataloji branding.
  useEffect(() => {
    const ident = String(userId || "").trim();
    if (!ident || canEdit) return undefined;
    if (readCachedCatalogLogoUrl(ident)) return undefined;
    // Logo/name already came with the HTML (window.__STORE__).
    if (storeSeedRef.current && readStoreSeed(ident)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/catalog/${encodeURIComponent(ident)}/branding`
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const logo = String(data?.branding?.logo_url || "").trim();
        const name = String(data?.branding?.store_name || "").trim();
        const loaderText = String(data?.loader?.text || "").trim();
        const loaderColor = String(data?.loader?.text_color || "").trim();
        if (logo) writeCachedCatalogLogoUrl(ident, logo);
        writeCachedCatalogLoaderPrefs(ident, loaderText, loaderColor);
        if (cancelled) return;
        if (logo || name) {
          setSettings((prev) => ({
            ...prev,
            ...(logo ? { logo } : {}),
            ...(name ? { storeName: name } : {}),
          }));
        }
        if (loaderText || loaderColor) {
          setLayouts((prev) => ({
            ...prev,
            catalog_layout_extras: {
              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
              ...(prev.catalog_layout_extras || {}),
              ...(loaderText ? { loader_text: loaderText } : {}),
              ...(loaderColor ? { loader_text_color: loaderColor } : {}),
            },
          }));
        }
      } catch {
        // Full catalog fetch still applies branding.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, canEdit]);

  const hidePublicCatalogShell =
    !canEdit && (catalogNotFound || catalogUnavailable);


const trackWhatsAppClick = async () => {
  try {
    const token = localStorage.getItem('storefront_token');
    await fetch(`${API_URL}/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        user_id: userId,
        event_type: 'whatsapp_order', // وسم خاص لتمييز طلبات الواتساب
      })
    });
  } catch (error) {
    console.error("Failed to track WhatsApp click:", error);
  }
};

const openWhatsAppProductInquiry = useCallback(
  (product) => {
    const waDigits = normalizeWhatsAppDigits(settings.whatsappNumber);
    if (!waDigits) return;
    const origin = window.location.origin || 'https://kataloji.com';
    const currentPath = String(window.location.pathname || "").replace(/\/+$/, "");
    const pid = (product?.id != null ? String(product.id) : "").trim();
    const hasPidInPath = pid ? currentPath.endsWith(`/${pid}`) : false;
    const productUrl =
      hasPidInPath
        ? `${origin}${currentPath}`
        : pid && customDomainMode
          ? `${origin}/${encodeURIComponent(pid)}`
          : pid && userId
            ? `${origin}/catalog/${encodeURIComponent(String(userId))}/${encodeURIComponent(pid)}`
            : `${origin}${currentPath}`;
    const message = `مرحبا، اريد الاستفسار عن هذا المنتج :\n${productUrl}`;
    const encoded = encodeURIComponent(message);

    // Track inquiry click (counts as "استفسر عن هذا المنتج")
    try {
      if (pid && userId) {
        fetch(`${API_URL}/track-product-inquiry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ catalog_id: String(userId), product_id: String(pid) })
        });
      }
    } catch {}

    openWhatsAppUrl(buildWhatsAppMeUrl(waDigits, message), null);
  },
  [settings.whatsappNumber, userId, customDomainMode]
);

  const loadCities = useCallback(async () => {
    setCitiesLoading(true);
    try {
      const res = await fetch(`${API_URL}/cities`);
      if (!res.ok) throw new Error('Failed to load cities');
      const data = await res.json().catch(() => ({}));
      setCities(Array.isArray(data.cities) ? data.cities : []);
    } catch (e) {
      console.error('Failed to load cities:', e);
      setCities([]);
    } finally {
      setCitiesLoading(false);
    }
  }, []);

  const citiesForSelectedRegion = useMemo(() => {
    const region = String(checkoutForm.deliveryRegion || '').trim();
    if (!region) return [];
    return (cities || []).filter((c) => String(c.region) === region);
  }, [cities, checkoutForm.deliveryRegion]);

  const regionSelectOptions = useMemo(
    () =>
      DELIVERY_REGIONS.map((r) => ({
        value: r.key,
        label: r.labelAr,
      })),
    []
  );

  const checkoutUsesWhatsapp = useMemo(
    () => shouldSendOrderViaWhatsapp(settings),
    [settings.sendOrdersToWhatsapp, settings.whatsappNumber]
  );

  const checkoutDeliveryFee = useMemo(() => {
    if (!settings.deliveryEnabled) return 0;
    const prices = settings.deliveryPrices || defaultDeliveryPricesMap();
    const isRestaurant = settings.checkoutFormType === 'restaurant';
    const orderType = isRestaurant
      ? String(checkoutForm.orderType || 'delivery').trim().toLowerCase()
      : 'delivery';
    if (orderType !== 'delivery') return 0;
    let region = String(checkoutForm.deliveryRegion || '').trim();
    if (!region && checkoutForm.cityId) {
      const picked = (cities || []).find(
        (c) => String(c.id) === String(checkoutForm.cityId)
      );
      region = String(picked?.region || '').trim();
    }
    if (!region && isRestaurant) {
      region = String(settings.storeDeliveryRegion || '').trim();
      if (!region && settings.storeCityId) {
        const storeCity = (cities || []).find(
          (c) => String(c.id) === String(settings.storeCityId)
        );
        region = String(storeCity?.region || '').trim();
      }
    }
    if (!region) return 0;
    const fee = deliveryFeeForRegion(prices, region);
    return fee ?? 0;
  }, [
    checkoutForm.deliveryRegion,
    checkoutForm.cityId,
    checkoutForm.orderType,
    cities,
    settings.deliveryPrices,
    settings.deliveryEnabled,
    settings.storeCityId,
    settings.storeDeliveryRegion,
    settings.checkoutFormType,
  ]);

  const checkoutShowsDelivery = useMemo(() => {
    const isRestaurant = settings.checkoutFormType === "restaurant";
    const orderType = isRestaurant
      ? checkoutForm.orderType || "delivery"
      : "delivery";
    let region = String(checkoutForm.deliveryRegion || "").trim();
    if (!region && checkoutForm.cityId) {
      const picked = (cities || []).find(
        (c) => String(c.id) === String(checkoutForm.cityId)
      );
      region = String(picked?.region || "").trim();
    }
    if (!region && isRestaurant) {
      region = String(settings.storeDeliveryRegion || "").trim();
    }
    return checkoutShouldShowDeliveryFee({
      deliveryEnabled: settings.deliveryEnabled === true,
      orderType,
      region,
      isRestaurant,
    });
  }, [
    checkoutForm.deliveryRegion,
    checkoutForm.cityId,
    checkoutForm.orderType,
    cities,
    settings.deliveryEnabled,
    settings.storeDeliveryRegion,
    settings.checkoutFormType,
  ]);

  const cartItemsSubtotal = useMemo(
    () =>
      cart.reduce((sum, item) => {
        const qty = Number(item.quantity || 1);
        return sum + cartLineUnitPrice(item) * qty;
      }, 0),
    [cart]
  );

  const submitWhatsappCartDirect = useCallback(() => {
    if (checkoutSubmitting) return;

    if (orderingBlockedByHours) {
      showOrderingClosedNotice();
      return;
    }

    const showCheckoutValidationModal = (title, message) => {
      setCheckoutError(null);
      setCheckoutValidationModal({
        open: true,
        title: title || "تنبيه",
        message,
      });
    };

    const showCheckoutStockModal = (message) => {
      let msg = String(message || "").trim();
      msg = msg
        .replace(
          /تأكد من مخزون المنتج أو عدّل المخزون من صفحة المنتجات\.?/g,
          ""
        )
        .replace(/\s{2,}/g, " ")
        .trim();
      if (!msg) {
        msg = "لا توجد كمية كافية في المخزون. يرجى تقليل الكمية أو التواصل مع المتجر.";
      } else if (!/تواصل|تقليل/i.test(msg)) {
        msg = `${msg} يرجى تقليل الكمية أو التواصل مع المتجر.`;
      }
      setCheckoutError(null);
      setCheckoutValidationModal({
        open: true,
        title: "المخزون",
        message: msg,
      });
    };

    const waDigits = normalizeWhatsAppDigits(settings.whatsappNumber);
    // Basic cart has no orders page: only a valid number is required (toggle is ignored).
    if (!isValidStoredWhatsappNumber(settings.whatsappNumber)) {
      showCheckoutValidationModal(
        canEdit ? "واتساب غير جاهز" : "واتساب غير متوفر",
        canEdit
          ? "أضف رقم واتساب صالح في الإعدادات حتى يتمكن الزبائن من إرسال السلة."
          : "الطلب عبر واتساب غير متاح حالياً."
      );
      return;
    }

    if (!cart || cart.length === 0) {
      showCheckoutValidationModal(
        "السلة فارغة",
        "السلة فارغة. أضف منتجات قبل إرسال الطلب."
      );
      return;
    }

    const cartStockErr = validateCartStockLimits(cart);
    if (cartStockErr) {
      showCheckoutStockModal(cartStockErr);
      return;
    }

    const waPopup = openWhatsAppPopupPlaceholder();
    setCheckoutSubmitting(true);
    setCheckoutError(null);

    try {
      const itemsSubtotal = cartItemsSubtotal;
      const waCart = cartItemsForWhatsApp(cart, catalogProductsById);
      const productLines = waCart.map((item, idx) => formatWhatsAppCartLine(item, idx));
      const headerLines = [
        whatsappOrderTitleForCart(cart),
        "",
        "*تفاصيل الطلب*",
        ...productLines,
        "",
        `*مجموع المنتجات: ₪${itemsSubtotal}*`,
      ];

      const waUrl = buildWhatsAppMeUrl(waDigits, headerLines.join("\n"));
      trackWhatsAppClick();
      const whatsappOpened = openWhatsAppUrl(waUrl, waPopup);

      setCart([]);
      setWholesaleEditingOrderId(null);
      setStorefrontEditingOrderId(null);
      didLoadWholesaleCartRef.current = false;
      try {
        if (shopper && storeKey) {
          storefrontFetch("/storefront/cart", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ catalog_slug: String(storeKey), items: [] }),
          }).catch(() => {});
        }
      } catch {
        /* ignore */
      }

      setIsCartOpen(false);
      setOrderSuccessInfo({
        orderType: "delivery",
        orderNumber: "",
        wasEdited: false,
        whatsappOpened: !!whatsappOpened,
        whatsappUrl: !whatsappOpened && waUrl ? waUrl : "",
        whatsappCartOnly: true,
      });
      setOrderSuccessOpen(true);
    } catch (e) {
      try {
        if (waPopup && !waPopup.closed) waPopup.close();
      } catch {
        /* ignore */
      }
      console.error("WhatsApp cart submit failed:", e);
      setCheckoutError(e?.message || "حدث خطأ أثناء إرسال الطلب عبر واتساب.");
    } finally {
      setCheckoutSubmitting(false);
    }
  }, [
    cart,
    cartItemsSubtotal,
    checkoutSubmitting,
    didLoadWholesaleCartRef,
    settings.whatsappNumber,
    orderingBlockedByHours,
    showOrderingClosedNotice,
    shopper,
    storeKey,
    storefrontFetch,
    setWholesaleEditingOrderId,
    canEdit,
  ]);

  const openCheckout = useCallback(() => {
    if (orderingBlockedByHours) {
      showOrderingClosedNotice();
      return;
    }
    setCheckoutError(null);
    setIsCheckoutOpen(true);
    const isRestaurant = settings.checkoutFormType === 'restaurant';
    const storeCityId = String(settings.storeCityId || '').trim();
    // Prefill name/phone for logged-in shoppers (without overwriting user edits).
    setCheckoutForm((p) => {
      const orderType = isRestaurant ? (p.orderType || 'delivery') : 'delivery';
      const next = {
        ...p,
        name: (p.name || '').trim()
          ? p.name
          : String(shopper?.full_name || '').trim(),
        phone: (p.phone || '').trim()
          ? p.phone
          : String(shopper?.phone || '').trim(),
      };
      if (isRestaurant && orderType === 'delivery') {
        const storeRegion = String(settings.storeDeliveryRegion || '').trim();
        if (storeRegion) {
          next.deliveryRegion = storeRegion;
        }
      }
      return next;
    });
    if (settings.checkoutFormType !== 'restaurant') {
      loadCities();
    }
  }, [loadCities, shopper, settings.checkoutFormType, settings.storeDeliveryRegion, orderingBlockedByHours, showOrderingClosedNotice]);

  const closeCheckout = useCallback(() => {
    setIsCheckoutOpen(false);
    setCheckoutError(null);
  }, []);

  // Lock background scroll when auth modal is open
  useEffect(() => {
    if (!authOpen) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, [authOpen]);

  const submitCheckout = useCallback(async () => {
    if (checkoutSubmitting) return;

    if (orderingBlockedByHours) {
      showOrderingClosedNotice();
      return;
    }

    const isRestaurant = settings.checkoutFormType === 'restaurant';
    const orderType = isRestaurant
      ? (checkoutForm.orderType || 'delivery')
      : 'delivery';

    const name = (checkoutForm.name || '').trim();
    const cityId = String(checkoutForm.cityId || '').trim();
    const address = (checkoutForm.address || '').trim();
    const phone = (checkoutForm.phone || '').trim();
    const altPhone = (checkoutForm.altPhone || '').trim();
    const notes = (checkoutForm.notes || '').trim();
    const tableNumber = (checkoutForm.tableNumber || '').trim();

    const showCheckoutValidationModal = (missingLabels) => {
      setCheckoutError(null);
      setCheckoutValidationModal({
        open: true,
        title: "حقول مطلوبة",
        message:
          missingLabels.length > 0
            ? `يرجى تعبئة الحقول الإلزامية التالية قبل المتابعة: ${missingLabels.join("، ")}.`
            : "يرجى تعبئة جميع الحقول الإلزامية قبل المتابعة.",
      });
    };

    const showCheckoutStockModal = (message) => {
      let msg = String(message || "").trim();
      msg = msg
        .replace(
          /تأكد من مخزون المنتج أو عدّل المخزون من صفحة المنتجات\.?/g,
          ""
        )
        .replace(/\s{2,}/g, " ")
        .trim();
      if (!msg) {
        msg = "لا توجد كمية كافية في المخزون. يرجى تقليل الكمية أو التواصل مع المتجر.";
      } else if (!/تواصل|تقليل/i.test(msg)) {
        msg = `${msg} يرجى تقليل الكمية أو التواصل مع المتجر.`;
      }
      setCheckoutError(null);
      setCheckoutValidationModal({
        open: true,
        title: "المخزون",
        message: msg,
      });
    };

    /** التحقّق من الحقول الإلزامية حسب نوع الفورم/الطلب */
    if (orderType === "delivery") {
      const missing = getDeliveryCheckoutMissingFields(checkoutForm, {
        isRestaurant,
        cities,
      });
      if (missing.length > 0) {
        showCheckoutValidationModal(missing);
        return;
      }
    } else if (orderType === "dine_in") {
      const missing = [];
      if (!name) missing.push("الاسم");
      if (!phone) missing.push("رقم الهاتف");
      if (!tableNumber) missing.push("رقم الطاولة");
      if (missing.length > 0) {
        showCheckoutValidationModal(missing);
        return;
      }
    } else if (orderType === "pickup") {
      const missing = [];
      if (!name) missing.push("الاسم");
      if (!phone) missing.push("رقم الهاتف");
      if (missing.length > 0) {
        showCheckoutValidationModal(missing);
        return;
      }
    }

    if (phone && !isValidCheckoutPhone(phone)) {
      showCheckoutValidationModal(["رقم الهاتف (10 أرقام فقط)"]);
      return;
    }

    if (!cart || cart.length === 0) {
      setCheckoutValidationModal({
        open: true,
        message: "السلة فارغة. أضف منتجات قبل إتمام الطلب.",
      });
      return;
    }

    const cartStockErr = validateCartStockLimits(cart);
    if (cartStockErr) {
      showCheckoutStockModal(cartStockErr);
      return;
    }

    if (wholesaleDealer) {
      const missingWholesale = cart.find(
        (item) => cartItemWholesaleUnitPrice(item) == null
      );
      if (missingWholesale) {
        setCheckoutError("سعر الجملة غير متوفر لهذا المنتج أو الخيار");
        return;
      }
    }

    const waDigits = normalizeWhatsAppDigits(settings.whatsappNumber);
    const shouldOpenWhatsapp = shouldSendOrderViaWhatsapp(settings);
    /** Must run in same turn as the button click — before any await. */
    const waPopup = shouldOpenWhatsapp ? openWhatsAppPopupPlaceholder() : null;

    // Meta Pixel InitiateCheckout — after validation, before the order POST
    trackMetaInitiateCheckout(cart, cartLineUnitPrice);
    trackTikTokInitiateCheckout(cart, cartLineUnitPrice);

    setCheckoutSubmitting(true);
    setCheckoutError(null);
    try {
      const isWholesaleCheckout = Boolean(wholesaleDealer);
      const items = cart.map((item) => {
        if (item?.isBundleOffer) {
          return {
            product_id: null,
            bundle_offer_section_id: String(item.bundleOfferSectionId || "").trim() || null,
            bundle_offer_id: String(item.bundleOfferId || "").trim() || null,
            bundle_picks: serializeBundlePicksForApi(item.bundlePicks),
            quantity: Number(item.quantity || 1),
            unit_price: cartLineUnitPrice(item),
            note: String(item.note || "").trim() || null,
          };
        }
        if (item?.isCustomOrder) {
          return {
            product_id: null,
            custom_order_section_id: String(item.customOrderSectionId || "").trim() || null,
            custom_order_answers: serializeCustomOrderAnswersForApi(item.customOrderAnswers),
            quantity: Number(item.quantity || 1),
            unit_price: cartLineUnitPrice(item),
            note: String(item.note || "").trim() || null,
          };
        }
        return {
        product_id: String(item.id),
        quantity: Number(item.quantity || 1),
        unit_price: cartLineUnitPrice(item),
        selected_variant: item.selectedVariant || null,
        selected_size: (item.selectedSize || '').trim() || null,
        selected_color: (item.selectedColor || '').trim() || null,
        note: String(item.note || '').trim() || null,
        selected_addons:
          Array.isArray(item.selectedAddons) && item.selectedAddons.length
            ? item.selectedAddons.map((a) => ({ id: String(a.id) }))
            : null,
      };
      });

      /** بناء البودي بحسب نوع الطلب */
      const orderBody = {
        customer_name: name,
        phone: normalizeCheckoutPhoneDigits(phone),
        alt_phone: altPhone ? normalizeCheckoutPhoneDigits(altPhone) : null,
        notes: notes || null,
        items,
        order_type: orderType,
      };
      if (orderType === 'delivery') {
        let regionKey = String(checkoutForm.deliveryRegion || '').trim();
        if (!regionKey && cityId) {
          const picked = (cities || []).find((c) => String(c.id) === String(cityId));
          regionKey = String(picked?.region || '').trim();
        }
        if (cityId) {
          orderBody.city_id = Number(cityId);
        }
        if (regionKey) {
          orderBody.delivery_region = regionKey;
        }
        orderBody.address = address;
      } else if (orderType === 'dine_in') {
        orderBody.table_number = tableNumber;
      }

      const ordersPath = isWholesaleCheckout
        ? `/wholesale/orders`
        : `/orders`;
      if (isWholesaleCheckout && wholesaleEditingOrderId) {
        orderBody.editing_order_id = wholesaleEditingOrderId;
      } else if (storefrontEditingOrderId && !isWholesaleCheckout) {
        orderBody.editing_order_id = storefrontEditingOrderId;
      }
      const resp = isWholesaleCheckout
        ? await wholesaleCatalogFetch(storeKey || userId, ordersPath, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(orderBody),
          }, wholesaleCatalogSlug || catalogSlug)
        : await fetch(`${API_URL}/catalog/${encodeURIComponent(String(userId))}/orders`, {
            method: "POST",
            headers: (() => {
              const token = localStorage.getItem("storefront_token");
              return {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              };
            })(),
            body: JSON.stringify(orderBody),
          });

      let receipt = null;
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const detail = String(err?.detail || "");
        if (
          isWholesaleCheckout &&
          wholesaleEditingOrderId &&
          (detail === "Cannot edit this order" ||
            detail.includes("Cannot edit") ||
            detail.includes("لا يمكن تعديل"))
        ) {
          setWholesaleEditingOrderId(null);
          try {
            await wholesaleCatalogFetch(storeKey, "/wholesale/cart", {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items: cart || [], editing_order_id: null }),
            }, wholesaleCatalogSlug || catalogSlug);
          } catch {
            /* ignore */
          }
          delete orderBody.editing_order_id;
          const retryResp = await wholesaleCatalogFetch(
            storeKey || userId,
            ordersPath,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(orderBody),
            },
            wholesaleCatalogSlug || catalogSlug
          );
          if (!retryResp.ok) {
            const retryErr = await retryResp.json().catch(() => ({}));
            throw new Error(retryErr?.detail || "فشل إنشاء الطلب");
          }
          receipt = await retryResp.json().catch(() => ({}));
        } else {
          throw new Error(detail || "فشل إنشاء الطلب");
        }
      } else {
        receipt = await resp.json().catch(() => ({}));
      }

      const wasWholesaleOrderEdit =
        isWholesaleCheckout && Boolean(receipt?.updated);
      const wasStorefrontOrderEdit =
        !isWholesaleCheckout &&
        (Boolean(receipt?.updated) || Boolean(storefrontEditingOrderId));
      const wholesaleOrderIdShort = receipt?.order?.id
        ? String(receipt.order.id).slice(0, 8)
        : "";
      const storeOrderNumber = String(receipt?.order?.order_number || "").trim();

      if (receipt?.editing_cleared) {
        setWholesaleEditingOrderId(null);
      }

      const productLines = cartItemsForWhatsApp(cart, catalogProductsById).map((item, idx) =>
        formatWhatsAppCartLine(item, idx)
      );
      const itemsSubtotal =
        receipt?.items_subtotal != null
          ? Number(receipt.items_subtotal) || 0
          : cartItemsSubtotal;
      const deliveryFee = isWholesaleCheckout
        ? 0
        : receipt?.delivery_fee != null && receipt?.delivery_fee !== ''
          ? Math.max(0, Number(receipt.delivery_fee) || 0)
          : orderType === 'delivery' && settings.deliveryEnabled
            ? checkoutDeliveryFee
            : 0;
      const grandTotal = isWholesaleCheckout
        ? itemsSubtotal
        : receipt?.grand_total != null
          ? Number(receipt.grand_total) || 0
          : itemsSubtotal + deliveryFee;

      /** بناء نص رسالة واتساب حسب نوع الطلب */
      const orderTypeLabel =
        orderType === 'dine_in'
          ? 'داخل المطعم (طاولة)'
          : orderType === 'pickup'
            ? 'استلام من المطعم'
            : 'توصيل';

      const customerLines = ['*بيانات الزبون*', `الاسم: ${name}`];
      if (orderType === 'delivery') {
        const regionKey =
          String(checkoutForm.deliveryRegion || '').trim() ||
          cities?.find((c) => String(c.id) === String(cityId))?.region ||
          '';
        if (regionKey) {
          customerLines.push(
            `المنطقة: ${deliveryRegionLabel(regionKey, 'ar')}`
          );
        }
        const isRestaurantOrder = settings.checkoutFormType === 'restaurant';
        const cityName = isRestaurantOrder
          ? String(settings.storeCityName || '').trim()
          : cities?.find((c) => String(c.id) === String(cityId))?.name || '';
        if (isRestaurantOrder) {
          if (cityName) customerLines.push(`المدينة: ${cityName}`);
          if (address) customerLines.push(`تفاصيل العنوان: ${address}`);
        } else {
          if (cityName) customerLines.push(`العنوان: ${cityName}`);
          if (address) customerLines.push(`تفاصيل العنوان: ${address}`);
        }
      } else if (orderType === 'dine_in') {
        customerLines.push(`رقم الطاولة: ${tableNumber}`);
      }
      customerLines.push(`الهاتف: ${phone}`);
      if (altPhone) customerLines.push(`هاتف بديل: ${altPhone}`);
      if (notes) customerLines.push(`ملاحظة: ${notes}`);

      const wholesaleDealerName = String(wholesaleDealer?.name || "").trim();
      const wholesaleDealerLines =
        isWholesaleCheckout && wholesaleDealerName
          ? [`*التاجر:* ${wholesaleDealerName}`, ""]
          : [];

      const editIntroLines = wasWholesaleOrderEdit
        ? [
            "*⬇️ طلبية معدّلة — ليست طلبية جديدة*",
            "*طلب جملة معدّل*",
            "تحديث لطلبية سابقة — تم تعديلها من قبل التاجر.",
            ...(wholesaleOrderIdShort
              ? [`رقم الطلبية: #${wholesaleOrderIdShort}`]
              : storeOrderNumber
                ? [`رقم الطلبية: ${formatOrderNumberDisplay(storeOrderNumber)}`]
                : []),
            "",
          ]
        : wasStorefrontOrderEdit
          ? [
              "*⬇️ طلبية معدّلة — ليست طلبية جديدة*",
              "*طلب معدّل من الزبون*",
              "تحديث لطلبية سابقة — تم تعديلها من قبل الزبون قبل تأكيد المتجر.",
              ...(storeOrderNumber
                ? [`رقم الطلبية: ${formatOrderNumberDisplay(storeOrderNumber)}`]
                : []),
              "",
            ]
          : [];

      const headerLines = [
        ...editIntroLines,
        ...(!wasWholesaleOrderEdit && !wasStorefrontOrderEdit
          ? [
              ...(storeOrderNumber
                ? [`*رقم الطلبية: ${formatOrderNumberDisplay(storeOrderNumber)}*`, ""]
                : []),
              isWholesaleCheckout
                ? whatsappOrderTitleForCart(cart, { isWholesale: true })
                : whatsappOrderTitleForCart(cart),
            ]
          : []),
        ...wholesaleDealerLines,
        `النوع: ${orderTypeLabel}`,
        '',
        ...customerLines,
        '',
        '*تفاصيل المنتجات*',
        ...productLines,
        '',
        `*مجموع المنتجات: ₪${itemsSubtotal}*`,
        ...(orderType === 'delivery' && settings.deliveryEnabled && !isWholesaleCheckout
          ? [`*التوصيل: ₪${deliveryFee}*`]
          : []),
        `*المجموع الكلي: ₪${grandTotal}*`,
      ].filter(Boolean);

      /** فتح واتساب إذا فعّل صاحب المتجر الخيار (نافذة مُسبقة لتجاوز حاجب النوافذ) */
      const waUrl = shouldOpenWhatsapp
        ? buildWhatsAppMeUrl(waDigits, headerLines.join("\n"))
        : "";
      let whatsappOpened = false;
      if (shouldOpenWhatsapp && waUrl) {
        trackWhatsAppClick();
        whatsappOpened = openWhatsAppUrl(waUrl, waPopup);
      }

      // Clear cart after successful order creation (local + server for logged-in shoppers)
      setCart([]);
      setWholesaleEditingOrderId(null);
      setStorefrontEditingOrderId(null);
      didLoadWholesaleCartRef.current = false;
      try {
        if (isWholesaleCheckout && storeKey) {
          await wholesaleCatalogFetch(storeKey, "/wholesale/cart", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [], editing_order_id: null }),
          });
          didLoadWholesaleCartRef.current = true;
        } else if (shopper && storeKey) {
          await storefrontFetch('/storefront/cart', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ catalog_slug: String(storeKey), items: [] }),
          });
        }
      } catch {}

      // Close checkout and cart after opening WhatsApp
      setIsCheckoutOpen(false);
      setIsCartOpen(false);
      // أظهر مودال نجاح الطلب — مع تمرير نوع الطلب وحالة فتح الواتساب لرسالة مخصّصة.
      setOrderSuccessInfo({
        orderType,
        orderNumber: storeOrderNumber,
        wasEdited: wasStorefrontOrderEdit || wasWholesaleOrderEdit,
        whatsappOpened: !!whatsappOpened,
        whatsappUrl:
          shouldOpenWhatsapp && !whatsappOpened && waUrl ? waUrl : "",
      });
      setOrderSuccessOpen(true);
    } catch (e) {
      try {
        if (waPopup && !waPopup.closed) waPopup.close();
      } catch (_) {
        /* ignore */
      }
      console.error('Checkout submit failed:', e);
      const errMsg = e?.message || "حدث خطأ أثناء إنشاء الطلب.";
      const isStockErr =
        errMsg.includes("المخزون") ||
        errMsg.includes("كمية كافية") ||
        errMsg.includes("كمية متوفرة");
      if (isStockErr) {
        showCheckoutStockModal(errMsg);
      } else {
        setCheckoutError(errMsg);
      }
    } finally {
      setCheckoutSubmitting(false);
    }
  }, [
    settings.whatsappNumber,
    settings.sendOrdersToWhatsapp,
    settings.checkoutFormType,
    settings.storeCityId,
    settings.deliveryPrices,
    settings.deliveryEnabled,
    checkoutSubmitting,
    checkoutForm,
    cart,
    cartItemsSubtotal,
    checkoutDeliveryFee,
    cities,
    userId,
    shopper,
    storeKey,
    storefrontFetch,
    wholesaleDealer,
    wholesaleEditingOrderId,
    storefrontEditingOrderId,
    setWholesaleEditingOrderId,
    didLoadWholesaleCartRef,
    orderingBlockedByHours,
    showOrderingClosedNotice,
  ]);
  /* حقن ألوان الثيم قبل أول رسمة لتفادي وميض الألوان */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const primary = canEdit ? (storeEdit.primaryColor || settings.primaryColor) : settings.primaryColor;
    const secondary = canEdit ? (storeEdit.secondaryColor || settings.secondaryColor) : settings.secondaryColor;
    const cols = canEdit ? (layoutsEdit.product_columns ?? layouts.product_columns) : layouts.product_columns;
    root.style.setProperty("--primary", primary || "#ffc200");
    root.style.setProperty("--primary-soft", `${(primary || "#ffc200")}15`);
    root.style.setProperty("--secondary", secondary || "#ff5b00");
    root.style.setProperty("--product-columns", String(cols ?? 3));

    const el = catalogPageRootRef.current;
    const x = catalogLayoutEffective;
    const gBg = layoutExtrasBool(x.global_page_bg_enabled);
    const darkSurf = catalogDarkSurfaceActive(x);
    const pageBg = x.page_bg || "#ffffff";
    if (el) {
      if (gBg) {
        el.style.background = pageBg;
      } else {
        el.style.background = "";
        el.style.removeProperty("background");
      }
      el.style.setProperty("--catalog-page-bg", pageBg);
      if (darkSurf) {
        el.style.setProperty("--catalog-section-surface", pageBg);
        el.style.setProperty(
          "--products-card-bg",
          String(x.products_card_bg_color || "").trim() || "#131c2e"
        );
        el.style.setProperty(
          "--products-title-color",
          String(x.products_heading_color || "").trim() || "#f1f5f9"
        );
        el.style.setProperty(
          "--products-subtitle-color",
          String(x.products_subtitle_color || "").trim() || "#94a3b8"
        );
      } else {
        el.style.removeProperty("--catalog-section-surface");
        if (String(x.products_card_bg_color || "").trim()) {
          el.style.setProperty("--products-card-bg", String(x.products_card_bg_color).trim());
        } else {
          el.style.removeProperty("--products-card-bg");
        }
      }
      const cartBorderW = Math.max(0, Math.min(12, Number(x.products_cart_icon_border_width) || 0));
      el.style.setProperty("--product-cart-icon-border-width", `${cartBorderW}px`);
      const cartBorderColor = String(x.products_cart_icon_border_color || "").trim();
      if (cartBorderColor) {
        el.style.setProperty("--product-cart-icon-border-color", cartBorderColor);
      } else {
        el.style.removeProperty("--product-cart-icon-border-color");
      }
      el.style.removeProperty("--catalog-text");
      el.style.removeProperty("--catalog-text-muted");
      el.style.setProperty(
        "--restaurant-topbar-bg",
        String(x.minimal_topbar_bg || "").trim() || x.restaurant_topbar_bg || "#0f172a"
      );
      el.style.setProperty(
        "--restaurant-topbar-text",
        String(x.minimal_topbar_store_name_color || "").trim() || x.restaurant_topbar_text || "#ffffff"
      );
    }
    root.style.removeProperty("--catalog-text");
    root.style.removeProperty("--catalog-text-muted");
  }, [
    canEdit,
    storeEdit.primaryColor,
    storeEdit.secondaryColor,
    settings.primaryColor,
    settings.secondaryColor,
    layoutsEdit.product_columns,
    layouts.product_columns,
    catalogLayoutEffective,
  ]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 901px)");
    const onChange = () => {
      if (mq.matches) setProductFiltersDrawerOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!productFiltersDrawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [productFiltersDrawerOpen]);

  const mapCatalogProduct = useCallback(
    (p) => {
      const img0 = p.images && p.images[0];
      const imgUrl =
        p.image ||
        (typeof img0 === 'string' ? img0 : img0?.image_url) ||
        p.displayImage ||
        '';
      const catRaw = p.category || p.categories?.name || "";
      const category =
        catRaw && String(catRaw).trim().toLowerCase() !== "general" ? catRaw : null;
      return {
        ...p,
        price: p.base_price ?? p.price,
        name: language === 'ar' ? (p.name_ar || p.name) : p.name,
        displayImage: imgUrl,
        image: imgUrl,
        category,
      };
    },
    [language]
  );

  const openProductModal = useCallback(
    (product) => {
      if (!product) return;
      setSelectedProduct(product);
      const pid = product.id || product.product_id;
      if (!pid) return;
      trackProductView(String(storeKey || userId || ""), String(pid), product);
      const enc = encodeURIComponent(String(pid));
      const nextPath =
        catalogPublicBasePath === "/"
          ? `/${enc}`
          : `${catalogPublicBasePath}/${enc}`;
      navigate(nextPath);
    },
    [navigate, storeKey, trackProductView, userId, catalogPublicBasePath]
  );

  const closeProductModal = useCallback(() => {
    setSelectedProduct(null);
    navigate(catalogPublicBasePath);
  }, [navigate, catalogPublicBasePath]);

  const onSelectProduct = useCallback(
    (product) => openProductModal(product),
    [openProductModal]
  );

  // Deep link: /catalog/:storeKey/:productId opens the same modal (no separate page).
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!storeKey || !productId) return;

      const existing = products.find((p) => String(p.id) === String(productId));
      if (existing) {
        setSelectedProduct(existing);
        trackProductView(String(storeKey || userId || ""), String(productId), existing);
        return;
      }

      try {
        const res = await fetch(
          `${API_URL}/catalog/${encodeURIComponent(storeKey)}/product/${encodeURIComponent(
            productId
          )}`,
          { headers: wholesaleCatalogHeaders() }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const p = data?.product;
        if (p) {
          const mapped = mapCatalogProduct(p);
          setSelectedProduct(mapped);
          trackProductView(String(storeKey || userId || ""), String(productId), mapped);
        }
      } catch {
        // ignore
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [API_URL, mapCatalogProduct, productId, products, storeKey, trackProductView, userId]);

  const scrollToProducts = useCallback((opts = {}) => {
    const extraDownPx = Number(opts.extraDownPx);
    const resolveEl = () =>
      document.getElementById("catalog-products-anchor") ||
      document.getElementById("products") ||
      document.querySelector(".products-section-flat") ||
      document.getElementById("categories-section") ||
      document.getElementById("categories-section-sidebar");
    const run = () => {
      const el = resolveEl();
      if (!el) return false;
      scrollCatalogElementIntoView(el, {
        stickyOffsetPx: 96,
        extraDownPx: Number.isFinite(extraDownPx) ? extraDownPx : 120,
      });
      return true;
    };
    // Immediate + retries (category fetch / layout shift)
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => run());
    } else {
      run();
    }
    window.setTimeout(run, 60);
    window.setTimeout(run, 180);
    window.setTimeout(run, 420);
    window.setTimeout(run, 750);
    window.setTimeout(run, 1200);
  }, []);

  const navigateBuilderCategory = useCallback(
    (catName) => {
      const name = String(catName || "").trim();
      setOffersOnly(false);
      if (!name || name.toLowerCase() === "general") {
        setCategoryFilter("All");
      } else {
        setCategoryFilter(name);
      }
      setPagination((prev) => ({ ...prev, page: 1, has_next: false, has_previous: false }));
      scrollToProducts({ extraDownPx: 120 });
    },
    [scrollToProducts]
  );

// const [settings, setSettings] = useState({
//   storeName: '',
//   logo: null,
//   whatsappNumber: '',
//   primaryColor: '#4f46e5',
//   secondaryColor: '#f3f4f6',
//   // حقول الفوتر الجديدة
//   footerBgColor: '#000000',
//   footerTextColor: '#ffffff',
//   phone: '',
//   email: '',
//   feature_description_1: '',
//         feature_description_2: '',
//         feature_description_3: '',
//   address: '',
//   facebook: '',
//   instagram: '',
//   headerTitle: '',
//   subheaderText: '',
//   heroUrl: '',
//   bgType: 'color'
// });

  // Check for video and image files
 


// أضف هذا الـ State أيضاً لاستقبال بيانات التنسيق

  // Check for image in public folder
  useEffect(() => {
    const checkMediaFiles = async () => {
      try {
        // Check for image (try both .jpg and .jpeg)
        try {
          const imageResponse1 = await fetch('/model.jpg', { method: 'HEAD' });
          if (imageResponse1.ok) {
            setHasImage(true);
            setImageUrl('/model.jpg');
            return;
          }
        } catch {}
        
        try {
          const imageResponse2 = await fetch('/model.jpeg', { method: 'HEAD' });
          if (imageResponse2.ok) {
            setHasImage(true);
            setImageUrl('/model.jpeg');
          }
        } catch {}
      } catch (error) {
        console.log('Media files check:', error);
      }
    };
    checkMediaFiles();
  }, []);


  const normalizeCatalogApiProducts = useCallback(
    (rawProducts) => {
      const mappedProducts = (rawProducts || []).map((p) => ({
        ...p,
        price: p.base_price,
        name: language === "ar" ? p.name_ar || p.name : p.name,
        displayImage: p.image || (p.images && p.images[0]),
        category:
          p.category && String(p.category).trim().toLowerCase() !== "general"
            ? p.category
            : null,
      }));
      return mappedProducts.filter((p) => {
        const v = p?.is_active;
        const a = p?.active;
        if (v === 0 || v === "0" || v === false) return false;
        if (a === 0 || a === "0" || a === false) return false;
        return true;
      });
    },
    [language]
  );

  const mergeSectionProducts = useCallback((incoming) => {
    if (!incoming?.length) return;
    setSectionProducts((prev) => {
      const map = new Map((prev || []).map((p) => [p.id, p]));
      for (const p of incoming) map.set(p.id, p);
      return Array.from(map.values());
    });
  }, []);

  const fetchCatalogPage = useCallback(
    async (pageNum, { append = false, background = false } = {}) => {
      if (editorMode && !(userId || "").trim()) return;
      const requestId = ++catalogFetchSeqRef.current;
      try {
        const shellReady = catalogShellReadyRef.current;
        const cacheKey = `${categoryFilter || "All"}|${searchDebounced || ""}|${pageNum}`;
        const cached = !append ? catalogPageCacheRef.current.get(cacheKey) : null;
        if (cached?.products && catalogBrandingReadyRef.current) {
          setProducts(cached.products);
          if (cached.pagination) setPagination((prev) => ({ ...prev, ...cached.pagination }));
          if (Array.isArray(cached.bundle_offers)) {
            setCatalogBundleOffers(cached.bundle_offers);
          }
          setProductsLoading(false);
          setLoading(false);
          // Instant category switch from cache (prefetch keeps it warm).
          // Never skip the network on first shell before branding/hero_url is applied.
          if (!background) return;
        } else if (!background) {
          // First load with server-injected store data: header + banner are already on screen,
          // so keep the page visible and only mark the products area as loading.
          const seeded =
            !shellReady &&
            !catalogBrandingReadyRef.current &&
            Boolean(storeSeedRef.current) &&
            Boolean(readStoreSeed(userId));
          setLoading(seeded ? false : !shellReady);
          setProductsLoading(seeded ? true : shellReady);
        }
        if (pageNum === 1 && !append && !cached && !background) {
          setCatalogError(null);
          setCatalogNotFound(false);
          setCatalogUnavailable(false);
        }

        const catalogParams = new URLSearchParams();
        catalogParams.set("page", String(pageNum));
        if (categoryFilter && categoryFilter !== "All") {
          catalogParams.set("category", categoryFilter);
        }
        if (searchDebounced) {
          catalogParams.set("search", searchDebounced);
        }
        // After first shell paint, always use lite products endpoint.
        if (shellReady || background) {
          catalogParams.set("lite", "1");
        }
        const catalogUrl = `${API_URL}/catalog/${encodeURIComponent((userId || "").trim())}?${catalogParams.toString()}`;
        const catalogHeaders = wholesaleCatalogHeaders();
        let response = null;
        const maxAttempts = background ? 1 : 5;
        // First visit to the store home with no dealer headers: the server-rendered shell already
        // started this exact request (see utils/earlyFetch.js); use it instead of a second round-trip.
        const earlyEligible =
          pageNum === 1 &&
          !append &&
          !background &&
          !shellReady &&
          (!categoryFilter || categoryFilter === "All") &&
          !searchDebounced &&
          Object.keys(catalogHeaders || {}).length === 0;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            response =
              (attempt === 0 && earlyEligible ? await takeEarlyResponse("catalog", catalogUrl) : null) ||
              (await fetch(catalogUrl, { headers: catalogHeaders }));
            // Retry transient upstream failures (Supabase disconnects → API 500).
            if (
              response.ok ||
              response.status === 404 ||
              response.status === 403 ||
              attempt === maxAttempts - 1
            ) {
              break;
            }
            if (response.status >= 500 || response.status === 429) {
              await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
              continue;
            }
            break;
          } catch (netErr) {
            if (attempt === maxAttempts - 1) throw netErr;
            await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
          }
        }
        if (!response) {
          throw new Error("Catalog fetch failed");
        }

        // Ignore stale responses from a previous category/search.
        if (requestId !== catalogFetchSeqRef.current && !append) {
          return;
        }

        if (!response.ok) {
          let detail = null;
          try {
            const errJson = await response.json();
            detail = errJson?.detail || null;
          } catch {
            // ignore parse errors
          }
          const msg =
            response.status === 404
              ? 'رابط المتجر غير صحيح أو لم يتم تفعيل رابط الكتالوج لهذا المتجر بعد.'
              : 'حدث خطأ أثناء تحميل المنتجات. حاول مرة أخرى.';
          const normalizedDetail = (detail || '').trim();
          const useDetail =
            normalizedDetail &&
            normalizedDetail !== 'Catalog not found' &&
            !isTechnicalCatalogErrorDetail(normalizedDetail);
          if (pageNum === 1 && !append && !background) {
            setProducts([]);
            if (response.status === 404) {
              setCatalogNotFound(true);
              setCatalogUnavailable(false);
            } else if (isCatalogStoreUnavailableError(response.status, normalizedDetail)) {
              setCatalogUnavailable(true);
              setCatalogNotFound(false);
              setCatalogError(null);
            }
          }
          if (
            !background &&
            !(pageNum === 1 && !append && isCatalogStoreUnavailableError(response.status, normalizedDetail))
          ) {
            setCatalogError(useDetail ? normalizedDetail : msg);
          }
          if (!background) {
            setLoading(false);
            setProductsLoading(false);
          }
          return;
        }

        const data = await response.json();
        if (requestId !== catalogFetchSeqRef.current && !append) {
          return;
        }
        if (!background) {
          setCatalogError(null);
          setCatalogNotFound(false);
          setCatalogUnavailable(false);
        }
        if (Array.isArray(data.bundle_offers)) {
          setCatalogBundleOffers(data.bundle_offers);
        }

        if (data) {
          const b = data.branding || {};
          if (data.branding) {
            applyWholesaleBrandingFlags(b);
            if (b.catalog_key) setCatalogSlug(String(b.catalog_key).trim());
          }

          if (pageNum === 1 && !append && data.branding) {
            const storeNameForUrl = (b.catalog_key || "").trim();
            if (
              !customDomainMode &&
              !didCanonicalizeUrl.current &&
              looksLikeUuid(userId) &&
              storeNameForUrl
            ) {
              didCanonicalizeUrl.current = true;
              const slugEnc = encodeURIComponent(storeNameForUrl);
              const newPath = productId
                ? menuOnly
                  ? `/catalog/${slugEnc}/menu/${encodeURIComponent(productId)}`
                  : `/catalog/${slugEnc}/${encodeURIComponent(productId)}`
                : menuOnly
                  ? `/catalog/${slugEnc}/menu`
                  : `/catalog/${slugEnc}`;
              navigate(newPath, { replace: true });
            }

            const heroFromApi = String(b.hero_url || "").trim();
            const heroResolved =
              heroFromApi || readCachedCatalogHeroUrl(userId) || "";
            writeCachedCatalogHeroUrl(userId, heroFromApi || heroResolved);
            const logoFromApi = String(b.logo_url || "").trim();
            const logoResolved =
              logoFromApi || readCachedCatalogLogoUrl(userId) || "";
            writeCachedCatalogLogoUrl(userId, logoFromApi || logoResolved);
            catalogBrandingReadyRef.current = true;
            // The API response supersedes the HTML-injected data; drop it so it can't go stale.
            if (storeSeedRef.current) {
              storeSeedRef.current = null;
              try {
                delete window.__STORE__;
              } catch {
                window.__STORE__ = undefined;
              }
            }

            if (data.layouts) {
              const topHero = data.layouts.show_top_hero ?? data.layouts.show_hero ?? true;
              const rawPub = data.layouts.catalog_layout_extras && typeof data.layouts.catalog_layout_extras === "object"
                ? data.layouts.catalog_layout_extras
                : null;
              const lx = { ...DEFAULT_CATALOG_LAYOUT_EXTRAS, ...(rawPub || {}) };
              migrateLegacyLayoutExtras(lx, rawPub);
              writeCachedCatalogLoaderPrefs(userId, lx.loader_text, lx.loader_text_color);
              setLayouts({
                sections: data.layouts.sections || [],
                best_products_ids: data.layouts.best_products_ids || [],
                show_hero: topHero,
                show_grid: data.layouts.show_grid ?? true,
                show_top_hero: topHero,
                show_features_bar: data.layouts.show_features_bar ?? true,
                product_columns: data.layouts.product_columns || 3,
                catalog_layout_extras: lx,
              });
            }

            setSettings({
              storeName: b.store_name || '',
              logo: logoResolved || null,
              whatsappNumber: b.whatsapp_number || '',
              primaryColor: b.primary_color || '#ffc200',
              secondaryColor: b.secondary_color || '#ff5b00',
              footerBgColor: canEdit ? (storeEdit.footerBgColor || b.footer_bg_color || '#000000') : (b.footer_bg_color || '#000000'),
              footerTextColor: canEdit ? (storeEdit.footerTextColor || b.footer_text_color || '#ffffff') : (b.footer_text_color || '#ffffff'),
              phone: b.phone_number || '',
              email: canEdit ? (storeEdit.email || b.email || '') : (b.email || ''),
              address: canEdit ? (storeEdit.address || b.address || '') : (b.address || ''),
              facebook: b.facebook_url || '',
              instagram: b.instagram_url || '',
              // لا نستخدم store_name كبديل لعنوان الهيرو — فارغ يعني صورة هيرو نظيفة بدون نص
              headerTitle: b.header_title_ar || '',
              subheaderText: b.subheader_text_ar || '',
              productsSectionTitle:
                canEdit ? (storeEdit.productsSectionTitle || b.products_section_title_ar || 'منتجاتنا المختارة') : (b.products_section_title_ar || 'منتجاتنا المختارة'),
              productsSectionSubtitle:
                canEdit ? (storeEdit.productsSectionSubtitle || b.products_section_subtitle_ar || 'تصفح تشكيلتنا الواسعة من أفضل المنتجات ذات الجودة العالية') : (b.products_section_subtitle_ar || 'تصفح تشكيلتنا الواسعة من أفضل المنتجات ذات الجودة العالية'),
              products_heading_align:
                canEdit ? (storeEdit.productsHeadingAlign || b.products_heading_align || 'start') : (b.products_heading_align || 'start'),
              heroUrl: heroResolved,
              bgType: heroResolved ? "image" : "color",
              layoutDirection: b.layout_direction || 'rtl',
              feature_description_1: b.feature_description_1 || '',
              feature_description_2: b.feature_description_2 || '',
              feature_description_3: b.feature_description_3 || '',
              feature_title_1: b.feature_title_1 || 'توصيل سريع',
              feature_title_2: b.feature_title_2 || 'ضمان الجودة',
              feature_title_3: b.feature_title_3 || 'دعم فني',
              features_bar_bg: b.features_bar_bg || '',
              features_bar_text: b.features_bar_text || '',
              features_bar_accent: b.features_bar_accent || '',
              sendOrdersToWhatsapp: parseSendOrdersToWhatsapp(
                b.send_orders_to_whatsapp
              ),
              showCatalogWhatsapp:
                b.show_catalog_whatsapp === true ||
                b.show_catalog_whatsapp === 1 ||
                String(b.show_catalog_whatsapp || "").toLowerCase() === "true",
              checkoutFormType:
                String(b.checkout_form_type || 'store').toLowerCase() === 'restaurant'
                  ? 'restaurant'
                  : 'store',
              storeCityId: b.city_id != null ? String(b.city_id) : '',
              storeCityName: b.store_city_name || '',
              storeDeliveryRegion: b.store_delivery_region || '',
              deliveryPrices: {
                ...defaultDeliveryPricesMap(),
                ...(b.delivery_prices && typeof b.delivery_prices === 'object'
                  ? b.delivery_prices
                  : {}),
              },
              deliveryEnabled:
                b.delivery_enabled === true ||
                b.delivery_enabled === 1 ||
                String(b.delivery_enabled || "").toLowerCase() === "true",
              businessHoursEnabled:
                b.business_hours_enabled === true ||
                b.business_hours_enabled === 1 ||
                String(b.business_hours_enabled || "").toLowerCase() === "true",
              businessHours: normalizeBusinessHours(b.business_hours),
              storefrontCartEnabled:
                b.storefront_cart_enabled !== false ||
                (b.storefront_cart_enabled == null &&
                  b.storefront_orders_enabled !== false),
              storefrontFullOrdersEnabled: b.storefront_orders_enabled !== false,
              returnExchangePolicy: String(b.return_exchange_policy || "").trim(),
              metaPixelId: String(b.meta_pixel_id || "").replace(/\D/g, ""),
              tiktokPixelId: String(b.tiktok_pixel_id || "").replace(/[^a-zA-Z0-9]/g, ""),
            });
          }

          const activeProducts = normalizeCatalogApiProducts(data.products);

          mergeSectionProducts(activeProducts);

          setProducts((prev) => {
            if (!append) return activeProducts;
            const seen = new Set(prev.map((p) => p.id));
            const merged = [...prev];
            for (const p of activeProducts) {
              if (!seen.has(p.id)) {
                seen.add(p.id);
                merged.push(p);
              }
            }
            return merged;
          });

          if (pageNum === 1 && !append) {
            setHasOffers(activeProducts.some((p) => Boolean(p.on_sale)));
          } else if (activeProducts.length > 0) {
            setHasOffers((prev) => prev || activeProducts.some((p) => Boolean(p.on_sale)));
          }

          if (pageNum === 1 && !append) {
            if (Array.isArray(data.category_options) && data.category_options.length > 0) {
              const opts = data.category_options.filter(
                (c) => String(c || "").trim().toLowerCase() !== "general"
              );
              setCategories(opts.length > 0 ? opts : ["All"]);
            } else if (
              data.branding &&
              activeProducts.length > 0
            ) {
              const uniqueCategories = [
                "All",
                ...new Set(activeProducts.map((p) => p.category).filter(Boolean)),
              ];
              setCategories(uniqueCategories);
            }
          }

          if (data.pagination) {
            setPagination((prev) => ({
              ...data.pagination,
              page_size: data.pagination.page_size ?? prev.page_size ?? 20,
            }));
          }

          if (!append && pageNum === 1) {
            catalogPageCacheRef.current.set(cacheKey, {
              products: activeProducts,
              pagination: data.pagination || null,
              bundle_offers: Array.isArray(data.bundle_offers)
                ? data.bundle_offers
                : undefined,
            });
          }
        }

        if (catalogBrandingReadyRef.current) {
          catalogShellReadyRef.current = true;
        }
        // Don't dismiss the full-page loader on the very first paint until branding
        // (hero_url / colors) has been applied — avoids the solid yellow empty hero flash.
        if (catalogBrandingReadyRef.current || shellReady || background || append) {
          setLoading(false);
        } else if (pageNum === 1 && !append) {
          // Full response without branding payload — still unblock the UI.
          catalogBrandingReadyRef.current = true;
          catalogShellReadyRef.current = true;
          setLoading(false);
        }
        setProductsLoading(false);
      } catch (error) {
        console.error("Error loading catalog:", error);
        setCatalogError('حدث خطأ أثناء تحميل المنتجات. حاول تحديث الصفحة.');
        setLoading(false);
        setProductsLoading(false);
      }
    },
    [
      editorMode,
      language,
      userId,
      categoryFilter,
      searchDebounced,
      wholesaleCatalogHeaders,
      applyWholesaleBrandingFlags,
      normalizeCatalogApiProducts,
      mergeSectionProducts,
    ]
  );

  const fetchSectionProductsBootstrap = useCallback(async () => {
    const ident = (userId || "").trim();
    if (!ident) return;
    try {
      const catalogParams = new URLSearchParams();
      catalogParams.set("page", "1");
      catalogParams.set("lite", "1");
      const response = await fetch(
        `${API_URL}/catalog/${encodeURIComponent(ident)}?${catalogParams.toString()}`,
        { headers: wholesaleCatalogHeaders() }
      );
      if (!response.ok) return;
      const data = await response.json();
      mergeSectionProducts(normalizeCatalogApiProducts(data.products));
    } catch {
      // Section products are best-effort; main catalog fetch handles errors.
    }
  }, [userId, wholesaleCatalogHeaders, normalizeCatalogApiProducts, mergeSectionProducts]);

  const [builderReloadTick, setBuilderReloadTick] = useState(0);
  const builderRecoveryAttemptsRef = useRef(0);
  const sectionProductRequestsRef = useRef(new Set());
  const sectionProductsInFlightRef = useRef(0);
  const [sectionProductsSettled, setSectionProductsSettled] = useState(false);
  useEffect(() => {
    setSectionProducts([]);
    sectionProductRequestsRef.current = new Set();
    sectionProductsInFlightRef.current = 0;
    setSectionProductsSettled(false);
  }, [userId]);

  useEffect(() => {
    fetchCatalogPage(1, { append: false });
  }, [fetchCatalogPage]);

  // Release the below-the-banner hold (see seedHold) once products + builder sections are in,
  // or when the store can't be shown; never hold longer than a few seconds.
  useEffect(() => {
    if (!seedHold) return;
    if (catalogNotFound || catalogUnavailable) {
      setSeedHold(false);
      return;
    }
    // Only the builder section LIST gates the release. Waiting for the main products request or for the
    // products listed inside sections made everything below the banner appear ~1s late. Sections that
    // list products now show a same-size skeleton until their products arrive (no layout jump), and the
    // products grid shows its own loading state.
    if (loading || builderLoading || !builderFetched) return;
    setSeedHold(false);
  }, [seedHold, loading, builderLoading, builderFetched, catalogNotFound, catalogUnavailable]);
  useEffect(() => {
    if (!seedHold) return undefined;
    const t = window.setTimeout(() => setSeedHold(false), 5000);
    return () => window.clearTimeout(t);
  }, [seedHold]);

  // Prefetch other categories in the background after first shell load.
  useEffect(() => {
    if (!catalogShellReadyRef.current) return;
    if (catalogNotFound || catalogUnavailable) return;
    const ident = (userId || "").trim();
    if (!ident) return;
    const cats = (categories || []).filter(
      (c) => c && c !== "All" && c !== categoryFilter
    );
    let cancelled = false;
    const run = async () => {
      for (const cat of cats.slice(0, 10)) {
        if (cancelled) return;
        const cacheKey = `${cat}|${searchDebounced || ""}|1`;
        if (catalogPageCacheRef.current.has(cacheKey)) continue;
        try {
          const params = new URLSearchParams();
          params.set("page", "1");
          params.set("category", cat);
          params.set("lite", "1");
          if (searchDebounced) params.set("search", searchDebounced);
          const response = await fetch(
            `${API_URL}/catalog/${encodeURIComponent(ident)}?${params.toString()}`,
            { headers: wholesaleCatalogHeaders() }
          );
          if (!response.ok) continue;
          const data = await response.json();
          const activeProducts = normalizeCatalogApiProducts(data.products);
          catalogPageCacheRef.current.set(cacheKey, {
            products: activeProducts,
            pagination: data.pagination || null,
          });
        } catch {
          // Prefetch is best-effort.
        }
      }
    };
    const t = window.setTimeout(run, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [
    categories,
    categoryFilter,
    searchDebounced,
    userId,
    catalogNotFound,
    catalogUnavailable,
    wholesaleCatalogHeaders,
    normalizeCatalogApiProducts,
  ]);

  useEffect(() => {
    if (canEdit || loading || productsLoading || catalogNotFound || catalogUnavailable) return;
    // Avoid a second full catalog round-trip on first paint — reuse main products.
    if ((products || []).length > 0) {
      mergeSectionProducts(products);
      return;
    }
    fetchSectionProductsBootstrap();
  }, [canEdit, loading, productsLoading, catalogNotFound, catalogUnavailable, fetchSectionProductsBootstrap, products, mergeSectionProducts]);

  useEffect(() => {
    if (!wholesaleDealer || canEdit) return;
    fetchCatalogPage(1, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wholesaleDealer?.id]);

  useEffect(() => {
    if (canEdit || catalogNotFound || catalogUnavailable) return;
    const loadBuilder = async () => {
      const ident = (userId || "").trim();
      if (!ident) return;
      setBuilderLoading(true);
      try {
        let res = null;
        for (let attempt = 0; attempt < 4; attempt += 1) {
          try {
            const builderUrl = `${API_URL}/builder/catalog/public/${encodeURIComponent(ident)}`;
            res =
              (attempt === 0 ? await takeEarlyResponse("builder", builderUrl) : null) ||
              (await fetch(builderUrl, { cache: "no-store" }));
            if (res.ok || (res.status >= 400 && res.status < 500)) break;
          } catch {
            res = null;
          }
          await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
        }
        if (!res || !res.ok) {
          setBuilderPage({ theme: null, sections: [] });
          if ((!res || res.status >= 500) && builderRecoveryAttemptsRef.current < 3) {
            builderRecoveryAttemptsRef.current += 1;
            window.setTimeout(() => setBuilderReloadTick((t) => t + 1), 4000 * builderRecoveryAttemptsRef.current);
          }
          return;
        }
        builderRecoveryAttemptsRef.current = 0;
        const data = await res.json();
        const sections = Array.isArray(data?.sections) ? data.sections : [];
        const normalized = sections
          .map((s, idx) => {
            const raw = s.settings || {};
            return {
              id: s.id || `sec_${idx}`,
              type: s.type,
              order: Number.isFinite(s.order) ? s.order : idx,
              settings: {
                ...BUILDER_FRAME_DEFAULTS,
                ...builderFontDefaultsForType(s.type),
                ...raw,
                style: {
                  backgroundColor: "#ffffff",
                  textColor: "#0f172a",
                  ...(raw.style || {}),
                },
              },
            };
          })
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const { before: pb, after: pa } = partitionBuilderSections(normalized);
        setBuilderPage({ theme: data?.page?.theme || null, sections: mergeBuilderSections(pb, pa) });
      } catch {
        setBuilderPage({ theme: null, sections: [] });
      } finally {
        setBuilderLoading(false);
        setBuilderFetched(true);
      }
    };
    loadBuilder();
  }, [userId, canEdit, catalogNotFound, catalogUnavailable, builderReloadTick]);

  useEffect(() => {
    if (canEdit || catalogNotFound || catalogUnavailable) return;
    const ident = (userId || "").trim();
    if (!ident) return;

    const sections = builderPage?.sections || [];
    const needed = new Set();
    for (const sec of sections) {
      const st = sec?.settings || {};
      for (const id of Array.isArray(st.product_ids) ? st.product_ids : []) {
        if (id != null && id !== "") needed.add(String(id));
      }
      for (const sl of Array.isArray(st.slides) ? st.slides : []) {
        const pid = sl?.product_id;
        if (pid != null && pid !== "") needed.add(String(pid));
      }
    }
    if (builderLoading || needed.size === 0) return;

    const have = new Set((sectionProducts || []).map((p) => String(p.id)));
    const requestKey = `${ident}|`;
    const requested = sectionProductRequestsRef.current;
    const missing = [...needed].filter((id) => !have.has(id) && !requested.has(requestKey + id));
    if (missing.length === 0) {
      if (sectionProductsInFlightRef.current === 0) setSectionProductsSettled(true);
      return;
    }

    missing.forEach((pid) => requested.add(requestKey + pid));
    setSectionProductsSettled(false);
    sectionProductsInFlightRef.current += 1;

    const fetchOne = async (pid) => {
      try {
        const res = await fetch(
          `${API_URL}/catalog/${encodeURIComponent(ident)}/product/${encodeURIComponent(pid)}`,
          { headers: wholesaleCatalogHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        return data?.product || null;
      } catch {
        return null;
      }
    };

    (async () => {
      let rows = null;
      try {
        const byIdsHeaders = wholesaleCatalogHeaders();
        const res =
          (Object.keys(byIdsHeaders || {}).length === 0 ? await takeEarlyProductsByIds(missing) : null) ||
          (await fetch(
            `${API_URL}/catalog/${encodeURIComponent(ident)}/products-by-ids?ids=${encodeURIComponent(missing.join(","))}`,
            { headers: byIdsHeaders }
          ));
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data?.products)) rows = data.products;
        }
      } catch {
        rows = null;
      }
      if (!rows) {
        rows = (await Promise.all(missing.map(fetchOne))).filter(Boolean);
      }
      const active = normalizeCatalogApiProducts(rows);
      if (active.length > 0) mergeSectionProducts(active);
      sectionProductsInFlightRef.current = Math.max(0, sectionProductsInFlightRef.current - 1);
      if (sectionProductsInFlightRef.current === 0) setSectionProductsSettled(true);
    })();
  }, [
    builderLoading,
    builderPage?.sections,
    sectionProducts,
    userId,
    canEdit,
    catalogNotFound,
    catalogUnavailable,
    wholesaleCatalogHeaders,
    normalizeCatalogApiProducts,
    mergeSectionProducts,
  ]);

  const renderBuilderSection = (sec) => {
    const st = sec?.settings || {};
    const { bg, fg } = resolveBuilderSectionColors(st, catalogLayoutEffective);
    const visible = st.visible !== false;
    if (!visible) return null;
    const secVariant = String(st.section_variant || "").toLowerCase();
    if (secVariant === "newsletter" || secVariant === "dual_promo" || secVariant === "blog" || secVariant === "flash_sale") {
      return null;
    }
    const frame = sectionFrameStyle(st, catalogLayoutEffective);
    const ha = sectionHeadingAlign(st);
    const wrapBase = { ...builderSectionWrapStyle(frame), ...sectionBlockMargins(st) };
    const outerStyle = canEdit ? { ...wrapBase, position: "relative" } : wrapBase;
    // Storefront: a section that lists products which haven't arrived yet gets a same-size skeleton
    // instead of popping in (and pushing everything below it down) when the products load.
    // products_carousel already renders its own skeleton.
    if (!canEdit && !sectionProductsSettled && sec.type !== "products_carousel") {
      const refIds = [
        ...(Array.isArray(st.product_ids) ? st.product_ids : []),
        ...(Array.isArray(st.slides) ? st.slides.map((sl) => sl?.product_id) : []),
      ].filter((id) => id != null && id !== "");
      if (refIds.some((id) => !catalogProductsById.has(Number(id)) && !catalogProductsById.has(id))) {
        const skCount = Math.max(2, Math.min(4, Number(st.columns) || refIds.length || 4));
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle} aria-busy="true">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${skCount}, minmax(0, 1fr))`,
                gap: 14,
                padding: frame?.pad || 16,
                boxSizing: "border-box",
              }}
            >
              {Array.from({ length: skCount }).map((_, i) => (
                <div key={i} className="catalog-pc-skeleton" aria-hidden>
                  <div className="catalog-pc-skeleton__img" />
                  <div className="catalog-pc-skeleton__line" />
                  <div className="catalog-pc-skeleton__line catalog-pc-skeleton__line--short" />
                </div>
              ))}
            </div>
          </div>
        );
      }
    }
    const sectionEditBtn = canEdit ? (
      <button
        type="button"
        className="catalog-studio-edit-chip"
        aria-label="تعديل السكشن في التخصيص"
        onClick={(e) => {
          e.stopPropagation();
          openStudioBuilderSectionForm(sec.id);
        }}
      >
        <Pencil size={15} strokeWidth={2.25} />
      </button>
    ) : null;
    const openSecSidebar = () => openStudioBuilderSectionForm(sec.id);
    const patchSec = (patch) => patchBuilderSectionById(sec.id, patch);
    const inlineText = (field, textStyle = {}, { multiline = false, placeholder = "" } = {}) => (
      <EditableInline
        value={st[field] || ""}
        placeholder={placeholder}
        onChange={(v) => patchSec({ [field]: v })}
        onOpenSidebar={openSecSidebar}
        textStyle={textStyle}
        multiline={multiline}
      />
    );
    const patchSecFaq = (itemIndex, patch) => {
      const cur = Array.isArray(st.faq_items) ? [...st.faq_items] : [];
      while (cur.length <= itemIndex) cur.push({ q: "", a: "" });
      cur[itemIndex] = { ...cur[itemIndex], ...patch };
      patchSec({ faq_items: cur });
    };
    const titlePx = sectionTitleFontPx(st);
    const subPx = sectionSubtitleFontPx(st);
    const img0 =
      Array.isArray(st.images) && st.images.length > 0
        ? st.images[0]?.url || st.images[0]
        : "";

    if (sec.type === "hero") {
      const heroUrls = (Array.isArray(st.images) ? st.images : [])
        .map((x) => (typeof x === "string" ? x : x?.url))
        .filter(Boolean);
      const carouselHero = Boolean(st.hero_as_carousel) && heroUrls.length > 0;
      const hk = (st.section_height || "md").toString();
      const heroSlideH = hk === "sm" ? 200 : hk === "lg" ? 340 : 280;
      const frameRadHero = Number(frame?.radius ?? 0);
      const innerRH = frameRadHero > 0 ? Math.max(0, frameRadHero - 4) : 0;
      const heroValignRaw = (st.hero_copy_valign || "start").toString();
      const heroContentJustify =
        heroValignRaw === "center" ? "center" : heroValignRaw === "end" ? "flex-end" : "flex-start";

      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
          {sectionEditBtn}
          <section
            className="catalog-skip-global-palette"
            style={{
              borderRadius: frame.radius,
              overflow: "hidden",
              border: frame.border,
              background: carouselHero
                ? bg
                : img0
                  ? `linear-gradient(0deg, ${bg}cc, ${bg}cc), url(${img0}) center/cover`
                  : bg,
              color: fg,
              minHeight: sectionContentMinHeight(st),
              direction: settings.layoutDirection || "rtl",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="catalog-builder-hero-body"
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: heroContentJustify,
                padding: frame.pad,
                boxSizing: "border-box",
                minHeight: 0,
              }}
            >
            {(() => {
              const t0 = String(st.title || "").trim();
              const t1 = String(settings.headerTitle || "").trim();
              const t2 = String(settings.storeName || "").trim();
              const titleShown = t0 || t1 || t2;
              return titleShown ? (
                <div style={{ fontSize: titlePx, fontWeight: 950, lineHeight: 1.1, textAlign: ha }}>{t0 || t1 || t2}</div>
              ) : null;
            })()}
            {(() => {
              const s0 = String(st.subtitle || "").trim();
              const s1 = String(settings.subheaderText || "").trim();
              const subShown = s0 || s1;
              return subShown ? (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: subPx,
                    opacity: 0.92,
                    maxWidth: ha === "center" ? "100%" : 820,
                    marginLeft: ha === "center" ? "auto" : undefined,
                    marginRight: ha === "center" ? "auto" : undefined,
                    lineHeight: 1.7,
                    textAlign: ha,
                  }}
                >
                  {s0 || s1}
                </div>
              ) : null;
            })()}
            {carouselHero ? (
              <div style={{ marginTop: 18 }}>
                <BuilderHeroCarousel
                  urls={heroUrls}
                  slideH={heroSlideH}
                  innerR={innerRH}
                  rtl={(settings.layoutDirection || "rtl") === "rtl"}
                  frameBorder={frame.border}
                  dotColor={catalogHeroPrimary}
                />
              </div>
            ) : null}
            <div style={{ marginTop: 18, textAlign: ha }}>
              <button
                type="button"
                className="catalog-btn catalog-hero-cta-btn"
                onClick={scrollToProducts}
                style={{
                  background: fg,
                  color: bg,
                  borderRadius: 14,
                  fontWeight: 900,
                  padding: "10px 16px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                اكتشف المجموعة
              </button>
            </div>
            </div>
          </section>
        </div>
      );
    }

    if (sec.type === "text") {
      const textVariant = String(st.section_variant || "").toLowerCase();
      const faqItems = Array.isArray(st.faq_items)
        ? st.faq_items
        : String(st.body || "")
            .split("\n\n")
            .filter(Boolean)
            .map((chunk) => {
              const parts = chunk.split("\n");
              return { q: parts[0] || "", a: parts.slice(1).join("\n") || "" };
            });

      if (textVariant === "flash_sale") {
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section
              className="catalog-section--flash-sale catalog-builder-sec-surface"
              data-section-height={(st.section_height || "md").toString()}
              style={builderSectionSurfaceStyle(st, frame, bg, fg)}
            >
              <div className="catalog-flash-sale-bar" style={{ textAlign: ha }}>
                <div>
                  {(String(st.title || "").trim() || canEdit) ? (
                    <div className="catalog-flash-sale-bar__title">
                      {inlineText("title", { fontWeight: 950 }, { placeholder: "عروض فلاش" })}
                    </div>
                  ) : null}
                  {(String(st.subtitle || "").trim() || canEdit) ? (
                    <div className="catalog-flash-sale-bar__sub">
                      {inlineText("subtitle", {}, { placeholder: "وصف قصير" })}
                    </div>
                  ) : null}
                </div>
                <CatalogProCountdown
                  endAt={st.countdown_end}
                  compact
                  {...resolveCountdownTheme(st, catalogThemePrimary, { onDark: false })}
                />
              </div>
            </section>
          </div>
        );
      }

      if (textVariant === "blog") {
        const posts = Array.isArray(st.blog_posts) ? st.blog_posts : [];
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section className="catalog-section--blog catalog-builder-sec-surface" data-section-height={(st.section_height || "md").toString()} style={builderSectionSurfaceStyle(st, frame, bg, fg)}>
              {String(st.title || "").trim() ? <div style={{ fontSize: titlePx, fontWeight: 950, textAlign: ha, marginBottom: 8 }}>{String(st.title).trim()}</div> : null}
              {st.subtitle ? <div style={{ marginBottom: 20, fontSize: subPx, opacity: 0.75, textAlign: ha }}>{st.subtitle}</div> : null}
              <div className="catalog-blog-grid">
                {posts.map((post, pi) => {
                  const img = String(post.image_url || post.image || "").trim();
                  const href = String(post.link_url || "#").trim() || "#";
                  return (
                    <a key={pi} href={href} className="catalog-blog-card">
                      {img ? <img src={img} alt="" className="catalog-blog-card__img" width={800} height={600} loading="lazy" decoding="async" /> : <div className="catalog-blog-card__img catalog-blog-card__img--empty" />}
                      <div className="catalog-blog-card__meta">{String(post.subtitle || "").trim()}</div>
                      <div className="catalog-blog-card__title">{String(post.title || "").trim()}</div>
                    </a>
                  );
                })}
              </div>
            </section>
          </div>
        );
      }

      if (textVariant === "faq") {
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section
              className="catalog-section--faq catalog-builder-sec-surface"
              data-section-height={(st.section_height || "md").toString()}
              style={builderSectionSurfaceStyle(st, frame, bg, fg)}
            >
              {(String(st.title || "").trim() || canEdit) ? (
                <div style={{ fontSize: titlePx, fontWeight: 950, textAlign: ha, marginBottom: 8 }}>
                  {inlineText("title", { fontWeight: 950 }, { placeholder: "أسئلة شائعة" })}
                </div>
              ) : null}
              {(String(st.subtitle || "").trim() || canEdit) ? (
                <div style={{ marginTop: 8, fontSize: subPx, opacity: 0.75, textAlign: ha, marginBottom: 20 }}>
                  {inlineText("subtitle", {}, { placeholder: "وصف القسم" })}
                </div>
              ) : null}
              <div className="catalog-faq-list">
                {faqItems.map((item, fi) => (
                  <div key={fi} className="catalog-faq-item">
                    <div className="catalog-faq-item__q">
                      <EditableInline
                        value={String(item.q || item.question || "")}
                        placeholder="السؤال"
                        onChange={(v) => patchSecFaq(fi, { q: v })}
                        onOpenSidebar={openSecSidebar}
                      />
                    </div>
                    <div className="catalog-faq-item__a">
                      <EditableInline
                        value={String(item.a || item.answer || "")}
                        placeholder="الجواب"
                        multiline
                        onChange={(v) => patchSecFaq(fi, { a: v })}
                        onOpenSidebar={openSecSidebar}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        );
      }

      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
          {sectionEditBtn}
          <section
            style={{
              borderRadius: frame.radius,
              overflow: "hidden",
              border: frame.border,
              background: img0 ? `linear-gradient(0deg, ${bg}cc, ${bg}cc), url(${img0}) center/cover` : bg,
              color: fg,
              padding: 0,
              direction: settings.layoutDirection || "rtl",
              position: "relative",
            }}
          >
            <div
              style={{
                padding: frame.pad,
                boxSizing: "border-box",
                minHeight: sectionContentMinHeight(st),
              }}
            >
          {String(st.title || "").trim() ? (
            <div style={{ fontSize: titlePx, fontWeight: 950, textAlign: ha, lineHeight: 1.2 }}>{String(st.title).trim()}</div>
          ) : null}
          {st.subtitle ? (
            <div style={{ marginTop: 8, fontSize: subPx, opacity: 0.9, lineHeight: 1.7, textAlign: ha }}>
              {st.subtitle}
            </div>
          ) : null}
          {st.body ? (
            <div
              style={{
                marginTop: 12,
                opacity: 0.95,
                lineHeight: 1.9,
                whiteSpace: "pre-wrap",
                textAlign: ha,
              }}
            >
              {st.body}
            </div>
          ) : null}
            </div>
          </section>
        </div>
      );
    }

    if (sec.type === "video") {
      const videoUrl = String(st.video_url || "").trim();
      const posterUrl = String(st.poster_url || "").trim();
      const videoFit = st.video_fit === "contain" ? "contain" : "cover";
      const videoHeight = sectionHeightScale(st).banner;
      const isCompactVideo =
        storefrontIsCompact ||
        (canEdit && (studioPreviewFrame === "mobile" || studioPreviewFrame === "tablet"));
      const videoFitEffective = isCompactVideo ? "contain" : videoFit;
      const rawBg = String(bg || "").trim().toLowerCase();
      // الخلفية الداكنة القديمة كانت تملأ السكشن حول الفيديو — نخفيها
      const surfaceBg =
        !rawBg ||
        rawBg === "#0f172a" ||
        rawBg === "rgb(15, 23, 42)" ||
        rawBg === "transparent"
          ? "transparent"
          : bg;
      const surfaceFg =
        surfaceBg === "transparent"
          ? fg && String(fg).toLowerCase() !== "#ffffff"
            ? fg
            : "#0f172a"
          : fg;
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
          {sectionEditBtn}
          <section
            className={`catalog-video-section catalog-builder-sec-surface${isCompactVideo ? " catalog-video-section--compact" : ""}`}
            data-section-height={(st.section_height || "md").toString()}
            style={{
              ...builderSectionSurfaceStyle(st, frame, surfaceBg, surfaceFg),
              minHeight: 0,
              background: surfaceBg,
              border: frame.border === "none" || surfaceBg === "transparent" ? "none" : frame.border,
              boxShadow: "none",
            }}
          >
            {(String(st.title || "").trim() || String(st.subtitle || "").trim() || canEdit) ? (
              <div className="catalog-pro-section-head" style={proSectionHeadWrapStyle(ha)}>
                {(String(st.title || "").trim() || canEdit) ? (
                  <h2 style={proSectionTitleStyle(st)}>{canEdit ? inlineText("title", proSectionTitleStyle(st), { placeholder: "عنوان الفيديو" }) : String(st.title).trim()}</h2>
                ) : null}
                {(String(st.subtitle || "").trim() || canEdit) ? (
                  <p style={proSectionSubtitleStyle(st)}>{canEdit ? inlineText("subtitle", proSectionSubtitleStyle(st), { placeholder: "وصف الفيديو" }) : String(st.subtitle).trim()}</p>
                ) : null}
              </div>
            ) : null}
            <CatalogSectionVideoPlayer
              videoUrl={videoUrl}
              posterUrl={posterUrl}
              videoFit={videoFitEffective}
              autoplay={Boolean(st.video_autoplay)}
              loop={Boolean(st.video_loop)}
              muted={Boolean(st.video_muted)}
              height={videoHeight}
              borderRadius={frame.radius}
              compact={isCompactVideo}
            />
          </section>
        </div>
      );
    }

    if (sec.type === "image_banner") {
      const bannerScale = sectionHeightScale(st);
      const bannerVariant = String(st.section_variant || "").toLowerCase();
      const bannerImg = normalizeImageUrlFromSettings(st) || img0;
      const linkUrlRaw = String(st.link_url || "").trim();
      const linkUrl = resolveBuilderCtaUrl(linkUrlRaw);
      const fit = (st.image_fit || "cover").toString() === "contain" ? "contain" : "cover";
      const altText =
        String(st.alt_text || "").trim() ||
        String(settings.storeName || "").trim() ||
        "Banner";
      const media = bannerImg ? (
        <div
          style={{
            position: "relative",
            height: bannerScale.banner,
            minHeight: bannerScale.banner,
            maxHeight: bannerScale.banner,
            overflow: "hidden",
            background: bg,
          }}
        >
          <img
            src={bannerImg}
            alt={altText}
            width={1600}
            height={600}
            loading="lazy"
            decoding="async"
            className="catalog-banner-section-img catalog-banner-section-img--bg"
            data-section-height={(st.section_height || "md").toString()}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </div>
      ) : (
        <div
          className="catalog-banner-section-empty"
          data-section-height={(st.section_height || "md").toString()}
          style={{
            padding: "min(10vw, 56px) 16px",
            textAlign: "center",
            color: "#94a3b8",
            fontWeight: 850,
            fontSize: subPx,
            background: bg,
            minHeight: bannerScale.banner,
          }}
        >
          {canEdit ? "ارفع صورة للبانر من لوحة التخصيص" : null}
        </div>
      );
      const promoTitle = String(st.title || "").trim();
      const promoSub = String(st.subtitle || "").trim();
      const promoCta = String(st.promo_cta_label || "تسوّق العرض").trim();
      const promoUrl = resolveBuilderCtaUrl(st.promo_cta_url || linkUrl);
      const promoCtaFg = String(st.promo_cta_fg || fg || "#111827").trim();
      const promoCtaBg = String(st.promo_cta_bg || "#ffffff").trim();
      const promoCtaStyle = {
        color: promoCtaFg,
        background: promoCtaBg,
        borderRadius: 10,
        ["--promo-cta-bg"]: promoCtaBg,
        ["--promo-cta-fg"]: promoCtaFg,
      };

      if (bannerVariant === "dual_promo") {
        const slots = Array.isArray(st.promo_slots) ? st.promo_slots : [];
        const safeSlots = (slots.length ? slots : [{ title: promoTitle, subtitle: promoSub, cta: promoCta, url: promoUrl, image: bannerImg }, { title: "", subtitle: "", cta: "", url: "#products", image: "" }]).slice(0, 2);
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section className="catalog-section--dual-promo catalog-builder-sec-surface" data-section-height={(st.section_height || "md").toString()} style={builderSectionSurfaceStyle(st, frame, bg, fg)}>
              <div className="catalog-dual-promo-grid">
                {safeSlots.map((slot, si) => {
                  const simg = String(slot.image || slot.image_url || "").trim();
                  return (
                    <div key={si} className="catalog-dual-promo-card">
                      {simg ? <img src={simg} alt="" className="catalog-dual-promo-card__bg" width={800} height={450} loading="lazy" decoding="async" /> : <div className="catalog-dual-promo-card__bg catalog-dual-promo-card__bg--empty" />}
                      <div className="catalog-dual-promo-card__overlay">
                        {slot.title ? <h3>{String(slot.title)}</h3> : null}
                        {slot.subtitle ? <p>{String(slot.subtitle)}</p> : null}
                        {slot.cta ? (
                          <a className="catalog-banner-promo-cta" href={resolveBuilderCtaUrl(slot.url)}>
                            {String(slot.cta)}
                          </a>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        );
      }

      if (bannerVariant === "countdown") {
        const bannerH = sectionHeightScale(st).banner;
        const overlayColor = String(st.promo_overlay_color || "#0f172a").trim();
        const overlayOpacity = Number(st.promo_overlay_opacity);
        const overlayAlpha = Number.isFinite(overlayOpacity) ? Math.min(1, Math.max(0, overlayOpacity)) : 0.55;
        const cdTheme = resolveCountdownTheme(st, "#ffffff", { onDark: true });
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section
              className="catalog-section--countdown-banner catalog-builder-sec-surface"
              data-section-height={(st.section_height || "md").toString()}
              style={{
                borderRadius: frame.radius,
                border: frame.border,
                background: bg,
                color: fg,
                overflow: "hidden",
                padding: 0,
                direction: settings.layoutDirection || "rtl",
              }}
            >
              <div
                className="catalog-countdown-banner-wrap"
                data-section-height={(st.section_height || "md").toString()}
                style={{ height: bannerH, minHeight: bannerH, maxHeight: bannerH, position: "relative", overflow: "hidden" }}
              >
                {bannerImg ? (
                  <img
                    src={bannerImg}
                    alt={altText}
                    className="catalog-banner-section-img catalog-banner-section-img--bg catalog-countdown-banner-wrap__img"
                    width={1600}
                    height={600}
                    loading="lazy"
                    decoding="async"
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                ) : (
                  <div className="catalog-countdown-banner-wrap__placeholder" style={{ position: "absolute", inset: 0, background: bg }} />
                )}
                <div
                  className="catalog-countdown-banner-overlay"
                  style={{ background: hexToRgba(overlayColor, overlayAlpha), color: fg }}
                >
                  <div className="catalog-countdown-banner-overlay__inner" style={{ textAlign: ha, padding: frame.pad, boxSizing: "border-box" }}>
                    {(promoTitle || canEdit) ? (
                      <h2 style={proSectionTitleStyle(st)}>{canEdit ? inlineText("title", proSectionTitleStyle(st), { placeholder: "عنوان العرض" }) : promoTitle}</h2>
                    ) : null}
                    {(promoSub || canEdit) ? (
                      <p style={proSectionSubtitleStyle(st)}>{canEdit ? inlineText("subtitle", proSectionSubtitleStyle(st), { placeholder: "وصف العرض" }) : promoSub}</p>
                    ) : null}
                    <CatalogProCountdown endAt={st.countdown_end} compact={false} {...cdTheme} />
                    {(promoCta || canEdit) ? (
                      <a className="catalog-banner-promo-cta" href={promoUrl} style={promoCtaStyle}>
                        {canEdit ? inlineText("promo_cta_label", { color: promoCtaFg }, { placeholder: "تسوّق العرض" }) : promoCta}
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>
          </div>
        );
      }

      const promoBannerH = sectionHeightScale(st).banner;
      const inner =
        bannerVariant === "promo" && bannerImg ? (
          <div className="catalog-banner-promo-wrap" style={{ height: promoBannerH, minHeight: promoBannerH, maxHeight: promoBannerH, position: "relative", overflow: "hidden" }}>
            <img
              src={bannerImg}
              alt={altText}
              width={1600}
              height={600}
              loading="lazy"
              decoding="async"
              className="catalog-banner-section-img catalog-banner-section-img--bg"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
            />
            <div className="catalog-banner-promo-overlay" style={{ color: fg }}>
              <div className="catalog-banner-promo-overlay__inner" style={{ textAlign: ha, padding: frame.pad, boxSizing: "border-box" }}>
                {(promoTitle || canEdit) ? (
                  <h2 style={{ ...proSectionTitleStyle(st), color: fg }}>{canEdit ? inlineText("title", { ...proSectionTitleStyle(st), color: fg }, { placeholder: "عنوان العرض" }) : promoTitle}</h2>
                ) : null}
                {(promoSub || canEdit) ? (
                  <p style={{ ...proSectionSubtitleStyle(st), color: fg }}>{canEdit ? inlineText("subtitle", { ...proSectionSubtitleStyle(st), color: fg }, { placeholder: "وصف العرض" }) : promoSub}</p>
                ) : null}
                {(promoCta || canEdit) ? (
                  <a className="catalog-banner-promo-cta" href={promoUrl} style={promoCtaStyle}>
                    {canEdit ? inlineText("promo_cta_label", { color: promoCtaFg }, { placeholder: "تسوّق العرض" }) : promoCta}
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        ) : linkUrl ? (
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ display: "block", lineHeight: 0 }}>
            {media}
          </a>
        ) : (
          media
        );
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
          {sectionEditBtn}
          <section
            className={bannerVariant === "promo" ? "catalog-section--promo" : undefined}
            style={{
              borderRadius: frame.radius,
              overflow: "hidden",
              border: frame.border,
              background: bg,
              color: fg,
              padding: 0,
              direction: settings.layoutDirection || "rtl",
              position: "relative",
            }}
          >
            {inner}
          </section>
        </div>
      );
    }

    if (sec.type === "text_image_split") {
      const splitScale = sectionHeightScale(st);
      const splitFrame = sectionFrameStyle(st, catalogLayoutEffective);
      const splitWrapBase = { ...builderSectionWrapStyle(splitFrame), ...sectionBlockMargins(st) };
      const splitOuterStyle = canEdit ? { ...splitWrapBase, position: "relative" } : splitWrapBase;
      const splitImg = normalizeImageUrlFromSettings(st) || "";
      const splitImgPosition = heroBackgroundPositionFromXY(st.image_position_x, st.image_position_y);
      const imageOnLeft = (st.image_side || "left") === "left";
      const bullets = String(st.bullet_list || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const waDigitsForSplit = normalizeWhatsAppDigits(settings.whatsappNumber);
      const storeWaHref = waDigitsForSplit ? `https://wa.me/${waDigitsForSplit}` : "";
      const socialRows = [
        {
          key: "instagram",
          url: String(st.social_instagram || "").trim(),
          Icon: Instagram,
          label: "Instagram",
        },
        {
          key: "facebook",
          url: String(st.social_facebook || "").trim(),
          Icon: Facebook,
          label: "Facebook",
        },
        { key: "twitter", url: String(st.social_twitter || "").trim(), Icon: Twitter, label: "X" },
        {
          key: "whatsapp",
          url: String(st.social_whatsapp || "").trim() || storeWaHref,
          Icon: MessageCircle,
          label: "WhatsApp",
        },
        { key: "tiktok", url: String(st.social_tiktok || "").trim(), Icon: null, label: "TikTok" },
      ].filter((r) => r.url);
      const cta1Label = String(st.cta_label_1 || "").trim();
      const cta1Url = resolveExternalUrl(st.cta_url_1) || resolveBuilderCtaUrl(st.cta_url_1);
      const cta2Label = String(st.cta_label_2 || "").trim();
      const cta2Url = resolveExternalUrl(st.cta_url_2) || resolveBuilderCtaUrl(st.cta_url_2);
      const splitVariant = String(st.section_variant || "").toLowerCase();
      if (splitVariant === "about_cards") {
        const aboutCards = Array.isArray(st.about_cards) ? st.about_cards : [];
        const heroImg = splitImg;
        const imageOnLeftAbout = (st.image_side || "left") !== "right";
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={splitOuterStyle}>
            {sectionEditBtn}
            <section
              className="catalog-section--about-cards catalog-builder-sec-surface"
              data-section-height={(st.section_height || "md").toString()}
              style={{
                ...builderSectionSurfaceStyle(st, splitFrame, bg, fg),
                borderRadius: splitFrame.radius,
                overflow: "hidden",
                border: splitFrame.border,
              }}
            >
              {String(st.title || "").trim() ? (
                <div className="catalog-pro-section-head" style={proSectionHeadWrapStyle(ha, 20)}>
                  <h2 style={proSectionTitleStyle(st)}>{String(st.title).trim()}</h2>
                  {String(st.subtitle || "").trim() ? <p style={proSectionSubtitleStyle(st)}>{st.subtitle}</p> : null}
                </div>
              ) : null}
              <div
                className="catalog-about-cards-grid"
                style={{ flexDirection: imageOnLeftAbout ? "row" : "row-reverse" }}
              >
                <div className="catalog-about-cards-grid__media">
                  {heroImg ? <img src={heroImg} alt={String(st.title || "").trim()} width={800} height={800} loading="lazy" decoding="async" /> : <div className="catalog-about-cards-grid__media-empty" />}
                </div>
                <div className="catalog-about-cards-grid__stack">
                  {aboutCards.map((card, ci) => (
                    <div key={ci} className="catalog-about-card">
                      <h3>{String(card.title || "").trim()}</h3>
                      <p>{String(card.body || "").trim()}</p>
                      {card.cta ? (
                        <a className="catalog-about-card__cta" href={resolveExternalUrl(card.url) || resolveBuilderCtaUrl(card.url)}>{String(card.cta)}</a>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        );
      }
      const textCol = (
        <div className="catalog-split-content" style={{ minWidth: 0, textAlign: ha }}>
          {String(st.title || "").trim() ? (
            <div className="catalog-split-title" style={{ fontSize: titlePx }}>{String(st.title).trim()}</div>
          ) : null}
          {st.subtitle ? (
            <div className="catalog-split-subtitle" style={{ fontSize: subPx }}>{st.subtitle}</div>
          ) : null}
          {splitVariant === "brand_story" && st.body ? (
            <div className="catalog-split-story-body">
              {String(st.body)
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean)
                .map((line, li) => (
                  <div key={li} className="catalog-split-story-line">
                    {line.replace(/^[•\-]\s*/, "")}
                  </div>
                ))}
            </div>
          ) : splitVariant === "newsletter" ? (
            <>
              {st.body ? <div className="catalog-split-body">{st.body}</div> : null}
              <div className="catalog-newsletter-form">
                <input
                  type="email"
                  className="catalog-newsletter-input"
                  placeholder={String(st.newsletter_placeholder || "بريدك الإلكتروني")}
                  readOnly
                  aria-label="البريد الإلكتروني"
                />
                <button type="button" className="catalog-newsletter-btn">
                  {String(st.newsletter_button || "اشترك")}
                </button>
              </div>
            </>
          ) : st.body ? (
            <div className="catalog-split-body">{st.body}</div>
          ) : null}
          {bullets.length ? (
            <ul
              className="catalog-split-bullets"
              style={{
                margin: "14px 0 0",
                paddingInlineStart: 22,
                textAlign: ha,
              }}
            >
              {bullets.map((b, bi) => (
                <li key={bi}>{b}</li>
              ))}
            </ul>
          ) : null}
          {socialRows.length ? (
            <div
              className="catalog-split-social"
              style={{
                marginTop: 16,
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                justifyContent: ha === "center" ? "center" : ha === "end" ? "flex-end" : "flex-start",
              }}
            >
              {socialRows.map((r) => (
                <a
                  key={r.key}
                  href={resolveExternalUrl(r.url) || r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={r.label}
                  className="catalog-split-social-link"
                  style={{
                    "--catalog-split-accent": catalogThemePrimary,
                    "--catalog-split-secondary": (catalogThemeSecondary || "#f3f4f6"),
                  }}
                >
                  {r.Icon ? <r.Icon size={18} strokeWidth={2.25} aria-hidden /> : <span style={{ fontSize: 11, fontWeight: 900 }}>TT</span>}
                </a>
              ))}
            </div>
          ) : null}
          {cta1Label ? (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                justifyContent: ha === "center" ? "center" : ha === "end" ? "flex-end" : "flex-start",
              }}
            >
              <a
                href={cta1Url}
                target="_blank"
                rel="noopener noreferrer"
                className="catalog-split-cta catalog-split-cta--primary"
                style={{
                  "--catalog-split-accent": catalogThemePrimary,
                  "--catalog-split-secondary": (catalogThemeSecondary || "#f3f4f6"),
                  "--catalog-split-accent-contrast": (catalogThemeSecondary || "#fff"),
                }}
              >
                {cta1Label}
              </a>
              {cta2Label ? (
                <a
                  href={cta2Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="catalog-split-cta catalog-split-cta--outline"
                  style={{
                    "--catalog-split-accent": catalogThemePrimary,
                    "--catalog-split-secondary": (catalogThemeSecondary || "#f3f4f6"),
                  }}
                >
                  {cta2Label}
                </a>
              ) : null}
            </div>
          ) : cta2Label ? (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: ha === "center" ? "center" : ha === "end" ? "flex-end" : "flex-start",
              }}
            >
              <a
                href={cta2Url}
                target="_blank"
                rel="noopener noreferrer"
                className="catalog-split-cta catalog-split-cta--outline"
                style={{
                  "--catalog-split-accent": catalogThemePrimary,
                  "--catalog-split-secondary": (catalogThemeSecondary || "#f3f4f6"),
                }}
              >
                {cta2Label}
              </a>
            </div>
          ) : null}
        </div>
      );
      const imageCol = (
        <div
          className={`catalog-split-media${splitImg ? " catalog-split-media--bg" : ""}`}
          style={
            splitImg
              ? {
                  "--catalog-split-media-bg": `url(${JSON.stringify(splitImg)})`,
                  backgroundPosition: splitImgPosition,
                }
              : undefined
          }
          role={splitImg ? "img" : undefined}
          aria-label={splitImg ? String(st.image_alt || st.title || "صورة السكشن") : undefined}
        >
          {!splitImg ? <span className="catalog-split-media-empty">صورة السكشن</span> : null}
        </div>
      );
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={splitOuterStyle}>
          {sectionEditBtn}
          <section
            className={`catalog-split-section${splitVariant === "brand_story" ? " catalog-section--brand-story" : ""}${splitVariant === "newsletter" ? " catalog-section--newsletter" : ""}`}
            data-section-height={(st.section_height || "md").toString()}
            style={{
              overflow: "hidden",
              borderRadius: splitFrame.radius,
              "--builder-section-radius": `${splitFrame.radius}px`,
              "--catalog-split-section-h": `${splitScale.splitH}px`,
              border: splitFrame.border,
              background: bg,
              color: fg,
              minHeight: splitScale.splitH,
              direction: settings.layoutDirection || "rtl",
            }}
          >
            <div
              className="catalog-split-grid"
              style={{
                alignItems: "stretch",
                padding: splitVariant === "brand_story" ? splitFrame.pad : 0,
                boxSizing: "border-box",
              }}
            >
              {imageOnLeft ? (
                <>
                  {textCol}
                  {imageCol}
                </>
              ) : (
                <>
                  {imageCol}
                  {textCol}
                </>
              )}
            </div>
          </section>
        </div>
      );
    }

    if (sec.type === "features_grid") {
      const count = Math.max(2, Math.min(4, Number(st.cards_count || 3)));
      const itemsRaw = Array.isArray(st.items) ? st.items : [];
      const items = itemsRaw.slice(0, count).map((it) => ({
        ...(it || {}),
        // backward compat
        subtitle: it?.subtitle ?? "",
        details: it?.details ?? it?.text ?? "",
        image_url: it?.image_url ?? "",
        image_position_x: it?.image_position_x ?? 50,
        image_position_y: it?.image_position_y ?? 50,
        image_mode: it?.image_mode ?? "",
        cover_grad_from: it?.cover_grad_from ?? "",
        cover_grad_to: it?.cover_grad_to ?? "",
        cover_grad_opacity: it?.cover_grad_opacity ?? "",
        text_size: it?.text_size ?? "",
        cta_label: String(it?.cta_label ?? it?.ctaLabel ?? it?.button_label ?? it?.link_label ?? "").trim(),
        cta_url: String(it?.cta_url ?? it?.ctaUrl ?? it?.href ?? it?.link_url ?? "").trim(),
        btn_variant: it?.btn_variant ?? "",
        btn_bg: it?.btn_bg ?? "",
        btn_fg: it?.btn_fg ?? "",
        btn_border: it?.btn_border ?? "",
        btn_radius: it?.btn_radius ?? "",
        card_bg_color: it?.card_bg_color ?? "",
        card_bg_opacity: it?.card_bg_opacity ?? "",
        card_text: it?.card_text ?? "",
        card_border_color: it?.card_border_color ?? "",
        card_border_opacity: it?.card_border_opacity ?? "",
        card_shadow: it?.card_shadow ?? "",
      }));
      while (items.length < count) {
        items.push({ title: "", subtitle: "", details: "", image_url: "", image_position_x: 50, image_position_y: 50, image_mode: "", cover_grad_from: "", cover_grad_to: "", cover_grad_opacity: "", text_size: "", cta_label: "", cta_url: "", btn_variant: "", btn_bg: "", btn_fg: "", btn_border: "", btn_radius: "", card_bg_color: "", card_bg_opacity: "", card_text: "", card_border_color: "", card_border_opacity: "", card_shadow: "" });
      }

      const defaultBgColor = String(st.card_bg_color || "#ffffff").trim();
      const defaultBgOpacity = Math.min(1, Math.max(0, Number(st.card_bg_opacity ?? 0.6)));
      const defaultCardBg = applyOpacityToColor(defaultBgColor, defaultBgOpacity);
      const defaultCardText = String(st.card_text || "#0f172a").trim();
      const defaultBorderColor = String(st.card_border_color || "#0f172a").trim();
      const defaultBorderOpacity = Math.min(1, Math.max(0, Number(st.card_border_opacity ?? 0.12)));
      const defaultCardBorder = applyOpacityToColor(defaultBorderColor, defaultBorderOpacity);
      const defaultShadowKey = String(st.card_shadow || "none").trim();
      const shadowFromKey = (shadowKey) =>
        shadowKey === "lg"
          ? "0 18px 40px rgba(15,23,42,0.16)"
          : shadowKey === "md"
            ? "0 12px 28px rgba(15,23,42,0.14)"
            : shadowKey === "soft"
              ? "0 8px 18px rgba(15,23,42,0.10)"
              : "none";
      const defaultShadow = shadowFromKey(defaultShadowKey);
      const gridVariant = String(st.section_variant || "").toLowerCase();
      const cardRadiusKey = String(st.card_radius || "md").trim();
      const cardRadius =
        cardRadiusKey === "none" ? 0 : cardRadiusKey === "lg" ? 18 : cardRadiusKey === "sm" ? 12 : 14;
      const gridVariantClass =
        gridVariant === "trust"
          ? " catalog-section--trust"
          : gridVariant === "categories"
            ? " catalog-section--categories"
            : gridVariant === "stats"
              ? " catalog-section--stats-grid"
              : "";
      const cardHeightKey = String(st.card_height || "md").trim();
      const cardMinH = cardHeightKey === "lg" ? 300 : cardHeightKey === "sm" ? 200 : 260;
      const cardCoverMinH = cardHeightKey === "lg" ? 400 : cardHeightKey === "sm" ? 240 : 320;
      const cardMaxW = undefined;
      const imageModeDefault = String(st.card_image_mode || "circle").trim();
      const coverGradFromDefault = String(st.card_cover_grad_from || "#0f172a").trim();
      const coverGradToDefault = String(st.card_cover_grad_to || "#0f172a").trim();
      const coverGradOpacityDefault = Math.min(1, Math.max(0, Number(st.card_cover_grad_opacity ?? 0.45)));
      const titleSizeDefault = String(st.card_title_size || st.card_text_size || "md").trim();
      const subtitleSizeDefault = String(st.card_subtitle_size || st.card_text_size || "md").trim();
      const bodySizeDefault = String(st.card_text_size || "md").trim();
      const btnVariantDefault = String(st.card_btn_variant || "outline").trim();
      const btnBgDefault = String(st.card_btn_bg || "#0f172a").trim();
      const btnFgDefault = String(st.card_btn_fg || "#ffffff").trim();
      const btnBorderDefault = String(st.card_btn_border || "#0f172a").trim();
      const btnRadiusDefault = Math.max(0, Math.min(24, Number(st.card_btn_radius ?? 12)));

      if (gridVariant === "trust") {
        const patchTrustItem = (idx, patch) => {
          const cur = Array.isArray(st.items) ? [...st.items] : [];
          while (cur.length <= idx) cur.push({});
          cur[idx] = { ...(cur[idx] || {}), ...patch };
          patchSec({ items: cur });
        };
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section className="catalog-section--trust catalog-builder-sec-surface" data-section-height={(st.section_height || "md").toString()} style={builderSectionSurfaceStyle(st, frame, bg, fg)}>
              {(String(st.title || "").trim() || canEdit) ? (
                <div className="catalog-pro-section-head" style={proSectionHeadWrapStyle(ha)}>
                  <h2 style={proSectionTitleStyle(st)}>{canEdit ? inlineText("title", proSectionTitleStyle(st), { placeholder: "عنوان القسم" }) : String(st.title).trim()}</h2>
                  {(String(st.subtitle || "").trim() || canEdit) ? (
                    <p style={proSectionSubtitleStyle(st)}>{canEdit ? inlineText("subtitle", proSectionSubtitleStyle(st), { placeholder: "وصف" }) : st.subtitle}</p>
                  ) : null}
                </div>
              ) : null}
              <div className="catalog-trust-grid">
                {items.map((it, idx) => {
                  const iconKey = String(it.icon || ["truck", "shield", "delivery", "support"][idx] || "truck").toLowerCase();
                  const IconCmp = TRUST_SECTION_ICONS[iconKey] || Truck;
                  const imgUrl = String(it.image_url || "").trim();
                  const iconColor = String(st.icon_color || catalogThemePrimary || settings.primaryColor || "#111827").trim();
                  return (
                    <div key={idx} className="catalog-trust-item">
                      <span className="catalog-trust-item__icon" style={{ color: iconColor }} aria-hidden>
                        {imgUrl ? (
                          <img src={imgUrl} alt="" className="catalog-trust-item__custom-img" width={28} height={28} loading="lazy" decoding="async" />
                        ) : (
                          <IconCmp size={28} strokeWidth={1.75} color={iconColor} />
                        )}
                      </span>
                      <div className="catalog-trust-item__title" style={{ fontSize: sectionTitleFontPx(st) }}>
                        {canEdit ? (
                          <EditableInline value={it.title || ""} placeholder="عنوان" onChange={(v) => patchTrustItem(idx, { title: v })} onOpenSidebar={openSecSidebar} />
                        ) : (
                          String(it.title || "").trim()
                        )}
                      </div>
                      <div className="catalog-trust-item__details" style={{ fontSize: sectionSubtitleFontPx(st) }}>
                        {canEdit ? (
                          <EditableInline value={it.details || ""} placeholder="وصف" onChange={(v) => patchTrustItem(idx, { details: v })} onOpenSidebar={openSecSidebar} multiline />
                        ) : (
                          String(it.details || "").trim()
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        );
      }

      const dashCategoryItems =
        Array.isArray(st.category_items) && st.category_items.length > 0 ? st.category_items : null;

      if (gridVariant === "categories") {
        const catRows = dashCategoryItems || items;
        const isSquare = String(st.card_shape || "").toLowerCase() === "square";
        const catRtl = (settings.layoutDirection || "rtl") === "rtl";
        const heightKey = String(st.card_height || "md").toLowerCase();
        const cardMaxPx =
          CATEGORIES_CARD_MAX_WIDTH_PX[heightKey] || CATEGORIES_CARD_MAX_WIDTH_PX.md;
        const showCatLabelRaw = st.show_category_label;
        const showCatLabel =
          showCatLabelRaw === false || String(showCatLabelRaw).toLowerCase() === "false"
            ? false
            : showCatLabelRaw === true || String(showCatLabelRaw).toLowerCase() === "true"
              ? true
              : CATEGORIES_SECTION_SHOW_LABEL;
        /**
         * Desktop: one horizontal row; card width = 1/pageSize (stable on last page).
         * Mobile / tablet / studio mobile|tablet preview: 2×2 grid so cards stay readable.
         */
        const isStudioCompactFrame =
          canEdit && (studioPreviewFrame === "mobile" || studioPreviewFrame === "tablet");
        const isMobileCats = storefrontIsCompact || isStudioCompactFrame;
        const desktopPageSize = Math.max(2, Math.min(6, Number(st.cards_count) || 4));
        const mobilePageSize = Math.min(4, desktopPageSize, catRows.length || 1);
        const categoryPageSize = Math.max(
          1,
          Math.min(catRows.length || 1, isMobileCats ? Math.max(1, mobilePageSize) : desktopPageSize)
        );
        const categoryPageCount = Math.max(1, Math.ceil((catRows.length || 0) / categoryPageSize));
        const categoryPage = Math.min(
          Math.max(0, Number(categoryCarouselPages[sec.id] || 0)),
          categoryPageCount - 1
        );
        const visibleCategoryRows = catRows.slice(
          categoryPage * categoryPageSize,
          (categoryPage + 1) * categoryPageSize
        );
        const moveCategoryPage = (nextPage, e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          const target = Math.min(Math.max(0, nextPage), categoryPageCount - 1);
          setCategoryCarouselPages((prev) => ({
            ...prev,
            [sec.id]: target,
          }));
          // لا تدع التركيز يسبب سكرول عمودي للصفحة
          try {
            if (e?.currentTarget && typeof e.currentTarget.blur === "function") {
              e.currentTarget.blur();
            }
          } catch {}
        };
        const categorySwipeHandlers =
          categoryPageCount > 1
            ? {
                onTouchStart: (e) => {
                  const t = e.touches[0];
                  categorySwipeStartRef.current = { id: sec.id, x: t.clientX, y: t.clientY };
                },
                onTouchEnd: (e) => {
                  const start = categorySwipeStartRef.current;
                  categorySwipeStartRef.current = null;
                  if (!start || start.id !== sec.id) return;
                  const t = e.changedTouches[0];
                  const dx = t.clientX - start.x;
                  const dy = t.clientY - start.y;
                  if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
                  // RTL: the "next" page sits on the left, so a finger moving right reveals it.
                  const goNext = catRtl ? dx > 0 : dx < 0;
                  moveCategoryPage(categoryPage + (goNext ? 1 : -1));
                },
              }
            : {};
        const visibleCount = visibleCategoryRows.length || 1;
        /** Desktop: divisor = page size. Mobile 2-col: divisor = 2 (half width each). */
        const slotCount = isMobileCats ? 2 : Math.max(1, categoryPageSize);
        const catGapPx = isMobileCats ? 8 : 12;
        const catTrackMax = `calc((100% - ${(slotCount - 1) * catGapPx}px) / ${slotCount})`;
        const gridCols = isMobileCats ? (visibleCount === 1 ? 1 : 2) : visibleCount;
        const catCardAccent = readableAccentOnWhite(
          catalogThemePrimary,
          readableAccentOnWhite(catalogThemeSecondary, "#0f172a")
        );
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section className="catalog-section--categories catalog-builder-sec-surface" data-section-height={(st.section_height || "md").toString()} style={builderSectionSurfaceStyle(st, frame, bg, fg)}>
              {(String(st.title || "").trim() || canEdit) ? (
                <div className="catalog-pro-section-head" style={proSectionHeadWrapStyle(ha)}>
                  <h2 style={proSectionTitleStyle(st)}>{canEdit ? inlineText("title", proSectionTitleStyle(st), { placeholder: "تسوّق من أهم الفئات" }) : String(st.title).trim()}</h2>
                  {(String(st.subtitle || "").trim() || canEdit) ? (
                    <p style={proSectionSubtitleStyle(st)}>{canEdit ? inlineText("subtitle", proSectionSubtitleStyle(st), { placeholder: "وصف" }) : st.subtitle}</p>
                  ) : null}
                </div>
              ) : null}
              <div
                className={`catalog-categories-carousel${isMobileCats ? " catalog-categories-carousel--mobile" : ""}`}
                {...categorySwipeHandlers}
              >
                {categoryPageCount > 1 ? (
                  <>
                    <button
                      type="button"
                      className="catalog-categories-carousel__arrow catalog-categories-carousel__arrow--prev"
                      onClick={(e) => moveCategoryPage(categoryPage - 1, e)}
                      disabled={categoryPage === 0}
                      aria-label="Previous categories"
                    >
                      {catRtl ? <ChevronRight size={20} aria-hidden /> : <ChevronLeft size={20} aria-hidden />}
                    </button>
                    <button
                      type="button"
                      className="catalog-categories-carousel__arrow catalog-categories-carousel__arrow--next"
                      onClick={(e) => moveCategoryPage(categoryPage + 1, e)}
                      disabled={categoryPage >= categoryPageCount - 1}
                      aria-label="Next categories"
                    >
                      {catRtl ? <ChevronLeft size={20} aria-hidden /> : <ChevronRight size={20} aria-hidden />}
                    </button>
                  </>
                ) : null}
                <div
                  className={`catalog-builder-features-grid catalog-categories-dash-grid${isMobileCats ? " catalog-categories-dash-grid--mobile" : " catalog-categories-dash-grid--row"}${isSquare ? " catalog-categories-dash-grid--square" : ""}${showCatLabel ? "" : " catalog-categories-dash-grid--no-label"}`}
                  data-cat-count={visibleCount}
                  data-cat-cols={gridCols}
                  data-cat-slots={slotCount}
                  data-show-label={showCatLabel ? "1" : "0"}
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${gridCols}, minmax(0, ${catTrackMax}))`,
                    gap: catGapPx,
                    justifyContent: cardsRowJustify(st.cards_align),
                    ["--cat-dash-cols"]: slotCount,
                    ["--cat-visible-cols"]: gridCols,
                    ["--cat-card-max"]: `${cardMaxPx}px`,
                    ["--cat-track-max"]: catTrackMax,
                    ["--cat-gap"]: `${catGapPx}px`,
                  }}
                >
                {categoryPageCount > 1 ? (
                  <CategoryImagesWarmer
                    urls={catRows.map((it) => String(it?.image_url || "").trim()).filter(Boolean)}
                  />
                ) : null}
                {visibleCategoryRows.map((it, idx) => {
                  const img = String(it.image_url || "").trim();
                  const label = String(it.name || it.title || it.slug || "").trim();
                  return (
                    <a
                      key={`${categoryPage}-${idx}`}
                      href="#products"
                      className="catalog-dash-category-card"
                      aria-label={label || undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateBuilderCategory(label);
                      }}
                      style={{
                        borderRadius: frame.radius,
                        "--cat-card-accent": catCardAccent,
                        "--cat-i": idx,
                        "--cat-cta-dir": catRtl ? -1 : 1,
                      }}
                    >
                      <span className="catalog-dash-category-card__media" aria-hidden={!img}>
                        {img ? (
                          <img src={img} alt="" width={400} height={400} loading={categoryPage === 0 ? "lazy" : "eager"} decoding="async" />
                        ) : (
                          <span className="catalog-dash-category-card__placeholder" />
                        )}
                      </span>
                      {showCatLabel ? (
                        <>
                          <span className="catalog-dash-category-card__shade" aria-hidden />
                          <span className="catalog-dash-category-card__label">{label}</span>
                        </>
                      ) : null}
                      <span className="catalog-dash-category-card__shine" aria-hidden />
                      <span
                        className={`catalog-dash-category-card__cta${showCatLabel ? " catalog-dash-category-card__cta--icon" : ""}`}
                        aria-hidden
                      >
                        {showCatLabel ? null : (
                          <span className="catalog-dash-category-card__cta-text">{catRtl ? "تسوّق الآن" : "Shop now"}</span>
                        )}
                        <span className="catalog-dash-category-card__cta-arrow">
                          {catRtl ? <ChevronLeft size={16} strokeWidth={2.6} /> : <ChevronRight size={16} strokeWidth={2.6} />}
                        </span>
                      </span>
                    </a>
                  );
                })}
                </div>
                {categoryPageCount > 1 ? (
                  <div className="catalog-categories-carousel__dots" role="tablist" aria-label="Category pages">
                    {Array.from({ length: categoryPageCount }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`catalog-categories-carousel__dot${i === categoryPage ? " is-active" : ""}`}
                        aria-label={`Page ${i + 1}`}
                        aria-selected={i === categoryPage}
                        onClick={(e) => moveCategoryPage(i, e)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        );
      }

      if (gridVariant === "category_circles") {
        const itemsAllCircles = Array.isArray(st.items) ? st.items : [];
        const catRows = itemsAllCircles.filter((it) => {
          if (canEdit) return true;
          return Boolean(String(it?.title || it?.name || it?.slug || "").trim() || String(it?.image_url || "").trim());
        });
        const circlePx = circleImageSizePx(st.circle_image_size);
        const patchCircleItem = (idx, patch) => {
          const cur = [...itemsAllCircles];
          while (cur.length <= idx) cur.push({ title: "", subtitle: "", details: "", image_url: "" });
          cur[idx] = { ...(cur[idx] || {}), ...patch };
          patchSec({ items: cur, cards_count: cur.length });
        };
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section
              className="catalog-section--category-circles catalog-builder-sec-surface"
              data-section-height={(st.section_height || "md").toString()}
              data-circle-size={circlePx}
              style={{ ...builderSectionSurfaceStyle(st, frame, bg, fg), "--circle-size": `${circlePx}px` }}
            >
              {(String(st.title || "").trim() || canEdit) ? (
                <div className="catalog-pro-section-head" style={proSectionHeadWrapStyle(ha)}>
                  <h2 style={proSectionTitleStyle(st)}>{canEdit ? inlineText("title", proSectionTitleStyle(st), { placeholder: "تسوّق من أهم الفئات" }) : String(st.title).trim()}</h2>
                  {(String(st.subtitle || "").trim() || canEdit) ? (
                    <p style={proSectionSubtitleStyle(st)}>{canEdit ? inlineText("subtitle", proSectionSubtitleStyle(st), { placeholder: "وصف" }) : st.subtitle}</p>
                  ) : null}
                </div>
              ) : null}
              <div
                className="catalog-category-circles-row"
                style={{ justifyContent: cardsRowJustify(st.cards_align), "--circle-size": `${circlePx}px` }}
              >
                {catRows.map((it, idx) => {
                  const img = String(it.image_url || "").trim();
                  const label = String(it.name || it.title || it.slug || "").trim();
                  const sub = String(it.subtitle || it.details || "").trim();
                  return (
                    <a
                      key={idx}
                      href="#products"
                      className="catalog-category-circle"
                      style={{ "--circle-size": `${circlePx}px` }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateBuilderCategory(label);
                      }}
                    >
                      <span className="catalog-category-circle__img-wrap">
                        {img ? <img src={img} alt="" width={circlePx} height={circlePx} loading="lazy" decoding="async" /> : <span className="catalog-category-circle__placeholder" />}
                      </span>
                      <span
                        className="catalog-category-circle__label"
                        style={categoryCircleGlyphStyle(label)}
                      >
                        {canEdit ? (
                          <EditableInline
                            value={label}
                            placeholder="عنوان الفئة"
                            onChange={(v) => patchCircleItem(idx, { title: v, name: v })}
                            onOpenSidebar={openSecSidebar}
                          />
                        ) : (
                          label
                        )}
                      </span>
                      {(sub || canEdit) ? (
                        <span className="catalog-category-circle__sub">
                          {canEdit ? (
                            <EditableInline
                              value={sub}
                              placeholder="وصف قصير"
                              onChange={(v) => patchCircleItem(idx, { subtitle: v, details: v })}
                              onOpenSidebar={openSecSidebar}
                            />
                          ) : (
                            sub
                          )}
                        </span>
                      ) : null}
                    </a>
                  );
                })}
              </div>
            </section>
          </div>
        );
      }

      if (gridVariant === "deals_row") {
        const productsMap = canEdit ? ownerProductsById : catalogProductsById;
        const dealIds = Array.isArray(st.product_ids) ? st.product_ids : [];
        const dealProducts = dealIds
          .map((id) => productsMap.get(id) ?? productsMap.get(Number(id)))
          .filter(Boolean);
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section className="catalog-section--deals-row catalog-builder-sec-surface" data-section-height={(st.section_height || "md").toString()} style={builderSectionSurfaceStyle(st, frame, bg, fg)}>
              {(String(st.title || "").trim() || canEdit) ? (
                <div className="catalog-pro-section-head" style={proSectionHeadWrapStyle(ha)}>
                  <h2 style={proSectionTitleStyle(st)}>{canEdit ? inlineText("title", proSectionTitleStyle(st), { placeholder: "عروض اليوم" }) : String(st.title).trim()}</h2>
                  {(String(st.subtitle || "").trim() || canEdit) ? (
                    <p style={proSectionSubtitleStyle(st)}>{canEdit ? inlineText("subtitle", proSectionSubtitleStyle(st), { placeholder: "لفترة محدودة" }) : st.subtitle}</p>
                  ) : null}
                </div>
              ) : null}
              <div className="catalog-deals-row catalog-deals-row--products products-flat-grid" style={{ marginTop: 14, ["--fp-cols"]: String(Math.max(2, Math.min(4, dealProducts.length || 4))) }}>
                {dealProducts.length > 0 ? (
                  dealProducts.map((p, idx) => (
                    <ProductCard
                      key={p.id}
                      product={mapCatalogProduct(p)}
                      primaryColor={catalogThemePrimary}
                      secondaryColor={catalogThemeSecondary}
                      onSelect={onSelectProduct}
                      index={idx}
                      addMode={catalogVisualTheme === "restaurant" ? "text" : "icon"}
                    />
                  ))
                ) : canEdit ? (
                  <div style={{ gridColumn: "1 / -1", padding: 24, textAlign: "center", color: "#94a3b8", fontWeight: 800 }}>
                    اختر منتجات من إعدادات القسم
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        );
      }
      if (gridVariant === "stats") {
        const patchStat = (idx, patch) => {
          const cur = Array.isArray(st.items) ? [...st.items] : [];
          while (cur.length <= idx) cur.push({});
          cur[idx] = { ...(cur[idx] || {}), ...patch };
          patchSec({ items: cur });
        };
        return (
          <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
            {sectionEditBtn}
            <section className="catalog-section--stats catalog-builder-sec-surface" data-section-height={(st.section_height || "md").toString()} style={builderSectionSurfaceStyle(st, frame, bg, fg)}>
              <div className="catalog-stats-grid">
                {items.map((it, idx) => (
                  <div key={idx} className="catalog-stats-item">
                    <div className="catalog-stats-item__value">
                      {canEdit ? (
                        <EditableInline value={it.title || ""} placeholder="10K+" onChange={(v) => patchStat(idx, { title: v })} onOpenSidebar={openSecSidebar} />
                      ) : (
                        String(it.title || "").trim()
                      )}
                    </div>
                    {(String(it.subtitle || "").trim() || canEdit) ? (
                      <div className="catalog-stats-item__stars">
                        {canEdit ? (
                          <EditableInline value={it.subtitle || ""} placeholder="★★★★★" onChange={(v) => patchStat(idx, { subtitle: v })} onOpenSidebar={openSecSidebar} />
                        ) : (
                          String(it.subtitle).trim()
                        )}
                      </div>
                    ) : null}
                    <div className="catalog-stats-item__label">
                      {canEdit ? (
                        <EditableInline value={it.details || ""} placeholder="عملاء سعداء" onChange={(v) => patchStat(idx, { details: v })} onOpenSidebar={openSecSidebar} />
                      ) : (
                        String(it.details || "").trim()
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        );
      }
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
          {sectionEditBtn}
          <section
            className={`catalog-features-section catalog-builder-sec-surface${gridVariantClass}`.trim()}
            data-section-height={(st.section_height || "md").toString()}
            style={{
              ...builderSectionSurfaceStyle(st, frame, bg, fg),
              borderRadius: frame.radius,
              overflow: "hidden",
              border: frame.border,
              background: img0 ? bg : bg,
              color: fg,
              padding: 0,
              direction: settings.layoutDirection || "rtl",
              position: "relative",
            }}
          >
            {img0 ? (
              <img
                src={img0}
                alt=""
                aria-hidden
                width={1600}
                height={600}
                loading="lazy"
                decoding="async"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", zIndex: 0, pointerEvents: "none" }}
              />
            ) : null}
            {img0 ? <div aria-hidden style={{ position: "absolute", inset: 0, background: `${bg}cc`, zIndex: 1, pointerEvents: "none" }} /> : null}
            <div style={{ padding: frame.pad, boxSizing: "border-box", position: "relative", zIndex: 2 }}>
          {(String(st.title || "").trim() || canEdit) ? (
            <div style={{ fontSize: titlePx, fontWeight: 950, textAlign: ha, lineHeight: 1.2 }}>
              {canEdit ? inlineText("title", { fontWeight: 950 }, { placeholder: "عنوان القسم" }) : String(st.title).trim()}
            </div>
          ) : null}
          {(String(st.subtitle || "").trim() || canEdit) ? (
            <div style={{ marginTop: 8, fontSize: subPx, opacity: 0.9, lineHeight: 1.7, textAlign: ha }}>
              {canEdit ? inlineText("subtitle", {}, { placeholder: "وصف القسم" }) : st.subtitle}
            </div>
          ) : null}
          <div
            className="catalog-builder-features-grid"
            style={{
              marginTop: 14,
              display: "grid",
              gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
              gap: 12,
              ["--builder-fp-cols"]: String(count),
            }}
          >
            {(items.length ? items : Array.from({ length: count }).map(() => ({}))).map((it, idx) => {
              const cardBgColor = String(it.card_bg_color || "").trim();
              const cardBgOpacity = it.card_bg_opacity != null && it.card_bg_opacity !== "" ? Math.min(1, Math.max(0, Number(it.card_bg_opacity))) : null;
              const cardBg = cardBgColor ? applyOpacityToColor(cardBgColor, cardBgOpacity == null ? defaultBgOpacity : cardBgOpacity) : defaultCardBg;

              const cardText = String(it.card_text || defaultCardText).trim() || defaultCardText;

              const cardBorderColor = String(it.card_border_color || "").trim();
              const cardBorderOpacity = it.card_border_opacity != null && it.card_border_opacity !== "" ? Math.min(1, Math.max(0, Number(it.card_border_opacity))) : null;
              const cardBorder = cardBorderColor ? applyOpacityToColor(cardBorderColor, cardBorderOpacity == null ? defaultBorderOpacity : cardBorderOpacity) : defaultCardBorder;
              const shadowKey = String(it.card_shadow || defaultShadowKey).trim() || defaultShadowKey;
              const shadow = shadowFromKey(shadowKey) || defaultShadow;
              const imageUrl = String(it.image_url || "").trim();
              const imageMode = String(it.image_mode || imageModeDefault).trim() || imageModeDefault;
              const coverGradFrom = String(it.cover_grad_from || coverGradFromDefault).trim() || coverGradFromDefault;
              const coverGradTo = String(it.cover_grad_to || coverGradToDefault).trim() || coverGradToDefault;
              const coverGradOpacity =
                it.cover_grad_opacity != null && it.cover_grad_opacity !== ""
                  ? Math.min(1, Math.max(0, Number(it.cover_grad_opacity)))
                  : coverGradOpacityDefault;
              const coverA = applyOpacityToColor(coverGradFrom, coverGradOpacity);
              const coverB = applyOpacityToColor(coverGradTo, coverGradOpacity);
              const btnLabel = String(it.cta_label || "").trim();
              const btnUrl = resolveBuilderCtaUrl(it.cta_url);
              const cardTextAlign = "start";
              const btnJustify = "flex-start";
              const renderCardCta = (opts = {}) => {
                const compactPadding = Boolean(opts?.compactPadding);
                if (!btnLabel) return null;
                const padY = compactPadding ? 10 : 9;
                const padX = compactPadding ? 14 : 12;
                const base = {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: `${padY}px ${padX}px`,
                  borderRadius: btnRadius,
                  textDecoration: "none",
                  fontWeight: compactPadding ? 950 : 900,
                  border: `1px solid ${btnVariant === "solid" ? btnBg : cardText}`,
                  color: btnVariant === "solid" ? btnFg : cardText,
                  background: btnVariant === "solid" ? btnBg : "transparent",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  lineHeight: 1.25,
                };
                const wrap = { marginTop: 12, display: "flex", justifyContent: btnJustify };
                return (
                  <div style={wrap}>
                    <a
                      className="catalog-features-card-cta"
                      href={btnUrl}
                      style={base}
                    >
                      {btnLabel}
                    </a>
                  </div>
                );
              };
              const scaleKey = String(it.text_size || "").trim();
              const titleKey = scaleKey || titleSizeDefault || "md";
              const subKey = scaleKey || subtitleSizeDefault || "md";
              const bodyKey = scaleKey || bodySizeDefault || "md";
              const titleFs = titleKey === "lg" ? 26 : titleKey === "sm" ? 19 : 22;
              const subFs = subKey === "lg" ? 17 : subKey === "sm" ? 14 : 15;
              const bodyFs = bodyKey === "lg" ? 14 : bodyKey === "sm" ? 12 : 13;
              const btnVariant = String(it.btn_variant || btnVariantDefault).trim() || btnVariantDefault;
              const btnBg = String(it.btn_bg || btnBgDefault).trim() || btnBgDefault;
              const btnFg = String(it.btn_fg || btnFgDefault).trim() || btnFgDefault;
              const btnBorder = String(it.btn_border || btnBorderDefault).trim() || btnBorderDefault;
              const btnRadius = it.btn_radius != null && it.btn_radius !== "" ? Math.max(0, Math.min(24, Number(it.btn_radius))) : btnRadiusDefault;
              const hasCoverTint =
                Boolean(String(it.card_bg_color || "").trim()) || (it.card_bg_opacity != null && it.card_bg_opacity !== "");
              const imageObjectPosition = heroBackgroundPositionFromXY(it.image_position_x, it.image_position_y);
              const coverBgLayers =
                imageUrl && imageMode === "cover"
                  ? (() => {
                      const bgImages = [
                        `linear-gradient(180deg, ${coverA}, ${coverB})`,
                        ...(hasCoverTint ? [`linear-gradient(0deg, ${cardBg}, ${cardBg})`] : []),
                        `url(${imageUrl})`,
                      ];
                      const n = bgImages.length;
                      return {
                        backgroundImage: bgImages.join(", "),
                        backgroundPosition: bgImages.map((_, li) => (li === n - 1 ? imageObjectPosition : "center")).join(", "),
                        backgroundSize: bgImages.map((_, li) => (li === n - 1 ? "cover" : "auto")).join(", "),
                        backgroundRepeat: "no-repeat",
                      };
                    })()
                  : null;
              return (
              <div
                key={idx}
                style={{
                  border: `1px solid ${cardBorder}`,
                  borderRadius: cardRadius,
                  padding: imageUrl && imageMode === "cover" ? 0 : 14,
                  ...(coverBgLayers || { background: cardBg }),
                  color: cardText,
                  boxShadow: shadow,
                  minHeight: imageUrl && imageMode === "cover" ? cardCoverMinH : cardMinH,
                  maxWidth: cardMaxW,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  textAlign: cardTextAlign,
                  position: "relative",
                }}
              >
                {imageUrl && imageMode === "cover" ? (
                  <>
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "62%",
                        pointerEvents: "none",
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 38%, rgba(0,0,0,0.2) 62%, rgba(0,0,0,0) 100%)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        padding: 16,
                        textAlign: cardTextAlign,
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={{
                          paddingTop: 6,
                          textShadow: "0 2px 14px rgba(0,0,0,0.55)",
                        }}
                      >
                        {String(it.title || "").trim() ? <div style={{ fontWeight: 980, fontSize: titleFs, lineHeight: 1.15 }}>{String(it.title).trim()}</div> : null}
                        {String(it.subtitle || "").trim() ? <div style={{ marginTop: 10, fontSize: subFs, fontWeight: 650, opacity: 0.98, lineHeight: 1.55 }}>{String(it.subtitle).trim()}</div> : null}
                        {String(it.details || "").trim() ? <div style={{ marginTop: 8, fontSize: bodyFs, opacity: 0.92, lineHeight: 1.65 }}>{String(it.details).trim()}</div> : null}
                        {renderCardCta({ compactPadding: true })}
                      </div>
                    </div>
                  </>
                ) : imageUrl && imageMode === "circle" ? (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                    <img
                      src={imageUrl}
                      alt=""
                      width={96}
                      height={96}
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: 96,
                        height: 96,
                        borderRadius: 999,
                        objectFit: "cover",
                        objectPosition: imageObjectPosition,
                        border: `1px solid ${cardBorder}`,
                      }}
                    />
                  </div>
                ) : null}

                {imageUrl && imageMode === "cover" ? null : String(it.title || "").trim() ? (
                  <div style={{ fontWeight: 950, fontSize: titleFs, lineHeight: 1.15 }}>{String(it.title).trim()}</div>
                ) : null}
                {imageUrl && imageMode === "cover" ? null : String(it.subtitle || "").trim() ? (
                  <div style={{ marginTop: 8, fontSize: subFs, opacity: 0.85, lineHeight: 1.6 }}>
                    {String(it.subtitle).trim()}
                  </div>
                ) : null}
                {imageUrl && imageMode === "cover" ? null : String(it.details || it.text || "").trim() ? (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: bodyFs,
                      opacity: 0.9,
                      lineHeight: 1.8,
                    }}
                  >
                    {String(it.details || it.text || "").trim()}
                  </div>
                ) : null}
                {imageUrl && imageMode === "cover" ? null : renderCardCta({ compactPadding: false })}
              </div>
              );
            })}
          </div>
            </div>
          </section>
        </div>
      );
    }

    if (sec.type === "featured_products") {
      const ids = Array.isArray(st.product_ids) ? st.product_ids : [];
      const cols = Number(st.columns || 3);
      const max = Number(st.max_cards || 8);
      const layoutRaw = String(st.products_layout || "grid").toLowerCase();
      const fpLayoutBase = layoutRaw === "list" || layoutRaw === "gallery" ? layoutRaw : "grid";
      const fpLayout = catalogVisualTheme === "restaurant" ? (fpLayoutBase === "gallery" ? "gallery" : "grid") : fpLayoutBase;
      const fpGridClass = `products-flat-grid featured-products-grid${fpLayout === "list" ? " products-flat-grid--list" : ""}${fpLayout === "gallery" ? " products-flat-grid--gallery" : ""}`;
      const picked = ids
        .map((id) => (canEdit ? ownerProductsById.get(id) : catalogProductsById.get(id)))
        .filter(Boolean)
        .slice(0, max);

      const featuredSectionClass = [
        "catalog-featured-products-section",
        catalogVisualTheme === "restaurant" ? "products-section-flat" : "",
      ]
        .filter(Boolean)
        .join(" ");

      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
          {sectionEditBtn}
          <section
            className={featuredSectionClass}
            data-product-image-shape={
              catalogVisualTheme === "restaurant" ? productsSectionDesign.imageShape : undefined
            }
            data-product-cards={
              catalogVisualTheme === "restaurant" && productsSectionDesign.cardStyle !== "default"
                ? productsSectionDesign.cardStyle
                : undefined
            }
            style={{
              borderRadius: frame.radius,
              overflow: "hidden",
              border: frame.border,
              background: img0 ? `linear-gradient(0deg, ${bg}cc, ${bg}cc), url(${img0}) center/cover` : bg,
              color: fg,
              padding: 0,
              direction: settings.layoutDirection || "rtl",
            }}
          >
            <div
              style={{
                padding: frame.pad,
                boxSizing: "border-box",
                minHeight: sectionContentMinHeight(st),
              }}
            >
          {String(st.title || "").trim() ? (
            <div style={{ fontSize: titlePx, fontWeight: 950, textAlign: ha, lineHeight: 1.2 }}>{String(st.title).trim()}</div>
          ) : null}
          {st.subtitle ? (
            <div style={{ marginTop: 8, fontSize: subPx, opacity: 0.9, lineHeight: 1.7, textAlign: ha }}>
              {st.subtitle}
            </div>
          ) : null}

          <div
            className={fpGridClass}
            style={{
              marginTop: 14,
              gap: 20,
              "--fp-cols": String(Math.max(1, Math.min(Number(cols) || 3, 4))),
            }}
          >
            {picked.length > 0
              ? picked.map((p, idx) => (
                  <ProductCard
                    key={p.id}
                    product={mapCatalogProduct(p)}
                    primaryColor={catalogThemePrimary}
                    secondaryColor={catalogThemeSecondary}
                    onSelect={onSelectProduct}
                    index={idx}
                    addMode={catalogVisualTheme === "restaurant" ? "text" : "icon"}
                  />
                ))
              : Array.from({ length: Math.min(max, 6) }).map((_, idx) => (
                  <div
                    key={`ph_${idx}`}
                    style={{
                      border: "1px dashed rgba(15,23,42,0.2)",
                      borderRadius: 16,
                      minHeight: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#94a3b8",
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    اختر منتجات من لوحة التحكم
                  </div>
                ))}
          </div>
            </div>
          </section>
        </div>
      );
    }

    if (sec.type === "products_carousel") {
      const pcWrap = {
        ...builderSectionWrapStyle(frame),
        ...sectionBlockMargins(st),
      };
      const pcOuter = canEdit ? { ...pcWrap, position: "relative" } : pcWrap;
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={pcOuter}>
          {sectionEditBtn}
          <ProductsCarouselSection
            st={st}
            fg={fg}
            frame={frame}
            accentColor={catalogThemePrimary}
            secondaryColor={catalogThemeSecondary}
            headingAlign={ha}
            titleFontPx={titlePx}
            subtitleFontPx={subPx}
            productsById={canEdit ? ownerProductsById : catalogProductsById}
            mapProduct={mapCatalogProduct}
            onSelectProduct={onSelectProduct}
            productsSettled={canEdit || sectionProductsSettled}
            layoutDirection={settings.layoutDirection}
            productsSectionDesign={productsSectionDesign}
            catalogThemePrimary={catalogThemePrimary}
            catalogThemeSecondary={catalogThemeSecondary}
            catalogVisualTheme={catalogVisualTheme}
          />
        </div>
      );
    }

    if (sec.type === "info_cards") {
      const cards = Array.isArray(st.cards) ? st.cards : [];
      const cols = Math.min(4, Math.max(1, Number(st.columns) || 3));
      const accent = String(catalogThemePrimary || "").trim();
      const accentUsable = accent && !/^#?f{3}(f{3})?$/i.test(accent.replace("#", "")) ? accent : "#0f172a";
      const iconColor = String(st.icon_color || "").trim() || accentUsable;
      const iconBg = String(st.icon_bg || "").trim();
      const cardBg = String(st.card_bg || "").trim();
      const fileUrl = String(st.file_url || "").trim();
      const fileName = String(st.file_name || "").trim() || "file.pdf";
      const downloadHref = fileUrl
        ? fileUrl.includes("/storage/v1/object/public/") && !/[?&]download=/.test(fileUrl)
          ? `${fileUrl}${fileUrl.includes("?") ? "&" : "?"}download=${encodeURIComponent(fileName)}`
          : fileUrl
        : "";
      const buttonLabel = String(st.button_label || "").trim() || "تحميل الملف";
      const titleStyle = { ...proSectionTitleStyle(st), lineHeight: 1.2 };
      const subStyle = { ...proSectionSubtitleStyle(st), lineHeight: 1.6 };
      const subWrapStyle = {
        ...subStyle,
        maxWidth: 720,
        marginInlineStart: ha === "center" || ha === "end" ? "auto" : 0,
        marginInlineEnd: ha === "center" ? "auto" : 0,
      };
      const cardBorderW = st.card_border_width === undefined || st.card_border_width === "" ? 1 : Math.max(0, Math.min(6, Number(st.card_border_width) || 0));
      const cardBorderColor = String(st.card_border_color || "").trim();
      const cardStyle = {
        ...(cardBg ? { background: cardBg } : {}),
        ...(cardBorderColor || cardBorderW !== 1
          ? { border: cardBorderW > 0 ? `${cardBorderW}px solid ${cardBorderColor || "rgba(15, 23, 42, 0.07)"}` : "none" }
          : {}),
      };
      const iconShape = String(st.icon_shape || "rounded");
      const iconBoxPx = { sm: 44, md: 54, lg: 66 }[String(st.icon_size || "md")] || 54;
      const iconBorderW = Math.max(0, Math.min(6, Number(st.icon_border_width) || 0));
      const iconBorderColor = String(st.icon_border_color || "").trim() || iconColor;
      const iconBoxStyle = {
        color: iconColor,
        background: st.icon_bg_transparent ? "transparent" : iconBg || `color-mix(in srgb, ${iconColor} 12%, transparent)`,
        width: iconBoxPx,
        height: iconBoxPx,
        borderRadius: iconShape === "circle" ? "50%" : iconShape === "square" ? 4 : Math.round(iconBoxPx * 0.3),
        border: iconBorderW > 0 ? `${iconBorderW}px solid ${iconBorderColor}` : "none",
      };
      const patchCard = (idx, patch) => {
        const cur = [...cards];
        cur[idx] = { ...(cur[idx] || {}), ...patch };
        patchSec({ cards: cur });
      };
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
          {sectionEditBtn}
          <section
            className="catalog-section--info-cards catalog-builder-sec-surface"
            style={{ ...builderSectionSurfaceStyle(st, frame, bg, fg), minHeight: undefined }}
          >
            {(String(st.title || "").trim() || String(st.subtitle || "").trim() || canEdit) ? (
              <div className="catalog-info-cards__head" style={proSectionHeadWrapStyle(ha, 22)}>
                {(String(st.title || "").trim() || canEdit) ? (
                  <h2 style={titleStyle}>
                    {canEdit ? inlineText("title", titleStyle, { placeholder: "العنوان الرئيسي" }) : String(st.title).trim()}
                  </h2>
                ) : null}
                {(String(st.subtitle || "").trim() || canEdit) ? (
                  <p style={subWrapStyle}>
                    {canEdit ? inlineText("subtitle", subStyle, { placeholder: "العنوان الفرعي", multiline: true }) : st.subtitle}
                  </p>
                ) : null}
              </div>
            ) : null}
            {cards.length > 0 ? (
              <div className="catalog-info-cards__grid" style={{ "--info-cols": cols }}>
                {cards.map((card, idx) => {
                  const iconDef = INFO_CARD_ICONS[String(card?.icon || "").toLowerCase()] || INFO_CARD_ICONS.building;
                  const IconCmp = iconDef?.Icon || FileText;
                  return (
                    <div
                      key={card?.id || idx}
                      className="catalog-info-card"
                      style={cardStyle}
                    >
                      <span
                        className="catalog-info-card__icon"
                        style={iconBoxStyle}
                        aria-hidden
                      >
                        <IconCmp size={Math.round(iconBoxPx * 0.48)} strokeWidth={1.8} />
                      </span>
                      <h3 className="catalog-info-card__title">
                        {canEdit ? (
                          <EditableInline value={card?.title || ""} placeholder="عنوان البطاقة" onChange={(v) => patchCard(idx, { title: v })} onOpenSidebar={openSecSidebar} />
                        ) : (
                          String(card?.title || "").trim()
                        )}
                      </h3>
                      {(String(card?.text || "").trim() || canEdit) ? (
                        <p className="catalog-info-card__text">
                          {canEdit ? (
                            <EditableInline value={card?.text || ""} placeholder="شرح مختصر" onChange={(v) => patchCard(idx, { text: v })} onOpenSidebar={openSecSidebar} multiline />
                          ) : (
                            String(card?.text || "").trim()
                          )}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {downloadHref ? (
              <div className="catalog-info-cards__actions" style={{ justifyContent: cardsRowJustify(["start", "center", "end"].includes(st.button_align) ? st.button_align : ha) }}>
                <a
                  className="catalog-info-cards__download"
                  href={downloadHref}
                  download={fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: String(st.button_bg || "").trim() || "#0f172a",
                    color: String(st.button_text_color || "").trim() || "#ffffff",
                  }}
                  onClick={(e) => {
                    if (canEdit) e.stopPropagation();
                  }}
                >
                  <Download size={18} strokeWidth={2.2} aria-hidden />
                  <span>{buttonLabel}</span>
                </a>
              </div>
            ) : null}
          </section>
        </div>
      );
    }

    if (sec.type === "custom_order") {
      const coWrap = {
        ...builderSectionWrapStyle(frame),
        ...sectionBlockMargins(st),
      };
      const coOuter = canEdit ? { ...coWrap, position: "relative" } : coWrap;
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={coOuter}>
          {sectionEditBtn}
          <CustomOrderSection
            sectionId={sec.id}
            settings={st}
            frame={frame}
            fg={fg}
            titlePx={titlePx}
            subtitlePx={subPx}
            headingAlign={ha}
            primaryColor={catalogThemePrimary}
            secondaryColor={catalogThemeSecondary}
            showStorefrontCart={showStorefrontCart}
            menuOnly={menuOnly}
            onAddToCart={handleCustomOrderAddToCart}
          />
        </div>
      );
    }

    if (sec.type === "bundle_offers") {
      const boWrap = {
        ...builderSectionWrapStyle(frame),
        ...sectionBlockMargins(st),
        border: "none",
        boxShadow: "none",
      };
      const boOuter = canEdit ? { ...boWrap, position: "relative" } : boWrap;
      const allProducts = canEdit ? ownerProducts : products;
      const mapById = canEdit ? ownerProductsById : catalogProductsById;
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={boOuter}>
          {sectionEditBtn}
          <BundleOffersSection
            sectionId={sec.id}
            settings={st}
            externalOffers={catalogBundleOffers}
            catalogId={userId}
            frame={frame}
            fg={fg}
            titlePx={titlePx}
            subtitlePx={subPx}
            headingAlign={ha}
            primaryColor={catalogThemePrimary}
            secondaryColor={catalogThemeSecondary}
            products={allProducts}
            productsById={mapById}
            showStorefrontCart={showStorefrontCart}
            menuOnly={menuOnly}
            onAddToCart={handleBundleOfferAddToCart}
          />
        </div>
      );
    }

    if (sec.type === "carousel") {
      const carouselWrap = {
        ...builderSectionWrapStyle(frame),
        ...sectionBlockMargins(st),
      };
      const carouselOuter = canEdit ? { ...carouselWrap, position: "relative" } : carouselWrap;
      return (
        <div key={sec.id} id={`builder-sec-${sec.id}`} style={carouselOuter}>
          {sectionEditBtn}
          <CarouselSection
            sec={sec}
            st={st}
            fg={fg}
            frame={frame}
            accentColor={catalogThemePrimary}
            headingAlign={ha}
            titleFontPx={titlePx}
            subtitleFontPx={subPx}
            productsById={canEdit ? ownerProductsById : catalogProductsById}
            mapProduct={mapCatalogProduct}
            onSelectProduct={onSelectProduct}
            onScrollToCatalogProducts={focusCatalogProductSearch}
          />
        </div>
      );
    }

    return (
      <div key={sec.id} id={`builder-sec-${sec.id}`} style={outerStyle}>
        {sectionEditBtn}
        <section
          style={{
            borderRadius: frame.radius,
            overflow: "hidden",
            border: frame.border,
            background: "#f8fafc",
            color: "#475569",
            padding: 0,
            direction: settings.layoutDirection || "rtl",
          }}
        >
          <div
            style={{
              padding: frame.pad,
              boxSizing: "border-box",
              minHeight: sectionContentMinHeight(st),
            }}
          >
          <div style={{ fontWeight: 950, fontSize: titlePx }}>قسم: {sec.type}</div>
          <div style={{ marginTop: 6, fontSize: subPx }}>
            هذا النوع لسه مش متطبق بالكامل في صفحة العرض، لكنه محفوظ ويظهر كـ
            عنصر مؤقت.
          </div>
          </div>
        </section>
      </div>
    );
  };

  const handleLoadMore = useCallback(async () => {
    const p = paginationRef.current;
    if (!p.has_next || loadMoreInFlightRef.current) return;
    loadMoreInFlightRef.current = true;
    try {
      await fetchCatalogPage(p.page + 1, { append: true });
    } finally {
      loadMoreInFlightRef.current = false;
    }
  }, [fetchCatalogPage]);

  useEffect(() => {
    if (selectedProduct) {
      setProductQuantity(1);
      setCurrentImageIndex(0);
      setSelectedAddonIds([]);

      setSelectedPriceObj({
        price: selectedProduct.price,
        label: 'الافتراضي',
        compareAt: selectedProduct.base_compare_at ?? null,
      });
      const sizesList = sortOptionListByPrice(
        normalizeOptionList(selectedProduct.sizes),
        selectedProduct.extra_prices,
        selectedProduct.price
      );
      const colorsList = normalizeOptionList(selectedProduct.colors);
      const soleSize = sizesList.length === 1 ? sizesList[0] : null;
      const soleColor = colorsList.length === 1 ? colorsList[0] : null;
      setSelectedSize(
        soleSize && !optionDepleted(selectedProduct, "size", soleSize, soleColor) ? soleSize : null
      );
      setSelectedColor(
        soleColor && !optionDepleted(selectedProduct, "color", soleColor, soleSize) ? soleColor : null
      );
    }
  }, [selectedProduct]);

  // When a size is chosen, auto-pick the matching extra price (if any).
  useEffect(() => {
    if (!selectedProduct) return;
    if (!selectedSize) return;
    const extras = Array.isArray(selectedProduct.extra_prices)
      ? selectedProduct.extra_prices
      : [];
    if (extras.length === 0) return;
    const match = extras.find((p) => {
      const label = (p.description_ar || p.description_en || "").trim();
      const sz = String(selectedSize).trim();
      return label === sz || label.startsWith(`${sz} - `);
    });
    const fallback =
      match ||
      extras.find((p) => {
        const label = (p.description_ar || p.description_en || "").trim();
        return label === "جميع الأحجام" || label.startsWith("جميع الأحجام - ");
      });
    if (!fallback) return;
    setSelectedPriceObj({
      price: fallback.price,
      label: (fallback.description_ar || fallback.description_en || "").trim(),
      compareAt: fallback.compare_at_price ?? null,
    });
    const idx = extras.indexOf(fallback);
    if (idx >= 0) setSelectedPriceIndex(idx);
  }, [selectedProduct, selectedSize]);

  // When size/color selection changes, show the optionally linked gallery image.
  useEffect(() => {
    if (!selectedProduct) return;
    const idx = resolveVariantImageIndex(selectedProduct, selectedSize, selectedColor);
    if (idx != null) {
      setCurrentImageIndex(idx);
    }
  }, [selectedProduct, selectedSize, selectedColor]);

  const productModalSizesList = useMemo(() => {
    if (!selectedProduct) return [];
    return sortOptionListByPrice(
      normalizeOptionList(selectedProduct.sizes),
      selectedProduct.extra_prices,
      selectedProduct.price
    );
  }, [selectedProduct]);

  const productModalExtraPricesList = useMemo(() => {
    if (!selectedProduct) return [];
    return sortExtraPricesList(selectedProduct.extra_prices);
  }, [selectedProduct]);

  const productModalHasSizes = productModalSizesList.length > 0;

  useEffect(() => {
    if (!selectedProduct?.addons_enabled) return;
    setSelectedAddonIds((prev) => {
      const list = Array.isArray(selectedProduct.addons) ? selectedProduct.addons : [];
      const cur = Array.isArray(prev) ? prev : [];
      const next = cur.filter((id) => {
        const addon = list.find(
          (a) => String(a.id ?? "").trim().toLowerCase() === String(id ?? "").trim().toLowerCase()
        );
        return addon && isAddonAvailableForSize(addon, selectedSize, productModalHasSizes);
      });
      return next.length === cur.length ? prev : next;
    });
  }, [selectedProduct, selectedSize, productModalHasSizes]);

  useEffect(() => {
    if (!selectedProduct || !wholesaleDealer) return;
    const avail = availableQtyForSelection(
      selectedProduct,
      selectedSize,
      selectedColor
    );
    const sizes = normalizeOptionList(selectedProduct?.sizes);
    const colors = normalizeOptionList(selectedProduct?.colors);
    const needsPick =
      productHasAnyStockTracked(selectedProduct) &&
      avail == null &&
      (sizes.length > 0 || colors.length > 0);
    const maxQ = needsPick
      ? 1
      : maxQtyForSelection(selectedProduct, selectedSize, selectedColor);
    if (!Number.isFinite(maxQ) || maxQ >= 9999) return;
    setProductQuantity((q) => Math.max(1, Math.min(q, maxQ)));
  }, [selectedProduct, selectedSize, selectedColor, wholesaleDealer]);


  const filteredProducts = useMemo(() => {
    const maxP = parseFloat(String(priceMaxFilter).replace(/,/g, "."));
    const usePrice = String(priceMaxFilter || "").trim() !== "" && Number.isFinite(maxP);
    return products
      .filter((p) => (offersOnly ? Boolean(p.on_sale) : true))
      .filter((p) => (newProductsOnly ? isProductWithinNewBadgeWindow(p.created_at) : true))
      .filter((p) => {
        if (!usePrice) return true;
        return Number(p.price) <= maxP;
      });
  }, [products, offersOnly, newProductsOnly, priceMaxFilter]);

  const renderCategoryStrip = (extraClass = "", domId = "categories-section", stripOpts) => {
    const isVerticalStrip = String(extraClass || "").includes("vertical");
    const isCircleRow =
      !isVerticalStrip &&
      (String(extraClass || "").includes("circles") || Boolean(stripOpts?.circles));
    const alignRaw = String(catalogLayoutEffective.category_pills_align || "").trim().toLowerCase();
    const pillsAlign =
      !isVerticalStrip && !isCircleRow && ["start", "center", "end"].includes(alignRaw) ? alignRaw : undefined;
    const catRtl = (settings.layoutDirection || "rtl") === "rtl";
    const pillPrimaryOk = readableAccentOnWhite(catalogThemePrimary, "") !== "";
    const pillActiveBg = pillPrimaryOk
      ? String(catalogThemePrimary).trim()
      : readableAccentOnWhite(catalogThemeSecondary, "#0f172a");
    const pillActiveFg = pillPrimaryOk
      ? pickReadableIconColor(pillActiveBg, catalogThemeSecondary, "#ffffff")
      : pickReadableIconColor(pillActiveBg, "#ffffff", "#ffffff");
    const pillStyle = (active) =>
      isCircleRow || !active
        ? undefined
        : {
            backgroundColor: pillActiveBg,
            color: pillActiveFg,
            borderColor: pillActiveBg,
            "--cat-pill-active-bg": pillActiveBg,
            "--cat-pill-active-fg": pillActiveFg,
          };
    const renderFilterBtn = (option) => {
      const { key, label, active, onActivate } = option;
      const circleMeta = isCircleRow ? categoryCircleGlyphMeta(label) : null;
      return (
        <button
          key={key}
          type="button"
          className={`category-pill${active ? " active" : ""}${isCircleRow ? " category-pill--circle" : ""}`}
          onClick={onActivate}
          style={pillStyle(active)}
        >
          {isCircleRow && circleMeta ? (
            <span
              className="category-pill__circle-ring"
              style={{ width: circleMeta.circlePx, height: circleMeta.circlePx }}
            >
              <span
                className={`category-pill__circle-glyph${circleMeta.glyphClass ? ` ${circleMeta.glyphClass}` : ""}`}
                style={{
                  fontSize: `${circleMeta.fontSize}px`,
                  lineHeight: circleMeta.lineHeight,
                }}
              >
                {circleMeta.words.map((word, wi) => (
                  <React.Fragment key={wi}>
                    {wi > 0 ? <br /> : null}
                    {word}
                  </React.Fragment>
                ))}
              </span>
            </span>
          ) : (
            label
          )}
        </button>
      );
    };
    const filterOptions = [
      ...(hasOffers
        ? [{
            key: "__offers__",
            label: "عروض خاصة",
            active: offersOnly,
            onActivate: () => {
              setOffersOnly(true);
              if (categoryFilter !== "All") setCategoryFilter("All");
              setPagination((prev) => ({
                ...prev,
                page: 1,
                has_next: false,
                has_previous: false,
              }));
              stripOpts?.onPick?.();
              scrollToProducts({ extraDownPx: 120 });
            },
          }]
        : []),
      ...categories.map((cat) => ({
        key: cat,
        label: cat === "All" ? t.all : cat,
        active: categoryFilter === cat,
        onActivate: () => {
          if (offersOnly) setOffersOnly(false);
          setCategoryFilter(cat);
          setPagination((prev) => ({
            ...prev,
            page: 1,
            has_next: false,
            has_previous: false,
          }));
          stripOpts?.onPick?.();
          scrollToProducts({ extraDownPx: 120 });
        },
      })),
    ];

    if (isVerticalStrip) {
      return (
        <div
          id={domId}
          className={`categories-flat-container catalog-skip-global-palette categories-flat-container--vertical${extraClass ? ` ${extraClass}` : ""}`}
          style={{ scrollMarginTop: "80px" }}
        >
          {filterOptions.map((option) => renderFilterBtn(option))}
        </div>
      );
    }

    if (isCircleRow) {
      return (
        <div
          id={domId}
          className={`categories-flat-container catalog-skip-global-palette categories-flat-container--circles${extraClass ? ` ${extraClass}` : ""}`}
          style={{ scrollMarginTop: undefined }}
          data-category-pills-align={["start", "center", "end"].includes(alignRaw) ? alignRaw : "center"}
        >
          {filterOptions.map((option) => renderFilterBtn(option))}
        </div>
      );
    }

    return (
      <CatalogCategoryPillsStrip
        id={domId}
        options={filterOptions}
        rtl={catRtl}
        align={pillsAlign}
        primaryColor={catalogThemePrimary}
        secondaryColor={catalogThemeSecondary}
        className={extraClass}
        renderPill={renderFilterBtn}
      />
    );
  };

  const renderSidebarFilters = () => {
    const x = catalogLayoutEffective;
    if (!x.filters_sidebar) return null;
    return (
      <div className="catalog-sidebar-filters">
        <div className="catalog-sidebar-filters__head">
          <div className="catalog-sidebar-filters__title">تصفية النتائج</div>
          <p className="catalog-sidebar-filters__hint">فعّل ما يناسبك؛ التصفية تُطبّق فوراً على قائمة المنتجات.</p>
        </div>
        {x.filter_offers !== false && hasOffers ? (
          <div className="catalog-sidebar-filters__card">
            <StudioSwitch checked={offersOnly} onChange={(v) => setOffersOnly(v)} label="عروض فقط" />
            <span className="catalog-sidebar-filters__meta">منتجات عليها سعر عرض أو وسم عرض</span>
          </div>
        ) : null}
        {x.filter_new !== false ? (
          <div className="catalog-sidebar-filters__card">
            <StudioSwitch checked={newProductsOnly} onChange={(v) => setNewProductsOnly(v)} label="جديد فقط" />
            <span className="catalog-sidebar-filters__meta">إظهار المنتجات المعلّمة كجديدة</span>
          </div>
        ) : null}
        {x.filter_price !== false ? (
          <div className="catalog-sidebar-filters__row catalog-sidebar-filters__row--stack catalog-sidebar-filters__card">
            <div className="catalog-sidebar-filters__field-label">
              <span className="catalog-sidebar-filters__field-title">أقصى سعر</span>
              <span className="catalog-sidebar-filters__meta">بالشيكل؛ اترك الحقل فارغاً لإلغاء الحد</span>
            </div>
            <input
              type="number"
              min={0}
              step={1}
              value={priceMaxFilter}
              onChange={(e) => setPriceMaxFilter(e.target.value)}
              placeholder="بدون حد"
              className="catalog-sidebar-filters__input"
            />
          </div>
        ) : null}
      </div>
    );
  };

  /* CSS كله في Catalog.css؛ الألوان تُحقن في :root عبر useEffect */

  const modalBaseUnit = Number(selectedPriceObj?.price ?? selectedProduct?.price ?? 0);
  const modalVisibleAddons = useMemo(() => {
    if (!selectedProduct?.addons_enabled) return [];
    const list = Array.isArray(selectedProduct.addons) ? selectedProduct.addons : [];
    return list
      .map((a) => {
        const price = resolveAddonPriceForSize(a, selectedSize, productModalHasSizes);
        if (price == null) return null;
        return {
          id: String(a.id),
          name: String(a.name || ""),
          price,
          raw: a,
        };
      })
      .filter(Boolean);
  }, [selectedProduct, selectedSize, productModalHasSizes]);

  const modalPickedAddons = useMemo(() => {
    if (!selectedProduct?.addons_enabled) return [];
    if (!selectedAddonIds.length) return [];
    const idSet = new Set(
      selectedAddonIds.map((x) => String(x ?? '').trim().toLowerCase()).filter(Boolean)
    );
    return modalVisibleAddons
      .filter((a) => idSet.has(String(a.id ?? '').trim().toLowerCase()))
      .map((a) => ({
        id: a.id,
        name: a.name,
        price: a.price,
      }));
  }, [selectedProduct, modalVisibleAddons, selectedAddonIds]);
  const modalAddonsExtra = useMemo(
    () => modalPickedAddons.reduce((s, a) => s + a.price, 0),
    [modalPickedAddons]
  );
  const modalLineUnit =
    Math.round((modalBaseUnit + modalAddonsExtra + Number.EPSILON) * 100) / 100;

  const isSelectedOutOfStock = selectedProduct
    ? productGloballyOutOfStock(selectedProduct)
    : false;

  const wholesaleAvailQty =
    wholesaleDealer && selectedProduct
      ? availableQtyForSelection(selectedProduct, selectedSize, selectedColor)
      : null;
  const wholesaleNeedsVariantPick =
    Boolean(wholesaleDealer && selectedProduct && productHasAnyStockTracked(selectedProduct)) &&
    wholesaleAvailQty == null &&
    (() => {
      const sizes = normalizeOptionList(selectedProduct?.sizes);
      const colors = normalizeOptionList(selectedProduct?.colors);
      return sizes.length > 0 || colors.length > 0;
    })();
  const wholesaleMaxQtyRaw =
    wholesaleDealer && selectedProduct
      ? maxQtyForSelection(selectedProduct, selectedSize, selectedColor)
      : 9999;
  const wholesaleMaxQty = wholesaleNeedsVariantPick
    ? 1
    : wholesaleMaxQtyRaw;
  const wholesaleQtyCapped =
    wholesaleDealer &&
    Number.isFinite(wholesaleMaxQty) &&
    wholesaleMaxQty < 9999;
  const wholesaleQtyAtMax =
    wholesaleQtyCapped && productQuantity >= wholesaleMaxQty;
  const wholesaleSelectionUnavailable =
    wholesaleDealer && wholesaleAvailQty === 0;
  const modalWholesalePriceMissing = Boolean(selectedProduct?.wholesale_price_missing);
  const modalCannotPurchase =
    isSelectedOutOfStock ||
    wholesaleSelectionUnavailable ||
    orderingBlockedByHours ||
    modalWholesalePriceMissing;

  const toggleModalAddon = useCallback((id) => {
    const sid = String(id ?? '').trim().toLowerCase();
    if (!sid) return;
    setSelectedAddonIds((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      return cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid];
    });
  }, []);

  const renderShowcaseCartTopbar = (opts) => {
    const studioCanvas = Boolean(opts?.studioCanvas);
    if (catalogVisualTheme !== "showcase") return null;
    const topbarClassName =
      "catalog-showcase-cart-topbar" + (studioCanvas ? " catalog-showcase-cart-topbar--studio-canvas" : "");
    if (cartPlacement === "topbar") {
      return (
        <div className={topbarClassName} dir={settings.layoutDirection || "rtl"}>
          <div className="container d-flex align-items-center justify-content-between gap-2 py-2">
            <div className="d-flex align-items-center gap-2 min-w-0" style={{ flex: "1 1 auto", minWidth: 0 }}>
              {settings.logo ? (
                <img src={settings.logo} alt="" className="catalog-showcase-topbar__logo" width={36} height={36} decoding="async" />
              ) : null}
              <span className="catalog-showcase-topbar__name">{settings.storeName}</span>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap">
              {!shopper && !wholesaleDealer ? (
                <button
                  type="button"
                  className="catalog-topbar-icon-btn"
                  onClick={() => openAuth("login")}
                  aria-label="تسجيل الدخول"
                >
                  <User size={20} strokeWidth={2.25} />
                </button>
              ) : shopper && !wholesaleDealer ? (
                <div className="catalog-restaurant-slim-bar__userwrap">
                  <button
                    type="button"
                    className="catalog-topbar-icon-btn"
                    onClick={() => setRestaurantUserMenuOpen((v) => !v)}
                    aria-label="حسابي"
                  >
                    <User size={20} strokeWidth={2.25} />
                  </button>
                  {restaurantUserMenuOpen ? (
                    <>
                      <button
                        type="button"
                        className="catalog-restaurant-slim-bar__userbackdrop"
                        aria-label="إغلاق"
                        onClick={() => setRestaurantUserMenuOpen(false)}
                      />
                      <div className="catalog-restaurant-slim-bar__usermenu" role="menu" aria-label="قائمة الحساب">
                        <div className="catalog-restaurant-slim-bar__usermenu-title">
                          {String(shopper?.full_name || "").trim() || "حسابي"}
                        </div>
                        <button
                          type="button"
                          className="catalog-restaurant-slim-bar__usermenu-item"
                          onClick={() => {
                            setRestaurantUserMenuOpen(false);
                            logoutShopper();
                          }}
                        >
                          تسجيل الخروج
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}
              {showStorefrontCart ? (
              <button
                type="button"
                className="catalog-topbar-cart-icon"
                onClick={requestOpenCart}
                aria-label={t.cartAriaLabel(cart.length)}
              >
                <ShoppingCart size={20} strokeWidth={2.25} aria-hidden />
                <span className="catalog-floating-cart-corner-badge" aria-hidden>
                  {cart.length}
                </span>
              </button>
              ) : null}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={topbarClassName} dir={settings.layoutDirection || "rtl"} role="banner">
        <div className="container d-flex align-items-center py-2">
          <div className="d-flex align-items-center gap-2 min-w-0" style={{ flex: "1 1 auto", minWidth: 0 }}>
            {settings.logo ? (
              <img src={settings.logo} alt="" className="catalog-showcase-topbar__logo" width={36} height={36} decoding="async" />
            ) : null}
            <span className="catalog-showcase-topbar__name text-truncate">{settings.storeName}</span>
          </div>
        </div>
      </div>
    );
  };


  if (loading || (canEdit && builderLoading)) {
    const storeLogo =
      String(settings.logo || "").trim() ||
      readCachedCatalogLogoUrl(userId) ||
      "";
    const lxLive =
      (canEdit ? layoutsEdit?.catalog_layout_extras : layouts?.catalog_layout_extras) || {};
    const cachedLoader = readCachedCatalogLoaderPrefs(userId);
    const loaderText = String(lxLive.loader_text ?? cachedLoader.text ?? "").trim();
    const loaderColorRaw = String(
      lxLive.loader_text_color || cachedLoader.color || ""
    ).trim();
    const hasLoaderColor = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(loaderColorRaw);
    const hintColor = hasLoaderColor ? loaderColorRaw : "#111827";
    // Text إن وُجد؛ شريط ملون إن وُجد لون (حتى بدون نص)
    const showHint = Boolean(loaderText);
    const showBar = hasLoaderColor || (canEdit && Boolean(studioLoadHintRotating));

    if (!canEdit) {
      return (
        <FullscreenLoader
          logoSrc={storeLogo}
          alt={String(settings.storeName || "").trim() || "Catalog"}
          hint={showHint ? loaderText : ""}
          hintColor={hintColor}
          showProgressBar={showBar && hasLoaderColor}
          progressBarColor={hasLoaderColor ? loaderColorRaw : ""}
          blankUntilLogo={!storeLogo}
        />
      );
    }

    const studioHint =
      studioLang === "en"
        ? "Loading your catalog design…"
        : "جاري تحميل تصميم الكتالوج…";
    const hintCombined = [studioHint, studioLoadHintRotating].filter(Boolean).join(" — ");
    return (
      <FullscreenLoader
        logoSrc={storeLogo || KATALOJI_LOADER_FALLBACK_SRC}
        alt={String(settings.storeName || "").trim() || (storeLogo ? "Catalog" : "Kataloji")}
        hint={loaderText || hintCombined}
        hintColor={hintColor}
        showProgressBar
        progressBarColor={hasLoaderColor ? loaderColorRaw : ""}
      />
    );
  }

  return (
    <CatalogMenuOnlyContext.Provider value={menuOnly}>
    <CatalogCardPriceContext.Provider value={cardPricePrefs}>
    <>
    {canEdit ? <LanguageSwitchOverlay show={langSwitchOverlay} /> : null}
    {canEdit ? (
      <CatalogStudioTour
        enabled={canEdit}
        lang={studioLang}
        steps={catalogStudioTourSteps}
        storageKey={catalogStudioTourStorageKey}
        onStepChange={handleStudioTourStepChange}
        fetchServerCompleted={fetchCatalogStudioTourServerCompleted}
        markServerCompleted={markCatalogStudioTourServerCompleted}
      />
    ) : null}
    <ConfirmModal
      isOpen={Boolean(canEdit && themePresetConfirmOpen)}
      onClose={() => {
        setThemePresetConfirmOpen(false);
        setPendingThemePresetKey(null);
        cancelStudioThemePreview();
      }}
      onConfirm={async () => {
        const k = pendingThemePresetKey;
        setThemePresetConfirmOpen(false);
        setPendingThemePresetKey(null);
        if (!k) {
          setStudioThemePreview(null);
          return;
        }
        if (isCatalogThemeCatalogId(k)) {
          try {
            const data = await fetchCatalogThemeDetail(k);
            if (data) {
              applyCatalogThemeFromServer(data);
              setSelectedCatalogThemeId(String(k));
            }
          } catch (e) {
            setSaveError(formatCustomizerSaveError(e));
            cancelStudioThemePreview();
            return;
          }
        } else {
          setBuilderTheme(k);
          setSelectedCatalogThemeId("");
          applyThemePreset(k);
        }
        setStudioThemePreview(null);
      }}
      title={tStudio.themeApplyConfirmTitle}
      message={tStudio.themeApplyConfirmMessage}
      type="warning"
      language={studioLang === "en" ? "en" : "ar"}
      confirmText={tStudio.themeApplyConfirmAction}
      cancelText={tStudio.themeApplyCancel}
      overlayZIndex={10006}
    />
    <ConfirmModal
      isOpen={Boolean(canEdit && studioLeaveModalOpen && studioNavigateBlocker.state === "blocked")}
      onClose={() => {
        setStudioLeaveModalOpen(false);
        studioNavigateBlocker.reset?.();
      }}
      onConfirm={() => {
        setStudioLeaveModalOpen(false);
        studioNavigateBlocker.proceed?.();
      }}
      title={tStudio.leaveStudioTitle}
      message={tStudio.leaveStudioMessage}
      type="warning"
      language={studioLang === "en" ? "en" : "ar"}
      confirmText={tStudio.leaveStudioLeave}
      cancelText={tStudio.leaveStudioStay}
      overlayZIndex={10020}
    />
    {studioToast?.message ? (
      <div
        role="status"
        aria-live="polite"
        className="catalog-studio-toast"
        style={{
          position: "fixed",
          bottom: "max(20px, env(safe-area-inset-bottom))",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10008,
          background: "#111827",
          color: "#fff",
          fontWeight: 800,
          fontSize: 14,
          padding: "12px 18px",
          borderRadius: 12,
          boxShadow: "0 12px 32px rgba(15,23,42,0.25)",
          maxWidth: "min(420px, 92vw)",
          textAlign: "center",
        }}
      >
        {studioToast.message}
      </div>
    ) : null}
    <div
      ref={catalogPageRootRef}
      className={`catalog-page-root catalog-theme-${catalogVisualTheme}${
        hidePublicCatalogShell ? " catalog-page-root--unavailable" : ""
      }`}
      dir={settings.layoutDirection || "rtl"}
      aria-hidden={hidePublicCatalogShell ? true : undefined}
      data-cart-bump={cartBumpOn ? "1" : "0"}
      style={{
        ...(restaurantVars || {}),
        ...(catalogVisualTheme === "showcase" ? { paddingTop: canEdit ? 0 : 58 } : {}),
        ...(catalogUiFontFamily ? { fontFamily: catalogUiFontFamily } : {}),
        "--product-card-image-size": `${catalogLayoutEffective.product_card_image_size}px`,
        "--product-card-image-scale": String(productsSectionDesign.productImageFrameScale ?? 1),
        ...(productsSectionDesign.cardBorderRadiusPx != null
          ? { "--products-card-radius": `${productsSectionDesign.cardBorderRadiusPx}px` }
          : {}),
        ...(productsSectionDesign.cardBgColor
          ? { "--products-card-bg": productsSectionDesign.cardBgColor }
          : {}),
        "--product-cart-icon-border-width": `${productsSectionDesign.cartIconBorderWidthPx || 0}px`,
        ...(productsSectionDesign.cartIconBorderColor
          ? { "--product-cart-icon-border-color": productsSectionDesign.cartIconBorderColor }
          : {}),
        ...(catalogVisualTheme === "restaurant" &&
        (productsSectionDesign.imageShape === "square" ||
          productsSectionDesign.imageShape === "portrait") &&
        productsSectionDesign.restaurantSquareImageRadiusPx != null
          ? {
              "--restaurant-square-image-radius": `${productsSectionDesign.restaurantSquareImageRadiusPx}px`,
            }
          : {}),
        ...(String(catalogLayoutEffective.floating_cart_button_bg || "").trim()
          ? { "--floating-cart-bg": String(catalogLayoutEffective.floating_cart_button_bg).trim() }
          : {}),
        ...(String(catalogLayoutEffective.floating_cart_icon_color || "").trim()
          ? { "--floating-cart-icon": String(catalogLayoutEffective.floating_cart_icon_color).trim() }
          : {}),
        ...(catalogPageContentMaxPx != null
          ? { "--catalog-content-max": `${catalogPageContentMaxPx}px` }
          : { "--catalog-content-max": "100%" }),
        "--catalog-accent": catalogThemePrimary || "#111827",
      }}
      data-catalog-studio={canEdit ? "1" : "0"}
      data-catalog-global-bg={layoutExtrasBool(catalogLayoutEffective.global_page_bg_enabled) ? "1" : "0"}
      data-product-image-shape={productsSectionDesign.imageShape}
      data-product-image-fill={productsSectionDesign.productImageFillCard ? "1" : "0"}
      data-products-show-desc={catalogLayoutEffective.products_show_description ? "1" : "0"}
      data-restaurant-show-desc={catalogLayoutEffective.products_show_description ? "1" : "0"}
      data-product-cart-side={productsSectionDesign.cartIconSide || "right"}
      data-restaurant-decor={
        catalogVisualTheme === "restaurant"
          ? String(catalogLayoutEffective?.restaurant_decor_style || "none")
          : undefined
      }
      data-fashion-store={catalogLayoutEffective.fashion_store ? "1" : undefined}
      data-fashion-sharp={
        catalogLayoutEffective.fashion_sharp_corners || catalogLayoutEffective.fashion_store ? "1" : undefined
      }
      data-online-store-pro={catalogLayoutEffective.online_store_pro ? "1" : undefined}
      data-catalog-dark={catalogDarkSurfaceActive(catalogLayoutEffective) ? "1" : undefined}
      data-hero-layout={
        String(catalogLayoutEffective.hero_layout || "").trim() ? String(catalogLayoutEffective.hero_layout).trim() : undefined
      }
      data-cart-open={isCartOpen && showStorefrontCart ? "1" : "0"}
      data-product-modal-open={selectedProduct ? "1" : "0"}
      data-menu-only={menuOnly ? "1" : "0"}
    >
      {/* الألوان الديناميكية تُحقن في :root عبر useEffect أعلاه؛ الـ CSS كله في Catalog.css */}
      {seedHold ? (
        <style>{`.catalog-page-root .features-flat-bar,.catalog-page-root #products,.catalog-page-root #catalog-footer{display:none !important}`}</style>
      ) : null}

      {orderingBlockedByHours && !canEdit ? (
        <div className="catalog-closed-hours-banner" role="status">
          {closedHoursMessage}
        </div>
      ) : null}

      {canEdit && (
        <div ref={studioTopChromeRef} className="catalog-studio-top-chrome catalog-studio-top-chrome--elementor" dir={studioDir} style={{ position: "sticky", top: 0, zIndex: 10025 }}>
          <div className="catalog-studio-top-chrome__flex">
            <div className="catalog-studio-top-chrome__cluster catalog-studio-top-chrome__cluster--start">
              <button
                type="button"
                className="catalog-studio-top-chrome__icon-btn"
                data-tour="studio-panel-toggle"
                title={tStudio.layersPanel}
                aria-label={customizerOpen ? tStudio.hidePanel : tStudio.customize}
                aria-expanded={customizerOpen}
                aria-controls="catalog-customizer-panel"
                onClick={() => setCustomizerOpen((p) => !p)}
              >
                {customizerOpen ? <PanelRightClose size={18} strokeWidth={2.2} aria-hidden /> : <Layers size={18} strokeWidth={2.2} aria-hidden />}
              </button>
              <button
                type="button"
                className="catalog-studio-top-chrome__icon-btn"
                title={tStudio.lookQuick}
                aria-label={tStudio.lookQuick}
                onClick={() => {
                  setCustomizerOpen(true);
                  setCustomizerTab("look");
                  setCustomizerPane("form");
                  setCustomizerFormTarget({ kind: "look", key: "all" });
                  setSectionsMenuOpen(null);
                }}
              >
                <SlidersHorizontal size={18} strokeWidth={2.2} aria-hidden />
              </button>
              <div className="catalog-studio-top-chrome__doc-wrap" ref={studioDocMenuWrapRef}>
                <button
                  type="button"
                  className="catalog-studio-top-chrome__doc-btn"
                  aria-expanded={studioDocMenuOpen}
                  aria-haspopup="menu"
                  aria-label={tStudio.docMenuAria}
                  onClick={() => setStudioDocMenuOpen((v) => !v)}
                >
                  <span className="catalog-studio-top-chrome__doc-btn-label">
                    {String(settings?.storeName || storeKey || "Catalog").trim().slice(0, 28) || "Catalog"}
                    {studioUnsaved ? <span className="catalog-studio-top-chrome__doc-draft"> · {tStudio.draftBadge}</span> : null}
                  </span>
                  <ChevronDown size={16} strokeWidth={2.2} aria-hidden className="catalog-studio-top-chrome__doc-chevron" />
                </button>
                {studioDocMenuOpen ? (
                  <div className="catalog-studio-top-chrome__doc-popover" role="menu" dir={studioDir}>
                    <button
                      type="button"
                      role="menuitem"
                      className="catalog-studio-top-chrome__doc-popover-item"
                      onClick={() => {
                        setStudioDocMenuOpen(false);
                        navigate("/dashboard");
                      }}
                    >
                      {tStudio.backToDashboard}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="catalog-studio-top-chrome__doc-popover-item"
                      onClick={() => {
                        setStudioDocMenuOpen(false);
                        const slug = String(storeKey || userId || "").trim();
                        if (!slug) return;
                        window.open(`${window.location.origin}/catalog/${encodeURIComponent(slug)}`, "_blank", "noopener,noreferrer");
                      }}
                    >
                      {tStudio.visitorPreview}
                    </button>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className="catalog-studio-top-chrome__icon-btn"
                title={tStudio.shortcutsTitle}
                aria-label={tStudio.shortcutsTitle}
                onClick={() => setStudioShortcutsOpen(true)}
              >
                <Settings size={18} strokeWidth={2.2} aria-hidden />
              </button>
              <div ref={studioHistoryUiRef} className="catalog-studio-history-controls catalog-studio-history-controls--dark">
                <button
                  type="button"
                  data-tour="studio-undo"
                  onClick={() => undoStudioHistory()}
                  disabled={!studioHistoryUi.canUndo}
                  title={tStudio.undoTitle}
                  aria-label={tStudio.undoTitle}
                  className="catalog-studio-top-chrome__icon-btn catalog-studio-icon-btn--undo"
                >
                  <Undo2 size={18} strokeWidth={2.2} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => setStudioHistoryMenuOpen((v) => !v)}
                  title={tStudio.historyRecentTitle}
                  aria-label={tStudio.historyRecentTitle}
                  aria-expanded={studioHistoryMenuOpen}
                  aria-haspopup="listbox"
                  className="catalog-studio-top-chrome__icon-btn catalog-studio-icon-btn--history-menu"
                >
                  <History size={17} strokeWidth={2.2} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => redoStudioHistory()}
                  disabled={!studioHistoryUi.canRedo}
                  title={tStudio.redoTitle}
                  aria-label={tStudio.redoTitle}
                  className="catalog-studio-top-chrome__icon-btn catalog-studio-icon-btn--redo"
                >
                  <Redo2 size={18} strokeWidth={2.2} aria-hidden />
                </button>
                {studioHistoryMenuOpen ? (
                  <div className="catalog-studio-history-menu" role="listbox" aria-label={tStudio.historyRecentTitle} dir={studioDir}>
                    {studioHistoryUi.menuEntries.length === 0 ? (
                      <div className="catalog-studio-history-menu__empty" style={{ padding: "10px 12px", fontSize: 13 }}>
                        —
                      </div>
                    ) : (
                      studioHistoryUi.menuEntries
                        .map((entry, j) => ({
                          entry,
                          gi: studioHistoryUi.menuStartIdx + j,
                        }))
                        .reverse()
                        .map(({ entry, gi }) => {
                          const active = gi === studioHistoryRef.current.index;
                          return (
                            <button
                              key={entry.id}
                              type="button"
                              role="option"
                              aria-selected={active}
                              className={`catalog-studio-history-menu__item${active ? " is-active" : ""}`}
                              onClick={() => jumpStudioHistoryIndex(gi)}
                            >
                              <span className="catalog-studio-history-menu__label">{entry.label}</span>
                              <span className="catalog-studio-history-menu__time">
                                {new Date(entry.timestamp).toLocaleTimeString(studioLang === "en" ? "en" : "ar", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </button>
                          );
                        })
                    )}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className="catalog-studio-top-chrome__icon-btn"
                data-tour="studio-back-dashboard"
                onClick={() => navigate("/dashboard")}
                title={tStudio.backToDashboard}
                aria-label={tStudio.backToDashboard}
              >
                {studioDir === "rtl" ? <ChevronRight size={18} strokeWidth={2.2} aria-hidden /> : <ChevronLeft size={18} strokeWidth={2.2} aria-hidden />}
              </button>
            </div>

            <div className="catalog-studio-top-chrome__cluster catalog-studio-top-chrome__cluster--center">
              <div className="catalog-studio-top-chrome__devices" role="group" aria-label="Preview width">
                <button
                  type="button"
                  className={`catalog-studio-preview-toggle catalog-studio-preview-toggle--dark${studioPreviewFrame === "desktop" ? " is-active" : ""}`}
                  onClick={() => setStudioPreviewFrame("desktop")}
                  title={tStudio.previewDesktop}
                  aria-pressed={studioPreviewFrame === "desktop"}
                >
                  <Monitor size={18} aria-hidden />
                </button>
                <button
                  type="button"
                  className={`catalog-studio-preview-toggle catalog-studio-preview-toggle--dark${studioPreviewFrame === "tablet" ? " is-active" : ""}`}
                  onClick={() => setStudioPreviewFrame("tablet")}
                  title={tStudio.previewTablet}
                  aria-pressed={studioPreviewFrame === "tablet"}
                >
                  <Tablet size={18} aria-hidden />
                </button>
                <button
                  type="button"
                  className={`catalog-studio-preview-toggle catalog-studio-preview-toggle--dark${studioPreviewFrame === "mobile" ? " is-active" : ""}`}
                  onClick={() => setStudioPreviewFrame("mobile")}
                  title={tStudio.previewMobile}
                  aria-pressed={studioPreviewFrame === "mobile"}
                >
                  <Smartphone size={18} aria-hidden />
                </button>
              </div>
              {studioPreviewFrame === "desktop" ? (
                <label className="catalog-studio-zoom-control catalog-studio-zoom-control--dark">
                  <span className="catalog-studio-zoom-control__label">{tStudio.previewZoomLabel}</span>
                  <select
                    value={String(studioPreviewZoom)}
                    onChange={(e) => setStudioPreviewZoom(Number(e.target.value) || 100)}
                    aria-label={tStudio.previewZoomLabel}
                  >
                    {[75, 85, 100, 110, 125].map((z) => (
                      <option key={z} value={z}>
                        {z}%
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>

            <div className="catalog-studio-top-chrome__cluster catalog-studio-top-chrome__cluster--end">
              <button
                type="button"
                className="catalog-studio-top-chrome__icon-btn"
                title={tStudio.studioSearchFocus}
                aria-label={tStudio.studioSearchFocus}
                onClick={() => {
                  setCustomizerOpen(true);
                  setCustomizerPane("menu");
                  requestAnimationFrame(() => {
                    try {
                      studioPanelSearchInputRef.current?.focus();
                    } catch {}
                  });
                }}
              >
                <Search size={18} strokeWidth={2.2} aria-hidden />
              </button>
              <button
                type="button"
                className="catalog-studio-top-chrome__icon-btn"
                title={tStudio.shortcutsTitle}
                aria-label={tStudio.shortcutHelp}
                onClick={() => setStudioShortcutsOpen(true)}
              >
                <HelpCircle size={18} strokeWidth={2.2} aria-hidden />
              </button>
              <button
                type="button"
                className="catalog-studio-top-chrome__icon-btn"
                title={tStudio.visitorPreview}
                aria-label={tStudio.visitorPreview}
                onClick={() => {
                  const slug = String(storeKey || userId || "").trim();
                  if (!slug) return;
                  window.open(`${window.location.origin}/catalog/${encodeURIComponent(slug)}`, "_blank", "noopener,noreferrer");
                }}
              >
                <Eye size={18} strokeWidth={2.2} aria-hidden />
              </button>
              <button
                type="button"
                className="catalog-studio-top-chrome__icon-btn catalog-studio-top-chrome__icon-btn--lang"
                onClick={() => {
                  const newLang = studioLang === "en" ? "ar" : "en";
                  runMobileLanguageDirectionTransition(
                    setLangSwitchOverlay,
                    () => {
                      setStudioLang(newLang);
                    },
                    { forceLoader: true }
                  );
                }}
                aria-label="Toggle studio language"
                title={`${studioLang === "en" ? "Switch panel language" : "تبديل لغة اللوحة"} — ${tStudio.studioPanelLangNote}`}
              >
                <Languages size={18} aria-hidden />
              </button>
              <button
                type="button"
                onClick={saveAllCustomizer}
                disabled={savingCustomizer}
                data-tour="customizer-save"
                className="catalog-studio-publish-btn"
              >
                <span>{savingCustomizer ? tStudio.saving : saveUiSuccess ? tStudio.saveSuccess : tStudio.publish}</span>
                <ChevronDown size={16} strokeWidth={2.2} aria-hidden />
              </button>
            </div>
          </div>
          {(savingCustomizer && saveProgressStep) || saveError ? (
            <div className="catalog-studio-top-chrome__status-row" dir={studioDir}>
              {savingCustomizer && saveProgressStep ? (
                <span className="catalog-studio-save-phase catalog-studio-save-phase--dark">
                  {saveProgressStep === "settings"
                    ? tStudio.saveProgressSettings
                    : saveProgressStep === "layouts"
                      ? tStudio.saveProgressLayouts
                      : tStudio.saveProgressBuilder}
                </span>
              ) : null}
              {saveError ? (
                <div className="catalog-studio-save-error-banner catalog-studio-save-error-banner--compact" role="alert">
                  {saveErrorStep ? (
                    <div style={{ fontWeight: 900, marginBottom: 4 }}>
                      {tStudio.saveFailedStepPrefix}{" "}
                      {saveErrorStep === "settings"
                        ? tStudio.stepSettingsName
                        : saveErrorStep === "layouts"
                          ? tStudio.stepLayoutsName
                          : tStudio.stepBuilderName}
                    </div>
                  ) : null}
                  <div>{saveError}</div>
                  {saveErrorStep ? <div style={{ marginTop: 6, fontSize: 11, color: "#7f1d1d" }}>{tStudio.partialSaveWarning}</div> : null}
                </div>
              ) : null}
            </div>
          ) : null}
          {studioThemePreview ? (
            <div
              className="catalog-studio-theme-preview-banner"
              dir={studioDir}
              role="status"
              aria-live="polite"
            >
              <span className="catalog-studio-theme-preview-banner__text">{tStudio.themePreviewBadge}</span>
              <div className="catalog-studio-theme-preview-banner__actions">
                <button type="button" className="catalog-studio-theme-preview-banner__btn catalog-studio-theme-preview-banner__btn--ghost" onClick={() => cancelStudioThemePreview()}>
                  {tStudio.themePreviewCancel}
                </button>
                <button
                  type="button"
                  className="catalog-studio-theme-preview-banner__btn catalog-studio-theme-preview-banner__btn--primary"
                  onClick={() => setStudioThemePreview(null)}
                >
                  {tStudio.themePreviewKeep}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {!canEdit ? renderShowcaseCartTopbar() : null}

      {canEdit && studioShortcutsOpen ? (
        <div
          className="catalog-studio-shortcuts-overlay"
          role="presentation"
          onClick={() => setStudioShortcutsOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10005,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            className="catalog-studio-shortcuts-modal"
            dir={studioDir}
            role="dialog"
            aria-modal="true"
            aria-labelledby="catalog-studio-shortcuts-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="catalog-studio-shortcuts-modal__head">
              <h2 id="catalog-studio-shortcuts-title" className="catalog-studio-shortcuts-modal__title">
                {tStudio.shortcutsTitle}
              </h2>
              <button type="button" className="catalog-studio-shortcuts-modal__close" onClick={() => setStudioShortcutsOpen(false)} aria-label={tStudio.shortcutsClose}>
                <X size={18} aria-hidden />
              </button>
            </div>
            <ul className="catalog-studio-shortcuts-modal__list">
              <li>
                <kbd className="catalog-studio-shortcuts-kbd">Ctrl / ⌘</kbd> + <kbd className="catalog-studio-shortcuts-kbd">S</kbd>
                <span>{tStudio.shortcutSave}</span>
              </li>
              <li>
                <kbd className="catalog-studio-shortcuts-kbd">Ctrl / ⌘</kbd> + <kbd className="catalog-studio-shortcuts-kbd">Z</kbd>
                <span>{tStudio.shortcutUndo}</span>
              </li>
              <li>
                <kbd className="catalog-studio-shortcuts-kbd">Ctrl / ⌘</kbd> + <kbd className="catalog-studio-shortcuts-kbd">Y</kbd>
                <span>{tStudio.shortcutRedo}</span>
              </li>
              <li>
                <kbd className="catalog-studio-shortcuts-kbd">Ctrl / ⌘</kbd> + <kbd className="catalog-studio-shortcuts-kbd">P</kbd>
                <span>{tStudio.shortcutPanel}</span>
              </li>
              <li>
                <kbd className="catalog-studio-shortcuts-kbd">?</kbd>
                <span>{tStudio.shortcutHelp}</span>
              </li>
            </ul>
            <button type="button" className="catalog-studio-shortcuts-modal__done" onClick={() => setStudioShortcutsOpen(false)}>
              {tStudio.shortcutsClose}
            </button>
          </div>
        </div>
      ) : null}

      {/* Customizer sidebar (WordPress-like) */}
      {canEdit && (
        <div
          onClick={() => setCustomizerOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: customizerOpen ? "rgba(0,0,0,0.25)" : "transparent",
            pointerEvents: customizerOpen ? "auto" : "none",
            zIndex: 10000,
          }}
        >
          <div
            id="catalog-customizer-panel"
            className={`catalog-studio-customizer-panel${!studioLayoutWide ? " catalog-studio-customizer-panel--sheet" : ""}${addPickerOpen ? " is-section-picker-open" : ""}`}
            onClick={(e) => e.stopPropagation()}
            lang={studioLang}
            dir={studioDir}
            style={studioCustomizerPanelStyle}
          >
            <div
              className="catalog-studio-panel-header catalog-studio-panel-header--elementor"
              data-tour="studio-customizer-panel-header"
              style={{ flexShrink: 0, zIndex: 2 }}
            >
              <div className="catalog-studio-panel-header__elementor-top">
                <h2
                  className="catalog-studio-panel-header__library-title"
                  data-tour="studio-panel-library-title"
                >
                  {customizerPane === "menu" ? tStudio.panelLibraryTitle : tStudio.panelTitle}
                </h2>
                <div className="catalog-studio-panel-header__elementor-actions">
                  <button
                    type="button"
                    className="catalog-studio-panel-header__collapse-tab"
                    aria-label={tStudio.collapsePanel}
                    title={tStudio.collapsePanel}
                    onClick={() => {
                      setAddPickerOpen(false);
                      setCustomizerOpen(false);
                    }}
                  >
                    {studioDir === "ltr" ? <ChevronLeft size={18} strokeWidth={2.2} aria-hidden /> : <ChevronRight size={18} strokeWidth={2.2} aria-hidden />}
                  </button>
                  <button
                    type="button"
                    className="catalog-studio-panel-header__close"
                    onClick={() => {
                      setAddPickerOpen(false);
                      setCustomizerOpen(false);
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
              {customizerPane === "menu" ? (
                <>
                  <div
                    className="catalog-studio-panel-tabs"
                    role="tablist"
                    aria-label={tStudio.panelLibraryTitle}
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={studioPanelLibraryTab === "widgets"}
                      className={`catalog-studio-panel-tabs__btn${studioPanelLibraryTab === "widgets" ? " is-active" : ""}`}
                      onClick={() => setStudioPanelLibraryTab("widgets")}
                    >
                      {tStudio.tabWidgets}
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={studioPanelLibraryTab === "globals"}
                      className={`catalog-studio-panel-tabs__btn${studioPanelLibraryTab === "globals" ? " is-active" : ""}`}
                      onClick={() => setStudioPanelLibraryTab("globals")}
                    >
                      {tStudio.tabGlobals}
                    </button>
                  </div>
                  <div className="catalog-studio-panel-header__search-wrap">
                    <Search size={16} aria-hidden className="catalog-studio-panel-header__search-icon" />
                    <input
                      ref={studioPanelSearchInputRef}
                      type="search"
                      className="catalog-studio-panel-header__search"
                      value={customizerMenuSearch}
                      onChange={(e) => setCustomizerMenuSearch(e.target.value)}
                      placeholder={tStudio.menuSearchPlaceholder}
                      aria-label={tStudio.menuSearchPlaceholder}
                    />
                  </div>
                  {!studioWidgetsHintDismissed ? (
                    <div className="catalog-studio-widgets-hint" role="note">
                      <p className="catalog-studio-widgets-hint__text">{tStudio.widgetsFavoritesHint}</p>
                      <button type="button" className="catalog-studio-widgets-hint__gotit" onClick={dismissStudioWidgetsHint}>
                        {tStudio.widgetsFavoritesGotIt}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="catalog-studio-panel-header__form-eyebrow">
                  <LayoutTemplate size={16} aria-hidden />
                  <span>{tStudio.panelTitle}</span>
                </div>
              )}
            </div>

            {localDraftBanner ? (
              <div
                className="catalog-studio-local-draft-banner"
                dir={studioDir}
                style={{
                  margin: "0 10px 10px",
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: "#eff6ff",
                  border: "1px solid rgba(59,130,246,0.28)",
                  fontSize: 12,
                  fontWeight: 750,
                  color: "#1e3a8a",
                }}
              >
                <div style={{ marginBottom: 8, lineHeight: 1.45 }}>{tStudio.draftLocalBanner}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => restoreLocalStudioDraft()}
                    style={{
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 12px",
                      fontWeight: 900,
                      background: "#1d4ed8",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    {tStudio.draftRestore}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLocalDraftBanner(false)}
                    style={{
                      border: "1px solid rgba(30,58,138,0.35)",
                      borderRadius: 10,
                      padding: "8px 12px",
                      fontWeight: 800,
                      background: "#fff",
                      color: "#1e3a8a",
                      cursor: "pointer",
                    }}
                  >
                    {tStudio.draftDismiss}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="catalog-studio-panel-body">
              {addPickerOpen ? (
                <div
                  className="catalog-studio-section-picker-overlay"
                  data-studio-section-picker="1"
                  role="dialog"
                  aria-modal="true"
                  aria-label={tStudio.addPickerTitle}
                >
                  <div style={{ fontSize: 15, fontWeight: 950, color: "#0f172a", marginBottom: 4 }}>{tStudio.addPickerTitle}</div>
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800, marginBottom: 12 }}>
                    {sectionsManagerZone === "after" ? tStudio.addPickerZoneAfter : tStudio.addPickerZoneBefore}
                  </div>
                  <div className="catalog-studio-section-picker-overlay__grid">
                    {BUILDER_SECTION_TYPE_OPTIONS.map((b) => (
                      <button
                        key={`${b.type}:${b.presetId || "default"}`}
                        type="button"
                        className="catalog-studio-section-picker-option"
                        onClick={() => {
                          const afterProducts = sectionsManagerZone === "after";
                          setAddPickerOpen(false);
                          setSectionsManagerOpen(false);
                          addSectionAt(b.type, sectionsInsertIndex, {
                            afterProducts,
                            presetId: b.presetId || "",
                          });
                        }}
                      >
                        <CatalogStudioSectionPickerSkeleton type={b.type} presetId={b.presetId} />
                        <span className="catalog-studio-section-picker-option__label">+ {b.label}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="catalog-studio-section-picker-overlay__close"
                    onClick={() => {
                      setAddPickerOpen(false);
                      setSectionsManagerOpen(false);
                    }}
                  >
                    {tStudio.close}
                  </button>
                </div>
              ) : null}

            <div
              className="catalog-studio-panel-stack"
              style={{
                padding: "10px 10px 12px",
                flex: customizerPane === "menu" ? "1 1 auto" : "0 0 auto",
                minHeight: customizerPane === "menu" ? 0 : undefined,
                overflowY: customizerPane === "menu" ? "auto" : "visible",
                overflowX: "hidden",
              }}
            >
              {customizerPane === "form" ? (
                <div className="catalog-studio-form-head">
                  <button
                    type="button"
                    className="catalog-studio-form-head__back"
                    onClick={() => {
                      setCustomizerPane("menu");
                      setCustomizerFormTarget(null);
                      setSectionsMenuOpen(null);
                    }}
                  >
                    {studioDir === "rtl" ? (
                      <ChevronRight size={18} aria-hidden />
                    ) : (
                      <ChevronLeft size={18} aria-hidden />
                    )}
                    {tStudio.back}
                  </button>
                  <div className="catalog-studio-form-head__body">
                    <div className="catalog-studio-form-head__breadcrumb" aria-label={tStudio.breadcrumbCustomize}>
                      <span className="catalog-studio-form-head__crumb-muted">{tStudio.breadcrumbCustomize}</span>
                      <span className="catalog-studio-form-head__crumb-sep" aria-hidden="true">
                        /
                      </span>
                      <span className="catalog-studio-form-head__crumb-active">
                        {{
                          hero: tStudio.hero,
                          look: tStudio.lookAndFeel,
                          cart: tStudio.cartCustomizerTitle,
                          products: tStudio.products,
                          footer: tStudio.footerContact,
                          navbar: tStudio.navbar,
                          sections: tStudio.customSection,
                        }[customizerTab] || tStudio.panelTitle}
                      </span>
                    </div>
                    <div className="catalog-studio-form-head__title">
                      {{
                        hero: tStudio.hero,
                        look: tStudio.lookAndFeel,
                        cart: tStudio.cartCustomizerTitle,
                        products: tStudio.products,
                        footer: tStudio.footerContact,
                        navbar: tStudio.navbar,
                        sections: tStudio.customSection,
                      }[customizerTab] || tStudio.panelTitle}
                    </div>
                  </div>
                </div>
              ) : null}

              {customizerPane === "menu" ? (
                <div
                  className="catalog-studio-menu-grid"
                  data-tour="studio-menu-grid"
                  style={{
                    marginTop: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    maxHeight: "none",
                    overflowY: "visible",
                    paddingRight: 2,
                  }}
                >
                  {(() => {
                    const q = customizerMenuSearch.trim().toLowerCase();
                    const defs = [
                      { key: "builder", label: tStudio.addedSections, subtitle: tStudio.menuSubBuilder, icon: LayoutGrid },
                      { key: "hero", label: tStudio.hero, subtitle: tStudio.menuSubHero, icon: Palette },
                      { key: "navbar", label: tStudio.navbar, subtitle: tStudio.menuSubNavbar, icon: Menu },
                      { key: "products", label: tStudio.products, subtitle: tStudio.menuSubProducts, icon: Tag },
                      { key: "footer", label: tStudio.footer, subtitle: tStudio.menuSubFooter, icon: PanelBottom },
                      { key: "themes", label: tStudio.themes, subtitle: tStudio.menuSubThemes, icon: Brush },
                      { key: "look", label: tStudio.lookAndFeel, subtitle: tStudio.menuSubLook, icon: LayoutTemplate, opensForm: true },
                      { key: "cart", label: tStudio.cartCustomizerTitle, subtitle: tStudio.menuSubCart, icon: ShoppingCart, opensForm: true },
                    ];
                    const libFiltered =
                      studioPanelLibraryTab === "globals"
                        ? defs.filter((r) => ["look", "cart", "footer", "themes"].includes(r.key))
                        : defs.filter((r) => ["builder", "hero", "navbar", "products"].includes(r.key));
                    const list = !q ? libFiltered : libFiltered.filter((r) => `${r.label} ${r.subtitle}`.toLowerCase().includes(q));
                    if (!list.length) {
                      return (
                        <div className="catalog-studio-menu-empty" style={{ fontSize: 13, color: "#64748b", padding: "12px 8px", fontWeight: 700 }}>
                          {tStudio.noData}
                        </div>
                      );
                    }
                    return list.map((row) => {
                      const Icon = row.icon || LayoutGrid;
                      return (
                        <div key={row.key} className="catalog-studio-menu-card">
                          <button
                            type="button"
                            data-tour={`studio-menu-${row.key}`}
                            className={`catalog-studio-menu-card__main${sectionsMenuOpen === row.key ? " is-open" : ""}${row.opensForm ? " catalog-studio-menu-card__main--action" : ""}`}
                            onClick={() => {
                              if (row.opensForm) {
                                const tab = row.key === "cart" ? "cart" : "look";
                                setCustomizerTab(tab);
                                setCustomizerPane("form");
                                setCustomizerFormTarget({ kind: tab, key: "all" });
                                setSectionsMenuOpen(null);
                                return;
                              }
                              if (row.key === "builder" || row.key === "themes") {
                                setSectionsMenuOpen((p) => (p === row.key ? null : row.key));
                                return;
                              }
                              if (row.key === "hero" || row.key === "products" || row.key === "footer" || row.key === "navbar") {
                                const map = {
                                  hero: "catalog-hero",
                                  products: "catalog-products-anchor",
                                  footer: "catalog-footer",
                                  navbar: null,
                                };
                                const id = map[row.key];
                                if (id) {
                                  const el = document.getElementById(id);
                                  if (el && typeof el.scrollIntoView === "function") {
                                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                                  }
                                }
                                setCustomizerTab(row.key === "products" ? "products" : row.key);
                                setActiveKey(row.key);
                                setCustomizerPane("form");
                                setCustomizerFormTarget({
                                  kind: "core",
                                  key: row.key === "products" ? "products" : row.key,
                                });
                                setSectionsMenuOpen(null);
                              }
                            }}
                          >
                            <span className="catalog-studio-menu-card__icon" aria-hidden>
                              <Icon size={20} strokeWidth={2} />
                            </span>
                            <span className="catalog-studio-menu-card__text">
                              <span className="catalog-studio-menu-card__label">{row.label}</span>
                              <span className="catalog-studio-menu-card__sub">{row.subtitle}</span>
                            </span>
                            <span className="catalog-studio-menu-card__chevron" aria-hidden>
                              {row.opensForm ? (
                                studioDir === "rtl" ? (
                                  <ChevronLeft size={18} />
                                ) : (
                                  <ChevronRight size={18} />
                                )
                              ) : sectionsMenuOpen === row.key ? (
                                "−"
                              ) : (
                                "+"
                              )}
                            </span>
                          </button>

                      {sectionsMenuOpen === row.key && row.key === "themes" ? (
                        <div className="catalog-studio-acc-panel">
                          {catalogThemesLoading ? (
                            <div className="catalog-studio-field-hint" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <Loader2 size={16} className="catalog-spin" aria-hidden />
                              {tStudio.catalogThemesLoading}
                            </div>
                          ) : null}
                          {catalogThemesError ? (
                            <div className="catalog-studio-field-hint" style={{ color: "#b91c1c", marginBottom: 8 }}>
                              {tStudio.catalogThemesLoadError}: {catalogThemesError}
                            </div>
                          ) : null}
                          {!catalogThemesLoading && !catalogThemePickerOptions.length && !catalogThemesError ? (
                            <div className="catalog-studio-field-hint" style={{ marginBottom: 8 }}>
                              {tStudio.catalogThemesEmpty}
                            </div>
                          ) : null}
                          <StudioCardPicker
                            value={studioThemePickerValue}
                            disabled={catalogThemesLoading}
                            onChange={(v) => {
                              const next = String(v || "");
                              const cur = String(studioThemePickerValue || "");
                              if (next === cur) return;
                              setPendingThemePresetKey(next);
                              setThemePresetConfirmOpen(true);
                            }}
                            onPreview={(v) => beginStudioThemePreview(v)}
                            previewLabel={tStudio.themePreviewVerb}
                            label={tStudio.themePickerLabel}
                            hint={tStudio.themePickerHint}
                            columns={1}
                            options={studioThemePickerOptions}
                          />
                        </div>
                      ) : null}

                      {sectionsMenuOpen === row.key && row.key === "builder" ? (
                        <div className="catalog-studio-acc-panel catalog-studio-acc-panel--nested">
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {(() => {
                                const shown = canEdit ? (builderDraft || []) : (builderPage.sections || []);
                                const { before, after } = partitionBuilderSections(shown);
                                const restaurantBuilder = catalogVisualTheme === "restaurant";
                                const items = restaurantBuilder
                                  ? [
                                      { kind: "main_products" },
                                      ...shown.map((s) => ({ kind: "builder", id: s.id, sec: s })),
                                    ]
                                  : [
                                      ...before.map((s) => ({ kind: "builder", id: s.id, sec: s })),
                                      { kind: "main_products" },
                                      ...after.map((s) => ({ kind: "builder", id: s.id, sec: s })),
                                    ];

                                const flatShown = items.map((it) => (it.kind === "main_products" ? { kind: "main_products" } : { kind: "builder", id: it.id }));
                                const applyFlatOrder = (fromIdx, toIdx) => {
                                  if (!canEdit) return;
                                  if (restaurantBuilder) {
                                    if (fromIdx === 0 || toIdx === 0) return;
                                    const from = fromIdx - 1;
                                    const to = toIdx - 1;
                                    if (from < 0 || to < 0 || from >= shown.length || to >= shown.length) return;
                                    const next = [...shown];
                                    const [moved] = next.splice(from, 1);
                                    next.splice(to, 0, moved);
                                    setBuilderDraft(
                                      next.map((s) => ({
                                        ...s,
                                        settings: { ...(s.settings || {}), after_products: true },
                                      }))
                                    );
                                    return;
                                  }
                                  const nextFlat = reorderWithMainProductsBoundary(flatShown, fromIdx, toIdx);
                                  const nextBefore = [];
                                  const nextAfter = [];
                                  let seenMain = false;

                                  nextFlat.forEach((x) => {
                                    if (x.kind === "main_products") {
                                      seenMain = true;
                                      return;
                                    }
                                    const sec = shown.find((s) => s.id === x.id);
                                    if (!sec) return;
                                    const patched = { ...sec, settings: { ...(sec.settings || {}), after_products: seenMain } };
                                    if (seenMain) nextAfter.push(patched);
                                    else nextBefore.push(patched);
                                  });
                                  setBuilderDraft(mergeBuilderSections(nextBefore, nextAfter));
                                };

                                const onDropAt = (toIdx) => {
                                  if (dragOrderFrom == null) return;
                                  const fromIdx = dragOrderFrom;
                                  setDragOrderFrom(null);
                                  applyFlatOrder(fromIdx, toIdx);
                                };

                                return items.length ? (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {canEdit ? (
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                        <button
                                          type="button"
                                          onClick={() => openSectionTypePicker({ zone: "before", index: 0 })}
                                          style={{
                                            flex: 1,
                                            border: "1px solid rgba(15,23,42,0.12)",
                                            background: "#111827",
                                            color: "#fff",
                                            borderRadius: 12,
                                            padding: "10px 12px",
                                            fontWeight: 950,
                                          }}
                                        >
                                          {tStudio.addNewSection}
                                        </button>
                                      </div>
                                    ) : null}
                                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>
                                      {tStudio.reorderHintShort}
                                    </div>
                                    {items.map((it, idx) => {
                                      if (it.kind === "main_products") {
                                        const moveMainUp = () => {
                                          if (!canEdit || restaurantBuilder) return;
                                          if (!after.length) return;
                                          const firstAfter = after[0];
                                          setBuilderDraft(mergeBuilderSections([...before, { ...firstAfter, settings: { ...(firstAfter.settings || {}), after_products: false } }], after.slice(1)));
                                        };
                                        const moveMainDown = () => {
                                          if (!canEdit || restaurantBuilder) return;
                                          if (!before.length) return;
                                          const lastBefore = before[before.length - 1];
                                          setBuilderDraft(mergeBuilderSections(before.slice(0, -1), [{ ...lastBefore, settings: { ...(lastBefore.settings || {}), after_products: true } }, ...after]));
                                        };
                                        return (
                                          <div
                                            key="__main_products__"
                                            draggable={Boolean(canEdit && !restaurantBuilder)}
                                            onDragStart={() => { if (!restaurantBuilder) setDragOrderFrom(idx); }}
                                            onDragOver={(e) => { if (!canEdit || restaurantBuilder) return; e.preventDefault(); }}
                                            onDrop={() => { if (!restaurantBuilder) onDropAt(idx); }}
                                            style={{
                                              width: "100%",
                                              border: "1px dashed rgba(15,23,42,0.25)",
                                              background: "#fff",
                                              borderRadius: 12,
                                              padding: "10px 10px",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              gap: 10,
                                            }}
                                          >
                                            <div style={{ fontWeight: 950, fontSize: 13 }}>{tStudio.mainProductsBlock}</div>
                                            {!restaurantBuilder ? (
                                              <div style={{ display: "flex", gap: 6 }}>
                                                <button type="button" onClick={moveMainUp} disabled={!canEdit || !after.length} style={{ width: 36, height: 36, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)", background: "#fff", opacity: canEdit && after.length ? 1 : 0.4 }} aria-label="تحريك للأعلى">
                                                  ↑
                                                </button>
                                                <button type="button" onClick={moveMainDown} disabled={!canEdit || !before.length} style={{ width: 36, height: 36, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)", background: "#fff", opacity: canEdit && before.length ? 1 : 0.4 }} aria-label="تحريك للأسفل">
                                                  ↓
                                                </button>
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      }

                                      const sec = it.sec;
                                      const label = getCatalogSectionNavLabel(sec, tStudio, studioLang);

                                      return (
                                        <div
                                          key={sec.id}
                                          draggable={Boolean(canEdit)}
                                          onDragStart={() => setDragOrderFrom(idx)}
                                          onDragOver={(e) => { if (!canEdit) return; e.preventDefault(); }}
                                          onDrop={() => onDropAt(idx)}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            background: "#fff",
                                            border: "1px solid rgba(15,23,42,0.12)",
                                            borderRadius: 12,
                                            padding: "8px 8px",
                                          }}
                                        >
                                          <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ fontWeight: 950, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
                                            <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                              {(sec.settings?.subtitle || "").toString().trim()}
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => moveSection(sec.id, "up")}
                                            disabled={!canEdit}
                                            style={{ border: "1px solid rgba(15,23,42,0.12)", background: "#fff", borderRadius: 10, width: 36, height: 36, opacity: canEdit ? 1 : 0.4 }}
                                            aria-label="تحريك للأعلى"
                                            title="تحريك للأعلى"
                                          >
                                            ↑
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => moveSection(sec.id, "down")}
                                            disabled={!canEdit}
                                            style={{ border: "1px solid rgba(15,23,42,0.12)", background: "#fff", borderRadius: 10, width: 36, height: 36, opacity: canEdit ? 1 : 0.4 }}
                                            aria-label="تحريك للأسفل"
                                            title="تحريك للأسفل"
                                          >
                                            ↓
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setCustomizerTab("sections");
                                              setActiveKey(`sec:${sec.id}`);
                                              setCustomizerPane("form");
                                              setCustomizerFormTarget({ kind: "builder", id: sec.id });
                                              const el = document.getElementById(`builder-sec-${sec.id}`);
                                              if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
                                            }}
                                            style={{ border: "1px solid rgba(15,23,42,0.12)", background: "#fff", borderRadius: 10, width: 36, height: 36, fontWeight: 950 }}
                                            aria-label="تعديل"
                                            title="تعديل"
                                          >
                                            <Pencil size={14} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => deleteSection(sec.id)}
                                            style={{ border: "1px solid rgba(239,68,68,0.35)", background: "#fff", color: "#b91c1c", borderRadius: 10, width: 36, height: 36 }}
                                            aria-label="حذف"
                                            title="حذف"
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>{tStudio.noAddedSections}</div>
                                );
                              })()}
                            </div>
                        </div>
                      ) : null}
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : null}
            </div>

            <div
              className="catalog-checkout-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="catalog-studio-panel-scroll"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  padding: "12px 12px 16px",
                  pointerEvents: addPickerOpen ? "none" : "auto",
                  opacity: addPickerOpen ? 0.35 : 1,
                }}
              >
            {customizerPane === "form" && customizerTab === "hero" && (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontWeight: 950 }}>{tStudio.heroSettings}</div>
                <StudioSwitch
                  checked={Boolean(layoutsEdit.show_top_hero)}
                  onChange={(v) => setLayoutsEdit((p) => ({ ...p, show_top_hero: v, show_hero: v }))}
                  label={tStudio.showHero}
                />

                <div style={{ marginTop: 4, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#334155" }}>{tStudio.minimalTopbarGroup}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.45 }}>{tStudio.minimalTopbarHint}</div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.minimalTopbarAuthDisplay}</div>
                    <select
                      value={String(catalogLayoutEffective.minimal_topbar_auth_display || "buttons")}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            minimal_topbar_auth_display: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    >
                      <option value="buttons">{tStudio.minimalTopbarAuthButtons}</option>
                      <option value="icon">{tStudio.minimalTopbarAuthIcon}</option>
                    </select>
                  </div>
                  <div style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.minimalTopbarBg}</div>
                        <input
                          type="color"
                          value={catalogLayoutEffective.minimal_topbar_bg || "#ffffff"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                minimal_topbar_bg: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.minimalTopbarStoreNameColor}</div>
                        <input
                          type="color"
                          value={catalogLayoutEffective.minimal_topbar_store_name_color || "#111827"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                minimal_topbar_store_name_color: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                    </div>

                    <div style={{ borderTop: "1px dashed #e2e8f0", paddingTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#475569" }}>{tStudio.minimalTopbarLoginBlock}</div>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.minimalTopbarButtonLabel}</div>
                        <input
                          value={catalogLayoutEffective.minimal_topbar_login_label || ""}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                minimal_topbar_login_label: e.target.value,
                              },
                            }))
                          }
                          placeholder={studioLang === "en" ? "Log in" : "تسجيل الدخول"}
                          style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tStudio.backgroundColor}</div>
                          <input
                            type="color"
                            value={catalogLayoutEffective.minimal_topbar_login_bg || "#ffffff"}
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  minimal_topbar_login_bg: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", height: 38, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tStudio.textColor}</div>
                          <input
                            type="color"
                            value={catalogLayoutEffective.minimal_topbar_login_text || catalogHeaderAuthAccent}
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  minimal_topbar_login_text: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", height: 38, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tStudio.minimalTopbarBorderColor}</div>
                          <input
                            type="color"
                            value={catalogLayoutEffective.minimal_topbar_login_border || catalogHeaderAuthAccent}
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  minimal_topbar_login_border: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", height: 38, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ borderTop: "1px dashed #e2e8f0", paddingTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#475569" }}>{tStudio.minimalTopbarRegisterBlock}</div>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.minimalTopbarButtonLabel}</div>
                        <input
                          value={catalogLayoutEffective.minimal_topbar_register_label || ""}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                minimal_topbar_register_label: e.target.value,
                              },
                            }))
                          }
                          placeholder={studioLang === "en" ? "Sign up" : "إنشاء حساب"}
                          style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tStudio.backgroundColor}</div>
                          <input
                            type="color"
                            value={catalogLayoutEffective.minimal_topbar_register_bg || catalogHeaderAuthAccent}
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  minimal_topbar_register_bg: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", height: 38, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tStudio.textColor}</div>
                          <input
                            type="color"
                            value={catalogLayoutEffective.minimal_topbar_register_text || "#ffffff"}
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  minimal_topbar_register_text: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", height: 38, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tStudio.minimalTopbarBorderColor}</div>
                          <input
                            type="color"
                            value={catalogLayoutEffective.minimal_topbar_register_border || catalogHeaderAuthAccent}
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  minimal_topbar_register_border: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", height: 38, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 4, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10, color: "#334155" }}>{tStudio.heroContentGroup}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.title}</div>
                    <input value={storeEdit.headerTitle} onChange={(e) => setStoreEdit((p) => ({ ...p, headerTitle: e.target.value }))} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.description}</div>
                    <textarea value={storeEdit.subheaderText} onChange={(e) => setStoreEdit((p) => ({ ...p, subheaderText: e.target.value }))} rows={4} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", resize: "vertical" }} />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{studioLang === "en" ? "Hero title color" : "لون عنوان الهيرو"}</div>
                    <input
                      type="color"
                      value={(layoutsEdit.catalog_layout_extras || {}).hero_title_color || "#ffffff"}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            hero_title_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.heroDescriptionColor}</div>
                    <input
                      type="color"
                      value={(layoutsEdit.catalog_layout_extras || {}).hero_subtitle_color || "#ffffff"}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            hero_subtitle_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.45 }}>
                      {studioLang === "en"
                        ? "Applies to the hero paragraph under the headline. The features bar has its own description color under «Bar colors»."
                        : "يُطبَّق على فقرة وصف الهيرو تحت العنوان. شريط المميزات له لون وصف منفصل ضمن «ألوان الشريط»."}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 4, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10, color: "#334155" }}>
                    {studioLang === "en" ? "Hero layout" : "تخطيط الهيرو"}
                  </div>
                  <select
                    value={String((layoutsEdit.catalog_layout_extras || {}).hero_layout || "")}
                    onChange={(e) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          hero_layout: e.target.value,
                        },
                      }))
                    }
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 10 }}
                  >
                    <option value="">{studioLang === "en" ? "Full background image" : "صورة خلفية كاملة"}</option>
                    <option value="split_circle">{studioLang === "en" ? "Text + circle image (menu)" : "نص + صورة دائرية (منيو)"}</option>
                  </select>
                  {String((layoutsEdit.catalog_layout_extras || {}).hero_layout || "") === "split_circle" ? (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                        {studioLang === "en" ? "Circle dish image" : "صورة الطبق الدائرية"}
                      </div>
                      <input
                        value={String((layoutsEdit.catalog_layout_extras || {}).hero_circle_image_url || "")}
                        onChange={(e) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              hero_circle_image_url: e.target.value,
                            },
                          }))
                        }
                        placeholder={studioLang === "en" ? "Leave empty to use hero image" : "فارغ = نفس صورة الهيرو"}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 10 }}
                      />
                    </>
                  ) : null}
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, marginTop: 4 }}>
                    {studioLang === "en" ? "Hero height" : "ارتفاع الهيرو"}
                  </div>
                  <select
                    value={String((layoutsEdit.catalog_layout_extras || {}).hero_height_mode || "")}
                    onChange={(e) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          hero_height_mode: e.target.value,
                        },
                      }))
                    }
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 10 }}
                  >
                    <option value="">{studioLang === "en" ? "Standard (66vh)" : "عادي (66% من الشاشة)"}</option>
                    <option value="compact">{studioLang === "en" ? "Compact" : "مضغوط"}</option>
                    <option value="tall">{studioLang === "en" ? "Tall" : "طويل"}</option>
                    <option value="full">{studioLang === "en" ? "Full screen" : "ملء الشاشة"}</option>
                    <option value="custom">{studioLang === "en" ? "Custom (px)" : "مخصص (بكسل)"}</option>
                  </select>
                  {String((layoutsEdit.catalog_layout_extras || {}).hero_height_mode || "") === "custom" ? (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                        {studioLang === "en" ? "Height in pixels" : "الارتفاع بالبكسل"}
                      </div>
                      <input
                        type="number"
                        min={280}
                        max={1200}
                        step={10}
                        value={String((layoutsEdit.catalog_layout_extras || {}).hero_height_px ?? "")}
                        onChange={(e) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              hero_height_px: e.target.value,
                            },
                          }))
                        }
                        placeholder={studioLang === "en" ? "e.g. 520" : "مثال: 520"}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 10 }}
                      />
                    </>
                  ) : null}
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                    {studioLang === "en" ? "Text alignment" : "محاذاة النص"}
                  </div>
                  <select
                    value={String((layoutsEdit.catalog_layout_extras || {}).hero_content_align || "")}
                    onChange={(e) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          hero_content_align: e.target.value,
                        },
                      }))
                    }
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 10 }}
                  >
                    <option value="">{studioLang === "en" ? "Auto" : "تلقائي"}</option>
                    <option value="start">{studioLang === "en" ? "Start (right in RTL)" : "بداية (يمين في RTL)"}</option>
                    <option value="center">{studioLang === "en" ? "Center" : "وسط"}</option>
                  </select>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                    {studioLang === "en" ? "Hero button label" : "نص زر الهيرو"}
                  </div>
                  <input
                    value={String((layoutsEdit.catalog_layout_extras || {}).hero_cta_label || "")}
                    onChange={(e) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          hero_cta_label: e.target.value,
                        },
                      }))
                    }
                    placeholder={studioLang === "en" ? "Order now" : "اطلب الآن"}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 10 }}
                  />
                  {(() => {
                    const ex = layoutsEdit.catalog_layout_extras || {};
                    const en = studioLang === "en";
                    const setHeroCta = (patch) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          ...patch,
                        },
                      }));
                    const labelStyle = { fontSize: 12, fontWeight: 900, marginBottom: 6 };
                    const fieldStyle = { width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 10 };
                    const chipStyle = (active) => ({
                      flex: 1,
                      padding: "8px 6px",
                      fontSize: 12,
                      fontWeight: 800,
                      borderRadius: 10,
                      cursor: "pointer",
                      border: active ? "1px solid #0f172a" : "1px solid rgba(15,23,42,0.12)",
                      background: active ? "#0f172a" : "#fff",
                      color: active ? "#fff" : "#0f172a",
                    });
                    const resetBtnStyle = {
                      fontSize: 11,
                      fontWeight: 800,
                      border: "1px solid rgba(15,23,42,0.12)",
                      background: "#fff",
                      borderRadius: 8,
                      padding: "4px 8px",
                      cursor: "pointer",
                    };
                    const colorField = (key, label, fallback) => {
                      const val = String(ex[key] || "").trim();
                      return (
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ ...labelStyle, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                            <span>{label}</span>
                            {val ? (
                              <button type="button" style={resetBtnStyle} onClick={() => setHeroCta({ [key]: "" })}>
                                {en ? "Reset" : "افتراضي"}
                              </button>
                            ) : null}
                          </div>
                          <input
                            type="color"
                            value={val || fallback}
                            onChange={(e) => setHeroCta({ [key]: e.target.value })}
                            style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 10 }}
                          />
                        </div>
                      );
                    };
                    const isCustomPos = String(ex.hero_cta_position || "").trim().toLowerCase() === "custom";
                    const numOr = (v, d) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? d : Number(v));
                    const deskX = numOr(ex.hero_cta_pos_x, 50);
                    const deskY = numOr(ex.hero_cta_pos_y, 75);
                    const editMobilePos = studioPreviewFrame === "mobile" || studioPreviewFrame === "tablet";
                    const hasMobilePos =
                      numOr(ex.hero_cta_mobile_pos_x, null) !== null || numOr(ex.hero_cta_mobile_pos_y, null) !== null;
                    const posX = editMobilePos ? numOr(ex.hero_cta_mobile_pos_x, deskX) : deskX;
                    const posY = editMobilePos ? numOr(ex.hero_cta_mobile_pos_y, deskY) : deskY;
                    const keyX = editMobilePos ? "hero_cta_mobile_pos_x" : "hero_cta_pos_x";
                    const keyY = editMobilePos ? "hero_cta_mobile_pos_y" : "hero_cta_pos_y";
                    const mobileScale = numOr(ex.hero_cta_mobile_scale, 0.72);
                    const radiusVal = ex.hero_cta_radius === null || ex.hero_cta_radius === undefined || ex.hero_cta_radius === "" ? null : Number(ex.hero_cta_radius);
                    return (
                      <div style={{ marginTop: 4, borderTop: "1px solid #eef2f7", paddingTop: 12, marginBottom: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10, color: "#334155" }}>
                          {en ? "Hero button style" : "تصميم زر الهيرو"}
                        </div>

                        <div style={labelStyle}>{en ? "Show button" : "إظهار الزر"}</div>
                        <select
                          value={String(ex.hero_cta_visibility || "")}
                          onChange={(e) => setHeroCta({ hero_cta_visibility: e.target.value })}
                          style={fieldStyle}
                        >
                          <option value="">{en ? "Auto (when title/description exist)" : "تلقائي (عند وجود عنوان أو وصف)"}</option>
                          <option value="always">{en ? "Always (even on image-only hero)" : "دائماً (حتى لو الهيرو صورة فقط)"}</option>
                          <option value="hidden">{en ? "Hidden" : "إخفاء"}</option>
                        </select>
                        {String(ex.hero_cta_visibility || "") !== "hidden" ? (
                          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800, margin: "-2px 0 12px", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={ex.hero_cta_hide_mobile === true || String(ex.hero_cta_hide_mobile) === "true"}
                              onChange={(e) => setHeroCta({ hero_cta_hide_mobile: e.target.checked })}
                            />
                            {en ? "Hide button on mobile" : "إخفاء الزر على الجوال"}
                          </label>
                        ) : null}

                        <div style={{ display: "flex", gap: 10 }}>
                          {colorField("hero_cta_bg_color", en ? "Button background" : "خلفية الزر", "#ffffff")}
                          {colorField("hero_cta_text_color", en ? "Text color" : "لون النص", "#111827")}
                        </div>
                        {colorField("hero_cta_border_color", en ? "Border color (optional)" : "لون الإطار (اختياري)", "#111827")}

                        <div style={labelStyle}>{en ? "Button size" : "حجم الزر"}</div>
                        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                          {[
                            ["sm", en ? "Small" : "صغير"],
                            ["", en ? "Medium" : "متوسط"],
                            ["lg", en ? "Large" : "كبير"],
                            ["xl", en ? "X-Large" : "كبير جداً"],
                          ].map(([v, l]) => (
                            <button
                              key={v || "md"}
                              type="button"
                              style={chipStyle(String(ex.hero_cta_size || "") === v)}
                              onClick={() => setHeroCta({ hero_cta_size: v })}
                            >
                              {l}
                            </button>
                          ))}
                        </div>

                        <div style={{ ...labelStyle, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span>
                            {en ? "Corner radius" : "استدارة الزوايا"}: {radiusVal === null ? (en ? "Default" : "افتراضي") : `${radiusVal}px`}
                          </span>
                          {radiusVal !== null ? (
                            <button type="button" style={resetBtnStyle} onClick={() => setHeroCta({ hero_cta_radius: null })}>
                              {en ? "Reset" : "افتراضي"}
                            </button>
                          ) : null}
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={40}
                          step={1}
                          value={radiusVal === null ? 4 : radiusVal}
                          onChange={(e) => setHeroCta({ hero_cta_radius: Number(e.target.value) })}
                          style={{ width: "100%", marginBottom: 10 }}
                        />

                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800, marginBottom: 12, cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(ex.hero_cta_shadow)}
                            onChange={(e) => setHeroCta({ hero_cta_shadow: e.target.checked })}
                          />
                          {en ? "Drop shadow" : "ظل للزر"}
                        </label>

                        <div style={labelStyle}>{en ? "Button position" : "موقع الزر"}</div>
                        <select
                          value={isCustomPos ? "custom" : ""}
                          onChange={(e) => setHeroCta({ hero_cta_position: e.target.value })}
                          style={fieldStyle}
                        >
                          <option value="">{en ? "Under the hero text" : "تحت نص الهيرو"}</option>
                          <option value="custom">{en ? "Free position on the image" : "موضع حر فوق الصورة"}</option>
                        </select>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: -4, marginBottom: 10, lineHeight: 1.45 }}>
                          {en
                            ? "If the hero has no title/description, the button is placed freely on the image."
                            : "إذا كان الهيرو بدون عنوان أو وصف، يُوضع الزر بموضع حر فوق الصورة تلقائياً."}
                        </div>

                        {!isCustomPos ? (
                          <>
                            <div style={labelStyle}>{en ? "Horizontal alignment" : "المحاذاة الأفقية"}</div>
                            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                              {[
                                ["", en ? "Auto" : "تلقائي"],
                                ["right", en ? "Right" : "يمين"],
                                ["center", en ? "Center" : "وسط"],
                                ["left", en ? "Left" : "يسار"],
                              ].map(([v, l]) => (
                                <button
                                  key={v || "auto"}
                                  type="button"
                                  style={chipStyle(String(ex.hero_cta_align || "") === v)}
                                  onClick={() => setHeroCta({ hero_cta_align: v })}
                                >
                                  {l}
                                </button>
                              ))}
                            </div>
                          </>
                        ) : null}

                        {isCustomPos || !String(storeEdit.headerTitle || "").trim() && !String(storeEdit.subheaderText || "").trim() ? (
                          <>
                            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                              <button type="button" style={chipStyle(!editMobilePos)} onClick={() => setStudioPreviewFrame("desktop")}>
                                {en ? "Desktop position" : "موضع اللابتوب"}
                              </button>
                              <button type="button" style={chipStyle(editMobilePos)} onClick={() => setStudioPreviewFrame("mobile")}>
                                {en ? "Mobile position" : "موضع الجوال"}
                              </button>
                            </div>
                            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10, lineHeight: 1.45, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                              <span>
                                {editMobilePos
                                  ? hasMobilePos
                                    ? en ? "Editing a separate position for mobile/tablet." : "تعدّل موضعاً خاصاً بالجوال والتابلت."
                                    : en ? "Mobile follows desktop — move it to set its own position." : "الجوال يتبع موضع اللابتوب — حرّكه ليصير له موضع خاص."
                                  : en ? "Editing the desktop position." : "تعدّل موضع اللابتوب."}
                              </span>
                              {editMobilePos && hasMobilePos ? (
                                <button
                                  type="button"
                                  style={resetBtnStyle}
                                  onClick={() => setHeroCta({ hero_cta_mobile_pos_x: "", hero_cta_mobile_pos_y: "" })}
                                >
                                  {en ? "Same as desktop" : "مثل اللابتوب"}
                                </button>
                              ) : null}
                            </div>
                            <div style={labelStyle}>{en ? "Horizontal" : "أفقياً"}</div>
                            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                              {[
                                [85, en ? "Right" : "يمين"],
                                [50, en ? "Center" : "وسط"],
                                [15, en ? "Left" : "يسار"],
                              ].map(([v, l]) => (
                                <button key={v} type="button" style={chipStyle(posX === v)} onClick={() => setHeroCta({ [keyX]: v })}>
                                  {l}
                                </button>
                              ))}
                            </div>
                            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                              {en ? "From left" : "من اليسار"}: {posX}%
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={1}
                              value={posX}
                              onChange={(e) => setHeroCta({ [keyX]: Number(e.target.value) })}
                              style={{ width: "100%", marginBottom: 10 }}
                            />

                            <div style={labelStyle}>{en ? "Vertical" : "عمودياً"}</div>
                            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                              {[
                                [20, en ? "Top" : "أعلى"],
                                [50, en ? "Middle" : "وسط"],
                                [80, en ? "Bottom" : "أسفل"],
                              ].map(([v, l]) => (
                                <button key={v} type="button" style={chipStyle(posY === v)} onClick={() => setHeroCta({ [keyY]: v })}>
                                  {l}
                                </button>
                              ))}
                            </div>
                            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                              {en ? "From top" : "من الأعلى"}: {posY}%
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={1}
                              value={posY}
                              onChange={(e) => setHeroCta({ [keyY]: Number(e.target.value) })}
                              style={{ width: "100%", marginBottom: 10 }}
                            />
                            {editMobilePos ? (
                              <>
                                <div style={labelStyle}>
                                  {en ? "Button size on mobile" : "حجم الزر على الجوال"}: {Math.round(mobileScale * 100)}%
                                </div>
                                <input
                                  type="range"
                                  min={40}
                                  max={120}
                                  step={2}
                                  value={Math.round(mobileScale * 100)}
                                  onChange={(e) => setHeroCta({ hero_cta_mobile_scale: Number(e.target.value) / 100 })}
                                  style={{ width: "100%", marginBottom: 10 }}
                                />
                              </>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>

                <div style={{ marginTop: 4, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10, color: "#334155" }}>{tStudio.heroLookGroup}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.heroPrimaryColor}</div>
                    <input
                      type="color"
                      value={
                        String((layoutsEdit.catalog_layout_extras || {}).hero_primary_color || "").trim() ||
                        storeEdit.primaryColor ||
                        "#4f46e5"
                      }
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            hero_primary_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.45 }}>{tStudio.heroPrimaryColorHint}</div>
                    {String((layoutsEdit.catalog_layout_extras || {}).hero_primary_color || "").trim() ? (
                      <button
                        type="button"
                        onClick={() =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              hero_primary_color: "",
                            },
                          }))
                        }
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          borderRadius: 10,
                          padding: "8px 10px",
                          cursor: "pointer",
                        }}
                      >
                        {tStudio.heroPrimaryFollowSite}
                      </button>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                      {studioLang === "en" ? "Wholesale strip accent color" : "لون شريط تجار الجملة"}
                    </div>
                    <input
                      type="color"
                      value={
                        String((layoutsEdit.catalog_layout_extras || {}).wholesale_strip_accent_color || "").trim() ||
                        "#fbbf24"
                      }
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            wholesale_strip_accent_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.45 }}>
                      {studioLang === "en"
                        ? "Applies to the wholesale bar under the header."
                        : "يُطبَّق على شريط تجار الجملة تحت الهيدر."}
                    </div>
                    {String((layoutsEdit.catalog_layout_extras || {}).wholesale_strip_accent_color || "").trim() ? (
                      <button
                        type="button"
                        onClick={() =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              wholesale_strip_accent_color: "",
                            },
                          }))
                        }
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          borderRadius: 10,
                          padding: "8px 10px",
                          cursor: "pointer",
                        }}
                      >
                        {studioLang === "en" ? "Reset to default amber" : "إعادة للاصفر الافتراضي"}
                      </button>
                    ) : null}

                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, marginTop: 14 }}>
                      {studioLang === "en"
                        ? "Wholesale login button color"
                        : "لون زر دخول تاجر الجملة"}
                    </div>
                    <input
                      type="color"
                      value={
                        String((layoutsEdit.catalog_layout_extras || {}).wholesale_auth_button_color || "").trim() ||
                        String((layoutsEdit.catalog_layout_extras || {}).wholesale_strip_accent_color || "").trim() ||
                        "#fbbf24"
                      }
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            wholesale_auth_button_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.45 }}>
                      {studioLang === "en"
                        ? "Login button in the wholesale popup. Empty follows the strip accent."
                        : "زر «دخول» في نافذة تاجر الجملة. فارغ = يتبع لون الشريط."}
                    </div>
                    {String((layoutsEdit.catalog_layout_extras || {}).wholesale_auth_button_color || "").trim() ? (
                      <button
                        type="button"
                        onClick={() =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              wholesale_auth_button_color: "",
                            },
                          }))
                        }
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          borderRadius: 10,
                          padding: "8px 10px",
                          cursor: "pointer",
                        }}
                      >
                        {studioLang === "en" ? "Follow strip color" : "اتباع لون الشريط"}
                      </button>
                    ) : null}

                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, marginTop: 14 }}>
                      {studioLang === "en"
                        ? "Wholesale login field border color"
                        : "لون إطار حقول دخول تاجر الجملة"}
                    </div>
                    <input
                      type="color"
                      value={
                        String((layoutsEdit.catalog_layout_extras || {}).wholesale_auth_field_color || "").trim() ||
                        String((layoutsEdit.catalog_layout_extras || {}).wholesale_auth_button_color || "").trim() ||
                        String((layoutsEdit.catalog_layout_extras || {}).wholesale_strip_accent_color || "").trim() ||
                        "#fbbf24"
                      }
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            wholesale_auth_field_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.45 }}>
                      {studioLang === "en"
                        ? "Border and focus ring around username/password fields."
                        : "لون الإطار والتركيز حول حقول اسم المستخدم وكلمة المرور."}
                    </div>
                    {String((layoutsEdit.catalog_layout_extras || {}).wholesale_auth_field_color || "").trim() ? (
                      <button
                        type="button"
                        onClick={() =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              wholesale_auth_field_color: "",
                            },
                          }))
                        }
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          borderRadius: 10,
                          padding: "8px 10px",
                          cursor: "pointer",
                        }}
                      >
                        {studioLang === "en" ? "Follow button color" : "اتباع لون الزر"}
                      </button>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                      <ImageIcon size={16} /> {tStudio.heroImageLabel}
                    </div>
                  {storeEdit.heroUrl ? (
                    <HeroImageFocalPicker
                      key={storeEdit.heroUrl}
                      imageUrl={storeEdit.heroUrl}
                      backgroundImageCss={cssBackgroundImageFromUrl(storeEdit.heroUrl)}
                      positionX={(layoutsEdit.catalog_layout_extras || {}).hero_bg_position_x}
                      positionY={(layoutsEdit.catalog_layout_extras || {}).hero_bg_position_y}
                      onChange={(x, y) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            hero_bg_position_x: x,
                            hero_bg_position_y: y,
                          },
                        }))
                      }
                      height={160}
                      ariaLabel={tStudio.heroImagePreview}
                      hint={tStudio.heroImagePositionHint}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: 100,
                        borderRadius: 12,
                        border: "1px dashed rgba(15,23,42,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#94a3b8",
                        fontSize: 12,
                        fontWeight: 700,
                        marginBottom: 8,
                        background: "#f8fafc",
                      }}
                    >
                      {tStudio.heroImagePlaceholder}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <label
                      style={{
                        flex: "1 1 auto",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid rgba(15,23,42,0.12)",
                        background: "#fff",
                        cursor: heroImageUploading ? "wait" : "pointer",
                        fontWeight: 800,
                        fontSize: 12,
                        opacity: heroImageUploading ? 0.65 : 1,
                        pointerEvents: heroImageUploading ? "none" : "auto",
                      }}
                    >
                      {heroImageUploading ? (
                        <Loader2 size={14} style={{ animation: "spin 0.9s linear infinite" }} aria-hidden />
                      ) : (
                        <ImageIcon size={14} />
                      )}
                      {heroImageUploading ? tStudio.heroImageUploading : storeEdit.heroUrl ? tStudio.changeImage : tStudio.uploadImage}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={heroImageUploading}
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          setHeroImageUploading(true);
                          try {
                            const url = await uploadBuilderImage(f, "desktop");
                            setStoreEdit((p) => ({ ...p, heroUrl: url, bgType: "image" }));
                          } catch (err) {
                            setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }));
                          } finally {
                            setHeroImageUploading(false);
                            try {
                              e.target.value = "";
                            } catch {}
                          }
                        }}
                      />
                    </label>
                    {storeEdit.heroUrl ? (
                      <button
                        type="button"
                        disabled={heroImageUploading}
                        onClick={() => setStoreEdit((p) => ({ ...p, heroUrl: "", bgType: "color" }))}
                        style={{
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          cursor: heroImageUploading ? "not-allowed" : "pointer",
                          fontWeight: 800,
                          fontSize: 12,
                          color: "#b91c1c",
                          opacity: heroImageUploading ? 0.45 : 1,
                        }}
                      >
                        {tStudio.delete}
                      </button>
                    ) : null}
                  </div>
                  <input
                    value={storeEdit.heroUrl}
                    onChange={(e) =>
                      setStoreEdit((p) => ({
                        ...p,
                        heroUrl: e.target.value,
                        bgType: e.target.value ? "image" : "color",
                      }))
                    }
                    placeholder={tStudio.heroUrlPlaceholder}
                    style={{ width: "100%", marginTop: 8, padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                  />
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.5 }}>
                    {tStudio.heroImageHint2}
                  </div>
                </div>

                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                      <Smartphone size={16} /> {tStudio.heroImageMobileLabel}
                    </div>
                    {String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "").trim() ? (
                      <div
                        style={{
                          width: "100%",
                          height: 140,
                          borderRadius: 12,
                          border: "1px solid rgba(15,23,42,0.12)",
                          backgroundImage: cssBackgroundImageFromUrl(
                            String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "").trim()
                          ),
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          marginBottom: 8,
                        }}
                        role="img"
                        aria-label={tStudio.heroImageMobileLabel}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: 88,
                          borderRadius: 12,
                          border: "1px dashed rgba(15,23,42,0.18)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#94a3b8",
                          fontSize: 12,
                          fontWeight: 700,
                          marginBottom: 8,
                          background: "#f8fafc",
                          textAlign: "center",
                          padding: "0 12px",
                        }}
                      >
                        {tStudio.heroImageMobilePlaceholder}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <label
                        style={{
                          flex: "1 1 auto",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          cursor: heroMobileUploading ? "wait" : "pointer",
                          fontWeight: 800,
                          fontSize: 12,
                          opacity: heroMobileUploading ? 0.65 : 1,
                          pointerEvents: heroMobileUploading ? "none" : "auto",
                        }}
                      >
                        {heroMobileUploading ? (
                          <Loader2 size={14} style={{ animation: "spin 0.9s linear infinite" }} aria-hidden />
                        ) : (
                          <ImageIcon size={14} />
                        )}
                        {heroMobileUploading
                          ? tStudio.heroImageUploading
                          : String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "").trim()
                            ? tStudio.changeImage
                            : tStudio.uploadImage}
                        <input
                          type="file"
                          accept="image/*"
                          disabled={heroMobileUploading}
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            setHeroMobileUploading(true);
                            try {
                              const url = await uploadBuilderImage(f, "mobile");
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  hero_mobile_url: url,
                                },
                              }));
                            } catch (err) {
                              setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }));
                            } finally {
                              setHeroMobileUploading(false);
                              try {
                                e.target.value = "";
                              } catch {}
                            }
                          }}
                        />
                      </label>
                      {String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "").trim() ? (
                        <button
                          type="button"
                          disabled={heroMobileUploading}
                          onClick={() =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                hero_mobile_url: "",
                              },
                            }))
                          }
                          style={{
                            padding: "10px 12px",
                            borderRadius: 12,
                            border: "1px solid rgba(15,23,42,0.12)",
                            background: "#fff",
                            cursor: heroMobileUploading ? "not-allowed" : "pointer",
                            fontWeight: 800,
                            fontSize: 12,
                            color: "#b91c1c",
                            opacity: heroMobileUploading ? 0.45 : 1,
                          }}
                        >
                          {tStudio.delete}
                        </button>
                      ) : null}
                    </div>
                    <input
                      value={String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "")}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            hero_mobile_url: e.target.value,
                          },
                        }))
                      }
                      placeholder={tStudio.heroUrlPlaceholder}
                      style={{ width: "100%", marginTop: 8, padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.5 }}>
                      {tStudio.heroImageMobileHint}
                    </div>
                    {(String(storeEdit.heroUrl || "").trim() ||
                      String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "").trim()) ? (
                      <>
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.heroMobileFitLabel}</div>
                          <select
                            value={
                              String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_fit || "contain").toLowerCase() ===
                              "cover"
                                ? "cover"
                                : "contain"
                            }
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  hero_mobile_fit: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          >
                            <option value="contain">{tStudio.heroMobileFitContain}</option>
                            <option value="cover">{tStudio.heroMobileFitCover}</option>
                          </select>
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.45 }}>
                            {tStudio.heroMobileFitHint}
                          </div>
                        </div>
                        {String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_fit || "contain").toLowerCase() ===
                          "cover" &&
                        String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "").trim() ? (
                          <div style={{ marginTop: 12 }}>
                            <HeroImageFocalPicker
                              key={`mob-focal-${String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "")}`}
                              imageUrl={String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "").trim()}
                              backgroundImageCss={cssBackgroundImageFromUrl(
                                String((layoutsEdit.catalog_layout_extras || {}).hero_mobile_url || "").trim()
                              )}
                              positionX={(layoutsEdit.catalog_layout_extras || {}).hero_mobile_bg_position_x}
                              positionY={(layoutsEdit.catalog_layout_extras || {}).hero_mobile_bg_position_y}
                              onChange={(x, y) =>
                                setLayoutsEdit((p) => ({
                                  ...p,
                                  catalog_layout_extras: {
                                    ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                    ...(p.catalog_layout_extras || {}),
                                    hero_mobile_bg_position_x: x,
                                    hero_mobile_bg_position_y: y,
                                  },
                                }))
                              }
                              height={160}
                              ariaLabel={tStudio.heroImageMobileLabel}
                              hint={tStudio.heroMobilePositionHint}
                            />
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>

                <div style={{ marginTop: 8, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontWeight: 950, marginBottom: 8 }}>{tStudio.featuresBarTitle}</div>
                  <StudioSwitch
                    checked={Boolean(layoutsEdit.show_features_bar)}
                    onChange={(v) => setLayoutsEdit((p) => ({ ...p, show_features_bar: v }))}
                    label={tStudio.showBar}
                  />
                  <div style={{ marginTop: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#334155" }}>
                      {tStudio.featuresBarColorsGroup}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 10,
                        alignItems: "end",
                      }}
                    >
                    {[
                      {
                        key: "bg",
                        label: tStudio.barBackground,
                        value: storeEdit.featuresBarBg || "#f8fafc",
                        onChange: (v) => setStoreEdit((p) => ({ ...p, featuresBarBg: v })),
                      },
                      {
                        key: "accent",
                        label: tStudio.headingsColor,
                        value: storeEdit.featuresBarAccent || storeEdit.primaryColor,
                        onChange: (v) => setStoreEdit((p) => ({ ...p, featuresBarAccent: v })),
                      },
                      {
                        key: "text",
                        label: tStudio.descriptionColor,
                        value: storeEdit.featuresBarText || "#475569",
                        onChange: (v) => setStoreEdit((p) => ({ ...p, featuresBarText: v })),
                      },
                    ].map((item) => (
                      <div
                        key={item.key}
                        style={{
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 900,
                            marginBottom: 6,
                            lineHeight: 1.35,
                            minHeight: 44,
                          }}
                        >
                          {item.label}
                        </div>
                        <input
                          type="color"
                          value={item.value}
                          onChange={(e) => item.onChange(e.target.value)}
                          style={{
                            width: "100%",
                            height: 42,
                            borderRadius: 12,
                            border: "1px solid rgba(15,23,42,0.12)",
                            marginTop: "auto",
                          }}
                        />
                      </div>
                    ))}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#334155" }}>
                    {tStudio.featuresBarColumnsGroup}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                    {[
                      { titleKey: "featureTitle1", descKey: "featureDesc1", label: tStudio.column1 },
                      { titleKey: "featureTitle2", descKey: "featureDesc2", label: tStudio.column2 },
                      { titleKey: "featureTitle3", descKey: "featureDesc3", label: tStudio.column3 },
                    ].map((row) => (
                      <div key={row.titleKey} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 10, background: "#fafafa" }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{row.label}</div>
                        <input value={storeEdit[row.titleKey]} onChange={(e) => setStoreEdit((p) => ({ ...p, [row.titleKey]: e.target.value }))} placeholder={tStudio.featureTitlePh} style={{ width: "100%", padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)", marginBottom: 6 }} />
                        <textarea value={storeEdit[row.descKey]} onChange={(e) => setStoreEdit((p) => ({ ...p, [row.descKey]: e.target.value }))} placeholder={tStudio.shortDescription} rows={2} style={{ width: "100%", padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)", resize: "vertical" }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {customizerPane === "form" && customizerTab === "navbar" && (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontWeight: 950 }}>{tStudio.navbarSettings}</div>
                <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>{tStudio.navbarEnableHint}</div>
                <StudioSwitch
                  checked={Boolean(catalogLayoutEffective.navbar_enabled)}
                  onChange={(v) => patchCatalogLayoutExtras({ navbar_enabled: v })}
                  label={tStudio.navbarEnable}
                />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarLinkColor}</div>
                    <input
                      type="color"
                      value={catalogLayoutEffective.navbar_link_color || "#111827"}
                      onChange={(e) => patchCatalogLayoutExtras({ navbar_link_color: e.target.value })}
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarLinkHoverColor}</div>
                    <input
                      type="color"
                      value={catalogLayoutEffective.navbar_link_hover_color || "#b45309"}
                      onChange={(e) => patchCatalogLayoutExtras({ navbar_link_hover_color: e.target.value })}
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarVisibleCount}</div>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={Number(catalogLayoutEffective.navbar_visible_count) || 4}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(10, Number(e.target.value) || 4));
                      patchCatalogLayoutExtras({ navbar_visible_count: n });
                    }}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                  />
                </div>

                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarMoreLabel}</div>
                  <input
                    value={catalogLayoutEffective.navbar_more_label || ""}
                    onChange={(e) => patchCatalogLayoutExtras({ navbar_more_label: e.target.value })}
                    placeholder={tStudio.navbarMoreLabelPh}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarMoreColor}</div>
                    <input
                      type="color"
                      value={
                        catalogLayoutEffective.navbar_more_color ||
                        catalogLayoutEffective.navbar_link_color ||
                        "#111827"
                      }
                      onChange={(e) => patchCatalogLayoutExtras({ navbar_more_color: e.target.value })}
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarMoreHoverColor}</div>
                    <input
                      type="color"
                      value={
                        catalogLayoutEffective.navbar_more_hover_color ||
                        catalogLayoutEffective.navbar_link_hover_color ||
                        "#b45309"
                      }
                      onChange={(e) => patchCatalogLayoutExtras({ navbar_more_hover_color: e.target.value })}
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                  </div>
                </div>

                <div style={{ borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, color: "#334155" }}>{tStudio.navbarLinksGroup}</div>
                    <button
                      type="button"
                      onClick={() => {
                        const next = [
                          ...parseCatalogNavbarLinks(catalogLayoutEffective.navbar_links),
                          {
                            id: newCatalogNavbarLinkId(),
                            label: studioLang === "en" ? "Products" : "منتجاتنا",
                            target: "products",
                            section_id: "",
                            url: "",
                            color: "",
                            hover_color: "",
                          },
                        ];
                        patchCatalogLayoutExtras({ navbar_links: next, navbar_enabled: true });
                      }}
                      style={{
                        border: "1px solid rgba(15,23,42,0.12)",
                        background: "#0f172a",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        fontWeight: 900,
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      + {tStudio.navbarAddLink}
                    </button>
                  </div>

                  {parseCatalogNavbarLinks(catalogLayoutEffective.navbar_links).length === 0 ? (
                    <div style={{ fontSize: 12, color: "#64748b", padding: "8px 0" }}>{tStudio.navbarEmpty}</div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {parseCatalogNavbarLinks(catalogLayoutEffective.navbar_links).map((link, idx) => {
                        const allLinks = parseCatalogNavbarLinks(catalogLayoutEffective.navbar_links);
                        const updateLink = (patch) => {
                          const next = allLinks.map((l, i) => (i === idx ? { ...l, ...patch } : l));
                          patchCatalogLayoutExtras({ navbar_links: next });
                        };
                        const sectionOptions = (builderDraft || []).map((sec) => ({
                          id: sec.id,
                          label: getCatalogSectionNavLabel(sec, tStudio, studioLang),
                        }));
                        const destValue =
                          link.target === "section" && link.section_id
                            ? `section:${link.section_id}`
                            : link.target === "url"
                              ? "url"
                              : link.target || "products";
                        return (
                          <div
                            key={link.id}
                            style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: 12,
                              padding: 12,
                              background: "#fafafa",
                              display: "grid",
                              gap: 8,
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarLinkLabel}</div>
                              <input
                                value={link.label}
                                onChange={(e) => updateLink({ label: e.target.value })}
                                placeholder={tStudio.navbarLinkLabelPh}
                                style={{ width: "100%", padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarLinkTarget}</div>
                              <select
                                value={destValue}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v.startsWith("section:")) {
                                    updateLink({
                                      target: "section",
                                      section_id: v.slice("section:".length),
                                      url: "",
                                    });
                                  } else if (v === "url") {
                                    updateLink({ target: "url", section_id: "" });
                                  } else {
                                    updateLink({ target: v, section_id: "", url: "" });
                                  }
                                }}
                                style={{ width: "100%", padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="hero">{tStudio.navbarTargetHero}</option>
                                <option value="products">{tStudio.navbarTargetProducts}</option>
                                <option value="footer">{tStudio.navbarTargetFooter}</option>
                                {sectionOptions.length > 0 ? (
                                  <optgroup label={tStudio.navbarPageSectionsGroup}>
                                    {sectionOptions.map((opt) => (
                                      <option key={opt.id} value={`section:${opt.id}`}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                ) : (
                                  <option value="section" disabled>
                                    {tStudio.navbarNoPageSections}
                                  </option>
                                )}
                                <option value="url">{tStudio.navbarTargetUrl}</option>
                              </select>
                            </div>
                            {link.target === "url" ? (
                              <>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarExternalUrl}</div>
                                  <input
                                    value={link.url || ""}
                                    onChange={(e) => updateLink({ url: e.target.value })}
                                    placeholder="https://"
                                    style={{ width: "100%", padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  />
                                </div>
                                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800 }}>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(link.open_in_new_tab)}
                                    onChange={(e) => updateLink({ open_in_new_tab: e.target.checked })}
                                  />
                                  {tStudio.navbarOpenNewTab}
                                </label>
                              </>
                            ) : null}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarLinkColorOverride}</div>
                                <input
                                  type="color"
                                  value={link.color || catalogLayoutEffective.navbar_link_color || "#111827"}
                                  onChange={(e) => updateLink({ color: e.target.value })}
                                  style={{ width: "100%", height: 36, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                />
                              </div>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tStudio.navbarLinkHoverOverride}</div>
                                <input
                                  type="color"
                                  value={link.hover_color || catalogLayoutEffective.navbar_link_hover_color || "#b45309"}
                                  onChange={(e) => updateLink({ hover_color: e.target.value })}
                                  style={{ width: "100%", height: 36, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const next = allLinks.filter((_, i) => i !== idx);
                                patchCatalogLayoutExtras({ navbar_links: next });
                              }}
                              style={{
                                border: "1px solid rgba(220,38,38,0.25)",
                                background: "#fff",
                                color: "#b91c1c",
                                borderRadius: 10,
                                padding: "8px 10px",
                                fontWeight: 900,
                                fontSize: 12,
                                cursor: "pointer",
                              }}
                            >
                              {tStudio.navbarRemoveLink}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {customizerPane === "form" && customizerTab === "look" && (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontWeight: 950 }}>{tStudio.catalogAppearance}</div>
                <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
                  {tStudio.lookHint}
                </div>

                <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <StudioCardPicker
                    value={String((layoutsEdit.catalog_layout_extras || {}).catalog_font_stack || "system")}
                    onChange={(v) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          catalog_font_stack: String(v),
                        },
                      }))
                    }
                    label={tStudio.fontFamilyTitle}
                    hint={tStudio.fontFamilyHint}
                    columns={1}
                    options={[
                      { value: "system", label: tStudio.fontStackSystem, desc: "" },
                      { value: "cairo", label: tStudio.fontStackCairo, desc: "Google Fonts" },
                      { value: "tajawal", label: tStudio.fontStackTajawal, desc: "Google Fonts" },
                      { value: "ibm_plex_arabic", label: tStudio.fontStackIbm, desc: "Google Fonts" },
                      { value: "almarai", label: tStudio.fontStackAlmarai, desc: "Google Fonts" },
                      { value: "noto_kufi", label: tStudio.fontStackNotoKufi, desc: "Google Fonts" },
                    ]}
                  />
                </div>

                <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <StudioCardPicker
                    value={String((layoutsEdit.catalog_layout_extras || {}).builder_page_container_width || "boxed").toString()}
                    onChange={(v) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          builder_page_container_width: String(v),
                        },
                      }))
                    }
                    label={tStudio.pageContentWidthTitle}
                    hint={tStudio.pageContentWidthHint}
                    columns={1}
                    options={[
                      { value: "boxed", label: tStudio.contentWidthBoxed, desc: "" },
                      { value: "wide", label: tStudio.contentWidthWide, desc: "" },
                      { value: "full", label: tStudio.contentWidthFull, desc: "" },
                    ]}
                  />
                </div>

                <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontWeight: 950, marginBottom: 8 }}>{tStudio.generalColors}</div>
                  <StudioSwitch
                    checked={layoutExtrasBool((layoutsEdit.catalog_layout_extras || {}).global_page_bg_enabled)}
                    onChange={(v) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          global_page_bg_enabled: Boolean(v),
                        },
                      }))
                    }
                    label={tStudio.enablePageBgLong}
                  />
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.5 }}>
                    {tStudio.pageBgHint}
                  </div>
                  {layoutExtrasBool((layoutsEdit.catalog_layout_extras || {}).global_page_bg_enabled) ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.backgroundColor}</div>
                      <input
                        type="color"
                        value={(layoutsEdit.catalog_layout_extras || {}).page_bg || "#ffffff"}
                        onChange={(e) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              page_bg: e.target.value,
                            },
                          }))
                        }
                        style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                      />
                    </div>
                  ) : null}
                  {showCatalogDarkSurfaceToggle ? (
                    <div style={{ marginTop: 10 }}>
                      <StudioSwitch
                        checked={layoutExtrasBool((layoutsEdit.catalog_layout_extras || {}).catalog_dark_surface)}
                        onChange={(v) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              catalog_dark_surface: Boolean(v),
                            },
                          }))
                        }
                        label={studioLang === "en" ? "Dark section cards (dark themes)" : "بطاقات داكنة (للثيمات الداكنة)"}
                      />
                    </div>
                  ) : null}
                  <div style={{ marginTop: 14, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.loaderTextTitle}</div>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, lineHeight: 1.5 }}>
                      {tStudio.loaderTextHint}
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      value={String((layoutsEdit.catalog_layout_extras || {}).loader_text || "")}
                      placeholder={tStudio.loaderTextPlaceholder}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            loader_text: e.target.value,
                          },
                        }))
                      }
                      style={{
                        width: "100%",
                        height: 42,
                        borderRadius: 12,
                        border: "1px solid rgba(15,23,42,0.12)",
                        padding: "0 12px",
                        marginBottom: 10,
                      }}
                    />
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.loaderTextColor}</div>
                    <input
                      type="color"
                      value={
                        /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(
                          String((layoutsEdit.catalog_layout_extras || {}).loader_text_color || "").trim()
                        )
                          ? String((layoutsEdit.catalog_layout_extras || {}).loader_text_color).trim()
                          : "#111827"
                      }
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            loader_text_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                    {String((layoutsEdit.catalog_layout_extras || {}).loader_text_color || "").trim() ? (
                      <button
                        type="button"
                        onClick={() =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              loader_text_color: "",
                            },
                          }))
                        }
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          borderRadius: 10,
                          padding: "8px 12px",
                          cursor: "pointer",
                        }}
                      >
                        {tStudio.loaderTextColorClear}
                      </button>
                    ) : null}
                  </div>
                </div>

                <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontWeight: 950, marginBottom: 6 }}>{tStudio.siteAccentColors}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10, lineHeight: 1.5 }}>{tStudio.siteAccentHint}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.primaryColor}</div>
                      <input
                        type="color"
                        value={storeEdit.primaryColor || "#4f46e5"}
                        onChange={(e) => setStoreEdit((p) => ({ ...p, primaryColor: e.target.value }))}
                        style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.secondaryColor}</div>
                      <input
                        type="color"
                        value={storeEdit.secondaryColor || "#f3f4f6"}
                        onChange={(e) => setStoreEdit((p) => ({ ...p, secondaryColor: e.target.value }))}
                        style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {customizerPane === "form" && customizerTab === "cart" && (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontWeight: 950 }}>{tStudio.cartCustomizerTitle}</div>
                <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>{tStudio.cartCustomizerHint}</div>
                <div style={{ marginTop: 4 }}>
                  <StudioCardPicker
                    value={String((layoutsEdit.catalog_layout_extras || {}).cart_placement || "topbar")}
                    onChange={(v) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          cart_placement: String(v),
                        },
                      }))
                    }
                    label={tStudio.cartPositionLabel}
                    hint={tStudio.cartPositionHint}
                    columns={3}
                    options={[
                      { value: "topbar", label: tStudio.topbar, desc: tStudio.topbarDesc },
                      { value: "right", label: tStudio.right, desc: tStudio.floatingButton },
                      { value: "left", label: tStudio.left, desc: tStudio.floatingButton },
                    ]}
                  />
                </div>
                <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontWeight: 950, marginBottom: 6 }}>{tStudio.floatingCartBgLabel}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, lineHeight: 1.45 }}>
                    {tStudio.floatingCartBgHint}
                  </div>
                  <input
                    type="color"
                    value={
                      String((layoutsEdit.catalog_layout_extras || {}).floating_cart_button_bg || "").trim() ||
                      "#6366f1"
                    }
                    onChange={(e) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          floating_cart_button_bg: e.target.value,
                        },
                      }))
                    }
                    style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          floating_cart_button_bg: "",
                        },
                      }))
                    }
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      fontWeight: 800,
                      border: "1px solid rgba(15,23,42,0.12)",
                      background: "#fff",
                      borderRadius: 10,
                      padding: "8px 10px",
                      cursor: "pointer",
                    }}
                  >
                    {tStudio.reset}
                  </button>
                </div>
                <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontWeight: 950, marginBottom: 6 }}>{tStudio.floatingCartIconColorLabel}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, lineHeight: 1.45 }}>
                    {tStudio.floatingCartIconColorHint}
                  </div>
                  <input
                    type="color"
                    value={
                      String((layoutsEdit.catalog_layout_extras || {}).floating_cart_icon_color || "").trim() ||
                      "#ffffff"
                    }
                    onChange={(e) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          floating_cart_icon_color: e.target.value,
                        },
                      }))
                    }
                    style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          floating_cart_icon_color: "",
                        },
                      }))
                    }
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      fontWeight: 800,
                      border: "1px solid rgba(15,23,42,0.12)",
                      background: "#fff",
                      borderRadius: 10,
                      padding: "8px 10px",
                      cursor: "pointer",
                    }}
                  >
                    {tStudio.reset}
                  </button>
                </div>
              </div>
            )}

            {customizerPane === "form" && customizerTab === "products" && (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontWeight: 950 }}>{tStudio.productsSettings}</div>

                <div style={{ marginTop: 4, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10, color: "#334155" }}>{tStudio.productsTextsGroup}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsTitleLabel}</div>
                    <input value={storeEdit.productsSectionTitle} onChange={(e) => setStoreEdit((p) => ({ ...p, productsSectionTitle: e.target.value }))} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsSubtitleLabel}</div>
                    <textarea value={storeEdit.productsSectionSubtitle} onChange={(e) => setStoreEdit((p) => ({ ...p, productsSectionSubtitle: e.target.value }))} rows={3} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", resize: "vertical" }} />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <StudioCardPicker
                      value={(storeEdit.productsHeadingAlign || "start").toString()}
                      onChange={(v) => setStoreEdit((p) => ({ ...p, productsHeadingAlign: String(v) }))}
                      label={tStudio.productsAlign}
                      columns={3}
                      options={[
                        { value: "start", label: tStudio.productsAlignStart },
                        { value: "center", label: tStudio.productsAlignCenter },
                        { value: "end", label: tStudio.productsAlignEnd },
                      ]}
                    />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#475569" }}>{tStudio.productsSectionColorsMini}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsHeadingColorLabel}</div>
                        <input
                          type="color"
                          value={(layoutsEdit.catalog_layout_extras || {}).products_heading_color || "#1a1a1a"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                products_heading_color: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsSubtitleColorLabel}</div>
                        <input
                          type="color"
                          value={(layoutsEdit.catalog_layout_extras || {}).products_subtitle_color || "#64748b"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                products_subtitle_color: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsCardNameColorLabel}</div>
                        <input
                          type="color"
                          value={(layoutsEdit.catalog_layout_extras || {}).products_card_name_color || "#111111"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                products_card_name_color: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsCardDescColorLabel}</div>
                        <input
                          type="color"
                          value={(layoutsEdit.catalog_layout_extras || {}).products_card_desc_color || "#64748b"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                products_card_desc_color: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      {(() => {
                        const ex = layoutsEdit.catalog_layout_extras || {};
                        const en = studioLang === "en";
                        const setPrice = (patch) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              ...patch,
                            },
                          }));
                        const priceColor = String(ex.products_card_price_color || "").trim();
                        const priceBg = String(ex.products_card_price_bg_color || "").trim();
                        const pos = String(ex.products_card_price_position || "");
                        const resetStyle = {
                          fontSize: 11,
                          fontWeight: 800,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          borderRadius: 8,
                          padding: "3px 8px",
                          cursor: "pointer",
                        };
                        return (
                          <>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                                <span>{en ? "Price color" : "لون السعر"}</span>
                                {priceColor ? (
                                  <button type="button" style={resetStyle} onClick={() => setPrice({ products_card_price_color: "" })}>
                                    {en ? "Reset" : "افتراضي"}
                                  </button>
                                ) : null}
                              </div>
                              <input
                                type="color"
                                value={priceColor || storeEdit.primaryColor || "#111111"}
                                onChange={(e) => setPrice({ products_card_price_color: e.target.value })}
                                style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{en ? "Price position" : "موقع السعر"}</div>
                              <select
                                value={pos}
                                onChange={(e) => setPrice({ products_card_price_position: e.target.value })}
                                style={{ width: "100%", height: 42, padding: "0 10px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="">{en ? "Bottom, next to cart" : "أسفل البطاقة بجانب السلة"}</option>
                                <option value="below_name">{en ? "Under product name" : "تحت اسم المنتج"}</option>
                                <option value="on_image">{en ? "On the image" : "فوق صورة المنتج"}</option>
                              </select>
                            </div>
                            {pos !== "on_image" ? (
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{en ? "Price alignment" : "محاذاة السعر"}</div>
                                <select
                                  value={String(ex.products_card_price_align || "")}
                                  onChange={(e) => setPrice({ products_card_price_align: e.target.value })}
                                  style={{ width: "100%", height: 42, padding: "0 10px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                                >
                                  <option value="">{en ? "Auto (next to cart)" : "تلقائي (بجانب السلة)"}</option>
                                  <option value="right">{en ? "Right" : "يمين"}</option>
                                  <option value="center">{en ? "Center" : "وسط"}</option>
                                  <option value="left">{en ? "Left" : "يسار"}</option>
                                </select>
                              </div>
                            ) : null}
                            {pos === "on_image" ? (
                              <div style={{ gridColumn: "1 / -1" }}>
                                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                                  <span>{en ? "Price badge background" : "خلفية شارة السعر"}</span>
                                  {priceBg ? (
                                    <button type="button" style={resetStyle} onClick={() => setPrice({ products_card_price_bg_color: "" })}>
                                      {en ? "Reset" : "افتراضي"}
                                    </button>
                                  ) : null}
                                </div>
                                <input
                                  type="color"
                                  value={priceBg || "#ffffff"}
                                  onChange={(e) => setPrice({ products_card_price_bg_color: e.target.value })}
                                  style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                                />
                              </div>
                            ) : null}
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{en ? "Category name" : "اسم الفئة"}</div>
                              <select
                                value={String(ex.products_card_category_position || "")}
                                onChange={(e) => setPrice({ products_card_category_position: e.target.value })}
                                style={{ width: "100%", height: 42, padding: "0 10px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="">{en ? "Above product name" : "فوق اسم المنتج"}</option>
                                <option value="on_image">{en ? "Badge on the image" : "شارة فوق الصورة"}</option>
                                <option value="hidden">{en ? "Hidden" : "إخفاء"}</option>
                              </select>
                            </div>
                            {!String(ex.products_card_category_position || "") ? (
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                                  <span>{en ? "Category color" : "لون اسم الفئة"}</span>
                                  {String(ex.products_card_category_color || "").trim() ? (
                                    <button type="button" style={resetStyle} onClick={() => setPrice({ products_card_category_color: "" })}>
                                      {en ? "Reset" : "افتراضي"}
                                    </button>
                                  ) : null}
                                </div>
                                <input
                                  type="color"
                                  value={String(ex.products_card_category_color || "").trim() || "#64748b"}
                                  onChange={(e) => setPrice({ products_card_category_color: e.target.value })}
                                  style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                                />
                              </div>
                            ) : null}
                          </>
                        );
                      })()}
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsUnderlineColorLabel}</div>
                        <input
                          type="color"
                          value={
                            (layoutsEdit.catalog_layout_extras || {}).products_underline_color ||
                            storeEdit.primaryColor ||
                            "#ffc200"
                          }
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                products_underline_color: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 4, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10, color: "#334155" }}>{tStudio.productsGridNavGroup}</div>
                  <StudioCardPicker
                    value={String(layoutsEdit.product_columns || 3)}
                    onChange={(v) => setLayoutsEdit((p) => ({ ...p, product_columns: Number(v) }))}
                    label={tStudio.columns}
                    columns={3}
                    options={[
                      { value: "2", label: "2" },
                      { value: "3", label: "3" },
                      { value: "4", label: "4" },
                    ]}
                  />
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsLayout}</div>
                    <StudioCardPicker
                      value={(layoutsEdit.catalog_layout_extras || {}).products_main_layout || "grid"}
                      onChange={(v) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_main_layout: String(v),
                          },
                        }))
                      }
                      columns={1}
                      options={[
                        { value: "grid", label: studioLang === 'en' ? 'Grid' : 'شبكة', desc: studioLang === 'en' ? 'Equal columns' : 'أعمدة متساوية' },
                        { value: "list", label: studioLang === 'en' ? 'List' : 'قائمة', desc: studioLang === 'en' ? 'Menu-style: text + image' : 'مثل المنيو: نص + صورة' },
                        { value: "gallery", label: studioLang === 'en' ? 'Gallery' : 'معرض', desc: studioLang === 'en' ? 'Bento: one large item + side items' : 'bento: عنصر كبير + عناصر جانبية' },
                      ]}
                    />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#475569" }}>{tStudio.productsFiltersNavTitle}</div>
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.categoryNavPlacementLabel}</div>
                      <select
                        value={(layoutsEdit.catalog_layout_extras || {}).category_nav || "top"}
                        onChange={(e) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              category_nav: e.target.value,
                            },
                          }))
                        }
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                      >
                        <option value="top">{tStudio.categoryNavOptionTop}</option>
                        <option value="sidebar">{tStudio.categoryNavOptionSidebar}</option>
                      </select>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <StudioSwitch
                        checked={Boolean((layoutsEdit.catalog_layout_extras || {}).filters_sidebar)}
                        onChange={(v) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              filters_sidebar: v,
                            },
                          }))
                        }
                        label={tStudio.filtersSidebarLabel}
                      />
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.categoryPillsAlignLabel}</div>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, lineHeight: 1.45 }}>{tStudio.categoryPillsAlignHint}</div>
                      <select
                        value={String((layoutsEdit.catalog_layout_extras || {}).category_pills_align || "")}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              category_pills_align: v === "" ? "" : v,
                            },
                          }));
                        }}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                      >
                        <option value="">{tStudio.categoryPillsAlignDefault}</option>
                        <option value="start">{tStudio.productsAlignStart}</option>
                        <option value="center">{tStudio.productsAlignCenter}</option>
                        <option value="end">{tStudio.productsAlignEnd}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                  <div style={{ fontWeight: 950, marginBottom: 8 }}>{tStudio.productsSectionLookTitle}</div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.sectionPaddingY}</div>
                    <select
                      value={(layoutsEdit.catalog_layout_extras || {}).products_section_padding_y || "md"}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_section_padding_y: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    >
                      <option value="sm">{tStudio.paddingTight}</option>
                      <option value="md">{tStudio.paddingNormal}</option>
                      <option value="lg">{tStudio.paddingWide}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsSectionWidthLabel}</div>
                    <select
                      value={(layoutsEdit.catalog_layout_extras || {}).products_section_container_width || ""}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_section_container_width: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    >
                      <option value="">{tStudio.productsSectionWidthFollowPage}</option>
                      <option value="boxed">{tStudio.contentWidthBoxed}</option>
                      <option value="wide">{tStudio.contentWidthWide}</option>
                      <option value="full">{tStudio.contentWidthFull}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.sectionGridGap}</div>
                    <select
                      value={(layoutsEdit.catalog_layout_extras || {}).products_grid_gap || "md"}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_grid_gap: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    >
                      <option value="sm">{tStudio.gapSmall}</option>
                      <option value="md">{tStudio.gapMedium}</option>
                      <option value="lg">{tStudio.gapLarge}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productCardShapeSelect}</div>
                    <select
                      value={(layoutsEdit.catalog_layout_extras || {}).products_card_style || "default"}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_card_style: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    >
                      <option value="default">{tStudio.cardShapeDefault}</option>
                      <option value="flat">{tStudio.cardShapeFlat}</option>
                      <option value="soft">{tStudio.cardShapeSoft}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsCardBgColorLabel}</div>
                    <input
                      type="color"
                      value={(layoutsEdit.catalog_layout_extras || {}).products_card_bg_color || "#ffffff"}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_card_bg_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsCartIconSideLabel}</div>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6, lineHeight: 1.45 }}>
                      {tStudio.productsCartIconSideHint}
                    </div>
                    <select
                      value={
                        String((layoutsEdit.catalog_layout_extras || {}).products_cart_icon_side || "right").toLowerCase() ===
                        "left"
                          ? "left"
                          : "right"
                      }
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_cart_icon_side: e.target.value === "left" ? "left" : "right",
                          },
                        }))
                      }
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    >
                      <option value="right">{tStudio.productsCartIconSideRight}</option>
                      <option value="left">{tStudio.productsCartIconSideLeft}</option>
                    </select>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsCartIconBorderWidthLabel}</div>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6, lineHeight: 1.45 }}>
                      {tStudio.productsCartIconBorderHint}
                    </div>
                    <input
                      type="number"
                      min={0}
                      max={12}
                      step={1}
                      value={Number((layoutsEdit.catalog_layout_extras || {}).products_cart_icon_border_width) || 0}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_cart_icon_border_width: Math.max(0, Math.min(12, Number(e.target.value) || 0)),
                          },
                        }))
                      }
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsCartIconBorderColorLabel}</div>
                    <input
                      type="color"
                      value={(layoutsEdit.catalog_layout_extras || {}).products_cart_icon_border_color || "#111827"}
                      onChange={(e) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_cart_icon_border_color: e.target.value,
                          },
                        }))
                      }
                      style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productsCardRadiusPx}</div>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6, lineHeight: 1.45 }}>
                      {tStudio.productsCardRadiusHint}
                    </div>
                    <select
                      value={
                        (layoutsEdit.catalog_layout_extras || {}).products_card_border_radius === null ||
                        (layoutsEdit.catalog_layout_extras || {}).products_card_border_radius === undefined ||
                        (layoutsEdit.catalog_layout_extras || {}).products_card_border_radius === ""
                          ? ""
                          : String((layoutsEdit.catalog_layout_extras || {}).products_card_border_radius)
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            products_card_border_radius: raw === "" ? null : Number(raw),
                          },
                        }));
                      }}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                    >
                      <option value="">افتراضي (حسب الثيم)</option>
                      <option value="0">0 — زوايا حادة</option>
                      <option value="8">8px</option>
                      <option value="12">12px</option>
                      <option value="16">16px</option>
                      <option value="20">20px</option>
                      <option value="24">24px — دائري</option>
                    </select>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <StudioCardPicker
                      value={normalizeProductImageShape(
                        (layoutsEdit.catalog_layout_extras || {}).product_image_shape
                      )}
                      onChange={(v) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            product_image_shape: normalizeProductImageShape(v),
                          },
                        }))
                      }
                      label={tStudio.productImageShapeLabel}
                      hint={tStudio.productImageShapeHint}
                      columns={2}
                      options={[
                        { value: "circle", label: tStudio.shapeCircle, desc: tStudio.shapeCircleDesc },
                        { value: "square", label: tStudio.shapeSquare, desc: tStudio.shapeSquareDesc },
                      ]}
                    />
                  </div>
                  {builderTheme === "restaurant" &&
                  ["square", "portrait"].includes(
                    normalizeProductImageShape(
                      (layoutsEdit.catalog_layout_extras || {}).product_image_shape
                    )
                  ) ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                        {tStudio.restaurantSquareImageRadius}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6, lineHeight: 1.45 }}>
                        {tStudio.restaurantSquareImageRadiusHint}
                      </div>
                      <input
                        type="number"
                        min={0}
                        max={48}
                        step={1}
                        value={
                          (layoutsEdit.catalog_layout_extras || {}).restaurant_square_image_radius_px === null ||
                          (layoutsEdit.catalog_layout_extras || {}).restaurant_square_image_radius_px === undefined ||
                          (layoutsEdit.catalog_layout_extras || {}).restaurant_square_image_radius_px === ""
                            ? ""
                            : Number((layoutsEdit.catalog_layout_extras || {}).restaurant_square_image_radius_px)
                        }
                        onChange={(e) => {
                          const raw = e.target.value;
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              restaurant_square_image_radius_px:
                                raw.trim() === ""
                                  ? null
                                  : Math.max(0, Math.min(48, Math.round(Number(raw) || 0))),
                            },
                          }));
                        }}
                        placeholder={tStudio.restaurantSquareImageRadiusPlaceholder}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                      />
                    </div>
                  ) : null}
                  <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                    <div style={{ fontWeight: 950, marginBottom: 8 }}>{tStudio.productImageBlockTitle}</div>
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.productImageSizeLabel}</div>
                      {(() => {
                        const sizeNow = Math.max(
                          84,
                          Math.min(
                            360,
                            Number(
                              (layoutsEdit.catalog_layout_extras || {}).product_card_image_size ??
                                (layoutsEdit.catalog_layout_extras || {}).restaurant_card_image_size ??
                                DEFAULT_CATALOG_LAYOUT_EXTRAS.product_card_image_size
                            ) || DEFAULT_CATALOG_LAYOUT_EXTRAS.product_card_image_size
                          )
                        );
                        const shapeNow = normalizeProductImageShape(
                          (layoutsEdit.catalog_layout_extras || {}).product_image_shape
                        );
                        const dims = productImageDisplayDims(sizeNow, shapeNow);
                        return (
                          <>
                            <input
                              type="range"
                              min={84}
                              max={360}
                              value={sizeNow}
                              onChange={(e) => {
                                const n = Number(e.target.value);
                                setLayoutsEdit((p) => ({
                                  ...p,
                                  catalog_layout_extras: {
                                    ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                    ...(p.catalog_layout_extras || {}),
                                    product_card_image_size: n,
                                    restaurant_card_image_size: n,
                                  },
                                }));
                              }}
                              style={{ width: "100%" }}
                            />
                            <div
                              style={{
                                marginTop: 8,
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "baseline",
                                gap: "6px 12px",
                                fontSize: 13,
                                fontWeight: 900,
                                color: "#0f172a",
                              }}
                            >
                              <span style={{ color: "#64748b", fontWeight: 800, fontSize: 11 }}>
                                {tStudio.productImageSizeDims}
                              </span>
                              <span style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                                {dims.width}
                                <span style={{ opacity: 0.45, margin: "0 4px" }}>×</span>
                                {dims.height}
                                <span style={{ marginInlineStart: 4, fontSize: 11, fontWeight: 800, color: "#64748b" }}>
                                  px
                                </span>
                              </span>
                              <span style={{ fontSize: 11, fontWeight: 750, color: "#475569" }}>
                                ({dims.width}:{dims.height})
                              </span>
                            </div>
                            {shapeNow === "portrait" || shapeNow === "square" ? (
                              <div style={{ fontSize: 11, color: "#0f766e", marginTop: 4, fontWeight: 800 }}>
                                {tStudio.productImageSizeRatioFixed}
                              </div>
                            ) : null}
                            {sizeNow >= 360 ? (
                              <div style={{ fontSize: 11, color: "#0f766e", marginTop: 4, fontWeight: 800 }}>
                                {tStudio.productImageSizeMaxHint}
                              </div>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <StudioSwitch
                        checked={Boolean(
                          (layoutsEdit.catalog_layout_extras || {}).products_show_description ??
                            (layoutsEdit.catalog_layout_extras || {}).restaurant_show_desc ??
                            true
                        )}
                        onChange={(v) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              products_show_description: Boolean(v),
                              restaurant_show_desc: Boolean(v),
                            },
                          }))
                        }
                        label={tStudio.showProductDescription}
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                    <div style={{ fontWeight: 950, marginBottom: 8 }}>{tStudio.restaurantMenuColorsTitle}</div>
                    <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.55, marginBottom: 8 }}>
                      {tStudio.restaurantMenuColorsHint}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#475569",
                        lineHeight: 1.55,
                        marginBottom: 12,
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "#f1f5f9",
                        border: "1px solid rgba(15,23,42,0.08)",
                        fontWeight: 750,
                      }}
                    >
                      {tStudio.restaurantMenuColorsApplyNote}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#475569" }}>{tStudio.restaurantMenuBaseColors}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.restaurantPageBg}</div>
                        <input
                          type="color"
                          value={(layoutsEdit.catalog_layout_extras || {}).restaurant_bg || "#faf8f3"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                restaurant_bg: e.target.value,
                                ...(builderTheme === "restaurant" ? { page_bg: e.target.value } : {}),
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.restaurantInk}</div>
                        <input
                          type="color"
                          value={(layoutsEdit.catalog_layout_extras || {}).restaurant_ink || "#2d2416"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                restaurant_ink: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 12, fontSize: 12, fontWeight: 900, marginBottom: 8, color: "#475569" }}>{tStudio.restaurantMenuAccentColors}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.restaurantAccentGreen}</div>
                        <input
                          type="color"
                          value={(layoutsEdit.catalog_layout_extras || {}).restaurant_accent || "#3d6b4f"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                restaurant_accent: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.restaurantAccentWarm}</div>
                        <input
                          type="color"
                          value={(layoutsEdit.catalog_layout_extras || {}).restaurant_accent_warm || "#c8813a"}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                restaurant_accent_warm: e.target.value,
                              },
                            }))
                          }
                          style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                      </div>
                    </div>

                    {builderTheme === "restaurant" ? (
                      <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                        <div style={{ fontWeight: 950, marginBottom: 8 }}>{tStudio.restaurantTopbar}</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.backgroundColor}</div>
                            <input
                              type="color"
                              value={(layoutsEdit.catalog_layout_extras || {}).restaurant_topbar_bg || "#0f172a"}
                              onChange={(e) =>
                                setLayoutsEdit((p) => ({
                                  ...p,
                                  catalog_layout_extras: {
                                    ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                    ...(p.catalog_layout_extras || {}),
                                    restaurant_topbar_bg: e.target.value,
                                  },
                                }))
                              }
                              style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.textIconColor}</div>
                            <input
                              type="color"
                              value={(layoutsEdit.catalog_layout_extras || {}).restaurant_topbar_text || "#ffffff"}
                              onChange={(e) =>
                                setLayoutsEdit((p) => ({
                                  ...p,
                                  catalog_layout_extras: {
                                    ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                    ...(p.catalog_layout_extras || {}),
                                    restaurant_topbar_text: e.target.value,
                                  },
                                }))
                              }
                              style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <RestaurantCategoryBarCustomizerBlock
                      extras={layoutsEdit.catalog_layout_extras}
                      onPatch={(patch) =>
                        setLayoutsEdit((p) => ({
                          ...p,
                          catalog_layout_extras: {
                            ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                            ...(p.catalog_layout_extras || {}),
                            ...patch,
                          },
                        }))
                      }
                      tStudio={tStudio}
                    />

                    {builderTheme === "restaurant" ? (
                    <div style={{ marginTop: 12, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
                      <div style={{ fontWeight: 950, marginBottom: 8 }}>{tStudio.productsDecorTitle}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.decorPattern}</div>
                          <select
                            value={String((layoutsEdit.catalog_layout_extras || {}).restaurant_decor_style || "none")}
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  restaurant_decor_style: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          >
                            <option value="none">{tStudio.decorNone}</option>
                            <option value="circles">{tStudio.decorCircles}</option>
                            <option value="arcs">{tStudio.decorArcs}</option>
                            <option value="rings">{tStudio.decorRings}</option>
                            <option value="waves">{tStudio.decorWaves}</option>
                          </select>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.decorColor}</div>
                          <input
                            type="color"
                            value={(layoutsEdit.catalog_layout_extras || {}).restaurant_decor_color || "#c8813a"}
                            onChange={(e) =>
                              setLayoutsEdit((p) => ({
                                ...p,
                                catalog_layout_extras: {
                                  ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                  ...(p.catalog_layout_extras || {}),
                                  restaurant_decor_color: e.target.value,
                                },
                              }))
                            }
                            style={{ width: "100%", height: 42, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.decorOpacity}</div>
                        <input
                          type="range"
                          min={0}
                          max={0.6}
                          step={0.01}
                          value={Number((layoutsEdit.catalog_layout_extras || {}).restaurant_decor_opacity ?? 0.28)}
                          onChange={(e) =>
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                restaurant_decor_opacity: Number(e.target.value),
                              },
                            }))
                          }
                          style={{ width: "100%" }}
                        />
                      </div>
                    </div>
                    ) : null}
                  </div>

                </div>
              </div>
            )}

                        {customizerPane === "form" && customizerTab === "footer" && (
              <div className="catalog-studio-footer-form studio-stack" style={{ marginTop: 14 }}>
                <div>
                  <div style={{ fontWeight: 950, fontSize: 17 }}>{tStudio.footerContact}</div>
                  <p style={{ margin: "8px 0 0", fontSize: 13, color: "#64748b", fontWeight: 700, lineHeight: 1.55 }}>
                    {studioLang === "en"
                      ? "Configure the footer colors, contact information and social links."
                      : "قم بتكوين ألوان الفوتر ومعلومات الاتصال وروابط وسائل التواصل الاجتماعي."}
                  </p>
                </div>
                <StudioPanel title="ألوان الفوتر">
                  <div className="studio-grid-2">
                    <StudioColorField label={tStudio.footerBgColor} value={storeEdit.footerBgColor} fallback="#111827" onChange={(v) => setStoreEdit((p) => ({ ...p, footerBgColor: v }))} />
                    <StudioColorField label={tStudio.footerTextColor} value={storeEdit.footerTextColor} fallback="#ffffff" onChange={(v) => setStoreEdit((p) => ({ ...p, footerTextColor: v }))} />
                  </div>
                </StudioPanel>
                <StudioPanel title={tStudio.footerImageLabel}>
                  <p style={{ margin: "0 0 10px", fontSize: 12, color: "#64748b", fontWeight: 600, lineHeight: 1.5 }}>
                    {tStudio.footerImageHint}
                  </p>
                  {String((layoutsEdit.catalog_layout_extras || {}).footer_image_url || "").trim() ? (
                    <div
                      style={{
                        width: "100%",
                        height: 120,
                        borderRadius: 12,
                        border: "1px solid rgba(15,23,42,0.12)",
                        marginBottom: 8,
                        backgroundImage: cssBackgroundImageFromUrl(
                          String((layoutsEdit.catalog_layout_extras || {}).footer_image_url || "").trim()
                        ),
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                      role="img"
                      aria-label={tStudio.footerImageLabel}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: 80,
                        borderRadius: 12,
                        border: "1px dashed rgba(15,23,42,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#94a3b8",
                        fontSize: 12,
                        fontWeight: 700,
                        marginBottom: 8,
                        background: "#f8fafc",
                      }}
                    >
                      {tStudio.footerImagePlaceholder}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <label
                      style={{
                        flex: "1 1 auto",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid rgba(15,23,42,0.12)",
                        background: "#fff",
                        cursor: footerImageUploading ? "wait" : "pointer",
                        fontWeight: 800,
                        fontSize: 12,
                        opacity: footerImageUploading ? 0.65 : 1,
                        pointerEvents: footerImageUploading ? "none" : "auto",
                      }}
                    >
                      {footerImageUploading ? (
                        <Loader2 size={14} style={{ animation: "spin 0.9s linear infinite" }} aria-hidden />
                      ) : (
                        <ImageIcon size={14} />
                      )}
                      {footerImageUploading
                        ? tStudio.heroImageUploading
                        : String((layoutsEdit.catalog_layout_extras || {}).footer_image_url || "").trim()
                          ? tStudio.changeImage
                          : tStudio.uploadImage}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={footerImageUploading}
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          setFooterImageUploading(true);
                          try {
                            const url = await uploadBuilderImage(f);
                            setLayoutsEdit((p) => ({
                              ...p,
                              catalog_layout_extras: {
                                ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                                ...(p.catalog_layout_extras || {}),
                                footer_image_url: url,
                              },
                            }));
                          } catch (err) {
                            setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }));
                          } finally {
                            setFooterImageUploading(false);
                            try {
                              e.target.value = "";
                            } catch {}
                          }
                        }}
                      />
                    </label>
                    {String((layoutsEdit.catalog_layout_extras || {}).footer_image_url || "").trim() ? (
                      <button
                        type="button"
                        disabled={footerImageUploading}
                        onClick={() =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              footer_image_url: "",
                            },
                          }))
                        }
                        style={{
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid rgba(15,23,42,0.12)",
                          background: "#fff",
                          cursor: footerImageUploading ? "not-allowed" : "pointer",
                          fontWeight: 800,
                          fontSize: 12,
                          color: "#b91c1c",
                          opacity: footerImageUploading ? 0.45 : 1,
                        }}
                      >
                        {tStudio.delete}
                      </button>
                    ) : null}
                  </div>
                  <StudioInput
                    dir="ltr"
                    value={String((layoutsEdit.catalog_layout_extras || {}).footer_image_url || "")}
                    onChange={(e) =>
                      setLayoutsEdit((p) => ({
                        ...p,
                        catalog_layout_extras: {
                          ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                          ...(p.catalog_layout_extras || {}),
                          footer_image_url: e.target.value,
                        },
                      }))
                    }
                    placeholder={tStudio.heroUrlPlaceholder}
                    style={{ marginTop: 8 }}
                  />
                  {String((layoutsEdit.catalog_layout_extras || {}).footer_image_url || "").trim() ? (
                    <StudioField
                      label={`${tStudio.footerImageOverlayLabel} (${Math.round(
                        Math.min(
                          1,
                          Math.max(0, Number((layoutsEdit.catalog_layout_extras || {}).footer_image_overlay ?? 0.45))
                        ) * 100
                      )}%)`}
                    >
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={Math.round(
                          Math.min(
                            1,
                            Math.max(0, Number((layoutsEdit.catalog_layout_extras || {}).footer_image_overlay ?? 0.45))
                          ) * 100
                        )}
                        onChange={(e) =>
                          setLayoutsEdit((p) => ({
                            ...p,
                            catalog_layout_extras: {
                              ...DEFAULT_CATALOG_LAYOUT_EXTRAS,
                              ...(p.catalog_layout_extras || {}),
                              footer_image_overlay: Number(e.target.value) / 100,
                            },
                          }))
                        }
                        style={{ width: "100%" }}
                      />
                    </StudioField>
                  ) : null}
                </StudioPanel>
                {(() => {
                  const ex = layoutsEdit.catalog_layout_extras || {};
                  const extraPhones = Array.isArray(ex.footer_extra_phones) ? ex.footer_extra_phones : [];
                  const customFields = Array.isArray(ex.footer_custom_fields) ? ex.footer_custom_fields : [];
                  const showEmail = ex.footer_show_email !== false && String(ex.footer_show_email) !== "false";
                  const setPhoneAt = (i, v) => patchFooterExtras({ footer_extra_phones: extraPhones.map((x, idx) => (idx === i ? v : x)) });
                  const setFieldAt = (i, patch) =>
                    patchFooterExtras({ footer_custom_fields: customFields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) });
                  const rowStyle = { display: "flex", gap: 6, alignItems: "center", marginTop: 6 };
                  const delBtn = (onClick) => (
                    <button type="button" className="studio-btn" onClick={onClick} aria-label="حذف" style={{ color: "#b91c1c", flexShrink: 0 }}>
                      <Trash2 size={13} />
                    </button>
                  );
                  return (
                    <>
                      <StudioPanel
                        title="معلومات التواصل"
                        action={
                          <button
                            type="button"
                            className="studio-btn studio-btn--primary"
                            onClick={() => patchFooterExtras({ footer_extra_phones: [...extraPhones, ""] })}
                          >
                            <Plus size={14} /> رقم هاتف
                          </button>
                        }
                      >
                        <StudioField label={tStudio.phone}>
                          <StudioInput value={storeEdit.phone} onChange={(e) => setStoreEdit((p) => ({ ...p, phone: e.target.value }))} dir="ltr" />
                          {extraPhones.map((ph, i) => (
                            <div key={i} style={rowStyle}>
                              <StudioInput
                                value={ph}
                                onChange={(e) => setPhoneAt(i, e.target.value)}
                                dir="ltr"
                                placeholder={`رقم هاتف ${i + 2}`}
                                style={{ flex: 1, minWidth: 0 }}
                              />
                              {delBtn(() => patchFooterExtras({ footer_extra_phones: extraPhones.filter((_, idx) => idx !== i) }))}
                            </div>
                          ))}
                        </StudioField>
                        <StudioField label="رقم الفاكس">
                          <StudioInput value={ex.footer_fax || ""} onChange={(e) => patchFooterExtras({ footer_fax: e.target.value })} dir="ltr" placeholder="اتركه فارغاً لإخفائه" />
                        </StudioField>
                        <StudioField label={tStudio.email}>
                          <StudioInput type="email" value={storeEdit.email} onChange={(e) => setStoreEdit((p) => ({ ...p, email: e.target.value }))} dir="ltr" />
                        </StudioField>
                        <StudioSwitch
                          checked={showEmail}
                          onChange={(v) => patchFooterExtras({ footer_show_email: v })}
                          label="إظهار البريد الإلكتروني في الفوتر"
                        />
                        <StudioField label={tStudio.address}>
                          <StudioTextarea value={storeEdit.address} onChange={(e) => setStoreEdit((p) => ({ ...p, address: e.target.value }))} rows={2} />
                        </StudioField>
                      </StudioPanel>
                      <StudioPanel
                        title={`حقول إضافية (${customFields.length})`}
                        action={
                          <button
                            type="button"
                            className="studio-btn studio-btn--primary"
                            onClick={() =>
                              patchFooterExtras({
                                footer_custom_fields: [...customFields, { id: `f_${Date.now().toString(36)}`, label: "", value: "" }],
                              })
                            }
                          >
                            <Plus size={14} /> إضافة
                          </button>
                        }
                      >
                        {customFields.length === 0 ? (
                          <div style={{ fontSize: 12, opacity: 0.7 }}>
                            أضف أي معلومة بالاسم والقيمة، مثل «رقم مجاني» أو «الرقم المختصر».
                          </div>
                        ) : null}
                        {customFields.map((f, i) => (
                          <div key={f.id || i} style={{ ...rowStyle, marginTop: i === 0 ? 0 : 8 }}>
                            <StudioInput
                              value={f.label || ""}
                              onChange={(e) => setFieldAt(i, { label: e.target.value })}
                              placeholder="الاسم (مثلاً: رقم مجاني)"
                              style={{ flex: 1, minWidth: 0 }}
                            />
                            <StudioInput
                              value={f.value || ""}
                              onChange={(e) => setFieldAt(i, { value: e.target.value })}
                              placeholder="القيمة"
                              dir="auto"
                              style={{ flex: 1, minWidth: 0 }}
                            />
                            {delBtn(() => patchFooterExtras({ footer_custom_fields: customFields.filter((_, idx) => idx !== i) }))}
                          </div>
                        ))}
                      </StudioPanel>
                      <StudioPanel title="وسائل التواصل">
                        <StudioField label={tStudio.facebook}>
                          <StudioInput value={storeEdit.facebook} onChange={(e) => setStoreEdit((p) => ({ ...p, facebook: e.target.value }))} dir="ltr" placeholder="https://facebook.com/..." />
                        </StudioField>
                        <StudioField label={tStudio.instagram}>
                          <StudioInput value={storeEdit.instagram} onChange={(e) => setStoreEdit((p) => ({ ...p, instagram: e.target.value }))} dir="ltr" placeholder="https://instagram.com/..." />
                        </StudioField>
                        <StudioField label="تيك توك">
                          <StudioInput value={ex.footer_tiktok || ""} onChange={(e) => patchFooterExtras({ footer_tiktok: e.target.value })} dir="ltr" placeholder="https://www.tiktok.com/@..." />
                        </StudioField>
                      </StudioPanel>
                    </>
                  );
                })()}
              </div>
            )}

{customizerPane === "form" && customizerTab === "sections" && (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                {activeSection ? (
                  <div className="catalog-studio-section-form">
                    <StudioPanel title="إعدادات السكشن">
                    <StudioSwitch
                      checked={activeSection.settings?.visible !== false}
                      onChange={(v) => patchActiveSection({ visible: v })}
                      label="إظهار السكشن"
                    />
                    </StudioPanel>
                    {(BUILDER_SECTION_VARIANTS[activeSection.type] || []).length > 0 ? (
                      <StudioPanel title="نوع القسم">
                        <StudioSelect
                          value={String(activeSection.settings?.section_variant || "")}
                          onChange={(e) => patchActiveSection({ section_variant: e.target.value })}
                        >
                          {(BUILDER_SECTION_VARIANTS[activeSection.type] || []).map((opt) => (
                            <option key={opt.value || "default"} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </StudioSelect>
                      </StudioPanel>
                    ) : null}
                    {(() => {
                      const secV = String(activeSection.settings?.section_variant || "").toLowerCase();
                      const stSet = activeSection.settings || {};
                      const onImgErr = (err) => setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }));
                      if (activeSection.type === "text") {
                        if (secV === "faq") {
                          return (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                              <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                              <StudioFaqEditor settings={stSet} patch={patchActiveSection} />
                            </div>
                          );
                        }
                        if (secV === "blog") {
                          return (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                              <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                              <StudioBlogPostsEditor settings={stSet} patch={patchActiveSection} />
                            </div>
                          );
                        }
                        if (secV === "flash_sale") {
                          return (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                              <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                              <StudioCountdownEditor settings={stSet} patch={patchActiveSection} />
                            </div>
                          );
                        }
                      }
                      if (activeSection.type === "text_image_split" && secV === "about_cards") {
                        return (
                          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                            <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                            <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                            <StudioAboutCardsEditor
                              settings={stSet}
                              patch={patchActiveSection}
                              uploadBuilderImage={uploadBuilderImage}
                              builderImageUploadKey={builderImageUploadKey}
                              setBuilderImageUploadKey={setBuilderImageUploadKey}
                              sectionId={activeSection.id}
                              onUploadError={onImgErr}
                            />
                          </div>
                        );
                      }
                      if (activeSection.type === "text_image_split" && secV === "brand_story") {
                        return (
                          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                            <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                            <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                            <StudioField label="القصة / المحتوى">
                              <StudioTextarea value={stSet.body || ""} onChange={(e) => patchActiveSection({ body: e.target.value })} rows={6} placeholder="سطر لكل فقرة" />
                            </StudioField>
                            <StudioField label="رابط الزر" hint="مثال: https://facebook.com/yourpage">
                              <StudioInput dir="ltr" value={stSet.cta_url_1 || ""} onChange={(e) => patchActiveSection({ cta_url_1: e.target.value })} placeholder="https://" />
                            </StudioField>
                            <StudioField label="نص الزر">
                              <StudioInput value={stSet.cta_label_1 || ""} onChange={(e) => patchActiveSection({ cta_label_1: e.target.value })} placeholder="اعرف المزيد" />
                            </StudioField>
                          </div>
                        );
                      }
                      if (activeSection.type === "carousel") {
                        const cvRaw = secV === "instagram" ? "brands" : secV;
                        return (
                          <div style={{ marginTop: 10 }}>
                            <StudioCarouselSectionEditor
                              settings={stSet}
                              patch={patchActiveSection}
                              patchStyle={patchActiveSectionStyle}
                              variant={cvRaw}
                              uploadBuilderImage={uploadBuilderImage}
                              builderImageUploadKey={builderImageUploadKey}
                              setBuilderImageUploadKey={setBuilderImageUploadKey}
                              sectionId={activeSection.id}
                              onUploadError={onImgErr}
                              ownerProducts={ownerProducts}
                            />
                          </div>
                        );
                      }
                      if (activeSection.type === "features_grid") {
                        const gv = secV;
                        if (gv === "stats") {
                          return (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              <StudioSectionFrameSettings
                                settings={stSet}
                                patch={patchActiveSection}
                                patchStyle={patchActiveSectionStyle}
                                showHeadingAlign={false}
                                showTypography={false}
                              />
                              <StudioStatsEditor settings={stSet} patch={patchActiveSection} />
                            </div>
                          );
                        }
                        if (gv === "deals_row") {
                          return (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                              <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                              <StudioDealsProductsEditor settings={stSet} patch={patchActiveSection} ownerProducts={ownerProducts} />
                            </div>
                          );
                        }
                        if (gv === "categories") {
                          return (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                              <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                              <StudioCategoryLayoutFields settings={stSet} patch={patchActiveSection} variant={gv} />
                              <StudioCategoryItemsEditor
                                settings={stSet}
                                patch={patchActiveSection}
                                dashboardCategories={categories}
                                uploadBuilderImage={uploadBuilderImage}
                                builderImageUploadKey={builderImageUploadKey}
                                setBuilderImageUploadKey={setBuilderImageUploadKey}
                                sectionId={activeSection.id}
                                onUploadError={onImgErr}
                              />
                            </div>
                          );
                        }
                        if (gv === "category_circles") {
                          return (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                              <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                              <StudioCategoryLayoutFields settings={stSet} patch={patchActiveSection} variant={gv} />
                              <StudioCircleItemsEditor
                                settings={stSet}
                                patch={patchActiveSection}
                                uploadBuilderImage={uploadBuilderImage}
                                builderImageUploadKey={builderImageUploadKey}
                                setBuilderImageUploadKey={setBuilderImageUploadKey}
                                sectionId={activeSection.id}
                                onUploadError={onImgErr}
                              />
                            </div>
                          );
                        }
                        if (gv === "trust") {
                          return (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                              <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                              <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                              <StudioTrustItemsEditor
                                settings={stSet}
                                patch={patchActiveSection}
                                uploadBuilderImage={uploadBuilderImage}
                                builderImageUploadKey={builderImageUploadKey}
                                setBuilderImageUploadKey={setBuilderImageUploadKey}
                                sectionId={activeSection.id}
                                onUploadError={onImgErr}
                              />
                            </div>
                          );
                        }
                      }
                                            if (activeSection.type === "video") {
                                              const videoUploadKey = `section_video:${activeSection.id}`;
                                              const posterUploadKey = `section_video_poster:${activeSection.id}`;
                                              return (
                                                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                                                  <StudioSectionFrameSettings
                                                    settings={stSet}
                                                    patch={patchActiveSection}
                                                    patchStyle={(patch) => {
                                                      const next = { ...(patch || {}) };
                                                      // لا نسمح بإعادة الخلفية الداكنة الافتراضية القديمة بالخطأ
                                                      if (
                                                        String(next.backgroundColor || "").trim().toLowerCase() === "#0f172a"
                                                      ) {
                                                        next.backgroundColor = "transparent";
                                                      }
                                                      patchActiveSectionStyle(next);
                                                    }}
                                                  />
                                                  <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                                                  <StudioPanel title="الغلاف والفيديو">
                                                    <div className="studio-grid-2">
                                                      <StudioField label="ارتفاع الغلاف" hint="يتحكم بطول إطار الفيديو/الغلاف">
                                                        <StudioSelect
                                                          value={(stSet.section_height || "md").toString()}
                                                          onChange={(e) => patchActiveSection({ section_height: e.target.value })}
                                                        >
                                                          <option value="sm">قصير (280px)</option>
                                                          <option value="md">متوسط (400px)</option>
                                                          <option value="lg">طويل (520px)</option>
                                                        </StudioSelect>
                                                      </StudioField>
                                                      <StudioField label="حواف الغلاف" hint="استدارة زوايا إطار الفيديو">
                                                        <StudioSelect
                                                          value={(stSet.radius || "lg").toString()}
                                                          onChange={(e) => patchActiveSection({ radius: e.target.value })}
                                                        >
                                                          <option value="none">حادة (0)</option>
                                                          <option value="sm">خفيف</option>
                                                          <option value="md">متوسط</option>
                                                          <option value="lg">كبير</option>
                                                          <option value="xl">كبير جداً</option>
                                                        </StudioSelect>
                                                      </StudioField>
                                                    </div>
                                                    <StudioField label="رابط الفيديو">
                                                      <StudioInput dir="ltr" value={stSet.video_url || ""} onChange={(e) => patchActiveSection({ video_url: e.target.value })} placeholder="https://example.com/video.mp4" />
                                                    </StudioField>
                                                    {uploadBuilderImage ? (
                                                      <label className="studio-btn studio-btn--primary" style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", marginBottom: 10 }}>
                                                        {builderImageUploadKey === videoUploadKey ? <Loader2 size={14} className="catalog-spin" /> : null}
                                                        {builderImageUploadKey === videoUploadKey ? "جاري الرفع..." : "رفع ملف فيديو"}
                                                        <input
                                                          type="file"
                                                          accept="video/*"
                                                          disabled={builderImageUploadKey === videoUploadKey}
                                                          style={{ display: "none" }}
                                                          onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            setBuilderImageUploadKey(videoUploadKey);
                                                            try {
                                                              const url = await uploadBuilderImage(file);
                                                              patchActiveSection({ video_url: url });
                                                            } catch (err) {
                                                              onImgErr(err);
                                                            } finally {
                                                              setBuilderImageUploadKey(null);
                                                              e.target.value = "";
                                                            }
                                                          }}
                                                        />
                                                      </label>
                                                    ) : null}
                                                    <StudioField label="صورة الغلاف" hint="تظهر مع زر التشغيل فوقها قبل تشغيل الفيديو">
                                                      <StudioInput dir="ltr" value={stSet.poster_url || ""} onChange={(e) => patchActiveSection({ poster_url: e.target.value })} placeholder="https://example.com/poster.jpg" />
                                                    </StudioField>
                                                    {uploadBuilderImage ? (
                                                      <label className="studio-btn studio-btn--secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                                        {builderImageUploadKey === posterUploadKey ? <Loader2 size={14} className="catalog-spin" /> : null}
                                                        {builderImageUploadKey === posterUploadKey ? "جاري الرفع..." : "رفع صورة الغلاف"}
                                                        <input
                                                          type="file"
                                                          accept="image/*"
                                                          disabled={builderImageUploadKey === posterUploadKey}
                                                          style={{ display: "none" }}
                                                          onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            setBuilderImageUploadKey(posterUploadKey);
                                                            try {
                                                              const url = await uploadBuilderImage(file);
                                                              patchActiveSection({ poster_url: url });
                                                            } catch (err) {
                                                              onImgErr(err);
                                                            } finally {
                                                              setBuilderImageUploadKey(null);
                                                              e.target.value = "";
                                                            }
                                                          }}
                                                        />
                                                      </label>
                                                    ) : null}
                                                    <div className="studio-grid-2">
                                                      <StudioField label="ملء الفيديو">
                                                        <StudioSelect value={stSet.video_fit || "cover"} onChange={(e) => patchActiveSection({ video_fit: e.target.value })}>
                                                          <option value="cover">تغطية</option>
                                                          <option value="contain">إظهار الفيديو كاملاً</option>
                                                        </StudioSelect>
                                                      </StudioField>
                                                      <StudioField label="تشغيل تلقائي">
                                                        <StudioSelect value={stSet.video_autoplay ? "1" : "0"} onChange={(e) => patchActiveSection({ video_autoplay: e.target.value === "1" })}>
                                                          <option value="0">لا — زر فوق الغلاف</option>
                                                          <option value="1">نعم</option>
                                                        </StudioSelect>
                                                      </StudioField>
                                                    </div>
                                                    <StudioField label="تكرار الفيديو">
                                                      <StudioSelect value={stSet.video_loop ? "1" : "0"} onChange={(e) => patchActiveSection({ video_loop: e.target.value === "1" })}>
                                                        <option value="0">لا</option>
                                                        <option value="1">نعم</option>
                                                      </StudioSelect>
                                                    </StudioField>
                                                  </StudioPanel>
                                                </div>
                                              );
                                            }
                                            if (activeSection.type === "image_banner" && (secV === "promo" || secV === "countdown")) {
                        return (
                          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                            <StudioSectionFrameSettings settings={stSet} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                            <StudioSectionHeadingFields settings={stSet} patch={patchActiveSection} />
                            <StudioPromoBannerEditor
                              settings={stSet}
                              patch={patchActiveSection}
                              uploadBuilderImage={uploadBuilderImage}
                              builderImageUploadKey={builderImageUploadKey}
                              setBuilderImageUploadKey={setBuilderImageUploadKey}
                              sectionId={activeSection.id}
                              onUploadError={onImgErr}
                            />
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {/* تم إلغاء خيار "تحت قسم المنتجات الأساسي" لأن الترتيب يُدار من قائمة الأقسام */}
                    {activeSection.type === "products_carousel" ? (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>العنوان</div>
                          <input value={activeSection.settings?.title || ""} onChange={(e) => patchActiveSection({ title: e.target.value })} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>الوصف</div>
                          <input value={activeSection.settings?.subtitle || ""} onChange={(e) => patchActiveSection({ subtitle: e.target.value })} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>حجم العنوان</div>
                            <select
                              value={(activeSection.settings?.title_size || "md").toString()}
                              onChange={(e) => patchActiveSection({ title_size: e.target.value })}
                              style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            >
                              <option value="sm">صغير</option>
                              <option value="md">متوسط</option>
                              <option value="lg">كبير</option>
                              <option value="xl">كبير جداً</option>
                            </select>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>حجم الوصف</div>
                            <select
                              value={(activeSection.settings?.subtitle_size || "md").toString()}
                              onChange={(e) => patchActiveSection({ subtitle_size: e.target.value })}
                              style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            >
                              <option value="sm">صغير</option>
                              <option value="md">متوسط</option>
                              <option value="lg">كبير</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>عدد المنتجات الظاهرة</div>
                          <select
                            value={String(Math.max(0, Math.min(4, Number(activeSection.settings?.products_per_slide) || 0)))}
                            onChange={(e) => patchActiveSection({ products_per_slide: Number(e.target.value) })}
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          >
                            <option value="0">تلقائي — نفس أعمدة قسم المنتجات (موصى به)</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                          </select>
                        </div>

                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>إضافة المنتجات (بالترتيب في الكاروسيل)</div>
                          <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 10, maxHeight: 260, overflow: "auto", background: "#fff" }}>
                            {(ownerProducts || []).slice(0, 200).map((p) => {
                              const pids = Array.isArray(activeSection.settings?.product_ids) ? activeSection.settings.product_ids : [];
                              const checked = pids.includes(p.id);
                              return (
                                <div key={p.id} className="catalog-studio-product-pick-row">
                                  <span className="catalog-studio-product-pick-row__name">{p.name_ar || p.name}</span>
                                  <StudioSwitch
                                    checked={checked}
                                    onChange={(v) => {
                                      const next = v ? Array.from(new Set([...pids, p.id])) : pids.filter((x) => x !== p.id);
                                      patchActiveSection({ product_ids: next });
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {activeSection.type === "bundle_offers" ? (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                        <StudioSectionFrameSettings
                          settings={activeSection.settings || {}}
                          patch={patchActiveSection}
                          patchStyle={patchActiveSectionStyle}
                        />
                        <StudioSectionHeadingFields settings={activeSection.settings || {}} patch={patchActiveSection} />
                        <StudioBundleOffersEditor language={studioLang === "en" ? "en" : "ar"} />
                      </div>
                    ) : null}
                    {activeSection.type === "info_cards" ? (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                        <StudioInfoCardsEditor
                          settings={activeSection.settings || {}}
                          patch={patchActiveSection}
                          uploadBuilderImage={uploadBuilderImage}
                          builderImageUploadKey={builderImageUploadKey}
                          setBuilderImageUploadKey={setBuilderImageUploadKey}
                          sectionId={activeSection.id}
                          onUploadError={(err) => setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }))}
                        />
                        <StudioSectionFrameSettings
                          settings={activeSection.settings || {}}
                          patch={patchActiveSection}
                          patchStyle={patchActiveSectionStyle}
                          showSectionHeight={false}
                        />
                      </div>
                    ) : null}
                    {activeSection.type === "custom_order" ? (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                        <StudioSectionFrameSettings
                          settings={activeSection.settings || {}}
                          patch={patchActiveSection}
                          patchStyle={patchActiveSectionStyle}
                        />
                        <StudioSectionHeadingFields settings={activeSection.settings || {}} patch={patchActiveSection} />
                        <StudioCustomOrderEditor settings={activeSection.settings || {}} patch={patchActiveSection} />
                      </div>
                    ) : null}
                    {activeSection.type === "featured_products" ? (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                        <StudioSectionFrameSettings settings={activeSection.settings || {}} patch={patchActiveSection} patchStyle={patchActiveSectionStyle} />
                        <StudioPanel title="منتجات مميزة">
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>العنوان</div>
                          <input value={activeSection.settings?.title || ""} onChange={(e) => patchActiveSection({ title: e.target.value })} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>الوصف</div>
                          <input value={activeSection.settings?.subtitle || ""} onChange={(e) => patchActiveSection({ subtitle: e.target.value })} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <StudioCardPicker
                            value={(activeSection.settings?.heading_align || "start").toString()}
                            onChange={(v) => patchActiveSection({ heading_align: String(v) })}
                            label={tStudio.productsAlign}
                            columns={3}
                            options={[
                              { value: "start", label: tStudio.productsAlignStart },
                              { value: "center", label: tStudio.productsAlignCenter },
                              { value: "end", label: tStudio.productsAlignEnd },
                            ]}
                          />
                        </div>
                        <div style={{ marginTop: 2 }}>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>طريقة عرض المنتجات</div>
                          <select
                            value={activeSection.settings?.products_layout || "grid"}
                            onChange={(e) => patchActiveSection({ products_layout: e.target.value })}
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          >
                            <option value="grid">شبكة — أعمدة</option>
                            <option value="list">قائمة (منيو)</option>
                            <option value="gallery">معرض — عمود كبير وجنبه عمودان (bento)</option>
                          </select>
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.45 }}>
                            نفس خيارات قسم المنتجات الرئيسي. «الأعمدة» تؤثر على وضع الشبكة فقط.
                          </div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          <div style={{ opacity: (activeSection.settings?.products_layout || "grid") === "grid" ? 1 : 0.45 }}>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>الأعمدة</div>
                            <select
                              value={String(activeSection.settings?.columns || 3)}
                              onChange={(e) => patchActiveSection({ columns: Number(e.target.value) })}
                              disabled={(activeSection.settings?.products_layout || "grid") !== "grid"}
                              style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            >
                              <option value="2">2</option><option value="3">3</option><option value="4">4</option>
                            </select>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>عدد الكروت</div>
                            <select value={String(activeSection.settings?.max_cards || 8)} onChange={(e) => patchActiveSection({ max_cards: Number(e.target.value) })} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}>
                              {[4,6,8,10,12].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>
                        </div>
                        <div>
                          <div style={{ marginTop: 2, fontSize: 12, fontWeight: 900 }}>اختر المنتجات</div>
                          <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 10, maxHeight: 260, overflow: "auto", background: "#fff" }}>
                            {(ownerProducts || []).slice(0, 200).map((p) => {
                              const ids = Array.isArray(activeSection.settings?.product_ids) ? activeSection.settings.product_ids : [];
                              const checked = ids.includes(p.id);
                              return (
                                <div key={p.id} className="catalog-studio-product-pick-row">
                                  <span className="catalog-studio-product-pick-row__name">{p.name_ar || p.name}</span>
                                  <StudioSwitch
                                    checked={checked}
                                    onChange={(v) => {
                                      const next = v ? Array.from(new Set([...ids, p.id])) : ids.filter((x) => x !== p.id);
                                      patchActiveSection({ product_ids: next });
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        </StudioPanel>
                      </div>
                    ) : null}

                    {activeSection.type === "features_grid" && !isProGridVariant(String(activeSection.settings?.section_variant || "")) ? (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 14 }}>
                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 12,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", marginBottom: 10 }}>عنوان القسم والوصف</div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>عنوان القسم</div>
                            <input
                              value={activeSection.settings?.title || ""}
                              onChange={(e) => patchActiveSection({ title: e.target.value })}
                              placeholder="عنوان يظهر فوق البطاقات"
                              style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            />
                          </div>
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>وصف القسم</div>
                            <input
                              value={activeSection.settings?.subtitle || ""}
                              onChange={(e) => patchActiveSection({ subtitle: e.target.value })}
                              placeholder="وصف قصير تحت العنوان"
                              style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            />
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>حجم خط عنوان القسم</div>
                              <select
                                value={(activeSection.settings?.title_size || "md").toString()}
                                onChange={(e) => patchActiveSection({ title_size: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="sm">صغير</option>
                                <option value="md">متوسط</option>
                                <option value="lg">كبير</option>
                                <option value="xl">كبير جداً</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>حجم خط وصف القسم</div>
                              <select
                                value={(activeSection.settings?.subtitle_size || "md").toString()}
                                onChange={(e) => patchActiveSection({ subtitle_size: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="sm">صغير</option>
                                <option value="md">متوسط</option>
                                <option value="lg">كبير</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 12,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", marginBottom: 10 }}>التخطيط والمسافات</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>عدد البطاقات</div>
                              <select
                                value={String(Math.max(2, Math.min(4, Number(activeSection.settings?.cards_count || 3))))}
                                onChange={(e) => {
                                  const nextCount = Math.max(2, Math.min(12, Number(e.target.value) || 3));
                                  const cur = Array.isArray(activeSection.settings?.items) ? activeSection.settings.items : [];
                                  const next = [...cur];
                                  while (next.length < nextCount) {
                                    next.push({
                                      title: "",
                                      subtitle: "",
                                      details: "",
                                      image_url: "",
                                      image_position_x: 50,
                                      image_position_y: 50,
                                      image_mode: "",
                                      cover_grad_from: "",
                                      cover_grad_to: "",
                                      cover_grad_opacity: "",
                                      text_size: "",
                                      cta_label: "",
                                      cta_url: "",
                                      btn_variant: "",
                                      btn_bg: "",
                                      btn_fg: "",
                                      btn_border: "",
                                      btn_radius: "",
                                      card_bg_color: "",
                                      card_bg_opacity: "",
                                      card_text: "",
                                      card_border_color: "",
                                      card_border_opacity: "",
                                      card_shadow: "",
                                    });
                                  }
                                  patchActiveSection({ cards_count: nextCount, items: next.slice(0, nextCount) });
                                }}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                                <option value="5">5</option>
                                <option value="6">6</option>
                                <option value="8">8</option>
                                <option value="10">10</option>
                                <option value="12">12</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>طول البطاقات</div>
                              <select
                                value={(activeSection.settings?.card_height || "md").toString()}
                                onChange={(e) => patchActiveSection({ card_height: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="sm">قصير</option>
                                <option value="md">متوسط</option>
                                <option value="lg">طويل</option>
                              </select>
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>عرض السكشن</div>
                              <select
                                value={(activeSection.settings?.container_width || "boxed").toString()}
                                onChange={(e) => patchActiveSection({ container_width: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="boxed">عادي (ضمن عرض الصفحة)</option>
                                <option value="wide">عريض</option>
                                <option value="full">عرض كامل الشاشة</option>
                              </select>
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.45 }}>
                                عند ضبط «عرض عمود المحتوى» من المظهر العام، يُستخدم 1200px أو 1400px لكل الأقسام تلقائياً. اختر «عرض كامل الشاشة» هنا فقط لقسم يخرج عن الهامش (مثل كاروسيل).
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>شكل الظل (افتراضي للبطاقات)</div>
                              <select
                                value={String(activeSection.settings?.card_shadow || "none")}
                                onChange={(e) => patchActiveSection({ card_shadow: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="none">بدون</option>
                                <option value="soft">خفيف</option>
                                <option value="md">متوسط</option>
                                <option value="lg">قوي</option>
                              </select>
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>هامش من الأعلى</div>
                              <select
                                value={(activeSection.settings?.margin_top ?? "md").toString()}
                                onChange={(e) => patchActiveSection({ margin_top: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="none">بدون</option>
                                <option value="sm">صغير</option>
                                <option value="md">متوسط</option>
                                <option value="lg">كبير</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>هامش من الأسفل</div>
                              <select
                                value={(activeSection.settings?.margin_bottom ?? "md").toString()}
                                onChange={(e) => patchActiveSection({ margin_bottom: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="none">بدون</option>
                                <option value="sm">صغير</option>
                                <option value="md">متوسط</option>
                                <option value="lg">كبير</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 12,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", marginBottom: 10 }}>خطوط البطاقة (افتراضي)</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>عنوان البطاقة</div>
                              <select
                                value={String(activeSection.settings?.card_title_size || activeSection.settings?.card_text_size || "md")}
                                onChange={(e) => patchActiveSection({ card_title_size: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="sm">صغير</option>
                                <option value="md">متوسط</option>
                                <option value="lg">كبير</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>العنوان الفرعي</div>
                              <select
                                value={String(activeSection.settings?.card_subtitle_size || activeSection.settings?.card_text_size || "md")}
                                onChange={(e) => patchActiveSection({ card_subtitle_size: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="sm">صغير</option>
                                <option value="md">متوسط</option>
                                <option value="lg">كبير</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>نص الفقرة</div>
                              <select
                                value={String(activeSection.settings?.card_text_size || "md")}
                                onChange={(e) => patchActiveSection({ card_text_size: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="sm">صغير</option>
                                <option value="md">متوسط</option>
                                <option value="lg">كبير</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 12,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", marginBottom: 10 }}>الصورة في البطاقة (افتراضي)</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div style={{ gridColumn: "1 / -1" }}>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>نمط الصورة</div>
                              <select
                                value={String(activeSection.settings?.card_image_mode || "circle")}
                                onChange={(e) => patchActiveSection({ card_image_mode: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="circle">صورة مدوّرة فوق النص</option>
                                <option value="cover">صورة كاملة والنص داخلها</option>
                              </select>
                            </div>
                          </div>

                          {String(activeSection.settings?.card_image_mode || "circle") === "cover" ? (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون التدرّج 1 (افتراضي)</div>
                                <input type="color" value={String(activeSection.settings?.card_cover_grad_from || "#0f172a")} onChange={(e) => patchActiveSection({ card_cover_grad_from: e.target.value })} style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                              </div>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون التدرّج 2 (افتراضي)</div>
                                <input type="color" value={String(activeSection.settings?.card_cover_grad_to || "#0f172a")} onChange={(e) => patchActiveSection({ card_cover_grad_to: e.target.value })} style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                              </div>
                              <div style={{ gridColumn: "1 / -1" }}>
                                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>
                                  قوة التدرّج ({Math.round(Math.min(1, Math.max(0, Number(activeSection.settings?.card_cover_grad_opacity ?? 0.45))) * 100)}%)
                                </div>
                                <input type="range" min={0} max={100} step={1} value={Math.round(Math.min(1, Math.max(0, Number(activeSection.settings?.card_cover_grad_opacity ?? 0.45))) * 100)} onChange={(e) => patchActiveSection({ card_cover_grad_opacity: Number(e.target.value) / 100 })} style={{ width: "100%" }} />
                              </div>
                            </div>
                          ) : null}
                        </div>

                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 12,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", marginBottom: 10 }}>ألوان البطاقة (افتراضي)</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>خلفية البطاقة</div>
                              <input
                                type="color"
                                value={String(activeSection.settings?.card_bg_color || "#ffffff")}
                                onChange={(e) => patchActiveSection({ card_bg_color: e.target.value })}
                                style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              />
                              <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={Math.round(Math.min(1, Math.max(0, Number(activeSection.settings?.card_bg_opacity ?? 0.6))) * 100)}
                                onChange={(e) => patchActiveSection({ card_bg_opacity: Number(e.target.value) / 100 })}
                                style={{ width: "100%", marginTop: 8 }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون نص البطاقة</div>
                              <input
                                type="color"
                                value={String(activeSection.settings?.card_text || "#0f172a")}
                                onChange={(e) => patchActiveSection({ card_text: e.target.value })}
                                style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              />
                            </div>
                          </div>
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون إطار البطاقة</div>
                            <input
                              type="color"
                              value={String(activeSection.settings?.card_border_color || "#0f172a")}
                              onChange={(e) => patchActiveSection({ card_border_color: e.target.value })}
                              style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            />
                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 8, marginBottom: 4 }}>
                              شفافية الإطار ({Math.round(Math.min(1, Math.max(0, Number(activeSection.settings?.card_border_opacity ?? 0.12))) * 100)}%)
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={1}
                              value={Math.round(Math.min(1, Math.max(0, Number(activeSection.settings?.card_border_opacity ?? 0.12))) * 100)}
                              onChange={(e) => patchActiveSection({ card_border_opacity: Number(e.target.value) / 100 })}
                              style={{ width: "100%" }}
                            />
                          </div>
                        </div>

                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 12,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 950, color: "#334155", marginBottom: 10 }}>زر البطاقة (افتراضي)</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>نمط الزر</div>
                              <select
                                value={String(activeSection.settings?.card_btn_variant || "outline")}
                                onChange={(e) => patchActiveSection({ card_btn_variant: e.target.value })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              >
                                <option value="outline">حدود</option>
                                <option value="solid">ممتلئ</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>استدارة الزر</div>
                              <input
                                type="number"
                                min={0}
                                max={24}
                                value={Number(activeSection.settings?.card_btn_radius ?? 12)}
                                onChange={(e) => patchActiveSection({ card_btn_radius: Number(e.target.value) || 12 })}
                                style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                              />
                            </div>
                          </div>

                          {String(activeSection.settings?.card_btn_variant || "outline") === "solid" ? (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون خلفية الزر</div>
                                <input type="color" value={String(activeSection.settings?.card_btn_bg || "#0f172a")} onChange={(e) => patchActiveSection({ card_btn_bg: e.target.value })} style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                              </div>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون نص الزر</div>
                                <input type="color" value={String(activeSection.settings?.card_btn_fg || "#ffffff")} onChange={(e) => patchActiveSection({ card_btn_fg: e.target.value })} style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون إطار ونص الزر</div>
                              <input type="color" value={String(activeSection.settings?.card_btn_border || "#0f172a")} onChange={(e) => patchActiveSection({ card_btn_border: e.target.value })} style={{ width: "100%", height: 40, borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }} />
                            </div>
                          )}
                        </div>

                        <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a" }}>إعدادات كل بطاقة</div>
                        {(() => {
                          const count = Math.max(2, Math.min(4, Number(activeSection.settings?.cards_count || 3)));
                          const items = Array.isArray(activeSection.settings?.items) ? activeSection.settings.items : [];
                          const fixed = [...items];
                          while (fixed.length < count) {
                            fixed.push({
                              title: "",
                              subtitle: "",
                              details: "",
                              image_url: "",
                              image_position_x: 50,
                              image_position_y: 50,
                              image_mode: "",
                              cover_grad_from: "",
                              cover_grad_to: "",
                              cover_grad_opacity: "",
                              text_size: "",
                              cta_label: "",
                              cta_url: "",
                              btn_variant: "",
                              btn_bg: "",
                              btn_fg: "",
                              btn_border: "",
                              btn_radius: "",
                              card_bg_color: "",
                              card_bg_opacity: "",
                              card_text: "",
                              card_border_color: "",
                              card_border_opacity: "",
                              card_shadow: "",
                            });
                          }

                          const shadowOptions = [
                            { value: "", label: "افتراضي" },
                            { value: "none", label: "بدون" },
                            { value: "soft", label: "خفيف" },
                            { value: "md", label: "متوسط" },
                            { value: "lg", label: "قوي" },
                          ];

                          return fixed.slice(0, count).map((it, i) => (
                            <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 10, background: "#fafafa" }}>
                              <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>بطاقة {i + 1}</div>
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>عنوان البطاقة</div>
                                <input
                                  value={it.title || ""}
                                  onChange={(e) => {
                                    const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), title: e.target.value } : x));
                                    patchActiveSection({ items: next });
                                  }}
                                  placeholder="العنوان"
                                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                />
                              </div>
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>العنوان الفرعي</div>
                                <input
                                  value={it.subtitle || ""}
                                  onChange={(e) => {
                                    const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), subtitle: e.target.value } : x));
                                    patchActiveSection({ items: next });
                                  }}
                                  placeholder="نص فرعي"
                                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                />
                              </div>
                              <div style={{ marginBottom: 10 }}>
                                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>نص الفقرة</div>
                                <textarea
                                  value={it.details || ""}
                                  onChange={(e) => {
                                    const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), details: e.target.value } : x));
                                    patchActiveSection({ items: next });
                                  }}
                                  placeholder="التفاصيل"
                                  rows={3}
                                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)", resize: "vertical" }}
                                />
                              </div>

                              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginTop: 4, marginBottom: 4 }}>النصوص والزر</div>
                              <div style={{ marginBottom: 10 }}>
                                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>تكبير/تصغير كل النصوص لهذه البطاقة (اختياري)</div>
                                <select
                                  value={String(it.text_size || "")}
                                  onChange={(e) => {
                                    const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), text_size: e.target.value } : x));
                                    patchActiveSection({ items: next });
                                  }}
                                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                >
                                  <option value="">بدون — استخدم الأحجام العامة</option>
                                  <option value="sm">صغير</option>
                                  <option value="md">متوسط</option>
                                  <option value="lg">كبير</option>
                                </select>
                              </div>

                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>نص الزر</div>
                                  <input
                                    value={it.cta_label || ""}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), cta_label: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    placeholder="مثال: تعرّف أكثر"
                                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  />
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>رابط الزر (اختياري)</div>
                                  <input
                                    value={it.cta_url || ""}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), cta_url: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    placeholder="https://…"
                                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  />
                                </div>
                              </div>

                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>نمط الزر</div>
                                  <select
                                    value={String(it.btn_variant || "")}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), btn_variant: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  >
                                    <option value="">افتراضي من الأعلى</option>
                                    <option value="outline">حدود</option>
                                    <option value="solid">ممتلئ</option>
                                  </select>
                                </div>
                                <div />
                              </div>

                              {(() => {
                                const effBtnVar = String(it.btn_variant || activeSection.settings?.card_btn_variant || "outline").trim();
                                if (effBtnVar === "solid") {
                                  return (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون خلفية الزر</div>
                                        <input type="color" value={String(it.btn_bg || activeSection.settings?.card_btn_bg || "#0f172a")} onChange={(e) => { const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), btn_bg: e.target.value } : x)); patchActiveSection({ items: next }); }} style={{ width: "100%", height: 42, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }} />
                                      </div>
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون نص الزر</div>
                                        <input type="color" value={String(it.btn_fg || activeSection.settings?.card_btn_fg || "#ffffff")} onChange={(e) => { const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), btn_fg: e.target.value } : x)); patchActiveSection({ items: next }); }} style={{ width: "100%", height: 42, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }} />
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                    <div>
                                      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون إطار ونص الزر</div>
                                      <input type="color" value={String(it.btn_border || activeSection.settings?.card_btn_border || "#0f172a")} onChange={(e) => { const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), btn_border: e.target.value } : x)); patchActiveSection({ items: next }); }} style={{ width: "100%", height: 42, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>استدارة الزر</div>
                                      <input type="number" min={0} max={24} value={it.btn_radius !== null && it.btn_radius !== undefined && it.btn_radius !== "" ? Number(it.btn_radius) : Number(activeSection.settings?.card_btn_radius ?? 12)} onChange={(e) => { const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), btn_radius: Number(e.target.value) || 12 } : x)); patchActiveSection({ items: next }); }} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }} />
                                    </div>
                                  </div>
                                );
                              })()}

                              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginTop: 6, marginBottom: 4 }}>الصورة</div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                <div style={{ gridColumn: "1 / -1" }}>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>رابط الصورة (اختياري)</div>
                                  <input
                                    value={it.image_url || ""}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), image_url: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    placeholder="https://…"
                                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  />
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>رفع صورة</div>
                                  {(() => {
                                    const fgCardUploadKey = `fg_card:${activeSection.id}:${i}`;
                                    const fgCardUploading = builderImageUploadKey === fgCardUploadKey;
                                    return (
                                      <label
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: 10,
                                          border: "1px solid rgba(15,23,42,0.12)",
                                          borderRadius: 10,
                                          background: "#fff",
                                          padding: "10px 12px",
                                          cursor: fgCardUploading ? "wait" : "pointer",
                                          fontWeight: 900,
                                          minHeight: 42,
                                          boxSizing: "border-box",
                                          opacity: fgCardUploading ? 0.65 : 1,
                                          pointerEvents: fgCardUploading ? "none" : "auto",
                                        }}
                                      >
                                        {fgCardUploading ? (
                                          <Loader2 size={14} style={{ animation: "spin 0.9s linear infinite" }} aria-hidden />
                                        ) : null}
                                        {fgCardUploading ? tStudio.heroImageUploading : "اختيار ملف"}
                                        <input
                                          type="file"
                                          accept="image/*"
                                          disabled={fgCardUploading}
                                          style={{ display: "none" }}
                                          onChange={async (e) => {
                                            const f = e.target.files?.[0];
                                            if (!f) return;
                                            setBuilderImageUploadKey(fgCardUploadKey);
                                            try {
                                              const url = await uploadBuilderImage(f);
                                              const next = fixed.slice(0, count).map((x, idx) =>
                                                idx === i
                                                  ? {
                                                      ...(x || {}),
                                                      image_url: url,
                                                      image_position_x: x?.image_position_x ?? 50,
                                                      image_position_y: x?.image_position_y ?? 50,
                                                    }
                                                  : x
                                              );
                                              patchActiveSection({ items: next });
                                            } catch (err) {
                                              setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }));
                                            } finally {
                                              setBuilderImageUploadKey(null);
                                              try {
                                                e.target.value = "";
                                              } catch {}
                                            }
                                          }}
                                        />
                                      </label>
                                    );
                                  })()}
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>نمط عرض الصورة</div>
                                  <select
                                    value={String(it.image_mode || "")}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), image_mode: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  >
                                    <option value="">افتراضي من الأعلى</option>
                                    <option value="circle">مدوّرة فوق النص</option>
                                    <option value="cover">صورة كاملة والنص داخلها</option>
                                  </select>
                                </div>
                              </div>

                              {String(it.image_url || "").trim() ? (
                                <div style={{ marginBottom: 10 }}>
                                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.heroImagePosition}</div>
                                  <HeroImageFocalPicker
                                    key={`fg-card:${activeSection.id}:${i}:${it.image_url}`}
                                    imageUrl={String(it.image_url).trim()}
                                    backgroundImageCss={cssBackgroundImageFromUrl(it.image_url)}
                                    positionX={it.image_position_x}
                                    positionY={it.image_position_y}
                                    onChange={(posX, posY) => {
                                      const next = fixed.slice(0, count).map((row, idx) =>
                                        idx === i ? { ...(row || {}), image_position_x: posX, image_position_y: posY } : row
                                      );
                                      patchActiveSection({ items: next });
                                    }}
                                    height={160}
                                    ariaLabel={tStudio.heroImagePreview}
                                    hint={tStudio.heroImagePositionHint}
                                  />
                                </div>
                              ) : null}

                              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginTop: 4, marginBottom: 4 }}>تدرّج الصورة (عند اختيار «صورة كاملة»)</div>
                              {String(it.image_mode || "") === "cover" ? (
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون التدرّج 1</div>
                                    <input type="color" value={String(it.cover_grad_from || activeSection.settings?.card_cover_grad_from || "#0f172a")} onChange={(e) => { const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), cover_grad_from: e.target.value } : x)); patchActiveSection({ items: next }); }} style={{ width: "100%", height: 42, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }} />
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون التدرّج 2</div>
                                    <input type="color" value={String(it.cover_grad_to || activeSection.settings?.card_cover_grad_to || "#0f172a")} onChange={(e) => { const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), cover_grad_to: e.target.value } : x)); patchActiveSection({ items: next }); }} style={{ width: "100%", height: 42, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }} />
                                  </div>
                                  <div style={{ gridColumn: "1 / -1" }}>
                                    {(() => {
                                      const gOp =
                                        it.cover_grad_opacity !== null && it.cover_grad_opacity !== undefined && it.cover_grad_opacity !== ""
                                          ? Number(it.cover_grad_opacity)
                                          : Number(activeSection.settings?.card_cover_grad_opacity ?? 0.45);
                                      const gPct = Math.round(Math.min(1, Math.max(0, gOp)) * 100);
                                      return (
                                        <>
                                          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>قوة التدرّج ({gPct}%)</div>
                                          <input type="range" min={0} max={100} step={1} value={gPct} onChange={(e) => { const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), cover_grad_opacity: Number(e.target.value) / 100 } : x)); patchActiveSection({ items: next }); }} style={{ width: "100%" }} />
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              ) : null}

                              <div style={{ fontSize: 11, fontWeight: 900, color: "#64748b", marginTop: 4, marginBottom: 4 }}>مظهر البطاقة</div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>ظل البطاقة</div>
                                  <select
                                    value={String(it.card_shadow || "")}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), card_shadow: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  >
                                    {shadowOptions.map((o) => (
                                      <option key={o.value || "default"} value={o.value}>{o.label}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون نص البطاقة (اختياري)</div>
                                  <input
                                    type="color"
                                    value={/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(it.card_text || "").trim()) ? String(it.card_text).trim() : String(activeSection.settings?.card_text || "#0f172a")}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), card_text: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    style={{ width: "100%", height: 42, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  />
                                </div>
                              </div>

                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10, alignItems: "start" }}>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>خلفية فوق الصورة (اختياري، يظهر مع «صورة كاملة»)</div>
                                  <input
                                    type="color"
                                    value={String(it.card_bg_color || activeSection.settings?.card_bg_color || "#ffffff")}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), card_bg_color: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    style={{ width: "100%", height: 42, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  />
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={Math.round(
                                      Math.min(
                                        1,
                                        Math.max(
                                          0,
                                          it.card_bg_opacity !== null && it.card_bg_opacity !== undefined && it.card_bg_opacity !== ""
                                            ? Number(it.card_bg_opacity)
                                            : Number(activeSection.settings?.card_bg_opacity ?? 0.6)
                                        )
                                      ) * 100
                                    )}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), card_bg_opacity: Number(e.target.value) / 100 } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    style={{ width: "100%", marginTop: 8 }}
                                  />
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>لون إطار البطاقة (اختياري)</div>
                                  <input
                                    type="color"
                                    value={String(it.card_border_color || activeSection.settings?.card_border_color || "#0f172a")}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), card_border_color: e.target.value } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    style={{ width: "100%", height: 42, borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  />
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={Math.round(
                                      Math.min(
                                        1,
                                        Math.max(
                                          0,
                                          it.card_border_opacity !== null && it.card_border_opacity !== undefined && it.card_border_opacity !== ""
                                            ? Number(it.card_border_opacity)
                                            : Number(activeSection.settings?.card_border_opacity ?? 0.12)
                                        )
                                      ) * 100
                                    )}
                                    onChange={(e) => {
                                      const next = fixed.slice(0, count).map((x, idx) => (idx === i ? { ...(x || {}), card_border_opacity: Number(e.target.value) / 100 } : x));
                                      patchActiveSection({ items: next });
                                    }}
                                    style={{ width: "100%", marginTop: 8 }}
                                  />
                                </div>
                              </div>

                            </div>
                          ));
                        })()}
                      </div>
                    ) : null}
                    {!hasDedicatedStudioPanel(activeSection.type, String(activeSection.settings?.section_variant || "")) ? (
                      <StudioSectionFrameSettings
                        settings={activeSection.settings || {}}
                        patch={patchActiveSection}
                        patchStyle={patchActiveSectionStyle}
                      />
                    ) : null}

                    {activeSection.type === "hero" && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>محاذاة النص داخل السكشن (عمودياً)</div>
                        <select
                          value={(activeSection.settings?.hero_copy_valign || "start").toString()}
                          onChange={(e) => patchActiveSection({ hero_copy_valign: e.target.value })}
                          style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        >
                          <option value="start">أعلى</option>
                          <option value="center">وسط</option>
                          <option value="end">أسفل</option>
                        </select>
                      </div>
                    )}

                    {activeSection.type === "hero" && (
                      <div style={{ marginTop: 10 }}>
                        <StudioSwitch
                          checked={Boolean(activeSection.settings?.hero_as_carousel)}
                          onChange={(v) => patchActiveSection({ hero_as_carousel: v })}
                          label="سلايدر صور (عدة صور للهيرو)"
                        />
                        {activeSection.settings?.hero_as_carousel ? (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                              <ImageIcon size={16} /> صور السلايدر
                            </div>
                            {(Array.isArray(activeSection.settings?.images) && activeSection.settings.images.length
                              ? activeSection.settings.images
                              : [{ url: "" }]
                            ).map((im, ii) => {
                              const u = typeof im === "string" ? im : im?.url;
                              return (
                                <div key={ii} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                                  <input
                                    value={u || ""}
                                    onChange={(e) => {
                                      const cur = [...(Array.isArray(activeSection.settings?.images) ? activeSection.settings.images : [])];
                                      cur[ii] = { url: e.target.value };
                                      patchActiveSection({ images: cur });
                                    }}
                                    placeholder={`رابط صورة ${ii + 1}`}
                                    style={{ flex: 1, padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)" }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const cur = [...(Array.isArray(activeSection.settings?.images) ? activeSection.settings.images : [])];
                                      cur.splice(ii, 1);
                                      patchActiveSection({ images: cur.length ? cur : [{ url: "" }] });
                                    }}
                                    style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(15,23,42,0.12)", background: "#fff", fontWeight: 800, fontSize: 12 }}
                                  >
                                    حذف
                                  </button>
                                </div>
                              );
                            })}
                            {(() => {
                              const uploadKey = `hero_carousel_add:${activeSection.id}`;
                              const uploading = builderImageUploadKey === uploadKey;
                              return (
                                <label
                                  style={{
                                    marginTop: 10,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 10,
                                    border: "1px solid rgba(15,23,42,0.12)",
                                    borderRadius: 10,
                                    background: "#fff",
                                    padding: "10px 12px",
                                    cursor: uploading ? "wait" : "pointer",
                                    fontWeight: 900,
                                    fontSize: 12,
                                    minHeight: 42,
                                    boxSizing: "border-box",
                                    opacity: uploading ? 0.65 : 1,
                                    pointerEvents: uploading ? "none" : "auto",
                                  }}
                                >
                                  {uploading ? (
                                    <Loader2 size={14} style={{ animation: "spin 0.9s linear infinite" }} aria-hidden />
                                  ) : null}
                                  {uploading ? tStudio.heroImageUploading : "اختيار ملف"}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    disabled={uploading}
                                    style={{ display: "none" }}
                                    onChange={async (e) => {
                                      const f = e.target.files?.[0];
                                      if (!f) return;
                                      setBuilderImageUploadKey(uploadKey);
                                      try {
                                        const url = await uploadBuilderImage(f);
                                        const cur = [...(Array.isArray(activeSection.settings?.images) ? activeSection.settings.images : [])];
                                        cur.push({ url });
                                        patchActiveSection({ images: cur });
                                      } catch (err) {
                                        setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }));
                                      } finally {
                                        setBuilderImageUploadKey(null);
                                        try {
                                          e.target.value = "";
                                        } catch {}
                                      }
                                    }}
                                  />
                                </label>
                              );
                            })()}
                            <button
                              type="button"
                              onClick={() => {
                                const cur = [...(Array.isArray(activeSection.settings?.images) ? activeSection.settings.images : [])];
                                cur.push({ url: "" });
                                patchActiveSection({ images: cur });
                              }}
                              style={{
                                marginTop: 8,
                                width: "100%",
                                padding: "8px 10px",
                                borderRadius: 10,
                                border: "1px solid rgba(15,23,42,0.12)",
                                background: "#f8fafc",
                                fontWeight: 800,
                                fontSize: 12,
                              }}
                            >
                              + إضافة حقل رابط
                            </button>
                          </div>
                        ) : (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                              <ImageIcon size={16} /> صورة الهيرو
                            </div>
                            {(() => {
                              const uploadKey = `section_hero_single:${activeSection.id}`;
                              const uploading = builderImageUploadKey === uploadKey;
                              return (
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 10,
                                    border: "1px solid rgba(15,23,42,0.12)",
                                    borderRadius: 10,
                                    background: "#fff",
                                    padding: "10px 12px",
                                    cursor: uploading ? "wait" : "pointer",
                                    fontWeight: 900,
                                    fontSize: 12,
                                    minHeight: 42,
                                    boxSizing: "border-box",
                                    opacity: uploading ? 0.65 : 1,
                                    pointerEvents: uploading ? "none" : "auto",
                                  }}
                                >
                                  {uploading ? (
                                    <Loader2 size={14} style={{ animation: "spin 0.9s linear infinite" }} aria-hidden />
                                  ) : null}
                                  {uploading ? tStudio.heroImageUploading : "اختيار ملف"}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    disabled={uploading}
                                    style={{ display: "none" }}
                                    onChange={async (e) => {
                                      const f = e.target.files?.[0];
                                      if (!f) return;
                                      setBuilderImageUploadKey(uploadKey);
                                      try {
                                        const url = await uploadBuilderImage(f);
                                        patchActiveSection({ images: [{ url }] });
                                      } catch (err) {
                                        setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }));
                                      } finally {
                                        setBuilderImageUploadKey(null);
                                        try {
                                          e.target.value = "";
                                        } catch {}
                                      }
                                    }}
                                  />
                                </label>
                              );
                            })()}
                            <input
                              value={normalizeImageUrlFromSettings(activeSection.settings) || ""}
                              onChange={(e) => patchActiveSection({ images: [{ url: e.target.value }] })}
                              placeholder="أو رابط صورة"
                              style={{ width: "100%", marginTop: 8, padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {(activeSection.type === "image_banner" || activeSection.type === "text_image_split") && !isProImageBannerVariant(String(activeSection.settings?.section_variant || "")) && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                          <ImageIcon size={16} /> صورة السكشن
                        </div>
                        {(() => {
                          const uploadKey = `section_banner:${activeSection.id}`;
                          const uploading = builderImageUploadKey === uploadKey;
                          return (
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                border: "1px solid rgba(15,23,42,0.12)",
                                borderRadius: 10,
                                background: "#fff",
                                padding: "10px 12px",
                                cursor: uploading ? "wait" : "pointer",
                                fontWeight: 900,
                                fontSize: 12,
                                minHeight: 42,
                                boxSizing: "border-box",
                                opacity: uploading ? 0.65 : 1,
                                pointerEvents: uploading ? "none" : "auto",
                              }}
                            >
                              {uploading ? (
                                <Loader2 size={14} style={{ animation: "spin 0.9s linear infinite" }} aria-hidden />
                              ) : null}
                              {uploading ? tStudio.heroImageUploading : "اختيار ملف"}
                              <input
                                type="file"
                                accept="image/*"
                                disabled={uploading}
                                style={{ display: "none" }}
                                onChange={async (e) => {
                                  const f = e.target.files?.[0];
                                  if (!f) return;
                                  setBuilderImageUploadKey(uploadKey);
                                  try {
                                    const url = await uploadBuilderImage(f);
                                    patchActiveSection({
                                      images: [{ url }],
                                      image_position_x: activeSection.settings?.image_position_x ?? 50,
                                      image_position_y: activeSection.settings?.image_position_y ?? 50,
                                    });
                                  } catch (err) {
                                    setSaveError(formatCustomizerSaveError({ message: err?.message || "Upload failed" }));
                                  } finally {
                                    setBuilderImageUploadKey(null);
                                    try {
                                      e.target.value = "";
                                    } catch {}
                                  }
                                }}
                              />
                            </label>
                          );
                        })()}
                        <input
                          value={normalizeImageUrlFromSettings(activeSection.settings) || ""}
                          onChange={(e) => patchActiveSection({ images: [{ url: e.target.value }] })}
                          placeholder="أو رابط صورة"
                          style={{ width: "100%", marginTop: 8, padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        />
                        {activeSection.type === "text_image_split" && normalizeImageUrlFromSettings(activeSection.settings) ? (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>{tStudio.heroImagePosition}</div>
                            <HeroImageFocalPicker
                              key={`split:${activeSection.id}:${normalizeImageUrlFromSettings(activeSection.settings)}`}
                              imageUrl={normalizeImageUrlFromSettings(activeSection.settings)}
                              backgroundImageCss={cssBackgroundImageFromUrl(normalizeImageUrlFromSettings(activeSection.settings))}
                              positionX={activeSection.settings?.image_position_x}
                              positionY={activeSection.settings?.image_position_y}
                              onChange={(posX, posY) => patchActiveSection({ image_position_x: posX, image_position_y: posY })}
                              height={160}
                              ariaLabel={tStudio.heroImagePreview}
                              hint={tStudio.heroImagePositionHint}
                            />
                          </div>
                        ) : null}
                      </div>
                    )}

                    {activeSection.type === "image_banner" && (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                        {!["promo", "countdown", "dual_promo"].includes(String(activeSection.settings?.section_variant || "").toLowerCase()) ? (
                          <StudioSectionHeadingFields settings={activeSection.settings || {}} patch={patchActiveSection} />
                        ) : null}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>رابط عند الضغط (اختياري)</div>
                          <input
                            dir="ltr"
                            value={activeSection.settings?.link_url || ""}
                            onChange={(e) => patchActiveSection({ link_url: e.target.value })}
                            placeholder="https://"
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>نص بديل للصورة (وصولية)</div>
                          <input
                            value={activeSection.settings?.alt_text || ""}
                            onChange={(e) => patchActiveSection({ alt_text: e.target.value })}
                            placeholder={String(settings.storeName || "").trim() || "وصف مختصر للصورة"}
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>ملء الإطار</div>
                          <select
                            value={(activeSection.settings?.image_fit || "cover").toString()}
                            onChange={(e) => patchActiveSection({ image_fit: e.target.value })}
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          >
                            <option value="cover">تغطية — قص الصورة لملء العرض</option>
                            <option value="contain">احتواء — إظهار الصورة كاملة</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {((activeSection.type === "text" && !["faq", "blog", "flash_sale"].includes(String(activeSection.settings?.section_variant || "").toLowerCase()))
                      || (activeSection.type === "text_image_split"
                        && !["about_cards", "brand_story"].includes(String(activeSection.settings?.section_variant || "").toLowerCase()))) && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>الفقرة / المحتوى</div>
                        <textarea value={activeSection.settings?.body || ""} onChange={(e) => patchActiveSection({ body: e.target.value })} rows={5} style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", resize: "vertical" }} />
                      </div>
                    )}

                    {activeSection.type === "text_image_split"
                      && !["about_cards", "brand_story"].includes(String(activeSection.settings?.section_variant || "").toLowerCase()) && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>توزيع الصورة والنص</div>
                        <select
                          value={(activeSection.settings?.image_side || "left").toString()}
                          onChange={(e) => patchActiveSection({ image_side: e.target.value })}
                          style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                        >
                          <option value="left">الصورة على اليسار — النص على اليمين</option>
                          <option value="right">الصورة على اليمين — النص على اليسار</option>
                        </select>
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 6 }}>قائمة نقطية (سطر لكل بند)</div>
                          <textarea
                            value={activeSection.settings?.bullet_list || ""}
                            onChange={(e) => patchActiveSection({ bullet_list: e.target.value })}
                            rows={4}
                            placeholder={"مثال:\nجودة عالية\nشحن سريع"}
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)", resize: "vertical" }}
                          />
                        </div>
                        <div style={{ marginTop: 12, fontWeight: 950, marginBottom: 6 }}>روابط التواصل</div>
                        <div style={{ display: "grid", gap: 8 }}>
                          {[
                            ["social_instagram", "إنستغرام"],
                            ["social_facebook", "فيسبوك"],
                            ["social_twitter", "X / تويتر"],
                            ["social_whatsapp", "واتساب"],
                            ["social_tiktok", "تيك توك"],
                          ].map(([key, lab]) => (
                            <input
                              key={key}
                              value={activeSection.settings?.[key] || ""}
                              onChange={(e) => patchActiveSection({ [key]: e.target.value })}
                              placeholder={`${lab} — رابط`}
                              style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                            />
                          ))}
                        </div>
                        <div style={{ marginTop: 12, fontWeight: 950, marginBottom: 6 }}>أزرار</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          <input
                            value={activeSection.settings?.cta_label_1 || ""}
                            onChange={(e) => patchActiveSection({ cta_label_1: e.target.value })}
                            placeholder="نص الزر الأول"
                            style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                          <input
                            value={activeSection.settings?.cta_url_1 || ""}
                            onChange={(e) => patchActiveSection({ cta_url_1: e.target.value })}
                            placeholder="رابط الزر الأول"
                            style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                          <input
                            value={activeSection.settings?.cta_label_2 || ""}
                            onChange={(e) => patchActiveSection({ cta_label_2: e.target.value })}
                            placeholder="نص الزر الثاني"
                            style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                          <input
                            value={activeSection.settings?.cta_url_2 || ""}
                            onChange={(e) => patchActiveSection({ cta_url_2: e.target.value })}
                            placeholder="رابط الزر الثاني"
                            style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(15,23,42,0.12)" }}
                          />
                        </div>
                      </div>
                    )}

                    {/* تم نقل اختيار المنتجات + العنوان + الوصف لأعلى الفورم */}

                    {/* تم نقل إعدادات كاروسيل المنتجات إلى أعلى الفورم */}

                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>اختر سكشن من «الأقسام المضافة»</div>
                )}
              </div>
            )}
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {authOpen && (
        <div
          className="auth-modal-overlay"
          onClick={closeAuth}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '18px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(520px, 100%)',
              background: '#fff',
              borderRadius: '18px',
              padding: '0px',
              boxShadow: '0 18px 50px rgba(0,0,0,0.16)',
              direction: 'rtl',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '14px 16px 10px',
                background: '#fff',
                color: '#111',
                borderBottom: '1px solid #eef2f7',
              }}
            >
              <div className="d-flex align-items-start justify-content-between gap-2">
                <div>
                  <div style={{ fontWeight: 950, fontSize: '1.05rem', lineHeight: 1.2 }}>
                    {authMode === 'register' ? 'إنشاء حساب' : 'تسجيل الدخول'}
                  </div>
                  <div style={{ marginTop: 6, fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
                    {authMode === 'register'
                      ? 'أنشئ حسابك لحفظ السلة'
                      : 'سجّل دخولك لحفظ السلة'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeAuth}
                  aria-label="close"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    color: '#111',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Mode switch */}
              <div
                style={{
                  marginTop: 12,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 6,
                }}
              >
                <button
                  type="button"
                  onClick={() => setAuthMode('login')}
                  style={{
                    borderRadius: 10,
                    border: 'none',
                    padding: '10px 12px',
                    fontWeight: 950,
                    fontSize: 13,
                    background: authMode === 'login' ? '#fff' : 'transparent',
                    color: '#111',
                    boxShadow: authMode === 'login' ? '0 6px 16px rgba(0,0,0,0.10)' : 'none',
                  }}
                >
                  تسجيل الدخول
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMode('register')}
                  style={{
                    borderRadius: 10,
                    border: 'none',
                    padding: '10px 12px',
                    fontWeight: 950,
                    fontSize: 13,
                    background: authMode === 'register' ? '#fff' : 'transparent',
                    color: '#111',
                    boxShadow: authMode === 'register' ? '0 6px 16px rgba(0,0,0,0.10)' : 'none',
                  }}
                >
                  إنشاء حساب
                </button>
              </div>
            </div>

            <div style={{ padding: '14px 16px 16px' }}>
            {authError && (
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#991b1b',
                  padding: '10px 12px',
                  borderRadius: '12px',
                  fontWeight: 800,
                  marginBottom: 10,
                }}
              >
                {authError}
              </div>
            )}

            <form onSubmit={handleAuthSubmit} className="d-grid gap-2">
              {authMode === 'register' && (
                <div className="d-grid gap-1">
                  <label style={{ fontWeight: 900, color: '#111', fontSize: 13 }}>الاسم</label>
                  <input
                    className="form-control"
                    name="storefront_full_name"
                    autoComplete="name"
                    value={authForm.full_name}
                    onChange={(e) =>
                      setAuthForm((p) => ({ ...p, full_name: e.target.value }))
                    }
                    required
                    placeholder="مثال: أحمد محمد"
                    style={{
                      borderRadius: 12,
                      padding: '11px 12px',
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      fontWeight: 700,
                    }}
                  />
                </div>
              )}

              <div className="d-grid gap-1">
                <label style={{ fontWeight: 900, color: '#111', fontSize: 13 }}>رقم الجوال</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-control"
                    name="storefront_phone"
                    inputMode="tel"
                    autoComplete={authMode === 'login' ? 'username' : 'off'}
                    value={authForm.phone}
                    onChange={(e) =>
                      setAuthForm((p) => ({ ...p, phone: e.target.value }))
                    }
                    required
                    placeholder="مثال: 0591234567"
                    style={{
                      borderRadius: 12,
                      padding: '11px 12px',
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      fontWeight: 800,
                      letterSpacing: '0.02em',
                    }}
                  />
                </div>
              </div>

              <div className="d-grid gap-1">
                <label style={{ fontWeight: 900, color: '#111', fontSize: 13 }}>كلمة المرور</label>
                <input
                  className="form-control"
                  type="password"
                  name={authMode === 'login' ? 'storefront_password' : 'storefront_new_password'}
                  autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                  value={authForm.password}
                  onChange={(e) =>
                    setAuthForm((p) => ({ ...p, password: e.target.value }))
                  }
                  required
                  minLength={6}
                  maxLength={72}
                  placeholder="********"
                  style={{
                    borderRadius: 12,
                    padding: '11px 12px',
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    fontWeight: 800,
                  }}
                />
              </div>

              {authMode === 'register' && (
                <div className="d-grid gap-1">
                  <label style={{ fontWeight: 900, color: '#111', fontSize: 13 }}>تأكيد كلمة المرور</label>
                  <input
                    className="form-control"
                    type="password"
                    name="storefront_new_password_confirm"
                    autoComplete="new-password"
                    value={authForm.password_confirm}
                    onChange={(e) =>
                      setAuthForm((p) => ({
                        ...p,
                        password_confirm: e.target.value,
                      }))
                    }
                    required
                    minLength={6}
                    maxLength={72}
                    placeholder="********"
                    style={{
                      borderRadius: 12,
                      padding: '11px 12px',
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      fontWeight: 800,
                    }}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={authLoading}
                style={{
                  borderRadius: 14,
                  border: 'none',
                  padding: '12px 14px',
                  fontWeight: 950,
                  background: authLoading ? '#e5e7eb' : catalogThemePrimary,
                  color: authLoading ? '#6b7280' : catalogThemeSecondary,
                  boxShadow: 'none',
                  opacity: authLoading ? 0.9 : 1,
                }}
              >
                {authLoading ? 'جارٍ المتابعة...' : authMode === 'register' ? 'إنشاء حساب' : 'تسجيل الدخول'}
              </button>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginTop: 6,
                }}
              >
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 750 }}>
                  {authMode === 'login'
                    ? 'سيتم حفظ السلة تلقائيًا بعد تسجيل الدخول'
                    : 'بإنشاء الحساب أنت توافق على حفظ السلة لهذا الكتالوج'}
                </div>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      <div
        className="catalog-studio-storefront-canvas"
        data-studio-preview-frame={canEdit ? studioPreviewFrame : undefined}
        data-studio-preview-zoom={canEdit && studioPreviewFrame === "desktop" ? String(studioPreviewZoom) : undefined}
      >
        <div
          className="catalog-studio-storefront-canvas__inner"
          style={
            canEdit && studioPreviewFrame === "desktop" && studioPreviewZoom !== 100
              ? {
                  transform: `scale(${studioPreviewZoom / 100})`,
                  transformOrigin: "top center",
                }
              : undefined
          }
        >
      {canEdit ? renderShowcaseCartTopbar({ studioCanvas: true }) : null}
      {showCatalogMainHeader ? (
      <>
      <header className={`main-header${catalogNavbarUi.show ? " main-header--with-nav" : ""}`} style={{ marginTop: 0, ...catalogHeroTopbarUi.headerStyle }}>
        <div className="container catalog-main-header__inner d-flex justify-content-between align-items-center">
<div className="catalog-main-header__brand d-flex align-items-center gap-2 gap-md-3 min-w-0">
  <div className="catalog-main-header__brand-unit d-flex align-items-center gap-2 min-w-0">
    {settings.logo && (
      <img 
        src={settings.logo} 
        alt="Logo" 
        className="object-fit-contain catalog-header-logo catalog-main-header__logo"
        width={48}
        height={48}
        decoding="async"
        style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.05))' }} 
      />
    )}
    
    {settings.logo && settings.storeName && (
      <div className="catalog-main-header__divider" aria-hidden />
    )}

    <h3
      className="fw-800 m-0 catalog-store-title catalog-main-header__title catalog-store-title-fluid"
      style={{
        letterSpacing: "-0.02em",
        ...catalogHeroTopbarUi.storeTitleStyle,
      }}
      title={settings.storeName}
    >
      {settings.storeName}
    </h3>
  </div>
</div>

          {renderCatalogDesktopNav()}

          <div className="catalog-main-header__actions d-flex align-items-center gap-2 gap-md-3 justify-content-end flex-shrink-0">
             {catalogNavbarUi.show ? (
               <button
                 type="button"
                 className="catalog-nav-hamburger"
                 aria-label={tStudio.navbarMobileMenu}
                 aria-expanded={navbarMobileOpen}
                 onClick={() => {
                   setNavbarMoreOpen(false);
                   setNavbarMobileOpen(true);
                 }}
               >
                 <Menu size={22} strokeWidth={2.25} />
               </button>
             ) : null}
             <div className={`catalog-main-header__auth-slot${catalogNavbarUi.show ? " catalog-main-header__auth-slot--nav" : ""}`}>
             {!shopper && !wholesaleDealer ? (
               renderCatalogGuestAuth()
             ) : !wholesaleDealer ? (
               <>
                 <div className="d-flex d-md-none align-items-center gap-2 catalog-main-header__auth-mobile-user">
                   <div className="catalog-restaurant-slim-bar__userwrap">
                     <button
                       type="button"
                       className="catalog-topbar-icon-btn"
                       onClick={() => setRestaurantUserMenuOpen((v) => !v)}
                       aria-label="حسابي"
                       style={catalogHeroTopbarUi.mobileUserChipStyle}
                     >
                       <User size={20} strokeWidth={2.25} />
                     </button>
                     {restaurantUserMenuOpen ? (
                       <>
                         <button
                           type="button"
                           className="catalog-restaurant-slim-bar__userbackdrop"
                           aria-label="إغلاق"
                           onClick={() => setRestaurantUserMenuOpen(false)}
                         />
                         <div className="catalog-restaurant-slim-bar__usermenu" role="menu" aria-label="قائمة الحساب">
                           <div className="catalog-restaurant-slim-bar__usermenu-title">
                             {String(shopper?.full_name || "").trim() || "حسابي"}
                           </div>
                           <button
                             type="button"
                             className="catalog-restaurant-slim-bar__usermenu-item"
                             onClick={() => {
                               setRestaurantUserMenuOpen(false);
                               logoutShopper();
                             }}
                           >
                             تسجيل الخروج
                           </button>
                         </div>
                       </>
                     ) : null}
                   </div>
                 </div>
                 <div className="d-none d-md-flex align-items-center gap-2">
                 {(() => {
                   const name = String(shopper?.full_name || '').trim() || 'حسابي';
                   const hour = new Date().getHours();
                   const greet = hour < 12 ? 'صباح الخير' : 'مساء الخير';
                   const textRaw = name === 'حسابي' ? greet : `${greet} ${name}`;
                   const text = String(textRaw).replace(/\s+/g, ' ').trim();
                   return (
                 <div
                   style={catalogHeroTopbarUi.greetingStyle}
                   title={shopper?.phone || ''}
                 >
                   {text}
                 </div>
                   );
                 })()}
                 <button
                   type="button"
                   className="btn"
                   onClick={logoutShopper}
                   style={catalogHeroTopbarUi.logoutBtnStyle}
                 >
                   خروج
                 </button>
               </div>
               </>
             ) : null}
             </div>

             {cartPlacement === "topbar" && showStorefrontCart ? (
             <button
               type="button"
               className="catalog-topbar-cart-icon"
               onClick={requestOpenCart}
               aria-label={t.cartAriaLabel(cart.length)}
             >
                <ShoppingCart size={20} strokeWidth={2.25} aria-hidden />
                <span className="catalog-floating-cart-corner-badge" aria-hidden>
                  {cart.length}
                </span>
             </button>
             ) : null}
          </div>
        </div>
        {renderCatalogMobileNavDrawer()}
      </header>
      </>
      ) : catalogVisualTheme === "restaurant" ? (
      <header
        className="catalog-restaurant-slim-bar"
        style={{ marginTop: 0, direction: settings.layoutDirection || "rtl", ...catalogHeroTopbarUi.barStyle }}
      >
        <div className="container d-flex align-items-center justify-content-between gap-3 py-2">
          <div className="d-flex align-items-center gap-2 min-w-0" style={{ flex: "1 1 auto", minWidth: 0 }}>
            {settings.logo ? (
              <img src={settings.logo} alt="" className="catalog-restaurant-slim-bar__logo" width={40} height={40} decoding="async" />
            ) : null}
            <span
              className="catalog-restaurant-slim-bar__name"
              style={catalogHeroTopbarUi.storeTitleStyle}
            >
              {settings.storeName}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap">
            {!shopper && !wholesaleDealer ? (
              renderCatalogGuestAuth("catalog-restaurant-slim-bar__icon")
            ) : null}
            {shopper && !wholesaleDealer ? (
              <div className="catalog-restaurant-slim-bar__userwrap">
                <button
                  type="button"
                  className="catalog-restaurant-slim-bar__icon"
                  onClick={() => setRestaurantUserMenuOpen((v) => !v)}
                  aria-label="حسابي"
                >
                  <User size={20} strokeWidth={2.25} />
                </button>
                {restaurantUserMenuOpen ? (
                  <>
                    <button
                      type="button"
                      className="catalog-restaurant-slim-bar__userbackdrop"
                      aria-label="إغلاق"
                      onClick={() => setRestaurantUserMenuOpen(false)}
                    />
                    <div className="catalog-restaurant-slim-bar__usermenu" role="menu" aria-label="قائمة الحساب">
                      <div className="catalog-restaurant-slim-bar__usermenu-title">
                        {String(shopper?.full_name || "").trim() || "حسابي"}
                      </div>
                      <button
                        type="button"
                        className="catalog-restaurant-slim-bar__usermenu-item"
                        onClick={() => {
                          setRestaurantUserMenuOpen(false);
                          logoutShopper();
                        }}
                      >
                        تسجيل الخروج
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
            {cartPlacement === "topbar" && showStorefrontCart ? (
            <button
              type="button"
              className="catalog-restaurant-slim-bar__cart"
              onClick={requestOpenCart}
              aria-label={t.cartAriaLabel(cart.length)}
            >
              <ShoppingCart size={20} strokeWidth={2.25} aria-hidden />
              <span className="catalog-floating-cart-corner-badge" aria-hidden>
                {cart.length}
              </span>
            </button>
            ) : null}
          </div>
        </div>
      </header>
      ) : cartPlacement === "topbar" ? null : (
      <div
        className={floatingCartWrapperClass("catalog-floating-actions catalog-floating-actions--showcase")}
        style={{
          direction: settings.layoutDirection || "rtl",
          ...floatingCartChromeStyle,
        }}
        aria-label="إجراءات سريعة"
      >
        {!shopper ? (
          <button
            type="button"
            className="catalog-floating-action-btn"
            onClick={() => openAuth("login")}
            aria-label="تسجيل الدخول"
          >
            <User size={20} strokeWidth={2.25} />
          </button>
        ) : null}
        {showStorefrontCart ? (
        <button
          type="button"
          className="catalog-floating-action-btn catalog-floating-action-btn--cart"
          onClick={requestOpenCart}
          aria-label={t.cartAriaLabel(cart.length)}
        >
          <ShoppingCart size={20} strokeWidth={2.25} aria-hidden />
          <span className="catalog-floating-cart-corner-badge" aria-hidden>
            {cart.length}
          </span>
        </button>
        ) : null}
      </div>
      )}

      {catalogVisualTheme === "minimal" && cartPlacement !== "topbar" && showStorefrontCart ? (
        <div
          className={floatingCartWrapperClass("catalog-floating-actions catalog-floating-actions--solo-cart")}
          style={{ direction: settings.layoutDirection || "rtl", ...floatingCartChromeStyle }}
          aria-label="السلة"
        >
          <button
            type="button"
            className="catalog-floating-action-btn catalog-floating-action-btn--cart"
            onClick={requestOpenCart}
            aria-label={t.cartAriaLabel(cart.length)}
          >
            <ShoppingCart size={20} strokeWidth={2.25} aria-hidden />
            <span className="catalog-floating-cart-corner-badge" aria-hidden>
              {cart.length}
            </span>
          </button>
        </div>
      ) : null}

      {catalogVisualTheme === "restaurant" && cartPlacement !== "topbar" && showStorefrontCart ? (
        <div
          className={floatingCartWrapperClass("catalog-floating-actions catalog-floating-actions--solo-cart")}
          style={{ direction: settings.layoutDirection || "rtl", ...floatingCartChromeStyle }}
          aria-label="السلة"
        >
          <button
            type="button"
            className="catalog-floating-action-btn catalog-floating-action-btn--cart"
            onClick={requestOpenCart}
            aria-label={t.cartAriaLabel(cart.length)}
          >
            <ShoppingCart size={20} strokeWidth={2.25} aria-hidden />
            <span className="catalog-floating-cart-corner-badge" aria-hidden>
              {cart.length}
            </span>
          </button>
        </div>
      ) : null}

      {/* Always bottom-left; raised above the cart when the cart also floats on the left */}
      {showFloatingCatalogWhatsapp ? (
        <div
          className="catalog-floating-actions catalog-floating-actions--phys-left catalog-floating-actions--whatsapp-fab"
          style={{
            top: "auto",
            left: 16,
            right: "auto",
            bottom: `calc(${
              (canEdit ? 96 : 20) +
              (cartPlacement === "left" && showStorefrontCart ? 62 : 0)
            }px + env(safe-area-inset-bottom, 0px))`,
            zIndex: 13001,
          }}
          aria-label={language === "en" ? "WhatsApp" : "واتساب"}
        >
          {floatingWhatsappBtn}
        </div>
      ) : null}

      {!canEdit && (wholesaleCatalogEnabled || seedWholesaleStrip) ? (
        <CatalogWholesaleStrip
          enabled={wholesaleCatalogEnabled || seedWholesaleStrip}
          dealer={wholesaleDealer}
          accentColor={wholesaleStripAccent}
          onLoginClick={() => setWholesaleAuthOpen(true)}
          onOpenOrders={openWholesaleOrders}
          onLogout={() => {
            wholesaleLogout();
            setCart([]);
            fetchCatalogPage(1, { append: false });
          }}
        />
      ) : null}

      {/* HERO SECTION - PRO CLEAN LOOK: image only if uploaded from store, else color only */}
      {(canEdit ? layoutsEdit.show_top_hero : layouts.show_top_hero) && (() => {
        // في الستوديو: المعاينة تتبع مسودة storeEdit فقط حتى يظهر حذف الصورة فوراً (بدون الرجوع لـ settings.heroUrl).
        const heroBgUrl = canEdit
          ? String(storeEdit.heroUrl ?? "").trim()
          : (settings.heroUrl || "");
        const heroMobileUrl = String(catalogLayoutEffective.hero_mobile_url || "").trim();
        /** Separate mobile asset only when both desktop + mobile URLs exist */
        const heroHasResponsiveSwap = Boolean(heroBgUrl && heroMobileUrl);
        const heroSingleUrl = heroBgUrl || heroMobileUrl;
        const heroMobileShowUrl = heroMobileUrl || heroBgUrl;
        const heroMobileFit =
          String(catalogLayoutEffective.hero_mobile_fit || "contain").toLowerCase() === "cover"
            ? "cover"
            : "contain";
        const isStudioTabletFrame = canEdit && studioPreviewFrame === "tablet";
        const isStudioMobileFrame = canEdit && studioPreviewFrame === "mobile";
        const heroMobileBgPosition = heroBackgroundPositionFromXY(
          catalogLayoutEffective.hero_mobile_bg_position_x,
          catalogLayoutEffective.hero_mobile_bg_position_y
        );
        const heroTitleColorRaw = String(catalogLayoutEffective.hero_title_color || "").trim();
        const heroTitleStyle = heroTitleColorRaw ? { color: heroTitleColorRaw } : undefined;
        const heroSubColorRaw = String(catalogLayoutEffective.hero_subtitle_color || "").trim();
        const heroSubPStyle = heroSubColorRaw ? { color: heroSubColorRaw } : undefined;
        const heroSubTextStyle = heroSubColorRaw ? { color: heroSubColorRaw } : {};
        const heroBgPosition = heroBackgroundPositionFromXY(
          catalogLayoutEffective.hero_bg_position_x,
          catalogLayoutEffective.hero_bg_position_y
        );
        const heroContentAlign = String(catalogLayoutEffective.hero_content_align || "").toLowerCase();
        const heroLayout = String(catalogLayoutEffective.hero_layout || "").trim().toLowerCase();
        const heroSplitCircle = heroLayout === "split_circle";
        /** آيباد: دائماً إظهار الصورة كاملة؛ الجوال يحترم إعداد contain/cover */
        const heroUseContainLayout =
          !heroSplitCircle &&
          Boolean(heroSingleUrl) &&
          (heroMobileFit === "contain" || isStudioTabletFrame || (storefrontIsTablet && !isStudioMobileFrame));
        const heroEffectiveFit = heroUseContainLayout ? "contain" : heroMobileFit;
        const heroHeightModeRaw = String(catalogLayoutEffective.hero_height_mode || "").trim().toLowerCase();
        const heroHeightMode =
          heroHeightModeRaw === "full" ||
          heroHeightModeRaw === "compact" ||
          heroHeightModeRaw === "tall" ||
          heroHeightModeRaw === "custom" ||
          heroHeightModeRaw === "default"
            ? heroHeightModeRaw === "default"
              ? "compact"
              : heroHeightModeRaw
            : "standard";
        const heroHeightPxRaw = Number(catalogLayoutEffective.hero_height_px);
        const heroHeightPx =
          heroHeightMode === "custom" && Number.isFinite(heroHeightPxRaw)
            ? Math.max(280, Math.min(1200, heroHeightPxRaw))
            : null;
        const heroHeightAttr =
          heroHeightMode === "standard"
            ? undefined
            : heroHeightMode === "custom" && heroHeightPx
              ? "custom"
              : heroHeightMode;
        const heroHeightInlineStyle =
          heroHeightMode === "custom" && heroHeightPx
            ? heroSplitCircle
              ? { minHeight: heroHeightPx }
              : { height: heroHeightPx, minHeight: heroHeightPx }
            : {};
        const heroCircleUrl = String(catalogLayoutEffective.hero_circle_image_url || "").trim() || heroBgUrl;
        const heroCtaLabel =
          String(catalogLayoutEffective.hero_cta_label || "").trim() ||
          (catalogLayoutEffective.fashion_store ? "تسوّق الآن" : catalogVisualTheme === "restaurant" ? "اطلب الآن" : "استكشف المجموعة");
        const heroPageBg =
          catalogVisualTheme === "restaurant"
            ? catalogLayoutEffective.restaurant_bg || catalogLayoutEffective.page_bg || "#f2f2f2"
            : catalogLayoutEffective.page_bg || "#ffffff";
        // عنوان/وصف الهيرو من الحقول فقط — بدون الرجوع لاسم المتجر عند الفراغ
        const heroTitleValue = canEdit
          ? String(storeEdit.headerTitle ?? "")
          : String(settings.headerTitle || "");
        const heroDescValue = canEdit
          ? String(storeEdit.subheaderText ?? "")
          : String(settings.subheaderText || "");
        const hasHeroTitle = Boolean(heroTitleValue.trim());
        const hasHeroDesc = Boolean(heroDescValue.trim());
        const hasHeroCopy = hasHeroTitle || hasHeroDesc;
        const heroCtaVisibility = String(catalogLayoutEffective.hero_cta_visibility || "").trim().toLowerCase();
        const showHeroCta =
          heroCtaVisibility === "always" ? true : heroCtaVisibility === "hidden" ? false : hasHeroCopy;
        const heroCtaFloating =
          showHeroCta &&
          !heroSplitCircle &&
          (String(catalogLayoutEffective.hero_cta_position || "").trim().toLowerCase() === "custom" || !hasHeroCopy);
        const heroCtaClamp = (v, d) => {
          if (v === null || v === undefined || v === "") return d;
          const n = Number(v);
          return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : d;
        };
        const heroCtaPosX = heroCtaClamp(catalogLayoutEffective.hero_cta_pos_x, 50);
        const heroCtaPosY = heroCtaClamp(catalogLayoutEffective.hero_cta_pos_y, 75);
        const heroCtaMobilePosX = heroCtaClamp(catalogLayoutEffective.hero_cta_mobile_pos_x ?? "", heroCtaPosX);
        const heroCtaMobilePosY = heroCtaClamp(catalogLayoutEffective.hero_cta_mobile_pos_y ?? "", heroCtaPosY);
        const heroCtaMobileScaleRaw = Number(catalogLayoutEffective.hero_cta_mobile_scale);
        const heroCtaMobileScale = Number.isFinite(heroCtaMobileScaleRaw) && heroCtaMobileScaleRaw > 0
          ? Math.max(0.4, Math.min(1.2, heroCtaMobileScaleRaw))
          : 0.72;
        const heroCtaAlign = String(catalogLayoutEffective.hero_cta_align || "").trim().toLowerCase();
        const heroCtaBaseStyle = heroSplitCircle
          ? { backgroundColor: catalogLayoutEffective.hero_primary_color || catalogHeroPrimary || "#1a1a1a", color: "#fff" }
          : catalogLayoutEffective.fashion_store
            ? { backgroundColor: "#111827", color: "#fff" }
            : { backgroundColor: "#fff", color: catalogHeroPrimary };
        const heroCtaSizeStyle =
          {
            sm: { padding: "10px 26px", fontSize: "0.875rem" },
            lg: { padding: "16px 54px", fontSize: "1.15rem" },
            xl: { padding: "20px 68px", fontSize: "1.35rem" },
          }[String(catalogLayoutEffective.hero_cta_size || "").trim().toLowerCase()] || {};
        const heroCtaBg = String(catalogLayoutEffective.hero_cta_bg_color || "").trim();
        const heroCtaText = String(catalogLayoutEffective.hero_cta_text_color || "").trim();
        const heroCtaBorder = String(catalogLayoutEffective.hero_cta_border_color || "").trim();
        const heroCtaRadiusRaw = catalogLayoutEffective.hero_cta_radius;
        const heroCtaRadius =
          heroCtaRadiusRaw === null || heroCtaRadiusRaw === undefined || heroCtaRadiusRaw === ""
            ? null
            : Math.max(0, Math.min(999, Number(heroCtaRadiusRaw) || 0));
        const heroCtaStyle = {
          ...heroCtaBaseStyle,
          ...heroCtaSizeStyle,
          ...(heroCtaBg ? { backgroundColor: heroCtaBg, "--hero-cta-bg": heroCtaBg } : {}),
          ...(heroCtaText ? { color: heroCtaText, "--hero-cta-color": heroCtaText } : {}),
          ...(heroCtaBorder ? { border: `2px solid ${heroCtaBorder}` } : {}),
          ...(heroCtaRadius !== null ? { borderRadius: heroCtaRadius, "--hero-cta-radius": `${heroCtaRadius}px` } : {}),
          ...(catalogLayoutEffective.hero_cta_shadow ? { boxShadow: "0 8px 22px rgba(15, 23, 42, 0.28)" } : {}),
          ...(heroCtaFloating
            ? {
                "--hero-cta-x": `${heroCtaPosX}%`,
                "--hero-cta-y": `${heroCtaPosY}%`,
                "--hero-cta-mx": `${heroCtaMobilePosX}%`,
                "--hero-cta-my": `${heroCtaMobilePosY}%`,
                "--hero-cta-mscale": heroCtaMobileScale,
              }
            : {}),
        };
        const heroCtaButton = showHeroCta ? (
          <button
            type="button"
            className={`hero-action-btn${heroCtaFloating ? " hero-action-btn--floating" : ""}${
              catalogLayoutEffective.hero_cta_hide_mobile === true || String(catalogLayoutEffective.hero_cta_hide_mobile) === "true"
                ? " hero-action-btn--hide-mobile"
                : ""
            }${heroCtaBg ? " hero-action-btn--custom-bg" : ""}${heroCtaText ? " hero-action-btn--custom-color" : ""}${heroCtaRadius !== null ? " hero-action-btn--custom-radius" : ""}`}
            onClick={scrollToProducts}
            style={heroCtaStyle}
          >
            {heroCtaLabel}
          </button>
        ) : null;
        return (
<div className="hero-clean-wrap" style={{ position: "relative" }}>
  {canEdit ? (
    <button
      type="button"
      className="catalog-studio-edit-chip"
      aria-label="تعديل سكشن الهيرو"
      onClick={(e) => {
        e.stopPropagation();
        openStudioCoreForm("hero");
      }}
    >
      <Pencil size={15} strokeWidth={2.25} />
    </button>
  ) : null}
<section
  id="catalog-hero"
  className={`hero-clean-section catalog-skip-global-palette${heroSplitCircle ? " hero-clean-section--split-circle" : ""}${!hasHeroCopy ? " hero-clean-section--image-only" : ""}${heroUseContainLayout ? " hero-clean-section--mobile-contain" : ""}`}
  style={{ direction: "rtl", ...(heroSplitCircle ? { background: heroPageBg } : {}), ...heroHeightInlineStyle }}
  data-hero-height={heroHeightAttr}
  data-hero-align={heroContentAlign === "start" || heroContentAlign === "center" ? heroContentAlign : undefined}
  data-hero-layout={heroSplitCircle ? "split_circle" : undefined}
  data-hero-mobile-fit={!heroSplitCircle && heroSingleUrl ? heroEffectiveFit : undefined}
>
  {heroSplitCircle ? (
    <div className="hero-split-circle-decor" aria-hidden />
  ) : heroSingleUrl ? (
    <>
      {heroHasResponsiveSwap ? (
        <>
          <div
            key={`desk-${heroBgUrl}`}
            className="hero-bg-layer hero-bg-layer--desktop"
          >
            <HeroCoverImage src={heroBgUrl} position={heroBgPosition} fit="cover" viewport="desktop" />
          </div>
          <div
            key={`mob-${heroMobileShowUrl}-${heroEffectiveFit}`}
            className={`hero-bg-layer hero-bg-layer--mobile hero-bg-layer--mobile-${heroEffectiveFit}`}
          >
            {heroEffectiveFit === "contain" ? (
              <HeroContainImage src={heroMobileShowUrl} viewport="mobile" />
            ) : (
              <HeroCoverImage
                src={heroMobileShowUrl}
                position={heroMobileBgPosition}
                fit="cover"
                viewport="mobile"
              />
            )}
          </div>
        </>
      ) : heroEffectiveFit === "contain" ? (
        <>
          <div
            key={`desk-all-${heroSingleUrl}`}
            className="hero-bg-layer hero-bg-layer--desktop hero-bg-layer--all-desktop"
          >
            <HeroCoverImage
              src={heroSingleUrl}
              position={heroBgUrl ? heroBgPosition : "center center"}
              fit="cover"
              viewport="desktop"
            />
          </div>
          <div
            key={`mob-all-${heroSingleUrl}-contain`}
            className="hero-bg-layer hero-bg-layer--mobile hero-bg-layer--mobile-contain"
          >
            <HeroContainImage src={heroSingleUrl} viewport="mobile" />
          </div>
        </>
      ) : (
        <div
          key={`all-${heroSingleUrl}`}
          className="hero-bg-layer hero-bg-layer--all"
        >
          <HeroCoverImage
            src={heroSingleUrl}
            position={heroBgUrl ? heroBgPosition : heroMobileBgPosition}
            fit="cover"
          />
        </div>
      )}
      {hasHeroCopy ? (
        <div className="hero-overlay-layer" style={{ backgroundColor: catalogHeroPrimary }} />
      ) : null}
    </>
  ) : (
    <div className="hero-bg-layer" style={{ backgroundColor: catalogHeroPrimary, backgroundImage: "none" }} />
  )}

  {hasHeroCopy || heroSplitCircle ? (
  <div className="container hero-container-inner">
    <div className={`hero-content-reveal${heroSplitCircle ? " hero-content-reveal--split" : ""}`}>
      {hasHeroCopy ? (
      <div className="hero-copy-col">
        {hasHeroTitle || canEdit ? (
        <h1 className="hero-main-h1" style={heroTitleStyle} onClick={() => { if (!canEdit) return; openStudioCoreForm("hero"); }}>
          <EditableInline
            value={heroTitleValue}
            placeholder="عنوان الهيرو"
            onChange={(v) => setStoreEdit((p) => ({ ...p, headerTitle: v }))}
            textStyle={heroTitleStyle || {}}
          />
        </h1>
        ) : null}

        {hasHeroDesc || canEdit ? (
        <p className="hero-sub-p" style={heroSubPStyle} onClick={() => { if (!canEdit) return; openStudioCoreForm("hero"); }}>
          <EditableInline
            value={heroDescValue}
            placeholder="وصف الهيرو"
            onChange={(v) => setStoreEdit((p) => ({ ...p, subheaderText: v }))}
            multiline
            textStyle={heroSubTextStyle}
          />
        </p>
        ) : null}

        {heroCtaButton && !heroCtaFloating ? (
          heroCtaAlign === "right" || heroCtaAlign === "center" || heroCtaAlign === "left" ? (
            <div className="hero-action-btn-row" style={{ width: "100%", textAlign: heroCtaAlign }}>
              {heroCtaButton}
            </div>
          ) : (
            heroCtaButton
          )
        ) : null}
      </div>
      ) : null}
      {heroSplitCircle ? (
        <div className="hero-circle-col">
          {heroCircleUrl ? (
            <div className="hero-circle-frame">
              <img
                src={heroCircleUrl}
                alt={String(heroTitleValue || "").trim()}
                className="hero-circle-img"
                width={800}
                height={800}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          ) : (
            <div className="hero-circle-frame hero-circle-frame--empty" />
          )}
        </div>
      ) : null}
    </div>
  </div>
  ) : null}
  {heroCtaFloating ? heroCtaButton : null}
</section>
</div>
        );
      })()}

{/* FEATURES BAR - values from backend (store settings) */}
{showCatalogFeaturesBar && (
<section className="features-flat-bar" style={{ background: featureBarBg }}>
  <div className="container">
    <div className="features-flat-grid">
      <div className="feature-flat-item">
        <h4 style={{ color: featureBarAccent }}>{featureBarTitles[0]}</h4>
        <p style={{ color: featureBarText }}>{featureBarDescs[0] || ''}</p>
      </div>

      <div className="feature-flat-border" style={{ backgroundColor: `${featureBarAccent}20` }} />

      <div className="feature-flat-item">
        <h4 style={{ color: featureBarAccent }}>{featureBarTitles[1]}</h4>
        <p style={{ color: featureBarText }}>{featureBarDescs[1] || ''}</p>
      </div>

      <div className="feature-flat-border" style={{ backgroundColor: `${featureBarAccent}20` }} />

      <div className="feature-flat-item">
        <h4 style={{ color: featureBarAccent }}>{featureBarTitles[2]}</h4>
        <p style={{ color: featureBarText }}>{featureBarDescs[2] || ''}</p>
      </div>
    </div>
  </div>
</section>
)}

{/* BUILDER SECTIONS — قبل المنتجات الأساسية */}
{builderLoading || seedHold ? null : (() => {
  const shown = displayBuilderSections;
  const { before: beforeRaw } = partitionBuilderSections(shown);
  const before = catalogLayoutEffective.fashion_store
    ? orderFashionBuilderBeforeSections(beforeRaw)
    : beforeRaw;
  if (catalogVisualTheme === "restaurant") return null;
  if (catalogVisualTheme === "showcase") {
    // Showcase sections are rendered inside products section to preserve exact ordering.
    return null;
  }
  if (!before.length && !canEdit) return null;
  return (
    <div style={{ width: "100%", maxWidth: "100%", margin: 0, padding: 0 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          marginTop: catalogVisualTheme === "restaurant" ? 0 : 14,
          marginBottom: catalogVisualTheme === "restaurant" ? 0 : (before.length ? 14 : 0),
        }}
      >
        {canEdit ? <AddBetweenBar zone="before" index={0} /> : null}
        {before.map((sec, idx) => (
          <React.Fragment key={sec.id}>
            {renderBuilderSection(sec)}
            {canEdit ? <AddBetweenBar zone="before" index={idx + 1} /> : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
})()}

{/* PRODUCT SECTION - MINIMALIST GRID */}
<div
  id="products"
  className="products-section-flat"
  dir={settings.layoutDirection || "rtl"}
  data-product-cards={productsSectionDesign.cardStyle !== "default" ? productsSectionDesign.cardStyle : undefined}
  data-product-image-shape={productsSectionDesign.imageShape}
  data-products-section-width={productsSectionDesign.widthKey}
  style={{
    color: "var(--catalog-text, #1a1a1a)",
    ...productsSectionDesign.shellWrapStyle,
    "--products-section-max-width": productsSectionDesign.productsSectionMaxWidthCss,
    ...(productsSectionDesign.titleColor
      ? { "--products-title-color": productsSectionDesign.titleColor }
      : {}),
    ...(productsSectionDesign.subtitleColor
      ? { "--products-subtitle-color": productsSectionDesign.subtitleColor }
      : {}),
    ...(productsSectionDesign.cardNameColor
      ? { "--products-card-name-color": productsSectionDesign.cardNameColor }
      : {}),
    ...(productsSectionDesign.cardDescColor
      ? { "--products-card-desc-color": productsSectionDesign.cardDescColor }
      : {}),
    ...(productsSectionDesign.cardBorderRadiusPx != null
      ? { "--products-card-radius": `${productsSectionDesign.cardBorderRadiusPx}px` }
      : {}),
    ...(productsSectionDesign.cardBgColor
      ? { "--products-card-bg": productsSectionDesign.cardBgColor }
      : {}),
    "--product-cart-icon-border-width": `${productsSectionDesign.cartIconBorderWidthPx || 0}px`,
    ...(productsSectionDesign.cartIconBorderColor
      ? { "--product-cart-icon-border-color": productsSectionDesign.cartIconBorderColor }
      : {}),
  }}
>
  <div
    className={`catalog-products-shell catalog-products-shell--width catalog-products-shell--mobile-edge${productsChrome.aside ? " catalog-products-shell--aside" : ""}`}
    style={{
      paddingTop: `${productsSectionDesign.sectionPaddingY}px`,
      paddingBottom: `${productsSectionDesign.sectionPaddingY}px`,
      maxWidth: productsSectionDesign.productsSectionMaxWidthCss,
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      boxSizing: "border-box",
    }}
  >
    {productsChrome.aside ? (
      <>
        <aside className="catalog-products-aside catalog-products-aside--desktop">
          {renderSidebarFilters()}
          {productsChrome.categoryNav === "sidebar"
            ? renderCategoryStrip("categories-flat-container--vertical", "categories-section-sidebar")
            : null}
        </aside>

        <div className="catalog-products-filters-mobile" dir="ltr">
          <button
            type="button"
            className="catalog-products-filters-mobile__open"
            onClick={() => setProductFiltersDrawerOpen(true)}
            aria-label="فتح التصنيفات والفلاتر"
          >
            <span className="catalog-products-filters-mobile__icons" aria-hidden>
              <Menu size={20} strokeWidth={2.25} />
              <Filter size={16} strokeWidth={2.25} />
            </span>
            <span className="catalog-products-filters-mobile__text">
              <span className="catalog-products-filters-mobile__title">التصنيفات</span>
              <span className="catalog-products-filters-mobile__sub">اضغط هنا لاختيار الأقسام والفلاتر</span>
            </span>
          </button>
        </div>

        {productFiltersDrawerOpen ? (
          <div className="catalog-products-filters-drawer" role="dialog" aria-modal="true" aria-label="تصنيف وفلاتر المنتجات">
            <button
              type="button"
              className="catalog-products-filters-drawer__backdrop"
              aria-label="إغلاق"
              onClick={() => setProductFiltersDrawerOpen(false)}
            />
            <div className="catalog-products-filters-drawer__panel">
              <div className="catalog-products-filters-drawer__head">
                <span>تصنيف وفلاتر</span>
                <button
                  type="button"
                  className="catalog-products-filters-drawer__close"
                  onClick={() => setProductFiltersDrawerOpen(false)}
                  aria-label="إغلاق"
                >
                  <X size={22} />
                </button>
              </div>
              <div className="catalog-products-filters-drawer__body">
                {renderSidebarFilters()}
                {productsChrome.categoryNav === "sidebar"
                  ? renderCategoryStrip("categories-flat-container--vertical", "categories-section-sidebar-m", {
                      onPick: () => setProductFiltersDrawerOpen(false),
                    })
                  : null}
              </div>
            </div>
          </div>
        ) : null}
      </>
    ) : null}

    <div className="catalog-products-main">
    {/* عروض مخصصة — تحت الهيرو وفوق المنتجات (مطاعم) */}
    {catalogVisualTheme === "restaurant" ? (() => {
      const shown = displayBuilderSections;
      const list = Array.isArray(shown) ? shown : [];
      const offerSecs = list.filter((s) => s.type === "bundle_offers");
      if (!offerSecs.length) return null;
      return (
        <div className="catalog-bundle-offers-in-products">
          {offerSecs.map((sec) => (
            <React.Fragment key={sec.id}>{renderBuilderSection(sec)}</React.Fragment>
          ))}
        </div>
      );
    })() : null}
    {/* Showcase order (exact):
        1) featured_products
        2) carousel (images)
        3) main products grid (below)
        4) text_image_split (after main)
        5) products_carousel (after main)
    */}
    {catalogVisualTheme === "showcase" ? (() => {
      const shown = displayBuilderSections;
      const list = Array.isArray(shown) ? shown : [];
      // In showcase: allow ANY section type; keep user's order.
      const preSorted = list.filter((s) => !Boolean(s?.settings?.after_products));
      if (!preSorted.length && !canEdit) return null;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 14 }}>
          {canEdit ? <AddBetweenBar zone="before" index={0} /> : null}
          {preSorted.map((sec, idx) => (
            <React.Fragment key={sec.id}>
              {renderBuilderSection(sec)}
              {canEdit ? <AddBetweenBar zone="before" index={idx + 1} /> : null}
            </React.Fragment>
          ))}
        </div>
      );
    })() : null}

    <div
      id="catalog-products-search"
      className="catalog-products-search-row"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="catalog-products-search-inner d-flex align-items-center search-bar-nike">
        <Search size={18} className="search-icon-nike" aria-hidden />
        <input
          id="catalog-products-search-input"
          type="text"
          className="search-input-nike"
          placeholder={t.searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ textAlign: "right" }}
          autoComplete="off"
        />
      </div>
    </div>
    <div
      className="products-main-header"
      data-heading-align={(canEdit ? storeEdit.productsHeadingAlign : settings.products_heading_align) || "start"}
      style={{
        position: "relative",
        textAlign: (canEdit ? storeEdit.productsHeadingAlign : settings.products_heading_align) || "start",
      }}
    >
      {canEdit ? (
        <button
          type="button"
          className="catalog-studio-edit-chip"
          aria-label="تعديل قسم المنتجات"
          onClick={(e) => {
            e.stopPropagation();
            openStudioCoreForm("products");
          }}
        >
          <Pencil size={15} strokeWidth={2.25} />
        </button>
      ) : null}
      <h2 className="products-title-text">
        <EditableInline
          value={canEdit ? (storeEdit.productsSectionTitle ?? settings.productsSectionTitle) : settings.productsSectionTitle}
          placeholder="عنوان المنتجات"
          onChange={(v) => setStoreEdit((p) => ({ ...p, productsSectionTitle: v }))}
          textStyle={{}}
        />
      </h2>
      <div
        className="title-underline"
        style={{
          backgroundColor:
            productsSectionDesign.underlineColor ||
            catalogThemePrimary ||
            "#ffc200",
        }}
      />
      <p className="products-subtitle-text">
        <EditableInline
          value={canEdit ? (storeEdit.productsSectionSubtitle ?? settings.productsSectionSubtitle) : settings.productsSectionSubtitle}
          placeholder="وصف المنتجات"
          onChange={(v) => setStoreEdit((p) => ({ ...p, productsSectionSubtitle: v }))}
          multiline
          textStyle={{}}
        />
      </p>
    </div>

    {/* فئات أفقية: تحت العنوان الفرعي — مطاعم = دوائر ثابتة بدون شريط لاصق */}
    {productsChrome.categoryNav === "top"
      ? renderCategoryStrip(
          catalogVisualTheme === "restaurant" ? "categories-flat-container--circles" : "",
          "categories-section",
          catalogVisualTheme === "restaurant" ? { circles: true } : undefined
        )
      : null}

    {/* Showcase: فلاتر أفقية فقط عندما لا يُعرض عمود جانبي (وإلا تظهر في الـ aside) */}
    {catalogVisualTheme === "showcase" && !productsChrome.aside ? (
      <div className="showcase-inline-filters" onClick={(e) => e.stopPropagation()}>
        {renderSidebarFilters()}
      </div>
    ) : null}

    <div
      className={`catalog-products-dynamic${productsLoading || searchPending ? ' catalog-products-dynamic--loading' : ''}`}
    >
      {(productsLoading || searchPending) && (
        <div
          className="catalog-products-loading-overlay"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader2
            className="catalog-products-loading-spinner"
            size={40}
            style={{ color: catalogThemePrimary }}
            aria-hidden
          />
          <span className="visually-hidden">جاري تحميل المنتجات...</span>
        </div>
      )}

      {/* حاوية المنتجات */}
      <div
        id="catalog-products-anchor"
        className={`products-grid-wrapper${productsLoading || searchPending ? ' catalog-products-grid--muted' : ''}`}
        style={{ scrollMarginTop: catalogVisualTheme === "restaurant" ? 24 : 96 }}
      >
        {catalogError && !catalogNotFound && !catalogUnavailable && (
          <div
            style={{
              margin: '0 auto 16px auto',
              maxWidth: '900px',
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              color: '#9a3412',
              padding: '12px 14px',
              borderRadius: '10px',
              fontWeight: 600,
              lineHeight: 1.6,
            }}
          >
            {catalogError}
          </div>
        )}
        {filteredProducts.length === 0 && !productsLoading && !searchPending ? (
          <div className="empty-state-container">
            <ShoppingCart size={48} style={{ opacity: 0.2, marginBottom: '15px', color: catalogThemePrimary }} />
            <h3 style={{ color: catalogThemePrimary }}>
              {searchDebounced ? 'لا توجد نتائج' : 'لا توجد منتجات'}
            </h3>
            <p>
              {searchDebounced
                ? `لم يتم العثور على منتجات تطابق «${searchDebounced}»`
                : 'لم يتم العثور على منتجات تطابق هذا التصنيف حالياً'}
            </p>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div
            className={`products-flat-grid${productsSectionDesign.mainLayout === "list" ? " products-flat-grid--list" : ""}${productsSectionDesign.mainLayout === "gallery" ? " products-flat-grid--gallery" : ""}${productsSectionDesign.mainLayout === "list" && catalogVisualTheme === "restaurant" ? " catalog-theme-restaurant-menu" : ""}`}
            data-products-menu-layout={productsSectionDesign.mainLayout === "list" ? "restaurant" : undefined}
            style={{ gap: productsSectionDesign.gridGap }}
          >
            {filteredProducts.map((product, idx) => (
              <ProductCard
                key={product.id}
                product={product}
                primaryColor={catalogThemePrimary}
                secondaryColor={catalogThemeSecondary}
                onSelect={onSelectProduct}
                index={idx}
                addMode={catalogVisualTheme === "restaurant" ? "text" : "icon"}
              />
            ))}
          </div>
        ) : null}
      </div>

      {pagination.total_products > 0 && products.length > 0 && !catalogError && (
        <p
          className="catalog-load-more-count"
          style={{
            textAlign: 'center',
            margin: '16px 0 0',
            fontSize: '14px',
            color: '#666',
            fontWeight: 600,
          }}
        >
          عرض {products.length} من {pagination.total_products} منتجاً
        </p>
      )}

      {pagination.has_next && !catalogError && (
        <div className="catalog-load-more-wrap">
          <div className="catalog-pagination catalog-pagination--load-more">
            <button
              type="button"
              className="catalog-btn catalog-load-more-btn"
              onClick={handleLoadMore}
              disabled={productsLoading}
              style={{
                padding: '14px 40px',
                fontWeight: 700,
                fontSize: '15px',
                border: `2px solid ${catalogThemePrimary}`,
                background: productsLoading ? '#e8e8e8' : catalogThemePrimary,
                color: productsLoading ? '#888' : catalogThemeSecondary,
                cursor: productsLoading ? 'not-allowed' : 'pointer',
                borderRadius: '10px',
                minWidth: '220px',
              }}
            >
              {productsLoading ? 'جاري التحميل...' : 'تحميل المزيد'}
            </button>
          </div>
        </div>
      )}

      {/* Showcase: after-main sections (keep user's order) */}
      {catalogVisualTheme === "showcase" ? (() => {
        const shown = displayBuilderSections;
        const list = Array.isArray(shown) ? shown : [];
        const postSorted = list.filter((s) => Boolean(s?.settings?.after_products));
        if (!postSorted.length && !canEdit) return null;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
            {canEdit ? <AddBetweenBar zone="after" index={0} /> : null}
            {postSorted.map((sec, idx) => (
              <React.Fragment key={sec.id}>
                {renderBuilderSection(sec)}
                {canEdit ? <AddBetweenBar zone="after" index={idx + 1} /> : null}
              </React.Fragment>
            ))}
          </div>
        );
      })() : null}
    </div>
    </div>
  </div>
</div>

{/* أقسام البناء بعد المنتجات الأساسية */}
{builderLoading || seedHold ? null : (() => {
  const shown = displayBuilderSections;
  const { after, before } = partitionBuilderSections(shown);
  if (catalogVisualTheme === "showcase") {
    // Showcase sections are rendered inside products section to preserve exact ordering.
    return null;
  }
  const allForRestaurant =
    catalogVisualTheme === "restaurant"
      ? (Array.isArray(shown) ? shown.filter((s) => s.type !== "bundle_offers") : [])
      : after;
  if (!allForRestaurant.length && !canEdit) return null;
  return (
    <div style={{ width: "100%", maxWidth: "100%", margin: 0, padding: 0 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          marginTop: catalogVisualTheme === "restaurant" ? 0 : 14,
          marginBottom: catalogVisualTheme === "restaurant" ? 0 : 14,
        }}
      >
        {canEdit ? <AddBetweenBar zone="after" index={0} /> : null}
        {allForRestaurant.map((sec, idx) => (
          <React.Fragment key={sec.id}>
            {renderBuilderSection(sec)}
            {canEdit ? <AddBetweenBar zone="after" index={idx + 1} /> : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
})()}

{/* Legacy "hero-flat-premium" fallback was removed —
    only the modern hero (Hero/Products/Footer) is rendered now. */}

{/* Product Detail Modal - Ultra Modern Design */}
{selectedProduct && (
  <div 
    className="product-modal-overlay"
    onClick={(e) => e.target === e.currentTarget && closeProductModal()}
    style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(15px)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      direction: 'rtl'
    }}
  >
    <div className="product-modal-container">
      {/* Close Button */}
      <button
        type="button"
        className="product-modal-close-btn"
        onClick={() => closeProductModal()}
        aria-label="إغلاق"
      >
        <X size={22} color="#0f172a" strokeWidth={2.25} />
      </button>

      {/* 1. Image Gallery Section */}
      <div className="product-modal-gallery">
        {/* Main Image Display */}
        <div className="product-modal-main-img" style={{ position: 'relative' }}>
          {selectedProduct.on_sale && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '120px',
                  height: '120px',
                  overflow: 'hidden',
                  zIndex: 4,
                  pointerEvents: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    bottom: '24px',
                    right: '-50px',
                    width: '180px',
                    padding: '8px 0',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    color: '#fff',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '900',
                    letterSpacing: '0.04em',
                    transform: 'rotate(-45deg)',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                  }}
                >
                  عرض خاص
                </span>
              </div>
          )}
          <img 
            src={selectedProduct.images?.[currentImageIndex] || selectedProduct.image} 
            alt={selectedProduct.name}
            width={800}
            height={800}
            decoding="async"
            style={{
              width: "100%",
              height: "100%",
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              objectPosition: "center center",
              transition: "opacity 0.3s ease",
            }} 
          />
        </div>

        {/* Thumbnails Gallery */}
        {selectedProduct.images?.length > 1 && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {selectedProduct.images.map((img, idx) => (
              <div 
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                style={{
                  width: '60px', height: '60px', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                  border: currentImageIndex === idx ? `2px solid ${readableAccentOnWhite(catalogThemePrimary)}` : '2px solid transparent',
                  opacity: currentImageIndex === idx ? 1 : 0.6, transition: '0.2s'
                }}
              >
                <img src={img} alt={`${String(selectedProduct?.name || "").trim()} ${idx + 1}`.trim()} width={60} height={60} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2. Product Info Section */}
      <div className="product-modal-info">
        {selectedProduct.category && (
          <span style={{ 
            fontSize: '13px', fontWeight: '800', color: readableAccentOnWhite(catalogThemePrimary), 
            background: applyOpacityToColor(readableAccentOnWhite(catalogThemePrimary), 0.08), padding: '6px 14px', 
            borderRadius: '50px', alignSelf: 'flex-start', marginBottom: '15px' 
          }}>
            {selectedProduct.category}
          </span>
        )}
        
        <h1 className="product-modal-title">
          {selectedProduct.name_ar || selectedProduct.name}
        </h1>

        {isSelectedOutOfStock && (
          <div
            style={{
              marginTop: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              background: '#111',
              color: '#fff',
              padding: '8px 12px',
              fontSize: '14px',
              fontWeight: '800',
              borderRadius: 0,
            }}
          >
            نفذ من المخزن
          </div>
        )}
        
        {/* Current Price Selection Display */}
        {selectedProduct?.wholesale_price_missing ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>
              سعر الجملة لهذا المنتج غير محدد — تواصل معنا لمعرفته.
            </div>
            <WholesalePriceInquiryButton
              product={selectedProduct}
              whatsappNumber={settings.whatsappNumber}
              primaryColor={readableAccentOnWhite(catalogThemePrimary)}
              secondaryColor={pickReadableIconColor(readableAccentOnWhite(catalogThemePrimary), "#ffffff", "#0f172a")}
              block
            />
          </div>
        ) : (
        <div style={{ marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {modalBaseUnit > 0 &&
            modalAddonsExtra === 0 &&
            selectedPriceObj?.compareAt != null &&
            Number(selectedPriceObj.compareAt) !== modalBaseUnit && (
            <span style={{ fontSize: '1.35rem', fontWeight: '800', color: '#9ca3af', textDecoration: 'line-through' }}>
              ₪{selectedPriceObj.compareAt}
            </span>
          )}
          {modalLineUnit > 0 && (
            <span style={{ fontSize: '2rem', fontWeight: '800', color: '#000' }}>
              ₪{modalLineUnit}
            </span>
          )}
          {modalAddonsExtra > 0 ? (
            <span style={{ fontSize: '0.88rem', color: '#64748b', fontWeight: 700 }}>
              السعر الأساسي ₪{modalBaseUnit} + إضافات ₪{modalAddonsExtra}
            </span>
          ) : null}
        </div>
        )}

        {(() => {
          const sizesList = productModalSizesList;
          const colorsList = normalizeOptionList(selectedProduct?.colors);
          const variantPickHint = productVariantSelectionError(
            selectedProduct,
            selectedSize,
            selectedColor,
            language
          );
          if (sizesList.length === 0 && colorsList.length === 0) return null;
          return (
            <div className="product-modal-variants" style={{ marginBottom: '22px', display: 'grid', gap: '12px' }}>
              {sizesList.length > 0 && (
                <div id="product-modal-sizes">
                  <div style={{ fontWeight: '800', fontSize: '14px', marginBottom: '8px', color: '#111' }}>الأحجام</div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {sizesList.map((s) => {
                      const active = selectedSize === s;
                      const depleted = optionDepleted(selectedProduct, "size", s, selectedColor);
                      return (
                        <button
                          key={`size-opt-${s}`}
                          type="button"
                          onClick={() => !depleted && setSelectedSize(s)}
                          disabled={isSelectedOutOfStock || depleted}
                          style={{
                            minWidth: '44px',
                            height: '44px',
                            padding: '0 12px',
                            borderRadius: '999px',
                            fontWeight: 800,
                            color: '#111',
                            background: active ? applyOpacityToColor(readableAccentOnWhite(catalogThemePrimary), 0.1) : '#fff',
                            border: active ? `2px solid ${readableAccentOnWhite(catalogThemePrimary)}` : '1px solid #e5e5e5',
                            boxShadow: active ? `0 0 0 3px ${applyOpacityToColor(readableAccentOnWhite(catalogThemePrimary), 0.12)}` : 'none',
                            cursor: isSelectedOutOfStock || depleted ? 'not-allowed' : 'pointer',
                            opacity: isSelectedOutOfStock || depleted ? 0.45 : 1,
                            textDecoration: depleted ? 'line-through' : 'none',
                          }}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {colorsList.length > 0 && (
                <div id="product-modal-colors">
                  <div style={{ fontWeight: '800', fontSize: '14px', marginBottom: '8px', color: '#111' }}>الألوان</div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {colorsList.map((c) => {
                      const active = selectedColor === c;
                      const depleted = optionDepleted(selectedProduct, "color", c, selectedSize);
                      return (
                        <button
                          key={`color-opt-${c}`}
                          type="button"
                          onClick={() => !depleted && setSelectedColor(c)}
                          disabled={isSelectedOutOfStock || depleted}
                          style={{
                            minHeight: '44px',
                            padding: '10px 16px',
                            borderRadius: '999px',
                            fontWeight: 800,
                            color: '#111',
                            background: active ? applyOpacityToColor(readableAccentOnWhite(catalogThemePrimary), 0.1) : '#fff',
                            border: active ? `2px solid ${readableAccentOnWhite(catalogThemePrimary)}` : '1px solid #e5e5e5',
                            boxShadow: active ? `0 0 0 3px ${applyOpacityToColor(readableAccentOnWhite(catalogThemePrimary), 0.12)}` : 'none',
                            cursor: isSelectedOutOfStock || depleted ? 'not-allowed' : 'pointer',
                            opacity: isSelectedOutOfStock || depleted ? 0.45 : 1,
                            textDecoration: depleted ? 'line-through' : 'none',
                          }}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {variantPickHint &&
              (String(selectedSize || "").trim() || String(selectedColor || "").trim()) ? (
                <div
                  role="alert"
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 14,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    color: "#9a3412",
                  }}
                >
                  {variantPickHint.message}
                </div>
              ) : null}
              {wholesaleDealer && wholesaleAvailQty != null ? (
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 14,
                    background: wholesaleAvailQty > 0 ? "#f0fdf4" : "#fef2f2",
                    border: `1px solid ${wholesaleAvailQty > 0 ? "#bbf7d0" : "#fecaca"}`,
                    color: wholesaleAvailQty > 0 ? "#166534" : "#991b1b",
                  }}
                >
                  {wholesaleAvailQty > 0 ? (
                    <>
                      الكمية المتوفرة: <strong>{wholesaleAvailQty}</strong>
                    </>
                  ) : (
                    "غير متوفر من هذا الخيار"
                  )}
                </div>
              ) : null}
            </div>
          );
        })()}

        {wholesaleDealer &&
        wholesaleAvailQty != null &&
        normalizeOptionList(selectedProduct?.sizes).length === 0 &&
        normalizeOptionList(selectedProduct?.colors).length === 0 ? (
          <div
            style={{
              marginBottom: 22,
              padding: "10px 14px",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              background: wholesaleAvailQty > 0 ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${wholesaleAvailQty > 0 ? "#bbf7d0" : "#fecaca"}`,
              color: wholesaleAvailQty > 0 ? "#166534" : "#991b1b",
            }}
          >
            {wholesaleAvailQty > 0 ? (
              <>
                الكمية المتوفرة: <strong>{wholesaleAvailQty}</strong>
              </>
            ) : (
              "غير متوفر"
            )}
          </div>
        ) : null}

        {/* --- MULTIPLE PRICE OPTIONS --- */}
        {(productModalExtraPricesList?.length > 0) && !modalWholesalePriceMissing && (
          <div className="product-modal-extra-prices" style={{ marginBottom: '30px' }}>
            <span style={{ fontWeight: '700', fontSize: '14px', display: 'block', marginBottom: '12px', color: '#888' }}>
              اختر الخيار المناسب:
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>


              {/* Dynamic Prices from DB */}
              {productModalExtraPricesList.map((p, i) => (
                <button
                  key={i}
                  onClick={() => {
                    const label = (p.description_ar || p.description_en || "").trim();
                    setSelectedPriceObj({
                      price: p.price,
                      label,
                      compareAt: p.compare_at_price ?? null,
                    });

                    const sizesList = productModalSizesList;
                    const hit = sizesList.find((s) => {
                      const sz = String(s || "").trim();
                      if (!sz) return false;
                      return label === sz || label.startsWith(`${sz} - `);
                    });
                    if (hit) setSelectedSize(hit);
                  }}
                  style={{
                    padding: '14px 20px', borderRadius: '12px', border: '2px solid', textAlign: 'right',
                    borderColor: selectedPriceObj?.label === (p.description_ar || p.description_en) ? readableAccentOnWhite(catalogThemePrimary) : '#eee',
                    background: selectedPriceObj?.label === (p.description_ar || p.description_en) ? applyOpacityToColor(readableAccentOnWhite(catalogThemePrimary), 0.06) : '#fff',
                    cursor: 'pointer', transition: '0.2s', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12
                  }}
                >
                  <span style={{ fontWeight: '700', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    {p.compare_at_price != null && Number(p.compare_at_price) !== Number(p.price) && (
                      <span style={{ fontSize: '13px', color: '#9ca3af', textDecoration: 'line-through' }}>₪{p.compare_at_price}</span>
                    )}
                    <span>₪{p.price}</span>
                  </span>
                  {(() => {
                    const label = dedupeVariantLabel(p.description_ar || p.description_en || "");
                    const splitIdx = label.indexOf(" - ");
                    const left = splitIdx >= 0 ? label.slice(0, splitIdx).trim() : label;
                    const right = splitIdx >= 0 ? label.slice(splitIdx + 3).trim() : "";
                    return (
                      <span
                        style={{
                          fontWeight: 700,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 2,
                          minWidth: 0,
                        }}
                      >
                        <span style={{ fontWeight: 800 }}>{left || "—"}</span>
                        {right ? (
                          <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 600 }}>
                            {right}
                          </span>
                        ) : null}
                      </span>
                    );
                  })()}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedProduct.addons_enabled && showStorefrontCart && !modalWholesalePriceMissing && modalVisibleAddons.length > 0 && (
            <div style={{ marginBottom: 26 }}>
              <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 12, color: "#111" }}>
                إضافات اختيارية
                {productModalHasSizes && !selectedSize ? (
                  <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginTop: 4 }}>
                    اختر الحجم أولاً لعرض الإضافات المتاحة
                  </span>
                ) : null}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {modalVisibleAddons.map((a) => {
                  const aid = String(a.id ?? '').trim().toLowerCase();
                  const on = selectedAddonIds.includes(aid);
                  return (
                    <button
                      key={String(a.id)}
                      type="button"
                      onClick={() => !isSelectedOutOfStock && toggleModalAddon(a.id)}
                      disabled={isSelectedOutOfStock || (productModalHasSizes && !selectedSize)}
                      style={{
                        padding: "14px 16px",
                        borderRadius: 12,
                        border: `2px solid ${on ? readableAccentOnWhite(catalogThemePrimary) : "#eee"}`,
                        background: on ? applyOpacityToColor(readableAccentOnWhite(catalogThemePrimary), 0.06) : "#fff",
                        cursor: isSelectedOutOfStock ? "not-allowed" : "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        textAlign: "right",
                        opacity: isSelectedOutOfStock ? 0.5 : 1,
                      }}
                    >
                      <span style={{ fontWeight: 800, color: "#111" }}>{a.name}</span>
                      <span style={{ fontWeight: 800, color: readableAccentOnWhite(catalogThemePrimary) }}>
                        +₪{a.price}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        <p className="product-modal-desc">
          {selectedProduct.description_ar || selectedProduct.description || (t.experienceExcellence + " " + settings.storeName)}
        </p>

        {/* Quantity and CTA */}
        {modalWholesalePriceMissing ? null : (
        <div className="product-modal-actions" style={{ marginTop: 'auto', display: 'flex', gap: '15px', flexDirection: 'column' }}>
          {showStorefrontCart ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '700', color: '#111' }}>الكمية:</span>
            <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f5', borderRadius: '14px', padding: '5px' }}>
              <button
                aria-label={settings.language === "en" ? "Decrease quantity" : "تقليل الكمية"}
                onClick={() =>
                  !modalCannotPurchase &&
                  productQuantity > 1 &&
                  setProductQuantity(productQuantity - 1)
                }
                disabled={modalCannotPurchase}
                style={{
                  width: '40px',
                  height: '40px',
                  border: 'none',
                  background: 'none',
                  cursor: modalCannotPurchase ? 'not-allowed' : 'pointer',
                  opacity: modalCannotPurchase ? 0.5 : 1,
                }}
              >
                <Minus size={18}/>
              </button>
              <div
                style={{
                  width: '40px',
                  textAlign: 'center',
                  fontWeight: '800',
                  fontSize: '18px',
                  color: '#0f172a',
                }}
              >
                {productQuantity}
              </div>
              <button
                aria-label={settings.language === "en" ? "Increase quantity" : "زيادة الكمية"}
                onClick={() => {
                  if (modalCannotPurchase || wholesaleQtyAtMax) return;
                  const maxQ = wholesaleDealer
                    ? wholesaleMaxQty
                    : maxQtyForSelection(selectedProduct, selectedSize, selectedColor);
                  setProductQuantity((q) => Math.min(maxQ, q + 1));
                }}
                disabled={modalCannotPurchase || wholesaleQtyAtMax}
                style={{
                  width: '40px',
                  height: '40px',
                  border: 'none',
                  background: 'none',
                  cursor: modalCannotPurchase || wholesaleQtyAtMax ? 'not-allowed' : 'pointer',
                  opacity: modalCannotPurchase || wholesaleQtyAtMax ? 0.5 : 1,
                }}
              >
                <Plus size={18}/>
              </button>
            </div>
            {wholesaleQtyCapped && wholesaleAvailQty != null ? (
              <span style={{ fontSize: 13, color: '#64748b', fontWeight: 700 }}>
                الحد الأقصى {wholesaleMaxQty}
              </span>
            ) : null}
          </div>
          ) : null}

          {settings.whatsappNumber && settings.checkoutFormType !== "restaurant" && (
            <button
              type="button"
              onClick={() => openWhatsAppProductInquiry(selectedProduct)}
              className="catalog-btn product-modal-wa-btn"
            >
              <span style={{ display: "flex" }} aria-hidden>
                <WhatsAppBrandIcon size={22} />
              </span>
              للاستفسار عن المنتج من هنا
            </button>
          )}

          {showStorefrontCart ? (
          <button
            onClick={() => {
              if (modalCannotPurchase) return;
              const variantErr = productVariantSelectionError(
                selectedProduct,
                selectedSize,
                selectedColor,
                language
              );
              if (variantErr) {
                setProductPickModal({
                  open: true,
                  message: variantErr.message,
                  focus: variantErr.focus,
                });
                return;
              }
              const sizesList = productModalSizesList;
              const colorsList = normalizeOptionList(selectedProduct?.colors);
              if (wholesaleSelectionUnavailable) {
                setProductPickModal({
                  open: true,
                  message:
                    wholesaleAvailQty === 0
                      ? "لا توجد كمية متوفرة من هذا الصنف في المخزون."
                      : `لا توجد كمية كافية في المخزون. الكمية المتوفرة من هذا الصنف: ${wholesaleAvailQty}.`,
                });
                return;
              }
              const pickedAddons = modalPickedAddons;
              const variantForCart = wholesaleDealer
                ? resolveVariantLabelForSize(selectedProduct, selectedSize) ||
                  selectedPriceObj.label
                : selectedPriceObj.label;
              const nextItem = {
                ...selectedProduct,
                wholesale_price:
                  selectedProduct?.wholesale_price != null
                    ? selectedProduct.wholesale_price
                    : wholesaleDealer
                      ? modalBaseUnit
                      : null,
                baseUnitPrice: modalBaseUnit,
                price: modalLineUnit,
                compareAt: pickedAddons.length ? null : (selectedPriceObj.compareAt ?? null),
                selectedVariant: variantForCart,
                selectedSize: sizesList.length > 0 ? selectedSize : null,
                selectedColor: colorsList.length > 0 ? selectedColor : null,
                quantity: productQuantity,
                selectedAddons: pickedAddons.length ? pickedAddons : undefined,
              };
              const wantsNotes = Boolean(selectedProduct?.allow_notes || selectedProduct?.notes_enabled);
              const stockKey = cartLineStockKey(nextItem);
              const inCartQty = sumCartQtyForStockKey(cart, stockKey);
              const maxQ = maxQtyForSelection(
                selectedProduct,
                nextItem.selectedSize,
                nextItem.selectedColor
              );
              const nextTotal = inCartQty + productQuantity;
              if (Number.isFinite(maxQ) && maxQ < 9999 && nextTotal > maxQ) {
                setProductPickModal({
                  open: true,
                  message:
                    maxQ <= inCartQty
                      ? `لا توجد كمية إضافية متوفرة من هذا الخيار. المتوفر: ${maxQ} ولديك ${inCartQty} في السلة.`
                      : `الكمية المتوفرة من هذا الخيار: ${maxQ}. لديك ${inCartQty} في السلة — يمكنك إضافة ${maxQ - inCartQty} فقط.`,
                  focus: null,
                });
                return;
              }
              if (wantsNotes) {
                pendingCartItemRef.current = nextItem;
                setNotesValue("");
                setNotesModalOpen(true);
                return;
              }
              setCart(mergeCartLine(cart, nextItem));
              trackMetaAddToCart(nextItem, cartLineUnitPrice);
              trackTikTokAddToCart(nextItem, cartLineUnitPrice);
              bumpCartFeedback();
              setSelectedProduct(null);
              setProductQuantity(1);
            }}
            disabled={modalCannotPurchase}
            className="catalog-btn catalog-btn-confirm product-modal-confirm-btn"
            style={{
              background: modalCannotPurchase ? '#e8e8e8' : readableAccentOnWhite(catalogThemePrimary),
              color: modalCannotPurchase
                ? '#888'
                : readableAccentOnWhite(catalogThemePrimary) === String(catalogThemePrimary || "").trim()
                  ? pickReadableIconColor(catalogThemePrimary, catalogThemeSecondary, "#ffffff")
                  : '#ffffff',
              border: 'none',
              padding: '16px 24px',
              fontSize: '1rem',
              fontWeight: '800',
              marginTop: '10px',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              cursor: modalCannotPurchase ? 'not-allowed' : 'pointer',
              opacity: modalCannotPurchase ? 0.85 : 1,
            }}
          >
            <ShoppingCart size={20} />
            {t.confirmAdd}
          </button>
          ) : null}
        </div>
        )}
      </div>
    </div>
  </div>
)}

      {productPickModal.open ? (
        <div
          className="catalog-notes-overlay catalog-product-pick-overlay"
          onClick={(e) => e.target === e.currentTarget && closeProductPickModal()}
        >
          <div className="catalog-notes-modal catalog-product-pick-modal" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()} dir={settings.layoutDirection || "rtl"}>
            <div className="catalog-notes-head">
              <div>
                <div className="catalog-notes-title">اختيار المنتج</div>
                <div className="catalog-notes-sub">أكمل الخيارات المطلوبة قبل الإضافة إلى السلة</div>
              </div>
              <button
                type="button"
                className="catalog-notes-close"
                onClick={closeProductPickModal}
                aria-label="إغلاق"
              >
                ×
              </button>
            </div>
            <p className="catalog-product-pick-message">{productPickModal.message}</p>
            <div className="catalog-notes-actions">
              <button
                type="button"
                className="catalog-notes-btn catalog-notes-btn--primary"
                style={{
                  background: readableAccentOnWhite(catalogThemePrimary),
                  color:
                    readableAccentOnWhite(catalogThemePrimary) === String(catalogThemePrimary || "").trim()
                      ? pickReadableIconColor(catalogThemePrimary, catalogThemeSecondary, "#ffffff")
                      : "#ffffff",
                  border: "none",
                }}
                onClick={() => {
                  closeProductPickModal();
                }}
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {notesModalOpen ? (
        <div className="catalog-notes-overlay" onClick={(e) => e.target === e.currentTarget && setNotesModalOpen(false)}>
          <div className="catalog-notes-modal" onClick={(e) => e.stopPropagation()} dir={settings.layoutDirection || "rtl"}>
            <div className="catalog-notes-head">
              <div>
                <div className="catalog-notes-title">{settings.language === "en" ? "Notes" : "ملاحظات"}</div>
                <div className="catalog-notes-sub">{settings.language === "en" ? "Add optional notes for this product." : "أضف ملاحظات اختيارية لهذا المنتج."}</div>
              </div>
              <button type="button" className="catalog-notes-close" onClick={() => setNotesModalOpen(false)} aria-label="Close">
                ×
              </button>
            </div>

            <label className="catalog-notes-label">{settings.language === "en" ? "Your notes (optional)" : "ملاحظاتك (اختياري)"}</label>
            <textarea
              className="catalog-notes-textarea"
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              rows={4}
              placeholder={settings.language === "en" ? "e.g., Gift wrap, deliver after 6pm..." : "مثال: تغليف هدية، التسليم بعد 6 مساءً..."}
            />

            <div className="catalog-notes-actions">
              <button type="button" className="catalog-notes-btn catalog-notes-btn--ghost" onClick={() => setNotesModalOpen(false)}>
                {settings.language === "en" ? "Cancel" : "إلغاء"}
              </button>
              <button
                type="button"
                className="catalog-notes-btn catalog-notes-btn--primary"
                style={{
                  background: readableAccentOnWhite(catalogThemePrimary),
                  color:
                    readableAccentOnWhite(catalogThemePrimary) === String(catalogThemePrimary || "").trim()
                      ? pickReadableIconColor(catalogThemePrimary, catalogThemeSecondary, "#ffffff")
                      : "#ffffff",
                  border: "none",
                }}
                onClick={() => {
                  const pending = pendingCartItemRef.current;
                  if (!pending) {
                    setNotesModalOpen(false);
                    return;
                  }
                  const stockKey = cartLineStockKey(pending);
                  const inCartQty = sumCartQtyForStockKey(cart, stockKey);
                  const maxQ = maxQtyForSelection(
                    pending,
                    pending.selectedSize,
                    pending.selectedColor
                  );
                  const qty = Math.max(1, Number(pending.quantity) || 1);
                  const nextTotal = inCartQty + qty;
                  if (Number.isFinite(maxQ) && maxQ < 9999 && nextTotal > maxQ) {
                    setProductPickModal({
                      open: true,
                      message:
                        maxQ <= inCartQty
                          ? `لا توجد كمية إضافية متوفرة من هذا الخيار. المتوفر: ${maxQ} ولديك ${inCartQty} في السلة.`
                          : `الكمية المتوفرة من هذا الخيار: ${maxQ}. لديك ${inCartQty} في السلة — يمكنك إضافة ${maxQ - inCartQty} فقط.`,
                      focus: null,
                    });
                    return;
                  }
                  setCart((prev) =>
                    mergeCartLine(prev, {
                      ...pending,
                      note: String(notesValue || "").trim(),
                    })
                  );
                  trackMetaAddToCart(
                    { ...pending, note: String(notesValue || "").trim() },
                    cartLineUnitPrice
                  );
                  trackTikTokAddToCart(
                    { ...pending, note: String(notesValue || "").trim() },
                    cartLineUnitPrice
                  );
                  bumpCartFeedback();
                  pendingCartItemRef.current = null;
                  setNotesModalOpen(false);
                  setSelectedProduct(null);
                  setProductQuantity(1);
                }}
              >
                {settings.language === "en" ? "Confirm" : "تأكيد"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modern Cart Sidebar - Organized Layout */}
      {isCartOpen && showStorefrontCart && (
        <>
          <div 
            className="vh-100 vw-100 position-fixed top-0 start-0 cart-overlay-nike" 
            onClick={(e) => {
              // Only close if clicking directly on the overlay, not on children
              if (e.target === e.currentTarget) {
                setIsCartOpen(false);
              }
            }} 
            style={{zIndex: 5000}}
          ></div>
          <div 
            className="position-fixed top-0 h-100 bg-white cart-sidebar-nike d-flex flex-column cart-sidebar-nike--with-corner-close"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', left: 0, zIndex: 5001 }}
          >
            <button
              type="button"
              className="cart-sidebar-corner-close"
              onClick={() => setIsCartOpen(false)}
              aria-label="إغلاق السلة"
            >
              <X size={20} strokeWidth={2.25} />
            </button>
            {/* Cart Header - Fixed */}
            <div className="cart-header-section p-4 border-bottom" style={{ flexShrink: 0 }}>
              <h4 className="cart-title-nike m-0 cart-title-nike--with-close-pad">{t.yourBag} ({cart.length})</h4>
            </div>
            
            {/* Cart Items - Scrollable */}
            <div className="cart-items-section flex-grow-1 overflow-auto" style={{
              padding: '20px',
              height: '100%',
              overscrollBehavior: 'contain',
            }}>
              {cart.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center h-100">
                  <ShoppingCart size={64} className="text-muted mb-4 opacity-25" />
                  <p className="text-muted fs-5">{t.emptyCart}</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {cart.map((item, idx) => {
                    const quantity = item.quantity || 1;
                    const updateQuantity = (newQty) => {
                      if (newQty < 1) return;
                      const stockKey = cartLineStockKey(item);
                      const othersQty = sumCartQtyForStockKey(cart, stockKey, idx);
                      const maxQ = maxQtyForSelection(
                        item,
                        item.selectedSize,
                        item.selectedColor
                      );
                      let capped = newQty;
                      if (Number.isFinite(maxQ) && maxQ < 9999 && othersQty + capped > maxQ) {
                        capped = Math.max(1, maxQ - othersQty);
                        if (capped < newQty) {
                          const inCartTotal = othersQty + quantity;
                          setCheckoutValidationModal({
                            open: true,
                            title: "المخزون",
                            message:
                              maxQ <= inCartTotal
                                ? `لا توجد كمية إضافية متوفرة من هذا الخيار. المتوفر: ${maxQ} ولديك ${inCartTotal} في السلة.`
                                : `الكمية المتوفرة من هذا الخيار: ${maxQ}. لديك ${inCartTotal} في السلة — يمكنك إضافة ${maxQ - inCartTotal} فقط.`,
                          });
                        }
                      }
                      const updatedCart = cart.map((cartItem, i) =>
                        i === idx ? { ...cartItem, quantity: capped } : cartItem
                      );
                      setCart(updatedCart);
                    };
                    return (
                      <div key={idx} className="cart-item-organized" style={{
                        background: '#fff',
                        border: '1px solid #e5e5e5',
                        borderRadius: '12px',
                        padding: '16px',
                      }}>
                        <div className="d-flex gap-3" style={{direction: 'ltr'}}>
                          <div style={{flexShrink: 0}}>
                            <img 
                              src={item.image} 
                              className="cart-item-img"
                              alt={item.name}
                              width={120}
                              height={120}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>

                          <div className="flex-grow-1 d-flex flex-column" style={{direction: 'rtl', textAlign: 'right', minWidth: 0}}>
                            {item.category && (
                              <div style={{
                                fontSize: '11px',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: '#666',
                                marginBottom: '6px'
                              }}>
                                {item.category}
                              </div>
                            )}

                            <div className="d-flex justify-content-between align-items-start gap-2" style={{ marginBottom: 8 }}>
                              <div style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: '15px',
                                fontWeight: '700',
                                color: '#111',
                                lineHeight: '1.4',
                              }}>
                                {item.name}
                              </div>
                              <button 
                                type="button"
                                className="cart-remove-btn-organized" 
                                aria-label="إزالة من السلة"
                                onClick={() => setCart(cart.filter((_, i) => i !== idx))}
                                style={{
                                  flexShrink: 0,
                                  background: '#f5f5f5',
                                  border: 'none',
                                  borderRadius: '8px',
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#ff4757';
                                  e.currentTarget.style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#f5f5f5';
                                  e.currentTarget.style.color = '#999';
                                }}
                              >
                                <X size={16} style={{color: '#64748b'}} />
                              </button>
                            </div>
                            {formatCartLineDetailLines(item).length ? (
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#555', marginBottom: '8px', lineHeight: 1.5 }}>
                                {formatCartLineDetailLines(item).map((line, li) => (
                                  <div key={li}>{line}</div>
                                ))}
                              </div>
                            ) : formatCartLineExtra(item) ? (
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#555', marginBottom: '8px', lineHeight: 1.5 }}>
                                {formatCartLineExtra(item)}
                              </div>
                            ) : null}

                            {String(item.note || "").trim() ? (
                              <div style={{ fontSize: '13px', fontWeight: 750, color: '#0f172a', marginBottom: '10px', lineHeight: 1.6 }}>
                                <span style={{ color: '#64748b', fontWeight: 900 }}>
                                  ملاحظة:
                                </span>{" "}
                                <span>{String(item.note || "").trim()}</span>
                              </div>
                            ) : null}

                            {/* Price */}
                            <div style={{
                              fontSize: '18px',
                              fontWeight: '700',
                              color: '#111',
                              marginBottom: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 4,
                            }}>
                              {item.compareAt != null &&
                                Number(item.compareAt) !== cartLineUnitPrice(item) && (
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#9ca3af', textDecoration: 'line-through' }}>
                                  ₪{item.compareAt}
                                </span>
                              )}
                              <span>₪{cartLineUnitPrice(item)}</span>
                            </div>

                            {/* Quantity Controls */}
                            <div className="d-flex align-items-center gap-3" style={{marginTop: 'auto'}}>
                              <div className="d-flex align-items-center" style={{
                                border: '1px solid #e5e5e5',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                background: '#fff'
                              }}>
                                <button 
                                  aria-label={settings.language === "en" ? "Decrease quantity" : "تقليل الكمية"}
                                  onClick={() => updateQuantity(quantity - 1)}
                                  style={{
                                    background: '#fff',
                                    border: 'none',
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 0.2s ease',
                                    minWidth: '36px'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.background = '#fff'}
                                >
                                  <Minus size={16} style={{color: '#111'}} />
                                </button>
                                <div style={{
                                  padding: '8px 16px',
                                  fontSize: '16px',
                                  fontWeight: '800',
                                  color: '#0f172a',
                                  minWidth: '40px',
                                  textAlign: 'center',
                                  borderLeft: '1px solid #e5e5e5',
                                  borderRight: '1px solid #e5e5e5',
                                  background: '#f8fafc',
                                }}>
                                  {quantity}
                                </div>
                                <button 
                                  aria-label={settings.language === "en" ? "Increase quantity" : "زيادة الكمية"}
                                  onClick={() => updateQuantity(quantity + 1)}
                                  style={{
                                    background: '#fff',
                                    border: 'none',
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 0.2s ease',
                                    minWidth: '36px'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.background = '#fff'}
                                >
                                  <Plus size={16} style={{color: '#111'}} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Cart Footer - Fixed */}
            {cart.length > 0 && (
              <div className="cart-footer-section p-4 border-top bg-white" style={{
                flexShrink: 0,
                boxShadow: '0 -4px 20px rgba(0,0,0,0.05)'
              }}>
                {settings.deliveryEnabled && !wholesaleDealer ? (
                  <div className="cart-totals mb-4">
                    <div className="cart-totals__row">
                      <span>{t.itemsTotal}</span>
                      <span>₪{cartItemsSubtotal}</span>
                    </div>
                    <div className="cart-totals__row">
                      <span>{t.deliveryLabel}</span>
                      <span className={checkoutShowsDelivery ? undefined : "cart-totals__pending"}>
                        {checkoutShowsDelivery ? `₪${checkoutDeliveryFee}` : t.deliveryByRegion}
                      </span>
                    </div>
                    <div className="cart-totals__row cart-totals__row--grand">
                      <span>{t.grandTotal}</span>
                      <span>
                        ₪{cartItemsSubtotal + (checkoutShowsDelivery ? checkoutDeliveryFee : 0)}
                        {checkoutShowsDelivery ? null : (
                          <small className="cart-totals__plus"> {t.plusDelivery}</small>
                        )}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: '#666'
                    }}>
                      {t.subtotal}
                    </span>
                    <span style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#111'
                    }}>
                      ₪{cartItemsSubtotal}
                    </span>
                  </div>
                )}

                {(storefrontWhatsappCartOnly || checkoutUsesWhatsapp || !settings.deliveryEnabled) && (
                  <div className="cart-footer-notes">
                    {(storefrontWhatsappCartOnly || checkoutUsesWhatsapp) && (
                      <div className="cart-footer-note cart-footer-note--whatsapp">
                        <span className="cart-footer-note__icon" aria-hidden>
                          <MessageCircle size={16} strokeWidth={2.2} />
                        </span>
                        <div className="cart-footer-note__text">
                          <strong>{t.whatsappNoteTitle}</strong>
                          <span>{t.whatsappNoteBody(String(settings.storeName || "").trim())}</span>
                        </div>
                      </div>
                    )}
                    {!settings.deliveryEnabled && (
                      <div className="cart-footer-note cart-footer-note--delivery">
                        <span className="cart-footer-note__icon" aria-hidden>
                          <Truck size={16} strokeWidth={2.2} />
                        </span>
                        <div className="cart-footer-note__text">
                          <strong>{t.deliveryNoteTitle}</strong>
                          <span>{t.deliveryNoteBody}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Checkout Button */}
                <button 
                  className="catalog-btn w-100 py-3 fw-bold catalog-btn-whatsapp"
                  onClick={
                    storefrontWhatsappCartOnly
                      ? submitWhatsappCartDirect
                      : openCheckout
                  }
                  disabled={checkoutSubmitting}
                  style={{
                    background: checkoutSubmitting ? '#e8e8e8' : catalogThemePrimary,
                    color: checkoutSubmitting ? '#888' : catalogThemeSecondary,
                    border: `2px solid ${checkoutSubmitting ? '#d1d5db' : catalogThemeSecondary}`,
                    borderRadius: '10px',
                    fontSize: '0.9375rem',
                    fontWeight: '700',
                    letterSpacing: '0.02em',
                    cursor: checkoutSubmitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {checkoutSubmitting
                    ? 'جاري الإرسال...'
                    : storefrontWhatsappCartOnly
                      ? t.sendOrder
                      : checkoutUsesWhatsapp
                        ? t.sendOrder
                        : t.continueToOrder}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Checkout Modal (Customer Form) */}
      {isCheckoutOpen && (
        <div
          onClick={(e) => e.target === e.currentTarget && closeCheckout()}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 6000,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '16px',
            paddingTop: 'max(16px, env(safe-area-inset-top, 0px))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            direction: 'rtl',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '560px',
              maxHeight: 'calc(100dvh - 32px)',
              margin: 'auto',
              background: '#fff',
              borderRadius: '14px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                flexShrink: 0,
                padding: '16px 18px',
                borderBottom: '1px solid #eee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div style={{ fontWeight: 800, fontSize: '16px', color: '#111' }}>
                إتمام الطلب
              </div>
              <button
                type="button"
                onClick={closeCheckout}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div
              className="catalog-customer-checkout-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
              }}
            >
              <div style={{ padding: '16px 18px' }}>
                {checkoutError && (
                  <div
                    style={{
                      marginBottom: '12px',
                      background: '#fff7ed',
                      border: '1px solid #fed7aa',
                      color: '#9a3412',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      fontWeight: 700,
                    }}
                  >
                    {checkoutError}
                  </div>
                )}

                {(() => {
                  const isRestaurant = settings.checkoutFormType === 'restaurant';
                  const orderType = isRestaurant
                    ? (checkoutForm.orderType || 'delivery')
                    : 'delivery';
                  const inputStyle = {
                    width: '100%',
                    padding: '12px 12px',
                    borderRadius: '10px',
                    border: '1px solid #e5e5e5',
                  };
                  const ltrInputStyle = {
                    ...inputStyle,
                    direction: 'ltr',
                    textAlign: 'left',
                  };
                  const labelStyle = {
                    fontWeight: 700,
                    marginBottom: '6px',
                    color: '#111',
                  };

                  /** أزرار اختيار نوع الطلب لفورم المطاعم */
                  const restaurantTypeOptions = [
                    { value: 'dine_in', label: 'داخل المطعم' },
                    { value: 'pickup', label: 'استلام' },
                    { value: 'delivery', label: 'توصيل' },
                  ];

                  return (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {isRestaurant && (
                        <div>
                          <div style={labelStyle}>نوع الطلب</div>
                          <div
                            role="tablist"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, 1fr)',
                              gap: 8,
                              background: '#f5f5f5',
                              padding: 4,
                              borderRadius: 12,
                            }}
                          >
                            {restaurantTypeOptions.map((opt) => {
                              const active = orderType === opt.value;
                              return (
                                <button
                                  type="button"
                                  key={opt.value}
                                  role="tab"
                                  aria-selected={active}
                                  onClick={() => {
                                    const storeRegion = String(
                                      settings.storeDeliveryRegion || ''
                                    ).trim();
                                    setCheckoutForm((p) => ({
                                      ...p,
                                      orderType: opt.value,
                                      ...(opt.value === 'delivery' && storeRegion
                                        ? {
                                            deliveryRegion: storeRegion,
                                            cityId: '',
                                          }
                                        : {}),
                                    }));
                                    if (
                                      opt.value === 'delivery' &&
                                      settings.checkoutFormType !== 'restaurant' &&
                                      (!cities || cities.length === 0)
                                    ) {
                                      loadCities();
                                    }
                                  }}
                                  style={{
                                    padding: '10px 8px',
                                    borderRadius: 10,
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: 800,
                                    fontSize: 13,
                                    background: active ? '#fff' : 'transparent',
                                    color: active ? catalogThemePrimary : '#374151',
                                    boxShadow: active
                                      ? '0 2px 8px rgba(15,23,42,0.08)'
                                      : 'none',
                                  }}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div>
                        <div style={labelStyle}>الاسم</div>
                        <input
                          value={checkoutForm.name}
                          onChange={(e) =>
                            setCheckoutForm((p) => ({ ...p, name: e.target.value }))
                          }
                          type="text"
                          placeholder="الاسم الكامل"
                          style={inputStyle}
                        />
                      </div>

                      {orderType === 'delivery' && (
                        <>
                          {!isRestaurant && (
                            <div>
                              <div style={labelStyle}>منطقة التوصيل</div>
                              <select
                                value={checkoutForm.deliveryRegion}
                                onChange={(e) => {
                                  setCheckoutForm((p) => ({
                                    ...p,
                                    deliveryRegion: e.target.value,
                                    cityId: '',
                                  }));
                                  loadCities();
                                }}
                                disabled={citiesLoading}
                                style={inputStyle}
                              >
                                <option value="">
                                  {citiesLoading ? 'جاري التحميل…' : 'اختر المنطقة'}
                                </option>
                                {regionSelectOptions.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          {isRestaurant ? (
                            <>
                              <div>
                                <div style={labelStyle}>المدينة</div>
                                <input
                                  type="text"
                                  readOnly
                                  value={settings.storeCityName || ''}
                                  style={{
                                    ...inputStyle,
                                    background: '#f5f5f5',
                                    color: '#374151',
                                    cursor: 'default',
                                  }}
                                  aria-readonly="true"
                                />
                              </div>
                              <div>
                                <div style={labelStyle}>تفاصيل العنوان</div>
                                <input
                                  value={checkoutForm.address}
                                  onChange={(e) =>
                                    setCheckoutForm((p) => ({
                                      ...p,
                                      address: e.target.value,
                                    }))
                                  }
                                  type="text"
                                  placeholder="الشارع / الحارة / رقم البيت / تفاصيل إضافية"
                                  style={inputStyle}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <div style={labelStyle}>العنوان</div>
                                {citiesForSelectedRegion.length > 0 ? (
                                  <SearchableSelect
                                    value={checkoutForm.cityId}
                                    onChange={(cityId) =>
                                      setCheckoutForm((p) => ({
                                        ...p,
                                        cityId,
                                      }))
                                    }
                                    options={citiesForSelectedRegion}
                                    getOptionValue={(c) => String(c.id)}
                                    getOptionLabel={(c) => c.name}
                                    placeholder={
                                      citiesLoading
                                        ? 'جاري تحميل العناوين...'
                                        : !checkoutForm.deliveryRegion
                                          ? 'اختر المنطقة أولاً'
                                          : 'ابحث عن العنوان أو اختره…'
                                    }
                                    emptyMessage="لا توجد عناوين مطابقة"
                                    disabled={
                                      citiesLoading ||
                                      !checkoutForm.deliveryRegion
                                    }
                                  />
                                ) : (
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: 13,
                                      color: '#64748b',
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {checkoutForm.deliveryRegion
                                      ? 'اكتب المدينة والعنوان في «تفاصيل العنوان» أدناه.'
                                      : 'اختر المنطقة أولاً.'}
                                  </p>
                                )}
                              </div>
                              <div>
                                <div style={labelStyle}>تفاصيل العنوان</div>
                                <input
                                  value={checkoutForm.address}
                                  onChange={(e) =>
                                    setCheckoutForm((p) => ({
                                      ...p,
                                      address: e.target.value,
                                    }))
                                  }
                                  type="text"
                                  placeholder="الشارع / الحارة / رقم البيت / تفاصيل إضافية"
                                  style={inputStyle}
                                />
                              </div>
                            </>
                          )}
                        </>
                      )}

                      {orderType === 'dine_in' && (
                        <div>
                          <div style={labelStyle}>رقم الطاولة</div>
                          <input
                            value={checkoutForm.tableNumber}
                            onChange={(e) =>
                              setCheckoutForm((p) => ({
                                ...p,
                                tableNumber: e.target.value,
                              }))
                            }
                            type="text"
                            inputMode="numeric"
                            placeholder="مثلاً: 5"
                            style={inputStyle}
                          />
                        </div>
                      )}

                      {/* في فورم الاستلام (pickup) لا حاجة لمدينة/عنوان/طاولة */}

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '12px',
                        }}
                      >
                        <div>
                          <div style={labelStyle}>رقم الهاتف</div>
                          <input
                            value={checkoutForm.phone}
                            onChange={(e) =>
                              setCheckoutForm((p) => ({
                                ...p,
                                phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                              }))
                            }
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="0591234567"
                            style={ltrInputStyle}
                          />
                        </div>
                        {/* رقم بديل: في الستور والاستلام مفيد، نخفيه في dine_in */}
                        {orderType !== 'dine_in' && (
                          <div>
                            <div style={labelStyle}>رقم بديل (اختياري)</div>
                            <input
                              value={checkoutForm.altPhone}
                              onChange={(e) =>
                                setCheckoutForm((p) => ({
                                  ...p,
                                  altPhone: e.target.value,
                                }))
                              }
                              type="tel"
                              placeholder="اختياري"
                              style={ltrInputStyle}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <div style={labelStyle}>
                          {orderType === 'dine_in'
                            ? 'ملاحظات عامة (اختياري)'
                            : 'ملاحظة (اختياري)'}
                        </div>
                        <textarea
                          value={checkoutForm.notes}
                          onChange={(e) =>
                            setCheckoutForm((p) => ({
                              ...p,
                              notes: e.target.value,
                            }))
                          }
                          rows={2}
                          placeholder="أي تفاصيل إضافية..."
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {(() => {
              const isRestaurant = settings.checkoutFormType === 'restaurant';
              const orderType = isRestaurant
                ? checkoutForm.orderType || 'delivery'
                : 'delivery';
              if (cart.length === 0) return null;
              if (!isRestaurant && orderType !== 'delivery') return null;
              const showDelivery = checkoutShowsDelivery && !wholesaleDealer;
              const fee = showDelivery ? checkoutDeliveryFee : 0;
              const grand = cartItemsSubtotal + fee;
              return (
                <div
                  style={{
                    flexShrink: 0,
                    padding: '12px 18px',
                    borderTop: '1px solid #eee',
                    background: '#fff',
                    fontSize: 14,
                    color: '#0f172a',
                  }}
                >
                  {showDelivery ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 6,
                      fontWeight: 700,
                    }}
                  >
                    <span>مجموع المنتجات</span>
                    <span>₪{cartItemsSubtotal}</span>
                  </div>
                  ) : null}
                  {showDelivery ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 8,
                        color: '#475569',
                        fontWeight: 700,
                      }}
                    >
                      <span>التوصيل</span>
                      <span>₪{fee}</span>
                    </div>
                  ) : null}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontWeight: 900,
                      fontSize: 16,
                      color: '#0f172a',
                    }}
                  >
                    <span>{showDelivery ? 'المجموع الكلي' : 'المجموع'}</span>
                    <span>₪{grand}</span>
                  </div>
                </div>
              );
            })()}

            <div
              style={{
                flexShrink: 0,
                padding: '14px 18px',
                borderTop: '1px solid #eee',
                display: 'flex',
                gap: '10px',
              }}
            >
              <button
                type="button"
                onClick={closeCheckout}
                className="catalog-btn"
              style={{
                background: '#fff',
                color: catalogThemeSecondary,
                border: `2px solid ${catalogThemeSecondary}`,
                padding: '12px 14px',
                fontWeight: 800,
                borderRadius: '10px',
              }}
                disabled={checkoutSubmitting}
              >
                رجوع
              </button>
              <button
                type="button"
                onClick={submitCheckout}
                className="catalog-btn"
                style={{
                  flex: 1.5,
                  background: checkoutSubmitting ? '#e8e8e8' : catalogThemePrimary,
                  color: checkoutSubmitting ? '#888' : catalogThemeSecondary,
                  border: `2px solid ${checkoutSubmitting ? '#d1d5db' : catalogThemeSecondary}`,
                  padding: '12px 14px',
                  fontWeight: 900,
                  borderRadius: '10px',
                  cursor: checkoutSubmitting ? 'not-allowed' : 'pointer',
                }}
                disabled={checkoutSubmitting}
              >
                {checkoutSubmitting
                  ? 'جاري الإرسال...'
                  : checkoutUsesWhatsapp
                    ? 'متابعة إلى واتساب'
                    : `إرسال الطلب إلى ${String(settings.storeName || 'المتجر').trim() || 'المتجر'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={checkoutValidationModal.open}
        title={checkoutValidationModal.title || "حقول مطلوبة"}
        message={checkoutValidationModal.message}
        buttonText="حسناً"
        language="ar"
        onClose={() =>
          setCheckoutValidationModal({ open: false, message: "", title: "حقول مطلوبة" })
        }
      />

      {/* Order Success Modal */}
      {orderSuccessOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setOrderSuccessOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 6500,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            direction: 'rtl',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="تم إرسال الطلب"
            className="catalog-order-success-dialog catalog-order-success-dialog--enter"
            style={{
              width: '100%',
              maxWidth: 420,
              background: '#ffffff',
              borderRadius: 18,
              padding: '28px 22px 22px 22px',
              boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="catalog-order-success-check"
              style={{
                width: 72,
                height: 72,
                margin: '0 auto 14px',
                borderRadius: '50%',
                background: '#dcfce7',
                color: '#16a34a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div style={{ fontWeight: 900, fontSize: 20, color: '#0f172a', marginBottom: 8 }}>
              {orderSuccessInfo.wasEdited ? 'تم تحديث طلبك بنجاح' : 'تم إرسال طلبك بنجاح'}
            </div>
            {orderSuccessInfo.orderNumber ? (
              <div
                style={{
                  marginBottom: 14,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: '#f1f5f9',
                  color: '#0f172a',
                  fontWeight: 900,
                  fontSize: 16,
                  lineHeight: 1.6,
                }}
              >
                رقم الطلبية: {formatOrderNumberDisplay(orderSuccessInfo.orderNumber)}
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginTop: 4 }}>
                  احفظ هذا الرقم لتتبع طلبك من «تتبع طلبك بواسطة رقم الطلب» في أسفل الصفحة.
                </div>
              </div>
            ) : null}
            <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.7, marginBottom: 20 }}>
              {(() => {
                if (orderSuccessInfo.whatsappCartOnly) {
                  return orderSuccessInfo.whatsappOpened
                    ? 'تم إرسال تفاصيل السلة عبر واتساب. أكمل بيانات التوصيل في المحادثة مع المتجر.'
                    : orderSuccessInfo.whatsappUrl
                      ? 'اضغط الزر أدناه لفتح واتساب وإرسال تفاصيل السلة للمتجر.'
                      : 'تم تجهيز طلبك. تواصل مع المتجر عبر واتساب لإكمال الطلب.';
                }
                const t = orderSuccessInfo.orderType;
                const intro = orderSuccessInfo.wasEdited
                  ? 'تم حفظ التعديلات على طلبك. سيراجع صاحب المتجر الطلبية من جديد.'
                  : t === 'dine_in'
                    ? 'تم استلام طلبك داخل المطعم.'
                    : t === 'pickup'
                      ? 'تم استلام طلبك للاستلام من المطعم.'
                      : 'تم استلام طلبك وسنبدأ بتجهيزه قريباً.';
                const tail = orderSuccessInfo.whatsappOpened
                  ? ' فُتح الواتساب لإرسال نسخة من الطلب لصاحب المتجر.'
                  : orderSuccessInfo.whatsappUrl
                    ? ' اضغط الزر أدناه لفتح الواتساب وإرسال الطلبية.'
                    : orderSuccessInfo.orderNumber
                      ? ' يمكنك متابعة حالة طلبك باستخدام رقم الطلبية أعلاه.'
                      : '';
                return intro + tail;
              })()}
            </div>
            {orderSuccessInfo.whatsappUrl ? (
              <button
                type="button"
                onClick={() => {
                  openWhatsAppUrl(orderSuccessInfo.whatsappUrl, null);
                }}
                className="catalog-btn catalog-btn-whatsapp"
                style={{
                  width: '100%',
                  marginBottom: 10,
                  padding: '12px 14px',
                  fontWeight: 900,
                  borderRadius: 12,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <WhatsAppBrandIcon size={22} />
                {orderSuccessInfo.wasEdited
                  ? "فتح الواتساب وإرسال الطلبية المعدّلة"
                  : "فتح الواتساب وإرسال الطلبية"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setOrderSuccessOpen(false)}
              className="catalog-btn"
              style={{
                width: '100%',
                background: catalogThemePrimary,
                color: catalogThemeSecondary,
                border: 'none',
                padding: '12px 14px',
                fontWeight: 900,
                borderRadius: 12,
                cursor: 'pointer',
              }}
            >
              تم
            </button>
          </div>
        </div>
      )}

      {/* Return & exchange policy modal */}
      {returnPolicyModalOpen && settings.returnExchangePolicy ? (
        <div
          className="vh-100 vw-100 position-fixed top-0 start-0"
          style={{
            zIndex: 6500,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(3px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
            direction: settings.layoutDirection || "rtl",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setReturnPolicyModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="سياسة التبديل والترجيع"
            style={{
              width: "min(720px, 96vw)",
              maxHeight: "min(86vh, 720px)",
              overflow: "auto",
              background: catalogFooterColors.bg || catalogThemePrimary,
              color: catalogFooterColors.fg || "#ffffff",
              borderRadius: 18,
              border: `1px solid ${(catalogFooterColors.fg || "#ffffff")}22`,
              boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: 18,
                borderBottom: `1px solid ${(catalogFooterColors.fg || "#ffffff")}22`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                background: catalogFooterColors.bg || catalogThemePrimary,
                zIndex: 2,
              }}
            >
              <div style={{ fontWeight: 950, fontSize: 18 }}>سياسة التبديل والترجيع</div>
              <button
                type="button"
                onClick={() => setReturnPolicyModalOpen(false)}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  border: `1px solid ${(catalogFooterColors.fg || "#ffffff")}22`,
                  background: `${(catalogFooterColors.fg || "#ffffff")}12`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: catalogFooterColors.fg || "#ffffff",
                  cursor: "pointer",
                }}
                aria-label="إغلاق"
              >
                <X size={18} />
              </button>
            </div>
            <div
              style={{
                padding: "20px 22px 24px",
                lineHeight: 1.85,
                fontWeight: 600,
                fontSize: 15,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {settings.returnExchangePolicy}
            </div>
          </div>
        </div>
      ) : null}

      {/* Track Orders Modal */}
      {trackModalOpen && (
        <div
          className="vh-100 vw-100 position-fixed top-0 start-0"
          style={{
            zIndex: 6500,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(3px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
            direction: "rtl",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setTrackModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="تتبع الطلب"
            style={{
              width: "min(920px, 96vw)",
              maxHeight: "min(86vh, 820px)",
              overflow: "auto",
              background: catalogFooterColors.bg || catalogThemePrimary,
              color: catalogFooterColors.fg || "#ffffff",
              borderRadius: 18,
              border: `1px solid ${(catalogFooterColors.fg || "#ffffff")}22`,
              boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: 18,
                borderBottom: `1px solid ${(catalogFooterColors.fg || "#ffffff")}22`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                background: catalogFooterColors.bg || catalogThemePrimary,
                zIndex: 2,
              }}
            >
              <div style={{ fontWeight: 950, fontSize: 18 }}>تتبع الطلب برقم الطلبية</div>
              <button
                type="button"
                onClick={() => setTrackModalOpen(false)}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  border: `1px solid ${(catalogFooterColors.fg || "#ffffff")}22`,
                  background: `${(catalogFooterColors.fg || "#ffffff")}12`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: catalogFooterColors.fg || "#ffffff",
                  cursor: "pointer",
                }}
                aria-label="إغلاق"
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 12, lineHeight: 1.7 }}>
                أدخل رقم الطلبية الذي وصلك بعد إتمام الطلب (في رسالة الواتساب أو من صاحب المتجر)، وسيتم عرض حالة طلبك فقط.
              </div>

              <form onSubmit={handleTrackOrders} style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  required
                  placeholder="رقم الطلبية"
                  value={trackOrderNumber}
                  onChange={(e) => setTrackOrderNumber(e.target.value)}
                  style={{
                    flex: "1 1 260px",
                    minWidth: 220,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: `1px solid ${(catalogFooterColors.fg || "#ffffff")}22`,
                    background: `${(catalogFooterColors.fg || "#ffffff")}10`,
                    color: catalogFooterColors.fg || "#ffffff",
                    outline: "none",
                    direction: "ltr",
                    textAlign: "right",
                  }}
                />
                <button
                  type="submit"
                  disabled={trackLoading}
                  style={{
                    padding: "12px 16px",
                    borderRadius: 12,
                    border: "none",
                    background: trackLoading
                      ? `${(catalogFooterColors.fg || "#ffffff")}22`
                      : (catalogFooterColors.fg || "#ffffff"),
                    color: catalogFooterColors.bg || catalogThemePrimary,
                    fontWeight: 900,
                    cursor: trackLoading ? "not-allowed" : "pointer",
                    minWidth: 160,
                  }}
                >
                  {trackLoading ? "جاري البحث..." : "عرض الطلب"}
                </button>
              </form>

              {trackError ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 12,
                    border: "1px solid rgba(239,68,68,0.35)",
                    background: "rgba(239,68,68,0.10)",
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  {trackError}
                </div>
              ) : null}

              {trackedOrders && trackedOrders.length ? (
                <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
                  {trackedOrders.slice(0, 10).map((o) => {
                    const isOpen = expandedTrackedOrderId === o.id;
                    const items = Array.isArray(o.items) ? o.items : [];
                    return (
                      <div
                        key={o.id}
                        style={{
                          borderRadius: 16,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.06)",
                          overflow: "hidden",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedTrackedOrderId(isOpen ? null : o.id)}
                          style={{
                            width: "100%",
                            textAlign: "start",
                            padding: 14,
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                          }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                            <div style={{ fontWeight: 950, display: "flex", gap: 10, flexWrap: "wrap" }}>
                              <span>رقم الطلب:</span>
                              <span style={{ opacity: 0.92 }}>
                                {o.order_number
                                  ? formatOrderNumberDisplay(o.order_number)
                                  : String(o.id || "").slice(0, 8)}
                              </span>
                              <span style={{ opacity: 0.6 }}>•</span>
                              <span>الحالة:</span>
                              <span style={{ opacity: 0.92 }}>{orderStatusLabel(o.status, "ar")}</span>
                            </div>
                            <div
                              style={{
                                marginTop: 10,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                                alignItems: "center",
                              }}
                              aria-label="مراحل الطلب"
                            >
                              {ORDER_STATUS_VALUES.map((k, idx) => {
                                const cur = normalizeOrderStatus(o.status);
                                const stepIdx = ORDER_STATUS_VALUES.indexOf(cur);
                                const done = stepIdx >= idx;
                                const active = stepIdx === idx;
                                return (
                                  <span
                                    key={k}
                                    style={{
                                      fontSize: 11,
                                      fontWeight: active ? 950 : 700,
                                      opacity: done ? 1 : 0.38,
                                      padding: "4px 10px",
                                      borderRadius: 999,
                                      border: `1px solid rgba(255,255,255,${done ? 0.35 : 0.12})`,
                                      background: active ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.06)",
                                      lineHeight: 1.35,
                                      maxWidth: "100%",
                                    }}
                                  >
                                    {idx + 1}. {orderStatusLabel(k, "ar")}
                                  </span>
                                );
                              })}
                            </div>
                            {o?.is_customer_modified ? (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 12,
                                  fontWeight: 800,
                                  color: "#bfdbfe",
                                  background: "rgba(59, 130, 246, 0.2)",
                                  border: "1px solid rgba(96, 165, 250, 0.45)",
                                  borderRadius: 10,
                                  padding: "6px 10px",
                                  lineHeight: 1.5,
                                }}
                              >
                                لقد قمت بتعديل هذه الطلبية
                              </div>
                            ) : null}
                            {o?.is_owner_modified ? (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 12,
                                  fontWeight: 800,
                                  color: "#fde68a",
                                  background: "rgba(251, 191, 36, 0.18)",
                                  border: "1px solid rgba(251, 191, 36, 0.35)",
                                  borderRadius: 10,
                                  padding: "6px 10px",
                                  lineHeight: 1.5,
                                }}
                              >
                                تم تعديل هذه الطلبية من قبل صاحب المتجر
                              </div>
                            ) : null}
                            <div style={{ fontSize: 13, opacity: 0.85, display: "flex", gap: 12, flexWrap: "wrap" }}>
                              <span>التاريخ: {formatTrackDate(o.created_at)}</span>
                              {o?.city?.name ? <span>المدينة: {o.city.name}</span> : null}
                              <span>عدد القطع: {formatTrackNumber(o.items_count || 0)}</span>
                              <span>المنتجات: {formatTrackMoney(o.items_subtotal ?? o.total ?? 0)}</span>
                              {trackOrderShowsDelivery(o) ? (
                                <span>التوصيل: {formatTrackMoney(o.delivery_fee ?? 0)}</span>
                              ) : null}
                              <span>الإجمالي: {formatTrackMoney(o.total ?? 0)}</span>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 8,
                              flexShrink: 0,
                            }}
                          >
                            {o.can_edit ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditTrackedOrder(o);
                                }}
                                disabled={trackEditLoading}
                                style={{
                                  padding: "8px 12px",
                                  borderRadius: 10,
                                  border: "1px solid rgba(255,255,255,0.35)",
                                  background: "rgba(255,255,255,0.15)",
                                  color: "#fff",
                                  fontWeight: 900,
                                  fontSize: 12,
                                  cursor: trackEditLoading ? "not-allowed" : "pointer",
                                }}
                              >
                                تعديل
                              </button>
                            ) : o?.edit_block_message ? (
                              <div style={{ fontSize: 12, opacity: 0.9, maxWidth: 240, textAlign: "end" }}>
                                {String(o.edit_block_message)}
                              </div>
                            ) : null}
                            <div style={{ opacity: 0.85, fontWeight: 950 }}>
                              {isOpen ? "إخفاء" : "عرض التفاصيل"}
                            </div>
                          </div>
                        </button>

                        {isOpen ? (
                          <div style={{ padding: "0 14px 14px" }}>
                            <div
                              style={{
                                borderTop: "1px solid rgba(255,255,255,0.10)",
                                paddingTop: 12,
                                display: "grid",
                                gap: 10,
                              }}
                            >
                              {o?.customer_name ? (
                                <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.7 }}>
                                  <span style={{ fontWeight: 900 }}>الاسم: </span>
                                  <span style={{ opacity: 0.9 }}>{o.customer_name}</span>
                                </div>
                              ) : null}
                              {o?.address ? (
                                <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.7 }}>
                                  <span style={{ fontWeight: 900 }}>العنوان: </span>
                                  <span style={{ opacity: 0.9 }}>{o.address}</span>
                                </div>
                              ) : null}
                              {o?.notes ? (
                                <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.7 }}>
                                  <span style={{ fontWeight: 900 }}>ملاحظات: </span>
                                  <span style={{ opacity: 0.9 }}>{o.notes}</span>
                                </div>
                              ) : null}

                              <div style={{ marginTop: 6 }}>
                                <div style={{ fontWeight: 950, marginBottom: 10 }}>المنتجات</div>
                                {items.length ? (
                                  <div style={{ display: "grid", gap: 10 }}>
                                    {items.map((it) => (
                                      <div
                                        key={it.id || `${it?.product?.id}-${it?.selected_variant || ""}`}
                                        style={{
                                          borderRadius: 14,
                                          border: "1px solid rgba(255,255,255,0.10)",
                                          background: "rgba(255,255,255,0.05)",
                                          padding: 12,
                                        }}
                                      >
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                          <div style={{ fontWeight: 950 }}>{formatTrackItemTitle(it)}</div>
                                          <div style={{ fontWeight: 950, opacity: 0.95 }}>
                                            {formatTrackMoney(it.line_total || 0)}
                                          </div>
                                        </div>
                                        {formatTrackItemOptions(it) ? (
                                          <div style={{ marginTop: 6, fontSize: 12.5, opacity: 0.85, lineHeight: 1.6 }}>
                                            {formatTrackItemOptions(it)}
                                          </div>
                                        ) : null}
                                        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9, display: "flex", gap: 12, flexWrap: "wrap" }}>
                                          <span>الكمية: {formatTrackNumber(it.quantity || 0)}</span>
                                          <span>سعر القطعة: {formatTrackMoney(it.unit_price || 0)}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 13, opacity: 0.8 }}>لا توجد تفاصيل منتجات.</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}



<div style={{ position: "relative" }}>
  {canEdit ? (
    <button
      type="button"
      className="catalog-studio-edit-chip catalog-studio-edit-chip--footer"
      aria-label="تعديل الفوتر"
      onClick={(e) => {
        e.stopPropagation();
        openStudioCoreForm("footer");
      }}
    >
      <Pencil size={15} strokeWidth={2.25} />
    </button>
  ) : null}
{wholesaleEditingOrderId || storefrontEditingOrderId ? (
  <div
    className="text-center py-2"
    style={{ background: "#fef3c7", color: "#92400e", fontWeight: 800, fontSize: 13 }}
    dir="rtl"
  >
    أنت تعدّل طلباً غير مؤكّد — عدّل من السلة ثم أرسل الطلبية من جديد.
  </div>
) : null}
<footer
  id="catalog-footer"
  className={`footer-flat-clean catalog-skip-global-palette${
    String(catalogLayoutEffective.footer_image_url || "").trim() ? " footer-flat-clean--has-bg-image" : ""
  }`}
  style={{
    backgroundColor: catalogFooterColors.bg || catalogThemePrimary,
    color: catalogFooterColors.fg || "#ffffff",
    direction: "rtl",
    padding: "52px 0 36px 0",
    "--catalog-footer-accent": (() => {
      const accent = String(catalogThemePrimary || "#22c55e").trim() || "#22c55e";
      return accent;
    })(),
    "--catalog-footer-btn-fg": (() => {
      const accent = String(catalogThemePrimary || "#22c55e").trim() || "#22c55e";
      const rgb = parseCssColorToRgb(accent);
      if (rgb && relativeLuminanceFromRgb(rgb) > 0.55) return "#0f172a";
      return "#ffffff";
    })(),
    "--catalog-footer-fg": catalogFooterColors.fg || "#ffffff",
    ...(String(catalogLayoutEffective.footer_image_url || "").trim()
      ? {
          ["--footer-bg-overlay"]: String(
            Math.min(
              1,
              Math.max(0, Number(catalogLayoutEffective.footer_image_overlay ?? 0.45))
            )
          ),
        }
      : {}),
  }}
>
  {String(catalogLayoutEffective.footer_image_url || "").trim() ? (
    <div
      className="footer-flat-clean__bg"
      aria-hidden
      style={{
        backgroundImage: cssBackgroundImageFromUrl(
          String(catalogLayoutEffective.footer_image_url || "").trim()
        ),
      }}
    />
  ) : null}
  <div className="container footer-flat-clean__content">
    <div className="footer-grid-layout">
      
      {/* 1. قسم معلومات المتجر */}
      <div className="footer-info-col">
        <h4 className="footer-logo-text">
          {catalogFooterDisplay.storeName}
        </h4>
        {catalogFooterDisplay.tagline ? (
          <p className="footer-description-text">
            {catalogFooterDisplay.tagline}
          </p>
        ) : null}
        
        <div className="footer-contact-details">
          {catalogFooterDisplay.address && (
            <div className="contact-item">
              <span className="contact-label">الموقع:</span>
              <span className="contact-value">{catalogFooterDisplay.address}</span>
            </div>
          )}
          {footerPhones.length > 0 && (
            <div className="contact-item">
              <span className="contact-label">الهاتف:</span>
              <span className="contact-value footer-contact-multi">
                {footerPhones.map((ph, i) => (
                  <span key={`${ph}-${i}`}>
                    <a href={`tel:${ph.replace(/[^\d+]/g, "")}`} className="footer-contact-link" dir="ltr">
                      {ph}
                    </a>
                    {i < footerPhones.length - 1 ? "،" : null}
                  </span>
                ))}
              </span>
            </div>
          )}
          {footerFax && (
            <div className="contact-item">
              <span className="contact-label">فاكس:</span>
              <span className="contact-value" dir="ltr">{footerFax}</span>
            </div>
          )}
          {footerEmail && (
            <div className="contact-item">
              <span className="contact-label">البريد الإلكتروني:</span>
              <a href={`mailto:${footerEmail}`} className="contact-value footer-contact-link" dir="ltr">
                {footerEmail}
              </a>
            </div>
          )}
          {footerCustomFields.map((f) => (
            <div className="contact-item" key={f.id}>
              {f.label ? <span className="contact-label">{f.label}:</span> : null}
              <span className="contact-value" dir="auto">{f.value}</span>
            </div>
          ))}
        </div>

        {/* روابط التواصل الاجتماعي - Flat Icons */}
        {(catalogFooterDisplay.whatsappNumber ||
          catalogFooterDisplay.facebook ||
          catalogFooterDisplay.instagram ||
          footerTiktok) ? (
          <div className="footer-social-wrapper">
            <h5 className="social-title">تابعنا</h5>
            <div className="social-links">
              {catalogFooterDisplay.whatsappNumber && (
                <a
                  href={`https://wa.me/${normalizeWhatsAppDigits(catalogFooterDisplay.whatsappNumber)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="social-icon-btn catalog-footer-social-wa"
                  aria-label="WhatsApp"
                >
                  <WhatsAppBrandIcon size={28} className="catalog-footer-wa-icon" />
                </a>
              )}
              {catalogFooterDisplay.facebook && (
                <a href={catalogFooterDisplay.facebook} target="_blank" rel="noreferrer" className="social-icon-btn" aria-label="Facebook">
                  <Facebook size={28} color={catalogFooterDisplay.fg || "#ffffff"} strokeWidth={2.2} />
                </a>
              )}
              {catalogFooterDisplay.instagram && (
                <a href={catalogFooterDisplay.instagram} target="_blank" rel="noreferrer" className="social-icon-btn" aria-label="Instagram">
                  <Instagram size={28} color={catalogFooterDisplay.fg || "#ffffff"} strokeWidth={2.2} />
                </a>
              )}
              {footerTiktok && (
                <a href={footerTiktok} target="_blank" rel="noreferrer" className="social-icon-btn" aria-label="TikTok" style={{ color: catalogFooterDisplay.fg || "#ffffff" }}>
                  <TikTokBrandIcon size={26} />
                </a>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* 2. نموذج التواصل - Minimalist Form */}
      <div className="footer-form-col">
        <h4 className="form-title">تواصل معنا</h4>
        <form onSubmit={handleContactSubmit} className="footer-contact-form">
          <div className="input-group-flat">
            <input 
              type="text" 
              required
              placeholder="الاسم الكامل" 
              value={contactForm.name}
              onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
              style={{
                borderBottom: `1px solid ${catalogFooterColors.fg}40`,
                color: catalogFooterColors.fg,
              }}
            />
          </div>
          <div className="input-group-flat">
            <textarea 
              required
              placeholder="رسالتك هنا..." 
              value={contactForm.message}
              onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
              rows="2"
              style={{
                borderBottom: `1px solid ${catalogFooterColors.fg}40`,
                color: catalogFooterColors.fg,
              }}
            ></textarea>
          </div>
          <button 
            type="submit"
            className="catalog-footer-btn catalog-footer-btn--primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'جاري الإرسال...' : 'إرسال الرسالة'}
          </button>
        </form>

        {storefrontFullOrdersEnabled ? (
        <div style={{ marginTop: 22, paddingTop: 20, borderTop: `1px solid ${catalogFooterColors.fg}15` }}>
          <h4 className="form-title" style={{ marginBottom: 12 }}>تتبع طلبك</h4>
          <button
            type="button"
            className="catalog-footer-btn catalog-footer-btn--ghost catalog-footer-btn--block"
            onClick={() => setTrackModalOpen(true)}
          >
            تتبع طلبك بواسطة رقم الطلب
          </button>
        </div>
        ) : null}
      </div>
    </div>

    {/* Copyright Layer */}
    <div className="footer-bottom-bar" style={{ borderTop: `1px solid ${catalogFooterColors.fg}15` }}>
      {settings.returnExchangePolicy ? (
        <button
          type="button"
          className="catalog-footer-btn catalog-footer-btn--ghost"
          style={{ marginBottom: 14 }}
          onClick={() => setReturnPolicyModalOpen(true)}
        >
          سياسة التبديل والترجيع
        </button>
      ) : null}
      <p dir="rtl">
        جميع الحقوق محفوظة
        {catalogFooterDisplay.storeName ? ` - ${catalogFooterDisplay.storeName}` : ""}
        {" "}
        <bdi dir="ltr">© {new Date().getFullYear()}</bdi>
      </p>
      <a
        className="footer-powered-by"
        href="https://kataloji.com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Powered by Kataloji"
      >
        <span className="footer-powered-by__text">Powered by</span>
        <img
          className="footer-powered-by__logo"
          src={KATALOJI_WORDMARK_SRC}
          alt="Kataloji"
          width={180}
          height={36}
          loading="lazy"
          decoding="async"
        />
      </a>
    </div>
  </div>
</footer>
</div>
        </div>
      </div>
    </div>
    {cartToastText ? (
      <div className="catalog-cart-toast" role="status" aria-live="polite">
        {cartToastText}
      </div>
    ) : null}
    <CatalogNotFoundModal
      open={catalogNotFound && !canEdit}
      language={settings.layoutDirection === "ltr" ? "en" : "ar"}
      slug={String(storeKey || userId || "").trim()}
      onGoHome={() => navigate("/")}
    />
    {catalogUnavailable && !canEdit ? (
      <CatalogStoreUnavailable
        language={settings.layoutDirection === "ltr" ? "en" : "ar"}
      />
    ) : null}
    <CatalogWholesaleAuthModal
      open={wholesaleAuthOpen}
      storeKey={storeKey}
      catalogSlug={wholesaleCatalogSlug || catalogSlug}
      initialUsername={wholesaleLoginUsername}
      lockUsername={wholesaleLoginUsernameLocked}
      logoSrc={String(settings.logo || "").trim()}
      storeName={String(settings.storeName || "").trim()}
      accentColor={wholesaleStripAccent}
      buttonColor={wholesaleAuthButtonColor}
      fieldColor={wholesaleAuthFieldColor}
      onClose={() => setWholesaleAuthOpen(false)}
      onSuccess={(dealer, token) => {
        setShopper(null);
        handleWholesaleLoginSuccess(dealer, token);
        if (!token) fetchCatalogPage(1, { append: false });
      }}
    />
    <CatalogWholesaleOrdersModal
      open={wholesaleOrdersOpen}
      storeKey={storeKey}
      catalogSlug={wholesaleCatalogSlug || catalogSlug}
      storeName={String(settings?.storeName || "").trim()}
      dealerName={String(wholesaleDealer?.name || "").trim()}
      onClose={() => setWholesaleOrdersOpen(false)}
      onNeedLogin={() => {
        setWholesaleOrdersOpen(false);
        setWholesaleAuthOpen(true);
      }}
      onEditOrder={onWholesaleEditOrder}
    />
    </>
    </CatalogCardPriceContext.Provider>
    </CatalogMenuOnlyContext.Provider>
  );
}
